import { CARD_MINIMUM_CHARGE_MESSAGE, isBelowCardMinimum } from "../../src/config/cardMinimum.ts";
import {
  mountTicketPresentation,
  createTicketLineFrame,
  createTicketLinePrice,
  createTicketStateButton,
  createTicketLineEditButton,
  paintTicketState,
} from "./ticketPresentation.ts";
import { normalizeLeadingDot } from "../../src/domain/leadingDot.ts";
import { wireStatusLink } from "./supportStatusLink.ts";
import { createNotificationsPage } from "./notificationsPage.ts";
import { bundledRevision, startNewVersionCheck } from "./newVersionCheck.ts";
import { startErrorTracking } from "./errorTracking.ts";
import { createSupportHelp, visibleScreenName } from "./supportHelp.ts";
import { createHelpPanel } from "./helpPanel.ts";
import { createWillyChat } from "./willy.ts";
import { willyRouteAllowed } from "./willyModel.ts";
import { LOCATION_HELP_ROLES } from "./helpRoles.ts";
import { locationHelpTarget } from "./helpScreenMap.ts";
import { pageLabelFrom, routeFrom } from "./supportHelpModel.ts";
import { currentProductSelling } from "./api.ts";
import { createNetworkOfferNotice } from "./networkOfferNotice.ts";
import { mountPossibleDuplicateClients } from "./possibleDuplicateClients.ts";
import { clientLastVisitText } from "./clientIdentityDisplay.ts";
import { createReaderDropdown } from "./readerDropdown.ts";
import { mountClientReceiptEmail } from "./clientReceiptEmail.ts";
import { reportArrival } from "./reportArrival.ts";
// DOM wiring for the ALPHA-06/07 bartender ticket UI. Framework-free by
// design, matching harness/client/src/main.ts's own approach: this file
// owns element lookup, event handling, and rendering; ./api.ts owns the
// HTTP calls; ./session.ts owns the login stub; ./theme.ts owns theme
// selection and persistence; ./catalogDisplay.ts owns category display
// order/labels; ./liquorMatch.ts owns the recipe-to-liquor-slot lookup;
// ./money.ts owns cents formatting.
//
// Three screens share this one document, exactly one visible at a time:
// login, Open Tabs (ALPHA-07, docs/product-requirements.md P0.3's
// employee landing screen, modeled on design/mockups/tabs.html), and the
// ring screen (ALPHA-06's selling surface and ticket rail). Open Tabs is
// where every session lands after login and where Close returns to.

import {
  floorMenuGridItems,
  floorMenuUnavailableReason,
  floorMenuUnavailableText,
  popularNowItems,
} from "./menuGridModel.ts";
import { mountTicketReceiptRow } from "./ticketReceipt.ts";
import { installKeyboardInset } from "./keyboardInset.ts";
import { discountKindLabel } from "./discountKind.ts";
import { createExportMenu } from "./exportMenu.ts";
import { type GateExportFormat } from "./exportMenuModel.ts";
import { deliverExportFile } from "./googleSheets.ts";
import { mountInvoiceCard } from "./invoiceCard.ts";
import { compareNames, sortByName } from "./nameSort.ts";
import {
  setGuestServiceToday as saveGuestServiceToday,
  addGuest,
  addInventorySaleLine,
  addLine,
  cancelSplitPlan,
  changeLineGuest,
  changeLineLiquor,
  changePin,
  cancelOrder,
  closeOrder,
  collectShare,
  createOrder,
  createProvisionalClient,
  createSplitPlan,
  finalizeBusinessDay,
  getCloseoutCsv,
  getCloseoutSummary,
  getReportContext,
  getReportLocations,
  getHoldForOrder,
  getVoidHistory,
  getVoidHistoryFile,
  getLiquorProduct,
  getVendor,
  getMenuDrink,
  moveLineToTab,
  getMenuItems,
  getPopularMenuItems,
  getMenuStockState,
  recordMenuStockFlag,
  type MenuStockDrinkDto,
  type MenuStockIngredientDto,
  getLineDrinkCost,
  getOrder,
  postIdCheckSkip,
  postIdVerification,
  getPaymentPreview,
  getPayments,
  getSession,
  getSharePaymentPreview,
  showOrderReaderCart,
  showShareReaderCart,
  getSplitPreview,
  listActionReasons,
  listApprovalCandidates,
  listLiquorProducts,
  listRecentLiquors,
  getMenuCounts,
  listTransfers as listStockTransfers,
  getOpeningBalanceStatus,
  listManagerExceptions,
  listStockFlags,
  listOpenTabs,
  listOpenTabSummaries,
  listRecentlyClosedTabs,
  listSavedCardsForOrder,
  mergeManyTabs,
  type TabMergeDto,
  listSplitPayments,
  getReaderHealth,
  listSelectableTerminals,
  getDeviceIdentity,
  listUsers,
  login,
  type SignInBody,
  logoutRequest,
  openTabHold,
  payOrder,
  authorizeReceiptSale,
  getReceiptAuthorization,
  printReceipt,
  reprintReceipt,
  approveReceiptTipCap,
  recordReceiptTipOverride,
  collectReceiptTipRemainder,
  leaveReceiptTipRemainder,
  prepareLine,
  priceQuote,
  quoteInventorySale,
  recordStockFlag,
  cancelReaderAction,
  getRememberedTerminal,
  registerTerminal,
  rememberTerminal,
  removeLine,
  checkPaymentAttemptRequest,
  recordOutsideCollectionRequest,
  resolveManagerExceptionRequest,
  listVoidReasons,
  applyOrderDiscount,
  getOrderDiscounts,
  listDiscountReasons,
  listDiscounts,
  changeLinePrice,
  listPriceChangeReasons,
  restoreLinePrice,
  getLinePriceHistory,
  type LinePriceChangeDto,
  type LineStockOverrideDto,
  type ActionReasonDto,
  type IdVerificationViewDto,
  type LastReopenDto,
  type PriceChangeReasonDto,
  removeOrderDiscount,
  removeScheduledSpecial,
  type DiscountDto,
  type OrderDiscountApplicationDto,
  type OrderDiscountsDto,
  type RemovedManualDiscountDto,
  type RemovedScheduledSpecialDto,
  unprepareOrderLine,
  voidOrderLine,
  listRefundableLines,
  refundAmountPayment,
  refundPaymentLines,
  listRefundReasons,
  type RefundableLineDto,
  type RefundCardDto,
  type RefundReasonDto,
  createPaymentLink,
  listPaymentLinks,
  reconcilePaymentLink,
  cancelPaymentLink,
  type PaymentLinkDto,
  approvePurchaseOrder,
  correctReceipt,
  getCart,
  getPurchaseApprovalThreshold,
  getDraftProposals,
  getOrderReceivingView,
  getPurchaseOrder,
  getPurchaseOrderPdf,
  getAbc2goExportPreview,
  getAbc2goExportFile,
  listPurchaseOrders,
  listReceipts,
  placeCart,
  listReceiptVendors,
  recordOutsideSystemReceipt,
  recordStockReceipt,
  removeCartLine,
  setCartLine,
  transitionPurchaseOrder,
  type CartDto,
  type CartEstimateDto,
  type CartLineBlockerDto,
  type CartLineDto,
  type CartVendorSummaryDto,
  type DraftProposalDto,
  type InventoryReceiptDto,
  type LineReceivingStateDto,
  type OrderReceivingViewDto,
  type OrderSubstitutionDto,
  type PlacedOrderDto,
  type PurchaseExceptionReasonDto,
  type PurchaseExceptionResolutionDto,
  type PurchaseOrderDetailDto,
  type PurchaseOrderLineDto,
  type PurchaseOrderStatusChangeDto,
  type PurchaseOrderSummaryDto,
  PURCHASE_EXCEPTION_REASONS,
  PURCHASE_EXCEPTION_RESOLUTIONS,
  removeSavedCard,
  reopenOrder,
  resolveNoTip,
  captureSignature,
  getReceiptOptions,
  recordReceiptChoice,
  type ReceiptOptionsDto,
  type SignatureStrokeDto,
  type ReceiptDeliveryMethodDto,
  searchClients,
  setAuthToken,
  setMyTheme,
  getNetworkBrand,
  getNetworkBrandFont,
  getNetworkBrandLogo,
  shouldClearPayIdempotencyKeyOnFailure,
  updateMyProfile,
  getMyNotificationPreferences,
  putMyNotificationPreference,
  vaultCardFromCapture,
  getSavedCardMatch,
  type ApiErrorBody,
  type ClientSearchResultDto,
  type CloseoutSummaryDto,
  type GuestDto,
  type HoldIncrementOutcomeDto,
  type InventorySaleKind,
  type LiquorProductDto,
  type ManagerExceptionDto,
  type PaymentResolutionActionDto,
  type MenuDrinkComponentDto,
  type RecipeMethodDto,
  type MenuDrinkDto,
  type MenuDrinkListItemDto,
  type MenuDrinkModifierDetailDto,
  type MenuItemDto,
  type OpenTabDto,
  type OrderDto,
  type OrderHoldDto,
  type OrderLineComponentInput,
  type OrderLineWithComponentsDto,
  type AmountSelectionDto,
  type GuestShareDto,
  type PaymentAttemptDto,
  type PaymentWithTipSplitsDto,
  type PaymentPreviewDto,
  type ReceiptAuthorizationStateDto,
  type LocationPrinterChoiceDto,
  type PriceQuoteLineInput,
  type PriceQuoteModifierInput,
  type PriceQuoteResponse,
  type PricingModel,
  type SavedCardDto,
  type SavedCardEntryDto,
  type SplitPlanDto,
  type SplitShareDto,
  type SelectableTerminalDto,
  type ApiResult,
  type TipSelectionInput,
  type UserDto,
  type VoidHistoryDto,
  resetSessionLostLatch,
  type SessionLostReason,
  setSessionLostHandler,
  withoutSessionLostHandling,
  type ReportId,
  supportRequestCall,
  helpGuidesCall,
  willyCall,
  helpImageCall,
} from "./api.ts";
import { mountBarcodeReceive, type BarcodeReceiveElements } from "./barcodeReceive.ts";
import * as admin from "./admin.ts";
import { mountSystemChecksStrip } from "./systemChecks.ts";
import { mountCardNudge } from "./cardNudge.ts";
import { clearStripeReturnMarker as clearWizardStripeReturnMarker } from "./setupWizardModel.ts";
// ONB4 slice 1: the first sign-in setup wizard.
import {
  configureSetupWizard,
  openSetupWizard,
  openSetupWizardIfPaused,
  openSetupWizardOnRoyaltyReturn,
  openSetupWizardOnStripeReturn,
  openSetupWizardOnSubscriptionDebitReturn,
  resetSetupWizard,
  setupWizardEnabled,
} from "./setupWizard.ts";
// UX108: the address of the screen (URL hash) and non-sensitive typed drafts, both so a refresh keeps the work.
import {
  clearRoute,
  readInitialRoute,
  republishLastRoute,
  requestRoutePublish,
  setRoutePopHandler,
  setRouteProvider,
  withReplaceOnly,
} from "./routeHistory.ts";
import { parseRoute, type ReportRange, type Route } from "./screenRoute.ts";
import {
  asRecord,
  asString,
  clearAllDrafts,
  clearDraft,
  loadDraft,
  saveDraft,
  type DraftScope,
} from "./screenDrafts.ts";
import {
  readArrival as readCardPaymentsArrival,
  withoutArrival as withoutCardPaymentsArrival,
  type CardPaymentsArrival,
} from "./cardPaymentsModel.ts";
import {
  readRoyaltyArrival,
  withoutRoyaltyArrival,
  type RoyaltyArrival,
} from "./royaltyAuthorizationModel.ts";
import {
  readSubscriptionDebitArrival,
  withoutSubscriptionDebitArrival,
  type SubscriptionDebitArrival,
} from "./bankAccountsModel.ts";
import {
  readAccountingArrival,
  withoutAccountingArrival,
  type AccountingArrival,
} from "./accountingModel.ts";
import { onStartingStockMayHaveChanged } from "./startingStockMenuSignal.ts";
import { createCashDrawer } from "./cashDrawer.ts";
import { renderCloseoutCash } from "./closeoutCash.ts";
import { renderCloseoutAwaitingTip } from "./closeoutAwaitingTip.ts";
import { createCashTender, type CashTenderPick } from "./cashTender.ts";
import { createAwaitingTipSheet } from "./awaitingTipSheet.ts";
import {
  cardLabel,
  isReaderTipSale,
  isReceiptTipSale,
  readTipEntry,
  receiptPrintView,
} from "./receiptTipModel.ts";
import { createRemainingTipSheet, type RemainingTipOutcome } from "./remainingTipSheet.ts";
import {
  OVER_CAP_OVERRIDE_REASON,
  phaseFromAttempt,
  phaseFromRefusal,
} from "./remainingTipModel.ts";
import {
  cashLockedLabel,
  cashOffered,
  changeFromAttempt,
  confirmEnabled,
  tipSectionVisible,
  type PayTender,
} from "./cashTenderModel.ts";
import { transfersToReceive as transfersToReceiveCount } from "./transfersModel.ts";
// UX116: the three function hubs the account menu's group rows open (Reports opens its home, RPTLAYOUT1-F1-S2).
import {
  configureFunctionHubs,
  openHub,
  resetFunctionHubs,
  type HubTarget,
} from "./functionHubs.ts";
import { isHubId, type HubId } from "./functionHubsModel.ts";
import type { InventoryHomeTarget } from "./inventoryHome.ts";
import type { CountClosedSummary } from "./countTableModel.ts";
import {
  ACCOUNT_MENU_GROUPS,
  CASH_DRAWER_ROW_ID,
  STARTING_STOCK_ROW_ID,
  chipBadgeCount,
  groupBadge,
  groupHint,
  groupVisible,
  menuBadgesFromCounts,
  needsYouRows,
  visibleItems,
  withoutHiddenRows,
  type MenuBadge,
  type MenuCountsBody,
} from "./accountMenu.ts";
import {
  CARD_REMOVAL_PENDING_LABEL,
  CARD_REMOVAL_RETRY_LABEL,
  removalToast,
  retryToast,
  savedCardRemovalState,
  withPending,
  withoutPending,
  type PendingCardDetach,
} from "./savedCardRemovalModel.ts";
import { defaultSavedCardId, newestSavedCardFirst } from "./payCardDefaultModel.ts";
import { createReceiveVendorController, type ReceiveVendorLoadResult } from "./receiveVendor.ts";
import {
  createReceiveScan,
  type ReceiveScanController,
  type ReceiveScanOrigin,
} from "./receiveScan.ts";
import { createOrderingScreens, type OrderingScreensController, type OrderingView } from "./orderingScreens.ts";
import { enteredQuantityText, recordedToast, recordedVendorText } from "./receiveVendorModel.ts";
import { resolveActorDisplayName } from "./actorDisplay.ts";
import {
  classifyApprovalRetryResult,
  approvalCandidateBadgeRole,
  selectApprovalCandidates,
  type ApprovalCandidateSource,
} from "./approvalFlow.ts";
import { categoryLabel, formatRecipeQuantity, pricingModelLabel } from "./catalogDisplay.ts";
import { findLiquorSlotComponent, normalizeOunces } from "./liquorMatch.ts";
// UX17 (docs/adr/ADR-004-ui-composition.md Decision 3, as amended):
// FLOOR_HTML_ROOTS is scripts/floorSurfaces.ts's own canonical list, read
// by check-touch-only.ts and check-floor-viewport.ts too, and its header
// states those two gates "must never be able to drift apart." The SCREENS
// registry below derives each screen's surface from THIS import rather
// than storing a second, hand-written copy of the same fact.
import { FLOOR_HTML_ROOTS } from "../../scripts/floorSurfaces.ts";
import {
  CHECK_PAYMENT_BUSY_LABEL,
  CHECK_PAYMENT_FAILURE_TOAST,
  CHECK_PAYMENT_LABEL,
  checkPaymentToast,
  CLEAR_HOLD_BUTTON_TEXT,
  CLEAR_HOLD_CANCEL_TEXT,
  CLEAR_HOLD_CONFIRM_TEXT,
  CLEAR_HOLD_FAILURE_TOAST,
  clearHoldPanelText,
  clearHoldSuccessToast,
  type ClearHoldDisposition,
  formatManagerExceptionAge,
  isPaymentDisagreementKind,
  PAYMENT_RESOLUTION_CONFIRM_TEXT,
  PAYMENT_RESOLUTION_FAILURE_TOAST,
  PAYMENT_RESOLUTION_NOTE_REQUIRED_ARIA_LABEL,
  PAYMENT_RESOLUTION_NOTE_REQUIRED_PLACEHOLDER,
  PAYMENT_RESOLUTION_QUESTION,
  PAYMENT_RESOLUTION_RECORD_LINE,
  PAYMENT_RESOLUTION_SUCCESS_TOAST,
  paymentResolutionOptions,
  paymentResolutionSatisfied,
  HOLD_NEEDS_MANAGER_BADGE_TEXT,
  HOLD_PROVIDER_MISMATCH_TICKET_BANNER_TEXT,
  managerExceptionKindHelp,
  managerExceptionKindLabel,
  managerExceptionRowAmountText,
  isLocationDayRow,
  drawerOpenAtCloseTitle,
  RESOLUTION_NOTE_OPTIONAL_ARIA_LABEL,
  resolveNoteFieldText,
  resolveNoteSatisfied,
  TEST_HOLD_CLEARED_NOTE_TEXT,
} from "./managerExceptionDisplay.ts";
import {
  basisPointsToPercentString,
  type CapturedPaymentsSummary,
  formatBasisPointsAsPercent,
  formatCents,
  formatPaidCountLabel,
  parseDollarsToCentsExact,
  summarizeCapturedPayments,
} from "./money.ts";
import { cutoffText, dayLabel } from "./reportFormat.ts";
import { finalizeWords } from "./closeoutFinalizeWords.ts";
import {
  buildCloseoutChecks,
  buildCloseoutVerdict,
  passedSummaryLine,
  type CloseoutCheck,
} from "./closeoutChecksModel.ts";
import { renderCloseoutWaterfall } from "./closeoutMoneyChart.ts";
import { buildWaterfall } from "./closeoutWaterfallModel.ts";
import { refundCardLabel, refundCardSublabel } from "./refundCards.ts";
import { PayIdempotencyKeyMap, type PayKeySignal } from "./payIdempotencyKeys.ts";
import { payConfirmWording } from "./payConfirmWording.ts";
import { lineSignature, previewLinesChanged } from "./paymentPreviewStaleness.ts";
import { businessDaySheetCopy } from "./businessDayConfirm.ts";
import {
  shouldShowPayCloseRefusalToast,
  translateHoldError,
  translatePayCloseError,
} from "./payCloseErrorMessages.ts";
import { discountRefusalMessage } from "./discountRefusal.ts";
import {
  ON_READER_HINT,
  ON_READER_TITLE,
  WAITING_HINT,
  WAITING_LINE_DELAY_MS,
  WAITING_TITLE,
  cancelOutcomeView,
  payResultView,
  receiptPaidTitle,
  shareCollectView,
  type PayStatusTone,
} from "./terminalReaderDisplay.ts";
import {
  CARD_READER_REGISTERED_EVENT,
  OTHER_LOCATION_TERMINAL_DROPPED_TOAST,
  REMEMBERED_TERMINAL_WAIT_MS,
  TERMINAL_NOT_SAVED_TOAST,
  decideSignInTerminal,
  signInTerminalToast,
  soleReaderOf,
  topbarTerminalLabel,
  type LocationReader,
} from "./terminalSignInModel.ts";
import {
  READER_ASK_TITLE,
  chargingReaderLine,
  decideFromReaderList,
  decideReaderBeforeCharge,
  readerAskLine,
  readerAskReasonForRefusal,
  type ReaderAskReason,
} from "./cardReaderAskModel.ts";
import { initRailResize } from "./railResize.ts";
import {
  READER_HEALTH_REFRESH_MS,
  closeoutReaderLine,
  readerPillShortText,
  readerPillText,
  type ReaderHealthState,
} from "./readerHealthModel.ts";
import {
  belowParButtonDisabledReason,
  belowParProposalsToStage,
  formatOrderUnitBottles,
  formatOrderUnitLabel,
  formatProposalOrderCoverage,
  formatProposalVendorCost,
  formatCartMoney,
  stagedQuantityForProposal,
  computeExceptionResolutionTarget,
  computeReceiptCorrectionQuantityDelta,
  cartEstimateHeading,
  cartEstimateValue,
  cartLineUnitLabel,
  cartSummaryChips,
  cartSummaryCounts,
  ORDER_SUBSTITUTION_LABEL,
  SUBSTITUTE_CONTROL_LABEL,
  describeSubstitution,
  substitutionsForLine,
  describeCartLineBlockers,
  proposalStatusChipLabel,
  describeVendorMinimum,
  describeVendorMissingList,
  describeVendorSchedule,
  describePurchaseOrderApproval,
  describeStatusHistoryRow,
  formatCartLineQuantity,
  formatCartLineTotal,
  formatCartLineUnitPrice,
  formatLineExtendedCost,
  formatLineOrderedQuantity,
  formatLineUnitCost,
  formatOrderEstimatedTotal,
  formatOrderLineCount,
  placedOrderWords,
  formatOutsideSystemReceiptReference,
  formatProposalOnHandWarning,
  formatProposalSuggested,
  formatProposalUnitDisplay,
  formatReceiptSummary,
  formatReceivingQuantity,
  groupCartLinesByVendor,
  vendorEstimateHeading,
  vendorEstimateValue,
  proposalShortfallIsZero,
  roundQuantityToWholeUnit,
  purchaseExceptionReasonLabel,
  purchaseExceptionResolutionLabel,
  purchaseOrderStatusLabel,
  purchaseOrderIsReceivable,
  describeOrderWhen,
  receivingCostFieldLabels,
  receivingLineIsClosed,
  receivingLineIsPacked,
  describeOrderUnitQuantity,
  orderUnitReceivingOption,
  ORDER_UNIT_RECEIVING_UNIT_VALUE,
  receivingLinePrefill,
  receivingUnitOptionsForItemKind,
  sortProposalsForDisplay,
  CONTAINER_RECEIVING_UNIT_VALUE,
  type CartVendorGroup,
  type ProposalSortOrder,
  type ReceivingProductContainerInfo,
  type ReceivingUnitOption,
} from "./purchasingDisplay.ts";
import { formatDraftMoney } from "./invoiceDraftDisplay.ts";
import { mountTerminalSetup, type TerminalSetupScreen } from "./terminalSetup.ts";
import { mountWelcome, type WelcomeFlow } from "./welcomeLink.ts";
import { parseWelcomeFragment } from "./welcomeLinkModel.ts";
import { DISCONNECTED_NOTICE } from "./terminalSetupModel.ts";
import {
  clearPersistedHoldKey,
  clearPersistedPayKey,
  clearSession,
  clearTerminalConfig,
  enterSupportTerminalConfig,
  isSupportTerminalConfig,
  applyDeviceIdentity,
  brandNameFor,
  floorLocationNameFor,
  deviceLineFor,
  hasAnyPermission,
  hasPermission,
  loadPersistedHoldKey,
  loadPersistedPayKeys,
  loadSession,
  loadTerminalConfig,
  pinLengthRangeText,
  roleDisplayLabel,
  savePaymentTerminalSelection,
  savePersistedHoldKey,
  savePersistedPayKey,
  saveSession,
  saveTerminalConfig,
  type Session,
  type TerminalConfig,
} from "./session.ts";
import {
  chooseTopbarLayout,
  TOPBAR_LOGO_ASPECT,
  TOPBAR_MARK_ASPECT,
  type TopbarCompactLevel,
  type TopbarMeasure,
} from "./topbarBrand.ts";
import {
  filterTabs,
  highlightSegments,
  type SearchableTab,
  type TabChipFilters,
  type TabFilterMode,
  type TabMatch,
  tokenizeSearch,
} from "./tabSearch.ts";
import { formatTabAge, tabAgeInMinutes, type TabAge } from "./tabAge.ts";
import {
  countUnpaidTabsOpenedBy,
  groupOpenTabsByOpener,
  unpaidTabsSentence,
  unpaidTabsTitle,
} from "./signOutTabs.ts";
import {
  clearSupportTab,
  endSupportSessionRequest,
  sendSupportCloseNotice,
  isFreshOpen,
  isSupportPage,
  loadSupportTab,
  mountSupportBanner,
  redeemHandoff,
  saveSupportTab,
  showSupportEnded,
  SUPPORT_PATH,
  waitForHandoffCode,
  type SupportBanner,
  type SupportTabState,
} from "./supportEntry.ts";
import { startSupportNotice } from "./supportNotice.ts";
import { startBillingPauseNotice } from "./billingPauseNotice.ts";
import {
  applyTheme,
  clearStoredThemeId,
  DEFAULT_THEME_ID,
  THEME_OPTIONS,
} from "./theme.ts";
import { applyBrandTheme, registerUploadedFont } from "./brandThemeDom.ts";
import { uploadedFontFamily } from "../../src/domain/brandFontCatalog.ts";
import {
  parseMemberBrandReply,
  followsBrandRead,
  pickerValueFor,
  profileThemeChoices,
  savedThemeForPick,
  resolveThemeId,
  startingThemeId,
  type MemberBrand,
} from "./memberBrandModel.ts";
import { capitalizeServerMessage } from "./serverMessageDisplay.ts";
import {
  backInStockToast,
  candidateCategories,
  candidateEntrySubText,
  clearOfferProducts,
  clearOfferText,
  historyByEntryKey,
  historyLines,
  markOutCandidates,
  markedOutToast,
  outOfStockEntries,
  outRightNow,
  outRowSubText,
  recomputeDrink86,
  sinceByProduct,
  sinceFromMenuState,
  stock86CategoryLabel,
  stock86DrinkKey,
  stock86Entries,
  stock86IngredientKey,
  formatStock86Time,
  type Stock86Entry,
  type Stock86HistoryEvent,
  type Stock86Since,
} from "./stock86.ts";
import { floorQuantityState } from "./inventoryListSort.ts";
import { closeMyTips, initMyTips, openMyTips } from "./myTips.ts";
import { buildPriceHistoryView, businessDateLabel, changeCountLabel } from "./priceHistoryModel.ts";
import {
  closePriceHistorySheet,
  openPriceHistorySheet,
  type PriceHistoryLoad,
} from "./priceHistorySheet.ts";
import {
  createPaymentLinkPoller,
  type PaymentLinkCheckOutcome,
  type PaymentLinkPoller,
  type PaymentLinkPollStopReason,
} from "./paymentLinkPoller.ts";
import { isDiagnosticsRequested, isLocalDevelopmentHost } from "./localEnvironment.ts";
import {
  applyMenuFilters,
  baseOptions,
  itemCountLabel,
  kindFilterLabel,
  menuFiltersActive,
  NO_MENU_FILTERS,
  toggleKindFilter,
  type MenuFilters,
} from "./menuFilters.ts";
import { menuQueryWords, nameMatchesMenuQuery } from "./menuSearch.ts";
import { servingPriceLabel } from "./menuEntryModel.ts";
import { usDate } from "./dateText.ts";

// ---------------------------------------------------------------------
// ALPHA-10 item 1: alphabetical menu, A-Z jump strip
// ---------------------------------------------------------------------
//
// The bartender menu grid is no longer organized by alcohol type (owner
// direction, 2026-08-20: "some drinks are made with multiple different
// alcohol types, so spirit-category grouping misplaces them"). Every
// active drink is listed alphabetically by name in one flat list, grouped
// visually into letter sections purely so a jump has somewhere to land;
// the letter itself carries no selection or filtering meaning the way the
// old category pills did. ALPHABET_JUMP_LETTERS is a fixed 27-entry set
// (A-Z plus "#" for a name that does not start with a letter), rendered
// as a stable grid of touch targets regardless of which letters the
// current menu actually uses, per the work item's "letters with no
// drinks visibly inactive" instruction: the strip's shape never changes,
// only which buttons are enabled.
const ALPHABET_JUMP_LETTERS = [
  "#",
  ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)),
];

/** The jump-strip bucket a drink's name sorts into: its first character,
 * uppercased, when that character is A-Z, otherwise "#" (a number, symbol,
 * or non-Latin leading character). Also used as the section key drinks are
 * grouped under in the grid itself, so a jump always lands on a real
 * section heading. */
function alphaBucketFor(name: string): string {
  const first = name.trim().charAt(0).toUpperCase();
  return first >= "A" && first <= "Z" ? first : "#";
}

// ---------------------------------------------------------------------
// State
// ---------------------------------------------------------------------

interface PublicationQuoteBinding {publicationId:string;editVersion:number;contentRevision:number;targetRevision:number}

interface TicketLineState {
  publicationBinding?:PublicationQuoteBinding;
  orderLineId: string;
  /** Undefined for an inventory-sale line (lineKind other than
   * "cocktail"): those have no menu drink at all. See db/schema.ts's
   * orderLines.menuDrinkId comment; mirrors OrderLineDto.menu_drink_id
   * being nullable. */
  menuDrinkId?: string;
  /** Inventory track (track/inventory) Slice 3, Amendment 2026-08-22
   * (twelfth). Mirrors OrderLineDto.line_kind exactly. "cocktail" for
   * every pre-Slice-3 line and every line added through the unchanged
   * cocktail add-line flow; "pour"/"beer"/"packaged" for a direct
   * inventory sale (see addInventorySaleToTicket). Drives which
   * price-quote line shape recomputeQuote/summarizeTab build for this
   * line (src/api/validation.ts's PriceQuoteLineInput.saleKind), and
   * whether the ticket line shows a liquor-selection affordance at all. */
  lineKind: "cocktail" | InventorySaleKind;
  name: string;
  /** Selected liquor's display name, when this line required a selection. */
  detail?: string;
  liquorProductId?: string;
  ounces?: string;
  preparedState: "not_prepared" | "prepared";
  /** VER72-F1. Whether Prepared wrote an inventory posting for this line, the
   * stored fact (never a guess from the recipe): false for a drink with no
   * catalog product (DEC-218), true for a liquor drink. Undefined until read,
   * which keeps the original wording. */
  inventoryPosted?: boolean;
  /** AU14-F15 / DEC-276. The server-resolved "sold past an 86" record (who, who
   * approved, when, why), drawn as a grey strip under the drink. Undefined for a
   * line that was not sold past an 86. */
  stockOverride?: LineStockOverrideDto;
  /** M5-R10 (DEC-355). How many price changes and put-backs the line has in the
   * ledger, from the ticket read and refreshed after every change. Drives the
   * strip's badge and the line menu's Price history row; undefined is none. */
  priceChangeCount?: number;
  /** TKTPRICE1 (DEC-360). The line's STORED price, the amount Pay charges,
   * exactly as the server returned it: from the add-line response, the order
   * read (reconstructOrderLine), or the quote's answer for a line already on
   * the order, which the server answers from the stored price. Undefined until
   * the server has said, and then the row draws no figure. It is never
   * computed or estimated in the browser. */
  lineTotalCents?: number;
  /** ALPHA-10 item 2. True when this liquor selection was made through the
   * liquor sheet's "More spirits" expansion, outside the drink's own
   * allowed categories. Shown plainly on the ticket line
   * (renderTicketLines); never inferred, always the exact flag the sheet
   * (or, for a resumed line, orderLine.is_substitution) recorded. */
  isSubstitution?: boolean;
  /** ALPHA-07, resumed tabs only. False when a resumed line could not be
   * matched back to a live, priceable menu drink and liquor selection
   * (see reconstructOrderLine's module comment): either the drink itself
   * was renamed or removed since, or its liquor selection could not be
   * reconstructed from its saved order-line components. Either way there
   * is nothing safe to price. Excluded from the price-quote batch and
   * shown as "Price unavailable" (never a guessed dollar amount) plus a
   * persistent "needs a manager" note (renderTicketLines); the line
   * itself, and its Prepared toggle, still render normally, and it always
   * blocks Pay while never blocking Close on its own (renderTicketActions,
   * design-gate fix 2026-08-20). A line added through the normal Add to
   * Ticket flow is always priceable; undefined and true both mean
   * "priceable" wherever this is read. INV19 Slice 2 reuses this exact
   * mechanism for a live line whose price stopped resolving after a
   * recompute (see unpriceableReason immediately below): both origins set
   * priceable = false and render identically as far as Pay/Close gating
   * goes, distinguished only by which message is shown. */
  priceable?: boolean;
  /** INV19 Slice 2 (docs/build-briefs/INV19-pour-size-price-override.md,
   * "Slice 2 design"). Set by recomputeQuote when the batched price-quote
   * route returns this line as `priceable: false` (a pour-size override
   * that has resolved negative against its tier since this line was
   * added, per DEC-139). Rendered on the ticket row in place of a price,
   * distinct from the resumed-tab "needs a manager to fix the menu" text:
   * this is the server's own pricing-conflict message, since a repriced
   * override and a broken recipe match send whoever is asked to fix it to
   * two different screens. Undefined for a resumed-tab match miss (that
   * origin's own fixed text is used instead) and for any priceable line. */
  unpriceableReason?: string;
  /** ALPHA-12. Undefined on a single-guest-free ticket (today's behavior);
   * the guest this line was attributed to, once the ticket has named one
   * or more guests. See db/schema.ts's orderLines.guestId comment. */
  guestId?: string;
  /** VOC1-F7 Slice 2 (DEC-171). True when this cocktail line's stored
   * `ounces` differs from its recipe's own default pour, so the ticket
   * badge (renderTicketLines) shows a plain-language pour label. Set at add
   * time from the liquor sheet's own chip pick (state.sheet.
   * pourModifierApplied), at change-liquor time from the same sheet, and at
   * tab-resume time by comparing the resumed pour_ounces against a fresh
   * lookup of the recipe's own default (pourDiffersFromRecipeDefault);
   * never inferred from price alone. Undefined/false for a direct
   * inventory-sale line and for a recipe-default cocktail pour alike. */
  pourModifierApplied?: boolean;
  /** VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
   * 2.5). Every catalog modifier this line was rung with, DISPLAY ONLY:
   * the ticket line's own badges (buildTicketLineRow) read label/
   * priceAdjustmentCents from here, but the line's own price always comes
   * from the server (lineTotalCents), never recomputed from this list.
   * Set at add time from the sheet's own selection (addDrinkToTicket, and
   * as of VOC1-F4-F2 addInventorySaleToTicket too) and at tab-resume time
   * from the stored order_line_modifiers rows (reconstructOrderLine),
   * never re-derived from the drink's CURRENT attachments or catalog price
   * -- the same "read the stored fact" discipline
   * menuDrinkId/liquorProductId/pourOunces already establish. Undefined
   * for a cocktail or a direct inventory-sale line alike that carries no
   * modifier. */
  appliedModifiers?: { label: string; priceAdjustmentCents: number }[];
  /** VOC1-F4. The bare `drink_modifier` VALUES this line was rung with
   * (as opposed to appliedModifiers' own label/price display pair),
   * resent on every later recompute (buildPriceQuoteLine). Required for
   * the identical reason pourModifierApplied's own comment states:
   * resolveAndPriceLine (and, as of VOC1-F4-F2,
   * resolveAndPriceInventorySaleLine) has no "already on the order"
   * bypass for a modifier selection the way override86 gets via
   * resolveExistingLineOverride86 -- an omitted or empty
   * modifierSelections resolves zero modifiers regardless of what the
   * line was actually added with, so a recompute that dropped this would
   * silently reprice a modified line back down to its base price. Set at
   * add time from the sheet's own selection and at tab-resume time from
   * the stored order_line_modifiers rows, the same two sites
   * appliedModifiers itself is set from. */
  modifierSelections?: string[];
}

interface MenuDrinkDetail {
  menuDrink: MenuDrinkDto;
  components: MenuDrinkComponentDto[];
  /** ALPHA-10 item 2. The drink's full allowed-category set; empty when
   * the drink does not require a liquor selection. */
  liquorSlots: string[];
  /** VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
   * 2.2). This drink's own attached modifiers, empty when it has none. */
  modifiers: MenuDrinkModifierDetailDto[];
  /** RECUX2-F1. The franchisor's glass, garnish and steps for a drink made from a franchisor recipe; read only. */
  recipeMethod?: RecipeMethodDto | null;
}

/** ALPHA-12/VOC1-F5/VOC1-F7/VOC1-F4: the one shared "what was picked on
 * the liquor (or modifiers-only) sheet" shape, threaded from
 * handleLiquorAddClick through addDrinkToTicketWithGuestPick into
 * addDrinkToTicket. liquorProductId/ounces/name are optional (rather than
 * the pre-VOC1-F4 shape's required trio): a drink with no liquor slot at
 * all that still carries an attached modifier (openModifiersOnlySheet)
 * has a real modifier selection to carry but no liquor selection to go
 * with it, and buildLineComponents below already branches on their
 * absence the identical way it already does for the plain
 * addDrinkToTicketWithGuestPick(detail) call a liquor-free, modifier-free
 * drink has always used. */
/** AU14-F15 / DEC-276. A selection that sells an 86'd item carries the reason picked
 * on the 86 sheet (id for the request, label for the manager PIN pad's context
 * line); an ordinary selection is `false`. Threaded through every step between the
 * sheet and the request as one value, so the reason can never be dropped on the
 * way: the compiler finds every site. */
type Override86 = false | { reasonId: string; reasonLabel: string };

/** The body fields a PERSISTING 86 request carries (add-line, inventory-sale,
 * change-liquor); a read-only quote carries only the plain signal and needs no
 * reason. */
function override86Fields(override86: Override86): {
  override86?: true;
  override86ReasonId?: string;
} {
  return override86 === false ? {} : { override86: true, override86ReasonId: override86.reasonId };
}

/** AU14-F15 / DEC-276. Runs one PERSISTING 86 request. An ordinary request is a
 * plain call, untouched (the approval flow's re-entrancy lock must never cancel a
 * second ordinary add). An 86 sale goes through the manager approval prompt, which
 * only appears when the server's first, no-approval attempt answers
 * approval_required; the reason was picked BEFORE the PIN, so the PIN pad's context
 * line carries it, and the retry carries the same reason under the same key.
 * Resolves undefined when the approval was canceled: nothing changed. */
async function runPersistingSale<T extends Partial<ApiErrorBody>>(
  override86: Override86,
  actionLabel: string,
  attempt: (approval?: { approverUserId: string; approverPin: string }) => Promise<ApiResult<T>>,
  isCurrent?: () => boolean,
): Promise<ApiResult<T> | undefined> {
  if (isCurrent?.() === false) return undefined;
  if (override86 === false) {
    return attempt();
  }
  let attempts = 0;
  const gated = await requestApproval(
    actionLabel,
    (approval) => {
      attempts += 1;
      return attempt(approval);
    },
    () => { if (isCurrent?.() !== false) showRingScreen(); },
    { pinContext: `${actionLabel} \u00b7 ${override86.reasonLabel}`, isCurrent },
  );
  if (gated.canceled || isCurrent?.() === false) {
    return undefined;
  }
  // A second attempt means the manager's picker and PIN pad were shown: the
  // flow leaves the caller to take the screen back (the void flow's own shape).
  // A first attempt that simply succeeded never left the ring screen.
  if (attempts > 1) {
    showRingScreen();
  }
  return gated.result;
}

interface DrinkSelection {
  publicationBinding?:PublicationQuoteBinding;
  liquorProductId?: string;
  ounces?: string;
  name?: string;
  isSubstitution?: boolean;
  /** VOC1-F5 Slice 3 (DEC-161). See addDrinkToTicket's own comment. */
  override86?: Override86;
  /** VOC1-F7 Slice 2. See addDrinkToTicket's own comment. */
  pourModifierApplied?: boolean;
  /** VOC1-F4 (Section 2.4). See addDrinkToTicket's own comment. */
  modifierSelections?: string[];
}

// FLOORADD1: each tap keeps its own ticket context. Do not supersede
// independent ordinary adds with a global latest-tap counter.
interface FloorSaleContext {
  generation: number;
  orderId: string;
  visit: number;
}

let floorTicketVisit = 0;

function captureFloorSaleContext(): FloorSaleContext | undefined {
  if (state.session === undefined || state.orderId === undefined) return undefined;
  return { generation: sessionGeneration, orderId: state.orderId, visit: floorTicketVisit };
}

function floorSaleContextIsCurrent(context: FloorSaleContext | undefined): boolean {
  return context !== undefined && sessionIsCurrent(context.generation) &&
    state.session !== undefined && state.orderId === context.orderId && floorTicketVisit === context.visit;
}

interface SheetState {
  saleContext?: FloorSaleContext;
  /** VOC1-F4-F2 (Section 2.5): undefined exactly when inventorySaleProduct
   * below is set -- a direct inventory-sale item (a pour, a beer, a wine,
   * a can or a bottle) is not a menu drink at all, so there is no
   * MenuDrinkDto to carry. Every function that reads this field is a
   * cocktail-sheet-only or modifiers-only-mode function (updateLiquor
   * PricePreview, updateModifiersOnlyPricePreview, loadLiquorRowPrices,
   * liquorSheetSubtitleText), never called while inventorySaleProduct is
   * set, and each guards defensively for the compiler's own sake. */
  menuDrink?: MenuDrinkDto;
  components: MenuDrinkComponentDto[];
  /** ALPHA-10 item 2. See MenuDrinkDetail.liquorSlots. */
  allowedCategories: string[];
  liquorProducts: LiquorProductDto[];
  selectedLiquorProductId?: string;
  ounces?: string;
  /** ALPHA-10 item 2 (2026-08-20 amendment: "substitutions allowed even
   * off the defined recipe"). False by default: the sheet opens showing
   * only the drink's own allowedCategories, browse and search scoped to
   * that set. True once the bartender taps the explicit "More spirits"
   * expansion (handleMoreSpiritsClick), which re-browses/searches across
   * EVERY active liquor regardless of category. The deliberate act of
   * opening that expansion is what marks a selection made afterward as a
   * substitution (handleLiquorRowClick), not a category comparison: even
   * a liquor that happens to still be in allowedCategories counts as a
   * substitution once picked from the expanded view, matching the product
   * owner's "make substitution a deliberate act" instruction. Never reset
   * back to false while the sheet stays open (there is no "collapse" UI;
   * see openLiquorSheet's own reset on the NEXT sheet open). */
  browseAllActive: boolean;
  /** LIQSEARCH1. While the search box has text and More spirits has not been
   * tapped, the list also holds matches outside the drink's allowed
   * categories; these are their ids. Picking one is a substitution exactly as
   * a pick after More spirits is (see selectionIsSubstitution), and the
   * server prices it with isSubstitution true in the same batched quote. */
  extraLiquorIds?: Set<string>;
  /** True when liquorMatch.ts's findLiquorSlotComponent found no
   * is_liquor_slot component for this drink at all, or found one whose
   * unit could not be parsed as ounces. Blocks Add to Ticket outright
   * with a plain-language "needs a recipe update" message; there is no
   * estimated-price fallback anywhere in this codebase's money path. See
   * liquorMatch.ts's module header. */
  matchMiss: boolean;
  /** ALPHA-08. Set when this sheet was opened from the line-edit sheet's
   * "Change liquor" action rather than the normal Add to Ticket flow.
   * When present, confirming a selection calls changeLineLiquor against
   * this existing line instead of addLine for a new one; see
   * handleLiquorAddClick's own branch. */
  changeLineOrderId?: string;
  /** VOC1-F5 Slice 3 (DEC-161). True exactly when selectedLiquorProductId
   * is a flagged-86 product AND this selection went through the override
   * confirm sheet (selectLiquorRow below). Read by updateLiquorPricePreview
   * (so the live preview quote carries override86 too) and by
   * handleLiquorAddClick (so the eventual add-line, or change-liquor call
   * in "Change liquor" mode, carries it). Slice 3's client half originally
   * shipped believing changeLineLiquor did not accept override86 at all, so
   * a flagged row stayed disabled in "Change liquor" mode (changeLineOrderId
   * defined) regardless of permission; the route now accepts it (server
   * fix, same DEC-161 ruling), so this flag is set and read there exactly
   * as it is for a fresh add. Reset on every openLiquorSheet call and every
   * new row selection, never carried over from a prior selection. */
  overrideSelected?: Override86;
  /** CORE7. Set when the DRINK itself was 86'd (its own flag, or an 86'd ingredient) and the bartender went through
   * the same Sell anyway sheet to open this one. Carried by every fresh quote and the add, never by a change-liquor
   * (the drink is already on the ticket). */
  drinkOverride86?: Override86;
  /** VOC1-F2. Populated once loadLiquorRowPrices' own batched price-quote
   * request (one per liquor-list load, Section 2.2) resolves; undefined
   * while that request is still in flight or has not been sent yet (an
   * empty list, or every listed product flagged 86). Keyed by
   * liquorProductId, one entry per row whose price resolved. Reset to
   * undefined by loadLiquorProducts on every fresh list load so a stale
   * figure from the PREVIOUS list never lingers into the next render. */
  liquorRowFinalCents?: Map<string, number>;
  /** VOC1-F2. liquorProductIds from the current, non-flagged listing
   * whose price could not be resolved this load (an unpriceable line, or
   * the whole batch request failing) -- rendered as "Price unavailable"
   * rather than a blank, stale, or zero figure. A flagged-86 row is never
   * added here: it is excluded from the batch entirely (Section 2.4) and
   * shows no price text at all, the badge already being the explanation. */
  liquorRowUnavailableIds?: Set<string>;
  /** ZEROPRICE1: rows the server refused as having no price, shown as "No price set". */
  liquorRowNoPriceIds?: Set<string>;
  /** LIQPICK1. True once a holder of the sell-past-86 override taps "Show N unavailable": the 86'd and out-of-stock
   * rows then render as they always did. Absent or false hides them for everyone. Lives for the open sheet. */
  showUnavailableLiquors?: boolean;
  /** VOC1-F7 Slice 2 (docs/build-briefs/VOC1-F7-cocktail-pour-modifier.md,
   * Section 2.5). The recipe's own default ounces, captured once at
   * openLiquorSheet from the same recipeMatch that seeds `ounces` below and
   * never mutated afterward. Undefined exactly when matchMiss is true (no
   * recipe default exists to offer or reset to). The pour-size chip row's
   * own baseline, and what a chip's own value is compared against to decide
   * pourModifierApplied. */
  recipeDefaultOunces?: string;
  /** VOC1-F7 Slice 2. True exactly when `ounces` above was set by an
   * explicit chip tap whose value differs from recipeDefaultOunces; false
   * for the recipe default itself, mirroring overrideSelected's own
   * "actually happened, not inferred" shape. Reset to false on every
   * openLiquorSheet call, every fresh liquor-list load, and every fresh row
   * selection (DEC-172: a modifier chosen for one bottle is never silently
   * carried onto another whose own configured pour sizes may differ). Read
   * by updateLiquorPricePreview/loadLiquorRowPrices (so the live quote
   * carries it) and handleLiquorAddClick (so the eventual add-line/
   * change-liquor call does too). */
  pourModifierApplied: boolean;
  /** VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
   * 2.5). This drink's own attached modifiers, set once at open from
   * MenuDrinkDetail.modifiers. The one later mutation is VOC1-F4-F3's
   * dropRetiredModifierAndRerender, which flips an entry's active flag to
   * false when the server refuses it as retired; nothing is ever added.
   * Empty for a drink with no attachments; renderModifierChips hides the
   * row entirely in that case. */
  menuDrinkModifiers: MenuDrinkModifierDetailDto[];
  /** VOC1-F4. The chip picks made THIS sheet session, reset to empty on
   * every openLiquorSheet/openModifiersOnlySheet call, mirroring
   * SheetState's own "actually happened, not inferred" convention for
   * pourModifierApplied/overrideSelected above. */
  selectedModifierValues: Set<string>;
  /** VOC1-F4 (Section 2.5). True only for a drink with
   * requires_liquor_selection false that still carries at least one
   * attached modifier: this sheet's liquor-picking controls (search, list,
   * More spirits, the pour-size row) are hidden, and only the title, the
   * modifier chip row and the confirm control show. Set by
   * openModifiersOnlySheet, always false from openLiquorSheet. */
  modifiersOnlyMode: boolean;
  /** VOC1-F4-F4: a per-sheet request counter, incremented immediately
   * before every price-quote request updateLiquorPricePreview or
   * updateModifiersOnlyPricePreview sends (a pour-size or modifier chip
   * tap, or an ordinary liquor-row selection). Each caller captures the
   * post-increment value before its own await and discards its response
   * once this counter has moved on, so a slower EARLIER response can
   * never overwrite the preview (or Add to Ticket's own enablement) with
   * a stale ounces/modifier selection once a LATER request has already
   * started, even though both requests price the same product. Reset to
   * 0 on every openLiquorSheet/openModifiersOnlySheet call, the same
   * "fresh sheet, fresh counter" shape sessionGeneration already
   * establishes at the whole-session scope. */
  quoteRequestSeq: number;
  /** FLOORQUOTE1: only the current selection's successful quote permits confirmation. */
  quoteReady?: boolean;
  publicationBinding?:PublicationQuoteBinding;
  /** FLOORQUOTE2: recorded add-ons retained by Change Liquor, sent only to server quote math. */
  carriedModifiers?: PriceQuoteModifierInput[];
  liquorListRequestSeq?: number;
  /** VOC1-F3: the ids the Recent strip may show, newest first, as the server returned them. Undefined until the
   * read lands (and for a sheet that never asks), which hides the strip. Never a selection. */
  recentLiquorIds?: string[];
  /** VOC1-F4-F2 (Section 2.5). Set only by openInventorySaleModifiersSheet:
   * this sheet is showing a direct inventory-sale item's own no-liquor-
   * picking modifiers sheet (whole-unit, B5 shelf pour, or a pour-size
   * already chosen), the sibling of modifiersOnlyMode above for an item
   * that is not a menu drink at all. Undefined from every cocktail-sheet
   * open (openLiquorSheet/openModifiersOnlySheet). menuDrinkModifiers and
   * selectedModifierValues above are reused unchanged for this mode too
   * (both kind-agnostic field names despite the menuDrink prefix, VOC1-F4
   * Section 2.5's own note); only the eventual add call and the live quote
   * differ (addInventorySaleToTicketWithGuestPick / quoteInventorySale
   * instead of addDrinkToTicketWithGuestPick / priceQuote). */
  inventorySaleProduct?: {
    product: LiquorProductDto;
    saleKind: InventorySaleKind;
    ounces?: string;
    override86: Override86;
  };
  /** VOC1-F4-F2 (Section 2.5). A per-sheet request counter for
   * updateInventorySaleModifiersPreview only, the identical
   * discard-a-superseded-response shape commit 6b28c73 gave the cocktail
   * sheet's own quoteRequestSeq (not yet in this worktree's base; built
   * independently here so the eventual merge only has to unify the two,
   * never invent this one). Optional and untouched by every cocktail/
   * modifiersOnlyMode sheet open, so this addition touches neither of
   * those two functions. */
  inventorySaleQuoteRequestSeq?: number;
}

/** A ticket closed on this location's current business day. REFUNDFIND1: the list comes from the server
 * (GET /tabs/recently-closed), so it holds tickets closed by anyone and survives sign-out and reload. The ticket this
 * login just closed is added at once, so the card is there before the server read returns. */
interface RecentlyClosedTab {
  orderId: string;
  /** UX20: the same tabDisplayLabel rule the open-tabs card itself uses,
   * computed once at close time from the ticket screen's own order name
   * and guests, so a closed tab's card never disagrees with what the open
   * card showed a moment earlier. */
  label: string;
  closedAt: number;
  /** UX102: what the ticket came to (drinks and tax paid, tip not counted, as the ticket's Paid row reads), when the
   * payments summary was loaded at close time. LOUNGERMAJ1 (lounger-08): the tip is added in, because the card says what
   * was charged. */
  totalCents?: number;
  /** The tip inside totalCents, when there was one. */
  tipCents?: number;
  /** CLOSEDVIEW1: who closed it, as the signed-in session named them when the ticket closed. */
  closedBy?: string;
}

/** ALPHA-07: one reconstructed order line, produced by
 * reconstructOrderLine from a resumed tab's saved order_line_components.
 * priceable mirrors TicketLineState.priceable directly. */
interface ReconstructedLine {
  ticketLine: TicketLineState;
  priceable: boolean;
}

interface TabSummary {
  drinkCount: number;
  /** What the card's headline shows: what a Pay right now would charge, tax included (LOCFLOOR1, sweep B1). */
  totalCents: number | undefined;
  /** The whole ticket with tax before discounts and payments; shown as a quiet second line only when it differs. */
  ticketTotalCents?: number;
  unpricedCount: number;
  /** Sixteenth amendment item 2: true once a payment has brought this
   * tab's balance to zero (every line covered, per getPaymentPreview's
   * own uncoveredLines, the same source of truth Pay itself reads), while
   * the tab is still open. Always false for a genuinely empty ticket
   * (drinkCount 0): nothing was ever brought to zero there, there was
   * simply nothing to pay yet. */
  fullyPaid: boolean;
  /** RCPT1-S3: the card is authorized and a tip written on the receipt is still to be entered. */
  awaitingTip?: boolean;
}

// ---------------------------------------------------------------------
// ALPHA-12 multi-guest tickets and split payments (docs/superpowers/
// plans/2026-08-19-alpha-work-breakdown.md, Amendment 2026-08-21 fifth;
// docs/product-requirements.md P0.19).
// ---------------------------------------------------------------------

/** Which of the three pay modes the currently-open pay-confirm sheet
 * applies to. "whole" is the ordinary, unchanged ALPHA-08 behavior (every
 * uncovered line); "guest" is "pay my drinks" (one guest's own uncovered
 * lines); "share" is one share of an in-progress equal split. See
 * openPayConfirmSheet and handlePayConfirmClick, the two functions that
 * branch on this. */
type PayScope =
  | { type: "whole" }
  | { type: "guest"; guestId: string; guestLabel: string }
  // SPLIT1: the No guest row of By guest, paid on its own.
  | { type: "unassigned" }
  // SPLIT1: By amount. `selection` is what the server resolves; only `cents` carries a figure typed on the pad.
  | { type: "amount"; selection: AmountSelectionDto }
  | { type: "share"; shareId: string; shareIndex: number; shareCount: number };

/** Independent-review Finding A, 2026-08-26. The stable identity of "one
 * intent to collect this money", used as the key into
 * state.payIdempotencyKeysByTarget to decide whether opening the pay
 * confirm sheet is CONTINUING an attempt already in flight (a retry:
 * reuse that target's own pending key) or STARTING a new one (mint a
 * fresh key). "share" is keyed on shareId alone -- a share id already
 * uniquely identifies one collectShare call, is never reused across
 * plans or orders, and is the same value the "same pending share tapped
 * again" retry journey (the split-progress sheet) always passes back in.
 * "whole" and "guest" both fold in orderId, since a bare guestId is only
 * unique within one order and "whole" would otherwise collide across
 * every ticket. See state.payIdempotencyKeysByTarget's own doc comment
 * for the full reasoning this exists to serve. */
function computePayIntentTarget(orderId: string, scope: PayScope): string {
  switch (scope.type) {
    case "whole":
      return `whole:${orderId}`;
    case "guest":
      return `guest:${orderId}:${scope.guestId}`;
    case "unassigned":
      return `unassigned:${orderId}`;
    case "amount": {
      // One key per distinct intent: the same amount asked again is a retry (same key), another amount is new.
      const detail =
        scope.selection.type === "cents"
          ? String(scope.selection.cents)
          : scope.selection.type === "guest"
            ? scope.selection.guestId
            : "";
      return `amount:${orderId}:${scope.selection.type}:${detail}`;
    }
    case "share":
      return `share:${scope.shareId}`;
  }
}

/** T6: the pay-key map's operational signal, wired to the only
 * channels this UI has: console.warn for a shift log a supporting
 * laptop can read, and the debug panel's own counter (rendered by
 * renderPayKeyDebug). Never logs a key VALUE; the key is the thing a
 * duplicate charge would replay. */
function reportPayKeySignal(event: PayKeySignal): void {
  if (event.kind === "approaching_cap") {
    console.warn(
      `pay idempotency keys: ${event.size} unresolved payment intents are pending, ` +
        "approaching the cap; this many simultaneously-ambiguous payments is not a normal shift",
    );
  } else {
    console.warn(
      `pay idempotency keys: evicted the oldest unresolved intent (${event.target}) at the cap; ` +
        "a retry of that exact payment will now mint a fresh key, so check it against the ledger",
    );
    // PAY7 Section 2.8: eviction from memory and eviction from persisted
    // storage happen at the one place the map already reports it, rather
    // than needing a second signal.
    const tenantId = state.session?.tenantId;
    if (tenantId !== undefined) {
      clearPersistedPayKey(tenantId, event.target);
    }
  }
}

/** Returns the pending idempotency key for `target`, minting and
 * recording a fresh one only if none is already pending -- the map
 * entry point both openPayConfirmSheet (mint-or-reuse at sheet-open) and
 * handlePayConfirmClick (read the same value at Confirm time, via the
 * identical target computation) call, so the two moments can never
 * disagree about which key a given intent uses. See
 * state.payIdempotencyKeysByTarget's own doc comment for the full
 * lifetime rule; the cap, eviction, and signal live in
 * ui/src/payIdempotencyKeys.ts (T6), unit-tested there.
 *
 * PAY7 Section 2.8: persists on every call, whether the key was just
 * minted or already pending (cheap and idempotent), so the durable copy
 * exists the MOMENT a key is minted, before the network request that
 * might fail ambiguously even starts. */
function getOrMintPayIdempotencyKey(target: string): string {
  const key = state.payIdempotencyKeysByTarget.getOrMint(target);
  const tenantId = state.session?.tenantId;
  if (tenantId !== undefined) {
    savePersistedPayKey(tenantId, target, key);
  }
  renderPayKeyDebug();
  return key;
}

/** Removes `target`'s own pending key once its payment attempt has
 * definitively resolved (captured, or a definitive non-5xx rejection),
 * so the next Pay tap for that same target starts a genuinely new
 * attempt with its own fresh key. Never touches any OTHER target's
 * entry -- the entire point of keying this map per-target instead of
 * the single shared slot the gate defeated.
 *
 * PAY7 Section 2.8: mirrors the clear into persisted storage in the same
 * call, so the two can never drift apart. */
function clearPayIdempotencyKey(target: string): void {
  state.payIdempotencyKeysByTarget.clear(target);
  const tenantId = state.session?.tenantId;
  if (tenantId !== undefined) {
    clearPersistedPayKey(tenantId, target);
  }
  renderPayKeyDebug();
}

/** PAY7 Section 2.9: clears every pay idempotency key still pending for
 * `orderId`, across every PayScope shape this app ever mints one for
 * (computePayIntentTarget's own three cases). Called at the two points an
 * earlier attempt under one of these keys is proven definitively dead: the
 * moment a ticket actually closes (finishClosedTicket, reached by every
 * closing path -- a settling Pay/collectShare, a Walkout, a comp, or an
 * explicit Close) and defensively again when a closed ticket is reopened
 * (handleReopenClick), covering the residual case a ticket closed before
 * this fix shipped, or some other closing path this brief's own reasoning
 * did not enumerate. See that brief's Section 2.9 for the full domain
 * guarantee this relies on (assertNoPendingPaymentBlocksOrder, checked
 * inside closeOrder's own locked transaction before every close). */
function clearPayIdempotencyKeysForOrder(orderId: string): void {
  // PAY19 (docs/build-briefs/PAY19-reopen-sweep-race.md): the whole- and
  // guest-scoped targets are now read straight off the map itself
  // (targetsForOrder), not reconstructed from state.guests. Reconstructing
  // them required state.guests to already reflect THIS order, which is
  // only true once enterTicket has run for it -- by which point the ring
  // screen (and Pay) has already been interactive for a window, and a key
  // minted in that window was exactly what this function was wiping.
  // Reading the map's own keys needs no per-order state at all, so this
  // is safe to call before enterTicket has ever run, which
  // handleReopenClick now does.
  for (const target of state.payIdempotencyKeysByTarget.targetsForOrder(orderId)) {
    clearPayIdempotencyKey(target);
  }
  if (state.activeSplitPlan !== undefined) {
    for (const share of state.activeSplitPlan.shares) {
      clearPayIdempotencyKey(
        computePayIntentTarget(orderId, {
          type: "share",
          shareId: share.id,
          shareIndex: share.share_index,
          shareCount: state.activeSplitPlan.plan.share_count,
        }),
      );
    }
  }
}

/** The pending "add this drink" call, held while a guest-attribution
 * choice is being made (openChoiceSheet's own async gap between opening
 * and the bartender's tap). Undefined whenever no add is waiting on a
 * guest pick. */
interface PendingAdd {
  detail: MenuDrinkDetail;
  selection?: { liquorProductId: string; ounces: string; name: string; isSubstitution?: boolean };
}

/** RCPT1-S3: a ticket whose card is authorized and whose receipt tip is still to be entered, as the server states it. */
type AwaitingTipState = Extract<ReceiptAuthorizationStateDto, { awaiting: true }>;

const state: {
  session?: Session;
  menuDrinks: MenuDrinkListItemDto[];
  /** Inventory track Slice 3, Amendment 2026-08-22 (twelfth): the full
   * alphabetical menu, cocktails and direct-sale items (pour/draft beer/
   * packaged) together. renderMenuGrid's own data source; state.menuDrinks
   * above stays a cocktail-only DERIVED view (filtered from this once per
   * loadMenu), kept only because loadCheapestLiquorPrices/tilePriceLabel
   * already read it and porting those to the union type is out of scope
   * for this slice's own UX decisions. */
  menuItems: MenuItemDto[];
  menuDetailCache: Map<string, MenuDrinkDetail>;
  cheapestPerOunceCentsByCategory: Map<string, number>;
  /** Inventory track Slice 3, Amendment 2026-08-22 (thirteenth). This
   * location's configured pricing model, loaded once at ticket entry
   * (enterTicket) and used only to LABEL a pour/draft-beer tile's price
   * (tilePriceLabelForPour); never used to compute a charge itself, which
   * always comes from the server (quoteInventorySale / the price-quote
   * saleKind branch). "per_ounce" until the first settings read resolves
   * it, matching tenantSettings.ts's own bootstrap default. */
  pricingModel: PricingModel;
  orderId?: string;
  orderStatus?: string;
  orderName?: string;
  /** AU14-F4 / DEC-276: the ticket's most recent reopen (who, approver, when,
   * why) for the grey record strip; null when never reopened. Set by
   * enterTicket, cleared with the ticket. */
  lastReopen?: LastReopenDto | null;
  /** ID1 (PRD P0.10): the ID Verified row's state for the open ticket (the location's switch, whether the ticket has
   * alcohol, the newest check), read from the server and refreshed when the ticket's drinks change. Undefined until
   * read, which draws no row. Cleared with the ticket. */
  idVerification?: IdVerificationViewDto;
  /** ALPHA-08 delta payments (owner ruling 2026-08-21): true once at
   * least one simulated payment has succeeded for the active ticket this
   * session. Historical/display use only now (the post-pay status
   * message, and this session's own memory that a first payment
   * happened); Pay's own enabled/disabled state and each line's edit
   * control are driven by state.paymentPreview's live uncovered set
   * instead (this file's renderTicketActions/renderTicketLines), since a
   * paid tab can gain new, genuinely payable lines afterward. */
  orderPaid: boolean;
  lines: TicketLineState[];
  quote?: PriceQuoteResponse;
  /** Discounts slice A: what is currently on this ticket and what it does
   * to the money, straight from the server. Never recomputed in the
   * browser: the clamp is money, and money is the server's. Undefined
   * until the first successful read, and left in place on a failed one so
   * a transient error cannot make a discounted ticket look undiscounted. */
  orderDiscounts?: OrderDiscountsDto;
  sheet?: SheetState;
  openTabs: OpenTabDto[];
  /** TAB1 (DEC-270): the Open Tabs find row. Kept in state, not the DOM, so
   * the filter and the typed text survive a data refresh and a return from
   * a ticket; both reset at sign-out (handleLogOut). */
  tabFilterMode: TabFilterMode;
  tabSearchText: string;
  /** TAB3: the chip row. Same lifetime as the find row; reset at sign-out. */
  tabStatusFilter: "unpaid" | "awaiting" | "paid" | undefined;
  tabOver1Hr: boolean;
  recentlyClosedTabs: RecentlyClosedTab[];
  /** CLOSEDVIEW1: set while the ring screen shows a closed ticket read only (opened from Recently closed). Every
   * ticket control is withheld while it is set; nothing opened this way writes to the server. */
  closedView?: { entry: RecentlyClosedTab };
  /** TM1 (docs/backlog.md; DEC-230, owner ruling "merge only"), widened by TAB2
   * (DEC-340). true while Open Tabs is in merge-selection mode (the "Merge tabs"
   * toggle). The first tab tapped is mergeTargetOrderId (stays open, receives
   * everything); every later tap adds a tab to mergeSourceOrderIds (each will
   * close), in tap order. Tapping a marked card again removes it. */
  tabMergeModeActive: boolean;
  mergeSourceOrderIds: string[];
  mergeTargetOrderId?: string;
  /** Recipe match misses this shift (see SheetState.matchMiss). Debug/QA
   * telemetry only, never shown to a customer; rendered into the session
   * debug panel so a floor lead can see a misconfigured recipe surfaced
   * without digging through logs. */
  matchMissCount: number;
  lastMatchMissDrinkName?: string;
  /** QA-gate fix (double-charge race, 2026-08-20) + independent-review
   * Finding A correction (2026-08-26) + gate re-review correction
   * (2026-08-26, this pass): the pending idempotency key for EVERY
   * in-flight or ambiguously-failed pay intent this session, keyed by
   * computePayIntentTarget's own return value (whole:orderId,
   * guest:orderId:guestId, share:shareId). One key, minted once per
   * INTENT to collect this money (not once per opening of the sheet,
   * and not in one shared slot).
   *
   * Two prior designs, both defeated by the real UI journey. The
   * original was a single scalar (no target at all): openPayConfirmSheet
   * unconditionally re-minted a fresh UUID on every open, so a preserved
   * key from an ambiguous failure was thrown away the moment the sheet
   * reopened for the retry itself -- independent-review Finding A. The
   * Finding A fix added a SINGLE target alongside the single key
   * (reuse only when the target matched the slot's own), which fixed the
   * same-target reopen but not the case a live gate probe found next:
   * guest Alpha's pay fails ambiguously (key preserved for Alpha), the
   * bartender pays guest Beta next -- an entirely ordinary, undiscouraged
   * action -- which overwrites the single slot with Beta's key/target,
   * and retrying Alpha then reads Beta's target, sees a mismatch, and
   * mints a brand-new key: exactly the double-charge exposure this
   * mechanism exists to prevent. The same loss reaches across different
   * ORDERS (switching tables) and does not even require the intervening
   * payment to complete, since openPayConfirmSheet overwrote the slot on
   * OPEN, before any request was sent. A map, one entry per target, has
   * no shared slot for an unrelated intent to overwrite.
   *
   * Lifetime rule, chosen deliberately: an entry is written the moment a
   * key is minted for a target (openPayConfirmSheet, via
   * getOrMintPayIdempotencyKey) and deleted the moment THAT target's own
   * payment attempt definitively resolves -- captured, or a definitive
   * non-5xx rejection the server answered before any adapter call
   * (shouldClearPayIdempotencyKeyOnFailure's own rule, unchanged; see
   * clearPayIdempotencyKey's call sites in handlePayConfirmClick). An
   * ambiguous failure (networkError, or a bare 5xx) leaves the entry in
   * place on purpose -- that is the whole point: an unresolved "might
   * have charged" attempt for target X must survive any number of OTHER
   * targets being opened, paid, or cancelled in between, and the
   * eventual retry of X must still replay the SAME key the payment
   * provider's own idempotency cache has already seen.
   *
   * Never make a stale key sticky forever, and never let it leak onto a
   * different target: payment_attempts' own uniqueness constraint is
   * (tenant_id, idempotency_key) ALONE, not scoped by order, guest, or
   * share, so reusing a key across a genuinely different intent would
   * return the WRONG payment's result instead of charging the new one --
   * this is exactly why per-target keying (not "one key, never cleared")
   * matters, and exactly why every entry is still deleted on its own
   * definitive resolution rather than kept around indefinitely.
   *
   * Growth is bounded two ways, both defensive rather than load-bearing
   * in the ordinary case: every entry is removed on its own resolution,
   * so a healthy shift holds at most the handful of genuinely in-flight
   * ambiguous failures at once -- realistically zero to one. As a
   * backstop against a pathological run (a payment-provider outage
   * leaving many targets simultaneously ambiguous across one long kiosk
   * shift), PAY_IDEMPOTENCY_KEY_MAP_MAX_ENTRIES caps the map and evicts
   * the OLDEST unresolved entry once exceeded. Deliberately NOT
   * time-based: an idempotency key's whole job is to protect against a
   * double charge for as long as its payment might still be genuinely
   * ambiguous, and a server-side Pending payment (Review 24) can
   * legitimately stay unresolved for a long time pending manual
   * reconciliation -- an age-based eviction would silently reintroduce
   * the double-charge risk for exactly the slow-to-resolve case this
   * whole mechanism exists to protect. Reaching the size cap at all is
   * itself an operational signal (this many simultaneously-unresolved
   * ambiguous payments is not a normal shift), not a case this code
   * tries to make safe on its own. Deliberately NOT cleared at logout
   * either, for the identical reason: a different employee (or the same
   * one, signing back in) completing an interrupted retry must still see
   * the preserved key, and clearing it at logout would silently reopen
   * this exact bug for that path.
   *
   * T6 (2026-09-01): the map itself now lives in
   * ui/src/payIdempotencyKeys.ts so its cap, eviction, and signal are
   * unit-tested, and the "reaching the cap is itself an operational
   * signal" sentence above is now literal: crossing the warning
   * threshold and every eviction report through reportPayKeySignal
   * (console.warn plus the debug panel's own counter). */
  payIdempotencyKeysByTarget: PayIdempotencyKeyMap;
  /** PAY13 Section 2c/F2 (docs/build-briefs/PAY13-adopt-orphaned-hold.md):
   * a page-lifetime in-memory fallback for the hold-open idempotency key,
   * the same shape payIdempotencyKeysByTarget already gives Pay. Without
   * this, performOpenHold called loadPersistedHoldKey fresh from
   * sessionStorage on EVERY tap with no in-memory cache at all, so a
   * device whose sessionStorage throws (a disabled API, a security
   * error, a quota failure) minted a BRAND NEW key on every single
   * retry, for the WHOLE page load, not only across a reload --
   * reproducing this brief's own Headline defect. Consulted FIRST in
   * getOrMintHoldIdempotencyKey; storage is still consulted on a cache
   * miss (survives a reload when storage genuinely works) and still
   * written to on every call (best-effort), but is no longer the ONLY
   * place this key is remembered. One entry per orderId, since a hold
   * has no guest/share split to key on. */
  holdIdempotencyKeysByOrderId: Map<string, string>;
  /** Design-gate fix (2026-08-20): the raw `message`/`error` from the
   * most recent Pay or Close server rejection, kept for the debug panel
   * only. Staff-facing surfaces (toast, pay-status) always show
   * translatePayCloseError's plain-language text instead; see that
   * function's header. */
  lastPayCloseErrorDetail?: string;
  /** ALPHA-08. Nothing pre-selected when the sheet opens; Confirm stays
   * disabled until the bartender taps a preset, a custom amount, or
   * explicit No tip, matching design/principles.md rule 4 ("no soft
   * default") the same way the liquor sheet already does. */
  selectedTip?: TipSelectionInput;
  /** The server's untipped whole-ticket payable preview: actual uncovered
   * lines and remaining amount, refreshed after ticket/payment changes.
   * Explicit checkout tip choices belong only to payConfirmPreview. */
  paymentPreview?: PaymentPreviewDto;
  /** ALPHA-08 pay warning. Set by loadPayWarning (openPayConfirmSheet)
   * from the most recent Captured payment already on this order, if any;
   * undefined means no warning applies. Never cleared while the sheet is
   * open (the banner must not disappear once shown, per the product
   * owner's own 2026-08-20 ruling). Amendment 2026-08-22 (fifth), item 1:
   * this field alone no longer decides whether the FULL alarming banner
   * (with its acknowledgment gate) shows; see
   * payWarningRequiresAcknowledgment. It stays the source of the banner's
   * own amount/time text on a genuinely reopened tab. */
  payWarningPriorPayment?: { amount: string; capturedAt: string };
  /** Amendment 2026-08-22 (fifth), item 1: every Captured payment already
   * on this order (not just the most recent one), set by loadPayWarning
   * alongside payWarningPriorPayment above. Drives the neutral
   * informational note (describeAlreadyCollectedNote) shown in place of
   * the full warning when this order was never closed and reopened -
   * the ordinary sequential multi-guest case. */
  payWarningCaptured: { guestId: string | null; amount: string }[];
  payWarningAcknowledged: boolean;
  /** CL2. True while the zero-due prompt is showing its SELF-CLOSED
   * wording: the server already closed this ticket, so the prompt's own
   * action leaves the screen rather than issuing a Close command. */
  ticketSelfClosed: boolean;
  /** Design/QA gate finding C: true when the most recent payment-history
   * read (loadPayWarning) failed. Fails CLOSED: while this is true,
   * updatePayConfirmButtonState keeps Confirm disabled regardless of tip
   * choice or acknowledgment, since an unknown prior-payment answer must
   * never be treated as "no prior payment." Cleared only by a read that
   * actually succeeds (a retry). */
  payWarningReadFailed: boolean;
  /** ALPHA-08 line-edit sheet: which line the currently-open sheet
   * targets. Undefined whenever the sheet is closed. */
  lineEditTargetOrderLineId?: string;
  /** Design/QA gate finding H: when the closeout summary was last
   * successfully loaded, for the "as of" line and the stale-figures note.
   * Client-side only: the summary itself is a live, not-finalized
   * aggregation recomputed fresh on every read (src/domain/closeout.ts's
   * own module header), so "freshness" here means "how long since this
   * browser last asked," not a server-stamped watermark. */
  closeoutLastLoadedAt?: number;
  /** CL6. The business date the closeout screen is currently viewing;
   * defaults to today each time the screen is opened. Stepped by the
   * previous/next day arrows, never typed. */
  closeoutBusinessDate?: string;
  closeoutLocationId?: string;
  /** CL6-F1, RPT1-F5. The tenant's current business date as the SERVER states
   * it (GET /reports/context: the tenant-local date minus one day before the
   * cutoff), fetched each time a dated screen opens. The "today" every
   * default and forward-arrow limit uses; the browser clock is never read. */
  tenantBusinessDate?: string;
  /** CL6. The last successfully loaded summary, kept so the Finalize
   * confirmation sheet can list the carryover preview without another
   * fetch. */
  closeoutSummary?: CloseoutSummaryDto;
  /** CL6. One idempotency key per opening of the finalize sheet, so a
   * double-tap of Confirm replays rather than double-finalizing (the
   * server's UNIQUE keys defend regardless; this keeps the happy path
   * clean). */
  closeoutFinalizeKey?: string;
  /** M12. The business date the void-history screen is currently
   * viewing; defaults to today each time the screen is opened. Stepped
   * by the previous/next day arrows, never typed, mirroring
   * closeoutBusinessDate above exactly. */
  voidHistoryBusinessDate?: string;
  /** ALPHA-12. Empty for the ordinary single-implicit-guest ticket. */
  guests: GuestDto[];
  /** ALPHA-12. Set only while the pay-confirm sheet is open; undefined the
   * rest of the time. See PayScope's own doc comment. */
  payScope?: PayScope;
  /** CASH1-S2-U1. Whether the open pay-confirm sheet offers Card or Cash (this location takes cash and this session can read the
   * drawer), from a fresh read each time the sheet opens. False means the sheet is the card sheet it always was. */
  payCashOffered?: boolean;
  /** CASH1-S2-U1-F1: set when Cash is on but no drawer is open and this person cannot open one. */
  payCashLockedLabel?: string | null;
  /** CASH1-S2-U1. The tender picked on the open sheet; undefined until one is picked (neither is preselected). */
  payTender?: PayTender;
  /** RCPT1-S3: set while the open ticket's card is authorized and a tip written on its receipt is still to be entered. */
  awaitingTip?: AwaitingTipState;
  /** RCPT1-S3: the ticket whose receipt authorization answered "reader" (the card cannot add a tip later), so its Pay shows the
   * ordinary tip choice for this sale. */
  receiptSaleSuppressedOrderId?: string;
  /** ALPHA-12 equal split. The order's own current IN-PROGRESS plan (its
   * shares), loaded on enterTicket and refreshed after every share
   * collection; undefined once no plan is active (never started, or the
   * most recent one already completed). Drives the pay button's "resume
   * the split in progress" shortcut and the split-progress sheet. */
  activeSplitPlan?: { plan: SplitPlanDto; shares: SplitShareDto[] };
  /** M15. Whether this ticket's active-split read is still pending, completed
   * successfully, or failed. Ordinary Pay is available only in `ready`; an
   * absent activeSplitPlan while `loading` or `failed` is not evidence that no
   * split exists. */
  activeSplitPlanLoadState: "loading" | "ready" | "failed";
  /** ALPHA-12 equal split setup: the stepper's current value while the
   * split-setup sheet is open. */
  splitSetupShareCount: number;
  /** ALPHA-12: an add-drink call waiting on a guest-attribution choice.
   * See PendingAdd's own doc comment. */
  pendingAdd?: PendingAdd;
  /** ALPHA-12: the pay-confirm sheet's OWN preview, distinct from
   * state.paymentPreview (which always reflects the whole ticket, for the
   * ring screen behind the sheet, without checkout tip). Scoped to
   * state.payScope and its explicit checkout tip, guest-filtered for
   * "guest", and a single
   * share's own figures (adapted into the same shape) for "share". Kept
   * separate so any checkout read while the sheet is open never
   * overwrites the ring screen's untipped whole-ticket view underneath
   * it. See refreshPayConfirmPreview. */
  payConfirmPreview?: PaymentPreviewDto;
  /** Amendment 2026-08-22 (fifth), item 3: which guest's most recent
   * Captured payment happened when, for ordering paid sections within the
   * paid group ("Order within the paid group by payment time"). Refreshed
   * alongside refreshPaymentPreview by refreshCapturedPaymentTimes; see
   * that function's own header comment for the null-guestId fallback this
   * pairs with. Keyed by guestId; the "Unassigned" section is not a real
   * guest and is never a key here, it falls back to
   * latestCapturedPaymentTime instead (sectionPaidAt). */
  capturedPaymentTimeByGuestId: Map<string, string>;
  /** CORE3. Ids of this ticket's lines a refund returned (whole lines), from the same payments read as the Paid row.
   * A covered line in this set reads Refunded instead of Paid. */
  refundedLineIds: Set<string>;
  /** Amendment 2026-08-22 (fifth), item 3. The latest Captured payment's
   * own timestamp across the whole order, regardless of which guest(s) it
   * covered; the fallback sectionPaidAt uses for a section
   * capturedPaymentTimeByGuestId cannot match by guestId directly (most
   * commonly Unassigned, since only a whole/multi-guest payment ever
   * fully covers it). */
  latestCapturedPaymentTime?: string;
  /** UX62 (docs/build-briefs/UX62-paid-ticket-totals.md): the ticket
   * rail's persistent Paid row (#paid-row) and Tips row (#tips-row), both
   * in renderPaidRow. Refreshed alongside capturedPaymentTimeByGuestId by
   * refreshCapturedPaymentTimes, summing every Captured payment's own
   * drinks-plus-tax portion (paidCents, amount minus that payment's own
   * tip) and tip (tipCents) separately (owner ruling, 2026-09-28: Paid
   * excludes tip so it agrees with Total; see summarizeCapturedPayments's
   * own header comment in money.ts). UX62-F1 (docs/build-briefs/UX62-F1-
   * refunded-line.md) adds refundedCents, the sum of every Captured
   * payment's own completed refunds (drinks plus tax; tip is never
   * refunded), powering the Refunded row (#refunded-row) directly below
   * Tips -- Paid itself is unchanged, still the gross amount charged.
   * Undefined before the first successful read for this ticket; a count
   * of 0 (no Captured payments yet) keeps all three rows hidden, the same
   * distinction T19's savedCards field documents for "not yet known"
   * versus "known and empty". */
  capturedPaymentsSummary?: CapturedPaymentsSummary;
  /** TOTALSDESIGN1: one entry per captured payment, read from the same getPayments response as the summary. */
  capturedCharges?: { label: string; at: string; amountCents: number }[];
  /** REFUNDALL1. True when a Captured payment on this ticket still has drinks-and-tax left to return. Drives the header
   * Refund button; the sheet itself re-reads the server's own figures. */
  ticketRefundable?: boolean;
  /** Payments-track Slice 3: every non-removed saved card on the active
   * order (any guest), loaded on ticket resume and refreshed after a
   * vault or removal. Powers "Use saved card" at Pay (shown only when
   * non-empty) and the saved-cards management sheet.
   *
   * T19 (docs/build-briefs/T19-saved-cards-unknown-until-loaded.md):
   * `undefined` means "not yet known for this ticket" (before
   * loadSavedCards resolves); an array, including `[]`, means "known."
   * manageCardsOptionEligible/keepTabOpenOptionEligible below both treat
   * `undefined` as ineligible, never as "confirmed no card," so More
   * cannot offer the wrong card-dependent option while this is loading or
   * still carrying the previous ticket's own resolved value. */
  savedCards: SavedCardEntryDto[] | undefined;
  /** AU14-F3-F3-F1-F7: cards removed on this ticket whose provider delete is
   * not confirmed yet. Reset whenever another ticket is entered. */
  pendingCardDetaches: PendingCardDetach[];
  /** Payments-track Slice 3: the pay-confirm sheet's current "Pay with"
   * choice. Undefined means "New card" (byte-for-byte the pre-slice
   * card-present flow); reset to undefined every time the sheet opens
   * (openPayConfirmSheet), never carried over from a previous attempt. */
  selectedSavedCardId?: string;
  /** CARDDEFAULT2: false from the moment the Pay sheet starts opening until the saved-card list outcome is in. */
  payCardsResolved?: boolean;
  /** Payments-track Slice 3: set right after a successful fresh
   * card-present capture with one resolvable guest, so the consent
   * screen can offer to vault it. Undefined the rest of the time,
   * including after a payment charged through an ALREADY-saved card
   * (vaulting an already-vaulted card is not offered). */
  pendingCardConsent?: { orderGuestId: string; guestLabel: string; sourcePaymentAttemptId: string };
  /** Payments-track Slice 4 (DEC-002). The active order's current hold,
   * of whatever status, loaded on ticket resume and refreshed after
   * opening one or after an add-line response's own "holdIncrement"
   * field signals a change. Undefined means "not loaded yet"; null means
   * "loaded, this order has never had one." Powers the ticket rail's own
   * hold-status badge and gates the "Keep tab open" action (offered only
   * when this is null/undefined AND no card is on file). */
  activeHold?: OrderHoldDto | null;
  /** PAY14 (docs/build-briefs/PAY14-order-hold-provider-recheck.md, Section
   * 8.1, D2): true while the active hold is known to have been placed
   * through a payment processor this location no longer uses (the hold
   * read's providerMismatch, a Pay refusal hold_provider_mismatch, or the
   * increment outcome of the same name). The badge then reads "Hold needs
   * a manager" in the danger style. Reset on every ticket entry and exit. */
  holdProviderMismatch: boolean;
  /** PAY14 (Section 8.1, the Q3 note): true once THIS device's own hold
   * read, Pay or drink add set a test (simulated) hold aside on this
   * ticket; the badge slot shows "Test hold cleared. Take a card at Pay."
   * until the bartender leaves the ticket. Reset on entry and exit. */
  testHoldClearedNoteVisible: boolean;
  /** AU8 (docs/backlog.md): every OPEN manager exception this tenant
   * currently carries, tenant-wide, refreshed on Open Tabs load and on
   * ticket entry (refreshManagerExceptions). Powers both the Open Tabs
   * badge/sheet (all rows) and the ticket-rail banner (filtered to
   * state.orderId). Stays [] for a session whose own permission snapshot
   * denies admin.manager_exception.view -- refreshManagerExceptions never
   * even issues the request in that case, the same "check hasPermission
   * before calling" convention admin.ts's reconciliationQueueVisible
   * already uses, so nothing here is ever a second authorization
   * boundary. */
  managerExceptions: ManagerExceptionDto[];
  /** UX80-F1: the account menu's other badge counts (cart lines, orders waiting for approval,
   * clients to match, counts in progress), from GET .../menu-counts. A key is absent when the
   * session may not see it. Refreshed on menu open and on loadOpenTabs, never on a timer. */
  menuCounts: MenuCountsBody;
  /** INV38: whether starting stock is set for every item, for the account menu's Set starting
   * stock row. `unknown` until the first answer (the row stays hidden, so a finished location never
   * sees it drawn), `unreadable` when the read failed (the row shows, since "cannot tell" must
   * not remove the way in). Kept across menu opens; a refresh replaces it only with an answer. */
  startingStockMenu: "unknown" | "needed" | "complete" | "unreadable";
  /** Best-effort tab-name cache for the sheet's own rows (getOrder, keyed
   * by order id), populated lazily the first time the sheet opens rather
   * than N+1 reads on every refreshManagerExceptions poll. Never the
   * authorization boundary for anything: a lookup failure (a permission
   * gap, a deleted order) degrades to a plain "This tab" fallback label,
   * it never hides or blocks the row itself. */
  managerExceptionOrderLabels: Map<string, string>;
  /** Which row's inline resolve panel (note + Confirm/Cancel) is open, at
   * most one at a time. */
  managerExceptionResolvingId?: string;
  /** Disables a row's own Resolve/Confirm controls while that row's own
   * request is in flight, the same per-row busy-id shape admin.ts's own
   * reconciliationRowBusyId uses, so a second tap cannot double-submit. */
  managerExceptionBusyId?: string;
  /** PAY14 Slice B: the Clear hold panel's picked row, for the row whose
   * panel is open (managerExceptionResolvingId), so a list re-render (a
   * refresh, the busy state, a failed clear) keeps the manager's choice
   * and Confirm's enabled state. Nothing is picked by default. */
  managerExceptionClearHoldChoice?: ClearHoldDisposition;
  /** PAY34-F1, PAY34-F2: the picked action of the payment resolution panel, kept across re-renders like the
   * Clear hold choice above. Nothing is picked by default. */
  managerExceptionPaymentChoice?: PaymentResolutionActionDto;
  /** PO6 Slice 2. The cart screen's own last-loaded data, kept so a
   * quantity edit or a line removal can re-render the vendor groups from
   * a fresh read after its own write, without disturbing the proposals
   * list already on screen. */
  purchasingCartLines: CartLineDto[];
  purchasingCartLocationId: string | undefined;
  /** PO6-S2-F2 and PO8: the server's per-vendor estimate, minimum status
   * and schedule, and the cart estimate, from the same response as the
   * lines (never computed here). */
  purchasingCartVendorSummaries: CartVendorSummaryDto[];
  purchasingCartEstimate: CartEstimateDto | null;
  purchasingCartProposals: DraftProposalDto[];
  /** INV8 (DEC-176) Section 2.5: the Low list's own client-side sort,
   * re-applied with no new network call whenever it changes. Reset to the
   * default ("shortfall") each time the screen is opened fresh, the same
   * "own visit" reset purchasingCartPlacedOrders below already gets. */
  purchasingCartProposalsSort: ProposalSortOrder;
  /** PO6 Slice 2. Every order THIS VISIT to the cart screen has placed,
   * each one a direct copy of what placeCart's own response returned,
   * never recomputed. Cleared each time the screen is opened fresh
   * (showPurchasingCartScreen), never merely appended to across visits. */
  purchasingCartPlacedOrders: PlacedOrderDto[];
  /** PO6 Slice 4. The receiving screen's own last-loaded order id and
   * lines, kept so a successful receipt can re-render the lines from a
   * fresh GET .../receiving read after its own write, the same shape
   * purchasingCartLines above uses. `receivingUnitPriceCentsByLiquorProductId`
   * caches each line's product's own unit_price_cents (GET .../liquor-
   * products/:id, a separate call: see ui/src/purchasingDisplay.ts's
   * receivingUnitOptionsForItemKind for why -- ITEM2 Slice 5 moved this
   * from the product's item_kind to its unit_price_cents, non-null exactly
   * on a whole-unit product, so a tenant-added whole-unit kind resolves
   * correctly with no separate sale_mechanism fetch), so reopening the
   * same order or seeing the same product twice on one order never
   * re-fetches it. INV8-F1 Section 5 widened the cached value from a bare
   * `number | null` to also carry container_type/size_value/size_unit off
   * that SAME already-fetched response, so the container chip (Section 5)
   * needs no second, redundant fetch of its own. */
  receivingOrderId: string | undefined;
  receivingLines: LineReceivingStateDto[];
  /** PO13: the substitutions recorded on the order being received, from the same read as the lines. */
  receivingSubstitutions: OrderSubstitutionDto[];
  /** CORE9: each line's unit cost at placement (cents per order unit), by purchase order line id, read from the order
   * itself. A line absent from the map (the read failed) prefills no cost. */
  receivingUnitCostCentsByLineId: Map<string, number | null>;
  receivingUnitPriceCentsByLiquorProductId: Map<
    string,
    { unitPriceCents: number | null } & ReceivingProductContainerInfo
  >;
  /** PO6 Slice 5. The order detail screen's own last-loaded receiving
   * view (GET .../purchasing/orders/:orderId/receiving), fetched
   * whenever the loaded order can enter or is in `exception`, so the
   * exception panel's target-state preview and legality checks read the
   * SAME derived variance the server itself would. Undefined when this
   * order's own detail has not fetched it (no permission, or the order
   * can neither enter nor is in `exception`). */
  purchasingOrderDetailReceivingView: OrderReceivingViewDto | undefined;
  /** PO6 Slice 5. Each order line's own receipts (GET .../inventory/
   * receipts?liquorProductId=..., filtered client-side to this line's
   * own purchaseOrderLineId -- no route returns receipts scoped to an
   * order or a line), keyed by purchaseOrderLineId, so a correction's
   * own reload only re-fetches the one line's receipts it touched. */
  purchasingOrderLineReceipts: Map<string, InventoryReceiptDto[]>;
  /** PO6 Slice 6. Every delivery THIS VISIT to the outside-order screen
   * has recorded, each one a direct copy of what recordOutsideSystemReceipt's
   * own response returned (plus the product name, known client-side from
   * the search that selected it, since the response itself carries none).
   * Cleared each time the screen is opened fresh
   * (showPurchasingOutsideOrderScreen), the same "session-only, own visit"
   * shape purchasingCartPlacedOrders above uses. */
  outsideOrderRecordedReceipts: OutsideOrderRecordedReceipt[];
} = {
  menuDrinks: [],
  menuItems: [],
  pricingModel: "per_ounce",
  menuDetailCache: new Map(),
  cheapestPerOunceCentsByCategory: new Map(),
  orderPaid: false,
  lines: [],
  openTabs: [],
  tabFilterMode: "all",
  tabSearchText: "",
  tabStatusFilter: undefined,
  tabOver1Hr: false,
  recentlyClosedTabs: [],
  tabMergeModeActive: false,
  mergeSourceOrderIds: [],
  matchMissCount: 0,
  payWarningAcknowledged: false,
  ticketSelfClosed: false,
  payWarningReadFailed: false,
  payWarningCaptured: [],
  guests: [],
  activeSplitPlanLoadState: "ready",
  splitSetupShareCount: 2,
  capturedPaymentTimeByGuestId: new Map(),
  refundedLineIds: new Set(),
  savedCards: undefined,
  pendingCardDetaches: [],
  holdProviderMismatch: false,
  testHoldClearedNoteVisible: false,
  managerExceptions: [],
  menuCounts: {},
  startingStockMenu: "unknown",
  managerExceptionOrderLabels: new Map(),
  purchasingCartLines: [],
  purchasingCartLocationId: undefined,
  purchasingCartVendorSummaries: [],
  purchasingCartEstimate: null,
  purchasingCartProposals: [],
  purchasingCartProposalsSort: "shortfall",
  purchasingCartPlacedOrders: [],
  receivingOrderId: undefined,
  receivingLines: [],
  receivingSubstitutions: [],
  receivingUnitCostCentsByLineId: new Map(),
  receivingUnitPriceCentsByLiquorProductId: new Map(),
  purchasingOrderDetailReceivingView: undefined,
  purchasingOrderLineReceipts: new Map(),
  outsideOrderRecordedReceipts: [],
  payIdempotencyKeysByTarget: new PayIdempotencyKeyMap(
    () => crypto.randomUUID(),
    (event) => reportPayKeySignal(event),
  ),
  holdIdempotencyKeysByOrderId: new Map<string, string>(),
};

let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
// MyTime track (track/mytime) Slice 2: Add Guest client search's own
// debounce timer and in-flight-request canceller, module-level state kept
// separate from `state` (the giant shared ticket-rail object) the same
// way searchDebounceTimer above already is -- this is UI-transient state
// belonging to one sheet, not ticket data. addGuestSearchResults is the
// last response actually rendered, read back by handleAddGuestResultTap
// (the DOM row itself carries only an id, never the whole result object,
// so the tap handler looks the full result back up here).
let addGuestSearchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
let addGuestSearchAbortController: AbortController | undefined;
let addGuestSearchResults: ClientSearchResultDto[] = [];
/** LOUNGERMAJ1 (lounger-13): the last search could not be answered (not "no match"), so the sheet says so and offers a retry. */
let addGuestSearchFailed = false;
let toastTimer: ReturnType<typeof setTimeout> | undefined;

// PO6 Slice 2: the cart screen's own "Add a product" search box.
// Transient UI state for one widget, kept separate from `state` for the
// same reason addGuestSearchDebounceTimer/addGuestSearchResults above
// are.
let purchasingCartProductSearchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
let purchasingCartProductSearchResults: LiquorProductDto[] = [];

// PO6 Slice 6: the outside-order screen's own "Find the product" search
// box (the identical debounce-then-tap-a-row shape the cart screen's own
// search above uses) and which product, if any, is currently selected
// for entry. Transient UI state for this one widget, kept separate from
// `state` for the same reason the cart's own search state above is.
let purchasingOutsideOrderProductSearchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
let purchasingOutsideOrderProductSearchResults: LiquorProductDto[] = [];
let purchasingOutsideOrderSelectedProduct: LiquorProductDto | undefined;

/** One row of the outside-order screen's own "Recorded this visit" list:
 * the write's own response (id, quantity, unit, source_reference) plus
 * the product name, which that response does not carry and which this
 * screen already knows from the search that selected it. */
interface OutsideOrderRecordedReceipt {
  readonly id: string;
  readonly productName: string;
  readonly quantity: string;
  readonly unit: string;
  readonly sourceReference: string | null;
  /** INV35-B1-F1 (DEC-300). The vendor picked for this delivery, or undefined. */
  readonly vendorName: string | undefined;
}

// Auth track (track/auth): which server/tenant THIS terminal talks to
// (session.ts's own header on why this is separate from, and survives,
// the logged-in Session above). Mirrored here from loadTerminalConfig/
// saveTerminalConfig/clearTerminalConfig so every screen that needs it
// (the user picker's read, a PIN login attempt) does not re-read
// localStorage on every keystroke; kept in sync at every write site
// (handleTerminalConnected, handleDeviceNotRecognized, init).
let terminalConfigState: TerminalConfig | undefined;
// CL8 (docs/build-briefs/CL8-card-reader-health.md): the server's last answer about THIS device's reader. Memory only; a
// reload asks again. Set only from a successful answer, so a failed read leaves the pill as it was and never invents a state.
let readerHealthReading: { terminalId: string; state: ReaderHealthState; atMs: number } | undefined;
let readerHealthInFlight = false;

// ---------------------------------------------------------------------
// Element lookup
// ---------------------------------------------------------------------

mountTicketPresentation(document);

function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) {
    throw new Error(`expected element for selector "${selector}"`);
  }
  return element;
}

// Auth track (track/auth), Slice 1: the three screens that replaced the
// old single free-text #login-screen/#login-form/#login-employee-id/
// #login-theme-select stub (index.html's own header comment on this
// screen swap). SEC2-S3: #login-api-base-url survives (behind "Change server");
// #login-tenant-id is gone with the typed tenant id.
// UX108: the empty shell that paints first (ui/index.html); hideAllScreens hides it.
const bootShell = mustFind<HTMLElement>("#boot-shell");
const bootRetryButton = mustFind<HTMLButtonElement>("#boot-retry");
/** How long the page-load session check may take before the page offers Retry. */
const BOOT_SESSION_CHECK_LIMIT_MS = 10_000;
const terminalSetupScreen = mustFind<HTMLElement>("#terminal-setup-screen");
// ONBW1-S1 (DEC-416): the screens a new owner sees after opening the welcome link (ui/src/welcomeLink.ts draws them).
const welcomeScreen = mustFind<HTMLElement>("#welcome-screen");
// Terminal-assignment slice: the manager-gated picker step between login
// and Open Tabs, offered exactly once per device (see
// completeLoginAndShowOpenTabs's own gate below).
const terminalPickerScreen = mustFind<HTMLElement>("#terminal-picker-screen");
const userPickerScreen = mustFind<HTMLElement>("#user-picker-screen");
// RBAC track: manager-approval flow, step 1 of 2. See this file's own
// "Manager approval" section, near the pin-entry screen wiring below.
const approvalPickerScreen = mustFind<HTMLElement>("#approval-picker-screen");
const approvalPickerActionEl = mustFind<HTMLElement>("#approval-picker-action");
const approvalPickerStatusEl = mustFind<HTMLElement>("#approval-picker-status");
const approvalPickerListEl = mustFind<HTMLElement>("#approval-picker-list");
const approvalPickerEmptyEl = mustFind<HTMLElement>("#approval-picker-empty");
const approvalPickerCancelButton = mustFind<HTMLButtonElement>("#approval-picker-cancel");
const pinEntryScreen = mustFind<HTMLElement>("#pin-entry-screen");
const profileScreen = mustFind<HTMLElement>("#profile-screen");
const notificationsScreen = mustFind<HTMLElement>("#notifications-screen");
const openTabsScreen = mustFind<HTMLElement>("#open-tabs-screen");
const accountNotificationsButton = mustFind<HTMLButtonElement>("#open-tabs-account-notifications");
const ringScreen = mustFind<HTMLElement>("#ring-screen");

// STALEBUNDLE1: the floor screens (Open Tabs, the ticket, the user tiles and the PIN pad) reload themselves for a new
// version only at a safe moment; every other screen is a console and gets a Reload banner below the header instead.
const FLOOR_SCREEN_IDS = [
  "welcome-screen",
  "terminal-setup-screen",
  "user-picker-screen",
  "terminal-picker-screen",
  "approval-picker-screen",
  "pin-entry-screen",
  "open-tabs-screen",
  "ring-screen",
];
const FLOOR_SAFE_SCREEN_IDS = ["user-picker-screen", "open-tabs-screen"];
const screenShown = (id: string): boolean => document.getElementById(id)?.hidden === false;
// ERRTRACK1: a screen that breaks shows a plain sentence and a short code (no Copy on the floor) and sends a scrubbed report.
startErrorTracking({
  console: "location",
  surface: () => (FLOOR_SCREEN_IDS.some(screenShown) ? "floor" : "console"),
  ids: () => ({ tenantId: terminalConfigState?.tenantId, locationId: terminalConfigState?.locationId }),
  screen: () => {
    const shown = FLOOR_SCREEN_IDS.find(screenShown);
    return shown === undefined ? undefined : `${window.location.pathname}${shown}`;
  },
});
startNewVersionCheck({
  headerSelector: "#app-topbar",
  surface: () => (FLOOR_SCREEN_IDS.some(screenShown) ? "floor" : "console"),
  floorScreenIsSafe: () => FLOOR_SAFE_SCREEN_IDS.some(screenShown) && !screenShown("ring-screen"),
});

// SEC2-S3 (DEC-321): the setup screen takes a setup code on an in-page keypad. ui/src/terminalSetup.ts draws
// it; the two callbacks below are the only places it touches this file's terminal state.
const terminalSetup: TerminalSetupScreen = mountTerminalSetup(terminalSetupScreen, {
  defaultApiBaseUrl: () =>
    window.location.origin !== "null" && window.location.protocol !== "file:"
      ? window.location.origin
      : "http://localhost:3000",
  currentConfig: () => terminalConfigState,
  onConnected: (config) => handleTerminalConnected(config),
  onContinue: () => handleTerminalSetupContinue(),
});

const welcomeFlow: WelcomeFlow = mountWelcome(welcomeScreen, {
  apiBaseUrl: () =>
    window.location.origin !== "null" && window.location.protocol !== "file:"
      ? window.location.origin
      : "http://localhost:3000",
  currentConfig: () => terminalConfigState,
  onConnected: (config) => {
    // A new binding starts clean: nothing of an earlier session on this page outlives it.
    setAuthToken(undefined);
    clearSession();
    handleTerminalConnected(config);
  },
  onSignedIn: async (config, body) => {
    // The same sign-in the PIN pad makes, with no forced change after it (the owner just chose this PIN).
    establishSignedInSession(config, body);
    pinEntry = undefined;
    firstSignInSetupPending = true;
    await completeLoginAndShowOpenTabs();
  },
  showScreen: () => showWelcomeScreen(),
  onUseCode: () => showTerminalSetupScreen(),
  onContinue: () => handleTerminalSetupContinue(),
});

const userTileListEl = mustFind<HTMLElement>("#user-tile-list");
const userPickerEmptyEl = mustFind<HTMLElement>("#user-picker-empty");
const userPickerDeviceLineEl = mustFind<HTMLElement>("#user-picker-device-line");
const userPickerDeviceNameEl = mustFind<HTMLElement>("#user-picker-device-name");

const terminalPickerManagerEl = mustFind<HTMLElement>("#terminal-picker-manager");
const terminalPickerBlockedEl = mustFind<HTMLElement>("#terminal-picker-blocked");
const terminalPickerCurrentEl = mustFind<HTMLElement>("#terminal-picker-current");
const terminalPickerListEl = mustFind<HTMLElement>("#terminal-picker-list");
const terminalPickerEmptyEl = mustFind<HTMLElement>("#terminal-picker-empty");
const terminalPickerRegisterSectionEl = mustFind<HTMLElement>("#terminal-picker-register-section");
const terminalPickerRegisterNameInput = mustFind<HTMLInputElement>(
  "#terminal-picker-register-name",
);
const terminalPickerRegisterButton = mustFind<HTMLButtonElement>(
  "#terminal-picker-register-button",
);
const terminalPickerStatusEl = mustFind<HTMLElement>("#terminal-picker-status");
const terminalPickerBackButton = mustFind<HTMLButtonElement>("#terminal-picker-back");
const terminalPickerBlockedLogoutButton = mustFind<HTMLButtonElement>(
  "#terminal-picker-blocked-logout",
);

const pinEntryTitleEl = mustFind<HTMLElement>("#pin-entry-title");
const pinEntrySubtitleEl = mustFind<HTMLElement>("#pin-entry-subtitle");
const pinEntryDisplayEl = mustFind<HTMLElement>("#pin-entry-display");
const pinEntryErrorEl = mustFind<HTMLElement>("#pin-entry-error");
const pinDigitButtons = Array.from(
  document.querySelectorAll<HTMLButtonElement>('[data-testid^="pin-digit-"]'),
);
const pinClearButton = mustFind<HTMLButtonElement>("#pin-clear");
const pinBackspaceButton = mustFind<HTMLButtonElement>("#pin-backspace");
const pinSubmitButton = mustFind<HTMLButtonElement>("#pin-submit");
const pinCancelButton = mustFind<HTMLButtonElement>("#pin-cancel");

const profileNameEl = mustFind<HTMLElement>("#profile-name");
const profileThemeSelect = mustFind<HTMLSelectElement>("#profile-theme-select");
const profileChangePinButton = mustFind<HTMLButtonElement>("#profile-change-pin");

// USR4 (docs/build-briefs/USR4-my-profile-details.md): "Your details".
const profileDisplayNameInput = mustFind<HTMLInputElement>("#profile-display-name");
const profileFirstNameInput = mustFind<HTMLInputElement>("#profile-first-name");
const profileLastNameInput = mustFind<HTMLInputElement>("#profile-last-name");
const profileEmailInput = mustFind<HTMLInputElement>("#profile-email");
const profilePhoneInput = mustFind<HTMLInputElement>("#profile-phone");
const profileDisplayNameErrorEl = mustFind<HTMLElement>(
  '[data-testid="profile-display-name-error"]',
);
const profileFirstNameErrorEl = mustFind<HTMLElement>('[data-testid="profile-first-name-error"]');
const profileLastNameErrorEl = mustFind<HTMLElement>('[data-testid="profile-last-name-error"]');
const profileEmailErrorEl = mustFind<HTMLElement>('[data-testid="profile-email-error"]');
const profilePhoneErrorEl = mustFind<HTMLElement>('[data-testid="profile-phone-error"]');
const profileSaveButton = mustFind<HTMLButtonElement>("#profile-save");

// UX17 (docs/build-briefs/UX17-persistent-navigation.md): ONE shared
// topbar, outside every screen <section>, replacing the nine per-screen
// copies (one dropdown handle each, one My Profile button each) this
// block used to declare. Ids keep the "open-tabs-" prefix the removed
// per-screen copies used, chosen by measured e2e usage (see the build
// brief). accountMyProfileButton is hidden specifically while the
// Profile screen itself is active (setAccountMenuProfileMode), the same
// behavior the removed profile-only dropdown achieved by simply never
// carrying the entry.
const appTopbar = mustFind<HTMLElement>("#app-topbar");
// DEC-501 (UIFIX3): the approval banner is gone; a count badge on Ordering in the menu and in the topbar replaces it.
const topbarApprovalBadge = mustFind<HTMLButtonElement>("#topbar-approval-badge");
const topbarApprovalCount = mustFind<HTMLElement>("#topbar-approval-count");
const networkOfferNotice = createNetworkOfferNotice({
  anchor: appTopbar, current: () => state.session,
  locationId: () => loadTerminalConfig()?.locationId, deviceId: () => loadTerminalConfig()?.deviceId,
  active: () => !appTopbar.hidden && state.session !== undefined,
  toast: (message, variant) => showToast(message, variant),
});
const purchaseCancelDialog = mustFind<HTMLDialogElement>("#purchase-cancel-dialog");
const purchaseCancelSummary = mustFind<HTMLElement>("#purchase-cancel-summary");
const purchaseCancelReason = mustFind<HTMLSelectElement>("#purchase-cancel-reason");
const purchaseCancelError = mustFind<HTMLElement>("#purchase-cancel-error");
const purchaseCancelKeep = mustFind<HTMLButtonElement>("#purchase-cancel-keep");
const purchaseCancelConfirm = mustFind<HTMLButtonElement>("#purchase-cancel-confirm");
const purchaseCancelAction = mustFind<HTMLButtonElement>("#purchasing-order-detail-cancel");

const homeButton = mustFind<HTMLButtonElement>("#open-tabs-home-button");
const topbarSecondaryActionButton = mustFind<HTMLButtonElement>("#topbar-secondary-action");
// HIGFIX3: where the button sits in the console header, so placeTopbarSecondaryAction can put it back.
const topbarSecondaryHome = {
  parent: topbarSecondaryActionButton.parentElement,
  next: topbarSecondaryActionButton.nextSibling,
};
const topbarPricingModelEl = mustFind<HTMLElement>("#topbar-pricing-model");
const sessionTenant = mustFind<HTMLElement>('[data-testid="open-tabs-session-tenant"]');
const accountTrigger = mustFind<HTMLButtonElement>("#open-tabs-account-trigger");
const accountDropdown = mustFind<HTMLElement>("#open-tabs-account-dropdown");
const sessionEmployeeBadge = mustFind<HTMLElement>(
  '[data-testid="open-tabs-session-employee-badge"]',
);
const sessionEmployeeName = mustFind<HTMLElement>(
  '[data-testid="open-tabs-session-employee-name"]',
);
const accountMyProfileButton = mustFind<HTMLButtonElement>("#open-tabs-account-my-profile");
// RPT1-F4 (DEC-285): My Tips, beside My Profile; every signed-in employee.
const accountMyTipsButton = mustFind<HTMLButtonElement>("#open-tabs-account-my-tips");
const openManagerSettingsButton = mustFind<HTMLButtonElement>("#open-manager-settings");
const logOutButton = mustFind<HTMLButtonElement>("#open-tabs-log-out");
// SUPPORT1: Get help, in the console account menu only.
const getHelpButton = mustFind<HTMLButtonElement>("#open-tabs-get-help");
// STATUSPAGE1: System status, a link to the public status page. Shown only to people who see Settings (managers), never
// as a floor control, and only when SUPPORT_STATUS_URL is set.
const accountStatusLink = wireStatusLink(mustFind<HTMLAnchorElement>("#open-tabs-account-status-link"));
accountStatusLink.setAllowed(false);
// UX80 (DEC-298): the two level account menu. The markup is static (every
// moved button keeps its id and test id); ui/src/accountMenu.ts is the table
// that says which rows a person sees, and refreshAccountMenuRows applies it.
const accountMenuWhoBadge = mustFind<HTMLElement>("#account-menu-who-badge");
const accountMenuWhoName = mustFind<HTMLElement>("#account-menu-who-name");
const accountMenuWhoRole = mustFind<HTMLElement>("#account-menu-who-role");
const accountMenuChipCount = mustFind<HTMLElement>("#account-menu-chip-count");
const needsYouEl = mustFind<HTMLElement>("#needs-you");
const needsYouRowsEl = mustFind<HTMLElement>("#needs-you-rows");
const accountMenuRuleGroups = mustFind<HTMLElement>("#account-menu-rule-groups");
const adminOpsBackButton = mustFind<HTMLButtonElement>("#admin-ops-back");

const themeLink = mustFind<HTMLLinkElement>("link[data-theme-link]");
// Amendment 2026-08-22 (sixth), item 1: resizable ticket rail. Wired here,
// at module load, rather than inside enterTicket/session setup below:
// unlike the ticket contents themselves, the rail's width is a device
// setting that exists whether or not a ticket is currently loaded, so it
// applies immediately rather than waiting on a session. See
// railResize.ts's own header comment for the touch-and-mouse drag
// mechanics and the persistence-tradeoff note against theme.ts.
const railResizeHandle = mustFind<HTMLElement>("#rail-resize-handle");
const ticketRailEl = mustFind<HTMLElement>("#ticket-rail");
initRailResize(railResizeHandle, ticketRailEl);
const alphabetStrip = mustFind<HTMLElement>("#alphabet-strip");
const menuScroll = mustFind<HTMLElement>("#menu-scroll");
const menuIndexRow = mustFind<HTMLElement>("#menu-index-row");
const menuSearchInput = mustFind<HTMLInputElement>("#menu-search-input");
const menuSearchClear = mustFind<HTMLButtonElement>("#menu-search-clear");
const menuFilterPopular = mustFind<HTMLButtonElement>("#menu-filter-popular");
const menuFilterDrinks = mustFind<HTMLButtonElement>("#menu-filter-drinks");
const menuFilterBottles = mustFind<HTMLButtonElement>("#menu-filter-bottles");
const menuFilterBase = mustFind<HTMLElement>("#menu-filter-base");
const menuFilterBaseButton = mustFind<HTMLButtonElement>("#menu-filter-base-button");
const menuFilterBaseLabel = mustFind<HTMLElement>("#menu-filter-base-label");
const menuFilterBaseClear = mustFind<HTMLButtonElement>("#menu-filter-base-clear");
const menuFilterScrim = mustFind<HTMLElement>("#menu-filter-scrim");
const menuFilterList = mustFind<HTMLElement>("#menu-filter-list");
const orderStatusEl = mustFind<HTMLElement>("#order-status");
const ticketSub = mustFind<HTMLElement>("#ticket-sub");
// AU8 (docs/backlog.md): the ticket rail's own plain-language heads-up,
// shown BEFORE a bartender or manager taps Pay/Close and hits the
// existing ManagerExceptionBlocksOrderError refusal (translatePayCloseError's
// manager_exception_blocks_order entry). Gated on admin.manager_exception.view
// (renderTicketManagerExceptionBanner); a Lounger without that permission
// keeps today's behavior exactly -- the banner never renders for them, only
// the refusal itself still applies if they somehow tap Pay/Close anyway.
const ticketManagerExceptionBanner = mustFind<HTMLElement>("#ticket-manager-exception-banner");
const ticketManagerExceptionText = mustFind<HTMLElement>("#ticket-manager-exception-text");
const ticketManagerExceptionViewButton = mustFind<HTMLButtonElement>(
  "#ticket-manager-exception-view",
);
// PAY5-F1 (docs/backlog.md, docs/build-briefs/PAY5-F1-terminal-notice-in-
// topbar.md): the top bar's own terminal segment, visible on every floor
// screen with a topbar (not only while a ticket happens to be open,
// unlike the PAY7 ticket-header chip this replaces). Never permission-
// gated (every floor role benefits equally from knowing to find a
// manager, and showing it leaks nothing sensitive -- device-local
// config, not order or client data), and scoped to the one terminal-
// failure state the client can know about without a fresh network round
// trip (renderTopbarTerminalSegment below explains why the other two
// PAY5/PAY6 codes are not included). Exactly one of
// topbarTerminalStatusEl / topbarTerminalWarningButton is visible at a
// time. Tapping the warning button opens topbarTerminalDetail with the
// full approved sentence, verbatim.
const topbarTerminalStatusEl = mustFind<HTMLElement>("#topbar-terminal-status");
const topbarTerminalWarningButton = mustFind<HTMLButtonElement>("#topbar-terminal-warning");
const topbarTerminalDetail = mustFind<HTMLElement>("#topbar-terminal-detail");
const topbarReaderTrigger = mustFind<HTMLButtonElement>("#topbar-reader-trigger");
const readerDropdown = createReaderDropdown({
  panel: topbarTerminalDetail,
  triggers: [topbarReaderTrigger, topbarTerminalWarningButton],
  context: () => {
    const session = state.session;
    const config = terminalConfigState;
    if (session === undefined || config === undefined || !hasPermission(session, TERMINAL_PICKER_PERMISSION)) return undefined;
    return { identity: `${session.token}:${config.deviceToken}:${config.locationId ?? ""}`, apiBaseUrl: session.apiBaseUrl, tenantId: session.tenantId, currentReaderId: config.paymentTerminalId };
  },
  setup: () => {
    // UIFIX5: readers are registered under Settings, Devices, so the row shows only for a session that can open
    // Settings at all.
    if (openManagerSettingsButton.hidden) return undefined;
    return () => {
      showAdminScreen();
      document.getElementById("admin-group-tab-tablets")?.click();
    };
  },
  selected: (reader) => {
    const config = terminalConfigState;
    if (config === undefined) return;
    terminalConfigState = savePaymentTerminalSelection(config, reader.id, reader.display_name);
    readerHealthReading = undefined;
    resetReaderCartDisplay();
    if (!payConfirmScrim.hidden) { updatePayConfirmSummary(); updatePayConfirmButtonState(); }
    renderTopbarTerminalSegment();
  },
});
// ALPHA-12 guests, redesigned per Amendment 2026-08-22 (item 3): one
// persistent Add Guest control in the ticket rail, no chips row.
const addGuestRowEl = mustFind<HTMLElement>("#add-guest-row");
const addGuestButtonEl = mustFind<HTMLButtonElement>("#add-guest-button");
const noOrderPanel = mustFind<HTMLElement>("#no-order-panel");
const noOrderBackToTabsButton = mustFind<HTMLButtonElement>("#no-order-back-to-tabs");
const possibleDuplicatesScreen = mustFind<HTMLElement>("#possible-duplicates-screen");
const possibleDuplicatesReport = mountPossibleDuplicateClients({
  element: possibleDuplicatesScreen,
  session: () => state.session !== undefined && hasPermission(state.session, "guest.directory.duplicates.view") ? state.session : undefined,
  back: () => openFunctionHub("shift"),
});
function openPossibleDuplicates(): void {
  const session = state.session;
  if (session === undefined || !hasPermission(session, "guest.directory.duplicates.view")) return;
  hideAllScreens();
  possibleDuplicatesScreen.hidden = false;
  showTopbarChrome("possible-duplicates-screen");
  possibleDuplicatesReport.show();
}
const ticketLinesEl = mustFind<HTMLElement>("#ticket-lines");
// UX44 Section 2b (docs/build-briefs/UX44-ticket-line-name-whole-price-
// below.md, amendment after QA's Section 6 rail-resize finding). A rail
// drag (railResize.ts's initRailResize) sets #ticket-rail's own width
// directly and never calls renderTicketLines(), so refreshLineNameStacking()
// and refreshLineTruncationChevrons() -- both only ever invoked from that
// function's own two return paths -- went stale on resize: a row stacked
// at the 400px default stayed stacked after widening to 560px, wasting a
// row of vertical space. This width-only ResizeObserver on #ticket-lines
// (the actual ancestor refreshLineNameStacking()'s own top.clientWidth is
// measured from, not #ticket-rail directly) re-runs both refresh functions,
// batched to one requestAnimationFrame per genuine width change, so
// stacking and truncation stay correct through a live drag, a viewport
// resize, or any other cause of #ticket-lines changing width. Created here,
// immediately after ticketLinesEl's own declaration above (reusing that
// binding, not redeclaring it): referencing ticketLinesEl any earlier in
// this module (e.g. beside initRailResize(railResizeHandle, ticketRailEl),
// main.ts:1357) would read the const before its own declaration in the
// same scope, since ticketLinesResizeObserver.observe(ticketLinesEl) below
// runs synchronously at module load, not deferred inside a callback.
// refreshLineNameStacking and refreshLineTruncationChevrons themselves are
// hoisted function declarations defined later in this file, and are only
// ever invoked from inside the observer's own requestAnimationFrame
// callback below, which cannot run until the whole module has finished its
// synchronous top-to-bottom execution, so their own later position in the
// file is not order-sensitive. The width===lastTicketLinesWidthPx guard is
// what prevents a loop: .line-top-stacked changes a row's own height, never
// #ticket-lines's own width, so nothing here can cycle, and the guard
// additionally means the callback body only ever runs when the observed
// width has genuinely changed from the last width it acted on.
let lastTicketLinesWidthPx: number | undefined;
let ticketLinesResizeFrame: number | undefined;
const ticketLinesResizeObserver = new ResizeObserver((entries) => {
  const width = entries[0]?.contentBoxSize?.[0]?.inlineSize ?? entries[0]?.contentRect.width;
  if (width === undefined || width === lastTicketLinesWidthPx) {
    return;
  }
  if (ticketLinesResizeFrame !== undefined) {
    cancelAnimationFrame(ticketLinesResizeFrame);
  }
  ticketLinesResizeFrame = requestAnimationFrame(() => {
    ticketLinesResizeFrame = undefined;
    lastTicketLinesWidthPx = width;
    refreshLineNameStacking();
    refreshLineNameWordTruncation();
    reapplyExpandedLineNames();
    refreshLineTruncationChevrons();
  });
});
ticketLinesResizeObserver.observe(ticketLinesEl);
const ticketTotalsEl = mustFind<HTMLElement>("#ticket-totals");
const discountLinesEl = mustFind<HTMLElement>("#discount-lines");
const lineEditDiscountButton = mustFind<HTMLButtonElement>("#line-edit-discount");
const lineEditRemoveSpecialButton = mustFind<HTMLButtonElement>("#line-edit-remove-special");
// M5: the Change price row on the line sheet, and the sheet it opens.
const lineEditChangePriceButton = mustFind<HTMLButtonElement>("#line-edit-change-price");
// M5-R10: the Price history row beside it, with the change count.
const lineEditPriceHistoryButton = mustFind<HTMLButtonElement>("#line-edit-price-history");
const lineEditPriceHistoryCountEl = mustFind<HTMLElement>("#line-edit-price-history-count");
// The Change price sheet is a second sheet INSIDE #line-edit-scrim (the chrome
// ratchet refuses a new scrim), shown in place of the line sheet.
const lineEditSheetEl = mustFind<HTMLElement>("#line-edit-sheet");
const priceChangeSheetEl = mustFind<HTMLElement>("#price-change-sheet");
const priceChangeCloseButton = mustFind<HTMLButtonElement>("#price-change-close");
const priceChangeCancelButton = mustFind<HTMLButtonElement>("#price-change-cancel");
const priceChangeSubmitButton = mustFind<HTMLButtonElement>("#price-change-submit");
const priceChangeRestoreButton = mustFind<HTMLButtonElement>("#price-change-restore");
const priceChangeSubtitleEl = mustFind<HTMLElement>("#price-change-subtitle");
const priceChangeMenuPriceEl = mustFind<HTMLElement>("#price-change-menu-price");
const priceChangeMathEl = mustFind<HTMLElement>("#price-change-math");
const priceChangeNewEl = mustFind<HTMLElement>("#price-change-new");
const priceChangeNewValueEl = mustFind<HTMLElement>("#price-change-new-value");
const priceChangeDiffEl = mustFind<HTMLElement>("#price-change-diff");
const priceChangePresetsEl = mustFind<HTMLElement>("#price-change-presets");
const priceChangePadEl = mustFind<HTMLElement>("#price-change-pad");
const priceChangeReasonsEl = mustFind<HTMLElement>("#price-change-reasons");
const priceChangeReasonStateEl = mustFind<HTMLElement>("#price-change-reason-state");
const priceChangeBlockedEl = mustFind<HTMLElement>("#price-change-blocked");
const priceChangeDeepEl = mustFind<HTMLElement>("#price-change-deep");
const priceChangeBelowCostEl = mustFind<HTMLElement>("#price-change-below-cost");
const priceChangeApprovalNoteEl = mustFind<HTMLElement>("#price-change-approval-note");
const priceChangesNoteEl = mustFind<HTMLElement>("#price-changes-note");
const pinEntryContextEl = mustFind<HTMLElement>("#pin-entry-context");
const payConfirmDiscountRow = mustFind<HTMLElement>("#pay-confirm-discount-row");
const payConfirmDiscountEl = mustFind<HTMLElement>("#pay-confirm-discount");
const subtotalAmountEl = mustFind<HTMLElement>("#subtotal-amount");
const taxAmountEl = mustFind<HTMLElement>("#tax-amount");
const totalAmountEl = mustFind<HTMLElement>("#total-amount");
// DUALPRICE1: the Cash and Card totals under Total.
const totalLabelEl = mustFind<HTMLElement>("#total-label");
const dualPricingTotalsEl = mustFind<HTMLElement>("#dual-pricing-totals");
const dualCashAmountEl = mustFind<HTMLElement>("#dual-cash-amount");
const dualCardAmountEl = mustFind<HTMLElement>("#dual-card-amount");
const dualCardNoteEl = mustFind<HTMLElement>("#dual-card-note");
// UX62 (docs/build-briefs/UX62-paid-ticket-totals.md): the persistent Paid
// row, directly under Total, and the Tips row directly under THAT (owner
// ruling, 2026-09-28: Paid excludes tip so it agrees with Total; Tips is
// its own line, shown only when captured tips are above zero); see
// renderPaidRow.
const paidRowEl = mustFind<HTMLElement>("#paid-row");
const paidAmountEl = mustFind<HTMLElement>("#paid-amount");
const paidCountEl = mustFind<HTMLElement>("#paid-count");
const tipsRowEl = mustFind<HTMLElement>("#tips-row");
const tipsAmountEl = mustFind<HTMLElement>("#tips-amount");
const chargedRowEl = mustFind<HTMLElement>("#charged-row");
// UX62-F1 (docs/build-briefs/UX62-F1-refunded-line.md): a third row,
// directly under Tips, shown only when a completed refund exists on the
// ticket. Owner ruling, 2026-09-28: "Separate Refunded line" -- Paid stays
// exactly as UX62 built it (the gross drinks-plus-tax amount charged,
// never netted for a refund); see renderPaidRow.
const refundedRowEl = mustFind<HTMLElement>("#refunded-row");
const refundedAmountEl = mustFind<HTMLElement>("#refunded-amount");
// Sixteenth amendment item 2: the "All paid. Close this ticket?" prompt.
const zeroDuePromptEl = mustFind<HTMLElement>("#zero-due-prompt");
const zeroDuePromptText = mustFind<HTMLElement>("#zero-due-prompt-text");
const zeroDueDismissButton = mustFind<HTMLButtonElement>("#zero-due-dismiss");
const zeroDueCloseButton = mustFind<HTMLButtonElement>("#zero-due-close");
const ticketActionsEl = mustFind<HTMLElement>("#ticket-actions");
const prepareAllButton = mustFind<HTMLButtonElement>("#prepare-all");
const payButton = mustFind<HTMLButtonElement>("#pay-button");
// A3, ANSWERED 2026-08-29: the floor's explicit no-tip action. See
// handleNoTipClick below and index.html's own comment on this button.
const noTipButton = mustFind<HTMLButtonElement>("#no-tip-button");
const payBlockReason = mustFind<HTMLElement>("#pay-block-reason");
const payFailEl = mustFind<HTMLElement>("#pay-fail");
const payFailText = mustFind<HTMLElement>("#pay-fail-text");
const payRetryButton = mustFind<HTMLButtonElement>("#pay-retry-button");
const payStatusEl = mustFind<HTMLElement>("#pay-status");
// Design/QA gate LOW correction, 2026-08-21: the retry affordance after a
// network-failed share collection. See index.html's own comment on this
// button and handlePayStatusRetryClick below.
const payStatusRetryButton = mustFind<HTMLButtonElement>("#pay-status-retry-button");
const payReaderCancelButton = mustFind<HTMLButtonElement>("#pay-reader-cancel-button");
const payCheckButton = mustFind<HTMLButtonElement>("#pay-check-button");
// Design/QA gate finding F: per-maker tip split, post-payment.
const payTipSplitEl = mustFind<HTMLElement>("#pay-tip-split");
const debugApiBase = mustFind<HTMLElement>('[data-testid="debug-api-base"]');
const debugTenant = mustFind<HTMLElement>('[data-testid="debug-tenant"]');
const debugEmployee = mustFind<HTMLElement>('[data-testid="debug-employee"]');
const debugOrderId = mustFind<HTMLElement>('[data-testid="debug-order-id"]');
const debugTaxRate = mustFind<HTMLElement>('[data-testid="debug-tax-rate"]');
const debugMatchMisses = mustFind<HTMLElement>('[data-testid="debug-match-misses"]');
// T6: the pay-key population counter.
const debugPayKeyCount = mustFind<HTMLElement>('[data-testid="debug-pay-key-count"]');
// PAY7 Section 3 test 9: the current ticket's own "whole" target key,
// test-only.
const debugPayKeyCurrent = mustFind<HTMLElement>('[data-testid="debug-pay-key-current"]');
const debugLastPayCloseError = mustFind<HTMLElement>('[data-testid="debug-last-pay-close-error"]');
// UX71 (docs/backlog.md): the debug panel carries session-internal
// identifiers (API base, tenant id, employee id) that no staff screen
// should show outside local development. Hidden by the ACTUAL page
// hostname, not a build flag, since the identical bundle is served on
// stage0, production, and a local/e2e run alike; see localEnvironment.ts
// for the exact loopback vocabulary and why every existing #debug-panel
// e2e assertion (all of which run against 127.0.0.1) is unaffected.
const debugPanel = mustFind<HTMLElement>("#debug-panel");
// UIFIX2 (SF-13): also hidden from staff unless diagnostics mode is on (localEnvironment.ts isDiagnosticsRequested).
// The elements stay in the page so every spec that reads their text keeps working.
function readStoredDiagnostics(): string | null {
  try {
    return window.localStorage.getItem("diagnostics");
  } catch {
    return null;
  }
}
const diagnosticsStored = readStoredDiagnostics();
const diagnosticsMode = isDiagnosticsRequested(window.location.search, diagnosticsStored);
debugPanel.hidden = !(isLocalDevelopmentHost(window.location.hostname) && diagnosticsMode);
const liquorScrim = mustFind<HTMLElement>("#liquor-scrim");
const liquorSheetTitle = mustFind<HTMLElement>("#liquor-sheet-title");
const liquorSheetSubtitle = mustFind<HTMLElement>("#liquor-sheet-subtitle");
const liquorSheetClose = mustFind<HTMLButtonElement>("#liquor-sheet-close");
// VOC1-F4 (Section 2.5): toggled hidden entirely in "modifiers-only" mode
// (openModifiersOnlySheet), a drink with no liquor slot that still carries
// at least one attached modifier -- this same sheet is reused rather than
// a new scrim (check:chrome-ratchet), so these two liquor-only affordances
// have to be hideable independently of the rest of the sheet.
const liquorRequiredFlag = mustFind<HTMLElement>("#liquor-required-flag");
const liquorSearchRow = mustFind<HTMLElement>("#liquor-search-row");
const liquorSearchLine = mustFind<HTMLElement>("#liquor-search-line");
const liquorSearch = mustFind<HTMLInputElement>("#liquor-search");
const liquorRecentEl = mustFind<HTMLElement>("#liquor-recent");
const liquorRecentChipsEl = mustFind<HTMLElement>("#liquor-recent-chips");
const liquorList = mustFind<HTMLElement>("#liquor-list");
const liquorPricePreview = mustFind<HTMLElement>("#liquor-price-preview");
const liquorPriceBreakdown = mustFind<HTMLElement>("#liquor-price-breakdown");
// VOC1-F7 Slice 2 (Section 2.5): the pour-size chip row, between the price
// breakdown and Add to Ticket.
const liquorPourSizeRow = mustFind<HTMLElement>("#liquor-pour-size-row");
// VOC1-F4 (Section 2.5): the modifier chip row, between the pour-size row
// and Add to Ticket.
const liquorModifierRow = mustFind<HTMLElement>("#liquor-modifier-row");
const liquorAddButton = mustFind<HTMLButtonElement>("#liquor-add");
const moreSpiritsButton = mustFind<HTMLButtonElement>("#more-spirits");
const substitutionNoteEl = mustFind<HTMLElement>("#substitution-note");
const toastEl = mustFind<HTMLElement>("#toast");
// STK1 (DEC-269): the 86 list's own DOM. See the STK1 block below.
const stock86OpenButton = mustFind<HTMLButtonElement>("#stock86-open-button");
const menu86Button = mustFind<HTMLButtonElement>("#menu-86-button");
const stock86CountEl = mustFind<HTMLElement>('[data-testid="stock86-count"]');
const stock86Backdrop = mustFind<HTMLElement>("#stock86-backdrop");
const stock86DoneButton = mustFind<HTMLButtonElement>("#stock86-done");
const stock86OutCountEl = mustFind<HTMLElement>("#stock86-out-count");
const stock86OutListEl = mustFind<HTMLElement>("#stock86-out-list");
const stock86OosCountEl = mustFind<HTMLElement>("#stock86-oos-count");
const stock86OosListEl = mustFind<HTMLElement>("#stock86-oos-list");
const stock86SearchInput = mustFind<HTMLInputElement>("#stock86-search");
const stock86ChipsEl = mustFind<HTMLElement>("#stock86-chips");
const stock86CandidateListEl = mustFind<HTMLElement>("#stock86-candidate-list");
const stock86ToastEl = mustFind<HTMLElement>("#stock86-toast");
const stock86ToastText = mustFind<HTMLElement>("#stock86-toast-text");
const stock86ToastUndo = mustFind<HTMLButtonElement>("#stock86-toast-undo");

// ONB4 slice 1: the setup wizard screen (ui/src/setupWizard.ts owns its contents).
const setupWizardScreen = mustFind<HTMLElement>("#setup-wizard-screen");
// ALPHA-07 Open Tabs screen.
const newTicketNameInput = mustFind<HTMLInputElement>("#new-ticket-name");
// ONB2 S3: the first-sign-in "Set up this location" banner on Open Tabs.
const firstDayBanner = mustFind<HTMLElement>("#first-day-banner");
const firstDayBannerText = mustFind<HTMLElement>("#first-day-banner-text");
const settingsSetupOpen = mustFind<HTMLButtonElement>("#admin-setup-open");
const firstDayBannerOpen = mustFind<HTMLButtonElement>("#first-day-banner-open");
const firstDayBannerDismiss = mustFind<HTMLButtonElement>("#first-day-banner-dismiss");
// CARDNUDGE1: the card at the top of Open Tabs that says how to finish card payments.
const cardNudge = mountCardNudge(mustFind<HTMLElement>("#card-nudge"), {
  apiBaseUrl: () => requireSession().apiBaseUrl,
  tenantId: () => requireSession().tenantId,
  canShow: () =>
    state.session !== undefined &&
    hasPermission(state.session, "admin.terminal.view") &&
    hasPermission(state.session, "admin.payments.onboard"),
  isCurrent: () => state.session !== undefined,
  beforeOpen: () => {
    // Home sends the browser, so the return belongs to Settings, not the wizard (same as the Settings button). Cleared
    // before the Stripe tab opens so the new tab does not inherit a wizard marker.
    clearWizardStripeReturnMarker(window.sessionStorage);
  },
  prepareMissingStep: async () => {
    if (state.session !== undefined) await admin.readCardPaymentsPrerequisites(state.session);
  },
  missingStep: () => {
    const step = admin.cardPaymentsMissingStep();
    if (step === null) return null;
    return {
      ...step,
      open: () => {
        showAdminScreen();
        step.open();
      },
    };
  },
});
// True from a sign-in that required a new PIN (the account's first sign-in) until Open Tabs has shown for it. Session
// state only, never stored: a returning sign-in finds it false.
let firstSignInSetupPending = false;
const newTicketButton = mustFind<HTMLButtonElement>("#new-ticket");
const openTabsListEl = mustFind<HTMLElement>("#open-tabs-list");
const recentlyClosedSection = mustFind<HTMLElement>("#recently-closed-section");
const recentlyClosedListEl = mustFind<HTMLElement>("#recently-closed-list");

// TM1 (docs/backlog.md; DEC-230, owner ruling "merge only"). Open Tabs
// merge-selection mode chrome.
const tabMergeToggleButton = mustFind<HTMLButtonElement>("#tab-merge-toggle");
// TAB1 (DEC-270): the Open Tabs find row.
const tabFindEl = mustFind<HTMLElement>("#tab-find");
const tabFilterAllButton = mustFind<HTMLButtonElement>("#tab-filter-all");
const tabFilterMineButton = mustFind<HTMLButtonElement>("#tab-filter-mine");
const tabFilterAllCountEl = mustFind<HTMLElement>("#tab-filter-all-count");
const tabFilterMineCountEl = mustFind<HTMLElement>("#tab-filter-mine-count");
// TAB3: the status and age chips.
const tabChipUnpaidButton = mustFind<HTMLButtonElement>("#tab-chip-unpaid");
const tabChipPaidButton = mustFind<HTMLButtonElement>("#tab-chip-paid");
const tabChipAwaitingButton = mustFind<HTMLButtonElement>("#tab-chip-awaiting");
const tabChipAwaitingCountEl = mustFind<HTMLElement>("#tab-chip-awaiting-count");
const tabChipOver1HrButton = mustFind<HTMLButtonElement>("#tab-chip-over1hr");
const tabChipUnpaidCountEl = mustFind<HTMLElement>("#tab-chip-unpaid-count");
const tabChipPaidCountEl = mustFind<HTMLElement>("#tab-chip-paid-count");
const tabChipOver1HrCountEl = mustFind<HTMLElement>("#tab-chip-over1hr-count");
const tabSearchInput = mustFind<HTMLInputElement>("#tab-search");
const tabSearchClearButton = mustFind<HTMLButtonElement>("#tab-search-clear");
const tabFindEmptyEl = mustFind<HTMLElement>("#tab-find-empty");
const tabMergeModeBanner = mustFind<HTMLElement>("#tab-merge-mode-banner");
const tabMergeModeCancelButton = mustFind<HTMLButtonElement>("#tab-merge-mode-cancel");
const tabMergeActionBar = mustFind<HTMLElement>("#tab-merge-action-bar");
const tabMergeActionBarSummary = mustFind<HTMLElement>("#tab-merge-action-bar-summary");
const tabMergeContinueButton = mustFind<HTMLButtonElement>("#tab-merge-continue");
// GATE-CLEANUP-1: the confirm step no longer has its own scrim/sheet; see
// openTabMergeConfirmSheet below, which drives the shared openChoiceSheet
// (#choice-scrim) instead.

// ALPHA-10 item 3 / ALPHA-11 manager settings screen. Screen-chrome DOM
// refs only (visibility, the shared topbar handles this screen's own
// tenant label/back control like every other screen since UX17); every
// element inside the screen's three sections is owned by admin.ts.
const adminScreen = mustFind<HTMLElement>("#admin-screen");

// UX18 (docs/build-briefs/UX18-manager-surfaces-to-admin.md; DEC-061):
// Closeout Summary and the Manager Exceptions REVIEW surface moved here,
// from the floor's own topbar, onto Settings > Operations. The
// in-the-moment approval prompt (#approval-picker-screen,
// #pin-entry-screen) is untouched and stays on the floor; see this
// item's own build brief for why conflating the two would break service.
const adminOpenCloseoutSummaryButton = mustFind<HTMLButtonElement>("#admin-open-closeout-summary");
// M12: the Void History button, same Reports and reviews block, same
// "own gate, hidden outright" shape as the Closeout Summary button
// immediately above.
const adminOpenVoidHistoryButton = mustFind<HTMLButtonElement>("#admin-open-void-history");
const adminOpenManagerExceptionsButton = mustFind<HTMLButtonElement>(
  "#admin-open-manager-exceptions",
);
const adminManagerExceptionsBadgeCount = mustFind<HTMLElement>(
  "#admin-manager-exceptions-badge-count",
);

// ALPHA-07 pay confirm sheet.
const payConfirmScrim = mustFind<HTMLElement>("#pay-confirm-scrim");
const payConfirmClose = mustFind<HTMLButtonElement>("#pay-confirm-close");
const payConfirmTotal = mustFind<HTMLElement>("#pay-confirm-total");
// Design/QA gate finding D: "what is being charged now" list.
const payConfirmChargingNow = mustFind<HTMLElement>("#pay-confirm-charging-now");
const payConfirmChargingNowList = mustFind<HTMLElement>("#pay-confirm-charging-now-list");
const payConfirmSubtotal = mustFind<HTMLElement>("#pay-confirm-subtotal");
const payConfirmTax = mustFind<HTMLElement>("#pay-confirm-tax");
// DUALPRICE1: the Card price row and the note for a way of paying it is not built for.
const payConfirmCardPriceRow = mustFind<HTMLElement>("#pay-confirm-card-price-row");
const payConfirmCardPrice = mustFind<HTMLElement>("#pay-confirm-card-price");
const payConfirmDualNote = mustFind<HTMLElement>("#pay-confirm-dual-note");
const payConfirmTip = mustFind<HTMLElement>("#pay-confirm-tip");
// CASH1-S2-U1: the Card or Cash choice, and the parts of the sheet Cash hides.
const payTenderRow = mustFind<HTMLElement>("#pay-tender-row");
const payTenderCardButton = mustFind<HTMLButtonElement>("#pay-tender-card");
const payTenderCashButton = mustFind<HTMLButtonElement>("#pay-tender-cash");
const payTipSection = mustFind<HTMLElement>("#tip-section");
const payConfirmTipRow = mustFind<HTMLElement>("#pay-confirm-tip-row");
const payCancelButton = mustFind<HTMLButtonElement>("#pay-cancel-button");
const payConfirmButton = mustFind<HTMLButtonElement>("#pay-confirm-button");
const payReaderCartStatus = mustFind<HTMLElement>("#pay-reader-cart-status");
const payConfirmBadge = mustFind<HTMLElement>("#pay-confirm-badge");
// ALPHA-12: which pay mode this Confirm applies to.
const payConfirmScope = mustFind<HTMLElement>("#pay-confirm-scope");
// UX25 / DEC-167: reaches Split equally from a 0-or-1-guest ticket with no
// guest naming required, since the mode picker never appears for it.
const payConfirmSplitButton = mustFind<HTMLButtonElement>("#pay-confirm-split-button");

// Payments-track Slice 3: "Use saved card" and its management sheet.
const payConfirmCardRow = mustFind<HTMLButtonElement>("#pay-confirm-card-row");
const payConfirmCardValue = mustFind<HTMLElement>("#pay-confirm-card-value");
const payConfirmManageCardsLink = mustFind<HTMLButtonElement>("#pay-confirm-manage-cards");
const paySaveClientRow = mustFind<HTMLElement>("#pay-save-client-row");
const paySaveClientCheckbox = mustFind<HTMLInputElement>("#pay-save-client-checkbox");
// TERMREADER1 (DEC-487): the card reader a card payment will use, the Switch reader link, and the question sheet.
const payConfirmReaderRow = mustFind<HTMLElement>("#pay-confirm-reader-row");
const payConfirmReaderName = mustFind<HTMLElement>("#pay-confirm-reader-name");
const payConfirmSwitchReaderButton = mustFind<HTMLButtonElement>("#pay-confirm-switch-reader");
// Built on first use, not in the markup: the chrome ratchet (ADR-004 Decision 5) refuses a new scrim copy.
const cardReaderAskScrim = document.createElement("div");
cardReaderAskScrim.className = "scrim scrim-top";
cardReaderAskScrim.id = "card-reader-ask-scrim";
cardReaderAskScrim.hidden = true;
const cardReaderAskTitle = document.createElement("span");
cardReaderAskTitle.className = "sheet-title";
cardReaderAskTitle.id = "card-reader-ask-title";
const cardReaderAskClose = document.createElement("button");
cardReaderAskClose.type = "button";
cardReaderAskClose.className = "btn-icon";
cardReaderAskClose.id = "card-reader-ask-close";
cardReaderAskClose.dataset["testid"] = "card-reader-ask-close";
cardReaderAskClose.setAttribute("aria-label", "Close");
cardReaderAskClose.textContent = "\u2715";
const cardReaderAskLine = document.createElement("p");
cardReaderAskLine.id = "card-reader-ask-line";
cardReaderAskLine.dataset["testid"] = "card-reader-ask-line";
cardReaderAskLine.setAttribute("role", "status");
const cardReaderAskList = document.createElement("div");
cardReaderAskList.id = "card-reader-ask-list";
cardReaderAskList.dataset["testid"] = "card-reader-ask-list";
{
  const sheet = document.createElement("div");
  sheet.className = "sheet";
  sheet.id = "card-reader-ask-sheet";
  sheet.dataset["testid"] = "card-reader-ask-sheet";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");
  sheet.setAttribute("aria-labelledby", "card-reader-ask-title");
  const header = document.createElement("div");
  header.className = "sheet-header";
  const titleRow = document.createElement("div");
  titleRow.className = "sheet-title-row";
  titleRow.append(cardReaderAskTitle, cardReaderAskClose);
  header.append(titleRow);
  const body = document.createElement("div");
  body.className = "sheet-body";
  body.append(cardReaderAskLine, cardReaderAskList);
  sheet.append(header, body);
  cardReaderAskScrim.append(sheet);
  document.body.append(cardReaderAskScrim);
}
const cardConsentScrim = mustFind<HTMLElement>("#card-consent-scrim");
const cardConsentGuest = mustFind<HTMLElement>("#card-consent-guest");
const cardConsentAckRow = mustFind<HTMLElement>("#card-consent-ack-row");
const cardConsentAckCheckbox = mustFind<HTMLInputElement>("#card-consent-ack-checkbox");
const cardConsentSaveButton = mustFind<HTMLButtonElement>("#card-consent-save");
const cardConsentDeclineButton = mustFind<HTMLButtonElement>("#card-consent-decline");

// SIG1 (docs/build-briefs/SIG1-signature-and-receipt.md): touch-drawn
// signature plus DEC-016's printed/emailed/none receipt choice, shown at
// the moment a comp/Walkout-settled ticket closes via the no-tip action.
const signatureReceiptScrim = mustFind<HTMLElement>("#signature-receipt-scrim");
const signatureReceiptCloseButton = mustFind<HTMLButtonElement>("#signature-receipt-close");
const signatureStepEl = mustFind<HTMLElement>("#signature-step");
const signatureCanvas = mustFind<HTMLCanvasElement>("#signature-canvas");
const signatureClearButton = mustFind<HTMLButtonElement>("#signature-clear");
const signatureContinueButton = mustFind<HTMLButtonElement>("#signature-continue");
const receiptChoiceStepEl = mustFind<HTMLElement>("#receipt-choice-step");
const signatureReceiptTitleEl = mustFind<HTMLElement>("#signature-receipt-title");
const receiptChoicePrintedButton = mustFind<HTMLButtonElement>("#receipt-choice-printed");
const receiptChoiceEmailedButton = mustFind<HTMLButtonElement>("#receipt-choice-emailed");
const receiptChoiceNoneButton = mustFind<HTMLButtonElement>("#receipt-choice-none");
const receiptEmailRow = mustFind<HTMLElement>("#receipt-email-row");
const receiptEmailInput = mustFind<HTMLInputElement>("#receipt-email-input");
const receiptEmailSendButton = mustFind<HTMLButtonElement>("#receipt-email-send");
const clientReceiptEmail = mountClientReceiptEmail({
  session: () => state.session,
  payer: () => pendingSignatureReceiptOrderId === undefined ? undefined : {
    orderId: pendingSignatureReceiptOrderId,
    ...(pendingReceiptPaymentAttemptId === undefined ? {} : {paymentAttemptId:pendingReceiptPaymentAttemptId}),
  },
  updated: async () => {
    const active = state.session;const orderId = pendingSignatureReceiptOrderId;const paymentId = pendingReceiptPaymentAttemptId;
    if(active === undefined || orderId === undefined)return;
    const reply = await getReceiptOptions(active.apiBaseUrl,active.tenantId,orderId,paymentId);
    if(state.session?.token !== active.token || pendingSignatureReceiptOrderId !== orderId || pendingReceiptPaymentAttemptId !== paymentId)return;
    applyReceiptOptions(reply.status === 200 ? reply.body : undefined);
  },
}, receiptEmailInput, receiptEmailSendButton);
const cardConsentCloseButton = mustFind<HTMLButtonElement>("#card-consent-close");
const savedCardsScrim = mustFind<HTMLElement>("#saved-cards-scrim");
const savedCardsList = mustFind<HTMLElement>("#saved-cards-list");
const savedCardsCloseButton = mustFind<HTMLButtonElement>("#saved-cards-close");
const ticketHoldBadge = mustFind<HTMLElement>("#ticket-hold-badge");
// RCPT1-S3: the Awaiting tip ticket.
const ticketAwaitingBadge = mustFind<HTMLElement>("#ticket-awaiting-badge");
const authorizedRowEl = mustFind<HTMLElement>("#authorized-row");
const authorizedAmountEl = mustFind<HTMLElement>("#authorized-amount");
const authorizedCardEl = mustFind<HTMLElement>("#authorized-card");
const awaitingTipRowEl = mustFind<HTMLElement>("#awaiting-tip-row");
const awaitingTipActionsEl = mustFind<HTMLElement>("#awaiting-tip-actions");
const awaitingTipReprintButton = mustFind<HTMLButtonElement>("#awaiting-tip-reprint");
const awaitingTipEnterButton = mustFind<HTMLButtonElement>("#awaiting-tip-enter");
const awaitingTipStatusEl = mustFind<HTMLElement>("#awaiting-tip-status");
const actionRowPrimaryEl = mustFind<HTMLElement>(
  "#ticket-actions .action-row-primary:not(#awaiting-tip-actions)",
);
const payConfirmReceiptNote = mustFind<HTMLElement>("#pay-confirm-receipt-note");
// UX39 (docs/backlog.md; docs/build-briefs/UX39-ticket-header-more-
// button.md, DEC-195): replaces the four standalone header chips this
// file used to carry here (Discount, Payments, Manage saved cards, Keep
// tab open -- #ticket-discount-button/#payments-button/#ticket-rail-
// manage-cards-button/#keep-tab-open-button, all removed from index.html)
// with one 44px icon button opening a choice sheet built fresh at tap
// time. See renderTicketMoreButton/handleTicketMoreClick below.
const ticketMoreButton = mustFind<HTMLButtonElement>("#btn-ticket-more");

// ALPHA-12: the reusable choice sheet (pay-mode picker, "pay my drinks"
// guest picker, add-drink guest picker, split-progress list).
const choiceScrim = mustFind<HTMLElement>("#choice-scrim");
const choiceTitle = mustFind<HTMLElement>("#choice-title");
const choiceSubtitle = mustFind<HTMLElement>("#choice-subtitle");
const choiceNote = mustFind<HTMLElement>("#choice-note");
const choiceError = mustFind<HTMLElement>("#choice-error");
const choiceList = mustFind<HTMLElement>("#choice-list");
const choiceClose = mustFind<HTMLButtonElement>("#choice-close");
const choiceCancelButton = mustFind<HTMLButtonElement>("#choice-cancel");
// GATE4 follow-up: the informational-mode note and paired footer actions.
// See index.html's own #choice-info-note/#choice-footer-actions comment.
const choiceInfoNote = mustFind<HTMLElement>("#choice-info-note");
const choiceFooterDefault = mustFind<HTMLElement>("#choice-footer-default");
const choiceFooterActions = mustFind<HTMLElement>("#choice-footer-actions");
const choiceActionsCancelButton = mustFind<HTMLButtonElement>("#choice-actions-cancel");
// AU14-F4 / DEC-276: the required-reason sheet, a second sheet inside
// #choice-scrim (see index.html's own comment on #reason-sheet).
const choiceSheetEl = mustFind<HTMLElement>("#choice-sheet");
const reasonSheetEl = mustFind<HTMLElement>("#reason-sheet");
// CL6-F1-F3 (DEC-286): the floor's "Card payments are turned off" sheet, a third
// sheet inside #choice-scrim (see index.html's own comment on #business-day-sheet).
const businessDaySheetEl = mustFind<HTMLElement>("#business-day-sheet");
const businessDaySheetTitleEl = mustFind<HTMLElement>("#business-day-sheet-title");
const businessDaySheetBodyEl = mustFind<HTMLElement>("#business-day-sheet-body");
const businessDaySheetSafeEl = mustFind<HTMLElement>("#business-day-sheet-safe-text");
const businessDaySheetNextEl = mustFind<HTMLElement>("#business-day-sheet-next");
const businessDaySheetSettingsButton = mustFind<HTMLButtonElement>("#business-day-sheet-settings");
const businessDaySheetOkButton = mustFind<HTMLButtonElement>("#business-day-sheet-ok");
const reasonTitleEl = mustFind<HTMLElement>("#reason-title");
const reasonSubtitleEl = mustFind<HTMLElement>("#reason-subtitle");
const reasonContextEl = mustFind<HTMLElement>("#reason-context");
const reasonExtraEl = mustFind<HTMLElement>("#reason-extra");
const reasonExtraActionButton = mustFind<HTMLButtonElement>("#reason-extra-action");
const reasonExtraNoteEl = mustFind<HTMLElement>("#reason-extra-note");
const reasonContextTitleEl = mustFind<HTMLElement>("#reason-context-title");
const reasonContextDetailEl = mustFind<HTMLElement>("#reason-context-detail");
const reasonContextChangeEl = mustFind<HTMLElement>("#reason-context-change");
const reasonHeadingEl = mustFind<HTMLElement>("#reason-heading");
const reasonStateEl = mustFind<HTMLElement>("#reason-state");
const reasonTilesEl = mustFind<HTMLElement>("#reason-tiles");
const reasonBlockedEl = mustFind<HTMLElement>("#reason-blocked");
const reasonNoteEl = mustFind<HTMLElement>("#reason-note");
const reasonCloseButton = mustFind<HTMLButtonElement>("#reason-close");
const reasonCancelButton = mustFind<HTMLButtonElement>("#reason-cancel");
const reasonConfirmButton = mustFind<HTMLButtonElement>("#reason-confirm");
const ticketReopenStripEl = mustFind<HTMLElement>("#ticket-reopen-strip");
const closedReceiptEl = mustFind<HTMLElement>("#closed-receipt");
const ticketIdRowEl = mustFind<HTMLElement>("#ticket-id-row");
const closeoutActionsWithReasonEl = mustFind<HTMLElement>("#closeout-actions-with-reason");
const choicePrimaryActionButton = mustFind<HTMLButtonElement>("#choice-primary-action");

// ALPHA-12 equal-split setup (share-count stepper).
const splitSetupScrim = mustFind<HTMLElement>("#split-setup-scrim");
const splitSetupClose = mustFind<HTMLButtonElement>("#split-setup-close");
const splitSetupDecrementButton = mustFind<HTMLButtonElement>("#split-setup-decrement");
const splitSetupIncrementButton = mustFind<HTMLButtonElement>("#split-setup-increment");
const splitSetupCountEl = mustFind<HTMLElement>("#split-setup-count");
const splitSetupPreviewEl = mustFind<HTMLElement>("#split-setup-preview");
const splitSetupCancelButton = mustFind<HTMLButtonElement>("#split-setup-cancel");
const splitSetupStartButton = mustFind<HTMLButtonElement>("#split-setup-start");

// SPLIT1 By amount pad (index.html's #amount-pad-scrim) and the rail's Balance row.
// A fourth sheet inside #choice-scrim (check:chrome-ratchet refuses a new scrim), beside the reason and business day sheets.
const amountPadSheetEl = mustFind<HTMLElement>("#amount-pad-sheet");
const amountPadClose = mustFind<HTMLButtonElement>("#amount-pad-close");
const amountPadCancelButton = mustFind<HTMLButtonElement>("#amount-pad-cancel");
const amountPadContinueButton = mustFind<HTMLButtonElement>("#amount-pad-continue");
const amountPadTitleEl = mustFind<HTMLElement>("#amount-pad-title");
const amountPadBalanceLabelEl = mustFind<HTMLElement>("#amount-pad-balance-label");
const amountPadFieldEl = mustFind<HTMLElement>("#amount-pad-field");
const amountPadOverEl = mustFind<HTMLElement>("#amount-pad-over");
const amountPadBalanceEl = mustFind<HTMLElement>("#amount-pad-balance");
const amountPadChipsEl = mustFind<HTMLElement>("#amount-pad-chips");
const balanceRowEl = mustFind<HTMLElement>("#balance-row");
const balanceAmountEl = mustFind<HTMLElement>("#balance-amount");

// ALPHA-12: name a guest on the ticket. MyTime track (track/mytime)
// Slice 2 adds client search, the new-client capture panel, and the
// freshness line; the free-text fallback (addGuestLabelInput/
// addGuestCancelButton/addGuestSaveButton) is unchanged.
const addGuestScrim = mustFind<HTMLElement>("#add-guest-scrim");
const addGuestClose = mustFind<HTMLButtonElement>("#add-guest-close");
const addGuestLabelInput = mustFind<HTMLInputElement>("#add-guest-label-input");
const addGuestLabelCounterEl = mustFind<HTMLElement>("#add-guest-label-counter");
const addGuestCancelButton = mustFind<HTMLButtonElement>("#add-guest-cancel");
const addGuestSaveButton = mustFind<HTMLButtonElement>("#add-guest-save");
const addGuestFreshnessEl = mustFind<HTMLElement>("#add-guest-freshness");
const addGuestSearchStateEl = mustFind<HTMLElement>("#add-guest-search-state");
const addGuestSearchInput = mustFind<HTMLInputElement>("#add-guest-search-input");
const addGuestResultsEl = mustFind<HTMLElement>("#add-guest-results");
const addGuestNewClientPromptButton = mustFind<HTMLButtonElement>("#add-guest-new-client-prompt");
const addGuestNewClientPanelEl = mustFind<HTMLElement>("#add-guest-new-client-panel");
const addGuestNewClientNameInput = mustFind<HTMLInputElement>("#add-guest-new-client-name-input");
const addGuestNewClientPhoneInput = mustFind<HTMLInputElement>("#add-guest-new-client-phone-input");
const addGuestNewClientBackButton = mustFind<HTMLButtonElement>("#add-guest-new-client-back");
const addGuestNewClientSaveButton = mustFind<HTMLButtonElement>("#add-guest-new-client-save");
const addGuestFreetextDividerEl = mustFind<HTMLElement>("#add-guest-freetext-divider");
const addGuestFreetextRowEl = mustFind<HTMLElement>("#add-guest-freetext-row");

// ALPHA-08 tips.
const tipPresetRow = mustFind<HTMLElement>("#tip-preset-row");
const tipBaseNote = mustFind<HTMLElement>("#tip-base-note");
const tipSyncStatus = mustFind<HTMLElement>("#tip-sync-status");
const tipSyncText = mustFind<HTMLElement>("#tip-sync-text");
const tipSyncRetry = mustFind<HTMLButtonElement>("#tip-sync-retry");
const tipCustomInput = mustFind<HTMLInputElement>("#tip-custom-input");
const tipCustomApplyButton = mustFind<HTMLButtonElement>("#tip-custom-apply");
const tipNoneButton = mustFind<HTMLButtonElement>("#tip-none-button");

// ALPHA-08 pay warning (product owner ruling, 2026-08-20 third amendment).
const payWarningBanner = mustFind<HTMLElement>("#pay-warning-banner");
const payWarningPrior = mustFind<HTMLElement>("#pay-warning-prior");
const payWarningAckRow = mustFind<HTMLElement>("#pay-warning-ack-row");
const payWarningAckCheckbox = mustFind<HTMLInputElement>("#pay-warning-ack-checkbox");
// Design/QA gate finding C: fail-closed banner and retry affordance for a
// failed payment-history read.
const payWarningErrorBanner = mustFind<HTMLElement>("#pay-warning-error-banner");
const payWarningRetryButton = mustFind<HTMLButtonElement>("#pay-warning-retry-button");
// Amendment 2026-08-22 (fifth), item 1: the neutral, non-alarming
// counterpart to payWarningBanner, shown instead of it (never together)
// for an ordinary in-session sequential guest payment.
const payWarningNote = mustFind<HTMLElement>("#pay-warning-note");

// ALPHA-08 line-edit sheet (remove / change liquor).
const lineEditScrim = mustFind<HTMLElement>("#line-edit-scrim");
const lineEditClose = mustFind<HTMLButtonElement>("#line-edit-close");
const lineEditSubtitle = mustFind<HTMLElement>("#line-edit-subtitle");
// VOC1-F8 build-time amendment, dated 2026-09-22: the ticket line's own
// read-only recipe control, moved here from a fourth row button (see
// buildTicketLineRow's own comment for why).
const lineEditViewRecipeButton = mustFind<HTMLButtonElement>("#line-edit-view-recipe");
const lineEditChangeLiquorButton = mustFind<HTMLButtonElement>("#line-edit-change-liquor");
// ALPHA-12.
const lineEditChangeGuestButton = mustFind<HTMLButtonElement>("#line-edit-change-guest");
const lineEditMoveToTabButton = mustFind<HTMLButtonElement>("#line-edit-move-to-tab");
const lineEditRepeatButton = mustFind<HTMLButtonElement>("#line-edit-repeat");
const ticketRepeatButton = mustFind<HTMLButtonElement>("#btn-ticket-repeat");
const lineEditRemoveButton = mustFind<HTMLButtonElement>("#line-edit-remove");
const lineEditUnprepareButton = mustFind<HTMLButtonElement>("#line-edit-unprepare");
const lineEditPreparedNote = mustFind<HTMLElement>("#line-edit-prepared-note");

// M4 (DEC-035): the refund flow's own sheet. Its entry point used to be
// the standalone #payments-button; UX39 folded that into #btn-ticket-more
// (ticketMoreButton above).
const refundScrim = mustFind<HTMLElement>("#refund-scrim");
// REFUNDALL1: the one-sheet whole-ticket refund and its header button.
const ticketRefundButton = mustFind<HTMLButtonElement>("#btn-ticket-refund");
// One shared overlay (#refund-scrim) holds both refund sheets, one visible at a time.
const refundTicketScrim = refundScrim;
const refundDrinksSheetEl = mustFind<HTMLElement>("#refund-sheet");
const refundTicketSheetEl = mustFind<HTMLElement>("#refund-ticket-sheet");
const refundTicketSubtitle = mustFind<HTMLElement>("#refund-ticket-subtitle");
const refundTicketCards = mustFind<HTMLElement>("#refund-ticket-cards");
const refundTicketTipNote = mustFind<HTMLElement>("#refund-ticket-tip-note");
const refundTicketTipList = mustFind<HTMLElement>("#refund-ticket-tip-list");
const refundTicketTipToggle = mustFind<HTMLButtonElement>("#refund-ticket-tip-toggle");
const refundTicketTipLabel = mustFind<HTMLElement>("#refund-ticket-tip-label");
const refundTicketTipSublabel = mustFind<HTMLElement>("#refund-ticket-tip-sublabel");
const refundTicketFallback = mustFind<HTMLElement>("#refund-ticket-fallback");
const refundTicketReasons = mustFind<HTMLElement>("#refund-ticket-reasons");
const refundTicketConfirm = mustFind<HTMLButtonElement>("#refund-ticket-confirm");
const refundTicketSome = mustFind<HTMLButtonElement>("#refund-ticket-some");
const refundTicketCancel = mustFind<HTMLButtonElement>("#refund-ticket-cancel");
const refundTicketClose = mustFind<HTMLButtonElement>("#refund-ticket-close");
const refundClose = mustFind<HTMLButtonElement>("#refund-close");
const refundSubtitle = mustFind<HTMLElement>("#refund-subtitle");
const refundLinesList = mustFind<HTMLElement>("#refund-lines-list");
const refundCardsNote = mustFind<HTMLElement>("#refund-cards-note");
const refundCardsList = mustFind<HTMLElement>("#refund-cards-list");
const refundTotalEl = mustFind<HTMLElement>("#refund-total");
const refundConfirmButton = mustFind<HTMLButtonElement>("#refund-confirm");
const refundCancelButton = mustFind<HTMLButtonElement>("#refund-cancel");

// PL1 (DEC-011 items 6 and 7): the card-not-present payment link. UX13
// merged its own trigger into #payments-button (handleTicketPaymentsChipClick);
// this scrim and its own controls (send/check/cancel) are unchanged.
const paymentLinkScrim = mustFind<HTMLElement>("#payment-link-scrim");
const paymentLinkClose = mustFind<HTMLButtonElement>("#payment-link-close");
const paymentLinkSubtitle = mustFind<HTMLElement>("#payment-link-subtitle");
const paymentLinkSendButton = mustFind<HTMLButtonElement>("#payment-link-send");
const paymentLinkActive = mustFind<HTMLElement>("#payment-link-active");
const paymentLinkStatusEl = mustFind<HTMLElement>("#payment-link-status");
const paymentLinkUrlEl = mustFind<HTMLElement>("#payment-link-url");
const paymentLinkAutoNote = mustFind<HTMLElement>("#payment-link-auto-note");
const paymentLinkCheckButton = mustFind<HTMLButtonElement>("#payment-link-check");
const paymentLinkCancelButton = mustFind<HTMLButtonElement>("#payment-link-cancel");

// ALPHA-08 closeout summary screen. Its own topbar chrome (tenant label,
// back control) is the shared one since UX17; the entry point into this
// screen (adminOpenCloseoutSummaryButton) is declared above with the
// admin screen's own refs since UX18 moved it there.
const closeoutScreen = mustFind<HTMLElement>("#closeout-screen");
// UX116: the one screen the four function hubs share (ui/index.html #function-hub-screen).
const functionHubScreen = mustFind<HTMLElement>("#function-hub-screen");
// The sheet itself is a GLOBAL overlay (a DOM sibling of every screen
// section, like #toast), not nested inside #open-tabs-screen, so it opens
// identically whether tapped from the badge above or from the ticket
// banner's own View button without leaving whichever screen is current.
const managerExceptionsScrim = mustFind<HTMLElement>("#manager-exceptions-scrim");
const managerExceptionsCloseButton = mustFind<HTMLButtonElement>("#manager-exceptions-close");
const managerExceptionsEmpty = mustFind<HTMLElement>("#manager-exceptions-empty");
const managerExceptionsListEl = mustFind<HTMLElement>("#manager-exceptions-list");
const closeoutBusinessDateEl = mustFind<HTMLElement>("#closeout-business-date");
const closeoutLocationWrapEl = mustFind<HTMLElement>("#closeout-location-wrap");
const closeoutLocationSelectEl = mustFind<HTMLSelectElement>("#closeout-location");
// Design/QA gate finding H: freshness controls.
const closeoutLastUpdatedEl = mustFind<HTMLElement>("#closeout-last-updated");
const closeoutRefreshButton = mustFind<HTMLButtonElement>("#closeout-refresh");
const closeoutStaleNoteEl = mustFind<HTMLElement>("#closeout-stale-note");
// CL6: DEC-010's two named views, each its own grid under its own
// literal heading, plus day navigation, the finalize action, its
// confirmation sheet, and the finalized banner.
const closeoutCashEl = mustFind<HTMLElement>("#closeout-cash");
const closeoutAwaitingTipEl = mustFind<HTMLElement>("#closeout-awaiting-tip");
// CORE11 and CORE13.
const closeoutWaterfallEl = mustFind<HTMLElement>("#closeout-waterfall");
const closeoutWaterfallNoteEl = mustFind<HTMLElement>("#closeout-waterfall-note");
const closeoutSimulatedNoteEl = mustFind<HTMLElement>("#closeout-simulated-note");
const closeoutMoneyTakeawayEl = mustFind<HTMLElement>("#closeout-money-takeaway");
const closeoutSideStatsEl = mustFind<HTMLElement>("#closeout-side-stats");
const closeoutVerdictChipEl = mustFind<HTMLElement>("#closeout-verdict-chip");
const closeoutVerdictSymbolEl = mustFind<HTMLElement>("#closeout-verdict-symbol");
const closeoutVerdictChipTextEl = mustFind<HTMLElement>("#closeout-verdict-chip-text");
const closeoutVerdictTitleEl = mustFind<HTMLElement>("#closeout-verdict-title");
const closeoutVerdictHelpEl = mustFind<HTMLElement>("#closeout-verdict-help");
const closeoutExceptionsEl = mustFind<HTMLElement>("#closeout-exceptions");
const closeoutChecksEl = mustFind<HTMLElement>("#closeout-checks");
const closeoutPassedLineEl = mustFind<HTMLElement>("#closeout-passed-line");
const closeoutReopenedSectionEl = mustFind<HTMLElement>("#closeout-reopened-section");
const closeoutFinalizeAvailabilityEl = mustFind<HTMLElement>("#closeout-finalize-availability");
const closeoutPrintButton = mustFind<HTMLButtonElement>("#closeout-print");
const closeoutExportButton = mustFind<HTMLButtonElement>("#closeout-export");
const closeoutExportStatusEl = mustFind<HTMLElement>("#closeout-export-status");
const closeoutSpecialsEl = mustFind<HTMLElement>("#closeout-specials");
const closeoutPriceChangesEl = mustFind<HTMLElement>("#closeout-price-changes");
const closeoutPrevDayButton = mustFind<HTMLButtonElement>("#closeout-prev-day");
const closeoutNextDayButton = mustFind<HTMLButtonElement>("#closeout-next-day");
const closeoutNotFinalizedBannerEl = mustFind<HTMLElement>("#closeout-not-finalized-banner");
const closeoutFinalizedBannerEl = mustFind<HTMLElement>("#closeout-finalized-banner");
const closeoutFinalizedDetailEl = mustFind<HTMLElement>("#closeout-finalized-detail");
const closeoutCarriedOverEl = mustFind<HTMLElement>("#closeout-carried-over");
const closeoutFinalizeButton = mustFind<HTMLButtonElement>("#closeout-finalize-button");
const closeoutFinalizeScrim = mustFind<HTMLElement>("#closeout-finalize-scrim");
const closeoutFinalizeCloseButton = mustFind<HTMLButtonElement>("#closeout-finalize-close");
const closeoutFinalizeCancelButton = mustFind<HTMLButtonElement>("#closeout-finalize-cancel");
const closeoutFinalizeConfirmButton = mustFind<HTMLButtonElement>("#closeout-finalize-confirm");
const closeoutFinalizeTitleEl = mustFind<HTMLElement>("#closeout-finalize-title");
const closeoutFinalizeSummaryEl = mustFind<HTMLElement>("#closeout-finalize-summary");
const closeoutFinalizeCarryoverCountEl = mustFind<HTMLElement>(
  "#closeout-finalize-carryover-count",
);
const closeoutFinalizeCarryoverListEl = mustFind<HTMLElement>("#closeout-finalize-carryover-list");
const closeoutFinalizeSettlementEl = mustFind<HTMLElement>("#closeout-finalize-settlement");
const closeoutFinalizeSettlementListEl = mustFind<HTMLElement>(
  "#closeout-finalize-settlement-list",
);
const closeoutFinalizeSettlementReasonsEl = mustFind<HTMLElement>(
  "#closeout-finalize-settlement-reasons",
);
/** PAY1-S4-F2-F8-F5 slice 4 (PROVISIONAL): the difference this sheet shows and the reason picked for declaring it.
 * Undefined when the day has nothing to declare. */
let closeoutFinalizeDeclaration:
  | { capturedDiffCents: number; refundedDiffCents: number; reasonId: string | undefined }
  | undefined;
// Design/QA gate finding F: per-maker tips for the day.
const closeoutTipsByMakerCountEl = mustFind<HTMLElement>("#closeout-tips-by-maker-count");
const closeoutTipsByMakerListEl = mustFind<HTMLElement>("#closeout-tips-by-maker-list");
const closeoutReopenedCountEl = mustFind<HTMLElement>("#closeout-reopened-count");
const closeoutReopenedListEl = mustFind<HTMLElement>("#closeout-reopened-list");
const closeoutOpenTabsListEl = mustFind<HTMLElement>("#closeout-open-tabs-list");
const closeoutOpenTabsByBartenderEl = mustFind<HTMLElement>("#closeout-open-tabs-by-bartender");
const closeoutUnpriceableListEl = mustFind<HTMLElement>("#closeout-unpriceable-list");
// CL7: failed/unknown payments, terminal registration, and MyTime sync.
const closeoutFailedPaymentsListEl = mustFind<HTMLElement>("#closeout-failed-payments-list");
const closeoutPendingPaymentsListEl = mustFind<HTMLElement>("#closeout-pending-payments-list");
const closeoutOutsideCollectionsListEl = mustFind<HTMLElement>(
  "#closeout-outside-collections-list",
);
const closeoutSettlementSectionEl = mustFind<HTMLElement>("#closeout-settlement-section");
const closeoutSettlementStatusEl = mustFind<HTMLElement>("#closeout-settlement-status");
const closeoutSettlementListEl = mustFind<HTMLElement>("#closeout-settlement-list");
const closeoutTerminalStatusListEl = mustFind<HTMLElement>("#closeout-terminal-status-list");
const closeoutSyncStatusBodyEl = mustFind<HTMLElement>("#closeout-sync-status-body");

// M12 void-history screen. Sibling to #closeout-screen above, not a
// block inside it (DEC-101's "a surface of its own"), so it gets its own
// full set of refs rather than reusing any of the closeout ones, even
// though the day-navigation shape is identical.
const voidHistoryScreen = mustFind<HTMLElement>("#void-history-screen");
const voidHistoryBusinessDateEl = mustFind<HTMLElement>("#void-history-business-date");
const voidHistoryPrevDayButton = mustFind<HTMLButtonElement>("#void-history-prev-day");
const voidHistoryNextDayButton = mustFind<HTMLButtonElement>("#void-history-next-day");
const voidHistoryListEl = mustFind<HTMLElement>("#void-history-list");
const voidHistoryExportButton = mustFind<HTMLButtonElement>("#void-history-export");
const voidHistoryExportStatusEl = mustFind<HTMLElement>("#void-history-export-status");

// PO6-R1 (DEC-056): the three purchasing entry points, moved off Open
// Tabs' own topbar (UX15's merged control, gone) onto Settings >
// Purchasing. Their own visibility and click behavior are in
// refreshPurchasingAdminButtonsVisibility/the click listeners below.
const adminPurchasingOpenOrdersButton = mustFind<HTMLButtonElement>(
  "#admin-purchasing-open-orders",
);
const adminPurchasingOpenCartButton = mustFind<HTMLButtonElement>("#admin-purchasing-open-cart");
const adminPurchasingOpenOutsideOrderButton = mustFind<HTMLButtonElement>(
  "#admin-purchasing-open-outside-order",
);
// PO6 Slice 1 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S1-order-list-and-detail.md). Read only: the
// purchase order list and detail screen. PO6-R1 (DEC-056): reached from
// Settings > Purchasing's own #admin-purchasing-open-orders button,
// gated on purchasing.order.view.
const purchasingOrdersScreen = mustFind<HTMLElement>("#purchasing-orders-screen");
const purchasingOrdersListView = mustFind<HTMLElement>("#purchasing-orders-list-view");
const purchasingOrdersStatusEl = mustFind<HTMLElement>("#purchasing-orders-status");
const purchasingOrdersEmptyEl = mustFind<HTMLElement>("#purchasing-orders-empty");
const purchasingOrdersListEl = mustFind<HTMLElement>("#purchasing-orders-list");
const purchasingOrderDetailView = mustFind<HTMLElement>("#purchasing-order-detail-view");
const purchasingOrderDetailBackButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-back",
);
const purchasingOrderDetailVendorEl = mustFind<HTMLElement>("#purchasing-order-detail-vendor");
const purchasingOrderDetailStatusEl = mustFind<HTMLElement>("#purchasing-order-detail-status");
const purchasingOrderDetailStateEl = mustFind<HTMLElement>(
  '[data-testid="purchasing-order-detail-state"]',
);
const purchasingOrderDetailEstimatedTotalEl = mustFind<HTMLElement>(
  '[data-testid="purchasing-order-detail-estimated-total"]',
);
const purchasingOrderDetailLineCountEl = mustFind<HTMLElement>(
  '[data-testid="purchasing-order-detail-line-count"]',
);
const purchasingOrderDetailApprovalEl = mustFind<HTMLElement>(
  '[data-testid="purchasing-order-detail-approval"]',
);
const purchasingOrderDetailPlacedByEl = mustFind<HTMLElement>(
  '[data-testid="purchasing-order-detail-placed-by"]',
);
// PO3/TF4 (DEC-228). Document download actions.
const purchasingOrderDetailDownloadPdfButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-download-pdf",
);
const purchasingOrderDetailExportAbc2goButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-export-abc2go",
);
const purchasingOrderDetailReceiveButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-receive",
);
const purchasingOrderDetailAbc2goLinesEl = mustFind<HTMLElement>(
  "#purchasing-order-detail-abc2go-lines",
);
const purchasingOrderDetailAbc2goWarningEl = mustFind<HTMLElement>(
  "#purchasing-order-detail-abc2go-warning",
);
const purchasingOrderDetailDocumentsStatusEl = mustFind<HTMLElement>(
  "#purchasing-order-detail-documents-status",
);
const purchasingOrderDetailSendNoteEl = mustFind<HTMLElement>("#purchasing-order-detail-send-note");
// PO6-S2-F1: the order's own lines, projected only by the detail route.
const purchasingOrderDetailLinesEl = mustFind<HTMLElement>("#purchasing-order-detail-lines");
const purchasingOrderDetailStatusHistoryEl = mustFind<HTMLElement>(
  "#purchasing-order-detail-status-history",
);
// PO6 Slice 3 (docs/build-briefs/PO6-S3-approval.md). Approve, and the
// inline approver/PIN form it opens, both live directly on the order
// detail view -- an admin surface, so a select and a keyboard-entered
// PIN are legitimate here, unlike the floor's own digit pad.
const purchasingOrderDetailApproveButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-approve-button",
);
const purchasingOrderDetailApproveForm = mustFind<HTMLElement>(
  "#purchasing-order-detail-approve-form",
);
const purchasingOrderDetailApproveApproverSelect = mustFind<HTMLSelectElement>(
  "#purchasing-order-detail-approve-approver",
);
const purchasingOrderDetailApprovePinInput = mustFind<HTMLInputElement>(
  "#purchasing-order-detail-approve-pin",
);
const purchasingOrderDetailApproveSubmitButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-approve-submit",
);
const purchasingOrderDetailApproveCancelButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-approve-cancel",
);
const purchasingOrderDetailApproveStatusEl = mustFind<HTMLElement>(
  "#purchasing-order-detail-approve-status",
);

// PO6 Slice 5 (docs/build-briefs/PO6-S5-corrections-and-exceptions.md).
// Reporting and resolving this order's own `exception` state, both
// forms living directly on the order detail view, the same admin-
// surface posture Slice 3's own approve panel above established.
const purchasingOrderDetailExceptionReportButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-exception-report-button",
);
const purchasingOrderDetailExceptionReportForm = mustFind<HTMLElement>(
  "#purchasing-order-detail-exception-report-form",
);
const purchasingOrderDetailExceptionReasonPicker = mustFind<HTMLElement>(
  "#purchasing-order-detail-exception-reason-picker",
);
const purchasingOrderDetailExceptionNoteInput = mustFind<HTMLInputElement>(
  "#purchasing-order-detail-exception-note",
);
const purchasingOrderDetailExceptionReportSubmitButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-exception-report-submit",
);
const purchasingOrderDetailExceptionReportCancelButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-exception-report-cancel",
);
const purchasingOrderDetailExceptionReportStatusEl = mustFind<HTMLElement>(
  "#purchasing-order-detail-exception-report-status",
);
const purchasingOrderDetailExceptionCurrentEl = mustFind<HTMLElement>(
  "#purchasing-order-detail-exception-current",
);
const purchasingOrderDetailExceptionResolveButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-exception-resolve-button",
);
const purchasingOrderDetailExceptionResolveForm = mustFind<HTMLElement>(
  "#purchasing-order-detail-exception-resolve-form",
);
const purchasingOrderDetailExceptionResolutionPicker = mustFind<HTMLElement>(
  "#purchasing-order-detail-exception-resolution-picker",
);
const purchasingOrderDetailExceptionResolveTargetEl = mustFind<HTMLElement>(
  "#purchasing-order-detail-exception-resolve-target",
);
const purchasingOrderDetailExceptionResolveSubmitButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-exception-resolve-submit",
);
const purchasingOrderDetailExceptionResolveCancelButton = mustFind<HTMLButtonElement>(
  "#purchasing-order-detail-exception-resolve-cancel",
);
const purchasingOrderDetailExceptionResolveStatusEl = mustFind<HTMLElement>(
  "#purchasing-order-detail-exception-resolve-status",
);

// PO6 Slice 6 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S6-outside-system-orders.md). PO6-R1 (DEC-056):
// reached from Settings > Purchasing's own
// #admin-purchasing-open-outside-order button, gated independently on
// inv.receipt.record.

// PO6 Slice 2 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S2-cart-and-placement.md). PO6-R1 (DEC-056):
// reached from Settings > Purchasing's own #admin-purchasing-open-cart
// button, gated on purchasing.cart.manage.
const purchasingCartScreen = mustFind<HTMLElement>("#purchasing-cart-screen");
const purchasingCartStatusEl = mustFind<HTMLElement>("#purchasing-cart-status");
const purchasingCartProposalsEmptyEl = mustFind<HTMLElement>("#purchasing-cart-proposals-empty");
const purchasingCartProposalsListEl = mustFind<HTMLElement>("#purchasing-cart-proposals-list");
// INV8 (DEC-176) Section 2.5: a picker, never typed (this is an admin/
// pointer surface, purchasing-cart-screen having been explicitly removed
// from FLOOR_HTML_ROOTS by DEC-056/PO6-R1, so a <select> is legitimate
// here, not a floor-touch violation).
const purchasingCartProposalsSortSelect = mustFind<HTMLSelectElement>(
  "#purchasing-cart-proposals-sort",
);
// UX16 (DEC-058), criterion 2: stages every below-par proposal into the
// cart at once, at each product's own computed shortfall.
const purchasingCartAddAllBelowParButton = mustFind<HTMLButtonElement>(
  "#purchasing-cart-add-all-below-par",
);
// UX65: a visible, plain-text reason next to the button above (not a
// title/tooltip) shown only while it is disabled, computed from the
// loaded proposals by belowParButtonDisabledReason.
const purchasingCartAddAllBelowParReasonEl = mustFind<HTMLParagraphElement>(
  "#purchasing-cart-add-all-below-par-reason",
);
const purchasingCartProductSearchInput = mustFind<HTMLInputElement>(
  "#purchasing-cart-product-search",
);
const purchasingCartProductResultsEl = mustFind<HTMLElement>("#purchasing-cart-product-results");
const purchasingCartEmptyEl = mustFind<HTMLElement>("#purchasing-cart-empty");
const cartSectionsEl = mustFind<HTMLElement>("#cart-sections");
const purchasingCartVendorGroupsEl = mustFind<HTMLElement>("#purchasing-cart-vendor-groups");
const purchasingCartSummaryEl = mustFind<HTMLElement>("#purchasing-cart-summary");
const purchasingCartPlacedEmptyEl = mustFind<HTMLElement>("#purchasing-cart-placed-empty");
const purchasingCartPlacedListEl = mustFind<HTMLElement>("#purchasing-cart-placed-list");
// UX66 part C: the confirmation banner shown immediately after a
// vendor's order places (design/mockups/purchasing-cart-flow.html).
const purchasingCartConfirmationBannerEl = mustFind<HTMLElement>(
  "#purchasing-cart-confirmation-banner",
);
const purchasingCartConfirmationTitleEl = mustFind<HTMLElement>(
  "#purchasing-cart-confirmation-title",
);
const purchasingCartConfirmationDetailEl = mustFind<HTMLElement>(
  "#purchasing-cart-confirmation-detail",
);
const purchasingCartConfirmationViewButton = mustFind<HTMLButtonElement>(
  "#purchasing-cart-confirmation-view",
);
const purchasingCartConfirmationDismissButton = mustFind<HTMLButtonElement>(
  "#purchasing-cart-confirmation-dismiss",
);
purchasingCartConfirmationDismissButton.addEventListener("click", () => {
  purchasingCartConfirmationBannerEl.hidden = true;
});

// PO6 Slice 4 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S4-receiving.md). Reached from a "Receive" button
// on a Purchase Orders list row, gated on inv.receipt.record. ADMIN
// SURFACE since PO6-R1 (DEC-056, superseding ADR-003 Decision 2): see
// ui/index.html's own comment on this screen's root.
const purchasingReceivingScreen = mustFind<HTMLElement>("#purchasing-receiving-screen");
const purchasingReceivingVendorEl = mustFind<HTMLElement>("#purchasing-receiving-vendor");
const purchasingReceivingStatusLabelEl = mustFind<HTMLElement>(
  "#purchasing-receiving-status-label",
);
const purchasingReceivingStatusEl = mustFind<HTMLElement>("#purchasing-receiving-status");
const purchasingReceivingExceptionEl = mustFind<HTMLElement>("#purchasing-receiving-exception");

// B6 (docs/build-briefs/B6-barcode-receive.md): the scan overlay inside the receiving screen. ui/src/barcodeReceive.ts draws
// it; this file supplies the order it receives against and what happens after Finish.
const purchasingReceivingScanButton = mustFind<HTMLButtonElement>("#purchasing-receiving-scan");
function b6(id: string): HTMLElement {
  return mustFind<HTMLElement>(`#b6-${id}`);
}
const barcodeReceive = mountBarcodeReceive(
  {
    layer: b6("layer"),
    back: b6("back") as HTMLButtonElement,
    orderTag: b6("order-tag"),
    leftTitle: b6("left-title"),
    cameraState: b6("camera-state"),
    closeCamera: b6("close-camera") as HTMLButtonElement,
    cam: b6("cam"),
    video: b6("video") as HTMLVideoElement,
    facing: b6("facing"),
    flip: b6("flip") as HTMLButtonElement,
    light: b6("light") as HTMLButtonElement,
    frame: b6("frame"),
    hint: b6("hint"),
    found: b6("found"),
    foundName: b6("found-name"),
    foundNote: b6("found-note"),
    foundChips: b6("found-chips"),
    foundMinus: b6("found-minus") as HTMLButtonElement,
    foundPlus: b6("found-plus") as HTMLButtonElement,
    foundQty: b6("found-qty"),
    denied: b6("denied"),
    deniedTitle: b6("denied-title"),
    tryAgain: b6("try-again") as HTMLButtonElement,
    pickByHand: b6("pick-by-hand") as HTMLButtonElement,
    review: b6("review"),
    scanMore: b6("scan-more") as HTMLButtonElement,
    addByHand: b6("add-by-hand") as HTMLButtonElement,
    count: b6("count"),
    rows: b6("rows"),
    sum: b6("sum"),
    finish: b6("finish") as HTMLButtonElement,
    backdrop: b6("backdrop"),
    unknown: b6("unknown"),
    unknownCode: b6("unknown-code"),
    unknownLink: b6("unknown-link") as HTMLButtonElement,
    unknownSkip: b6("unknown-skip") as HTMLButtonElement,
    replace: b6("replace"),
    replaceItem: b6("replace-item"),
    replaceOld: b6("replace-old"),
    replaceNew: b6("replace-new"),
    replaceYes: b6("replace-yes") as HTMLButtonElement,
    replaceNo: b6("replace-no") as HTMLButtonElement,
    discard: b6("discard"),
    discardCount: b6("discard-count"),
    discardYes: b6("discard-yes") as HTMLButtonElement,
    discardNo: b6("discard-no") as HTMLButtonElement,
    pick: b6("pick"),
    pickTitle: b6("pick-title"),
    pickCancel: b6("pick-cancel") as HTMLButtonElement,
    pickKinds: b6("pick-kinds"),
    pickLetters: b6("pick-letters"),
    pickList: b6("pick-list"),
    pickCodeKinds: b6("pick-codekinds"),
    pickError: b6("pick-error"),
    pickConfirm: b6("pick-confirm") as HTMLButtonElement,
  } satisfies BarcodeReceiveElements,
  {
    session: () => requireSession(),
    loadOrder: async () => {
      const orderId = state.receivingOrderId;
      if (orderId === undefined) return undefined;
      const session = requireSession();
      const [detail, receiving] = await Promise.all([
        getPurchaseOrder(session.apiBaseUrl, session.tenantId, orderId),
        getOrderReceivingView(session.apiBaseUrl, session.tenantId, orderId),
      ]);
      if (detail.status !== 200 || receiving.status !== 200) return undefined;
      const costs = new Map(
        detail.body.order.lines.map((line) => [line.id, line.unitCostCentsAtPlacement]),
      );
      return {
        vendorId: detail.body.order.vendorId,
        vendorName: detail.body.order.vendorName,
        lines: receiving.body.lines.map((line) => ({
          lineId: line.purchaseOrderLineId,
          productId: line.liquorProductId,
          outstandingQuantity: line.outstandingQuantity,
          bottlesPerOrderUnit: line.bottlesPerOrderUnit ?? null,
          unitCostCentsAtPlacement: costs.get(line.purchaseOrderLineId) ?? null,
        })),
      };
    },
    onReceived: (productIds, summary) => {
      if (summary.length > 0) showToast(summary, "success");
      void offerClearStock86(productIds, summary);
      void loadPurchasingReceivingView();
    },
    toast: (message, kind) => showToast(message, kind === "success" ? "success" : "danger"),
  },
);
purchasingReceivingScanButton.addEventListener("click", () => void barcodeReceive.open());
const purchasingReceivingLinesEl = mustFind<HTMLElement>("#purchasing-receiving-lines");
// PO7-S1: the Invoice card hosts (ui/src/invoiceCard.ts), one per screen.
const purchasingReceivingInvoicesEl = mustFind<HTMLElement>("#purchasing-receiving-invoices");
const purchasingOrderDetailInvoicesEl = mustFind<HTMLElement>("#purchasing-order-detail-invoices");
const purchasingOrderDetailWaitingEl = mustFind<HTMLElement>("#purchasing-order-detail-waiting");
// PO7-S5 (docs/build-briefs/PO7-S5-invoice-scan-receive.md): scan an invoice to receive, a floor surface reached from the
// order receiving screen and from Receive stock. Built by ui/src/receiveScan.ts.
const receiveScanScreen = mustFind<HTMLElement>("#receive-scan-screen");
const purchasingReceivingScanInvoiceButton = mustFind<HTMLButtonElement>(
  "#purchasing-receiving-scan-invoice",
);
const purchasingOutsideOrderScanInvoiceButton = mustFind<HTMLButtonElement>(
  "#purchasing-outside-order-scan-invoice",
);
/** The order the receiving screen is showing, so Back from a scan returns to it. */
let receivingScreenOrder: PurchaseOrderSummaryDto | undefined;
const receiveScanController: ReceiveScanController = createReceiveScan({
  root: receiveScanScreen,
  session: () => requireSession(),
  hasPermission: (permissionId) => {
    const current = state.session;
    return current !== undefined && hasPermission(current, permissionId);
  },
  showToast: (message, variant) => showToast(message, variant),
  onLeave: (origin) => {
    if (origin.kind === "order" && receivingScreenOrder !== undefined) {
      void showPurchasingReceivingScreen(receivingScreenOrder);
    } else {
      showPurchasingOutsideOrderScreen();
    }
  },
  onReviewInvoice: () => {
    if (receivingScreenOrder !== undefined) {
      void showPurchasingReceivingScreen(receivingScreenOrder);
    } else {
      showPurchasingOutsideOrderScreen();
    }
  },
  onReceived: (productIds) => {
    void offerClearStock86(productIds);
  },
});

// INVDESIGN1-O1: the four ordering screens (What to order, the order draft, Open orders, Receive an order), built by
// ui/src/orderingScreens.ts into one root.
const orderingScreen = mustFind<HTMLElement>("#ordering-screen");
const orderingController: OrderingScreensController = createOrderingScreens({
  root: orderingScreen,
  session: () => requireSession(),
  hasPermission: (permissionId) => {
    const current = state.session;
    return current !== undefined && hasPermission(current, permissionId);
  },
  showToast: (message, variant) => showToast(message, variant),
  openInventoryHub: () => openFunctionHub("inventory"),
  openPastOrders: () => showPurchasingOrdersScreen(),
  openOrderDetail: (orderId) => {
    void openRoute({ kind: "orders", orderId }, requireSession(), undefined);
  },
  openVendors: () => {
    showAdminScreen();
    void admin.openAdminScreen(requireSession(), { section: "purchasing" });
  },
  openSetPar: () => {
    showAdminScreen();
    void admin.openAdminScreen(requireSession(), { section: "inventory" });
  },
});

function showOrderingScreen(view: OrderingView): void {
  hideAllScreens();
  orderingScreen.hidden = false;
  showTopbarChrome("ordering-screen");
  orderingController.open(view);
}

// PO6 Slice 6 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S6-outside-system-orders.md). Reached from
// Settings > Purchasing's own "Receive stock (no purchase order)" button (PO6-R1,
// DEC-056), gated on inv.receipt.record. ADMIN SURFACE, the same
// judgment as Slice 4's receiving screen: see ui/index.html's own
// comment on this screen's root.
const purchasingOutsideOrderScreen = mustFind<HTMLElement>("#purchasing-outside-order-screen");
const purchasingOutsideOrderStatusEl = mustFind<HTMLElement>("#purchasing-outside-order-status");
const purchasingOutsideOrderProductSearchInput = mustFind<HTMLInputElement>(
  "#purchasing-outside-order-product-search",
);
const purchasingOutsideOrderProductResultsEl = mustFind<HTMLElement>(
  "#purchasing-outside-order-product-results",
);
const purchasingOutsideOrderEntryEl = mustFind<HTMLElement>("#purchasing-outside-order-entry");
// INV35-B1-F1: the product search, shown while no item is being entered and hidden once one is, so
// the entry (and its vendor row) sits higher on the screen at the floor tablet's height.
const purchasingOutsideOrderFindEl = mustFind<HTMLElement>("#purchasing-outside-order-find");
const purchasingOutsideOrderEntryProductEl = mustFind<HTMLElement>(
  "#purchasing-outside-order-entry-product",
);
const purchasingOutsideOrderEntryChangeButton = mustFind<HTMLButtonElement>(
  "#purchasing-outside-order-entry-change",
);
const purchasingOutsideOrderQuantityInput = mustFind<HTMLInputElement>(
  "#purchasing-outside-order-quantity-input",
);
const purchasingOutsideOrderUnitPickerEl = mustFind<HTMLElement>(
  "#purchasing-outside-order-unit-picker",
);
const purchasingOutsideOrderCostInput = mustFind<HTMLInputElement>(
  "#purchasing-outside-order-cost-input",
);
const purchasingOutsideOrderReferenceInput = mustFind<HTMLInputElement>(
  "#purchasing-outside-order-reference-input",
);
const purchasingOutsideOrderEntryErrorEl = mustFind<HTMLElement>(
  "#purchasing-outside-order-entry-error",
);
const purchasingOutsideOrderSubmitButton = mustFind<HTMLButtonElement>(
  "#purchasing-outside-order-submit",
);
const purchasingOutsideOrderRecordedEmptyEl = mustFind<HTMLElement>(
  "#purchasing-outside-order-recorded-empty",
);
const purchasingOutsideOrderRecordedListEl = mustFind<HTMLElement>(
  "#purchasing-outside-order-recorded-list",
);
// INV35-B1-F1 (DEC-300): the optional vendor row and its "Pick a vendor" sheet. The backdrop is
// deliberately not a .scrim (see ui/index.html) and is closed by the sign-out sweep
// (closeAllFloorSheets); ui/src/receiveVendor.ts draws everything inside it and the vendor row.
const receiveVendorBackdrop = mustFind<HTMLElement>("#receive-vendor-backdrop");
const receiveVendorController = createReceiveVendorController({
  elements: {
    control: mustFind<HTMLElement>("#receive-vendor-control"),
    carry: mustFind<HTMLElement>("#receive-vendor-carry"),
    backdrop: receiveVendorBackdrop,
    sheet: mustFind<HTMLElement>("#receive-vendor-sheet"),
    done: mustFind<HTMLButtonElement>("#receive-vendor-done"),
    search: mustFind<HTMLInputElement>("#receive-vendor-search"),
    searchClear: mustFind<HTMLButtonElement>("#receive-vendor-search-clear"),
    recent: mustFind<HTMLElement>("#receive-vendor-recent"),
    list: mustFind<HTMLElement>("#receive-vendor-list"),
    count: mustFind<HTMLElement>("#receive-vendor-count"),
    toggle: mustFind<HTMLButtonElement>("#receive-vendor-toggle"),
    barNote: mustFind<HTMLElement>("#receive-stock-bar-note"),
  },
  // The green confirmation of the previous delivery would sit over the sheet's title and Done.
  onOpenSheet: () => {
    toastEl.hidden = true;
  },
  fetchList: async (liquorProductId): Promise<ReceiveVendorLoadResult> => {
    const session = requireSession();
    const result = await listReceiptVendors(session.apiBaseUrl, session.tenantId, {
      liquorProductId,
    });
    if (result.status !== 200) {
      return { ok: false, message: result.body.message ?? "Could not load the vendor list." };
    }
    return {
      ok: true,
      list: {
        vendors: result.body.vendors,
        usedLately: result.body.usedLately,
        usualVendorId: result.body.usualVendorId,
      },
    };
  },
});

// ---------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------

/** CL6-F1 / RPT1-F5: the tenant's current business date, from the server.
 * The browser's own date is the wrong source twice over: it is UTC (so it is
 * already tomorrow in the Eastern evening) and it ignores the tenant's
 * business day cutoff. Returns undefined, after telling the user, when the
 * server cannot be reached; a screen then stays where it is rather than
 * guessing a date. */
async function refreshTenantBusinessDate(): Promise<string | undefined> {
  const session = requireSession();
  const result = await getReportContext(session.apiBaseUrl, session.tenantId);
  if (result.status !== 200 || typeof result.body.today !== "string") {
    showToast(
      `Could not load today's business date. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return undefined;
  }
  state.tenantBusinessDate = result.body.today;
  return result.body.today;
}

/** CL6: pure calendar arithmetic on a "YYYY-MM-DD" label, anchored at
 * UTC noon so DST can never shift it (the same shape
 * src/domain/businessDayCaptureSchedule.ts's addDaysToIsoDate uses). */
function addDaysToIsoDate(dateIso: string, days: number): string {
  const [y, m, d] = dateIso.split("-");
  const shifted = new Date(Date.UTC(Number(y ?? 0), Number(m ?? 1) - 1, Number(d ?? 1) + days, 12));
  return shifted.toISOString().slice(0, 10);
}

/** Design-gate fix (2026-08-20, finding 2 non-blocking correction): the
 * toast was unconditionally styled danger-red (styles.css's old
 * unqualified `.toast` rule), so a plainly good outcome like "Ticket
 * closed." read as an error. `variant` picks one of the three semantic
 * toast classes defined in styles.css; every call site below states its
 * variant explicitly rather than relying on a default, so a new caller
 * cannot silently fall back to the wrong color. */
export type ToastVariant = "danger" | "success" | "neutral" | "warning";

/** UIFIX2 (SF-9): where the toast goes while a sheet is open. Returns undefined when no sheet is open. A top-anchored
 * sheet (a scrim-top scrim: Pay, Add guest, the liquor picker) is left to UX102's bottom dock, which is clear of it. A toast is
 * about 44px tall; the band above the sheet takes it when the sheet starts at least 60px down the screen,
 * otherwise it sits directly above the sheet's own footer so it never covers a footer button. */
function toastSheetBox(): { top: number; left: number; right: number } | undefined {
  const sheets = Array.from(document.querySelectorAll<HTMLElement>(".scrim:not([hidden]):not(.scrim-top) > .sheet"));
  const sheet = sheets.reverse().find((node) => node.offsetParent !== null || node.getClientRects().length > 0);
  if (sheet === undefined) {
    return undefined;
  }
  const box = sheet.getBoundingClientRect();
  if (box.width === 0 || box.height === 0) {
    return undefined;
  }
  const toastHeight = 52;
  const footer = sheet.querySelector<HTMLElement>(".sheet-footer");
  let top: number;
  if (box.top >= toastHeight + 8) {
    top = Math.round(box.top - toastHeight - 4);
  } else if (footer !== null && footer.getBoundingClientRect().height > 0) {
    top = Math.round(footer.getBoundingClientRect().top - toastHeight - 4);
  } else {
    top = Math.round(box.bottom - toastHeight - 4);
  }
  return {
    top: Math.max(8, top),
    left: Math.round(box.left),
    right: Math.round(window.innerWidth - box.right),
  };
}

/** UX102 (click-through review, Toasts): where the one toast slot sits. Signed in, it docks to the bottom of
 * the menu pane, clear of the A to Z strip, the name field and the list headers at the top; beside the ticket
 * rail it stops short of the rail, and with the rail stacked under the menu (portrait) it sits just above the
 * rail, so it never lies over Pay. A sheet that is anchored to the bottom (a choice list) puts it back at the
 * top, over the dimmed page; before sign-in there is no menu and it stays at the top. */
function dockToastSlot(): void {
  const slot = toastEl.parentElement;
  if (slot === null) {
    return;
  }
  // UIFIX2 (SF-9, style guide section 7): with a sheet open the toast sits just above that sheet's footer, in one
  // place whatever the screen, and never over a button: in the dimmed band above the sheet when there is room,
  // else directly above the sheet's footer.
  const openSheet = toastSheetBox();
  if (openSheet !== undefined) {
    slot.dataset["dock"] = "sheet";
    slot.style.setProperty("--toast-sheet-top", `${openSheet.top}px`);
    slot.style.setProperty("--toast-sheet-left", `${openSheet.left}px`);
    slot.style.setProperty("--toast-sheet-right", `${openSheet.right}px`);
    return;
  }
  if (state.session === undefined) {
    slot.dataset["dock"] = "top";
    return;
  }
  slot.dataset["dock"] = "bottom";
  let right = 0;
  let bottom = 16;
  const rail = ticketRailEl.getBoundingClientRect();
  if (ticketRailEl.offsetParent !== null && rail.width > 0 && rail.height > 0) {
    if (rail.left > window.innerWidth * 0.4) {
      right = Math.round(window.innerWidth - rail.left);
    } else {
      bottom = Math.round(window.innerHeight - rail.top + 8);
    }
  }
  slot.style.setProperty("--toast-clear-right", `${right}px`);
  slot.style.setProperty("--toast-clear-bottom", `${bottom}px`);
}

/** UIFIX2 (SF-9): a toast can be raised a moment before the sheet it belongs to is shown. While a toast is up, a
 * sheet opening or closing re-docks it, so it is never left lying over the sheet's footer. */
let toastDockObserver: MutationObserver | undefined;

export function showToast(message: string, variant: ToastVariant): void {
  dockToastSlot();
  if (toastDockObserver === undefined) {
    toastDockObserver = new MutationObserver(() => {
      if (!toastEl.hidden) {
        dockToastSlot();
      }
    });
    toastDockObserver.observe(document.body, { attributes: true, attributeFilter: ["hidden"], subtree: true });
  }
  toastEl.textContent = message;
  toastEl.className = `toast toast-${variant}`;
  toastEl.hidden = false;
  if (toastTimer !== undefined) {
    clearTimeout(toastTimer);
  }
  toastTimer = setTimeout(() => {
    toastEl.hidden = true;
  }, 4000);
}

export function requireSession(): Session {
  if (state.session === undefined) {
    throw new Error("no active session");
  }
  return state.session;
}

/** SET16-F1: requireSession()'s non-throwing counterpart, for a call
 * site whose own await can span a real session teardown (a confirm
 * sheet an admin may leave open, a settings fetch) and that already
 * knows how to bail out quietly when it resumes to find the session
 * gone, rather than let requireSession()'s throw surface as an
 * unhandled rejection. requireSession() stays the throwing default for
 * every ordinary call site. */
export function sessionOrUndefined(): Session | undefined {
  return state.session;
}

/** AU13-F1 (docs/build-briefs/AU13-F1-a-late-response-after-sign-out-is-
 * dropped.md): the floor-side counterpart to admin.ts's own SET16-F4
 * loadGeneration/loadIsCurrent. A loader captures `const gen =
 * sessionGeneration` before its first await and calls
 * `sessionIsCurrent(gen)` after every await, discarding the response
 * (writing no toast, no state, no DOM) once it is false. handleLogOut
 * INCREMENTS this, never resets it to 0: a reset would make a stale
 * in-flight response captured at 0 look current again to whichever
 * session starts next. Scoped to main.ts's own floor loaders rather than
 * shared with admin.ts's counter: the two teardown on different events
 * and guard different loaders. */
let sessionGeneration = 0;

function sessionIsCurrent(gen: number): boolean {
  return gen === sessionGeneration;
}

function employeeInitials(employeeId: string): string {
  const trimmed = employeeId.trim();
  return trimmed.length === 0 ? "?" : trimmed.slice(0, 2).toUpperCase();
}

/** T6: the debug panel's own view of the pay-key population, with the
 * above-threshold state carried in literal text, never color alone. */
function renderPayKeyDebug(): void {
  const size = state.payIdempotencyKeysByTarget.size;
  debugPayKeyCount.textContent = state.payIdempotencyKeysByTarget.aboveWarnThreshold
    ? `${size} (above warning threshold)`
    : `${size}`;
  // PAY7 Section 3 test 9: a pure, non-minting peek at the current
  // ticket's own "whole" target, so an e2e spec can observe the same key
  // surviving a real page reload.
  const orderId = state.orderId;
  const currentKey =
    orderId === undefined
      ? undefined
      : state.payIdempotencyKeysByTarget.peek(computePayIntentTarget(orderId, { type: "whole" }));
  debugPayKeyCurrent.textContent = currentKey ?? "(none)";
}

function renderMatchMissDebug(): void {
  debugMatchMisses.textContent =
    state.matchMissCount === 0
      ? "0"
      : `${state.matchMissCount} (last: ${state.lastMatchMissDrinkName ?? "?"})`;
}

// ---------------------------------------------------------------------
// Theme switcher
// ---------------------------------------------------------------------

// UX21 (DEC-117, docs/build-briefs/UX21-default-theme-profile-picker.md):
// My Profile's own "Color scheme" select is the only theme control in the
// product; the account-menu dropdown's Theme item is gone. Kept as an
// array (rather than a single element) so a second control could be added
// back without a structural change here; today it has exactly one entry.
const ALL_THEME_SELECTS: readonly HTMLSelectElement[] = [profileThemeSelect];

// TEN17 (DEC-117, DEC-342 Q5): the brand color scheme of the network this tenant belongs to, or null (not a
// member, no brand saved, signed out). Offered as ONE extra option after the shipped schemes, labelled
// "<network name> brand", and never applied unless the user picked it. Read from GET .../me/network-brand after
// sign-in and whenever My Profile opens; cleared at sign-out.
let memberBrand: MemberBrand | null = null;
// TEN17-F2 / TEN17-F3: the scheme the user chose in this sign-in, set the instant they choose, before the save
// returns. A brand read that lands late judges this choice, not the older saved value, so it can never undo it.
// TEN17-F4: undefined means nothing was chosen this sign-in; null means the user chose Default (follow the brand).
let chosenThemeId: string | null | undefined;

function populateThemeSelects(): void {
  for (const select of ALL_THEME_SELECTS) {
    select.innerHTML = "";
    for (const option of profileThemeChoices(THEME_OPTIONS, memberBrand)) {
      const el = document.createElement("option");
      el.value = option.id;
      el.textContent = option.label;
      select.append(el);
    }
  }
}

// TEN17-F1: the network logo in the floor header's left group. Shown only while the member has the brand scheme
// applied (the brand stays an optional extra, DEC-117) and the network has a logo. The picture is fetched with the
// session token and held as a blob URL keyed by its hash, so it is fetched once per logo.
const networkLogoEl = document.querySelector<HTMLImageElement>('[data-testid="app-network-logo"]');
let networkLogoShown: { sha256: string; url: string } | null = null;

async function syncNetworkLogo(brand: MemberBrand | null): Promise<void> {
  if (networkLogoEl === null) return;
  const wanted = brand === null ? null : brand.logo;
  if (brand === null || wanted === null) {
    networkLogoEl.hidden = true;
    networkLogoEl.removeAttribute("src");
    if (networkLogoShown !== null) URL.revokeObjectURL(networkLogoShown.url);
    networkLogoShown = null;
    return;
  }
  const s = state.session;
  if (s === undefined) return;
  if (networkLogoShown === null || networkLogoShown.sha256 !== wanted.sha256) {
    const result = await getNetworkBrandLogo(s.apiBaseUrl, s.tenantId, wanted.sha256);
    // The theme may have changed while the picture was on its way.
    if (
      memberBrand === null ||
      memberBrand.logo === null ||
      memberBrand.logo.sha256 !== wanted.sha256
    )
      return;
    if (result.status !== 200 || result.blob === undefined) return;
    if (networkLogoShown !== null) URL.revokeObjectURL(networkLogoShown.url);
    networkLogoShown = { sha256: wanted.sha256, url: URL.createObjectURL(result.blob) };
  }
  networkLogoEl.src = networkLogoShown.url;
  networkLogoEl.alt = brand.label;
  networkLogoEl.hidden = false;
}

// TEN17-F5: the brand's uploaded font, fetched with the session token and registered from the bytes (a stylesheet
// could not send the token). Fetched once per font id; a failure leaves the brand's fallback stack showing.
const networkFontsRequested = new Set<string>();

async function syncNetworkFont(brand: MemberBrand): Promise<void> {
  const file = brand.fontFile;
  const s = state.session;
  if (file === null || s === undefined || networkFontsRequested.has(file.id)) return;
  networkFontsRequested.add(file.id);
  const result = await getNetworkBrandFont(s.apiBaseUrl, s.tenantId, file.id, file.sha256);
  if (result.status !== 200 || result.blob === undefined) {
    networkFontsRequested.delete(file.id);
    return;
  }
  const registered = await registerUploadedFont(
    uploadedFontFamily(file.id),
    await result.blob.arrayBuffer(),
  );
  if (!registered) networkFontsRequested.delete(file.id);
}

function setTheme(themeId: string): void {
  // A `brand:` id applies only while it is exactly the scheme on offer; otherwise the product default applies.
  const resolution = resolveThemeId(themeId, memberBrand, DEFAULT_THEME_ID);
  if (resolution.kind === "brand") {
    applyBrandTheme(resolution.brand.themeId, resolution.brand.tokens, resolution.brand.font);
    void syncNetworkFont(resolution.brand);
    for (const select of ALL_THEME_SELECTS)
      select.value = pickerValueFor(savedThemeNow(), resolution.brand.themeId);
    void syncNetworkLogo(resolution.brand);
    return;
  }
  void syncNetworkLogo(null);
  applyTheme(resolution.id, themeLink);
  for (const select of ALL_THEME_SELECTS) {
    select.value = pickerValueFor(savedThemeNow(), resolution.id);
  }
}

/** TEN17-F4: the saved choice as of now. A choice made in this sign-in (set the instant it is made, before the
 * save returns) outranks the session's older value, so a late brand read never undoes it. NULL means the user
 * follows the default. */
function savedThemeNow(): string | null {
  if (chosenThemeId !== undefined) return chosenThemeId;
  return state.session?.defaultThemeId ?? null;
}

/** Reads the member brand and refreshes the Color scheme list. When the signed-in user's stored choice is the
 * brand scheme, applies it now that its tokens are known. A failed or malformed reply leaves the list as it was,
 * with no brand option (never a guessed one). */
async function refreshMemberBrand(): Promise<void> {
  const s = state.session;
  if (s === undefined) return;
  const result = await getNetworkBrand(s.apiBaseUrl, s.tenantId);
  if (state.session === undefined || state.session.tenantId !== s.tenantId) return;
  const parsed = result.status === 200 ? parseMemberBrandReply(result.body) : null;
  memberBrand = parsed === null ? null : parsed.brand;
  populateThemeSelects();
  // TEN17-F2 (DEC-427): a user with no saved choice starts on the brand when there is one; a saved brand id is
  // applied now that its tokens are known (and falls back to the product default if the brand is gone).
  const stored = savedThemeNow();
  if (followsBrandRead(stored)) {
    setTheme(startingThemeId(stored, memberBrand, DEFAULT_THEME_ID));
    return;
  }
  // Any other scheme is already on screen: put the list back on it and leave the page's theme alone.
  const showing = document.documentElement.dataset["theme"] ?? DEFAULT_THEME_ID;
  for (const select of ALL_THEME_SELECTS) select.value = showing;
}

// ---------------------------------------------------------------------
// UX17 (docs/build-briefs/UX17-persistent-navigation.md): ONE shared
// account-menu dropdown (Settings, Payment Terminal, Log out), wired once
// below, replacing the nine per-screen copies this section used to wire
// one at a time. My Profile is hidden specifically while the Profile
// screen itself is active (setAccountMenuProfileMode), the same behavior
// the removed profile-only dropdown achieved by simply never carrying
// that entry. UX21 removed the dropdown's own Theme item; My Profile's
// "Color scheme" select is the only theme control left.
// ---------------------------------------------------------------------

// Amendment 2026-08-21 (sixth): "the Settings entry point must be VISIBLE
// ONLY to users with proper rights." The server enforces the identical
// rule independently (requirePermission on every Settings route), so
// hiding this button is a convenience, never the real gate.
//
// Independent-review Finding B, 2026-08-26: this used to be gated by a
// hardcoded role-tier test -- true only for the legacy top two roles --
// which hid the ENTIRE Settings screen, Inventory and
// Counts included, from a standalone Inventory Manager and from a
// Lounger holding an ADDED Inventory Manager role (the owner's own stated
// multi-role use case), even though every server route already resolved
// their access correctly. Gated on ADMIN_TAB_PERMISSIONS below instead:
// the Settings button is now visible whenever the user's resolved
// permissions grant at least ONE of the admin screen's own tabs
// (admin.ts's showSection/ADMIN_TAB_PERMISSIONS is the same list, so a
// tab is never reachable from a URL/state change without also being
// visible from here). Still presentation only.
const SETTINGS_BUTTONS: readonly HTMLButtonElement[] = [openManagerSettingsButton];

// Terminal-assignment slice. Product-owner ruling 2026-08-28 (CLAUDE.md's
// permission-driven rule): this used to read isManagerTierRole(session.
// role), a hardcoded "admin" or "manager" test, four lines below a line
// that already did the right thing with hasAnyPermission -- inconsistent
// within one function. The screen it opens calls GET .../terminals, which
// src/api/server.ts gates on floor.terminal.select and nothing else, so
// that is the permission this button resolves. TERM1, 2026-09-30: this was
// admin.terminal.view, which only admin and manager hold and which also
// opens Settings > Payments > Terminals, so a Lounger could not choose
// this device's terminal. floor.terminal.select is held by every role that
// takes payments. Still presentation only; the route enforces it either way.

/** The permission GET .../terminals/selectable is gated on (src/api/
 * server.ts). TERM1: this is floor.terminal.select, not admin.terminal.view,
 * so a Lounger can choose this device's terminal without being handed the
 * Settings terminals view. One named constant so the account-menu button
 * and the picker screen's own branch below can never drift to two
 * different answers. */
const TERMINAL_PICKER_PERMISSION = "floor.terminal.select";

/** The permission POST .../terminals (register) is gated on. The picker's
 * "Register a new card reader" controls show only with it. */
const TERMINAL_REGISTER_PERMISSION = "admin.terminal.manage";

// PO6 Slice 1: the route this screen reads (GET .../purchasing/orders) is
// gated on purchasing.order.view (src/api/server.ts); reaching it through
// Settings > Purchasing's own button (PO6-R1, DEC-056) checks the SAME
// permission, independently of the other two destinations' own checks.
const PURCHASING_ORDER_VIEW_PERMISSION = "purchasing.order.view";

// PO6 Slice 2: the cart's own permission, checked separately from
// Purchase Orders above because it reads/writes a different route group
// (purchasing.cart.manage, not purchasing.order.view). The two happen to
// move together on every role template this codebase seeds today, but
// Settings > Purchasing still gates each of its three buttons by its
// OWN permission check, never inferred from another button's.
const PURCHASING_CART_MANAGE_PERMISSION = "purchasing.cart.manage";
/** POST .../purchasing/cart/place's own gate (src/api/server.ts). Checked
 * again per vendor group inside the cart screen itself (Place button
 * hidden, not merely disabled) so a session that loses this grant while
 * the screen is open never shows an enabled button the server would
 * refuse. */
const PURCHASING_ORDER_PLACE_PERMISSION = "purchasing.order.place";
/** POST .../purchasing/orders/:orderId/approve's own SECOND-person gate
 * (src/api/server.ts, requireSecondPersonApproval): the id this screen
 * asks GET .../approval-candidates about, and the id the approve request
 * itself is refused against when the picked approver does not hold it as
 * "allowed". Reaching the Approve button at all is still gated on
 * PURCHASING_ORDER_PLACE_PERMISSION above, matching the route's own
 * ordering (requirePermission on .place runs before
 * requireSecondPersonApproval on .approve). */
const PURCHASING_ORDER_APPROVE_PERMISSION = "purchasing.order.approve";
/** INVPOWALK1 (F3): this location's approval limit as the cart last read it, for the one line under Place that tells a
 * person who cannot approve that an order at or over it waits for a manager. null until read, and stays null when the read
 * is refused: the line is then absent, never a made-up figure. */
let purchasingApprovalThresholdCents: number | null = null;
async function loadPurchasingApprovalThresholdForCart(): Promise<void> {
  const session = requireSession();
  if (session.permissions[PURCHASING_ORDER_APPROVE_PERMISSION] === "allowed") return;
  const result = await getPurchaseApprovalThreshold(session.apiBaseUrl, session.tenantId);
  if (result.status !== 200 || typeof result.body.thresholdCents !== "number") return;
  if (purchasingApprovalThresholdCents === result.body.thresholdCents) return;
  purchasingApprovalThresholdCents = result.body.thresholdCents;
  renderPurchasingCartVendorGroups();
}

// PO6 Slice 4: POST .../inventory/receipts's own gate (src/api/
// server.ts), checked per row on the Purchase Orders list (Receive
// button hidden, not merely disabled) the same "own permission, own
// check, hidden outright" shape PURCHASING_ORDER_PLACE_PERMISSION above
// uses. Not one of the topbar SET_*_HIDDEN button groups: this is a
// per-row decision made at render time (renderPurchasingOrdersList),
// never a single screen-level button.
const INVENTORY_RECEIPT_RECORD_PERMISSION = "inv.receipt.record";

// PO6 Slice 6 (docs/build-briefs/PO6-S6-outside-system-orders.md). Record
// Outside Order's own button on Settings > Purchasing (PO6-R1, DEC-056)
// is gated on the SAME inv.receipt.record permission the per-row Receive
// button above uses (the route both call is identical), checked
// independently of the other two buttons' own permissions.

// PO6 Slice 5 (docs/build-briefs/PO6-S5-corrections-and-exceptions.md).
// GET .../inventory/receipts and GET .../inventory/receipts/:id/
// corrections' own gate: whether the order detail screen even attempts
// to load a line's own receipts and past corrections at all.
const INVENTORY_RECEIPT_VIEW_PERMISSION = "inv.receipt.view";
/** POST .../inventory/receipts/:receiptId/corrections' own gate,
 * approval_required for an Assistant Manager and allowed for Manager/
 * Admin (src/domain/permissions.ts, read directly). hasPermission
 * returns true for BOTH allowed and approval_required (session.ts's own
 * doc comment), so a Correct button is shown to anyone who can even
 * attempt this route, exactly PURCHASING_ORDER_APPROVE_PERMISSION's own
 * "own permission, own check, hidden outright" shape; the approval_
 * required retry itself is handled where the request is actually sent,
 * never here. */
const INVENTORY_RECEIPT_CORRECT_PERMISSION = "inv.receipt.correct";

// VOC1-F5 Slice 3 (docs/build-briefs/VOC1-F5-stock-states-on-the-floor-
// and-86-blocks-selection.md, Section 2; DEC-161). The one thing that lets
// a session sell past a flagged-86 refusal (Slice 2). hasPermission
// returns true for "allowed" (DEC-161's own template has no
// approval_required tier for this id at all), false for "denied" or a
// missing entry -- never a role-name comparison. Gates only whether the
// override affordance renders (stockStateBadge's own tile/row disabled
// state below); the server's own requirePermission call
// (src/api/server.ts's resolveOverride86) is the real, unbypassable gate.
const STOCK_FLAG_OVERRIDE_PERMISSION = "inv.stock_flag.override";
// STK1 (DEC-269). Setting or clearing an 86 (the floor 86 list button, Back
// in stock, the Settings controls), and reading the flag's own history
// (who and since when). A lounger holds record but NOT view (DEC-050), so
// the "Out since" line is best-effort. Permission-driven, never role-driven.
const STOCK_FLAG_RECORD_PERMISSION = "inv.stock_flag.record";
export const STOCK_FLAG_VIEW_PERMISSION = "inv.stock_flag.view";

function setSettingsControlsHidden(hidden: boolean): void {
  for (const button of SETTINGS_BUTTONS) {
    button.hidden = hidden;
  }
  accountStatusLink.setAllowed(!hidden);
}

function setTerminalControlsHidden(hidden: boolean): void {
  topbarReaderTrigger.disabled = hidden;
  topbarTerminalWarningButton.disabled = hidden;
  if (hidden) readerDropdown.close();
}

/** UX80 (DEC-298): applies ui/src/accountMenu.ts to the static markup. A row
 * shows only if the person holds its permission; a group row shows only if it
 * has a visible row, with its hint built from the rows that remain; the group
 * badge and the name chip count come from the badges that already exist in the
 * app (open manager exceptions). Called from every place that refreshes
 * permissions and whenever the exception count changes. */
function currentMenuBadges(): Record<string, MenuBadge | undefined> {
  const exceptionCount = state.managerExceptions.length;
  const badges = menuBadgesFromCounts(state.menuCounts);
  if (exceptionCount > 0) {
    badges["manager-exceptions"] = { count: exceptionCount, tone: "red" };
  }
  return badges;
}

/** UX80-F1: one cheap read of the other badge counts. Called on menu open and from loadOpenTabs
 * (where the manager exception count already refreshes), and nowhere else: there is no timer. An
 * in-flight read is shared, so a quick second open does not double the request. A failed read
 * keeps the last known counts. A response for a session that has ended writes nothing. */
function renderPurchaseApprovalNotice(): void {
  const count = state.menuCounts.ordersAwaitingYourApproval ?? 0;
  const session = state.session;
  const show = session !== undefined && !appTopbar.hidden &&
    session.permissions["purchasing.order.approve"] === "allowed" && count > 0 &&
    hasPermission(session, "purchasing.order.view");
  topbarApprovalBadge.hidden = !show;
  topbarApprovalCount.textContent = String(count);
  topbarApprovalBadge.setAttribute("aria-label", `Ordering, ${count} ${count === 1 ? "order" : "orders"} awaiting approval`);
}
topbarApprovalBadge.addEventListener("click", () => {
  if (state.session !== undefined && state.session.permissions["purchasing.order.approve"] === "allowed") {
    showPurchasingOrdersScreen();
  }
});
// DEPCHECKUI1 (DEC-612): the red strip under the header while a dependency check fails at this location. It reads in the
// background (at most every few minutes), keeps its last answer when a read fails, and never holds a screen back. The card in
// Settings tells it every answer it gets, so a Run checks now clears the strip at once.
const systemChecksStrip = mountSystemChecksStrip(mustFind<HTMLElement>("#system-checks-strip"), {
  apiBaseUrl: () => state.session?.apiBaseUrl ?? "",
  tenantId: () => state.session?.tenantId ?? "",
  canView: () => state.session !== undefined && hasPermission(state.session, "admin.tenant_settings.view"),
  deviceLocationId: () => loadTerminalConfig()?.locationId,
  isCurrent: () => state.session !== undefined,
  sessionKey: () => `${state.session?.tenantId ?? ""}/${state.session?.userId ?? ""}/${loadTerminalConfig()?.locationId ?? ""}`,
  openCard: () => {
    const active = state.session;
    if (active === undefined) return;
    showAdminScreen();
    void admin.openSystemChecksInSettings(active);
  },
});
admin.onSystemChecksResult((view) => systemChecksStrip.setResult(view));
// SYSLOC1 (Kerry, 2026-10-31): no System checks strip on location screens; the checks live in the software owner console.
// Live in-app signal for active approvers, without a notification job or outbound provider.
setInterval(() => {
  if (!document.hidden && state.session?.permissions["purchasing.order.approve"] === "allowed") {
    void refreshMenuCounts().catch(() => { topbarApprovalBadge.hidden = true; });
  }
}, 30_000);

let menuCountsInFlight: Promise<void> | undefined;
function refreshMenuCounts(): Promise<void> {
  const session = state.session;
  if (session === undefined) {
    return Promise.resolve();
  }
  if (menuCountsInFlight !== undefined) {
    return menuCountsInFlight;
  }
  const gen = sessionGeneration;
  const run = async (): Promise<void> => {
    try {
      const result = await getMenuCounts(session.apiBaseUrl, session.tenantId);
      if (!sessionIsCurrent(gen) || result.status !== 200 || state.session === undefined) {
        return;
      }
      // INVXFER1-S2: the Transfers row's badge, transfers Sent to this location and not yet received. The menu-counts route
      // knows nothing of it, so it is read from the transfers list; a failed read draws no badge.
      let toReceive: number | undefined;
      if (hasPermission(session, "inv.transfer.view")) {
        const sent = await listStockTransfers(session.apiBaseUrl, session.tenantId, {
          state: "sent",
          background: true,
        });
        const locationId = loadTerminalConfig()?.locationId;
        if (sent.status === 200 && locationId !== undefined) {
          toReceive = transfersToReceiveCount(sent.body.transfers, locationId);
        }
      }
      // MGRDAYMAJ1 (manager-day-04): the Needs you strip says "Yesterday" for yesterday, so the business date is read with the
      // counts, only when a past day is waiting. A failed read leaves the day named by its date, which is also true.
      if (result.body.counts.unfinalizedBusinessDate !== undefined) {
        const context = await getReportContext(session.apiBaseUrl, session.tenantId);
        if (context.status === 200 && typeof context.body.today === "string") {
          state.tenantBusinessDate = context.body.today;
        }
      }
      if (!sessionIsCurrent(gen) || state.session === undefined) {
        return;
      }
      state.menuCounts =
        toReceive === undefined
          ? result.body.counts
          : { ...result.body.counts, transfersToReceive: toReceive };
      refreshAccountMenuRows(state.session);
      renderPurchaseApprovalNotice();
    } finally {
      menuCountsInFlight = undefined;
    }
  };
  menuCountsInFlight = run();
  return menuCountsInFlight;
}

/** INV38: the Set starting stock row's state, from the same status read the page uses
 * (INV31, gated on inv.movement.record, the row's own permission, so a person without it
 * makes no call). Called at sign-in with the other menu data, on each menu open, and when
 * the api layer says a count closed, starting stock was saved or an item was created. The
 * cached answer stays in place while the read is in flight, so the menu never draws the row
 * and then takes it back. A response for a session that has ended writes nothing. */
let startingStockMenuInFlight: Promise<void> | undefined;
let startingStockMenuStale = false;
const STARTING_STOCK_MENU_PERMISSION = "inv.movement.record";
function refreshStartingStockMenu(): Promise<void> {
  const session = state.session;
  if (session === undefined || !hasPermission(session, STARTING_STOCK_MENU_PERMISSION)) {
    return Promise.resolve();
  }
  if (startingStockMenuInFlight !== undefined) {
    // A change landed while a read was out: it may predate the change, so read once more.
    startingStockMenuStale = true;
    return startingStockMenuInFlight;
  }
  const gen = sessionGeneration;
  const run = async (): Promise<void> => {
    try {
      do {
        startingStockMenuStale = false;
        const result = await getOpeningBalanceStatus(session.apiBaseUrl, session.tenantId);
        if (!sessionIsCurrent(gen) || state.session === undefined) {
          return;
        }
        state.startingStockMenu =
          result.status !== 200
            ? "unreadable"
            : result.body.initialStockCompleted
              ? "complete"
              : "needed";
        refreshAccountMenuRows(state.session);
      } while (startingStockMenuStale);
    } finally {
      startingStockMenuInFlight = undefined;
    }
  };
  startingStockMenuInFlight = run();
  return startingStockMenuInFlight;
}
onStartingStockMayHaveChanged(() => {
  void refreshStartingStockMenu();
});

/** The rows hidden from the menu by what the location has already done (INV38). */
function hiddenMenuRowIds(): ReadonlySet<string> {
  const hidden = new Set<string>();
  if (state.startingStockMenu === "complete" || state.startingStockMenu === "unknown") {
    hidden.add(STARTING_STOCK_ROW_ID);
  }
  // CASH1-S1-U1: the Cash drawer row shows only while the location's cash setting is on or a drawer is open.
  if (!cashDrawer.entryVisible()) hidden.add(CASH_DRAWER_ROW_ID);
  return hidden;
}

// CASH1-S1-U1 (docs/build-briefs/CASH1-S1-U1-drawer-screens.md): the cash drawer sheet and its row on the Shift hub.
const cashDrawer = createCashDrawer({
  apiBaseUrl: () => requireSession().apiBaseUrl,
  tenantId: () => requireSession().tenantId,
  has: (permissionId) => state.session !== undefined && hasPermission(state.session, permissionId),
  holdsOutright: (permissionId) => state.session?.permissions[permissionId] === "allowed",
  requestApproval,
  showOrigin: () => showFunctionHubScreen("shift"),
  onEntryChanged: () => {
    if (state.session !== undefined) refreshAccountMenuRows(state.session);
  },
  toast: (message) => showToast(message, "danger"),
});

// CASH1-S2-U1 (docs/build-briefs/CASH1-S2-U1-pay-screens.md): the Cash screen and the Change due screen at Pay.
const cashTender = createCashTender();
// RCPT1-S3: the Awaiting tip sheet, built at runtime like the Cash screen.
const awaitingTipSheet = createAwaitingTipSheet();
// RCPT1-S3-F1: the Remaining tip sheet (the rest of an approved tip above the limit, collected by a second tap), built at runtime.
const remainingTipSheet = createRemainingTipSheet();

function refreshAccountMenuRows(session: Session): void {
  const has = (id: string): boolean => hasPermission(session, id);
  const hiddenIds = hiddenMenuRowIds();
  const badges = currentMenuBadges();
  let anyGroup = false;
  for (const group of ACCOUNT_MENU_GROUPS) {
    const visible = visibleItems(withoutHiddenRows(group, hiddenIds), has);
    const shown = groupVisible(withoutHiddenRows(group, hiddenIds), has);
    const groupButton = accountDropdown.querySelector<HTMLElement>(
      `[data-menu-group="${group.id}"]`,
    );
    // UX116: the group's rows live on its hub screen, not in the dropdown. A direct group (Reports) has no hub.
    const level = document.querySelector<HTMLElement>(`[data-menu-level="${group.id}"]`);
    if (groupButton === null || (level === null && group.direct === undefined)) {
      continue;
    }
    groupButton.hidden = !shown;
    anyGroup = anyGroup || shown;
    const hintEl = groupButton.querySelector<HTMLElement>(".account-menu-sub");
    if (hintEl !== null) {
      hintEl.textContent = groupHint(withoutHiddenRows(group, hiddenIds), has);
    }
    const badgeEl = groupButton.querySelector<HTMLElement>(".account-menu-badge");
    const badge = groupBadge(withoutHiddenRows(group, hiddenIds), has, badges);
    if (badgeEl !== null) {
      badgeEl.hidden = badge === undefined;
      badgeEl.textContent = badge === undefined ? "" : String(badge.count);
      badgeEl.classList.toggle("account-menu-badge-red", badge?.tone === "red");
      badgeEl.classList.toggle("account-menu-badge-orange", badge?.tone === "orange");
      badgeEl.classList.toggle("account-menu-badge-blue", badge?.tone === "blue");
    }
    if (level === null) continue;
    // UX80-F1: the rows' own badges (cart, orders, count stock, match clients). The manager
    // exceptions row keeps its dedicated element, drawn below.
    for (const item of visible) {
      const rowBadgeEl = level.querySelector<HTMLElement>(`[data-menu-row-badge="${item.id}"]`);
      if (rowBadgeEl === null) continue;
      const rowBadge = badges[item.id];
      rowBadgeEl.hidden = rowBadge === undefined;
      rowBadgeEl.textContent = rowBadge === undefined ? "" : String(rowBadge.count);
      rowBadgeEl.classList.toggle("account-menu-badge-red", rowBadge?.tone === "red");
      rowBadgeEl.classList.toggle("account-menu-badge-orange", rowBadge?.tone === "orange");
      rowBadgeEl.classList.toggle("account-menu-badge-blue", rowBadge?.tone === "blue");
    }
    // Rows, and the small headings above runs of rows.
    let headingEl: HTMLElement | null = null;
    let headingHasRow = false;
    const closeHeading = (): void => {
      if (headingEl !== null) headingEl.hidden = !headingHasRow;
    };
    for (const item of group.items) {
      if (item.section !== undefined) {
        closeHeading();
        headingEl = level.querySelector<HTMLElement>(`[data-menu-section-for="${item.id}"]`);
        headingHasRow = false;
      }
      const rowVisible = visible.includes(item);
      const rowEl = level.querySelector<HTMLElement>(`[data-menu-item="${item.id}"]`);
      if (rowEl !== null) rowEl.hidden = !rowVisible;
      headingHasRow = headingHasRow || rowVisible;
    }
    closeHeading();
    // A column of buttons with none showing leaves no empty cell on the hub.
    for (const columnEl of Array.from(level.querySelectorAll<HTMLElement>(".hub-group"))) {
      columnEl.hidden = Array.from(
        columnEl.querySelectorAll<HTMLElement>("[data-menu-item]"),
      ).every((row) => row.hidden);
    }
  }
  accountMenuRuleGroups.hidden = !anyGroup;
  const exceptionCount = state.managerExceptions.length;
  const chip = chipBadgeCount(has, badges);
  accountMenuChipCount.hidden = chip === 0;
  accountMenuChipCount.textContent = chip === 0 ? "" : String(chip);
  adminManagerExceptionsBadgeCount.textContent = String(exceptionCount);
  adminManagerExceptionsBadgeCount.hidden = exceptionCount === 0;
  renderNeedsYou(has, badges);
}

/** MGRDAYMAJ1 (manager-day-04): the Needs you strip on Open Tabs. Its rows are the menu badges, so the chip count, the group
 * rows and this strip cannot disagree; each row presses the same menu row it counts. Hidden when nothing is waiting. */
function renderNeedsYou(
  has: (id: string) => boolean,
  badges: Record<string, MenuBadge | undefined>,
): void {
  const unfinalized = state.menuCounts.unfinalizedBusinessDate;
  const words: Record<string, string> = {};
  if (unfinalized !== undefined) {
    const isYesterday =
      state.tenantBusinessDate !== undefined &&
      addDaysToIsoDate(unfinalized, 1) === state.tenantBusinessDate;
    words["closeout"] = `${isYesterday ? "Yesterday" : dayLabel(unfinalized)} is not finalized`;
  }
  const rows = needsYouRows(has, badges, words);
  needsYouEl.hidden = rows.length === 0;
  needsYouRowsEl.replaceChildren(
    ...rows.map((row) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = `needs-you-row needs-you-row-${row.tone}`;
      button.dataset["testid"] = `needs-you-row-${row.rowId}`;
      button.textContent = row.text;
      button.addEventListener("click", () => {
        document.querySelector<HTMLElement>(`[data-menu-item="${row.rowId}"]`)?.click();
      });
      return button;
    }),
  );
}

/** UX116: opens the hub for a group, the one place every route into a hub passes through: the account
 * menu's group rows, the "Menu" back button on an operations screen and the cart's "Ordering menu" button.
 * A person with no row in the group is not taken to an empty hub. */
function openFunctionHub(group: HubId | "reports"): void {
  const session = state.session;
  if (session === undefined) return;
  const has = (id: string): boolean => hasPermission(session, id);
  const source = ACCOUNT_MENU_GROUPS.find((candidate) => candidate.id === group);
  if (source === undefined || !groupVisible(source, has)) return;
  // RPTLAYOUT1-F1-S2 (owner ruling 2026-10-19, "new replaces old"): Reports is not a hub, it opens the Reports home.
  if (group === "reports") {
    closeAllAccountMenus();
    openOperationsScreen("reports", {});
    return;
  }
  closeAllAccountMenus();
  showFunctionHubScreen(group);
  void refreshMenuCounts();
  void refreshStartingStockMenu();
  void cashDrawer.refreshEntry();
}

function roleText(roles: readonly string[]): string {
  return roles
    .map((role) => {
      const words = role.replace(/_/g, " ").trim();
      return words.charAt(0).toUpperCase() + words.slice(1);
    })
    .join(", ");
}

/** Opens an operations view (Reports, Count stock, Stock levels, Match clients,
 * Update prices, Set starting stock) from the account menu. */
function openOperationsScreen(
  view: admin.OperationsView,
  options: { report?: ReportId; open?: string; range?: ReportRange; stockFilter?: "low" | "86" } = {},
): void {
  showAdminScreen();
  void admin.openOperationsView(requireSession(), view, options);
}

// LOCORDER1 (B6): the Cart / Orders / Receive strip that sat under the Ordering hub is gone; the Ordering menu button in the
// header is the way between the Ordering screens.

/** SUPPORT1: the permission that shows Get help here and that the support routes require (src/api/helpRequestRoutes.ts). */
const SUPPORT_HELP_PERMISSION = "admin.tenant_settings.view";

/** SUPPORT1 (DEC-514 item 4): Get help, Report a problem and My requests, drawn by ui/src/supportHelp.ts over this console's own
 * session. The location name is the one in the header; the page name is the heading on screen. */
const getHelp = createSupportHelp({
  async request<T>(method: "GET" | "POST", suffix: string, body?: unknown) {
    const session = state.session;
    if (session === undefined) return { status: 401, body: null };
    const result = await supportRequestCall<T>(session.apiBaseUrl, session.tenantId, method, suffix, body);
    return { status: result.status, body: result.body as T | null };
  },
  screen: () => ({
    route: routeFrom(window.location.pathname, window.location.hash),
    label: pageLabelFrom(visibleScreenName("h1.tabs-page-title, h1.reports-title"), "Snip & Sip"),
  }),
  locationName: () => floorLocationName() ?? null,
  buildRevision: bundledRevision,
  badge: () => document.getElementById("open-tabs-get-help-badge"),
});

/** HELPDOCS1 (docs/build-briefs/HELPDOCS1-guides-and-screen-help.md): the always-visible Help button. It opens the docked guide
 * panel (ui/src/helpPanel.ts) on the section for the screen on show (helpScreenMap.ts), over this console's own session. */
const helpButton = mustFind<HTMLButtonElement>("#topbar-help");
const helpPanel = createHelpPanel(helpButton, {
  roles: LOCATION_HELP_ROLES,
  roleStorageKey: "snipsip.help.role",
  async request<T>(suffix: string) {
    const session = state.session;
    if (session === undefined) return { status: 401, body: null };
    const result = await helpGuidesCall<T>(session.apiBaseUrl, session.tenantId, suffix);
    return { status: result.status, body: result.body as T | null };
  },
  async requestImage(name: string) {
    const session = state.session;
    if (session === undefined) return null;
    return helpImageCall(session.apiBaseUrl, session.tenantId, name);
  },
  target: () =>
    locationHelpTarget(window.location.hash, (testId) => {
      const node = document.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
      return node !== null && node.getClientRects().length > 0;
    }),
  contactSupport: () => {
    if (!getHelpButton.hidden) getHelp.openSheet();
  },
});
/** AIHELP1 (docs/build-briefs/AIHELP1-willy-ai-support-chat.md; DEC-574): Willy, the AI support chat. A conversation bubble for the
 * people who hold the Get help permission, on every screen but the floor's tabs list and ticket. It reads the same session and the
 * same screen map as the Help panel, and shares the right edge with it: one panel at a time. */
const willy = createWillyChat({
  async request<T>(method: "GET" | "POST", suffix: string, body?: unknown) {
    const session = state.session;
    if (session === undefined) return { status: 401, body: null };
    const result = await willyCall<T>(session.apiBaseUrl, session.tenantId, method, suffix, body);
    return { status: result.status, body: result.body as T | null };
  },
  target: () =>
    locationHelpTarget(window.location.hash, (testId) => {
      const node = document.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
      return node !== null && node.getClientRects().length > 0;
    }),
  contactSupport: (prefill) => {
    if (!getHelpButton.hidden) getHelp.openPrefilled("question", prefill);
  },
  closeHelp: () => helpPanel.close(),
  openGuide: (target) => helpPanel.openTarget(target),
  personName: () => state.session?.employeeId ?? "",
  canShow: () => {
    const session = state.session;
    return session !== undefined && hasPermission(session, SUPPORT_HELP_PERMISSION) && willyRouteAllowed(parseRoute(window.location.hash));
  },
});
helpButton.addEventListener("click", () => {
  closeAllAccountMenus();
  willy.close();
  if (helpPanel.isOpen()) helpPanel.close();
  else helpPanel.openForScreen();
});

/** Independent-review Finding B, 2026-08-26. Refreshes every topbar
 * control's own visibility from a session's resolved permissions -- the one
 * place every login/session-refresh call site reaches, so none of them can
 * drift out of sync with each other. UX80 (DEC-298 point 6): the Settings row
 * shows for someone holding at least one settings manage permission or
 * admin.tenant_settings.view; the operational rows (ordering, inventory,
 * reports, closeout) are gated one by one in refreshAccountMenuRows. */
function refreshManagerControlsVisibility(session: Session): void {
  possibleDuplicatesReport.refreshPermission();
  setSettingsControlsHidden(!hasAnyPermission(session, admin.SETTINGS_ENTRY_PERMISSIONS));
  setTerminalControlsHidden(!hasPermission(session, TERMINAL_PICKER_PERMISSION));
  // SUPPORT1: the same permission the support routes check (admin.tenant_settings.view); a floor-only person holds none of it.
  getHelpButton.hidden = !hasPermission(session, SUPPORT_HELP_PERMISSION);
  willy.refreshVisibility();
  refreshAccountMenuRows(session);
  // STK1: the 86 list button follows the same permission snapshot.
  render86ListButton();
}

interface AccountMenuHandle {
  trigger: HTMLButtonElement;
  dropdown: HTMLElement;
}

// UX17: one shared trigger/dropdown pair (down from five, one per removed
// per-screen copy) plus, since PAY5-F2, the top bar's own no-terminal
// panel (docs/build-briefs/PAY5-F2-terminal-panel-tap-outside.md): two
// independent trigger/dropdown pairs that share nothing but the generic
// open/close/outside-tap/Escape mechanics below. isAccountMenuOpen,
// closeAccountMenu, closeAllAccountMenus, the outside-tap listener, and
// the Escape-handler loop are all already generic over any
// AccountMenuHandle, so adding the terminal panel here is what makes it
// close on a tap outside the same way the account dropdown already does,
// with no new function.
const ACCOUNT_MENUS: readonly AccountMenuHandle[] = [
  { trigger: accountTrigger, dropdown: accountDropdown },
];

function isAccountMenuOpen(menu: AccountMenuHandle): boolean {
  return !menu.dropdown.hidden;
}

function closeAccountMenu(menu: AccountMenuHandle): void {
  menu.dropdown.hidden = true;
  menu.trigger.setAttribute("aria-expanded", "false");
}

/** Closes every OTHER open dropdown first: only one can usefully be open
 * at a time (there is only one visible screen), but this keeps state
 * honest even if that ever changes. */
function closeAllAccountMenus(except?: AccountMenuHandle): void {
  for (const menu of ACCOUNT_MENUS) {
    if (menu !== except) {
      closeAccountMenu(menu);
    }
  }
}

function toggleAccountMenu(menu: AccountMenuHandle): void {
  const opening = !isAccountMenuOpen(menu);
  closeAllAccountMenus(menu);
  if (opening && menu.dropdown === accountDropdown && state.session !== undefined) {
    refreshAccountMenuRows(state.session);
    void refreshMenuCounts();
    void refreshStartingStockMenu();
    void cashDrawer.refreshEntry();
    if (!getHelpButton.hidden) void getHelp.refresh();
  }
  menu.dropdown.hidden = !opening;
  menu.trigger.setAttribute("aria-expanded", opening ? "true" : "false");
}

/** Touch-only rule: dismissible by tapping outside. A plain document-level
 * listener rather than a scrim (this is a small menu, not a form; it must
 * not block the rest of the screen the way a modal sheet's scrim does). */
document.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Node)) {
    return;
  }
  for (const menu of ACCOUNT_MENUS) {
    if (!isAccountMenuOpen(menu)) {
      continue;
    }
    if (!menu.trigger.contains(target) && !menu.dropdown.contains(target)) {
      closeAccountMenu(menu);
    }
  }
});

/** UX80 (DEC-298): the group rows, the "Menu" back rows, every row that opens
 * an operations view, the Ordering switch and the operations screens' own
 * "Menu" button. The rows that open an existing screen (cart, orders, outside
 * order, closeout, void history, manager exceptions) keep their original
 * listeners. */
function wireOperationsMenu(): void {
  for (const group of ACCOUNT_MENU_GROUPS) {
    accountDropdown
      .querySelector<HTMLElement>(`[data-menu-group="${group.id}"]`)
      ?.addEventListener("click", () => openFunctionHub(group.id));
  }
  mustFind<HTMLButtonElement>("#account-menu-possible-duplicates").addEventListener("click", openPossibleDuplicates);
  const views: Array<[string, admin.OperationsView, ReportId | undefined]> = [
    ["account-menu-price-list", "priceList", undefined],
    ["account-menu-stock-levels", "stockLevels", undefined],
    ["account-menu-count-stock", "counts", undefined],
    ["account-menu-past-counts", "counts", undefined],
    ["account-menu-starting-stock", "startingStock", undefined],
    ["account-menu-transfers", "transfers", undefined],
    ["account-menu-match-clients", "matchClients", undefined],
  ];
  for (const [id, view, report] of views) {
    mustFind<HTMLButtonElement>(`#${id}`).addEventListener("click", () => {
      // UIFIX5: Past counts opens the closed counts alone; Count stock opens with Start and Resume on top.
      if (view === "counts") admin.setCountsLandingPast(id === "account-menu-past-counts");
      openOperationsScreen(view, report === undefined ? {} : { report });
    });
  }
  // CASH1-S1-U1: the Cash drawer row opens the drawer sheet over the Shift hub.
  mustFind<HTMLButtonElement>("#account-menu-cash-drawer").addEventListener("click", () => {
    void cashDrawer.openSheet();
  });
  adminOpsBackButton.addEventListener("click", () => {
    openFunctionHub(admin.operationsViewGroup() ?? "inventory");
  });
}

/** Wires the ONE shared dropdown (UX17: down from five per-screen copies,
 * wired identically five times; UX21 removed the dropdown's own Theme
 * item, so this no longer wires one): trigger toggles it, My Profile
 * navigates (hidden instead while already on the Profile screen,
 * setAccountMenuProfileMode), Settings navigates (closing the dropdown
 * first) and Log out logs out (closing the dropdown first, though
 * handleLogOut tears down the whole screen anyway). The shared HOME
 * button is wired separately, once, in init() below, reading the current
 * screen's own registered `onHome` at click time rather than being bound
 * to one screen's handler here -- see that wiring's own comment for why
 * (Review 31 finding 2: re-wiring a SHARED element's handler per
 * navigation risks duplicate listeners; a single wire-once site reading
 * state at click time cannot accumulate them). */
function wireAccountMenu(
  menu: AccountMenuHandle,
  parts: {
    myProfileButton: HTMLButtonElement;
    myTipsButton: HTMLButtonElement;
    settingsButton: HTMLButtonElement;
    logoutButton: HTMLButtonElement;
  },
): void {
  menu.trigger.addEventListener("click", () => toggleAccountMenu(menu));
  parts.myProfileButton.addEventListener("click", () => {
    closeAccountMenu(menu);
    handleOpenMyProfileClick();
  });
  parts.myTipsButton.addEventListener("click", () => {
    closeAccountMenu(menu);
    handleOpenMyTipsClick();
  });
  parts.settingsButton.addEventListener("click", () => {
    closeAccountMenu(menu);
    handleOpenManagerSettingsClick();
  });
  parts.logoutButton.addEventListener("click", () => {
    closeAccountMenu(menu);
    requestLogOut();
  });
}

// RT3 (DEC-038, resolving the backlog row of the same name): requirePermission
// re-resolves on every SERVER request, so a role-template or per-user
// override change already binds immediately and unconditionally there
// (proven end to end in test/role-templates-api.test.ts). Only the
// BROWSER's own hasPermission/hasAnyPermission gates lagged: they read the
// PermissionSnapshot handed out at login and at GET .../auth/session, and
// nothing re-read it afterward, so an already-signed-in user's menus could
// still show (or hide) an entry a template change had just changed,
// sometimes for a whole shift. The product owner ruled the fix directly:
// re-read the snapshot on every SCREEN NAVIGATION, never on a poll and
// never over a push channel this product does not have -- the server stays
// the authority regardless (a stale tap still gets that route's own 403),
// this only shortens how long the MENU can disagree with it.
//
// One in-flight refresh at a time, deliberately: overlapping navigations
// (a fast double-tap) do not each start their own request, and the guard
// alone is what keeps this a per-navigation refresh rather than something
// that could be mistaken for a poll -- there is no timer anywhere in this
// function or its caller.
let permissionSnapshotRefreshInFlight = false;

/** Called once per screen navigation (from hideAllScreens below, the one
 * place every show*Screen function already passes through). Fires the
 * GET .../auth/session re-read in the BACKGROUND -- never awaited by the
 * navigation itself -- so no screen change waits on a network round trip
 * and no loading state has to be invented for it. On a clean 200 it
 * refreshes state.session's role/roles/permissions/mustChangePin/
 * defaultThemeId exactly the way resumeFromStoredConfig's own page-load
 * probe already does, then re-renders the permission-gated chrome every
 * screen can carry (refreshManagerControlsVisibility) plus the admin
 * screen's own left rail when that happens to be the screen on view.
 *
 * Deliberately NOT withoutSessionLostHandling: that helper exists for a
 * probe that is ASKING whether a token is still valid at all (the
 * page-load resume path). This call is a background refresh of a session
 * already trusted enough to be showing screens, so a genuine 401 here
 * means the token really did die, and AU11's existing machinery
 * (api.ts's noteResponse/sessionLostHandler, wired once in startIdleWatch)
 * should run exactly as it does for any other authenticated request --
 * unchanged and untouched by this function. A network failure is
 * different and is handled explicitly below: fetch() itself throwing
 * (offline, DNS, a dropped connection) says nothing about the token, so
 * the OLD snapshot is kept rather than treated as a sign-out; getSession
 * reports that case as ApiResult.status 0 (api.ts's networkErrorResult),
 * which the `result.status !== 200` check below already treats as "leave
 * the snapshot alone", the same as any other non-200 answer including a
 * 401 (noteResponse's own handler deals with that one, not this code). */
function refreshPermissionSnapshotOnNavigation(): void {
  const session = state.session;
  if (session === undefined || permissionSnapshotRefreshInFlight) {
    return;
  }
  permissionSnapshotRefreshInFlight = true;
  void (async () => {
    try {
      const result = await getSession(session.apiBaseUrl, session.tenantId);
      if (result.status !== 200) {
        return;
      }
      // The session this request was made for may have already changed
      // identity (a logout, or a fresh login) while it was in flight;
      // only apply a stale response to the SAME session it was requested
      // for, never to whatever is current now.
      const current = state.session;
      if (current === undefined || current.token !== session.token) {
        return;
      }
      const sessionUser = result.body.user;
      const refreshed: Session = {
        ...current,
        role: sessionUser.role,
        roles: sessionUser.roles,
        permissions: result.body.permissions,
        mustChangePin: sessionUser.mustChangePin,
        defaultThemeId: sessionUser.defaultThemeId,
        // T16/DEC-133: same "fresh server value wins" reasoning as the
        // fields immediately above.
        pinMinLength: result.body.pinMinLength,
        pinMaxLength: result.body.pinMaxLength,
      };
      saveSession(refreshed);
      state.session = refreshed;
      refreshManagerControlsVisibility(refreshed);
      if (!adminScreen.hidden) {
        admin.refreshTabVisibility(refreshed);
      }
      // VOC1-F8-F8 (independent review, 2026-09-22): the floor's own
      // recipe controls (renderMenuGrid's tile-recipe-button,
      // buildTicketLineRow's line-recipe-button and the edit sheet's
      // line-edit-view-recipe) are gated on this same snapshot, but
      // neither the menu grid nor the ticket lines otherwise re-render
      // just because this snapshot changed -- only a real navigation
      // (loadMenu, enterTicket) rebuilds them from a network fetch. Both
      // calls below re-render from state already held client-side (no
      // network round trip), so a mid-session permission revoke reaches
      // an already-open ring screen's own controls on the very next
      // navigation rather than only the next full menu/ticket reload. The
      // route stays the real, unbypassable gate regardless
      // (fetchMenuDrinkDetailForRecipeView); this only keeps the CONTROL
      // from lagging behind it.
      if (state.menuItems.length > 0) {
        renderMenuGrid();
      }
      if (state.orderId !== undefined) {
        renderTicket();
      }
    } finally {
      permissionSnapshotRefreshInFlight = false;
    }
  })();
}

// ---------------------------------------------------------------------
// Screen visibility
//
// UX17 (docs/adr/ADR-004-ui-composition.md Decision 3, as amended;
// docs/build-briefs/UX17-persistent-navigation.md). Screens register as
// DATA instead of hideAllScreens naming fourteen variables by hand.
// SURFACE (floor or admin) is never stored on a registration: it is
// derived by checking scripts/floorSurfaces.ts's own FLOOR_HTML_ROOTS,
// the one existing home for that fact (two gates already read it and
// must never be able to drift apart from each other, per that file's own
// header) -- restating it here would be a second home for the same fact,
// exactly what ADR-004 Decision 3 was amended to refuse after
// independent review. `onHome` is this screen's own "return to Open
// Tabs" behavior where it differs from the shared default (a ticket-rail
// reset on the ring screen, a menu refresh leaving Settings, a timer
// teardown leaving Closeout, no reload at all leaving Profile); the
// shared home button (wired ONCE, in setupEventListeners, never re-wired
// per navigation -- see that wiring's own comment for why) reads
// `currentScreenId` at click time and calls whichever screen is
// currently active own `onHome`, defaulting to `defaultTopbarHome`.
// `secondaryAction` is the one screen-specific destination the shared
// topbar still carries beneath the home button (DEC-059: "screen-specific
// chrome... may still change beneath it") -- today only the receiving
// screen's own "back to the order it came from", genuinely distinct from
// home; see the build brief for why every other screen's former second
// button was dropped as a duplicate of its own home icon instead of
// becoming a second registry field.
// ---------------------------------------------------------------------

type ScreenSurface = "floor" | "admin";

interface ScreenRegistration {
  readonly id: string;
  readonly element: HTMLElement;
  /** Derived, never hand-set: see registerScreen below. */
  readonly surface: ScreenSurface;
  /** Whether the shared topbar (#app-topbar) shows while this screen is
   * active. False only for the five pre-auth/mid-auth screens, which
   * never carried a topbar of their own either. */
  readonly hasTopbar: boolean;
  readonly onHome?: () => void;
  readonly secondaryAction?: { readonly label: string; readonly onClick: () => void };
  /** UX30: whether the shared topbar's pricing-model badge shows while
   * this screen is active. True only for the ring-screen registration;
   * every other registration leaves it unset (falsy), the same
   * default-off shape `secondaryAction` already uses. */
  readonly showsPricingModel?: boolean;
}

/** Builds one registration, computing `surface` from
 * scripts/floorSurfaces.ts's own FLOOR_HTML_ROOTS rather than accepting it
 * as a parameter -- the one place this file touches that fact, so no
 * caller below can accidentally hand-type "floor" or "admin" and create
 * the second home for it ADR-004 Decision 3 was amended to refuse. */
function registerScreen(
  id: string,
  element: HTMLElement,
  rest: Omit<ScreenRegistration, "id" | "element" | "surface">,
): ScreenRegistration {
  return { id, element, surface: FLOOR_HTML_ROOTS.includes(id) ? "floor" : "admin", ...rest };
}

const SCREENS: readonly ScreenRegistration[] = [
  registerScreen("terminal-setup-screen", terminalSetupScreen, { hasTopbar: false }),
  registerScreen("welcome-screen", welcomeScreen, { hasTopbar: false }),
  registerScreen("terminal-picker-screen", terminalPickerScreen, { hasTopbar: false }),
  registerScreen("user-picker-screen", userPickerScreen, { hasTopbar: false }),
  registerScreen("approval-picker-screen", approvalPickerScreen, { hasTopbar: false }),
  registerScreen("pin-entry-screen", pinEntryScreen, { hasTopbar: false }),
  registerScreen("profile-screen", profileScreen, {
    hasTopbar: true,
    onHome: () => handleProfileBackClick(),
  }),
  registerScreen("notifications-screen", notificationsScreen, {
    hasTopbar: true,
    onHome: () => showOpenTabsScreen(),
  }),
  registerScreen("setup-wizard-screen", setupWizardScreen, { hasTopbar: false }),
  registerScreen("open-tabs-screen", openTabsScreen, {
    hasTopbar: true,
    onHome: () => void loadOpenTabs(),
  }),
  registerScreen("admin-screen", adminScreen, {
    hasTopbar: true,
    onHome: () => handleAdminBackToTabsClick(),
  }),
  registerScreen("function-hub-screen", functionHubScreen, { hasTopbar: true }),
  registerScreen("possible-duplicates-screen", possibleDuplicatesScreen, { hasTopbar: true, onHome: () => openFunctionHub("shift") }),
  registerScreen("closeout-screen", closeoutScreen, {
    hasTopbar: true,
    onHome: () => handleCloseoutBackToTabsClick(),
  }),
  registerScreen("void-history-screen", voidHistoryScreen, {
    hasTopbar: true,
    onHome: () => handleVoidHistoryBackToTabsClick(),
  }),
  // LOCORDER1 (B6): the Cart / Orders / Receive strip is gone, so Purchase Orders and the outside purchase screen get the same
  // "Ordering menu" way back the cart has.
  registerScreen("purchasing-orders-screen", purchasingOrdersScreen, {
    hasTopbar: true,
    secondaryAction: { label: "Ordering menu", onClick: () => openFunctionHub("ordering") },
  }),
  // UX66 part C: an explicit "Back to Purchasing" control, replacing the
  // topbar Home button's former fall-through to defaultTopbarHome (Open
  // Tabs, a floor screen this admin-only flow never came from), the same
  // secondaryAction shape purchasing-receiving-screen below already uses
  // to return to Purchase Orders.
  registerScreen("purchasing-cart-screen", purchasingCartScreen, {
    hasTopbar: true,
    secondaryAction: {
      label: "Ordering menu",
      onClick: () => handlePurchasingCartBackToPurchasingClick(),
    },
  }),
  registerScreen("purchasing-receiving-screen", purchasingReceivingScreen, {
    hasTopbar: true,
    secondaryAction: {
      label: "Purchase Orders",
      onClick: () => handlePurchasingReceivingBackToOrdersClick(),
    },
  }),
  registerScreen("purchasing-outside-order-screen", purchasingOutsideOrderScreen, {
    hasTopbar: true,
    secondaryAction: { label: "Ordering menu", onClick: () => openFunctionHub("ordering") },
  }),
  registerScreen("ordering-screen", orderingScreen, {
    hasTopbar: true,
    secondaryAction: { label: "Ordering menu", onClick: () => openFunctionHub("ordering") },
  }),
  registerScreen("receive-scan-screen", receiveScanScreen, { hasTopbar: true }),
  registerScreen("ring-screen", ringScreen, {
    hasTopbar: true,
    onHome: () => handleBackToTabsClick(),
    showsPricingModel: true,
  }),
];

/** Every screen not given its own `onHome` above (the three purchasing
 * screens whose own former "Open Tabs" button called exactly this, read
 * side by side before consolidating -- see the build brief) shares this. */
function defaultTopbarHome(): void {
  showOpenTabsScreen();
  void loadOpenTabs();
}

/** Which registered screen is currently on screen, so the shared home
 * button and secondary-action button (each wired exactly ONCE, never
 * re-wired per navigation) can look up the right behavior at click time
 * instead of being re-attached to a specific screen's handler. */
let currentScreenId: string | undefined;
// Closeout reads belong to one screen visit and latest requested day.
let closeoutReadSequence = 0;

/** UX17-F1: this used to render the raw tenant UUID (session.tenantId).
 * No location-name field exists anywhere in this system (no tenants
 * table, no name column on tenant_settings) for a frontend-only item to
 * bind to instead, so this returns undefined unconditionally today; the
 * shared topbar shows nothing in that slot rather than a raw id that is
 * not information to a bartender or manager. Kept as its own function,
 * not inlined, so the day a location-name field exists this is the one
 * place that changes. */
function resolveLocationDisplayName(_session: Session): string | undefined {
  return floorLocationName();
}

/** UX96 (DEC-356): the one name the floor header shows, beside the home button: the location's name, or the
 * customer's name when this device holds no location name. Never both. */
function floorLocationName(): string | undefined {
  return floorLocationNameFor(terminalConfigState);
}

/** TEN12 (DEC-338): every title that names the business reads the customer name kept on this device (set at
 * setup, refreshed from the server), and the product name before the device knows its customer. */
function renderBrandName(): void {
  const name = brandNameFor(terminalConfigState);
  for (const element of document.querySelectorAll<HTMLElement>("[data-brand-name]")) {
    if (element.textContent !== name) element.textContent = name;
  }
  // UX99 (DEC-357): the cards always draw the full lockup; the location name below it is the heading (the location's
  // name, the customer's name when no location name is known, never both), and nothing before the device knows one.
  const location = floorLocationName() ?? "";
  for (const element of document.querySelectorAll<HTMLElement>("[data-location-name]")) {
    if (element.textContent !== location) element.textContent = location;
    element.toggleAttribute("hidden", location === "");
  }
  document.title = `${name} - Ticket`;
  sessionTenant.textContent = floorLocationName() ?? "";
  fitTopbarBrand();
}

/** UX96 (DEC-356): the logo is centered by the header's grid (equal side columns), so it is centered only while
 * the right group fits in its own column. This compacts the right group, one level at a time (gaps, the reader as a
 * short chip, the pricing model as a short chip, the user as initials), until it fits beside the lockup, and shows
 * the mark alone when no level fits beside the lockup. The logo stays centered in every case. */
function measureTopbarAt(lane: HTMLElement, level: TopbarCompactLevel): TopbarMeasure {
  const flags: Array<[string, boolean]> = [
    ["cGap", level >= 1],
    ["cReader", level >= 2],
    ["cPricing", level >= 3],
    ["cUser", level >= 4],
    ["cTight", level >= 5],
  ];
  for (const [name, on] of flags) {
    if (on) appTopbar.dataset[name] = "";
    else delete appTopbar.dataset[name];
  }
  // A control squeezed to no width is still there: only an element that is not rendered at all is left out.
  const widths = [...lane.children]
    .filter((child) => child.getClientRects().length > 0)
    .map((child) => (child as HTMLElement).offsetWidth);
  const spacing =
    (parseFloat(getComputedStyle(lane).columnGap) || 0) * Math.max(0, widths.length - 1);
  const bar = getComputedStyle(appTopbar);
  return {
    right: widths.reduce((sum, w) => sum + w, 0) + spacing,
    gap: parseFloat(bar.columnGap) || 0,
    inner:
      appTopbar.clientWidth -
      (parseFloat(bar.paddingLeft) || 0) -
      (parseFloat(bar.paddingRight) || 0),
  };
}

function fitTopbarBrand(): void {
  if (appTopbar.hidden) return;
  const lane = appTopbar.querySelector<HTMLElement>(".topbar-right");
  if (lane === null) return;
  const logoHeight =
    parseFloat(getComputedStyle(appTopbar.querySelector(".topbar-logo")!).height) || 32;
  const markHeight =
    parseFloat(getComputedStyle(appTopbar.querySelector(".topbar-mark")!).height) || 32;
  const layout = chooseTopbarLayout(
    logoHeight * TOPBAR_LOGO_ASPECT,
    markHeight * TOPBAR_MARK_ASPECT,
    (level) => measureTopbarAt(lane, level),
  );
  measureTopbarAt(lane, layout.level);
  if (appTopbar.dataset["brandFit"] !== layout.center)
    appTopbar.dataset["brandFit"] = layout.center;
  if (layout.fits) delete appTopbar.dataset["brandOverflow"];
  else appTopbar.dataset["brandOverflow"] = "";
}

// The right group's children keep their own natural size (styles.css .topbar-right > *), so observing them
// catches a label changing (Online, the reader, Pricing) as well as a button appearing.
const topbarFitObserver = new ResizeObserver(() => fitTopbarBrand());
topbarFitObserver.observe(appTopbar);
const topbarRightLane = appTopbar.querySelector(".topbar-right");
for (const child of topbarRightLane?.children ?? []) topbarFitObserver.observe(child);
// A control added to the right group later is measured too.
if (topbarRightLane !== null) {
  new MutationObserver((records) => {
    for (const record of records) {
      for (const added of record.addedNodes)
        if (added instanceof Element) topbarFitObserver.observe(added);
    }
    fitTopbarBrand();
  }).observe(topbarRightLane, { childList: true });
}

/** TEN12 (DEC-338): asks the server for the names it holds for this device and keeps them, so a rename in the
 * software owner console reaches a device that was set up before it. Display only: a failed answer changes
 * nothing, and a revoked credential is handled by the sign-in screen's own roster read. */
async function refreshDeviceIdentity(): Promise<void> {
  const before = terminalConfigState;
  if (before === undefined) return;
  const result = await getDeviceIdentity(before.apiBaseUrl, before.tenantId, before.deviceToken);
  if (result.status !== 200) return;
  const body = result.body;
  const latest = terminalConfigState;
  // The device was set up again, or forgotten, while the request ran: this answer is for the old one.
  if (
    latest === undefined ||
    latest.tenantId !== before.tenantId ||
    latest.deviceToken !== before.deviceToken
  ) {
    return;
  }
  const merged = applyDeviceIdentity(latest, {
    businessName: body.businessName,
    locationName: body.locationName,
    deviceName: body.deviceName,
  });
  saveTerminalConfig(merged);
  terminalConfigState = merged;
  renderBrandName();
  renderPickerDeviceLine(merged);
}

/** UX4's own profile-screen exception (no My Profile entry, see
 * index.html's own comment on the removed profile dropdown), now applied
 * to the ONE shared dropdown instead of simply being absent from a
 * screen-specific copy. UX21 removed the dropdown's own Theme item
 * entirely, so there is nothing left to hide for that entry. */
function setAccountMenuProfileMode(isProfileScreen: boolean): void {
  accountMyProfileButton.hidden = isProfileScreen;
  accountNotificationsButton.hidden = currentScreenId === "notifications-screen";
}

/** UX29, connectivity-only since PAY5-F1 (docs/build-briefs/PAY5-F1-
 * terminal-notice-in-topbar.md): this badge used to also carry the
 * terminal segment, joined by a middot; that segment now lives in
 * #topbar-terminal-status/#topbar-terminal-warning instead
 * (renderTopbarTerminalSegment below), rendered separately so a device
 * with no terminal set can become a real, independently tappable
 * control rather than half of one inert text node. Independent of any
 * session (readable pre-login too, though the topbar itself is hidden
 * then), and called both from showTopbarChrome (screen navigation) and
 * directly from the connectivity observer/online/offline listeners
 * registered in init() below, since those fire without any screen
 * navigation happening (Section 4 of the original UX29 build brief: the
 * discriminating case is the badge updating WHILE the employee stays on
 * the same screen). */

/** Shows or hides the shared topbar for the screen about to become
 * active, and renders every part of it that depends on WHICH screen that
 * is (session badge/name/location, the secondary-action button, the
 * profile-mode dropdown exception). Called by every show*Screen function
 * below after it un-hides its own element; hideAllScreens (below) hides
 * the topbar by default, so the five pre-auth screens need not call this
 * at all. */
function showTopbarChrome(id: string): void {
  currentScreenId = id;
  const registration = SCREENS.find((s) => s.id === id);
  if (registration === undefined || !registration.hasTopbar) {
    appTopbar.hidden = true;
    systemChecksStrip.hide();
    return;
  }
  appTopbar.hidden = false;
  systemChecksStrip.hide();
  renderPurchaseApprovalNotice();
  void networkOfferNotice.refresh();
  const session = requireSession();
  sessionTenant.textContent = resolveLocationDisplayName(session) ?? "";
  sessionEmployeeBadge.textContent = employeeInitials(session.employeeId);
  sessionEmployeeName.textContent = session.employeeId;
  accountMenuWhoBadge.textContent = employeeInitials(session.employeeId);
  accountMenuWhoName.textContent = session.employeeId;
  accountMenuWhoRole.textContent = roleText(session.roles);
  // PAY5-F1: every floor screen with a topbar shows this device's own
  // terminal-assignment state (DEC-196's own "on every floor screen"),
  // not only the ring screen while a ticket happens to be open.
  renderTopbarTerminalSegment();
  placeTopbarSecondaryAction(id, registration.secondaryAction?.label);
  // UX30: read fresh on every render rather than once at module load, so
  // a mid-shift pricing-model change (loadMenu re-fetches it at login and
  // again on leaving the admin screen) is reflected the next time the
  // session reaches the ring screen.
  // UIFIX2 (SF-13): the pill is a diagnostics aid, not staff chrome.
  topbarPricingModelEl.hidden = !(registration.showsPricingModel && diagnosticsMode);
  if (registration.showsPricingModel && diagnosticsMode) {
    topbarPricingModelEl.textContent = `Pricing: ${pricingModelLabel(state.pricingModel)}`;
    // UX96: the short chip a tight header shows (styles.css), the full text staying in the element for readers.
    topbarPricingModelEl.dataset["short"] = pricingModelLabel(state.pricingModel);
  }
  setAccountMenuProfileMode(id === "profile-screen");
  // UX119: the 86 list count on the Reports hub button follows the screen change.
  render86ListButton();
  // UX108: every signed-in screen publishes where it is, so a refresh can come back to it. The admin screen's
  // address depends on what its caller opens next (a Settings group or an operations view), which is set in the
  // same synchronous run, so it publishes one microtask later rather than guessing from the previous visit.
  if (id === "admin-screen") {
    queueMicrotask(() => requestRoutePublish());
  } else {
    requestRoutePublish();
  }
}

// Auth track (track/auth), Slice 1: four screens joined the original
// five (terminal setup, the user-tile picker, the shared PIN pad, My
// Profile), replacing the single free-text #login-screen. Every show*
// function below still follows the same "hide everything else, show
// exactly one" shape the pre-auth version established; hideAllScreens
// centralizes the "everything else" half so a fifteenth screen never has
// to remember to blank fourteen others by hand.
//
// RT3 (DEC-038): hideAllScreens is also the one place every screen
// navigation already passes through (every show*Screen function below
// calls it first), so it is the central hook the permission-snapshot
// refresh above is fired from -- one call site rather than one per screen.

function hideAllScreens(): void {
  closeoutReadSequence += 1;
  purchasingVendorNavigation += 1;
  admin.clearVendorOptionReturn();
  possibleDuplicatesReport.reset();
  readerDropdown.close();
  // PO7-S5: leaving the scan screen by any route stops its camera and its reading wait.
  receiveScanController.stop();
  orderingController.stop();
  refreshPermissionSnapshotOnNavigation();
  for (const screen of SCREENS) {
    screen.element.hidden = true;
  }
  // UX108: the first real screen has been chosen, so the empty boot shell has done its job.
  bootShell.hidden = true;
  // UX108: a page with nobody signed in carries no address. Signed in, the screen about to be shown publishes its
  // own (showTopbarChrome), so this is the only place the hash is ever cleared.
  if (state.session === undefined) {
    clearRoute();
  }
  appTopbar.hidden = true;
  systemChecksStrip.hide();
  topbarApprovalBadge.hidden = true;
  networkOfferNotice.reset();
  closePurchaseCancelDialog();
  currentScreenId = undefined;
  // PL3: a payment link is only re-checked while the screen that shows its
  // ticket is the one on screen. Every screen change ends the poll; the
  // ring screen and Open Tabs each restart their own when they come back.
  stopPaymentLinkWatches();
  // AU8: the ONE overlay this file wires to open from two different
  // places (Settings > Operations' own entry point and the ticket rail's
  // own banner), unlike every other scrim-backed sheet here, which each
  // belongs to a single screen and is always closed by its own flow
  // before navigation. Closed defensively on every screen change so it
  // can never appear to hang over whichever screen comes next.
  closeManagerExceptionsSheet();
  // B6: the scan overlay belongs to the receiving screen; leaving it (or signing out) stops the camera and drops the list.
  barcodeReceive.stop();
}

function showTerminalSetupScreen(options: { notice?: string } = {}): void {
  hideAllScreens();
  terminalSetupScreen.hidden = false;
  terminalSetup.show(options);
}

function showWelcomeScreen(): void {
  hideAllScreens();
  welcomeScreen.hidden = false;
}

function showUserPickerScreen(): void {
  hideAllScreens();
  userPickerScreen.hidden = false;
}



// RBAC track: manager-approval flow, step 1 of 2.
function showTerminalPickerScreen(): void {
  hideAllScreens();
  terminalPickerScreen.hidden = false;
}

function showApprovalPickerScreen(): void {
  hideAllScreens();
  approvalPickerScreen.hidden = false;
}

function showPinEntryScreen(): void {
  hideAllScreens();
  pinEntryScreen.hidden = false;
}

// Reached from the shared topbar's "My Profile" button, from wherever it
// was tapped. #profile-back's own former destination (home) always
// returned to Open Tabs rather than tracking which screen this visit
// came from (documented judgment call, per the work item's own "document
// that choice" instruction, now realized as this screen's own `onHome`
// in the SCREENS registry above): Open Tabs is reachable from every
// entry point and is this app's own "home", and a manager who opened My
// Profile from Settings can get back there in the same one tap they used
// to leave it.
function showProfileScreen(): void {
  hideAllScreens();
  profileScreen.hidden = false;
  showTopbarChrome("profile-screen");
  const session = requireSession();
  profileNameEl.textContent = session.employeeId;
  // USR4: "Your details" loads from the server every time the screen is
  // shown, not from whatever the session object happens to carry (the
  // session only ever carries displayName, under the employeeId field
  // name -- see submitLoginPin above) -- fire-and-forget, matching this
  // file's own showUserPickerScreen/loadUserPicker pattern.
  void loadProfileDetails();
  // TEN17: a brand saved since sign-in shows in the Color scheme list the next time My Profile opens.
  void refreshMemberBrand();
}

/** NOTIFPREF1: the person's own email choices, over this tablet's staff session. */
const notificationsPage = createNotificationsPage(mustFind<HTMLElement>("#notifications-body"), {
  testPrefix: "notifications",
  addressHint: "You have no email address on file. Add one in My Profile to choose your emails.",
  onBack: () => showOpenTabsScreen(),
  // A 401 is handled once for the whole page by api.ts (the session-lost handler); nothing more to do here.
  sessionLost: () => undefined,
  transport: {
    read: async () => {
      const s = state.session;
      return s === undefined ? { status: 401, body: null } : getMyNotificationPreferences(s.apiBaseUrl, s.tenantId);
    },
    save: async (kind, enabled) => {
      const s = state.session;
      return s === undefined
        ? { status: 401, body: null }
        : putMyNotificationPreference(s.apiBaseUrl, s.tenantId, { kind, enabled });
    },
  },
});

/** NOTIFPREF1: the person's own email choices. Back and Home both return to Open Tabs, as My Profile does. */
function showNotificationsScreen(): void {
  hideAllScreens();
  notificationsScreen.hidden = false;
  showTopbarChrome("notifications-screen");
  void notificationsPage.load();
}

/** ONB4 slice 1: the wizard owns the whole screen, with no shared topbar. */
function showSetupWizardScreen(): void {
  hideAllScreens();
  setupWizardScreen.hidden = false;
  showTopbarChrome("setup-wizard-screen");
}

function showOpenTabsScreen(): void {
  // CLOSEDVIEW1: leaving a closed ticket that was opened to look at ends the view and lets go of the ticket.
  if (state.closedView !== undefined) {
    resetTicketState();
  }
  hideAllScreens();
  openTabsScreen.hidden = false;
  showTopbarChrome("open-tabs-screen");
  // CARDNUDGE1: every way onto Open Tabs (sign-in, a reload, the home button, leaving the wizard) reads the card
  // payments state fresh; the card hides itself when it has nothing to say.
  void cardNudge.refresh();
  // Gate finding 7, 2026-08-27: this screen renders its own "Recently
  // closed, this shift" list from state EVERY time it is shown, so the
  // rows on it can never outlive the state behind them. Before this,
  // renderRecentlyClosed ran only inside loadOpenTabs, so any path that
  // re-showed Open Tabs without a refetch (the approval flow's own
  // returnTo, the profile and terminal-picker Back buttons) painted
  // whatever rows were last rendered -- including a live Reopen button
  // for a tab that had since been reopened. Tapping that stale row mints
  // a new idempotency key and spends a second manager approval on an
  // action that can only fail.
  renderRecentlyClosed();
  // PL3: resume the payment-link check for the tabs already listed; the
  // list reload that normally follows re-syncs it from fresh rows.
  syncOpenTabsPaymentLinkWatch();
}

// ALPHA-10 item 3: a separate screen from the bartender selling surface,
// per the work item's own "plainly labeled as manager settings, not
// bartender-facing" instruction. Reached only from the shared topbar's
// "Settings" item, never from the ring screen.
// RBAC track: exported so admin.ts's own count-session-close approval
// flow (handleConfirmCloseCountSession) can return to this screen after a
// Cancel or a completed approval, the same "caller states its own return
// screen" shape requestApproval's own returnTo parameter uses everywhere
// else (see this file's "Manager approval" section below).
export function showAdminScreen(): void {
  hideAllScreens();
  adminScreen.hidden = false;
  showTopbarChrome("admin-screen");
}

/** HIGFIX3 (F-21; design/style-guide.md "Page header"): the way back sits at the left of the page header, before the title,
 * never at the right of the console header. The one shared secondary-action button keeps its id, test id and wiring; this only
 * decides where it is drawn. A screen with a secondary action and a visible page header gets it as that header's first child
 * (the "Menu" button style, .page-back); any other screen keeps it hidden in the console header where it always lived. */
function visiblePageHeader(screenId: string): HTMLElement | undefined {
  const screen = document.getElementById(screenId);
  if (screen === null) return undefined;
  for (const header of screen.querySelectorAll<HTMLElement>(".tabs-page-header")) {
    let hiddenAbove = header.hidden;
    for (let node = header.parentElement; node !== null && node !== screen && !hiddenAbove; node = node.parentElement) {
      hiddenAbove = node.hidden;
    }
    if (!hiddenAbove) return header;
  }
  return undefined;
}
function placeTopbarSecondaryAction(screenId: string, label: string | undefined): void {
  const button = topbarSecondaryActionButton;
  const header = label === undefined ? undefined : visiblePageHeader(screenId);
  if (header !== undefined) {
    header.prepend(button);
    button.classList.add("page-back");
  } else {
    button.classList.remove("page-back");
    const home = topbarSecondaryHome;
    if (home.parent !== null && button.parentElement !== home.parent) home.parent.insertBefore(button, home.next);
  }
  button.hidden = label === undefined;
  button.textContent = label ?? "";
}

/** UX116: one of the four function hubs (Reports, Inventory, Ordering, Shift and closeout). The hub draws its
 * own tiles; the buttons on it are the rows the old drill-in list held. */
function showFunctionHubScreen(hub: HubId): void {
  hideAllScreens();
  functionHubScreen.hidden = false;
  currentHubId = hub;
  showTopbarChrome("function-hub-screen");
  openHub(requireSession(), hub);
}

/** The hub on screen, for its address. Set by showFunctionHubScreen, read by currentScreenRoute. */
let currentHubId: HubId | undefined;

/** INVDESIGN1-S2: where the Inventory home's buttons go. Build order saves that vendor's suggested lines as a draft and opens
 * the ordering slice's build page; Edit amounts opens What to order on that vendor. */
function openInventoryHomeTarget(target: InventoryHomeTarget): void {
  switch (target.kind) {
    case "view":
      if (target.view === "counts") admin.setCountsLandingPast(target.past === true);
      openOperationsScreen(target.view, {});
      return;
    case "build-order":
      showOrderingScreen({ kind: "what-to-order", vendorId: target.vendorId, build: true });
      return;
    case "edit-amounts":
      showOrderingScreen({ kind: "what-to-order", vendorId: target.vendorId });
      return;
    case "orders":
      showPurchasingOrdersScreen();
      return;
    case "settings":
      handleOpenManagerSettingsClick();
      return;
  }
}

/** INVDESIGN1-S2: the Inventory hub with "Count closed" and what to order, right after a count closes. */
function showInventoryAfterCount(summary: CountClosedSummary): void {
  const session = state.session;
  if (session === undefined) return;
  closeAllAccountMenus();
  hideAllScreens();
  functionHubScreen.hidden = false;
  currentHubId = "inventory";
  showTopbarChrome("function-hub-screen");
  openHub(session, "inventory", { afterCount: summary });
  void refreshMenuCounts();
}

/** UX116: what a tile does when tapped: the same navigation the matching old menu row made. */
function openHubTarget(target: HubTarget): void {
  switch (target.kind) {
    case "view":
      openOperationsScreen(
        target.view,
        target.stockFilter === undefined ? {} : { stockFilter: target.stockFilter },
      );
      return;
    case "cart":
      showPurchasingCartScreen();
      return;
    case "orders":
      showPurchasingOrdersScreen();
      return;
    case "closeout":
      void handleOpenCloseoutSummaryClick(target.date);
      return;
  }
}

// ALPHA-08: reached only from Settings > Operations' own "Closeout
// Summary" button (UX18, moved from Open Tabs' own former topbar
// button), manager-flavored but staff-readable (design/principles.md
// rule 5), never part of the bartender ring flow.
function showCloseoutScreen(): void {
  hideAllScreens();
  closeoutScreen.hidden = false;
  showTopbarChrome("closeout-screen");
}

// M12: reached only from Settings > Operations' own "Void History"
// button, mirroring showCloseoutScreen immediately above exactly -- a
// manager-flavored report screen sibling to Closeout Summary, never part
// of the bartender ring flow.
function showVoidHistoryScreen(): void {
  hideAllScreens();
  voidHistoryScreen.hidden = false;
  showTopbarChrome("void-history-screen");
}

// PO6 Slice 1, moved by PO6-R1 (DEC-056): reached only from Settings >
// Purchasing's own "Purchase Orders" button, mirroring showCloseoutScreen
// above (a manager-flavored report screen, never part of the bartender
// ring flow). Always opens on the list view; the detail view is a
// second, in-screen toggle (see
// showPurchasingOrderDetail below) rather than its own screen, so "Back
// to orders" never re-fetches the list.
function showPurchasingOrdersScreen(): void {
  purchasingOrderDetailRouteId = undefined;
  hideAllScreens();
  purchasingOrdersScreen.hidden = false;
  showTopbarChrome("purchasing-orders-screen");
  purchasingOrderDetailView.hidden = true;
  purchasingOrdersListView.hidden = false;
  purchasingOrdersStatusEl.hidden = true;
  purchasingOrdersStatusEl.textContent = "";
  purchasingOrdersEmptyEl.hidden = true;
  purchasingOrdersListEl.innerHTML = '<p class="tabs-loading">Loading…</p>';
  void loadPurchasingOrdersList();
}

async function loadPurchasingOrdersList(): Promise<void> {
  const session = requireSession();
  const result = await listPurchaseOrders(session.apiBaseUrl, session.tenantId);
  if (result.status !== 200) {
    purchasingOrdersListEl.innerHTML = "";
    purchasingOrdersStatusEl.hidden = false;
    purchasingOrdersStatusEl.textContent =
      result.body.message ?? "Could not load purchase orders. Check your connection.";
    return;
  }
  renderPurchasingOrdersList(result.body.orders);
}

function renderPurchasingOrdersList(orders: readonly PurchaseOrderSummaryDto[]): void {
  purchasingOrdersListEl.innerHTML = "";
  if (orders.length === 0) {
    purchasingOrdersEmptyEl.hidden = false;
    return;
  }
  purchasingOrdersEmptyEl.hidden = true;
  // LOCORDER1 (B6, design-language rule 8): the page's question is "what is arriving", so orders that can still be
  // received lead under Deliveries to receive, and every other order follows under Other orders. Same rows, same buttons.
  const waiting = orders.filter((order) => purchaseOrderIsReceivable(order.status));
  const others = orders.filter((order) => !purchaseOrderIsReceivable(order.status));
  const heading = (text: string, testId: string, note?: string): HTMLElement => {
    const head = document.createElement("div");
    head.className = "cart-section-head purchasing-orders-group-head";
    const title = document.createElement("h2");
    title.className = "section-title";
    title.dataset["testid"] = testId;
    title.textContent = text;
    head.append(title);
    if (note !== undefined) {
      const quiet = document.createElement("p");
      quiet.className = "cart-section-empty";
      quiet.textContent = note;
      head.append(quiet);
    }
    return head;
  };
  purchasingOrdersListEl.append(
    heading(
      "Deliveries to receive",
      "purchasing-orders-group-deliveries",
      waiting.length === 0 ? "Nothing is waiting to be received." : `${waiting.length} on order`,
    ),
  );
  if (waiting.length > 0) purchasingOrdersListEl.append(buildPurchasingOrderTable(waiting));
  if (others.length > 0) {
    purchasingOrdersListEl.append(heading("Other orders", "purchasing-orders-group-other"));
    purchasingOrdersListEl.append(buildPurchasingOrderTable(others));
  }
}

/** HIGFIX4A (F-29): one table for the orders of a group, a header row of column names over the same rows as before. */
function buildPurchasingOrderTable(orders: readonly PurchaseOrderSummaryDto[]): HTMLElement {
  const table = document.createElement("div");
  table.className = "purchasing-order-table";
  table.setAttribute("role", "table");
  const columns = document.createElement("div");
  columns.className = "purchasing-order-columns";
  columns.setAttribute("role", "row");
  for (const [name, cls] of [
    ["Vendor", ""],
    ["Status", ""],
    ["Placed", ""],
    ["Lines", "is-num"],
    ["Estimated total", "is-num"],
    ["", ""],
  ] as const) {
    const cell = document.createElement("span");
    cell.setAttribute("role", "columnheader");
    cell.className = cls;
    cell.textContent = name;
    columns.append(cell);
  }
  table.append(columns);
  for (const order of orders) table.append(buildPurchasingOrderRow(order));
  return table;
}

/** HIGFIX4A: the status as one chip, its symbol drawn by CSS so the text stays the plain label. */
const PURCHASE_ORDER_CHIP_TONE: Readonly<Record<string, string>> = {
  draft: "warn",
  placed: "neutral",
  partially_received: "warn",
  received: "good",
  cancelled: "neutral",
  exception: "bad",
};

function buildPurchasingOrderRow(order: PurchaseOrderSummaryDto): HTMLElement {
  const row = document.createElement("div");
  row.className = "purchasing-order-row";
  row.dataset["testid"] = "purchasing-order-row";
  row.dataset["orderId"] = order.id;

  const vendor = document.createElement("div");
  vendor.className = "purchasing-order-vendor";
  vendor.dataset["testid"] = "purchasing-order-vendor";
  vendor.textContent = order.vendorName;

  const status = document.createElement("div");
  status.className = `purchasing-order-status fl-chip fl-chip-${PURCHASE_ORDER_CHIP_TONE[order.status] ?? "neutral"}`;
  status.dataset["testid"] = "purchasing-order-status";
  status.textContent = purchaseOrderStatusLabel(order.status);

  const lineCount = document.createElement("div");
  lineCount.className = "purchasing-order-line-count";
  lineCount.dataset["testid"] = "purchasing-order-line-count";
  lineCount.textContent = formatOrderLineCount(order);

  const total = document.createElement("div");
  total.className = "purchasing-order-estimated-total";
  total.dataset["testid"] = "purchasing-order-estimated-total";
  total.textContent = formatOrderEstimatedTotal(order);

  const viewButton = document.createElement("button");
  viewButton.type = "button";
  viewButton.className = "btn-secondary btn-compact";
  viewButton.dataset["testid"] = "purchasing-order-view-button";
  viewButton.textContent = "View";
  viewButton.addEventListener("click", () => void showPurchasingOrderDetail(order.id));

  // PO6 Slice 4: hidden outright (not merely disabled) for anyone whose
  // resolved permission snapshot does not carry inv.receipt.record,
  // gated independently of the View button's own purchasing.order.view
  // gate (the list itself already required that permission to be on
  // screen at all). Opens the receiving screen directly from the order
  // this clerk is already looking at, no separate order-picker screen.
  const receiveButton = document.createElement("button");
  receiveButton.type = "button";
  receiveButton.className = "btn-secondary btn-compact";
  receiveButton.dataset["testid"] = "purchasing-order-receive-button";
  receiveButton.textContent = "Receive";
  // UX105: also hidden while the order is a draft, and once it is received or cancelled.
  receiveButton.hidden =
    !purchaseOrderIsReceivable(order.status) ||
    !hasPermission(requireSession(), INVENTORY_RECEIPT_RECORD_PERMISSION);
  receiveButton.addEventListener("click", () => void showPurchasingReceivingScreen(order));

  // UX105: two orders from one vendor read differently: placed date and who placed it.
  const when = document.createElement("div");
  when.className = "purchasing-order-when";
  when.dataset["testid"] = "purchasing-order-when";
  when.textContent = describeOrderWhen(order);

  // UIFIX5: the buttons share one right-aligned cell, so every row ends the same way whether or not it can be received.
  const actions = document.createElement("div");
  actions.className = "purchasing-order-actions";
  actions.append(viewButton, receiveButton);
  row.setAttribute("role", "row");
  row.append(vendor, status, when, lineCount, total, actions);
  return row;
}

/** PO6-S3: which order the detail view is currently showing, so the
 * Approve panel's own handlers (wired once, at module load, like every
 * other handler in this file) know which order to act on without a
 * parameter threaded through every one of them. Set at the top of every
 * load, including a failed one (undefined then), so a stale id from a
 * PREVIOUS order can never be approved by a handler reading this after
 * navigating away. */
let currentPurchasingOrderDetailId: string | undefined;
let purchaseOrderDetailVisit = 0;
interface PurchaseCancelContext { generation: number; visit: number; orderId: string; key: string; busy?: boolean; reason?: string }
let purchaseCancelContext: PurchaseCancelContext | undefined;
function purchaseCancelContextIsCurrent(context: PurchaseCancelContext): boolean {
  return sessionIsCurrent(context.generation) && purchaseOrderDetailVisit === context.visit &&
    currentPurchasingOrderDetailId === context.orderId && currentScreenId === "purchasing-orders-screen" &&
    !purchasingOrderDetailView.hidden;
}
function closePurchaseCancelDialog(): void {
  purchaseCancelContext = undefined;
  if (purchaseCancelDialog.open) purchaseCancelDialog.close();
}
purchaseCancelDialog.addEventListener("close", () => { if (!purchaseCancelDialog.open) purchaseCancelContext = undefined; });
purchaseCancelKeep.addEventListener("click", closePurchaseCancelDialog);
purchaseCancelReason.addEventListener("change", () => {
  purchaseCancelConfirm.disabled = purchaseCancelReason.value.length === 0;
});
purchaseCancelConfirm.addEventListener("click", () => { void submitPurchaseCancellation(); });
async function submitPurchaseCancellation(): Promise<void> {
  const context = purchaseCancelContext;
  if (context === undefined || !purchaseCancelContextIsCurrent(context) || purchaseCancelReason.value.length === 0) {
    closePurchaseCancelDialog(); return;
  }
  if (context.busy) return;
  context.busy = true;
  context.reason ??= purchaseCancelReason.value;
  const session = requireSession();
  purchaseCancelReason.disabled = true;
  purchaseCancelConfirm.disabled = true;
  purchaseCancelKeep.disabled = true;
  purchaseCancelError.hidden = true;
  const result = await transitionPurchaseOrder(session.apiBaseUrl, session.tenantId, context.orderId, {
    toStatus: "cancelled", reason: context.reason, idempotencyKey: context.key,
  });
  if (!purchaseCancelContextIsCurrent(context) || purchaseCancelContext !== context || !purchaseCancelDialog.open) return;
  context.busy = false;
  purchaseCancelKeep.disabled = false;
  purchaseCancelConfirm.disabled = false;
  if (result.status !== 200) {
    purchaseCancelError.hidden = false;
    purchaseCancelError.textContent = result.body.message ?? "Could not cancel order. Try again.";
    return;
  }
  closePurchaseCancelDialog();
  void refreshMenuCounts().catch(() => { topbarApprovalBadge.hidden = true; });
  await showPurchasingOrderDetail(context.orderId);
}

let purchasingOrderApprovalReady = false;
/** POAPPROVE1: true when the signed-in person may approve this order themselves (no second PIN). */
let purchasingOrderApproveDirect = false;

/** UX108: the order whose detail is on screen, for the screen's address. Set the moment the detail is asked for (the
 * address is the request, not the answer), cleared when the list is shown again. */
let purchasingOrderDetailRouteId: string | undefined;

/** Toggles to the detail view in place (no navigation): "Back to orders"
 * (purchasingOrderDetailBackButton below) toggles back and re-reads the list,
 * because an action on the detail (approve, cancel) changes the row. */
async function showPurchasingOrderDetail(orderId: string): Promise<void> {
  closePurchaseCancelDialog();
  const visit = ++purchaseOrderDetailVisit;
  const generation = sessionGeneration;
  currentPurchasingOrderDetailId = undefined;
  purchasingOrderDetailExportAbc2goButton.hidden = true;
  purchasingOrderDetailExportAbc2goButton.disabled = true;
  purchasingOrderDetailAbc2goWarningEl.hidden = true;
  purchasingOrderDetailAbc2goWarningEl.replaceChildren();
  purchasingOrderDetailAbc2goLinesEl.hidden = true;
  purchasingOrderDetailAbc2goLinesEl.replaceChildren();
  purchasingOrderApprovalReady = false;
  purchasingOrderDetailRouteId = orderId;
  requestRoutePublish();
  purchasingOrdersListView.hidden = true;
  purchasingOrderDetailView.hidden = false;
  purchasingOrderDetailStatusEl.hidden = true;
  purchasingOrderDetailStatusEl.textContent = "";
  purchasingOrderDetailVendorEl.textContent = "Loading…";
  purchasingOrderDetailLinesEl.innerHTML = "";
  purchasingOrderDetailStatusHistoryEl.innerHTML = "";
  resetPurchasingOrderApprovePanel();
  const session = requireSession();
  const result = await getPurchaseOrder(session.apiBaseUrl, session.tenantId, orderId);
  if (!sessionIsCurrent(generation) || visit !== purchaseOrderDetailVisit ||
      currentScreenId !== "purchasing-orders-screen" || purchasingOrderDetailView.hidden) return;
  if (result.status !== 200) {
    purchasingOrderDetailVendorEl.textContent = "";
    purchasingOrderDetailStatusEl.hidden = false;
    purchasingOrderDetailStatusEl.textContent =
      result.body.message ?? "Could not load this order. Check your connection.";
    return;
  }
  currentPurchasingOrderDetailId = orderId;
  renderPurchasingOrderDetail(result.body.order, result.body.statusHistory);
  purchasingOrderDetailDocumentsStatusEl.textContent = "";
  // PO6 Slice 5. Both independent of the render above and of each other:
  // the exception panel's own receiving-view read, and each line's own
  // receipts. Run concurrently rather than sequentially, since neither
  // depends on the other's result.
  await Promise.all([
    refreshPurchasingOrderDetailExceptionPanel(result.body.order),
    loadAllPurchasingOrderLineReceipts(result.body.order.lines),
    refreshPurchasingOrderDetailAbc2goWarning(orderId),
    loadPurchasingOrderDetailSubstitutions(orderId),
    mountInvoiceCard(
      purchasingOrderDetailInvoicesEl,
      orderId,
      "purchasing-order-detail",
      requireSession,
      offerClearForConfirmedInvoice,
    ),
  ]);
  // MGRDAYMAJ1 (manager-day-18): an order still waiting for approval has no invoice yet, so the card stays out of the way of the
  // decision, and the page says first that it is waiting on someone.
  const waiting = result.body.order.status === "draft";
  if (waiting) purchasingOrderDetailInvoicesEl.hidden = true;
  purchasingOrderDetailWaitingEl.hidden = !waiting;
  purchasingOrderDetailWaitingEl.textContent = waiting
    ? hasPermission(requireSession(), PURCHASING_ORDER_PLACE_PERMISSION)
      ? `Waiting for approval. Nothing has gone to ${result.body.order.vendorName} yet: approve it to place the order, or cancel it.`
      : `Waiting for approval. Nothing has gone to ${result.body.order.vendorName} yet.`
    : "";
}

/** PO13: marks the order, and the line, when a receipt on it delivered a different product than the line ordered.
 * Re-runs on every load of the detail, so a stale mark from the previous order never stays. */
async function loadPurchasingOrderDetailSubstitutions(orderId: string): Promise<void> {
  purchasingOrderDetailView
    .querySelectorAll('[data-testid="purchasing-order-detail-substitution-flag"]')
    .forEach((node) => node.remove());
  purchasingOrderDetailLinesEl
    .querySelectorAll('[data-testid="purchasing-order-detail-line-substitution"]')
    .forEach((node) => node.remove());
  purchasingOrderDetailView
    .querySelectorAll('[data-testid="purchasing-order-detail-short-flag"]')
    .forEach((node) => node.remove());
  purchasingOrderDetailLinesEl
    .querySelectorAll('[data-testid="purchasing-order-detail-line-received"]')
    .forEach((node) => node.remove());
  const session = requireSession();
  const result = await getOrderReceivingView(session.apiBaseUrl, session.tenantId, orderId);
  if (result.status !== 200 || currentPurchasingOrderDetailId !== orderId) {
    return;
  }
  appendPurchasingOrderReceivedProgress(result.body);
  const substitutions = result.body.substitutions;
  if (substitutions.length === 0) {
    return;
  }
  const flag = document.createElement("span");
  flag.className = "purchasing-substitution-flag";
  flag.dataset["testid"] = "purchasing-order-detail-substitution-flag";
  flag.textContent = ORDER_SUBSTITUTION_LABEL;
  purchasingOrderDetailStateEl.after(flag);
  for (const receipts of Array.from(
    purchasingOrderDetailLinesEl.querySelectorAll<HTMLElement>("[data-purchase-order-line-id]"),
  )) {
    const lineId = receipts.dataset["purchaseOrderLineId"];
    if (lineId === undefined) continue;
    const holder = document.createElement("div");
    appendSubstitutionNotes(
      holder,
      substitutionsForLine(substitutions, lineId),
      "purchasing-order-detail-line-substitution",
    );
    receipts.before(...Array.from(holder.children));
  }
}

/** INVPOWALK1 (F7, design-language rules 1 and 2): what actually came in, beside what was ordered. Each line of an order
 * with receipts says "Received 2 of 3 bottles", and an order closed as Received with a line below what was ordered says so
 * ("Received short"), so the total and the state never read as if everything arrived. Read from the receiving view the
 * detail already fetches; the state word itself is untouched. */
function appendPurchasingOrderReceivedProgress(view: OrderReceivingViewDto): void {
  if (view.status === "draft" || view.status === "cancelled" || view.status === "placed") return;
  let anyShort = false;
  for (const container of Array.from(
    purchasingOrderDetailLinesEl.querySelectorAll<HTMLElement>("[data-purchase-order-line-id]"),
  )) {
    const lineId = container.dataset["purchaseOrderLineId"];
    const line = view.lines.find((candidate) => candidate.purchaseOrderLineId === lineId);
    if (line === undefined) continue;
    const ordered = Number(line.orderedQuantity);
    const received = Number(line.receivedQuantity);
    const short = Math.max(0, ordered - received);
    if (short > 0) anyShort = true;
    const unit = line.orderUnit ?? null;
    const word = (count: number): string =>
      unit === null ? "" : ` ${unit.toLowerCase()}${count === 1 ? "" : "s"}`;
    const note = document.createElement("div");
    note.className = "purchasing-order-line-received";
    note.dataset["testid"] = "purchasing-order-detail-line-received";
    note.textContent =
      `Received ${formatReceivingQuantity(line.receivedQuantity)} of ${formatReceivingQuantity(line.orderedQuantity)}${word(ordered)}` +
      (view.status === "received" && short > 0
        ? `, ${formatReceivingQuantity(String(short))} short`
        : "");
    container.before(note);
  }
  if (view.status === "received" && anyShort) {
    const flag = document.createElement("span");
    flag.className = "purchasing-short-flag";
    flag.dataset["testid"] = "purchasing-order-detail-short-flag";
    flag.textContent = "Received short";
    purchasingOrderDetailStateEl.after(flag);
  }
}

// The scoped preview supplies file eligibility without requiring vendor-list permission.
async function refreshPurchasingOrderDetailAbc2goWarning(orderId: string): Promise<void> {
  const generation = sessionGeneration;
  const visit = purchaseOrderDetailVisit;
  const current = (): boolean =>
    sessionIsCurrent(generation) &&
    visit === purchaseOrderDetailVisit &&
    currentPurchasingOrderDetailId === orderId &&
    currentScreenId === "purchasing-orders-screen" &&
    !purchasingOrderDetailView.hidden;
  if (!current()) return;
  purchasingOrderDetailAbc2goWarningEl.hidden = true;
  purchasingOrderDetailAbc2goWarningEl.replaceChildren();
  purchasingOrderDetailAbc2goLinesEl.hidden = true;
  purchasingOrderDetailAbc2goLinesEl.replaceChildren();
  purchasingOrderDetailExportAbc2goButton.hidden = true;
  purchasingOrderDetailExportAbc2goButton.disabled = true;
  const session = requireSession();
  const result = await getAbc2goExportPreview(session.apiBaseUrl, session.tenantId, orderId);
  if (!current()) return;
  if (result.status !== 200) {
    // An unsupported method has no file action. Other failures remain visible and retryable.
    if (
      result.status === 400 &&
      result.body.message?.includes("set Upload file and an order layout")
    )
      return;
    purchasingOrderDetailAbc2goWarningEl.textContent =
      result.body.message ?? "Could not load the order file. Check your connection.";
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "btn-secondary btn-compact";
    retry.textContent = "Retry";
    retry.addEventListener("click", () => {
      if (current()) void refreshPurchasingOrderDetailAbc2goWarning(orderId);
    });
    purchasingOrderDetailAbc2goWarningEl.append(retry);
    purchasingOrderDetailAbc2goWarningEl.hidden = false;
    return;
  }
  purchasingOrderDetailExportAbc2goButton.hidden = false;
  purchasingOrderDetailExportAbc2goButton.disabled =
    !result.body.exportable ||
    result.body.refusedLines.length > 0 ||
    result.body.omittedLines.length > 0;
  if (purchasingOrderDetailExportAbc2goButton.disabled) {
    const reasons =
      result.body.refusedLines.length > 0
        ? result.body.refusedLines.map((line) => line.message)
        : result.body.omittedLines.map(
            (line) =>
              `${purchasingOrderDetailVendorEl.textContent ?? "Vendor"}: missing item code for ${line.productName}.`,
          );
    purchasingOrderDetailAbc2goWarningEl.textContent =
      reasons.join("\n") || "Order file cannot be downloaded.";
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "btn-secondary btn-compact";
    retry.textContent = "Refresh";
    retry.addEventListener("click", () => {
      if (current()) void refreshPurchasingOrderDetailAbc2goWarning(orderId);
    });
    purchasingOrderDetailAbc2goWarningEl.append(retry);
    purchasingOrderDetailAbc2goWarningEl.hidden = false;
    return;
  }
  for (const exported of result.body.rows) {
    const item = document.createElement("li");
    item.dataset["testid"] = "purchasing-order-detail-abc2go-line";
    item.textContent = `${exported.productName}: ${exported.itemCode}, ${exported.qty} ${exported.qty === 1 ? "bottle" : "bottles"} (${Number(exported.orderedQuantity)} ${(exported.orderUnit ?? "unit").toLowerCase()}, ${exported.bottlesPerOrderUnit} per ${(exported.orderUnit ?? "unit").toLowerCase()})`;
    purchasingOrderDetailAbc2goLinesEl.append(item);
  }
  purchasingOrderDetailAbc2goLinesEl.hidden = result.body.rows.length === 0;
}

/** Shared by both document buttons: fetches the binary, and either
 * triggers a browser save (via a throwaway object-URL anchor, the
 * standard pattern for a fetch-then-save flow with no server-rendered
 * link the browser could otherwise navigate to directly) or writes a
 * failure to the shared status line. */
async function downloadPurchasingOrderDocument(
  fetchFile: () => Promise<{
    status: number;
    blob: Blob | undefined;
    message: string | undefined;
    networkError: boolean;
  }>,
  filename: string,
): Promise<void> {
  purchasingOrderDetailDocumentsStatusEl.textContent = "";
  const result = await fetchFile();
  if (result.status !== 200 || result.blob === undefined) {
    purchasingOrderDetailDocumentsStatusEl.textContent = result.networkError
      ? "Could not download this file. Check your connection."
      : (result.message ?? "Could not download this file.");
    return;
  }
  const url = URL.createObjectURL(result.blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

purchasingOrderDetailDownloadPdfButton.addEventListener("click", () => {
  const orderId = currentPurchasingOrderDetailId;
  if (orderId === undefined) return;
  const session = requireSession();
  void downloadPurchasingOrderDocument(
    () => getPurchaseOrderPdf(session.apiBaseUrl, session.tenantId, orderId),
    `purchase-order-${orderId}.pdf`,
  );
});

purchasingOrderDetailExportAbc2goButton.addEventListener("click", () => {
  const orderId = currentPurchasingOrderDetailId;
  if (
    orderId === undefined ||
    purchasingOrderDetailExportAbc2goButton.disabled ||
    purchasingOrderDetailExportAbc2goButton.hidden
  )
    return;
  const session = requireSession();
  void downloadPurchasingOrderDocument(
    () => getAbc2goExportFile(session.apiBaseUrl, session.tenantId, orderId),
    `order-file-${orderId}.xlsx`,
  );
});

function renderPurchasingOrderDetail(
  order: PurchaseOrderDetailDto,
  statusHistory: readonly PurchaseOrderStatusChangeDto[],
): void {
  purchaseCancelAction.hidden = !hasPermission(requireSession(), PURCHASING_ORDER_PLACE_PERMISSION) ||
    !["draft", "placed"].includes(order.status) || (order.cancellationReasons?.length ?? 0) === 0;
  purchaseCancelAction.onclick = () => {
    const context: PurchaseCancelContext = { generation: sessionGeneration, visit: purchaseOrderDetailVisit,
      orderId: order.id, key: crypto.randomUUID() };
    if (!purchaseCancelContextIsCurrent(context)) return;
    purchaseCancelContext = context;
    purchaseCancelSummary.textContent = `${order.vendorName}, estimated ${formatOrderEstimatedTotal(order)}`;
    purchaseCancelReason.replaceChildren(new Option("Select reason", ""),
      ...(order.cancellationReasons ?? []).map((reason) => new Option(reason, reason)));
    purchaseCancelReason.disabled = false;
    purchaseCancelError.hidden = true;
    purchaseCancelConfirm.disabled = true;
    purchaseCancelKeep.disabled = false;
    purchaseCancelDialog.showModal();
  };
  purchasingOrderDetailVendorEl.textContent = order.vendorName;
  purchasingOrderDetailStateEl.textContent = purchaseOrderStatusLabel(order.status);
  // INVPOWALK1 (F6, DEC-364): Placed means sent and the person sends the PO; say who, in one line.
  purchasingOrderDetailSendNoteEl.hidden = order.status !== "placed";
  purchasingOrderDetailSendNoteEl.textContent = `Download the PO and send it to ${order.vendorName} yourself. This system does not email it.`;
  // The order's own TOTAL is always the aggregate estimatedTotalCents,
  // never a sum of the lines below (PO6-S2-F1's own invariant test,
  // test/purchasing.test.ts, proves server-side that the two agree).
  purchasingOrderDetailEstimatedTotalEl.textContent = formatOrderEstimatedTotal(order);
  purchasingOrderDetailLineCountEl.textContent = formatOrderLineCount(order);
  purchasingOrderDetailApprovalEl.textContent = describePurchaseOrderApproval(order);
  // PO6-S1-F2: the placer's own resolved display name, never the raw
  // placedByEmployeeId UUID (the bug this fixes; see
  // ui/src/purchasingDisplay.ts's formatPurchaseOrderPlacedBy).
  // UIFIX5: the label above already says "Placed by", so the value is the name alone.
  purchasingOrderDetailPlacedByEl.textContent = resolveActorDisplayName(order.placedByDisplayName);

  // PO6-S3: shown to anyone who can even reach this route as an actor
  // (purchasing.order.place -- the same grant POST .../approve itself
  // requires), whenever the order is still draft (i.e. awaiting
  // approval; placeCart never creates any OTHER status that still needs
  // one). Deliberately NOT conditioned on order.placedByEmployeeId: the
  // person who placed this order sees this button exactly like anyone
  // else who can act, per ADR-003's own ruling that self-approval is
  // refused visibly rather than by hiding the control.
  const session = requireSession();
  purchasingOrderApprovalReady = (order.approvalBlockers?.length ?? 0) === 0;
  if (!purchasingOrderApprovalReady) {
    purchasingOrderDetailStatusEl.hidden = false;
    purchasingOrderDetailStatusEl.textContent = order.approvalBlockers!.join("\n");
    purchasingOrderDetailStatusEl.style.whiteSpace = "pre-line";
  }
  const canAttemptApproval =
    hasPermission(session, PURCHASING_ORDER_PLACE_PERMISSION) && order.status === "draft" && purchasingOrderApprovalReady;
  resetPurchasingOrderApprovePanel();
  purchasingOrderDetailApproveButton.hidden = !canAttemptApproval;
  // POAPPROVE1 (DEC-459): whoever holds purchasing.order.approve (and did not place this order) approves in one tap from their
  // own sign-in; everyone else keeps the second-person form behind the plain Approve button.
  purchasingOrderApproveDirect =
    hasPermission(session, PURCHASING_ORDER_APPROVE_PERMISSION) && order.placedByEmployeeId !== session.userId;
  purchasingOrderDetailApproveButton.textContent = purchasingOrderApproveDirect ? "Approve order" : "Approve";
  // UX105: Receive on the detail too, under the same rule as the list row.
  purchasingOrderDetailReceiveButton.hidden =
    !purchaseOrderIsReceivable(order.status) ||
    !hasPermission(session, INVENTORY_RECEIPT_RECORD_PERMISSION);
  purchasingOrderDetailReceiveButton.onclick = () => void showPurchasingReceivingScreen(order);
  // PO6 Slice 5: reset synchronously here (so no prior order's own state
  // lingers for an instant); refreshPurchasingOrderDetailExceptionPanel
  // (async, called by showPurchasingOrderDetail alongside this function)
  // populates it from a fresh read.
  resetPurchasingOrderExceptionPanel();

  purchasingOrderDetailLinesEl.innerHTML = "";
  if (order.lines.length === 0) {
    const empty = document.createElement("p");
    empty.className = "tabs-empty";
    empty.dataset["testid"] = "purchasing-order-detail-lines-empty";
    empty.textContent = "No lines on this order.";
    purchasingOrderDetailLinesEl.append(empty);
  } else {
    for (const line of order.lines) {
      purchasingOrderDetailLinesEl.append(buildPurchasingOrderLineRow(line));
    }
  }

  purchasingOrderDetailStatusHistoryEl.innerHTML = "";
  for (const change of statusHistory) {
    purchasingOrderDetailStatusHistoryEl.append(buildStatusHistoryRow(change));
  }
}

function buildPurchasingOrderLineRow(line: PurchaseOrderLineDto): HTMLElement {
  const row = document.createElement("div");
  row.className = "purchasing-order-line-row";
  row.dataset["testid"] = "purchasing-order-detail-line-row";

  const product = document.createElement("div");
  product.className = "purchasing-order-line-product";
  product.dataset["testid"] = "purchasing-order-detail-line-product";
  product.textContent = line.productName;

  const quantity = document.createElement("div");
  quantity.className = "purchasing-order-line-quantity";
  quantity.dataset["testid"] = "purchasing-order-detail-line-quantity";
  quantity.textContent = `Ordered: ${formatLineOrderedQuantity(line)}`;

  const unitCost = document.createElement("div");
  unitCost.className = "purchasing-order-line-unit-cost";
  unitCost.dataset["testid"] = "purchasing-order-detail-line-unit-cost";
  unitCost.textContent = `Unit cost: ${formatLineUnitCost(line)}`;

  const extendedCost = document.createElement("div");
  extendedCost.className = "purchasing-order-line-extended-cost";
  extendedCost.dataset["testid"] = "purchasing-order-detail-line-extended-cost";
  extendedCost.textContent = `Extended: ${formatLineExtendedCost(line)}`;

  // PO6 Slice 5: this line's own receipts, populated asynchronously by
  // loadAllPurchasingOrderLineReceipts (fired once per order load, not
  // per row) once inv.receipt.view is confirmed; hidden until then, so a
  // session without that permission never shows an empty section.
  const receipts = document.createElement("div");
  receipts.className = "purchasing-order-line-receipts";
  receipts.dataset["testid"] = "purchasing-order-line-receipts";
  receipts.dataset["purchaseOrderLineId"] = line.id;
  receipts.hidden = true;

  // TF4-F3: the order unit and the bottle count it holds, beside the quantity.
  const bottlesText = formatOrderUnitLabel(line);
  if (bottlesText !== null && line.orderUnit !== null) {
    const bottles = document.createElement("div");
    bottles.className = "purchasing-order-line-bottles";
    bottles.dataset["testid"] = "purchasing-order-detail-line-bottles";
    bottles.textContent = bottlesText;
    row.append(product, quantity, bottles, unitCost, extendedCost, receipts);
    return row;
  }
  row.append(product, quantity, unitCost, extendedCost, receipts);
  return row;
}

function buildStatusHistoryRow(change: PurchaseOrderStatusChangeDto): HTMLElement {
  const described = describeStatusHistoryRow(change);

  const row = document.createElement("div");
  row.className = "purchasing-status-history-row";
  row.dataset["testid"] = "purchasing-status-history-row";

  const headline = document.createElement("div");
  headline.className = "purchasing-status-history-headline";
  headline.dataset["testid"] = "purchasing-status-history-headline";
  headline.textContent = described.headline;
  row.append(headline);

  if (described.reason !== null) {
    const reason = document.createElement("div");
    reason.className = "purchasing-status-history-reason";
    reason.dataset["testid"] = "purchasing-status-history-reason";
    reason.textContent = described.reason;
    row.append(reason);
  }

  // Slice 3b's audited exception entry/resolution: the reason a manager
  // raised it and, once resolved, the resolution chosen. Both come
  // straight from this row, never a role name or a guess.
  if (described.exceptionReason !== null) {
    const exceptionReason = document.createElement("div");
    exceptionReason.className = "purchasing-status-history-exception-reason";
    exceptionReason.dataset["testid"] = "purchasing-status-history-exception-reason";
    exceptionReason.textContent = `Exception reason: ${described.exceptionReason}`;
    row.append(exceptionReason);
  }
  if (described.resolution !== null) {
    const resolution = document.createElement("div");
    resolution.className = "purchasing-status-history-resolution";
    resolution.dataset["testid"] = "purchasing-status-history-resolution";
    resolution.textContent = `Resolution: ${described.resolution}`;
    row.append(resolution);
  }

  // The one place this screen can show a per-line ordered quantity at
  // all: the variance snapshot Slice 3b stores on the row that entered
  // or resolved `exception` (src/domain/receiving.ts's
  // listLineReceivingStates at that moment). Absent on every other row,
  // by construction (ui/src/purchasingDisplay.ts's parseVarianceLines).
  if (described.varianceLines.length > 0) {
    const varianceList = document.createElement("ul");
    varianceList.className = "purchasing-status-history-variance";
    varianceList.dataset["testid"] = "purchasing-status-history-variance";
    for (const line of described.varianceLines) {
      const item = document.createElement("li");
      item.dataset["testid"] = "purchasing-status-history-variance-line";
      item.textContent = `${line.productName}: ordered ${line.orderedQuantity}, received ${line.receivedQuantity}, outstanding ${line.outstandingQuantity}`;
      varianceList.append(item);
    }
    row.append(varianceList);
  }

  return row;
}

// ---------------------------------------------------------------------
// PO6 Slice 3 (docs/build-briefs/PO6-S3-approval.md; ADR-003 Decision 4,
// slice 3). Approve, on an order still draft (awaiting the configured
// threshold's approval). POST .../purchasing/orders/:orderId/approve
// (src/api/server.ts) always requires a second person's own
// approverUserId/approverPin, so this panel always collects both before
// sending anything, rather than reusing the floor's own
// #approval-picker-screen/#pin-entry-screen digit pad -- those are the
// floor's touch-only surfaces (FLOOR_HTML_ROOTS,
// scripts/floorSurfaces.ts), and this is a keyboard-and-pointer admin
// screen (ADR-003 Decision 2), so a select and a password field are this
// surface's own idiom, not a workaround.
// ---------------------------------------------------------------------

/** Hides the form and any prior status, clears the PIN field, and hides
 * the Approve button itself -- the button's own visibility is decided
 * fresh by renderPurchasingOrderDetail every time an order loads, never
 * left over from whatever order was showing before. */
function resetPurchasingOrderApprovePanel(): void {
  purchasingOrderDetailApproveButton.hidden = true;
  purchasingOrderDetailApproveForm.hidden = true;
  purchasingOrderDetailApproveApproverSelect.innerHTML = "";
  purchasingOrderDetailApproveApproverSelect.disabled = false;
  purchasingOrderDetailApprovePinInput.value = "";
  purchasingOrderDetailApproveSubmitButton.disabled = false;
  purchasingOrderDetailApproveStatusEl.hidden = true;
  purchasingOrderDetailApproveStatusEl.textContent = "";
}

function showPurchasingOrderApproveStatus(message: string, tone: "error" | "info" = "error"): void {
  purchasingOrderDetailApproveStatusEl.hidden = false;
  // INVPOWALK1 (B1 mitigation, color rules): red is for a refusal. "This device could not check who can approve" is help.
  purchasingOrderDetailApproveStatusEl.classList.toggle("admin-error", tone === "error");
  purchasingOrderDetailApproveStatusEl.classList.toggle("admin-field-help", tone === "info");
  purchasingOrderDetailApproveStatusEl.textContent = message;
}

/** Opens the inline approver/PIN form. Asks the server exactly who can
 * approve THIS permission first (GET .../approval-candidates), and only
 * falls back to the ordinary staff roster -- never a role name -- when
 * that read does not succeed, the identical authoritative-then-degrade
 * shape this file's own openApprovalPicker uses for the floor's PIN pad
 * (ui/src/approvalFlow.ts's selectApprovalCandidates, reused here
 * unchanged). The signed-in user is excluded from the list on both
 * paths (selectApprovalCandidates' own contract): requireSecondPersonApproval
 * refuses an approver who IS the acting user outright, so offering that
 * tile could only ever produce a guaranteed-failing submit. */
async function handleOpenPurchasingOrderApproveClick(): Promise<void> {
  if (!purchasingOrderApprovalReady) return;
  if (purchasingOrderApproveDirect) {
    await handleApprovePurchasingOrderDirectClick();
    return;
  }
  const session = requireSession();
  purchasingOrderDetailApproveButton.hidden = true;
  purchasingOrderDetailApproveForm.hidden = false;
  purchasingOrderDetailApproveStatusEl.hidden = true;
  purchasingOrderDetailApproveStatusEl.textContent = "";
  purchasingOrderDetailApproveApproverSelect.innerHTML = "";
  purchasingOrderDetailApproveApproverSelect.disabled = true;
  purchasingOrderDetailApproveSubmitButton.disabled = true;

  const authoritative = await listApprovalCandidates(session.apiBaseUrl, session.tenantId, {
    permissionId: PURCHASING_ORDER_APPROVE_PERMISSION,
  });
  const selection =
    authoritative.status === 200
      ? selectApprovalCandidates(authoritative.body.candidates, [], session.userId)
      : await (async () => {
          const roster = await listUsers(session.apiBaseUrl, session.tenantId, {
            activeOnly: true,
          });
          if (roster.status !== 200) {
            return undefined;
          }
          return selectApprovalCandidates(undefined, roster.body.users, session.userId);
        })();

  if (selection === undefined) {
    showPurchasingOrderApproveStatus("Could not load the staff list. Check your connection.");
    return;
  }

  if (selection.candidates.length === 0) {
    showPurchasingOrderApproveStatus(
      "No other approver is available on this device. Ask a different manager to sign in and approve this order.",
    );
    return;
  }

  // INVPOWALK1 (B1 mitigation): no name is preselected. The first person in the list was usually someone who cannot
  // approve, so a quick tap on Submit sent a guaranteed refusal. Submit already asks for a pick (empty value).
  purchasingOrderDetailApproveApproverSelect.append(new Option("Choose who is approving", ""));
  for (const candidate of selection.candidates) {
    const option = document.createElement("option");
    option.value = candidate.id;
    option.textContent = candidate.displayName;
    purchasingOrderDetailApproveApproverSelect.append(option);
  }
  purchasingOrderDetailApproveApproverSelect.disabled = false;
  purchasingOrderDetailApproveSubmitButton.disabled = false;
  if (selection.kind === "unverified") {
    showPurchasingOrderApproveStatus(
      "This device could not check who can approve, so everyone on shift is listed. Pick a manager; anyone else will be turned down.",
      "info",
    );
  }
}

/** POAPPROVE1 (DEC-459): one tap, no approver list and no PIN. The server resolves the signed-in person's own grant and
 * records them as approver; a refusal shows on the same status line the form uses. */
async function handleApprovePurchasingOrderDirectClick(): Promise<void> {
  const session = requireSession();
  const orderId = currentPurchasingOrderDetailId;
  if (orderId === undefined) {
    return;
  }
  purchasingOrderDetailApproveButton.disabled = true;
  const result = await approvePurchaseOrder(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
  });
  purchasingOrderDetailApproveButton.disabled = false;
  if (result.status !== 200) {
    showPurchasingOrderApproveStatus(
      result.body.message ??
        `Could not approve this order (${result.body.error ?? result.status}).`,
    );
    return;
  }
  void refreshMenuCounts().catch(() => { topbarApprovalBadge.hidden = true; });
  await showPurchasingOrderDetail(orderId);
}

function handleCancelPurchasingOrderApproveClick(): void {
  resetPurchasingOrderApprovePanel();
  purchasingOrderDetailApproveButton.hidden = !purchasingOrderApprovalReady;
}

/**
 * Sends BOTH approverUserId and approverPin on this one request (never a
 * first, approval-less attempt): the route requires a second person's
 * own credentials on every call it accepts, so there is no bare
 * "attempt" to retry the way the floor's requestApproval pattern
 * retries an approval_required 403.
 *
 * PO6-S3's own point: a refusal here -- wrong PIN, an approver who does
 * not hold purchasing.order.approve as allowed, OR the picked approver
 * being this order's own placer (approvePurchaseOrder's self-approval
 * check, src/domain/purchasing.ts) -- surfaces exactly on
 * #purchasing-order-detail-approve-status, right next to the control the
 * manager just used, and stays there (no timeout) until the next attempt
 * or Cancel, rather than a global toast that can time out or land off
 * whatever the manager is looking at (see this file's own DSC12 fix,
 * docs/backlog.md, for the precedent this follows: make the refusal
 * visible where the person already is, not merely visible somewhere).
 */
async function handleSubmitPurchasingOrderApproveClick(): Promise<void> {
  if (!purchasingOrderApprovalReady) return;
  const session = requireSession();
  const orderId = currentPurchasingOrderDetailId;
  if (orderId === undefined) {
    return;
  }
  const approverUserId = purchasingOrderDetailApproveApproverSelect.value;
  const approverPin = purchasingOrderDetailApprovePinInput.value;
  if (approverUserId.length === 0 || approverPin.length === 0) {
    showPurchasingOrderApproveStatus("Pick an approver and enter their PIN.");
    return;
  }
  purchasingOrderDetailApproveSubmitButton.disabled = true;
  const result = await approvePurchaseOrder(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
    approverUserId,
    approverPin,
  });
  purchasingOrderDetailApproveSubmitButton.disabled = false;
  if (result.status !== 200) {
    purchasingOrderDetailApprovePinInput.value = "";
    showPurchasingOrderApproveStatus(
      result.body.message ??
        `Could not approve this order (${result.body.error ?? result.status}).`,
    );
    return;
  }
  void refreshMenuCounts().catch(() => { topbarApprovalBadge.hidden = true; });
  await showPurchasingOrderDetail(orderId);
}

// ---------------------------------------------------------------------
// PO6 Slice 5 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md,
// Decision 4 slice 5; docs/build-briefs/PO6-S5-corrections-and-
// exceptions.md). Reporting and resolving this order's own `exception`
// state (PO4 Slice 3b's own fixed vocabularies), and correcting a
// posted receipt by selecting it. Both live on this same admin order
// detail screen, the same posture Slice 3's own approve panel already
// established.
// ---------------------------------------------------------------------

/** Full reset: hides both buttons and both forms, and drops the cached
 * receiving view. Called synchronously by renderPurchasingOrderDetail so
 * no prior order's own state lingers for an instant; the async refresh
 * below repopulates from a fresh read. */
function resetPurchasingOrderExceptionPanel(): void {
  state.purchasingOrderDetailReceivingView = undefined;
  purchasingOrderDetailExceptionReportButton.hidden = true;
  purchasingOrderDetailExceptionCurrentEl.hidden = true;
  purchasingOrderDetailExceptionCurrentEl.textContent = "";
  purchasingOrderDetailExceptionResolveButton.hidden = true;
  resetPurchasingOrderExceptionReportForm();
  resetPurchasingOrderExceptionResolveForm();
}

function resetPurchasingOrderExceptionReportForm(): void {
  purchasingOrderDetailExceptionReportForm.hidden = true;
  purchasingOrderDetailExceptionReasonPicker.innerHTML = "";
  purchasingOrderDetailExceptionNoteInput.value = "";
  purchasingOrderDetailExceptionReportSubmitButton.disabled = false;
  purchasingOrderDetailExceptionReportStatusEl.hidden = true;
  purchasingOrderDetailExceptionReportStatusEl.textContent = "";
  selectedExceptionReason = undefined;
}

function resetPurchasingOrderExceptionResolveForm(): void {
  purchasingOrderDetailExceptionResolveForm.hidden = true;
  purchasingOrderDetailExceptionResolutionPicker.innerHTML = "";
  purchasingOrderDetailExceptionResolveTargetEl.textContent = "";
  purchasingOrderDetailExceptionResolveSubmitButton.disabled = true;
  purchasingOrderDetailExceptionResolveStatusEl.hidden = true;
  purchasingOrderDetailExceptionResolveStatusEl.textContent = "";
  selectedExceptionResolution = undefined;
  selectedExceptionResolutionTarget = null;
}

/** Fetches this order's own receiving view (its per-line derived
 * variance) only when the caller can even attempt to report or resolve
 * an exception -- purchasing.order.place, the SAME acting-caller
 * permission POST .../transition itself requires, matching the approve
 * button's own gate above -- and the order's own status makes either
 * action possible. Skipped entirely otherwise, so a session with no
 * purchasing permission, or an order that can neither enter nor is in
 * `exception` (draft, received, cancelled), never issues the extra
 * request. */
async function refreshPurchasingOrderDetailExceptionPanel(
  order: Pick<PurchaseOrderDetailDto, "id" | "status">,
): Promise<void> {
  const session = requireSession();
  if (!hasPermission(session, PURCHASING_ORDER_PLACE_PERMISSION)) {
    return;
  }
  const canReport = order.status === "placed" || order.status === "partially_received";
  const canResolve = order.status === "exception";
  if (!canReport && !canResolve) {
    return;
  }
  const result = await getOrderReceivingView(session.apiBaseUrl, session.tenantId, order.id);
  if (result.status !== 200) {
    return;
  }
  // A stale response landing after the screen has already moved to a
  // different order: discard rather than populate the wrong order's own
  // panel. Mirrors this file's own countsLoadSequence precedent.
  if (currentPurchasingOrderDetailId !== order.id) {
    return;
  }
  state.purchasingOrderDetailReceivingView = result.body;
  purchasingOrderDetailExceptionReportButton.hidden = !canReport;
  if (canResolve) {
    purchasingOrderDetailExceptionCurrentEl.hidden = false;
    purchasingOrderDetailExceptionCurrentEl.textContent =
      result.body.latestExceptionReason === null
        ? "This order is in exception."
        : `Exception: ${purchaseExceptionReasonLabel(result.body.latestExceptionReason)}.`;
    purchasingOrderDetailExceptionResolveButton.hidden = false;
  }
}

function showPurchasingOrderExceptionReportStatus(message: string): void {
  purchasingOrderDetailExceptionReportStatusEl.hidden = false;
  purchasingOrderDetailExceptionReportStatusEl.textContent = message;
}

let selectedExceptionReason: PurchaseExceptionReasonDto | undefined;

function renderPurchasingOrderExceptionReasonPicker(): void {
  purchasingOrderDetailExceptionReasonPicker.innerHTML = "";
  for (const reasonValue of sortByName(PURCHASE_EXCEPTION_REASONS, purchaseExceptionReasonLabel)) {
    const optionLabel = document.createElement("label");
    optionLabel.className = "admin-chip-option";
    const input = document.createElement("input");
    input.type = "radio";
    input.name = "purchasing-order-detail-exception-reason";
    input.className = "admin-chip-option-input";
    input.dataset["testid"] = "purchasing-order-detail-exception-reason-option";
    input.dataset["value"] = reasonValue;
    // No default: a picker deciding a real business fact is never
    // pre-selected (CLAUDE.md's no-default discipline, the same choice
    // Slice 4's own receiving unit picker already makes).
    input.addEventListener("change", () => {
      if (input.checked) {
        selectedExceptionReason = reasonValue;
      }
    });
    const text = document.createElement("span");
    text.className = "admin-chip-option-label";
    text.textContent = purchaseExceptionReasonLabel(reasonValue);
    optionLabel.append(input, text);
    purchasingOrderDetailExceptionReasonPicker.append(optionLabel);
  }
}

function handleOpenPurchasingOrderExceptionReportClick(): void {
  purchasingOrderDetailExceptionReportButton.hidden = true;
  purchasingOrderDetailExceptionReportForm.hidden = false;
  purchasingOrderDetailExceptionReportStatusEl.hidden = true;
  purchasingOrderDetailExceptionReportStatusEl.textContent = "";
  purchasingOrderDetailExceptionNoteInput.value = "";
  selectedExceptionReason = undefined;
  renderPurchasingOrderExceptionReasonPicker();
}

function handleCancelPurchasingOrderExceptionReportClick(): void {
  resetPurchasingOrderExceptionReportForm();
  purchasingOrderDetailExceptionReportButton.hidden = false;
}

/** Enters `exception` with the picked vocabulary reason and an optional
 * free-text note (the domain's own `reason` field, not a fixed
 * vocabulary, so free text is not a defect on this admin surface).
 * purchasing.order.place is never approval_required on any seeded role
 * template (verified against src/domain/permissions.ts before this was
 * written), so this is a single attempt, no retry needed. */
async function handleSubmitPurchasingOrderExceptionReportClick(): Promise<void> {
  const session = requireSession();
  const orderId = currentPurchasingOrderDetailId;
  if (orderId === undefined) {
    return;
  }
  if (selectedExceptionReason === undefined) {
    showPurchasingOrderExceptionReportStatus("Pick what went wrong.");
    return;
  }
  const note = purchasingOrderDetailExceptionNoteInput.value.trim();
  purchasingOrderDetailExceptionReportSubmitButton.disabled = true;
  const result = await transitionPurchaseOrder(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
    toStatus: "exception",
    exceptionReason: selectedExceptionReason,
    ...(note.length > 0 ? { reason: note } : {}),
  });
  purchasingOrderDetailExceptionReportSubmitButton.disabled = false;
  if (result.status !== 200) {
    showPurchasingOrderExceptionReportStatus(
      result.body.message ??
        `Could not report this exception (${result.body.error ?? result.status}).`,
    );
    return;
  }
  await showPurchasingOrderDetail(orderId);
}

function showPurchasingOrderExceptionResolveStatus(message: string): void {
  purchasingOrderDetailExceptionResolveStatusEl.hidden = false;
  purchasingOrderDetailExceptionResolveStatusEl.textContent = message;
}

let selectedExceptionResolution: PurchaseExceptionResolutionDto | undefined;
/** Set only when the currently-picked resolution is legal against this
 * order's own current variance; this is the EXACT toStatus a submit
 * sends, computed and shown before submit, never chosen from a list. */
let selectedExceptionResolutionTarget: string | null = null;

/** Picking a resolution computes and DISPLAYS its target status before
 * submit (ui/src/purchasingDisplay.ts's computeExceptionResolutionTarget,
 * verified against src/domain/receiving.ts's resolvePurchaseOrderException
 * before this was written) and disables submit when the resolution's own
 * required variance shape does not hold against this order's own current
 * lines, with the same reason the server itself would refuse it with. */
function handleSelectPurchasingOrderExceptionResolution(
  resolution: PurchaseExceptionResolutionDto,
): void {
  selectedExceptionResolution = resolution;
  const view = state.purchasingOrderDetailReceivingView;
  if (view === undefined) {
    selectedExceptionResolutionTarget = null;
    purchasingOrderDetailExceptionResolveTargetEl.textContent =
      "Could not read this order's own current lines. Check your connection.";
    purchasingOrderDetailExceptionResolveSubmitButton.disabled = true;
    return;
  }
  const outcome = computeExceptionResolutionTarget(resolution, view.lines);
  if (!outcome.legal || outcome.targetStatus === null) {
    selectedExceptionResolutionTarget = null;
    purchasingOrderDetailExceptionResolveTargetEl.textContent = `Not available: ${outcome.illegalReason}`;
    purchasingOrderDetailExceptionResolveSubmitButton.disabled = true;
    return;
  }
  selectedExceptionResolutionTarget = outcome.targetStatus;
  purchasingOrderDetailExceptionResolveTargetEl.textContent = `This will move the order to: ${purchaseOrderStatusLabel(outcome.targetStatus)}`;
  purchasingOrderDetailExceptionResolveSubmitButton.disabled = false;
}

function renderPurchasingOrderExceptionResolutionPicker(): void {
  purchasingOrderDetailExceptionResolutionPicker.innerHTML = "";
  for (const resolutionValue of sortByName(
    PURCHASE_EXCEPTION_RESOLUTIONS,
    purchaseExceptionResolutionLabel,
  )) {
    const optionLabel = document.createElement("label");
    optionLabel.className = "admin-chip-option";
    const input = document.createElement("input");
    input.type = "radio";
    input.name = "purchasing-order-detail-exception-resolution";
    input.className = "admin-chip-option-input";
    input.dataset["testid"] = "purchasing-order-detail-exception-resolution-option";
    input.dataset["value"] = resolutionValue;
    input.addEventListener("change", () => {
      if (input.checked) {
        handleSelectPurchasingOrderExceptionResolution(resolutionValue);
      }
    });
    const text = document.createElement("span");
    text.className = "admin-chip-option-label";
    text.textContent = purchaseExceptionResolutionLabel(resolutionValue);
    optionLabel.append(input, text);
    purchasingOrderDetailExceptionResolutionPicker.append(optionLabel);
  }
}

function handleOpenPurchasingOrderExceptionResolveClick(): void {
  purchasingOrderDetailExceptionResolveButton.hidden = true;
  purchasingOrderDetailExceptionResolveForm.hidden = false;
  purchasingOrderDetailExceptionResolveStatusEl.hidden = true;
  purchasingOrderDetailExceptionResolveStatusEl.textContent = "";
  selectedExceptionResolution = undefined;
  selectedExceptionResolutionTarget = null;
  purchasingOrderDetailExceptionResolveTargetEl.textContent = "";
  purchasingOrderDetailExceptionResolveSubmitButton.disabled = true;
  renderPurchasingOrderExceptionResolutionPicker();
}

function handleCancelPurchasingOrderExceptionResolveClick(): void {
  resetPurchasingOrderExceptionResolveForm();
  purchasingOrderDetailExceptionResolveButton.hidden = false;
}

/** Sends the resolution and the EXACT toStatus
 * handleSelectPurchasingOrderExceptionResolution already computed and
 * displayed -- never a value re-derived here, and never a value a person
 * chose from a list. A toStatus that disagrees with what the server
 * itself computes is refused with 400 naming what it computed instead
 * (src/domain/receiving.ts's resolvePurchaseOrderException); that
 * refusal, like any other, surfaces verbatim on this form's own status
 * line. */
async function handleSubmitPurchasingOrderExceptionResolveClick(): Promise<void> {
  const session = requireSession();
  const orderId = currentPurchasingOrderDetailId;
  if (orderId === undefined) {
    return;
  }
  if (selectedExceptionResolution === undefined || selectedExceptionResolutionTarget === null) {
    showPurchasingOrderExceptionResolveStatus("Pick a resolution.");
    return;
  }
  purchasingOrderDetailExceptionResolveSubmitButton.disabled = true;
  const result = await transitionPurchaseOrder(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
    toStatus: selectedExceptionResolutionTarget,
    resolution: selectedExceptionResolution,
  });
  if (result.status !== 200) {
    purchasingOrderDetailExceptionResolveSubmitButton.disabled = false;
    showPurchasingOrderExceptionResolveStatus(
      result.body.message ??
        `Could not resolve this exception (${result.body.error ?? result.status}).`,
    );
    return;
  }
  await showPurchasingOrderDetail(orderId);
}

// ---------------------------------------------------------------------
// PO6 Slice 5, continued: correcting a posted receipt by selecting it.
// Nested under each order line (buildPurchasingOrderLineRow above),
// entirely JS-built like the receiving screen's own per-line entry --
// no static markup per line.
// ---------------------------------------------------------------------

/** Fired once per order load (never per row): the product-scoped read
 * this screen composes with each line's own id, filtered client-side,
 * since no route returns receipts scoped to an order or a line
 * (docs/build-briefs/PO6-S5-corrections-and-exceptions.md's own
 * "Findings"). Skipped entirely for a session without inv.receipt.view,
 * so that session's screen never shows an empty "Receipts" section it
 * has no right to populate. */
async function loadAllPurchasingOrderLineReceipts(
  lines: readonly PurchaseOrderLineDto[],
): Promise<void> {
  const session = requireSession();
  if (!hasPermission(session, INVENTORY_RECEIPT_VIEW_PERMISSION)) {
    return;
  }
  await Promise.all(lines.map((line) => loadPurchasingOrderLineReceipts(line)));
}

async function loadPurchasingOrderLineReceipts(line: PurchaseOrderLineDto): Promise<void> {
  const session = requireSession();
  const result = await listReceipts(session.apiBaseUrl, session.tenantId, {
    liquorProductId: line.liquorProductId,
  });
  if (result.status !== 200) {
    return;
  }
  // A product ordered on two different purchase orders: this line shows
  // only ITS OWN receipts (matched by purchase_order_line_id), never the
  // other order's, even though both share liquorProductId.
  const lineReceipts = result.body.receipts.filter(
    (receipt) => receipt.purchase_order_line_id === line.id,
  );
  state.purchasingOrderLineReceipts.set(line.id, lineReceipts);
  renderPurchasingOrderLineReceipts(line);
}

function renderPurchasingOrderLineReceipts(line: PurchaseOrderLineDto): void {
  const container = purchasingOrderDetailLinesEl.querySelector<HTMLElement>(
    `[data-purchase-order-line-id="${CSS.escape(line.id)}"]`,
  );
  if (container === null) {
    // The screen has already moved to a different order by the time this
    // (possibly slow) read landed; nothing to render into.
    return;
  }
  const receipts = state.purchasingOrderLineReceipts.get(line.id) ?? [];
  container.innerHTML = "";
  container.hidden = false;
  const heading = document.createElement("span");
  heading.className = "field-label";
  heading.textContent = "Receipts";
  container.append(heading);
  if (receipts.length === 0) {
    const empty = document.createElement("p");
    empty.className = "tabs-empty";
    empty.dataset["testid"] = "purchasing-order-line-receipts-empty";
    empty.textContent = "No receipts recorded against this line yet.";
    container.append(empty);
    return;
  }
  for (const receipt of receipts) {
    container.append(buildPurchasingOrderLineReceiptRow(line, receipt));
  }
}

function buildPurchasingOrderLineReceiptRow(
  line: PurchaseOrderLineDto,
  receipt: InventoryReceiptDto,
): HTMLElement {
  const row = document.createElement("div");
  row.className = "purchasing-order-line-receipt-row";
  row.dataset["testid"] = "purchasing-order-line-receipt-row";
  row.dataset["receiptId"] = receipt.id;

  const summary = document.createElement("span");
  summary.className = "purchasing-order-line-receipt-summary";
  summary.dataset["testid"] = "purchasing-order-line-receipt-summary";
  summary.textContent = formatReceiptSummary(receipt);
  row.append(summary);

  const session = requireSession();
  if (!hasPermission(session, INVENTORY_RECEIPT_CORRECT_PERMISSION)) {
    return row;
  }

  const correctButton = document.createElement("button");
  correctButton.type = "button";
  correctButton.className = "btn-secondary btn-compact";
  correctButton.dataset["testid"] = "purchasing-order-line-receipt-correct-button";
  correctButton.textContent = "Correct";
  row.append(correctButton);

  const form = buildPurchasingOrderLineReceiptCorrectionForm(line, receipt, correctButton);
  row.append(form);

  correctButton.addEventListener("click", () => {
    correctButton.hidden = true;
    form.hidden = false;
  });

  return row;
}

interface ReceiptCorrectionControls {
  readonly line: PurchaseOrderLineDto;
  readonly receipt: InventoryReceiptDto;
  readonly quantityInput: HTMLInputElement;
  readonly costInput: HTMLInputElement;
  readonly reasonInput: HTMLInputElement;
  readonly errorEl: HTMLElement;
  readonly submitButton: HTMLButtonElement;
  readonly approvalContainer: HTMLElement;
}

const CORRECTED_RECEIPT_QUANTITY_PATTERN = /^\d+(\.\d{1,4})?$/;

/** INV8-F1-F1. A container-count receipt's own quantity is corrected in
 * whatever container it was received in (bottle, can or keg), never
 * ounces: this is the same word INV8-F1's own receiving chip already
 * uses. "container" for the one case that should not occur from any
 * shipped screen (received_container_count set but received_container_
 * type unset), a defensive fallback rather than a blank label. */
function receiptContainerUnitLabel(receipt: InventoryReceiptDto): string {
  return receipt.received_container_type ?? "container";
}

function buildPurchasingOrderLineReceiptCorrectionForm(
  line: PurchaseOrderLineDto,
  receipt: InventoryReceiptDto,
  correctButton: HTMLButtonElement,
): HTMLElement {
  const form = document.createElement("div");
  form.className = "purchasing-order-line-receipt-correction-form";
  form.dataset["testid"] = "purchasing-order-line-receipt-correction-form";
  form.hidden = true;

  // INV8-F1-F1. A container-count receipt is corrected in its own
  // container count (bottles, cans or kegs), never the resolved ounces a
  // raw receipt is corrected in: receiving.ts's own line-state SUM reads
  // whichever this receipt actually was, so the form must pre-fill and
  // submit the matching shape. "What was actually received" (ADR-003
  // Decision 3), prefilled with the receipt's own current total in ITS
  // OWN unit: the corrected TOTAL, never a typed delta.
  // handleSubmitReceiptCorrectionClick derives the signed delta the route
  // actually accepts from this and the receipt's own stored total.
  const isContainerReceipt = receipt.received_container_count !== null;
  const quantityInput = document.createElement("input");
  quantityInput.type = "number";
  quantityInput.inputMode = "decimal";
  quantityInput.step = "1";
  quantityInput.min = "0";
  quantityInput.className = "field-input purchasing-order-line-receipt-correction-quantity-input";
  quantityInput.dataset["testid"] = "purchasing-order-line-receipt-correction-quantity-input";
  quantityInput.value = isContainerReceipt
    ? formatReceivingQuantity(receipt.received_container_count as string)
    : formatReceivingQuantity(receipt.quantity);
  quantityInput.setAttribute(
    "aria-label",
    isContainerReceipt
      ? `Corrected ${receiptContainerUnitLabel(receipt)} count received for ${line.productName}`
      : `Corrected quantity received for ${line.productName}`,
  );
  form.append(quantityInput);

  // Corrected cost per unit, optional: the price ITSELF (ADR-003
  // Decision 3, configuration-shaped), never a delta. Left blank, the
  // price stands.
  const costInput = document.createElement("input");
  costInput.type = "number";
  costInput.inputMode = "decimal";
  costInput.min = "0";
  costInput.step = "0.01";
  costInput.className = "field-input purchasing-order-line-receipt-correction-cost-input";
  costInput.dataset["testid"] = "purchasing-order-line-receipt-correction-cost-input";
  costInput.placeholder = "Corrected cost/unit $";
  costInput.setAttribute("aria-label", `Corrected cost per unit for ${line.productName}`);
  form.append(costInput);

  // Free text: the domain's own `reason` field carries no fixed
  // vocabulary (unlike exceptionReason/resolution above), and this is an
  // admin, keyboard-and-pointer surface.
  const reasonInput = document.createElement("input");
  reasonInput.type = "text";
  reasonInput.className = "field-input purchasing-order-line-receipt-correction-reason-input";
  reasonInput.dataset["testid"] = "purchasing-order-line-receipt-correction-reason-input";
  reasonInput.placeholder = "Reason for this correction";
  reasonInput.autocomplete = "off";
  form.append(reasonInput);

  const errorEl = document.createElement("div");
  errorEl.className = "purchasing-order-line-receipt-correction-error";
  errorEl.dataset["testid"] = "purchasing-order-line-receipt-correction-error";
  errorEl.hidden = true;

  const approvalContainer = document.createElement("div");
  approvalContainer.className = "purchasing-order-line-receipt-correction-approval";
  approvalContainer.dataset["testid"] = "purchasing-order-line-receipt-correction-approval";
  approvalContainer.hidden = true;

  const submitButton = document.createElement("button");
  submitButton.type = "button";
  submitButton.className = "btn-primary btn-compact";
  submitButton.dataset["testid"] = "purchasing-order-line-receipt-correction-submit";
  submitButton.textContent = "Submit correction";

  const cancelButton = document.createElement("button");
  cancelButton.type = "button";
  cancelButton.className = "btn-secondary btn-compact";
  cancelButton.dataset["testid"] = "purchasing-order-line-receipt-correction-cancel";
  cancelButton.textContent = "Cancel";
  cancelButton.addEventListener("click", () => {
    form.hidden = true;
    correctButton.hidden = false;
    quantityInput.value = isContainerReceipt
      ? formatReceivingQuantity(receipt.received_container_count as string)
      : formatReceivingQuantity(receipt.quantity);
    costInput.value = "";
    reasonInput.value = "";
    errorEl.hidden = true;
    approvalContainer.hidden = true;
    approvalContainer.innerHTML = "";
  });

  const controls: ReceiptCorrectionControls = {
    line,
    receipt,
    quantityInput,
    costInput,
    reasonInput,
    errorEl,
    submitButton,
    approvalContainer,
  };
  submitButton.addEventListener("click", () => void handleSubmitReceiptCorrectionClick(controls));

  form.append(errorEl, submitButton, cancelButton, approvalContainer);
  return form;
}

/** One attempt, replayed with the SAME idempotencyKey whether it is the
 * first try or an approval retry (PO4-F3's own reuse shape). First
 * attempt omits approval fields; inv.receipt.correct is approval_
 * required for an Assistant Manager, so a 403 approval_required opens
 * the inline approver-and-PIN panel rather than failing outright. */
async function handleSubmitReceiptCorrectionClick(
  controls: ReceiptCorrectionControls,
): Promise<void> {
  controls.errorEl.hidden = true;
  const rawCorrectedQuantity = normalizeLeadingDot(controls.quantityInput.value);
  if (!CORRECTED_RECEIPT_QUANTITY_PATTERN.test(rawCorrectedQuantity)) {
    controls.errorEl.hidden = false;
    controls.errorEl.textContent = "Enter what was actually received, e.g. 4 or 1.5.";
    return;
  }
  // INV8-F1-F1: a container-count receipt is corrected in its own
  // container count (containerCountDelta), never ounces (quantityDelta);
  // a raw receipt is the reverse. Both are the same signed-delta
  // computation over whatever the receipt's own original total was.
  const isContainerReceipt = controls.receipt.received_container_count !== null;
  let delta: string;
  try {
    delta = computeReceiptCorrectionQuantityDelta(
      isContainerReceipt
        ? (controls.receipt.received_container_count as string)
        : controls.receipt.quantity,
      rawCorrectedQuantity,
    );
  } catch (error) {
    controls.errorEl.hidden = false;
    controls.errorEl.textContent =
      error instanceof Error ? error.message : "Enter a valid corrected quantity.";
    return;
  }
  let correctedUnitCostCents: number | undefined;
  const rawCost = controls.costInput.value.trim();
  if (rawCost.length > 0) {
    try {
      correctedUnitCostCents = parseDollarsToCentsExact(rawCost, "corrected cost per unit");
    } catch (error) {
      controls.errorEl.hidden = false;
      controls.errorEl.textContent =
        error instanceof Error ? error.message : "Enter a valid corrected cost per unit.";
      return;
    }
  }
  const reason = controls.reasonInput.value.trim();
  if (reason.length === 0) {
    controls.errorEl.hidden = false;
    controls.errorEl.textContent = "Enter a reason for this correction.";
    return;
  }
  if (delta === "0" && correctedUnitCostCents === undefined) {
    controls.errorEl.hidden = false;
    controls.errorEl.textContent =
      "This changes neither the quantity nor the cost. Change one, or both.";
    return;
  }

  const session = requireSession();
  const idempotencyKey = crypto.randomUUID();
  const attempt = (approval?: { approverUserId: string; approverPin: string }) =>
    correctReceipt(session.apiBaseUrl, session.tenantId, controls.receipt.id, {
      idempotencyKey,
      ...(isContainerReceipt ? { containerCountDelta: delta } : { quantityDelta: delta }),
      reason,
      ...(correctedUnitCostCents !== undefined ? { correctedUnitCostCents } : {}),
      ...(approval ?? {}),
    });

  controls.submitButton.disabled = true;
  const first = await attempt();
  if (first.status === 200 || first.status === 201) {
    controls.submitButton.disabled = false;
    showToast(`Correction recorded for ${controls.line.productName}.`, "success");
    await loadPurchasingOrderLineReceipts(controls.line);
    return;
  }
  if (!first.networkError && first.status === 403 && first.body?.error === "approval_required") {
    controls.submitButton.disabled = false;
    await openInlineReceiptCorrectionApproval(controls, attempt);
    return;
  }
  controls.submitButton.disabled = false;
  controls.errorEl.hidden = false;
  controls.errorEl.textContent =
    first.body.message ?? `Could not correct this receipt (${first.body.error ?? first.status}).`;
}

/** Opens the inline approver/PIN panel inside this receipt's own
 * correction form, the same authoritative-then-degrade shape
 * handleOpenPurchasingOrderApproveClick above already uses, generalized
 * here to a first-attempt-then-retry flow (inv.receipt.correct is only
 * SOMETIMES approval_required, unlike order approval, which the route
 * always requires a second person for). A refusal on the retry --
 * wrong PIN, an approver without inv.receipt.correct, or any other
 * domain refusal -- shows on this same receipt's own error line,
 * persistent, and the panel stays open for another attempt. */
async function openInlineReceiptCorrectionApproval(
  controls: ReceiptCorrectionControls,
  attempt: (approval?: {
    approverUserId: string;
    approverPin: string;
  }) => ReturnType<typeof correctReceipt>,
): Promise<void> {
  const session = requireSession();
  controls.approvalContainer.hidden = false;
  controls.approvalContainer.innerHTML = "";

  const approverLabel = document.createElement("label");
  approverLabel.textContent = "Approver";
  const approverSelect = document.createElement("select");
  approverSelect.dataset["testid"] = "purchasing-order-line-receipt-correction-approver";
  approverSelect.disabled = true;

  const pinLabel = document.createElement("label");
  pinLabel.textContent = "Approver PIN";
  const pinInput = document.createElement("input");
  pinInput.type = "password";
  pinInput.inputMode = "numeric";
  pinInput.autocomplete = "off";
  pinInput.dataset["testid"] = "purchasing-order-line-receipt-correction-approver-pin";

  const confirmButton = document.createElement("button");
  confirmButton.type = "button";
  confirmButton.className = "btn-primary btn-compact";
  confirmButton.dataset["testid"] = "purchasing-order-line-receipt-correction-approval-submit";
  confirmButton.textContent = "Submit approval";
  confirmButton.disabled = true;

  controls.approvalContainer.append(
    approverLabel,
    approverSelect,
    pinLabel,
    pinInput,
    confirmButton,
  );

  const authoritative = await listApprovalCandidates(session.apiBaseUrl, session.tenantId, {
    permissionId: INVENTORY_RECEIPT_CORRECT_PERMISSION,
  });
  const selection =
    authoritative.status === 200
      ? selectApprovalCandidates(authoritative.body.candidates, [], session.userId)
      : await (async () => {
          const roster = await listUsers(session.apiBaseUrl, session.tenantId, {
            activeOnly: true,
          });
          if (roster.status !== 200) {
            return undefined;
          }
          return selectApprovalCandidates(undefined, roster.body.users, session.userId);
        })();

  if (selection === undefined) {
    controls.errorEl.hidden = false;
    controls.errorEl.textContent = "Could not load the staff list. Check your connection.";
    return;
  }
  if (selection.candidates.length === 0) {
    controls.errorEl.hidden = false;
    controls.errorEl.textContent =
      "No other approver is available on this device. Ask a manager to sign in and approve this correction.";
    return;
  }
  for (const candidate of selection.candidates) {
    const option = document.createElement("option");
    option.value = candidate.id;
    option.textContent = candidate.displayName;
    approverSelect.append(option);
  }
  approverSelect.disabled = false;
  confirmButton.disabled = false;

  confirmButton.addEventListener("click", () => {
    void (async () => {
      const approverUserId = approverSelect.value;
      const approverPin = pinInput.value;
      if (approverUserId.length === 0 || approverPin.length === 0) {
        controls.errorEl.hidden = false;
        controls.errorEl.textContent = "Pick an approver and enter their PIN.";
        return;
      }
      confirmButton.disabled = true;
      const retryResult = await attempt({ approverUserId, approverPin });
      if (retryResult.status === 200 || retryResult.status === 201) {
        showToast(`Correction recorded for ${controls.line.productName}.`, "success");
        await loadPurchasingOrderLineReceipts(controls.line);
        return;
      }
      confirmButton.disabled = false;
      pinInput.value = "";
      controls.errorEl.hidden = false;
      controls.errorEl.textContent =
        retryResult.body.message ??
        `Could not correct this receipt (${retryResult.body.error ?? retryResult.status}).`;
    })();
  });
}

// ---------------------------------------------------------------------
// PO6 Slice 2 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S2-cart-and-placement.md). The cart: draft
// proposals, search-and-add, edit/remove a line, and placement, one
// purchase order per vendor.
// ---------------------------------------------------------------------

/** PO6-R1 (DEC-056): reached only from Settings > Purchasing's own
 * "Purchasing Cart" button, mirroring showPurchasingOrdersScreen. Placed
 * orders shown lower on this
 * screen are scoped to THIS VISIT (state.purchasingCartPlacedOrders is
 * reset here), never carried over from a previous time the screen was
 * open. */
type PurchasingCartContext = { generation: number; visit: number; navigation: number };
let purchasingCartVisit = 0;
let purchasingVendorNavigation = 0;

function capturePurchasingCartContext(): PurchasingCartContext {
  return { generation: sessionGeneration, visit: purchasingCartVisit, navigation: purchasingVendorNavigation };
}

function purchasingCartContextIsCurrent(context: PurchasingCartContext): boolean {
  return sessionIsCurrent(context.generation) && state.session !== undefined &&
    context.visit === purchasingCartVisit && context.navigation === purchasingVendorNavigation &&
    !purchasingCartScreen.hidden;
}

function resetPurchasingCartSessionState(): void {
  purchasingCartVisit = purchasingCartVisit + 1;
  if (purchasingCartProductSearchDebounceTimer !== undefined) {
    clearTimeout(purchasingCartProductSearchDebounceTimer);
    purchasingCartProductSearchDebounceTimer = undefined;
  }
  state.purchasingCartLines = [];
  state.purchasingCartLocationId = undefined;
  state.purchasingCartVendorSummaries = [];
  state.purchasingCartEstimate = null;
  state.purchasingCartProposals = [];
  state.purchasingCartPlacedOrders = [];
  state.purchasingCartProposalsSort = "shortfall";
  purchasingCartProductSearchResults = [];
  purchasingCartProductSearchInput.value = "";
  for (const element of [purchasingCartProposalsListEl, purchasingCartProductResultsEl,
    purchasingCartVendorGroupsEl, purchasingCartSummaryEl, purchasingCartPlacedListEl]) {
    element.replaceChildren();
  }
  purchasingCartSummaryEl.hidden = true;
  purchasingCartProposalsEmptyEl.hidden = true;
  purchasingCartEmptyEl.hidden = true;
  cartSectionsEl.classList.remove("cart-has-lines");
  purchasingCartPlacedEmptyEl.hidden = true;
  purchasingCartStatusEl.hidden = true;
  purchasingCartStatusEl.textContent = "";
  purchasingCartConfirmationBannerEl.hidden = true;
  purchasingCartConfirmationTitleEl.textContent = "";
  purchasingCartConfirmationDetailEl.textContent = "";
  purchasingCartConfirmationViewButton.onclick = null;
  purchasingCartAddAllBelowParButton.disabled = true;
  purchasingCartAddAllBelowParReasonEl.hidden = true;
  purchasingCartAddAllBelowParReasonEl.textContent = "";
}

function showPurchasingCartScreen(): void {
  resetPurchasingCartSessionState();
  hideAllScreens();
  purchasingCartScreen.hidden = false;
  showTopbarChrome("purchasing-cart-screen");
  purchasingCartStatusEl.hidden = true;
  purchasingCartStatusEl.textContent = "";
  // UX66 part C: a confirmation from a previous visit must not still be
  // showing on a fresh open, the same "own visit" reset
  // purchasingCartPlacedOrders (below) already gets.
  purchasingCartConfirmationBannerEl.hidden = true;
  purchasingCartProductSearchInput.value = "";
  purchasingCartProductResultsEl.innerHTML = "";
  purchasingCartProductSearchResults = [];
  state.purchasingCartPlacedOrders = [];
  renderPurchasingCartPlacedOrders();
  // INV8 (DEC-176) Section 2.5: the Low list opens on its own default
  // sort every time the screen is freshly opened, the same "own visit"
  // reset purchasingCartPlacedOrders just got.
  state.purchasingCartProposalsSort = "shortfall";
  purchasingCartProposalsSortSelect.value = "shortfall";
  purchasingCartProposalsListEl.innerHTML = '<p class="tabs-loading">Loading…</p>';
  purchasingCartAddAllBelowParButton.disabled = true;
  // UX65: no reason to show yet, nothing has loaded.
  purchasingCartAddAllBelowParReasonEl.hidden = true;
  purchasingCartAddAllBelowParReasonEl.textContent = "";
  purchasingCartVendorGroupsEl.innerHTML = '<p class="tabs-loading">Loading…</p>';
  void loadPurchasingCartProposals();
  void loadPurchasingCart();
}

function showPurchasingCartError(message: string): void {
  purchasingCartStatusEl.hidden = false;
  purchasingCartStatusEl.textContent = message;
}

// --- Draft proposals (criteria 4 and 5: derived from low stock, out of
// stock, and par; reading them creates nothing). ---

async function loadPurchasingCartProposals(): Promise<void> {
  const session = requireSession();
  const cartContext = capturePurchasingCartContext();
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  const result = await getDraftProposals(session.apiBaseUrl, session.tenantId, {
    sources: ["below_par", "low_stock", "out_of_stock"],
  });
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  if (result.status !== 200) {
    purchasingCartProposalsListEl.innerHTML = "";
    showPurchasingCartError(
      result.body.message ?? "Could not load draft proposals. Check your connection.",
    );
    return;
  }
  state.purchasingCartProposals = result.body.proposals;
  renderPurchasingCartProposals();
}

function renderPurchasingCartProposals(): void {
  purchasingCartProposalsListEl.innerHTML = "";
  refreshAddAllBelowParButton();
  if (state.purchasingCartProposals.length === 0) {
    purchasingCartProposalsEmptyEl.hidden = false;
    return;
  }
  purchasingCartProposalsEmptyEl.hidden = true;
  // INV8 (DEC-176) Section 2.5: sorted for DISPLAY only, from the already-
  // fetched list; state.purchasingCartProposals itself is never reordered,
  // so re-sorting never re-fetches.
  const sorted = sortProposalsForDisplay(
    state.purchasingCartProposals,
    state.purchasingCartProposalsSort,
  );
  for (const proposal of sorted) {
    purchasingCartProposalsListEl.append(buildPurchasingCartProposalRow(proposal));
  }
}

/** UX16 (DEC-058), criterion 2: disabled rather than hidden when nothing
 * is below par right now, so the control stays a stable, findable part
 * of the screen rather than appearing and disappearing under a manager's
 * thumb. UX65: paired with a visible, plain-text reason (not a
 * title/tooltip) next to the button whenever it is disabled, computed
 * from the same loaded proposals; hidden the moment the button is
 * enabled again. */
function refreshAddAllBelowParButton(): void {
  purchasingCartAddAllBelowParButton.disabled =
    belowParProposalsToStage(state.purchasingCartProposals).length === 0;
  const reason = belowParButtonDisabledReason(state.purchasingCartProposals);
  purchasingCartAddAllBelowParReasonEl.hidden = reason === null;
  purchasingCartAddAllBelowParReasonEl.textContent = reason ?? "";
}

function buildPurchasingCartProposalRow(proposal: DraftProposalDto): HTMLElement {
  const row = document.createElement("div");
  row.className = "purchasing-cart-proposal-row";
  row.dataset["testid"] = "purchasing-cart-proposal-row";

  const product = document.createElement("div");
  product.className = "purchasing-cart-proposal-product";
  product.dataset["testid"] = "purchasing-cart-proposal-product";
  product.textContent = proposal.productName;

  const sources = document.createElement("div");
  sources.className = "purchasing-cart-proposal-sources";
  sources.dataset["testid"] = "purchasing-cart-proposal-sources";
  sources.textContent = proposalStatusChipLabel(proposal);

  // INV8 (DEC-176) Section 2.5: on hand and par shown in the item's own
  // count unit (bottles, kegs, cans, units) with the unit label, when one
  // resolves; the raw, unlabeled figures otherwise, paired with a hint
  // rather than a silently false-confident comparison.
  const unitDisplay = formatProposalUnitDisplay(proposal);

  const onHand = document.createElement("div");
  onHand.className = "purchasing-cart-proposal-onhand";
  onHand.dataset["testid"] = "purchasing-cart-proposal-onhand";
  onHand.textContent = `On hand: ${unitDisplay.onHandText}`;

  // UX16-F2 (docs/build-briefs/UX16-F2-negative-on-hand-shown-not-hidden.md;
  // DEC-178): a negative on-hand figure is shown, never hidden or refused,
  // with a plain warning naming the cause and the remedy. Null when on-hand
  // is zero or positive, matching formatProposalOnHandWarning's own
  // contract: nothing renders rather than an empty, invisible element.
  const onHandWarningText = formatProposalOnHandWarning(proposal);
  let onHandWarning: HTMLElement | null = null;
  if (onHandWarningText !== null) {
    onHandWarning = document.createElement("div");
    onHandWarning.className = "purchasing-cart-proposal-onhand-warning";
    onHandWarning.dataset["testid"] = "purchasing-cart-proposal-onhand-warning";
    onHandWarning.textContent = onHandWarningText;
  }

  // UX16 (DEC-058), criterion 1: the par level itself, next to on-hand,
  // so the number a suggestion is derived FROM is visible rather than
  // implied. "—" for a product with no par names that fact instead of
  // hiding it (see formatProposalParLevel's own comment).
  const par = document.createElement("div");
  par.className = "purchasing-cart-proposal-par";
  par.dataset["testid"] = "purchasing-cart-proposal-par";
  par.textContent = `Par: ${unitDisplay.parText}`;

  const suggested = document.createElement("div");
  suggested.className = "purchasing-cart-proposal-suggested";
  suggested.dataset["testid"] = "purchasing-cart-proposal-suggested";
  suggested.textContent = `Suggested: ${formatProposalSuggested(proposal)}`;

  const addButton = document.createElement("button");
  addButton.type = "button";
  addButton.className = "btn-secondary btn-compact";
  addButton.dataset["testid"] = "purchasing-cart-proposal-add";
  // PRD P0.12/DEC-176: the Low list is at-or-below par, so an
  // exactly-at-par row IS listed, with a zero shortfall. setCartLine's own
  // requirePositiveQuantity refuses exactly zero (proven in the backend
  // record), so this row's own Add-to-cart is disabled rather than
  // offered and left to fail; the unit figures above already show the
  // person this item is exactly at par.
  const shortfallIsZero = proposalShortfallIsZero(proposal);
  addButton.disabled = shortfallIsZero;
  addButton.textContent = shortfallIsZero ? "At par" : "Add to cart";
  addButton.addEventListener("click", () => void handleAddProposalToCart(proposal));

  // INVPOWALK1 (F1): two lines inside the card. Line one is the name, the status chip and the action; line two is the
  // facts (on hand, par, suggested, what the order is in), so nothing runs together or leaves the card.
  const facts = document.createElement("div");
  facts.className = "purchasing-cart-proposal-facts";
  facts.append(onHand, par, suggested);
  // LOCORDER1 (B7): who it would be ordered from and what it costs, so a proposal is judged without opening the cart.
  const vendorCostText = formatProposalVendorCost(proposal);
  if (vendorCostText !== null) {
    const vendorCost = document.createElement("div");
    vendorCost.className = "purchasing-cart-proposal-vendor";
    vendorCost.dataset["testid"] = "purchasing-cart-proposal-vendor";
    vendorCost.textContent = vendorCostText;
    facts.append(vendorCost);
  }
  // PO9: what the staged quantity is in, beside the quantity.
  const coverageText = formatProposalOrderCoverage(proposal);
  if (coverageText !== null) {
    const coverage = document.createElement("div");
    coverage.className = "purchasing-cart-proposal-order-coverage";
    coverage.dataset["testid"] = "purchasing-cart-proposal-order-coverage";
    coverage.textContent = coverageText;
    facts.append(coverage);
  }
  row.append(product, sources, addButton, facts);
  if (onHandWarning !== null) {
    row.append(onHandWarning);
  }
  if (unitDisplay.hint !== null) {
    const hint = document.createElement("div");
    hint.className = "purchasing-cart-proposal-unit-hint";
    hint.dataset["testid"] = "purchasing-cart-proposal-unit-hint";
    hint.textContent = unitDisplay.hint;
    row.append(hint);
  }
  return row;
}

/** Stages the proposal into the cart; PLACES NOTHING (criterion 5's own
 * "reviewable draft" requirement). suggestedQuantity pre-fills the
 * quantity a person can still edit in the cart section below, exactly
 * like a proposal's own par-minus-on-hand figure is a suggestion, not a
 * commitment. Defensive early return for a zero shortfall, mirroring the
 * disabled button above: the button itself already stops a real tap, but
 * this guard keeps the write from ever being attempted even if this
 * function is reached some other way. */
async function handleAddProposalToCart(proposal: DraftProposalDto): Promise<void> {
  if (proposalShortfallIsZero(proposal)) {
    return;
  }
  // PO9: the order-unit quantity the server worked out, never the bottle
  // shortfall (5 bottles short is 1 case of 12, not 5 cases).
  const quantity = stagedQuantityForProposal(proposal);
  if (quantity === null) {
    showToast(
      proposal.orderBlockedReason ?? "This item cannot be ordered from its shortfall.",
      "danger",
    );
    return;
  }
  await addCartLineAndReload(proposal.liquorProductId, quantity, proposal.productName);
}

/** UX16 (DEC-058), criterion 2: "a button to just add every bottle to a
 * new order that is below par". Stages every proposal
 * belowParProposalsToStage keeps, each at its own par-minus-on-hand
 * shortfall -- PLACES NOTHING, matching handleAddProposalToCart's own
 * single-line behavior, and every staged line stays individually
 * adjustable and removable in the cart section below.
 *
 * Sequential, not Promise.all: each write is its own PUT of one cart
 * line, and running them one at a time keeps the failure of one line
 * (a product deactivated mid-request, say) from racing the rest. */
async function handleAddAllBelowParClick(): Promise<void> {
  const session = requireSession();
  const cartContext = capturePurchasingCartContext();
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  const lines = belowParProposalsToStage(state.purchasingCartProposals);
  if (lines.length === 0) {
    return;
  }
  let staged = 0;
  const failed: string[] = [];
  for (const line of lines) {
    if (!purchasingCartContextIsCurrent(cartContext)) return;
    const result = await setCartLine(session.apiBaseUrl, session.tenantId, {
      liquorProductId: line.liquorProductId,
      quantity: line.quantity,
    });
    if (!purchasingCartContextIsCurrent(cartContext)) return;
    if (result.status !== 200) {
      failed.push(line.productName);
      continue;
    }
    applyCartResponse(result.body.cartLines);
    staged += 1;
  }
  renderPurchasingCartVendorGroups();
  if (failed.length === 0) {
    showToast(
      staged === 1
        ? "1 product staged at its par shortfall."
        : `${staged} products staged at their par shortfall.`,
      "success",
    );
  } else {
    showToast(`Staged ${staged} product(s); could not stage ${failed.join(", ")}.`, "danger");
  }
}

// --- Search and add (criterion 4's "search" source; never free text --
// a person taps a specific product ROW, never types a product name into
// the cart itself). ---

function handlePurchasingCartProductSearchInput(): void {
  if (purchasingCartProductSearchDebounceTimer !== undefined) {
    clearTimeout(purchasingCartProductSearchDebounceTimer);
  }
  const cartContext = capturePurchasingCartContext();
  purchasingCartProductSearchDebounceTimer = setTimeout(() => {
    purchasingCartProductSearchDebounceTimer = undefined;
    if (purchasingCartContextIsCurrent(cartContext)) void loadPurchasingCartProductSearchResults();
  }, 250);
}

async function loadPurchasingCartProductSearchResults(): Promise<void> {
  const session = requireSession();
  const cartContext = capturePurchasingCartContext();
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  const query = purchasingCartProductSearchInput.value.trim();
  if (query.length === 0) {
    purchasingCartProductSearchResults = [];
    purchasingCartProductResultsEl.innerHTML = "";
    return;
  }
  const result = await listLiquorProducts(session.apiBaseUrl, session.tenantId, { locationId: terminalConfigState?.locationId,
    q: query,
    activeOnly: true,
  });
  if (!purchasingCartContextIsCurrent(cartContext) ||
      purchasingCartProductSearchInput.value.trim() !== query) return;
  if (result.status !== 200) {
    purchasingCartProductResultsEl.innerHTML = "";
    showPurchasingCartError(
      result.body.message ?? "Could not search products. Check your connection.",
    );
    return;
  }
  purchasingCartProductSearchResults = result.body.liquorProducts.filter(
    (product) => { const selling = floorProductSelling(product); return selling?.active === true && selling.trackStock === true && product.stop_ordering !== true; },
  );
  renderPurchasingCartProductResults();
}

function renderPurchasingCartProductResults(): void {
  purchasingCartProductResultsEl.innerHTML = "";
  for (const product of purchasingCartProductSearchResults) {
    purchasingCartProductResultsEl.append(buildPurchasingCartProductResultRow(product));
  }
}

function buildPurchasingCartProductResultRow(product: LiquorProductDto): HTMLElement {
  const row = document.createElement("div");
  row.className = "purchasing-cart-product-result-row";
  row.dataset["testid"] = "purchasing-cart-product-result-row";

  const name = document.createElement("div");
  name.className = "purchasing-cart-product-result-name";
  name.dataset["testid"] = "purchasing-cart-product-result-name";
  name.textContent = product.name;

  const addButton = document.createElement("button");
  addButton.type = "button";
  addButton.className = "btn-secondary btn-compact";
  addButton.dataset["testid"] = "purchasing-cart-product-result-add";
  addButton.textContent = "Add to cart";
  addButton.addEventListener("click", () => void handleAddProductToCart(product));

  row.append(name, addButton);
  return row;
}

async function handleAddProductToCart(product: LiquorProductDto): Promise<void> {
  await addCartLineAndReload(product.id, "1", product.name);
}

/** Shared by the proposal-accept and search-add flows: PUT the line, then
 * re-render straight from THIS write's own response (never a second GET),
 * matching setCartLine's route (src/api/server.ts), which already returns
 * the full updated cart. */
async function addCartLineAndReload(
  liquorProductId: string,
  quantity: string,
  productName: string,
): Promise<void> {
  const session = requireSession();
  const cartContext = capturePurchasingCartContext();
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  const result = await setCartLine(session.apiBaseUrl, session.tenantId, {
    liquorProductId,
    quantity,
  });
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  if (result.status !== 200) {
    showToast(
      `Could not add ${productName} to the cart. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  applyCartResponse(result.body.cartLines);
  renderPurchasingCartVendorGroups();
  showToast(`${productName} added to the cart.`, "success");
}

// --- The cart itself, grouped by vendor (criteria 6, 7, and 8: the
// grouping placement will produce, visible before any of it places). ---

async function loadPurchasingCart(): Promise<void> {
  const session = requireSession();
  const cartContext = capturePurchasingCartContext();
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  const result = await getCart(session.apiBaseUrl, session.tenantId);
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  if (result.status !== 200) {
    purchasingCartVendorGroupsEl.innerHTML = "";
    showPurchasingCartError(
      result.body.message ?? "Could not load the cart. Check your connection.",
    );
    return;
  }
  applyCartResponse(result.body.cartLines);
  renderPurchasingCartVendorGroups();
  void loadPurchasingApprovalThresholdForCart().catch(() => undefined);
}

/** The one place a cart response (GET, PUT or DELETE) lands in state, so
 * the lines, the vendor summaries and the cart estimate always come from
 * the same read and cannot disagree. */
function applyCartResponse(cart: CartDto): void {
  state.purchasingCartLines = cart.lines;
  state.purchasingCartLocationId = cart.locationId;
  state.purchasingCartVendorSummaries = cart.vendorSummaries;
  state.purchasingCartEstimate = cart.cartEstimate;
}

function renderPurchasingCartVendorGroups(): void {
  purchasingCartVendorGroupsEl.innerHTML = "";
  purchasingCartSummaryEl.innerHTML = "";
  purchasingCartSummaryEl.hidden = true;
  // LOCORDER1 (B7): a cart with lines moves above the proposals; an empty one sits below them as one line.
  cartSectionsEl.classList.toggle("cart-has-lines", state.purchasingCartLines.length > 0);
  if (state.purchasingCartLines.length === 0) {
    purchasingCartEmptyEl.hidden = false;
    return;
  }
  purchasingCartEmptyEl.hidden = true;
  const session = requireSession();
  const canPlace = hasPermission(session, PURCHASING_ORDER_PLACE_PERMISSION);
  const estimate = state.purchasingCartEstimate;
  if (estimate !== null) {
    purchasingCartSummaryEl.append(...buildPurchasingCartSummaryParts(estimate));
    purchasingCartSummaryEl.hidden = false;
  }
  for (const group of groupCartLinesByVendor(state.purchasingCartLines)) {
    const summary = state.purchasingCartVendorSummaries.find(
      (candidate) => (candidate.vendorId ?? "unassigned") === group.vendorKey,
    );
    purchasingCartVendorGroupsEl.append(
      buildPurchasingCartVendorGroupCard(group, canPlace, summary),
    );
  }
}

type CartIconKind = "clock" | "alert" | "check";

/** The mockup's three small line icons (design/mockups/po-cart-total-
 * minimum.html), built as SVG nodes. Decorative: aria-hidden, because the
 * sentence beside each carries the meaning. */
function buildCartIcon(kind: CartIconKind): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("class", "purchasing-cart-icon");
  svg.setAttribute("viewBox", "0 0 20 20");
  svg.setAttribute("aria-hidden", "true");
  if (kind !== "check") {
    const circle = document.createElementNS(ns, "circle");
    circle.setAttribute("cx", "10");
    circle.setAttribute("cy", "10");
    circle.setAttribute("r", "9");
    circle.setAttribute("fill", "none");
    circle.setAttribute("stroke", "currentColor");
    circle.setAttribute("stroke-width", "2");
    svg.append(circle);
  }
  const path = document.createElementNS(ns, "path");
  path.setAttribute(
    "d",
    kind === "clock"
      ? "M10 5.5V10l3 2"
      : kind === "alert"
        ? "M10 5.5v5.5M10 13.6v1"
        : "M4 10.3l3.4 3.4L16 6.6",
  );
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "2");
  path.setAttribute("stroke-linecap", "round");
  svg.append(path);
  return svg;
}

/** PO6-S2-F2: the cart summary card above the vendor groups. Every figure
 * and count is the server's; this only words them. */
function buildPurchasingCartSummaryParts(estimate: CartEstimateDto): HTMLElement[] {
  const totalBlock = document.createElement("div");
  const heading = document.createElement("div");
  heading.className = "purchasing-cart-total-label";
  heading.dataset["testid"] = "purchasing-cart-summary-heading";
  heading.textContent = cartEstimateHeading(estimate);
  const value = document.createElement("div");
  value.className = "purchasing-cart-summary-value";
  value.dataset["testid"] = "purchasing-cart-summary-total";
  value.textContent = cartEstimateValue(estimate);
  totalBlock.append(heading, value);

  const facts = document.createElement("div");
  facts.className = "purchasing-cart-summary-facts";
  const counts = document.createElement("div");
  counts.className = "purchasing-cart-summary-counts";
  counts.dataset["testid"] = "purchasing-cart-summary-counts";
  counts.textContent = cartSummaryCounts(estimate);
  facts.append(counts);
  for (const text of cartSummaryChips(estimate)) {
    const chip = document.createElement("div");
    chip.className = "purchasing-cart-chip";
    chip.dataset["testid"] = "purchasing-cart-summary-chip";
    chip.append(buildCartIcon("alert"), document.createTextNode(text));
    facts.append(chip);
  }

  // UX102 (click-through review, Purchasing cart): the "Based on each vendor's listed prices" sentence under the
  // total is gone; the heading already says "Estimated".
  return [totalBlock, facts];
}

/** PO8: the order-by row under the vendor name. Never gates Place. */
function buildPurchasingCartScheduleRow(
  summary: CartVendorSummaryDto,
  options: { fixButton: boolean } = { fixButton: true },
): HTMLElement | null {
  const description = describeVendorSchedule(summary);
  if (description === null) {
    return null;
  }
  const row = document.createElement("div");
  row.className =
    description.tone === "warn"
      ? "purchasing-cart-schedule purchasing-cart-schedule--warn"
      : "purchasing-cart-schedule";
  row.dataset["testid"] = "purchasing-cart-vendor-group-schedule";
  row.dataset["scheduleKind"] = summary.schedule.kind;
  const text = document.createElement("p");
  text.className = "purchasing-cart-schedule-text";
  text.dataset["testid"] = "purchasing-cart-vendor-group-schedule-text";
  text.textContent = description.text;
  row.append(buildCartIcon(description.tone === "warn" ? "alert" : "clock"), text);
  const vendorId = summary.vendorId;
  if (
    options.fixButton &&
    description.needsDeliveryInfo &&
    vendorId !== null &&
    hasPermission(requireSession(), "inv.vendor.manage")
  ) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "purchasing-cart-fix-button";
    button.dataset["testid"] = "purchasing-cart-vendor-group-schedule-add-info";
    button.textContent = "Add delivery info";
    button.addEventListener("click", () => {
      showAdminScreen();
      void admin.openVendorContactEditor(requireSession(), vendorId);
    });
    row.append(button);
  }
  return row;
}

/** PO6-S2-F2 and PO8: the vendor group's footer block above Place: the
 * estimated total, then the minimum meter and one note (warn, met, or the
 * quiet "none"). Warns, never blocks. */
function buildPurchasingCartTotalRow(summary: CartVendorSummaryDto): HTMLElement {
  const row = document.createElement("div");
  row.className = "purchasing-cart-total-row";
  const block = document.createElement("div");
  block.className = "purchasing-cart-total-block";
  const label = document.createElement("div");
  label.className = "purchasing-cart-total-label";
  label.dataset["testid"] = "purchasing-cart-vendor-group-total-heading";
  label.textContent = vendorEstimateHeading(summary);
  const value = document.createElement("div");
  value.className = summary.totalIsComplete
    ? "purchasing-cart-total-value"
    : "purchasing-cart-total-value purchasing-cart-total-value--partial";
  value.dataset["testid"] = "purchasing-cart-vendor-group-total";
  value.textContent = vendorEstimateValue(summary);
  block.append(label, value);
  row.append(block);

  const minimum = describeVendorMinimum(summary);
  if (minimum !== null) {
    const wrap = document.createElement("div");
    wrap.className = "purchasing-cart-minimum";
    if (minimum.kind === "none") {
      const quiet = document.createElement("p");
      quiet.className = "purchasing-cart-minimum-quiet";
      quiet.dataset["testid"] = "purchasing-cart-vendor-group-minimum-none";
      quiet.textContent = minimum.quietText;
      wrap.append(quiet);
    } else {
      const meterText = document.createElement("div");
      meterText.className = "purchasing-cart-minimum-text";
      meterText.dataset["testid"] = "purchasing-cart-vendor-group-minimum-text";
      meterText.textContent = minimum.meterText;
      const meter = document.createElement("div");
      meter.className = "purchasing-cart-meter";
      meter.setAttribute("role", "img");
      meter.setAttribute("aria-label", minimum.meterText);
      meter.dataset["testid"] = "purchasing-cart-vendor-group-minimum-meter";
      const fill = document.createElement("div");
      fill.className =
        minimum.noteTone === "warn"
          ? "purchasing-cart-meter-fill purchasing-cart-meter-fill--warn"
          : "purchasing-cart-meter-fill purchasing-cart-meter-fill--ok";
      fill.style.width = `${minimum.fillPercent}%`;
      meter.append(fill);
      wrap.append(meterText, meter);
    }
    row.append(wrap);
  }
  return row;
}

function buildPurchasingCartMinimumNote(summary: CartVendorSummaryDto): HTMLElement | null {
  const minimum = describeVendorMinimum(summary);
  if (minimum === null || minimum.kind === "none") {
    return null;
  }
  const note = document.createElement("div");
  note.className =
    minimum.noteTone === "warn"
      ? "purchasing-cart-note purchasing-cart-note--warn"
      : "purchasing-cart-note purchasing-cart-note--ok";
  note.dataset["testid"] = "purchasing-cart-vendor-group-minimum-note";
  note.dataset["minimumStatus"] = summary.minimumStatus;
  const text = document.createElement("p");
  text.textContent = minimum.noteText;
  note.append(buildCartIcon(minimum.noteTone === "warn" ? "alert" : "check"), text);
  return note;
}

/** UX66 part C: every DISTINCT blocker across a vendor group's own
 * lines, each paired with the first line that carries it (the line a
 * "Edit this offer" fix link needs an id from). Distinct rather than one
 * entry per line: a group with three lines all missing a cost would
 * otherwise repeat the identical "missing a cost" row three times. */
function collectVendorGroupBlockers(
  group: CartVendorGroup,
): { blocker: CartLineBlockerDto; line: CartLineDto }[] {
  const seen = new Set<CartLineBlockerDto>();
  const result: { blocker: CartLineBlockerDto; line: CartLineDto }[] = [];
  for (const line of group.lines) {
    for (const blocker of line.blockers) {
      if (seen.has(blocker)) {
        continue;
      }
      seen.add(blocker);
      result.push({ blocker, line });
    }
  }
  return result;
}

/** UX66 part C, design/principles.md rule 3 ("blocked is a state you see
 * before you touch it, not an error after") extended one step further:
 * every blocker this vendor's own lines carry, named plainly, with a
 * direct fix link where the design/mockups/purchasing-cart-flow.html
 * proposal names one -- "Add a contact" for missing_contact (opens this
 * vendor's own settings row) and "Edit this offer" for missing_package OR
 * missing_cost (both live on the same vendor-offer row). PURCHBLOCK1:
 * vendor_inactive also opens the vendor's own row (its Vendor active switch),
 * every fix returns to this cart, and a person without the permission is told
 * who can instead of being shown a dead button. missing_vendor lives in the
 * "unassigned" group, whose lines carry their own Resolve button. Design
 * review, 2026-09-29: the built screen originally offered no fix link at
 * all for missing_cost, a dead end for the offer field this same editor
 * already fixes for its sibling blocker. */
function buildPurchasingCartVendorGroupBlockers(
  group: CartVendorGroup,
  summary: CartVendorSummaryDto | undefined,
): HTMLElement {
  // CORE9 (core walkthrough 2026-10-10, item 10): one card, one list of everything this vendor is missing, one link to
  // fix it. It used to be four rows, each with its own button.
  const banner = document.createElement("div");
  banner.className = "purchasing-cart-vendor-group-blockers";
  banner.dataset["testid"] = "purchasing-cart-vendor-group-blockers";
  const vendorId = group.vendorId;
  const collected = collectVendorGroupBlockers(group);
  const schedule = summary === undefined ? null : describeVendorSchedule(summary);
  const missing = describeVendorMissingList(
    collected.map((entry) => entry.blocker),
    schedule?.needsDeliveryInfo === true,
  );

  const heading = document.createElement("p");
  heading.className = "purchasing-cart-vendor-group-blocker-heading";
  heading.dataset["testid"] = "purchasing-cart-vendor-group-blocker-heading";
  heading.textContent = "Missing for this vendor";
  const list = document.createElement("ul");
  list.className = "purchasing-cart-vendor-group-blocker-list";
  list.dataset["testid"] = "purchasing-cart-vendor-group-blocker-list";
  for (const label of missing.items) {
    const item = document.createElement("li");
    item.className = "purchasing-cart-vendor-group-blocker-text";
    item.dataset["testid"] = "purchasing-cart-vendor-group-blocker-item";
    item.textContent = label;
    list.append(item);
  }
  banner.append(heading, list);

  // PURCHBLOCK1: each fix returns to this same cart (runCartFixFlow); a person who cannot make the fix is told who
  // can, never shown a button the server would refuse (design-language rules 3 and 11).
  const firstOfferLine = collected.find(
    (entry) => entry.blocker === "missing_package" || entry.blocker === "missing_cost",
  )?.line;
  const appendFixLink = (target: "vendor" | "offer", onClick: () => void): void => {
    const fixLink = document.createElement("button");
    fixLink.type = "button";
    fixLink.className = "purchasing-cart-vendor-group-blocker-fix";
    fixLink.dataset["testid"] = "purchasing-cart-vendor-group-blocker-fix";
    fixLink.dataset["target"] = target;
    fixLink.textContent = "Fix vendor setup";
    fixLink.addEventListener("click", onClick);
    banner.append(fixLink);
  };
  const appendDenied = (what: string): void => {
    const denied = document.createElement("p");
    denied.className = "purchasing-cart-vendor-group-blocker-denied";
    denied.dataset["testid"] = "purchasing-cart-vendor-group-blocker-denied";
    denied.textContent = `You cannot edit ${what}. Ask a manager who can.`;
    banner.append(denied);
  };
  if (missing.fixTarget === "vendor" && vendorId !== null) {
    if (canFixCartVendorSetup(requireSession())) {
      const focus = collected.some((entry) => entry.blocker === "vendor_inactive") ? "active" : "contact";
      appendFixLink("vendor", () => void fixCartVendorSetup(vendorId, group.vendorName, focus));
    } else {
      appendDenied("this vendor's setup");
    }
  } else if (missing.fixTarget === "offer" && firstOfferLine !== undefined) {
    if (canResolveCartVendorOption(requireSession())) {
      appendFixLink("offer", () => void resolveCartVendorOption(firstOfferLine));
    } else {
      appendDenied("Vendor Options");
    }
  }
  return banner;
}

function buildPurchasingCartVendorGroupCard(
  group: CartVendorGroup,
  canPlace: boolean,
  summary: CartVendorSummaryDto | undefined,
): HTMLElement {
  const card = document.createElement("div");
  // UX66 part C, design/principles.md rule 1 (state lives in color,
  // weight, and shape together, never color alone): the card's own
  // border color reinforces the status badge's text and background, both
  // set below, never carrying the state by itself.
  card.className = group.ready
    ? "purchasing-cart-vendor-group purchasing-cart-vendor-group--ready"
    : "purchasing-cart-vendor-group purchasing-cart-vendor-group--blocked";
  card.dataset["testid"] = "purchasing-cart-vendor-group";
  card.dataset["vendorKey"] = group.vendorKey;

  const header = document.createElement("div");
  header.className = "purchasing-cart-vendor-group-header";
  const name = document.createElement("span");
  name.className = "purchasing-cart-vendor-group-name";
  name.dataset["testid"] = "purchasing-cart-vendor-group-name";
  name.textContent = group.vendorName;
  const status = document.createElement("span");
  status.dataset["testid"] = "purchasing-cart-vendor-group-status";
  status.className = group.ready
    ? "purchasing-cart-vendor-group-status"
    : "purchasing-cart-vendor-group-status purchasing-cart-vendor-group-status--blocked";
  status.textContent = group.ready
    ? "Ready to place"
    : "Needs attention before this vendor can be placed";
  header.append(name, status);
  card.append(header);

  // PO8: the order-by row, between the vendor name and anything that needs
  // fixing. Never changes the card's border and never gates Place.
  // CORE9: a blocked vendor lists its missing delivery info in the one list below, with the one fix link, so the row
  // keeps what is known ("Delivers Monday and Tuesday") but drops its own second button.
  const listsDeliveryInfo = !group.ready && group.vendorId !== null;
  if (summary !== undefined) {
    const scheduleRow = buildPurchasingCartScheduleRow(summary, { fixButton: !listsDeliveryInfo });
    if (scheduleRow !== null) {
      card.append(scheduleRow);
    }
  }

  // UX66 part C: shown only for a real, blocked vendor -- the
  // "unassigned" group (missing_vendor, group.vendorId === null) has no
  // vendor settings row a fix link could ever point at, so it keeps
  // showing its reason only on the line itself, exactly as before.
  if (!group.ready && group.vendorId !== null) {
    card.append(buildPurchasingCartVendorGroupBlockers(group, summary));
  }

  const lines = document.createElement("div");
  lines.className = "purchasing-cart-vendor-group-lines";
  // UX66 part C, design review 2026-09-29: the vendor-group blocker
  // banner above (rendered whenever vendorId !== null) already states
  // every VENDOR-LEVEL blocker (missing_contact, vendor_inactive) once,
  // and that fact is identical for every line in the group (it comes
  // from the vendor row, not the offer row), so repeating it on every
  // single line added no information beyond what the banner already
  // said. A per-OFFER blocker (missing_package, missing_cost) is
  // different per line by definition, is what tells a manager WHICH
  // line among several needs the "Edit this offer" fix, and is exactly
  // what harness/e2e/po6-s2-cart-and-placement.spec.ts's own "missing
  // information is surfaced before placement" assertion depends on, so
  // it stays on the line. The one group with no banner (missing_vendor,
  // vendorId === null) has nowhere else its reason is shown, so its
  // lines keep it unfiltered.
  const suppressVendorLevelBlockers = group.vendorId !== null;
  for (const line of group.lines) {
    lines.append(buildPurchasingCartLineRow(line, suppressVendorLevelBlockers));
  }
  card.append(lines);

  // PO6-S2-F2 and PO8: the footer. Every group gets its estimated total
  // (the no-vendor group says "Not priced"); a real vendor also gets the
  // minimum meter, one note, and its Place button.
  const footer = document.createElement("div");
  footer.className = "purchasing-cart-vendor-group-footer";
  if (summary !== undefined) {
    footer.append(buildPurchasingCartTotalRow(summary));
    const minimumNote = buildPurchasingCartMinimumNote(summary);
    if (minimumNote !== null) {
      footer.append(minimumNote);
    }
  }
  // No vendor at all (missing_vendor): this group can never place, so no
  // button is offered rather than a permanently-disabled one with nothing
  // a manager can do about it from this screen.
  if (group.vendorId !== null) {
    // UX66 part C, design/principles.md rule 2 (one thumb, one obvious
    // next action): its own footer row, full width, visually separated
    // from the lines above rather than sharing btn-compact sizing with
    // every other button on the card, so a ready vendor's Place order is
    // unmistakable at a glance. PO8: the button's label, position and
    // enabled rule are unchanged; no minimum or cutoff ever disables it.
    const placeButton = document.createElement("button");
    placeButton.type = "button";
    placeButton.className = "btn-primary purchasing-cart-vendor-group-place";
    placeButton.dataset["testid"] = "purchasing-cart-vendor-group-place";
    placeButton.textContent = `Place order for ${group.vendorName}`;
    placeButton.disabled = !group.ready;
    placeButton.hidden = !canPlace;
    placeButton.addEventListener("click", () => void handlePlaceVendorGroup(group));
    footer.append(placeButton);
    // INVPOWALK1 (F3, design-language rules 3 and 11): say before the tap what happens next when this person cannot approve
    // and the order reaches the limit. The server decides (estimate at or over the limit makes a draft); this only tells.
    const ownApprove = requireSession().permissions[PURCHASING_ORDER_APPROVE_PERMISSION];
    if (
      canPlace &&
      group.ready &&
      ownApprove !== "allowed" &&
      purchasingApprovalThresholdCents !== null &&
      summary !== undefined &&
      summary.estimatedTotalCents >= purchasingApprovalThresholdCents
    ) {
      const note = document.createElement("p");
      note.className = "purchasing-cart-vendor-group-approval-note";
      note.dataset["testid"] = "purchasing-cart-vendor-group-approval-note";
      note.textContent = `This order is ${formatCents(purchasingApprovalThresholdCents)} or more, so a manager approves it before it is placed.`;
      footer.append(note);
    }
  }
  if (footer.childElementCount > 0) {
    card.append(footer);
  }

  return card;
}

// UX66 part C, design review 2026-09-29: the vendor-level blockers a
// group's own banner already states once for the whole vendor (see
// buildPurchasingCartLineRow's own `suppressVendorLevelBlockers`
// parameter above). missing_package and missing_cost are deliberately
// absent here: those come from the offer row, differ line to line, and
// stay on the line even when the banner is showing.
const VENDOR_LEVEL_CART_LINE_BLOCKERS: ReadonlySet<CartLineBlockerDto> = new Set([
  "missing_vendor",
  "vendor_inactive",
  "missing_contact",
]);

function canResolveCartVendorOption(active: Session): boolean {
  return ["admin.liquor_product.view", "inv.vendor.view", "inv.vendor_offer.view", "inv.vendor_offer.manage"]
    .every((permission) => hasPermission(active, permission));
}

async function resolveCartVendorOption(line: CartLineDto): Promise<void> {
  const siteId = state.purchasingCartLocationId;
  if (siteId === undefined || !canResolveCartVendorOption(requireSession())) return;
  await runCartFixFlow(
    "inventory",
    (active, returnContext) => admin.openVendorOfferEditor(active, line.liquorProductId, siteId, returnContext),
    "Could not open Vendor Options. Try again.",
    "Could not open Vendor Options. Check your connection and try again.",
  );
}

/** PURCHBLOCK1: the vendor row fix ("Fix vendor setup") for vendor-level reasons: inactive, contact, delivery info. */
function canFixCartVendorSetup(active: Session): boolean {
  return hasPermission(active, "inv.vendor.view") && hasPermission(active, "inv.vendor.manage");
}

async function fixCartVendorSetup(
  vendorId: string,
  vendorName: string,
  focus: "contact" | "active",
): Promise<void> {
  if (!canFixCartVendorSetup(requireSession())) return;
  await runCartFixFlow(
    "purchasing",
    (active, returnContext) => admin.openVendorContactEditor(active, vendorId, { ...returnContext, vendorName }, focus),
    "Could not open this vendor in Settings. Try again.",
    "Could not open this vendor in Settings. Check your connection and try again.",
  );
}

/** One subordinate flow out of the cart (design-language rule 5): opens the exact place to fix a blocked line, and
 * returns to the same cart. The cart lines live on the server, so returning reloads them with every quantity as it
 * was. The return is one-shot and bound to actor, tenant, location, session and cart visit (UIUX2). */
async function runCartFixFlow(
  section: "inventory" | "purchasing",
  open: (
    active: Session,
    returnContext: { isCurrent(): boolean; onClose(): void },
  ) => Promise<{ opened: boolean; message?: string }>,
  failedMessage: string,
  offlineMessage: string,
): Promise<void> {
  const active = requireSession();
  const siteId = state.purchasingCartLocationId;
  if (siteId === undefined) return;
  const visit = purchasingCartVisit;
  const generation = sessionGeneration;
  showAdminScreen();
  const navigation = purchasingVendorNavigation;
  const isCurrent = (): boolean => {
    const current = state.session;
    const route = admin.currentAdminRoute();
    return route.kind === "settings" && route.section === section &&
      current !== undefined && current.token === active.token &&
      current.userId === active.userId && current.tenantId === active.tenantId &&
      generation === sessionGeneration && visit === purchasingCartVisit &&
      navigation === purchasingVendorNavigation && !adminScreen.hidden &&
      hasPermission(current, PURCHASING_CART_MANAGE_PERMISSION) &&
      state.purchasingCartLocationId === siteId;
  };
  const resume = (message?: string): void => {
    if (!isCurrent()) return;
    hideAllScreens();
    document.getElementById("app-shell")?.scrollTo(0, 0);
    purchasingCartScreen.hidden = false;
    showTopbarChrome("purchasing-cart-screen");
    purchasingCartStatusEl.hidden = true;
    purchasingCartStatusEl.textContent = "";
    purchasingCartVendorGroupsEl.replaceChildren();
    purchasingCartSummaryEl.replaceChildren();
    purchasingCartSummaryEl.hidden = true;
    purchasingCartEmptyEl.hidden = true;
    purchasingCartProposalsListEl.replaceChildren();
    purchasingCartProposalsEmptyEl.hidden = true;
    purchasingCartAddAllBelowParButton.disabled = true;
    purchasingCartAddAllBelowParReasonEl.hidden = true;
    purchasingCartProductSearchResults = [];
    purchasingCartProductResultsEl.replaceChildren();
    purchasingCartVendorGroupsEl.textContent = "Loading cart…";
    purchasingCartProposalsListEl.textContent = "Loading…";
    if (message) showPurchasingCartError(message);
    void loadPurchasingCart();
    void loadPurchasingCartProposals();
    void loadPurchasingCartProductSearchResults();
  };
  try {
    const result = await open(active, { isCurrent, onClose: () => resume() });
    if (!result.opened && isCurrent()) resume(result.message ?? failedMessage);
  } catch {
    if (isCurrent()) resume(offlineMessage);
  }
}

function buildPurchasingCartLineRow(
  line: CartLineDto,
  suppressVendorLevelBlockers = false,
): HTMLElement {
  const row = document.createElement("div");
  row.className = "purchasing-cart-line-row";
  row.dataset["testid"] = "purchasing-cart-line-row";
  row.dataset["liquorProductId"] = line.liquorProductId;

  const product = document.createElement("div");
  product.className = "purchasing-cart-line-product";
  product.dataset["testid"] = "purchasing-cart-line-product";
  product.textContent = line.productName;

  // Quantity is the one thing a person types here: how many to order is
  // their decision, derived from nothing (CLAUDE.md's money rule). Every
  // other field on this row is a pass-through of what the route returned.
  // UX66: whole units only (a cart line orders whole bottles, cans, kegs
  // or units, never a fraction of one), so inputMode is "numeric" rather
  // than "decimal" and min is the smallest whole order, 1; a value typed
  // with a decimal point is rounded on commit by
  // handleCartLineQuantityCommit, the same rounding the Add flows use.
  const quantityInput = document.createElement("input");
  quantityInput.type = "number";
  quantityInput.inputMode = "numeric";
  quantityInput.step = "1";
  quantityInput.min = "1";
  quantityInput.className = "field-input purchasing-cart-line-quantity-input";
  quantityInput.dataset["testid"] = "purchasing-cart-line-quantity-input";
  quantityInput.value = formatCartLineQuantity(line);
  quantityInput.addEventListener("change", () => {
    void handleCartLineQuantityCommit(line, quantityInput);
  });

  // PO6-S2-F2: the unit is named beside the quantity ("6 Bottle", "2
  // Case") so the arithmetic is readable. PO9 is closed server-side (a
  // proposal carries the order-unit quantity); the bottle count this
  // quantity is shows on its own line below (TF4-F3).
  const quantityWrap = document.createElement("label");
  quantityWrap.className = "purchasing-cart-line-quantity";
  const quantityLabel = document.createElement("span");
  quantityLabel.className = "purchasing-cart-sr-only";
  quantityLabel.textContent = "Quantity";
  const unitLabel = document.createElement("span");
  unitLabel.className = "purchasing-cart-line-unit";
  unitLabel.dataset["testid"] = "purchasing-cart-line-unit";
  unitLabel.textContent = cartLineUnitLabel(line);
  quantityWrap.append(quantityLabel, quantityInput, unitLabel);

  const unitCost = document.createElement("div");
  unitCost.className =
    line.unitCostCents === null
      ? "purchasing-cart-line-unit-cost purchasing-cart-no-price"
      : "purchasing-cart-line-unit-cost";
  unitCost.dataset["testid"] = "purchasing-cart-line-unit-cost";
  unitCost.textContent = formatCartLineUnitPrice(line);

  const lineTotal = document.createElement("div");
  lineTotal.className =
    line.lineEstimateCents === null
      ? "purchasing-cart-line-total purchasing-cart-no-price"
      : "purchasing-cart-line-total";
  lineTotal.dataset["testid"] = "purchasing-cart-line-total";
  lineTotal.textContent = formatCartLineTotal(line);

  const removeButton = document.createElement("button");
  removeButton.type = "button";
  removeButton.className = "btn-secondary btn-compact";
  removeButton.dataset["testid"] = "purchasing-cart-line-remove";
  removeButton.textContent = "Remove";
  removeButton.addEventListener("click", () => void handleRemoveCartLine(line));

  row.append(product, quantityWrap, unitCost, lineTotal, removeButton);
  // PURCHBLOCK1: every reason fixed on the item's own Vendor Options (no vendor, order unit or pack size, cost) gets
  // the Resolve action; without permission the line says who can, rather than a button that would be refused.
  const needsVendorOption = line.blockers.some(
    (blocker) => blocker === "missing_vendor" || blocker === "missing_package" || blocker === "missing_cost",
  );
  if (needsVendorOption && !canResolveCartVendorOption(requireSession())) {
    const denied = document.createElement("p");
    denied.className = "purchasing-cart-line-resolve-denied";
    denied.dataset["testid"] = "purchasing-cart-line-resolve-denied";
    denied.textContent = "You cannot edit Vendor Options. Ask a manager who can.";
    row.append(denied);
  }
  if (needsVendorOption && canResolveCartVendorOption(requireSession())) {
    const resolve = document.createElement("button");
    resolve.type = "button";
    resolve.className = "btn-secondary btn-compact purchasing-cart-line-resolve";
    resolve.dataset["testid"] = "purchasing-cart-line-resolve";
    resolve.textContent = "Resolve Vendor Option";
    resolve.addEventListener("click", () => void resolveCartVendorOption(line));
    row.append(resolve);
  }

  // TF4-F3: the bottles this quantity is, so the manager sees what an ABC2GO
  // upload would carry. Recomputed by the server on every change.
  const bottlesText = formatOrderUnitBottles({
    orderUnit: line.orderUnit,
    bottlesPerOrderUnit: line.bottlesPerOrderUnit,
    bottleCount: line.bottleCount,
  });
  if (bottlesText !== null && line.orderUnit !== null) {
    const bottles = document.createElement("div");
    bottles.className = "purchasing-cart-line-bottles";
    bottles.dataset["testid"] = "purchasing-cart-line-bottles";
    bottles.textContent = bottlesText;
    row.append(bottles);
  }

  const visibleBlockers = suppressVendorLevelBlockers
    ? line.blockers.filter((blocker) => !VENDOR_LEVEL_CART_LINE_BLOCKERS.has(blocker))
    : line.blockers;
  const blockerText = describeCartLineBlockers({ blockers: visibleBlockers });
  if (blockerText !== null) {
    const blockers = document.createElement("div");
    blockers.className = "purchasing-cart-line-blockers";
    blockers.dataset["testid"] = "purchasing-cart-line-blockers";
    blockers.textContent = blockerText;
    row.append(blockers);
  }

  return row;
}

async function handleCartLineQuantityCommit(
  line: CartLineDto,
  input: HTMLInputElement,
): Promise<void> {
  const session = requireSession();
  const cartContext = capturePurchasingCartContext();
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  const raw = input.value.trim();
  if (raw.length === 0) {
    input.value = formatCartLineQuantity(line);
    return;
  }
  // UX66: whole units only. A value typed with a decimal point (or pasted
  // in) is rounded here, the same rounding the Add flows apply before
  // this line ever exists. Only a positive value is rounded:
  // roundQuantityToWholeUnit's own floor of 1 assumes a positive
  // shortfall (see its comment), so zero, negative, and non-numeric
  // input are left exactly as typed and refused by the server's own
  // validation below, exactly as before this change.
  const numeric = Number(raw);
  const quantity = Number.isFinite(numeric) && numeric > 0 ? roundQuantityToWholeUnit(raw) : raw;
  const result = await setCartLine(session.apiBaseUrl, session.tenantId, {
    liquorProductId: line.liquorProductId,
    quantity,
  });
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  if (result.status !== 200) {
    showToast(
      `Could not update the quantity. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    input.value = formatCartLineQuantity(line);
    return;
  }
  applyCartResponse(result.body.cartLines);
  renderPurchasingCartVendorGroups();
}

async function handleRemoveCartLine(line: CartLineDto): Promise<void> {
  const session = requireSession();
  const cartContext = capturePurchasingCartContext();
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  const result = await removeCartLine(session.apiBaseUrl, session.tenantId, line.liquorProductId);
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  if (result.status !== 200) {
    showToast(
      `Could not remove ${line.productName}. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  applyCartResponse(result.body.cartLines);
  renderPurchasingCartVendorGroups();
}

// --- Placement (criteria 6, 7, and 8). One purchase order per vendor,
// each placeCart call scoped to a single vendor's own ready lines so a
// vendor a manager has not resolved yet stays in the cart untouched. ---

/** placeCart's own refusal (CartNotReadyError, 400) is checked again
 * here even though the Place button is disabled while `group.ready` is
 * false: another tab or another employee can change vendor data between
 * this render and this click, and the server's own check is what
 * actually protects placement, never this screen's own copy of it. */
async function handlePlaceVendorGroup(group: CartVendorGroup): Promise<void> {
  if (group.vendorId === null) {
    return;
  }
  const session = requireSession();
  const cartContext = capturePurchasingCartContext();
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  const result = await placeCart(session.apiBaseUrl, session.tenantId, {
    idempotencyKey: crypto.randomUUID(),
    vendorId: group.vendorId,
  });
  if (!purchasingCartContextIsCurrent(cartContext)) return;
  if (result.status !== 201) {
    showPurchasingCartError(
      result.body.message ?? `Could not place ${group.vendorName}'s order. Check your connection.`,
    );
    return;
  }
  for (const order of result.body.orders) {
    state.purchasingCartPlacedOrders = [order, ...state.purchasingCartPlacedOrders];
  }
  renderPurchasingCartPlacedOrders();
  // UX66 part C: a placement confirmation the owner asked for ("Once
  // they are in your cart, then what is supposed to happen?"), reading
  // as an event rather than only the toast and a row in "Just placed"
  // below. The first order in the response is the one this vendor's own
  // cart lines actually placed (placeCart groups by vendor internally,
  // and this call itself was scoped to one vendorId), so there is
  // exactly one to show.
  const [placedOrder] = result.body.orders;
  if (placedOrder !== undefined) {
    showPurchasingCartConfirmation(placedOrder);
  }
  showToast(placedOrder?.status === "draft" ? `Order waiting for approval for ${group.vendorName}. Not sent.` :
    `Order saved for ${group.vendorName}. Not sent yet.`, "success");
  void refreshMenuCounts().catch(() => { topbarApprovalBadge.hidden = true; });
  await loadPurchasingCart();
  // INVPOWALK1 (F5): the Place button was far down the page; bring the confirmation (View purchase order, Keep ordering)
  // into view so the result is seen, not only a short toast.
  if (!purchasingCartConfirmationBannerEl.hidden) {
    purchasingCartConfirmationBannerEl.scrollIntoView({ block: "start" });
  }
}

/** UX66 part C: populates and reveals the confirmation banner for one
 * just-placed order, with a "View purchase order" action that goes
 * straight to that order's own detail screen -- the same destination the
 * "Just placed" row's own "View order" button already reaches
 * (buildPurchasingCartPlacedOrderRow below). Re-running this on a second
 * placement replaces the banner's own content and click target rather
 * than stacking a second listener, since this function is the only
 * caller and always overwrites `onclick` rather than adding one more
 * `addEventListener`. */
function showPurchasingCartConfirmation(order: PlacedOrderDto): void {
  // MGRDAYMAJ1 (manager-day-16): say plainly that the order is saved and not sent, and how this vendor takes orders.
  const words = placedOrderWords(order, null, order.approverNames);
  purchasingCartConfirmationTitleEl.textContent = words.title;
  purchasingCartConfirmationDetailEl.textContent = words.detail;
  // The vendor's own "How we order" answer lands a moment later; it only refines the sentence, so a failed read leaves the
  // plain one above standing.
  if (order.status !== "draft" && state.session !== undefined) {
    const session = state.session;
    void getVendor(session.apiBaseUrl, session.tenantId, order.vendorId)
      .then((vendor) => {
        if (vendor.status !== 200 || purchasingCartConfirmationBannerEl.hidden) return;
        if (purchasingCartConfirmationTitleEl.textContent !== words.title) return;
        purchasingCartConfirmationDetailEl.textContent = placedOrderWords(
          order,
          vendor.body.vendor.order_method ?? null,
        ).detail;
      })
      .catch(() => undefined);
  }
  purchasingCartConfirmationViewButton.onclick = () => {
    showPurchasingOrdersScreen();
    void showPurchasingOrderDetail(order.id);
  };
  // INVPOWALK1 (F4): an order still waiting for approval is not a success, so it does not wear the success color.
  // MGRDAYMAJ1 (manager-day-16): nor is an order that is saved and not yet sent, since the person still has to send it.
  purchasingCartConfirmationBannerEl.classList.add("purchasing-cart-confirmation-banner--waiting");
  purchasingCartConfirmationBannerEl.hidden = false;
}

function renderPurchasingCartPlacedOrders(): void {
  purchasingCartPlacedListEl.innerHTML = "";
  if (state.purchasingCartPlacedOrders.length === 0) {
    purchasingCartPlacedEmptyEl.hidden = false;
    return;
  }
  purchasingCartPlacedEmptyEl.hidden = true;
  for (const order of state.purchasingCartPlacedOrders) {
    purchasingCartPlacedListEl.append(buildPurchasingCartPlacedOrderRow(order));
  }
}

function buildPurchasingCartPlacedOrderRow(order: PlacedOrderDto): HTMLElement {
  const row = document.createElement("div");
  row.className = "purchasing-cart-placed-row";
  row.dataset["testid"] = "purchasing-cart-placed-row";

  const vendor = document.createElement("div");
  vendor.className = "purchasing-cart-placed-vendor";
  vendor.dataset["testid"] = "purchasing-cart-placed-vendor";
  vendor.textContent = order.vendorName;

  const status = document.createElement("div");
  status.className = "purchasing-cart-placed-status";
  status.dataset["testid"] = "purchasing-cart-placed-status";
  status.textContent = purchaseOrderStatusLabel(order.status);

  // Straight from placeCart's own response (PlacedOrderDto), the only
  // total this screen ever shows for a just-placed order; see this
  // file's module header and ui/src/purchasingDisplay.ts's own.
  const total = document.createElement("div");
  total.className = "purchasing-cart-placed-total";
  total.dataset["testid"] = "purchasing-cart-placed-total";
  total.textContent = formatOrderEstimatedTotal(order);

  const viewButton = document.createElement("button");
  viewButton.type = "button";
  viewButton.className = "btn-secondary btn-compact";
  viewButton.dataset["testid"] = "purchasing-cart-placed-view";
  viewButton.textContent = "View order";
  viewButton.addEventListener("click", () => {
    showPurchasingOrdersScreen();
    void showPurchasingOrderDetail(order.id);
  });

  row.append(vendor, status, total, viewButton);
  return row;
}

// ---------------------------------------------------------------------
// PO6 Slice 4 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S4-receiving.md). The receiving screen: one
// order's own lines, what each still owes, and recording a receipt
// against a line. ADMIN SURFACE since PO6-R1 (DEC-056): see
// ui/index.html's own comment on #purchasing-receiving-screen.
//
// NOTHING HERE IS RECOMPUTED. Ordered/received/outstanding are shown
// exactly as GET .../receiving returned them (bare NUMERIC(12,4) strings,
// trimmed for display by ui/src/purchasingDisplay.ts's
// formatReceivingQuantity, never re-derived); an over-receipt's refusal
// is the SERVER's own message (src/domain/receiving.ts's
// assertReceiptFits), shown on the line's own error area verbatim, never
// a client-side guess at the same rule.
// ---------------------------------------------------------------------

/** Reached from a Purchase Orders list row's own "Receive" button, which
 * already has this order's vendor name and status on hand (it built the
 * row), so this screen opens instantly with a real header instead of a
 * loading placeholder before its own GET .../receiving call returns. */
async function showPurchasingReceivingScreen(order: PurchaseOrderSummaryDto): Promise<void> {
  // UX108: set before the screen is shown, because showing it publishes its address.
  state.receivingOrderId = order.id;
  receivingScreenOrder = order;
  shortCloseArmed = false;
  shortOnTheWay = false;
  shortStatus.hidden = true;
  purchasingReceivingScanInvoiceButton.hidden = !hasPermission(
    requireSession(),
    INVENTORY_RECEIPT_RECORD_PERMISSION,
  );
  // The scan starts from the order's lines as loaded below, so it opens only once they are in.
  purchasingReceivingScanInvoiceButton.disabled = true;
  dropDraft(RECEIVE_ORDER_DRAFT);
  hideAllScreens();
  purchasingReceivingScreen.hidden = false;
  showTopbarChrome("purchasing-receiving-screen");
  purchasingReceivingVendorEl.textContent = order.vendorName;
  purchasingReceivingStatusLabelEl.textContent = purchaseOrderStatusLabel(order.status);
  purchasingReceivingStatusEl.hidden = true;
  purchasingReceivingStatusEl.textContent = "";
  purchasingReceivingExceptionEl.hidden = true;
  purchasingReceivingLinesEl.innerHTML = '<p class="tabs-loading">Loading…</p>';
  state.receivingLines = [];
  // PO7-S1: independent of the lines below; it paints when it is ready.
  void mountInvoiceCard(
    purchasingReceivingInvoicesEl,
    order.id,
    "purchasing-receiving",
    requireSession,
    offerClearForConfirmedInvoice,
  );
  await loadPurchasingReceivingView();
}

/** Every write on this screen (a successful receipt) re-runs this exact
 * function rather than patching state.receivingLines by hand, so the
 * remainder shown after a write is always what the server just computed,
 * never a client-side guess at its own arithmetic. */
async function loadPurchasingReceivingView(): Promise<void> {
  const orderId = state.receivingOrderId;
  if (orderId === undefined) {
    return;
  }
  const session = requireSession();
  const result = await getOrderReceivingView(session.apiBaseUrl, session.tenantId, orderId);
  if (result.status !== 200) {
    purchasingReceivingLinesEl.innerHTML = "";
    purchasingReceivingStatusEl.hidden = false;
    purchasingReceivingStatusEl.textContent =
      result.body.message ?? "Could not load this order's receiving state. Check your connection.";
    return;
  }
  state.receivingLines = result.body.lines;
  state.receivingSubstitutions = result.body.substitutions;
  await ensureReceivingItemKinds(result.body.lines);
  await ensureReceivingOrderCosts(orderId);
  renderPurchasingReceivingView(result.body);
  purchasingReceivingScanInvoiceButton.disabled = false;
}

/** CORE9: the order's own unit costs, for the prefill. A failed or refused read leaves the map empty, so the cost
 * fields stay blank rather than showing a guess. */
async function ensureReceivingOrderCosts(orderId: string): Promise<void> {
  const session = requireSession();
  const result = await getPurchaseOrder(session.apiBaseUrl, session.tenantId, orderId);
  state.receivingUnitCostCentsByLineId = new Map();
  if (result.status !== 200) {
    return;
  }
  for (const orderLine of result.body.order.lines) {
    state.receivingUnitCostCentsByLineId.set(orderLine.id, orderLine.unitCostCentsAtPlacement);
  }
}

/** GET .../receiving (and getPurchaseOrder's own line projection) return
 * no unit at all for a line -- neither the order's own placement unit
 * nor anything else -- so this screen reaches each line's product
 * (GET .../liquor-products/:id, already authorized under
 * admin.liquor_product.view, which every role template that also holds
 * inv.receipt.record already holds today) to learn its unit_price_cents
 * and offer that product's own on-hand unit vocabulary instead. See
 * docs/build-briefs/PO6-S4-receiving.md's own "Findings" section for the
 * full account of why. Cached per liquorProductId so reopening the same
 * order, or seeing the same product on two lines, never re-fetches it.
 *
 * ITEM2 Slice 5: caches `unit_price_cents` rather than `item_kind` as of
 * this item. This screen has no cheap access to the tenant's own item_kind
 * vocabulary (no fetch of it happens anywhere on this screen, and this
 * role's own permission set may not include admin.tenant_settings.view),
 * so deriving whole-unit-versus-per-ounce from the ALREADY-FETCHED
 * product's own unit_price_cents (non-null exactly on a whole-unit
 * product) avoids a second, permission-gated fetch this screen would
 * otherwise need for a tenant-added kind. */
async function ensureReceivingItemKinds(lines: readonly LineReceivingStateDto[]): Promise<void> {
  const session = requireSession();
  const missing = [...new Set(lines.map((line) => line.liquorProductId))].filter(
    (id) => !state.receivingUnitPriceCentsByLiquorProductId.has(id),
  );
  if (missing.length === 0) {
    return;
  }
  const results = await Promise.all(
    missing.map((id) => getLiquorProduct(session.apiBaseUrl, session.tenantId, id)),
  );
  missing.forEach((id, index) => {
    const result = results[index];
    if (result !== undefined && result.status === 200) {
      const product = result.body.liquorProduct;
      state.receivingUnitPriceCentsByLiquorProductId.set(id, {
        unitPriceCents: product.unit_price_cents,
        containerType: product.container_type,
        sizeValue: product.size_value,
        sizeUnit: product.size_unit,
        defaultKegSize: product.default_keg_size,
      });
    }
  });
}

/** The order's status as the receiving screen last read it; a line of a received order has nothing more to receive. */
let receivingViewStatus = "";

function renderPurchasingReceivingView(view: OrderReceivingViewDto): void {
  purchasingReceivingExceptionEl.hidden = view.latestExceptionReason === null;
  if (view.latestExceptionReason !== null) {
    // MGRDAYMAJ1 (manager-day-19): the words a person reads, not the stored names.
    const reason = purchaseExceptionReasonLabel(view.latestExceptionReason);
    purchasingReceivingExceptionEl.textContent =
      view.latestResolution === null
        ? `Exception: ${reason}. Not yet resolved.`
        : `Exception: ${reason}. Resolution: ${purchaseExceptionResolutionLabel(view.latestResolution)}.`;
  }
  receivingViewStatus = view.status;
  // UX105: the header state is read from the server on every load, never kept from the list row that opened this
  // screen, so it moves to Partially received and Received as the receipts land.
  purchasingReceivingStatusLabelEl.textContent = purchaseOrderStatusLabel(view.status);
  // B6: scanning receives against this order, so it is offered under the same rule as the per-line Record control.
  purchasingReceivingScanButton.hidden =
    !purchaseOrderIsReceivable(view.status) ||
    !hasPermission(requireSession(), INVENTORY_RECEIPT_RECORD_PERMISSION);

  purchasingReceivingLinesEl.innerHTML = "";
  for (const line of state.receivingLines) {
    purchasingReceivingLinesEl.append(buildPurchasingReceivingLineRow(line));
  }
  renderReceiveAllPanel(view.status);
  renderShortDeliveryPanel(view.status);
  // UX108: the rows were rebuilt (after a receipt, say), so the draft is re-read from what is on screen.
  saveReceiveOrderDraft();
}

function buildPurchasingReceivingLineRow(line: LineReceivingStateDto): HTMLElement {
  const row = document.createElement("div");
  row.className = "purchasing-receiving-line-row";
  row.dataset["testid"] = "purchasing-receiving-line-row";
  row.dataset["purchaseOrderLineId"] = line.purchaseOrderLineId;

  const header = document.createElement("div");
  header.className = "purchasing-receiving-line-header";
  const product = document.createElement("span");
  product.className = "purchasing-receiving-line-product";
  product.dataset["testid"] = "purchasing-receiving-line-product";
  product.textContent = line.productName;
  header.append(product);
  if (receivingLineIsClosed(line)) {
    const closed = document.createElement("span");
    closed.className = "purchasing-receiving-line-closed";
    closed.dataset["testid"] = "purchasing-receiving-line-closed";
    closed.textContent = "Fully received";
    header.append(closed);
  }
  row.append(header);

  const quantities = document.createElement("div");
  quantities.className = "purchasing-receiving-line-quantities";
  quantities.dataset["testid"] = "purchasing-receiving-line-quantities";
  // PO7-S4-F3: a line bought by the case reads in cases with the bottles beside.
  quantities.textContent =
    `Ordered: ${describeOrderUnitQuantity(line, formatReceivingQuantity(line.orderedQuantity))}  ` +
    `Received: ${describeOrderUnitQuantity(line, formatReceivingQuantity(line.receivedQuantity))}  ` +
    `Remaining: ${describeOrderUnitQuantity(line, formatReceivingQuantity(line.outstandingQuantity))}`;
  row.append(quantities);

  const receiptCount = document.createElement("div");
  receiptCount.className = "purchasing-receiving-line-receipt-count";
  receiptCount.dataset["testid"] = "purchasing-receiving-line-receipt-count";
  receiptCount.textContent =
    line.receiptCount === 0
      ? "No receipts recorded yet."
      : line.receiptCount === 1
        ? "1 receipt recorded."
        : `${line.receiptCount} receipts recorded.`;
  row.append(receiptCount);
  appendSubstitutionNotes(
    row,
    substitutionsForLine(state.receivingSubstitutions, line.purchaseOrderLineId),
    "purchasing-receiving-line-substitution",
  );

  // Fully received: nothing left to enter against this line. No input is
  // offered at all, rather than a disabled one with nothing a person can
  // do about it (the same choice PO6-S2's own missing-vendor cart group
  // makes for its own Place button).
  if (receivingLineIsClosed(line)) {
    return row;
  }
  // MGRDAYMAJ1 (manager-day-19): an order closed short is Received, and what was missing is no longer expected, so the line says
  // so instead of offering a receipt for it.
  if (receivingViewStatus === "received") {
    const closedShort = document.createElement("div");
    closedShort.className = "purchasing-receiving-line-receipt-count";
    closedShort.dataset["testid"] = "purchasing-receiving-line-closed-short";
    closedShort.textContent = `Closed short. ${describeOrderUnitQuantity(line, formatReceivingQuantity(line.outstandingQuantity))} not expected.`;
    row.append(closedShort);
    return row;
  }

  row.append(buildPurchasingReceivingLineEntry(line));
  return row;
}

/** PO13: one line of text per substitution recorded against a line, on the receiving and order detail screens. */
function appendSubstitutionNotes(
  into: HTMLElement,
  substitutions: readonly OrderSubstitutionDto[],
  testId: string,
): void {
  for (const sub of substitutions) {
    const note = document.createElement("div");
    note.className = "purchasing-substitution-note";
    note.dataset["testid"] = testId;
    note.textContent = describeSubstitution(sub);
    into.append(note);
  }
}

interface ReceivingSubstituteControl {
  element: HTMLElement;
  isOpen(): boolean;
}

/** PO13: the control on a receiving line for a delivery of a different product. A button opens the same product
 * search Receive stock uses (type, tap a result); nothing is preselected, and the picked product is named with a
 * way back to the ordered one. */
function buildReceivingSubstituteControl(
  line: LineReceivingStateDto,
  hooks: { onChange: (picked: LiquorProductDto | undefined) => void },
): ReceivingSubstituteControl {
  const wrap = document.createElement("div");
  wrap.className = "purchasing-receiving-substitute";
  wrap.dataset["testid"] = "purchasing-receiving-substitute";
  let open = false;
  let debounce: ReturnType<typeof setTimeout> | undefined;
  let searchSequence = 0;

  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "btn-secondary btn-compact";
  toggle.dataset["testid"] = "purchasing-receiving-substitute-toggle";
  toggle.textContent = SUBSTITUTE_CONTROL_LABEL;
  toggle.setAttribute("aria-expanded", "false");

  const panel = document.createElement("div");
  panel.className = "purchasing-receiving-substitute-panel";
  panel.dataset["testid"] = "purchasing-receiving-substitute-panel";
  panel.hidden = true;

  const search = document.createElement("input");
  search.type = "search";
  search.className = "field-input purchasing-receiving-substitute-search";
  search.dataset["testid"] = "purchasing-receiving-substitute-search";
  search.placeholder = "Search products";
  search.setAttribute("aria-label", `Product that arrived instead of ${line.productName}`);

  const results = document.createElement("div");
  results.className = "purchasing-receiving-substitute-results";
  results.dataset["testid"] = "purchasing-receiving-substitute-results";

  const chosen = document.createElement("div");
  chosen.className = "purchasing-receiving-substitute-chosen";
  chosen.dataset["testid"] = "purchasing-receiving-substitute-chosen";
  chosen.hidden = true;
  const chosenName = document.createElement("span");
  chosenName.className = "purchasing-receiving-substitute-chosen-name";
  chosenName.dataset["testid"] = "purchasing-receiving-substitute-chosen-name";
  const changeButton = document.createElement("button");
  changeButton.type = "button";
  changeButton.className = "btn-secondary btn-compact";
  changeButton.dataset["testid"] = "purchasing-receiving-substitute-change";
  changeButton.textContent = "Change";
  chosen.append(chosenName, changeButton);

  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "btn-secondary btn-compact";
  cancel.dataset["testid"] = "purchasing-receiving-substitute-cancel";
  cancel.textContent = "Cancel";

  panel.append(search, results, chosen, cancel);
  wrap.append(toggle, panel);

  const showPicking = (): void => {
    chosen.hidden = true;
    search.hidden = false;
    results.hidden = false;
    hooks.onChange(undefined);
  };
  const close = (): void => {
    open = false;
    panel.hidden = true;
    toggle.hidden = false;
    toggle.setAttribute("aria-expanded", "false");
    search.value = "";
    results.replaceChildren();
    showPicking();
  };
  toggle.addEventListener("click", () => {
    open = true;
    panel.hidden = false;
    toggle.hidden = true;
    toggle.setAttribute("aria-expanded", "true");
    search.focus();
  });
  cancel.addEventListener("click", close);
  changeButton.addEventListener("click", showPicking);

  const runSearch = async (): Promise<void> => {
    const query = search.value.trim();
    const mine = ++searchSequence;
    if (query.length === 0) {
      results.replaceChildren();
      return;
    }
    const session = requireSession();
    const result = await listLiquorProducts(session.apiBaseUrl, session.tenantId, { locationId: terminalConfigState?.locationId,
      q: query,
      activeOnly: true,
    });
    if (mine !== searchSequence) return;
    results.replaceChildren();
    if (result.status !== 200) return;
    for (const product of result.body.liquorProducts) {
      if (product.id === line.liquorProductId) continue;
      const row = document.createElement("div");
      row.className = "purchasing-receiving-substitute-result-row";
      row.dataset["testid"] = "purchasing-receiving-substitute-result-row";
      const name = document.createElement("span");
      name.className = "purchasing-receiving-substitute-result-name";
      name.textContent = product.name;
      const select = document.createElement("button");
      select.type = "button";
      select.className = "btn-secondary btn-compact";
      select.dataset["testid"] = "purchasing-receiving-substitute-result-select";
      select.textContent = "Select";
      select.addEventListener("click", () => {
        chosenName.textContent = product.name;
        chosen.hidden = false;
        search.hidden = true;
        results.hidden = true;
        results.replaceChildren();
        hooks.onChange(product);
      });
      row.append(name, select);
      results.append(row);
    }
  };
  search.addEventListener("input", () => {
    if (debounce !== undefined) clearTimeout(debounce);
    debounce = setTimeout(() => void runSearch(), 250);
  });

  return { element: wrap, isOpen: () => open };
}

const RECEIVING_QUANTITY_PATTERN = /^\d+(\.\d{1,4})?$/;

function buildPurchasingReceivingLineEntry(line: LineReceivingStateDto): HTMLElement {
  const entry = document.createElement("div");
  entry.className = "purchasing-receiving-line-entry";

  // The received QUANTITY is typed: a physical count of what arrived,
  // derived from nothing (ADR-003 Decision 3). type="number" is
  // self-describing under check:touch-only's own inputmode rule; the
  // explicit inputMode="decimal" is what actually switches a tablet's own
  // keyboard to a numeric pad with a decimal point.
  const quantityInput = document.createElement("input");
  quantityInput.type = "number";
  quantityInput.inputMode = "decimal";
  quantityInput.step = "1";
  quantityInput.min = "0.0001";
  quantityInput.className = "field-input purchasing-receiving-line-quantity-input";
  quantityInput.dataset["testid"] = "purchasing-receiving-line-quantity-input";
  quantityInput.placeholder = "Qty";
  quantityInput.setAttribute("aria-label", `Quantity received for ${line.productName}`);
  // INVPOWALK1 (F8): a caption that stays on screen. The placeholder vanished once the box was prefilled, leaving "3,
  // Ounces, Bottles, 55.00" with no word saying which was which.
  const quantityCaption = document.createElement("label");
  quantityCaption.className = "purchasing-receiving-line-cost-caption";
  quantityCaption.dataset["testid"] = "purchasing-receiving-line-quantity-caption";
  const quantityCaptionText = document.createElement("span");
  quantityCaptionText.textContent = "Quantity";
  quantityCaption.append(quantityCaptionText, quantityInput);
  entry.append(quantityCaption);

  const productInfo = state.receivingUnitPriceCentsByLiquorProductId.get(line.liquorProductId);
  // PO13: the product that actually arrived, when the manager says it was not the one ordered. The unit chips and the
  // cost wording follow it; the ordered product's own are used otherwise.
  let substitute: LiquorProductDto | undefined;
  const currentContainerInfo = (): ReceivingProductContainerInfo =>
    substitute !== undefined
      ? {
          containerType: substitute.container_type,
          sizeValue: substitute.size_value,
          sizeUnit: substitute.size_unit,
          defaultKegSize: substitute.default_keg_size,
        }
      : {
          containerType: productInfo?.containerType ?? null,
          sizeValue: productInfo?.sizeValue ?? null,
          sizeUnit: productInfo?.sizeUnit ?? null,
          defaultKegSize: productInfo?.defaultKegSize ?? null,
        };
  // PO7-S4-F3: a line bought by the case is received in cases (the one chip it
  // offers); the server posts the bottles they hold. Every other line keeps the
  // chips it always had.
  const packed = receivingLineIsPacked(line);
  const currentUnitOptions = (): readonly ReceivingUnitOption[] =>
    packed
      ? [orderUnitReceivingOption(line)]
      : receivingUnitOptionsForItemKind(
          substitute !== undefined
            ? substitute.unit_price_cents
            : (productInfo?.unitPriceCents ?? null),
          currentContainerInfo(),
        );
  const unitPicker = document.createElement("div");
  unitPicker.className = "admin-chip-select";
  unitPicker.dataset["testid"] = "purchasing-receiving-line-unit-picker";
  const unitGroupName = `purchasing-receiving-unit-${line.purchaseOrderLineId}`;
  const fillUnitChips = (unitOptions: readonly ReceivingUnitOption[]): void => {
    unitPicker.replaceChildren();
    for (const option of unitOptions) {
      const optionLabel = document.createElement("label");
      optionLabel.className = "admin-chip-option";
      const optionInput = document.createElement("input");
      optionInput.type = "radio";
      optionInput.name = unitGroupName;
      optionInput.className = "admin-chip-option-input";
      optionInput.value = option.value;
      optionInput.dataset["value"] = option.value;
      optionInput.dataset["testid"] = "purchasing-receiving-line-unit-option";
      // No default (CLAUDE.md: a picker deciding a real business fact is
      // never pre-selected), the same discipline
      // renderInventoryMovementUnitPicker's own comment states for the
      // identical shelf-side posture; this picker is rebuilt whole with
      // its line, or when the delivered product changes, so there is no
      // stale prior selection to preserve either.
      const optionText = document.createElement("span");
      optionText.className = "admin-chip-option-label";
      optionText.textContent = option.label;
      optionLabel.append(optionInput, optionText);
      unitPicker.append(optionLabel);
      // A line with exactly one unit chip has nothing to decide, so that chip starts chosen.
      if (unitOptions.length === 1) {
        optionInput.checked = true;
      }
    }
  };
  const unitOptions = currentUnitOptions();
  fillUnitChips(unitOptions);
  const unitCaption = document.createElement("div");
  unitCaption.className = "purchasing-receiving-line-cost-caption";
  unitCaption.dataset["testid"] = "purchasing-receiving-line-unit-caption";
  const unitCaptionText = document.createElement("span");
  unitCaptionText.textContent = "Unit";
  unitCaption.append(unitCaptionText, unitPicker);
  entry.append(unitCaption);

  // Received COST: configuration-shaped (ADR-003 Decision 3), entered
  // once per receipt line against an invoice, optional. Left empty, this
  // receipt behaves exactly as it always has (on-hand rises, no cost
  // recalculation); the domain's own cost-snapshot chain validates a
  // supplied value, never this screen. INV8-F1 Section 5: the field's own
  // label/aria-label follow whichever chip is selected, since the field
  // now means a different thing (cents per ounce, per container, or per
  // unit) depending on the chip.
  const costInput = document.createElement("input");
  costInput.type = "number";
  costInput.inputMode = "decimal";
  costInput.min = "0";
  costInput.step = "0.01";
  costInput.className = "field-input purchasing-receiving-line-cost-input";
  costInput.dataset["testid"] = "purchasing-receiving-line-cost-input";
  // INVPOWALK1 (F8): every line, not only a case line, keeps a caption that follows the chosen unit ("Cost per bottle").
  const costCaption = document.createElement("label");
  costCaption.className = "purchasing-receiving-line-cost-caption";
  costCaption.dataset["testid"] = "purchasing-receiving-line-cost-caption";
  const captionText = document.createElement("span");
  captionText.textContent = "Cost per bottle";
  costCaption.append(captionText, costInput);
  const applyCostFieldLabels = (): void => {
    const labels = receivingCostFieldLabels(
      selectedReceivingUnit(unitPicker),
      currentContainerInfo(),
      substitute !== undefined ? substitute.name : line.productName,
    );
    costInput.placeholder = labels.placeholder;
    costInput.setAttribute("aria-label", labels.ariaLabel);
    // A case line is always costed per bottle (its caption is fixed); every other line follows the unit chip.
    if (!packed) captionText.textContent = labels.placeholder.replace(/ \$$/, "");
  };
  applyCostFieldLabels();
  unitPicker.addEventListener("change", applyCostFieldLabels);
  entry.append(costCaption);

  const errorEl = document.createElement("div");
  errorEl.className = "purchasing-receiving-line-error";
  errorEl.dataset["testid"] = "purchasing-receiving-line-error";
  errorEl.id = `purchasing-receiving-error-${line.purchaseOrderLineId}`;
  errorEl.setAttribute("role", "alert");
  errorEl.hidden = true;
  // INVPOLISH1: a refusal belongs to the figure that caused it; changing the figure, the unit or the cost ends it.
  const clearRefusal = (): void => {
    errorEl.hidden = true;
    quantityInput.removeAttribute("aria-invalid");
  };
  quantityInput.addEventListener("input", clearRefusal);
  costInput.addEventListener("input", clearRefusal);
  unitPicker.addEventListener("change", clearRefusal);

  // PO13: "They sent something else". Opens the product search; the picked product is what the receipt is posted for.
  const substituteControl = buildReceivingSubstituteControl(line, {
    onChange: (picked) => {
      substitute = picked;
      errorEl.hidden = true;
      // The prefilled cost is the ordered product's; it is not carried to a different product.
      costInput.value = "";
      if (!packed) {
        fillUnitChips(currentUnitOptions());
      }
      applyCostFieldLabels();
      saveReceiveOrderDraft();
    },
  });
  entry.append(substituteControl.element);

  const submitButton = document.createElement("button");
  submitButton.type = "button";
  submitButton.className = "btn-primary btn-compact";
  submitButton.dataset["testid"] = "purchasing-receiving-line-submit";
  submitButton.textContent = "Record receipt";
  submitButton.addEventListener("click", () => {
    void handleRecordReceiptClick(line, {
      quantityInput,
      unitPickerEl: unitPicker,
      costInput,
      errorEl,
      submitButton,
      substitute: () => substitute,
      substituteOpen: () => substituteControl.isOpen(),
    });
  });
  entry.append(submitButton);

  // PO7-S4-F3: both readings on a row of their own, full width, so the cost field and the
  // Record button do not move as it fills in: "Recording 1 case (12 bottles) at $20.00 a
  // bottle = $240.00". Its height is held, empty or not.
  if (packed) {
    const bothReadings = document.createElement("div");
    bothReadings.className = "purchasing-receiving-line-both-readings";
    bothReadings.dataset["testid"] = "purchasing-receiving-line-both-readings";
    const refreshReadings = (): void => {
      const text = quantityInput.value.trim();
      const shown = describeOrderUnitQuantity(line, text);
      if (shown === text) {
        bothReadings.textContent = "";
        return;
      }
      let sentence = `Recording ${shown}`;
      const rawCost = costInput.value.trim();
      if (rawCost.length > 0 && Number(text) > 0) {
        try {
          const perBottle = parseDollarsToCentsExact(rawCost, "cost per bottle");
          const bottles = Number(text) * Number(line.bottlesPerOrderUnit);
          const total = Math.round(bottles * perBottle);
          sentence += ` at ${formatDraftMoney(perBottle)} a bottle = ${formatDraftMoney(total)}`;
        } catch {
          // A cost that does not parse yet is left out of the sentence; Record receipt says why.
        }
      }
      bothReadings.textContent = sentence;
    };
    quantityInput.addEventListener("input", refreshReadings);
    costInput.addEventListener("input", refreshReadings);
    entry.append(bothReadings);
  }
  entry.append(errorEl);

  // CORE9: the line starts with what is left to receive and what the order said it costs. Both stay editable and
  // Record receipt stays an explicit tap; where the order does not name the unit, no chip is chosen for the person.
  const prefill = receivingLinePrefill(
    line,
    state.receivingUnitCostCentsByLineId.get(line.purchaseOrderLineId),
    unitOptions,
  );
  quantityInput.value = prefill.quantity;
  if (prefill.unitValue !== undefined) {
    const chip = [...unitPicker.querySelectorAll<HTMLInputElement>('input[type="radio"]')].find(
      (input) => input.dataset["value"] === prefill.unitValue,
    );
    if (chip !== undefined) {
      chip.checked = true;
      applyCostFieldLabels();
      costInput.value = prefill.costDollars ?? "";
    }
  }
  if (packed) {
    quantityInput.dispatchEvent(new Event("input"));
  }
  // What the row started with, so a refresh keeps only what a person changed (UX108): an untouched prefill is not typed
  // work and must not be stored as a draft.
  entry.dataset["prefillQuantity"] = quantityInput.value;
  entry.dataset["prefillCost"] = costInput.value;
  entry.dataset["prefillUnit"] = selectedReceivingUnit(unitPicker) ?? "";

  return entry;
}

function selectedReceivingUnit(unitPickerEl: HTMLElement): string | undefined {
  const checked = unitPickerEl.querySelector<HTMLInputElement>('input[type="radio"]:checked');
  return checked?.dataset["value"];
}

/** The checked chip's own visible label text ("Bottles", "Cans",
 * "Ounces"), for a toast message written in the operator's own terms
 * rather than the wire value ("container") a person never typed or saw. */
function selectedReceivingUnitLabel(unitPickerEl: HTMLElement): string | undefined {
  const checked = unitPickerEl.querySelector<HTMLInputElement>('input[type="radio"]:checked');
  return (
    checked?.parentElement?.querySelector(".admin-chip-option-label")?.textContent ?? undefined
  );
}

interface ReceivingLineEntryControls {
  quantityInput: HTMLInputElement;
  unitPickerEl: HTMLElement;
  costInput: HTMLInputElement;
  errorEl: HTMLElement;
  submitButton: HTMLButtonElement;
  /** PO13: the product that arrived in place of the one ordered, once picked. */
  substitute: () => LiquorProductDto | undefined;
  /** PO13: the "They sent something else" panel is open, picked or not. */
  substituteOpen: () => boolean;
}

/** The one place a receiving line's receipt request is shaped, for Record receipt on a line card and for Receive all as
 * ordered (LOCORDER1), so the two post exactly the same fields for the same values: the order-unit, container-count or
 * quantity-and-unit form, the unit cost under the meaning the chosen unit gives it, the line it settles and any
 * substitution. A fresh idempotency key each call: one receipt, one key. */
function receiptParamsFor(
  line: LineReceivingStateDto,
  entered: {
    unit: string;
    rawQuantity: string;
    costCents: number | undefined;
    substitute?: LiquorProductDto | undefined;
  },
): Parameters<typeof recordStockReceipt>[2] {
  const { unit, rawQuantity, costCents, substitute } = entered;
  const isOrderUnitReceipt = unit === ORDER_UNIT_RECEIVING_UNIT_VALUE;
  const isContainerReceipt = unit === CONTAINER_RECEIVING_UNIT_VALUE;
  return {
    idempotencyKey: crypto.randomUUID(),
    liquorProductId: substitute !== undefined ? substitute.id : line.liquorProductId,
    purchaseOrderLineId: line.purchaseOrderLineId,
    ...(substitute !== undefined ? { substitutedForLiquorProductId: line.liquorProductId } : {}),
    ...(isOrderUnitReceipt
      ? {
          receivedOrderUnits: rawQuantity,
          ...(costCents !== undefined ? { unitCostCents: costCents } : {}),
        }
      : isContainerReceipt
        ? {
            receivedContainerCount: rawQuantity,
            ...(costCents !== undefined ? { costPerContainerCents: costCents } : {}),
          }
        : {
            quantity: rawQuantity,
            unit,
            ...(costCents !== undefined ? { unitCostCents: costCents } : {}),
          }),
  };
}

/** Explicit action, the same posture Prepared and Pay use: a receipt is
 * an append-only inventory and cost write, never auto-saved on blur the
 * way the (pre-placement, reversible) cart quantity is. Every refusal --
 * a bad quantity, no unit picked, a bad cost, or the SERVER's own
 * over-receipt refusal (assertReceiptFits) -- lands on this line's own
 * error area, never a screen-wide toast, so the reason stays next to the
 * field it concerns. The over-receipt case is read from the response
 * body verbatim: this function never recomputes or second-guesses
 * whether a quantity "should" fit. */
async function handleRecordReceiptClick(
  line: LineReceivingStateDto,
  controls: ReceivingLineEntryControls,
): Promise<void> {
  controls.errorEl.hidden = true;
  controls.quantityInput.removeAttribute("aria-invalid");
  const rawQuantity = normalizeLeadingDot(controls.quantityInput.value);
  if (!RECEIVING_QUANTITY_PATTERN.test(rawQuantity) || rawQuantity === "0") {
    controls.errorEl.hidden = false;
    controls.errorEl.textContent = "Enter a quantity greater than zero, e.g. 2 or 1.5.";
    return;
  }
  const substitute = controls.substitute();
  if (controls.substituteOpen() && substitute === undefined) {
    controls.errorEl.hidden = false;
    controls.errorEl.textContent = "Pick the product that arrived.";
    return;
  }
  if (substitute !== undefined && substitute.id === line.liquorProductId) {
    controls.errorEl.hidden = false;
    controls.errorEl.textContent = "Pick a different product than the one ordered.";
    return;
  }
  const unit = selectedReceivingUnit(controls.unitPickerEl);
  if (unit === undefined) {
    controls.errorEl.hidden = false;
    controls.errorEl.textContent = "Pick a unit.";
    return;
  }
  const isOrderUnitReceipt = unit === ORDER_UNIT_RECEIVING_UNIT_VALUE;
  // INV8-F1 Section 2/5: the container chip submits receivedContainerCount
  // in place of quantity/unit; every other chip (Ounces, Units, and the
  // still-unchanged keg-mismatch "Bottles" raw path) submits exactly as
  // before. Section 3: the cost field means cents per container under the
  // container chip, cents per unit otherwise, so it lands on the field
  // Section 3's own mutual-exclusivity rule expects.
  const isContainerReceipt = unit === CONTAINER_RECEIVING_UNIT_VALUE;
  let costCents: number | undefined;
  const rawCost = controls.costInput.value.trim();
  if (rawCost.length > 0) {
    try {
      costCents = parseDollarsToCentsExact(
        rawCost,
        isContainerReceipt
          ? "cost per container"
          : isOrderUnitReceipt
            ? "cost per bottle"
            : "cost per unit",
      );
    } catch (error) {
      controls.errorEl.hidden = false;
      controls.errorEl.textContent = error instanceof Error ? error.message : "Enter a valid cost.";
      return;
    }
  }

  const unitLabel = selectedReceivingUnitLabel(controls.unitPickerEl) ?? unit;
  const session = requireSession();
  controls.submitButton.disabled = true;
  const result = await recordStockReceipt(
    session.apiBaseUrl,
    session.tenantId,
    receiptParamsFor(line, { unit, rawQuantity, costCents, substitute }),
  );
  controls.submitButton.disabled = false;
  if (result.status !== 200 && result.status !== 201) {
    // The over-receipt refusal (and any other domain refusal) shown in
    // the operator's own terms: src/domain/receiving.ts's
    // assertReceiptFits names the outstanding remainder and points at
    // raising a receiving exception, and that is exactly what lands
    // here, never a raw error code or a client-side rewrite of it.
    // INVPOLISH1: the figure stays where it was typed, the box takes the same refused-field look a refused Settings discount
    // does (DSC37-F1), and the sentence sits under it. Editing the figure, the unit or the cost clears both together.
    controls.errorEl.hidden = false;
    controls.errorEl.textContent =
      result.body.message ?? "Could not record this receipt. Check your connection.";
    controls.quantityInput.setAttribute("aria-invalid", "true");
    controls.quantityInput.setAttribute("aria-describedby", controls.errorEl.id);
    return;
  }
  showToast(
    isOrderUnitReceipt
      ? `Recorded ${describeOrderUnitQuantity(line, rawQuantity)} for ${line.productName}.`
      : `Recorded ${enteredQuantityText(rawQuantity, unitLabel)} for ${substitute !== undefined ? substitute.name : line.productName}.`,
    "success",
  );
  // CORE7-F1: a receipt for an 86'd item offers to clear the 86 (one tap, never automatic).
  void offerClearStock86(
    [substitute !== undefined ? substitute.id : line.liquorProductId],
    isOrderUnitReceipt
      ? `Recorded ${describeOrderUnitQuantity(line, rawQuantity)} for ${line.productName}.`
      : `Recorded ${enteredQuantityText(rawQuantity, unitLabel)} for ${substitute !== undefined ? substitute.name : line.productName}.`,
  );
  await loadPurchasingReceivingView();
}

// ---------------------------------------------------------------------
// LOCORDER1 (B6): Receive all as ordered. Posts, line by line, the same receipt Record receipt posts on each card
// (receiptParamsFor, recordStockReceipt: the one inventory receipts command, so quantities, costs, audit rows, stock
// movements and the order's status settle exactly as per-line receipts do; barcodeReceive's Finish receive posts the same
// way). Each line starts from the same prefill its card shows (receivingLinePrefill). A line whose card the person changed,
// or whose unit the order does not name, is left out and stays on its card.
// ---------------------------------------------------------------------

interface ReceiveAllLinePlan {
  line: LineReceivingStateDto;
  unit: string;
  quantity: string;
  costCents: number | undefined;
  /** Estimated cost of the line at the order's own unit cost; undefined when the order carries none. */
  totalCents: number | undefined;
}

interface ReceiveAllPlan {
  included: ReceiveAllLinePlan[];
  /** Lines left on their own card, with the plain reason. */
  held: { productName: string; reason: string }[];
  /** Sum of the included lines' estimates; null when any included line has no cost on the order. */
  totalCents: number | null;
}

function receivingCardChanged(lineId: string): boolean {
  const row = Array.from(
    purchasingReceivingLinesEl.querySelectorAll<HTMLElement>("[data-purchase-order-line-id]"),
  ).find((candidate) => candidate.dataset["purchaseOrderLineId"] === lineId);
  if (row === undefined) return false;
  const entry = row.querySelector<HTMLElement>(".purchasing-receiving-line-entry");
  if (entry === null) return false;
  const quantity =
    row.querySelector<HTMLInputElement>('[data-testid="purchasing-receiving-line-quantity-input"]')?.value ?? "";
  const cost =
    row.querySelector<HTMLInputElement>('[data-testid="purchasing-receiving-line-cost-input"]')?.value ?? "";
  const picker = row.querySelector<HTMLElement>('[data-testid="purchasing-receiving-line-unit-picker"]');
  const unit = picker === null ? "" : (selectedReceivingUnit(picker) ?? "");
  const substituteOpen =
    row.querySelector<HTMLElement>('[data-testid="purchasing-receiving-substitute-panel"]')?.hidden === false;
  return (
    substituteOpen ||
    quantity !== entry.dataset["prefillQuantity"] ||
    cost !== entry.dataset["prefillCost"] ||
    unit !== entry.dataset["prefillUnit"]
  );
}

function planReceiveAllAsOrdered(): ReceiveAllPlan {
  const included: ReceiveAllLinePlan[] = [];
  const held: { productName: string; reason: string }[] = [];
  for (const line of state.receivingLines) {
    if (receivingLineIsClosed(line)) continue;
    if (receivingCardChanged(line.purchaseOrderLineId)) {
      held.push({ productName: line.productName, reason: "changed on its card" });
      continue;
    }
    const productInfo = state.receivingUnitPriceCentsByLiquorProductId.get(line.liquorProductId);
    const options: readonly ReceivingUnitOption[] = receivingLineIsPacked(line)
      ? [orderUnitReceivingOption(line)]
      : receivingUnitOptionsForItemKind(productInfo?.unitPriceCents ?? null, {
          containerType: productInfo?.containerType ?? null,
          sizeValue: productInfo?.sizeValue ?? null,
          sizeUnit: productInfo?.sizeUnit ?? null,
          defaultKegSize: productInfo?.defaultKegSize ?? null,
        });
    const unitCost = state.receivingUnitCostCentsByLineId.get(line.purchaseOrderLineId);
    const prefill = receivingLinePrefill(line, unitCost, options);
    if (prefill.quantity === "" || prefill.unitValue === undefined) {
      held.push({ productName: line.productName, reason: "pick its unit on its card" });
      continue;
    }
    let costCents: number | undefined;
    if (prefill.costDollars !== undefined) {
      costCents = parseDollarsToCentsExact(
        prefill.costDollars,
        prefill.unitValue === ORDER_UNIT_RECEIVING_UNIT_VALUE
          ? "cost per bottle"
          : prefill.unitValue === CONTAINER_RECEIVING_UNIT_VALUE
            ? "cost per container"
            : "cost per unit",
      );
    }
    const hasOrderCost = unitCost !== null && unitCost !== undefined && Number.isFinite(unitCost);
    included.push({
      line,
      unit: prefill.unitValue,
      quantity: prefill.quantity,
      costCents,
      totalCents: hasOrderCost ? Math.round(Number(prefill.quantity) * unitCost) : undefined,
    });
  }
  const allCosted = included.every((entry) => entry.totalCents !== undefined);
  return {
    included,
    held,
    totalCents: allCosted ? included.reduce((sum, entry) => sum + (entry.totalCents ?? 0), 0) : null,
  };
}

function lineCountText(count: number): string {
  return count === 1 ? "1 line" : `${count} lines`;
}

/** The panel above the lines: what Receive all would record, or why it is not offered. Called after every render of the
 * receiving view. */
let receiveAllOrderStatus = "";
function renderReceiveAllPanel(status: string): void {
  receiveAllOrderStatus = status;
  const panel = mustFind<HTMLElement>("#purchasing-receiving-all");
  const summary = mustFind<HTMLElement>("#purchasing-receiving-all-summary");
  const note = mustFind<HTMLElement>("#purchasing-receiving-all-note");
  const button = mustFind<HTMLButtonElement>("#purchasing-receiving-all-button");
  const offered =
    purchaseOrderIsReceivable(status) &&
    hasPermission(requireSession(), INVENTORY_RECEIPT_RECORD_PERMISSION);
  const open = state.receivingLines.filter((line) => !receivingLineIsClosed(line));
  panel.hidden = !offered || open.length === 0;
  if (panel.hidden) return;
  const plan = planReceiveAllAsOrdered();
  const total = plan.totalCents === null ? "" : `, ${formatCartMoney(plan.totalCents)}`;
  summary.textContent = `${lineCountText(open.length)} still to receive${plan.held.length === 0 ? total : ""}`;
  note.textContent =
    plan.held.length === 0
      ? "Everything arrived as ordered? Receive it in one step. A line that differs: use its card below."
      : `${lineCountText(plan.included.length)} can be received as ordered. ${plan.held.length === 1 ? "1 line stays" : `${plan.held.length} lines stay`} on its own card below.`;
  button.disabled = plan.included.length === 0;
  button.textContent = plan.held.length === 0 ? "Receive all as ordered" : `Receive ${lineCountText(plan.included.length)} as ordered`;
}

// MGRDAYMAJ1 (manager-day-19): a short delivery needs an answer. After a partial receipt the vendor either sends the rest or it
// is not coming; the screen asks, and "not coming" closes the order short with the two existing commands (report a short
// delivery, then accept it as short), so the order reads Received instead of waiting forever. The close is two taps: it cannot
// be undone, so the first tap names what it will do.
const shortPanel = mustFind<HTMLElement>("#purchasing-receiving-short");
const shortText = mustFind<HTMLElement>("#purchasing-receiving-short-text");
const shortCloseButton = mustFind<HTMLButtonElement>("#purchasing-receiving-short-close");
const shortWaitButton = mustFind<HTMLButtonElement>("#purchasing-receiving-short-wait");
const shortStatus = mustFind<HTMLElement>("#purchasing-receiving-short-status");
let shortCloseArmed = false;
let shortOnTheWay = false;
let shortBusy = false;

function renderShortDeliveryPanel(status: string): void {
  const open = state.receivingLines.filter((line) => !receivingLineIsClosed(line));
  const offered =
    status === "partially_received" &&
    open.length > 0 &&
    hasPermission(requireSession(), PURCHASING_ORDER_PLACE_PERMISSION);
  shortPanel.hidden = !offered;
  if (!offered) {
    shortCloseArmed = false;
    shortOnTheWay = false;
    return;
  }
  const missing = open
    .slice(0, 3)
    .map((line) => `${line.productName}: ${describeOrderUnitQuantity(line, formatReceivingQuantity(line.outstandingQuantity))}`)
    .join("; ");
  const more = open.length > 3 ? ` and ${open.length - 3} more` : "";
  shortText.textContent = shortOnTheWay
    ? `Noted. This order stays open as partially received until the rest arrives. Still to come: ${missing}${more}.`
    : shortCloseArmed
      ? `Close this order short? It will read Received, and the missing items (${missing}${more}) will not be expected. This cannot be undone.`
      : `Still to come: ${missing}${more}. Is the vendor still sending the rest?`;
  shortCloseButton.textContent = shortCloseArmed ? "Yes, close short" : "Rest is not coming (close short)";
  shortCloseButton.classList.toggle("btn-primary", shortCloseArmed);
  shortCloseButton.classList.toggle("btn-secondary", !shortCloseArmed);
  shortWaitButton.textContent = shortCloseArmed ? "Not yet" : "Rest is on the way";
  shortWaitButton.hidden = shortOnTheWay;
  shortCloseButton.hidden = shortOnTheWay;
}

shortWaitButton.addEventListener("click", () => {
  shortStatus.hidden = true;
  if (shortCloseArmed) {
    shortCloseArmed = false;
  } else {
    shortOnTheWay = true;
  }
  renderShortDeliveryPanel("partially_received");
});

shortCloseButton.addEventListener("click", () => {
  const order = receivingScreenOrder;
  if (order === undefined || shortBusy) return;
  if (!shortCloseArmed) {
    shortCloseArmed = true;
    shortStatus.hidden = true;
    renderShortDeliveryPanel("partially_received");
    return;
  }
  void closeOrderShort(order.id);
});

async function closeOrderShort(orderId: string): Promise<void> {
  const session = requireSession();
  shortBusy = true;
  shortCloseButton.disabled = true;
  shortWaitButton.disabled = true;
  shortStatus.hidden = true;
  try {
    const reported = await transitionPurchaseOrder(session.apiBaseUrl, session.tenantId, orderId, {
      idempotencyKey: crypto.randomUUID(),
      toStatus: "exception",
      exceptionReason: "short_delivery",
    });
    if (reported.status !== 200) {
      shortStatus.hidden = false;
      shortStatus.textContent = capitalizeServerMessage(
        reported.body.message ?? "Could not close this order short. Check your connection and try again.",
      );
      return;
    }
    const accepted = await transitionPurchaseOrder(session.apiBaseUrl, session.tenantId, orderId, {
      idempotencyKey: crypto.randomUUID(),
      toStatus: "received",
      resolution: "accepted_short",
    });
    if (accepted.status !== 200) {
      // The short delivery is on record and the order is waiting on this answer; say so, and leave the question open.
      shortStatus.hidden = false;
      shortStatus.textContent = capitalizeServerMessage(
        accepted.body.message ??
          "The short delivery is recorded, but the order was not closed. Open the purchase order to finish.",
      );
      return;
    }
    shortCloseArmed = false;
    showToast("Order closed short. The missing items are no longer expected.", "success");
    await loadPurchasingReceivingView();
  } finally {
    shortBusy = false;
    shortCloseButton.disabled = false;
    shortWaitButton.disabled = false;
  }
}

// A card edited (or its unit picked) changes which lines Receive all can take as ordered, so the panel follows.
for (const eventName of ["input", "change"]) {
  purchasingReceivingLinesEl.addEventListener(eventName, () => renderReceiveAllPanel(receiveAllOrderStatus));
}

const receiveAllDialog = mustFind<HTMLDialogElement>("#receive-all-dialog");
const receiveAllTitle = mustFind<HTMLElement>("#receive-all-title");
const receiveAllSummary = mustFind<HTMLElement>("#receive-all-summary");
const receiveAllHeld = mustFind<HTMLElement>("#receive-all-held");
const receiveAllError = mustFind<HTMLElement>("#receive-all-error");
const receiveAllCancel = mustFind<HTMLButtonElement>("#receive-all-cancel");
const receiveAllConfirm = mustFind<HTMLButtonElement>("#receive-all-confirm");
let receiveAllBusy = false;
let receiveAllDone = false;

mustFind<HTMLButtonElement>("#purchasing-receiving-all-button").addEventListener("click", () => {
  const order = receivingScreenOrder;
  if (order === undefined || receiveAllBusy) return;
  const plan = planReceiveAllAsOrdered();
  if (plan.included.length === 0) return;
  receiveAllDone = false;
  receiveAllError.hidden = true;
  receiveAllError.textContent = "";
  const total = plan.totalCents === null ? "" : `, ${formatCartMoney(plan.totalCents)}`;
  receiveAllSummary.textContent =
    `${order.vendorName}: ${lineCountText(plan.included.length)} at the quantities and costs on the order${total}. ` +
    "This adds them to stock and settles the order, the same as receiving each line.";
  receiveAllHeld.hidden = plan.held.length === 0;
  receiveAllHeld.textContent = plan.held.length === 0
    ? ""
    : `Not included: ${plan.held.map((entry) => `${entry.productName} (${entry.reason})`).join(", ")}.`;
  receiveAllTitle.textContent =
    plan.held.length === 0 ? "Receive all as ordered?" : `Receive ${lineCountText(plan.included.length)} as ordered?`;
  receiveAllConfirm.textContent = `Receive ${lineCountText(plan.included.length)}`;
  receiveAllConfirm.disabled = false;
  receiveAllCancel.textContent = "Cancel";
  receiveAllDialog.showModal();
});
receiveAllCancel.addEventListener("click", () => {
  if (!receiveAllBusy) receiveAllDialog.close();
});
receiveAllDialog.addEventListener("cancel", (event) => {
  if (receiveAllBusy) event.preventDefault();
});
receiveAllConfirm.addEventListener("click", () => {
  if (receiveAllDone) {
    receiveAllDialog.close();
    return;
  }
  void handleReceiveAllConfirm();
});

async function handleReceiveAllConfirm(): Promise<void> {
  if (receiveAllBusy) return;
  const order = receivingScreenOrder;
  if (order === undefined) return;
  // Re-plan at the tap, from what is on screen now, so the confirmation and the posting never disagree.
  const plan = planReceiveAllAsOrdered();
  const session = requireSession();
  receiveAllBusy = true;
  receiveAllConfirm.disabled = true;
  receiveAllCancel.disabled = true;
  receiveAllError.hidden = true;
  const received: string[] = [];
  let failure: string | undefined;
  for (const [index, entry] of plan.included.entries()) {
    receiveAllConfirm.textContent = `Recording ${index + 1} of ${plan.included.length}`;
    const result = await recordStockReceipt(
      session.apiBaseUrl,
      session.tenantId,
      receiptParamsFor(entry.line, { unit: entry.unit, rawQuantity: entry.quantity, costCents: entry.costCents }),
    );
    if (result.status !== 200 && result.status !== 201) {
      failure = `${entry.line.productName}: ${result.body.message ?? "Could not record this receipt. Check your connection."}`;
      break;
    }
    received.push(entry.line.liquorProductId);
  }
  receiveAllBusy = false;
  receiveAllCancel.disabled = false;
  if (failure === undefined) {
    receiveAllDialog.close();
    showToast(`Received ${lineCountText(received.length)} for ${order.vendorName}.`, "success");
    void offerClearStock86(received, `Received ${lineCountText(received.length)} for ${order.vendorName}.`);
  } else {
    // The lines recorded so far stay recorded; the screen below shows what is left, and the one that stopped says why.
    receiveAllDone = true;
    receiveAllError.hidden = false;
    receiveAllError.textContent =
      `Recorded ${lineCountText(received.length)} of ${plan.included.length}. Stopped at ${failure} The rest are still on their cards.`;
    receiveAllConfirm.disabled = false;
    receiveAllConfirm.textContent = "Close";
    receiveAllCancel.textContent = "Close";
  }
  await loadPurchasingReceivingView();
}

function handlePurchasingReceivingBackToOrdersClick(): void {
  showPurchasingOrdersScreen();
}

// UX66 part C: the Purchasing Cart's own "Back to Purchasing" control.
// This cart is reached only from Settings > Purchasing's own "Purchasing
// Cart" button (PO6-R1, DEC-056), so its own way back lands there too,
// never at the topbar's floor-bound defaultTopbarHome.
function handlePurchasingCartBackToPurchasingClick(): void {
  // UX80: the cart is reached from Ordering in the account menu, so its way
  // back is the Ordering hub (UX116), never Settings.
  openFunctionHub("ordering");
}

// ---------------------------------------------------------------------
// PO6 Slice 6 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S6-outside-system-orders.md). Recording an order
// placed outside this product: the SAME write the receiving screen above
// makes (recordOutsideSystemReceipt, which calls the identical
// POST .../inventory/receipts route recordStockReceipt calls, with
// source "outside_system" and no purchase-order line). ADMIN SURFACE,
// like the receiving screen above, since PO6-R1 (DEC-056): see
// ui/index.html's own comment on this screen's root.
// ---------------------------------------------------------------------

/** PO6-R1 (DEC-056): reached directly from Settings > Purchasing's own
 * "Receive stock (no purchase order)" button. Unlike the receiving screen, there is
 * no order to open this screen
 * WITH: it always starts at product search, product-selection state and
 * the "recorded this visit" list both cleared, the same "fresh every
 * visit" shape showPurchasingCartScreen already uses for its own placed-
 * orders list. */
function showPurchasingOutsideOrderScreen(): void {
  dropDraft(RECEIVE_STOCK_DRAFT);
  hideAllScreens();
  purchasingOutsideOrderScreen.hidden = false;
  showTopbarChrome("purchasing-outside-order-screen");
  purchasingOutsideOrderScanInvoiceButton.hidden = !hasPermission(
    requireSession(),
    INVENTORY_RECEIPT_RECORD_PERMISSION,
  );
  purchasingOutsideOrderStatusEl.hidden = true;
  purchasingOutsideOrderStatusEl.textContent = "";
  purchasingOutsideOrderProductSearchInput.value = "";
  purchasingOutsideOrderProductResultsEl.innerHTML = "";
  purchasingOutsideOrderProductSearchResults = [];
  purchasingOutsideOrderSelectedProduct = undefined;
  setOutsideOrderEntryVisible(false);
  state.outsideOrderRecordedReceipts = [];
  // INV35-B1-F1: the vendor carries within a visit only; opening the screen fresh drops it.
  receiveVendorController.reset();
  renderPurchasingOutsideOrderRecordedList();
}

/** PO7-S5: opens the invoice scan from the screen it was started on. */
function showReceiveScanScreen(origin: ReceiveScanOrigin): void {
  hideAllScreens();
  receiveScanScreen.hidden = false;
  showTopbarChrome("receive-scan-screen");
  receiveScanController.open(origin);
}

purchasingReceivingScanInvoiceButton.addEventListener("click", () => {
  const orderId = state.receivingOrderId;
  if (orderId === undefined || receivingScreenOrder === undefined) {
    return;
  }
  showReceiveScanScreen({
    kind: "order",
    orderId,
    vendorName: receivingScreenOrder.vendorName,
    lines: state.receivingLines,
  });
});
purchasingOutsideOrderScanInvoiceButton.addEventListener("click", () => {
  showReceiveScanScreen({ kind: "none" });
});

/** INV35-B1-F1: the entry and the product search are never shown together. */
function setOutsideOrderEntryVisible(visible: boolean): void {
  purchasingOutsideOrderEntryEl.hidden = !visible;
  purchasingOutsideOrderFindEl.hidden = visible;
}

/** INV35-B1-F1: closes the "Pick a vendor" sheet. Called from closeAllFloorSheets so sign-out
 * never leaves it open, and the pick goes with the visit (the next screen open resets it). */
function closeReceiveVendorSheet(): void {
  receiveVendorController.closeSheet();
  receiveVendorBackdrop.hidden = true;
}

function showPurchasingOutsideOrderError(message: string): void {
  purchasingOutsideOrderStatusEl.hidden = false;
  purchasingOutsideOrderStatusEl.textContent = message;
}

// --- Find the product (never typed into the write itself: a person taps
// a specific search result row, the same discipline the cart screen's own
// product search already uses). ---

function handlePurchasingOutsideOrderProductSearchInput(): void {
  if (purchasingOutsideOrderProductSearchDebounceTimer !== undefined) {
    clearTimeout(purchasingOutsideOrderProductSearchDebounceTimer);
  }
  purchasingOutsideOrderProductSearchDebounceTimer = setTimeout(() => {
    void loadPurchasingOutsideOrderProductSearchResults();
  }, 250);
}

async function loadPurchasingOutsideOrderProductSearchResults(): Promise<void> {
  const session = requireSession();
  const query = purchasingOutsideOrderProductSearchInput.value.trim();
  if (query.length === 0) {
    purchasingOutsideOrderProductSearchResults = [];
    purchasingOutsideOrderProductResultsEl.innerHTML = "";
    return;
  }
  const result = await listLiquorProducts(session.apiBaseUrl, session.tenantId, { locationId: terminalConfigState?.locationId,
    q: query,
    activeOnly: true,
  });
  if (result.status !== 200) {
    purchasingOutsideOrderProductResultsEl.innerHTML = "";
    showPurchasingOutsideOrderError(
      result.body.message ?? "Could not search products. Check your connection.",
    );
    return;
  }
  purchasingOutsideOrderProductSearchResults = result.body.liquorProducts;
  renderPurchasingOutsideOrderProductResults();
}

function renderPurchasingOutsideOrderProductResults(): void {
  purchasingOutsideOrderProductResultsEl.innerHTML = "";
  for (const product of purchasingOutsideOrderProductSearchResults) {
    purchasingOutsideOrderProductResultsEl.append(
      buildPurchasingOutsideOrderProductResultRow(product),
    );
  }
}

function buildPurchasingOutsideOrderProductResultRow(product: LiquorProductDto): HTMLElement {
  const row = document.createElement("div");
  row.className = "purchasing-outside-order-product-result-row";
  row.dataset["testid"] = "purchasing-outside-order-product-result-row";

  const name = document.createElement("div");
  name.className = "purchasing-outside-order-product-result-name";
  name.dataset["testid"] = "purchasing-outside-order-product-result-name";
  name.textContent = product.name;

  const selectButton = document.createElement("button");
  selectButton.type = "button";
  selectButton.className = "btn-secondary btn-compact";
  selectButton.dataset["testid"] = "purchasing-outside-order-product-result-select";
  selectButton.textContent = "Select";
  selectButton.addEventListener("click", () => handleSelectOutsideOrderProduct(product));

  row.append(name, selectButton);
  return row;
}

/** A tap on a search result, never a value typed into the write itself:
 * liquorProductId on the eventual POST .../inventory/receipts call always
 * comes from THIS product, picked here. */
function handleSelectOutsideOrderProduct(product: LiquorProductDto): void {
  purchasingOutsideOrderSelectedProduct = product;
  purchasingOutsideOrderEntryProductEl.textContent = product.name;
  purchasingOutsideOrderQuantityInput.value = "";
  purchasingOutsideOrderCostInput.value = "";
  purchasingOutsideOrderReferenceInput.value = "";
  purchasingOutsideOrderEntryErrorEl.hidden = true;
  purchasingOutsideOrderEntryErrorEl.textContent = "";
  renderPurchasingOutsideOrderUnitPicker(product);
  setOutsideOrderEntryVisible(true);
  saveReceiveStockDraft();
  // INV35-B1-F1: the vendor list and this item's usual vendor. The pick, if any, carries over.
  void receiveVendorController.loadFor(product.id);
}

function handleChangeOutsideOrderProductClick(): void {
  purchasingOutsideOrderSelectedProduct = undefined;
  setOutsideOrderEntryVisible(false);
  saveReceiveStockDraft();
}

/** The same fixed on-hand vocabulary the receiving screen's own unit
 * picker uses (ui/src/purchasingDisplay.ts's
 * receivingUnitOptionsForItemKind), keyed on the SELECTED product's own
 * unit_price_cents -- already on hand from the search result, unlike the
 * receiving screen, which needs a second GET .../liquor-products/:id call
 * because getOrderReceivingView's own line projection carries no product
 * fields at all. No default selected: a picker deciding a real business
 * fact is never pre-chosen. INV8-F1 Section 5: this product's own
 * container_type/size_value/size_unit are also already on hand from the
 * same search result, so a bottle/can-configured product offers the same
 * real container chip the receiving screen does. Also wires the cost
 * field's own dynamic label (Section 5's own "label follows the selected
 * chip" rule) to this picker's own change events. */
function renderPurchasingOutsideOrderUnitPicker(product: LiquorProductDto): void {
  purchasingOutsideOrderUnitPickerEl.innerHTML = "";
  const containerInfo: ReceivingProductContainerInfo = {
    containerType: product.container_type,
    sizeValue: product.size_value,
    sizeUnit: product.size_unit,
    defaultKegSize: product.default_keg_size,
  };
  const unitOptions = receivingUnitOptionsForItemKind(product.unit_price_cents, containerInfo);
  for (const option of unitOptions) {
    const optionLabel = document.createElement("label");
    optionLabel.className = "admin-chip-option";
    const optionInput = document.createElement("input");
    optionInput.type = "radio";
    optionInput.name = "purchasing-outside-order-unit";
    optionInput.className = "admin-chip-option-input";
    optionInput.value = option.value;
    optionInput.dataset["value"] = option.value;
    optionInput.dataset["testid"] = "purchasing-outside-order-unit-option";
    const optionText = document.createElement("span");
    optionText.className = "admin-chip-option-label";
    optionText.textContent = option.label;
    optionLabel.append(optionInput, optionText);
    purchasingOutsideOrderUnitPickerEl.append(optionLabel);
  }
  const applyOutsideOrderCostFieldLabels = (): void => {
    const labels = receivingCostFieldLabels(selectedOutsideOrderUnit(), containerInfo, undefined);
    purchasingOutsideOrderCostInput.placeholder = labels.placeholder;
    purchasingOutsideOrderCostInput.setAttribute("aria-label", labels.ariaLabel);
  };
  applyOutsideOrderCostFieldLabels();
  // .onchange, never addEventListener: unlike the receiving screen's own
  // per-line picker (a fresh element every render), this screen's picker
  // element is the SAME persistent node across every product selected in
  // one visit (module-level mustFind), so addEventListener here would
  // stack one more listener per product picked instead of replacing it.
  purchasingOutsideOrderUnitPickerEl.onchange = applyOutsideOrderCostFieldLabels;
}

function selectedOutsideOrderUnit(): string | undefined {
  const checked = purchasingOutsideOrderUnitPickerEl.querySelector<HTMLInputElement>(
    'input[type="radio"]:checked',
  );
  return checked?.dataset["value"];
}

/** Explicit action, the same posture Slice 4's own "Record receipt" uses:
 * a receipt is an append-only inventory and cost write, never auto-saved
 * on blur. Every refusal -- a bad quantity, no unit picked, a bad cost,
 * or a SERVER refusal (including the domain's own source-scoped
 * duplicate-reference refusal) -- lands on this entry's own error area,
 * verbatim, never a client-side guess at the rule. */
async function handleRecordOutsideOrderSubmitClick(): Promise<void> {
  const product = purchasingOutsideOrderSelectedProduct;
  if (product === undefined) {
    return;
  }
  purchasingOutsideOrderEntryErrorEl.hidden = true;
  const rawQuantity = normalizeLeadingDot(purchasingOutsideOrderQuantityInput.value);
  if (!RECEIVING_QUANTITY_PATTERN.test(rawQuantity) || rawQuantity === "0") {
    purchasingOutsideOrderEntryErrorEl.hidden = false;
    purchasingOutsideOrderEntryErrorEl.textContent =
      "Enter a quantity greater than zero, e.g. 2 or 1.5.";
    return;
  }
  const unit = selectedOutsideOrderUnit();
  if (unit === undefined) {
    purchasingOutsideOrderEntryErrorEl.hidden = false;
    purchasingOutsideOrderEntryErrorEl.textContent = "Pick a unit.";
    return;
  }
  // INV8-F1 Section 2/5: see handleRecordReceiptClick's identical branch.
  const isContainerReceipt = unit === CONTAINER_RECEIVING_UNIT_VALUE;
  let costCents: number | undefined;
  const rawCost = purchasingOutsideOrderCostInput.value.trim();
  if (rawCost.length > 0) {
    try {
      costCents = parseDollarsToCentsExact(
        rawCost,
        isContainerReceipt ? "cost per container" : "cost per unit",
      );
    } catch (error) {
      purchasingOutsideOrderEntryErrorEl.hidden = false;
      purchasingOutsideOrderEntryErrorEl.textContent =
        error instanceof Error ? error.message : "Enter a valid cost.";
      return;
    }
  }
  const rawReference = purchasingOutsideOrderReferenceInput.value.trim();
  const sourceReference = rawReference.length > 0 ? rawReference : undefined;

  const session = requireSession();
  // UX105: the unit as the person picked it, read before the picker is reset, for the toast and the history row.
  const enteredUnitLabel =
    purchasingOutsideOrderUnitPickerEl
      .querySelector<HTMLInputElement>('input[type="radio"]:checked')
      ?.parentElement?.querySelector(".admin-chip-option-label")?.textContent ?? unit;
  // INV35-B1-F1: read at the moment of the tap, so the vendor posted is the one the location shows.
  const vendor = receiveVendorController.chosen();
  purchasingOutsideOrderSubmitButton.disabled = true;
  const result = await recordOutsideSystemReceipt(session.apiBaseUrl, session.tenantId, {
    idempotencyKey: crypto.randomUUID(),
    liquorProductId: product.id,
    ...(isContainerReceipt
      ? {
          receivedContainerCount: rawQuantity,
          ...(costCents !== undefined ? { costPerContainerCents: costCents } : {}),
        }
      : {
          quantity: rawQuantity,
          unit,
          ...(costCents !== undefined ? { unitCostCents: costCents } : {}),
        }),
    ...(sourceReference !== undefined ? { sourceReference } : {}),
    ...(vendor !== undefined ? { vendorId: vendor.id } : {}),
  });
  purchasingOutsideOrderSubmitButton.disabled = false;
  if (result.status !== 200 && result.status !== 201) {
    // Includes the domain's own source-scoped duplicate-reference
    // refusal (migration 0085's inventory_receipts_source_reference_key):
    // shown here verbatim, never recomputed or restated.
    purchasingOutsideOrderEntryErrorEl.hidden = false;
    purchasingOutsideOrderEntryErrorEl.textContent =
      result.body.message ?? "Could not record this delivery. Check your connection.";
    return;
  }
  state.outsideOrderRecordedReceipts = [
    {
      id: result.body.receipt.id,
      productName: product.name,
      // UX105: what was entered (2, Bottles), not the ledger's resolved ounces from the response (67.628 oz).
      quantity: rawQuantity,
      unit: enteredUnitLabel,
      sourceReference: result.body.receipt.source_reference,
      vendorName: vendor?.name,
    },
    ...state.outsideOrderRecordedReceipts,
  ];
  renderPurchasingOutsideOrderRecordedList();
  showToast(recordedToast(rawQuantity, enteredUnitLabel, product.name, vendor), "success");
  // CORE7-F2: a delivery for an 86'd item offers to clear the 86 (one tap, never automatic).
  void offerClearStock86(
    [product.id],
    recordedToast(rawQuantity, enteredUnitLabel, product.name, vendor),
  );
  // Reset to product search, ready for the next line of the same outside
  // order: an outside-system delivery is often several products at once.
  purchasingOutsideOrderSelectedProduct = undefined;
  setOutsideOrderEntryVisible(false);
  dropDraft(RECEIVE_STOCK_DRAFT);
  purchasingOutsideOrderProductSearchInput.value = "";
  purchasingOutsideOrderProductSearchInput.focus();
  purchasingOutsideOrderProductResultsEl.innerHTML = "";
  purchasingOutsideOrderProductSearchResults = [];
}

function renderPurchasingOutsideOrderRecordedList(): void {
  purchasingOutsideOrderRecordedListEl.innerHTML = "";
  if (state.outsideOrderRecordedReceipts.length === 0) {
    purchasingOutsideOrderRecordedEmptyEl.hidden = false;
    return;
  }
  purchasingOutsideOrderRecordedEmptyEl.hidden = true;
  for (const receipt of state.outsideOrderRecordedReceipts) {
    purchasingOutsideOrderRecordedListEl.append(buildPurchasingOutsideOrderRecordedRow(receipt));
  }
}

function buildPurchasingOutsideOrderRecordedRow(receipt: OutsideOrderRecordedReceipt): HTMLElement {
  const row = document.createElement("div");
  row.className = "purchasing-outside-order-recorded-row";
  row.dataset["testid"] = "purchasing-outside-order-recorded-row";

  const product = document.createElement("div");
  product.className = "purchasing-outside-order-recorded-product";
  product.dataset["testid"] = "purchasing-outside-order-recorded-product";
  product.textContent = receipt.productName;

  const quantity = document.createElement("div");
  quantity.className = "purchasing-outside-order-recorded-quantity";
  quantity.dataset["testid"] = "purchasing-outside-order-recorded-quantity";
  quantity.textContent = enteredQuantityText(receipt.quantity, receipt.unit);

  const reference = document.createElement("div");
  reference.className = "purchasing-outside-order-recorded-reference";
  reference.dataset["testid"] = "purchasing-outside-order-recorded-reference";
  reference.textContent = formatOutsideSystemReceiptReference(receipt.sourceReference);

  const vendorLine = document.createElement("div");
  vendorLine.className = "purchasing-outside-order-recorded-vendor";
  vendorLine.dataset["testid"] = "purchasing-outside-order-recorded-vendor";
  vendorLine.textContent = recordedVendorText(receipt.vendorName);

  row.append(product, quantity, reference, vendorLine);
  return row;
}

function showRingScreen(): void {
  hideAllScreens();
  ringScreen.hidden = false;
  showTopbarChrome("ring-screen");
  // UX124: stock received or an 86 cleared since the menu last loaded.
  void refreshFloorMenuStock();
  const session = requireSession();
  debugApiBase.textContent = session.apiBaseUrl;
  debugTenant.textContent = session.tenantId;
  debugEmployee.textContent = session.employeeId;
  // PL3: a ticket that already has a link out for it resumes its automatic
  // status check whenever the ring screen is (re)shown.
  void resumeTicketPaymentLinkWatch();
}

// ---------------------------------------------------------------------
// Ticket reset
// ---------------------------------------------------------------------

function resetTicketState(): void {
  floorTicketVisit += 1;
  resetMenuSearch();
  state.orderId = undefined;
  state.orderStatus = undefined;
  state.orderName = undefined;
  state.lastReopen = undefined;
  ticketReopenStripEl.hidden = true;
  ticketReopenStripEl.innerHTML = "";
  clearClosedView();
  state.idVerification = undefined;
  idVerifyReadKey = "";
  ticketIdRowEl.hidden = true;
  ticketIdRowEl.replaceChildren();
  state.orderPaid = false;
  state.lines = [];
  expandedLineNameIds.clear();
  state.quote = undefined;
  state.paymentPreview = undefined;
  paymentPreviewFailedCount = 0;
  // ALPHA-12.
  state.guests = [];
  state.payScope = undefined;
  state.activeSplitPlan = undefined;
  state.activeSplitPlanLoadState = "ready";
  // Amendment 2026-08-22 (fifth), item 3.
  state.capturedPaymentTimeByGuestId = new Map();
  state.refundedLineIds = new Set();
  state.latestCapturedPaymentTime = undefined;
  // UX62.
  state.capturedPaymentsSummary = undefined;
    state.capturedCharges = undefined;
  state.ticketRefundable = undefined;
  // Payments-track Slice 4 (DEC-002).
  state.activeHold = undefined;
  // PAY14 (D2 and the Q3 note): both belong to the ticket being left.
  state.holdProviderMismatch = false;
  state.testHoldClearedNoteVisible = false;
  // T19: state.savedCards is deliberately NOT reset here. enterTicket is
  // the only path that ever makes the ring screen interactive again, and
  // it resets state.savedCards itself before doing so; duplicating that
  // reset here would be redundant in production and would silently
  // absorb the resume-path negative control the brief's own Section 4
  // requires (this codebase's Open Tabs screen never reads
  // state.savedCards, so there is no present reader this leaves stale).
  ticketHoldBadge.hidden = true;
  // RCPT1-S3: nothing of the ticket being left stays on screen.
  state.awaitingTip = undefined;
  state.receiptSaleSuppressedOrderId = undefined;
  awaitingTipStatusNote = undefined;
  awaitingTipSheet.close();
  ticketAwaitingBadge.hidden = true;
  authorizedRowEl.hidden = true;
  awaitingTipRowEl.hidden = true;
  awaitingTipActionsEl.hidden = true;
  awaitingTipStatusEl.hidden = true;
  actionRowPrimaryEl.hidden = false;
  ticketMoreButton.hidden = true;
  orderStatusEl.textContent = "No open ticket";
  ticketSub.textContent = "";
  noOrderPanel.hidden = false;
  addGuestRowEl.hidden = true;
  ticketLinesEl.hidden = true;
  ticketLinesEl.innerHTML = "";
  ticketTotalsEl.hidden = true;
  ticketActionsEl.hidden = true;
  payStatusEl.hidden = true;
  payStatusRetryButton.hidden = true;
  payTipSplitEl.hidden = true;
  debugOrderId.textContent = "(none)";
  // PAY7 Section 3 test 9: refreshes the current-ticket pay-key debug
  // field too, so it reads "(none)" once state.orderId is cleared rather
  // than showing whatever the last-open ticket's own key happened to be.
  renderPayKeyDebug();
  // Sixteenth amendment item 2: never leaks across tickets. See
  // hideZeroDuePrompt's own comment.
  hideZeroDuePrompt();
  // AU8: hidden immediately on leaving a ticket, without waiting on a
  // fetch -- state.managerExceptions itself is tenant-wide, not per-
  // ticket, so it is left alone here and simply re-filtered the next time
  // renderTicketManagerExceptionBanner runs for whichever order comes next.
  ticketManagerExceptionBanner.hidden = true;
}

/** Leaves the ring screen without closing the active order (it stays
 * exactly as it is server-side; Open Tabs will re-fetch its current
 * state fresh the next time it is resumed), and returns to Open Tabs. */
function handleBackToTabsClick(): void {
  resetTicketState();
  refreshTileDisabledState();
  showOpenTabsScreen();
  void loadOpenTabs();
}

// ---------------------------------------------------------------------
// Entering a ticket (New Ticket, resume, reopen all funnel through here)
// ---------------------------------------------------------------------

async function enterTicket(
  order: OrderDto,
  lines: TicketLineState[],
  guests: GuestDto[],
  lastReopen: LastReopenDto | null = null,
): Promise<void> {
  resetMenuSearch();
  floorTicketVisit += 1;
  // CLOSEDVIEW1: entering a live ticket (a Reopen from the closed view lands here) ends any read-only view.
  clearClosedView();
  state.orderId = order.id;
  const ticketContext = captureFloorSaleContext();
  state.orderStatus = order.status;
  state.orderName = order.name ?? undefined;
  // AU14-F4 / DEC-276: the record strip's own data, from the order read.
  state.lastReopen = lastReopen;
  // T19 (docs/build-briefs/T19-saved-cards-unknown-until-loaded.md): reset
  // to "not yet known" for THIS ticket before showRingScreen() below makes
  // #btn-ticket-more interactive, on every entry, cold-start or resume.
  // Neither field is otherwise assigned anywhere between here and their
  // own loadSavedCards/loadHoldStatus calls further down this function, so
  // without this, a resumed ticket would carry the PREVIOUS ticket's own
  // resolved values into this ticket's own render window. This is the
  // ONLY reset for state.savedCards in the whole file (resetTicketState
  // deliberately does not duplicate it: every path that makes the ring
  // screen interactive again goes through enterTicket, so a second reset
  // there would be redundant and would also hide this one's own absence
  // from the resume-path negative control the brief's own Section 4
  // requires).
  state.savedCards = undefined;
  state.pendingCardDetaches = [];
  state.activeHold = undefined;
  // PAY14: the badge's needs-a-manager state and the Q3 note are per
  // ticket; this ticket's own hold read below sets them afresh.
  state.holdProviderMismatch = false;
  state.testHoldClearedNoteVisible = false;
  // ALPHA-08 delta payments: reset to "unknown" rather than assuming
  // false. A resumed or reopened tab may already have covered lines from
  // an earlier payment (this session's or a prior one); orderPaid here is
  // display-only history for THIS session, refreshPaymentPreview below is
  // what actually establishes the real, current uncovered set.
  state.orderPaid = false;
  state.lines = lines;
  state.quote = undefined;
  // FLOORGLITCH1: the priced result (Subtotal, Tax and Total read it first) belongs to the previous ticket until this
  // ticket's own pricing arrives; never show another ticket's total beside this ticket's drinks.
  state.orderDiscounts = undefined;
  state.paymentPreview = undefined;
  paymentPreviewFailedCount = 0;
  // ALPHA-12.
  state.guests = guests;
  state.payScope = undefined;
  // M15: the ring screen below becomes interactive before the server's
  // active-split read finishes. Reset both the prior ticket's plan and the
  // knowledge state first, so undefined means "not known yet" until this
  // ticket's own successful response says there is no active split.
  state.activeSplitPlan = undefined;
  state.activeSplitPlanLoadState = "loading";
  // Amendment 2026-08-22 (fifth), item 3: reset to "unknown" rather than
  // carrying over the previous ticket's own section-ordering data;
  // refreshPaymentPreview (called just below) re-establishes it fresh for
  // this order before renderTicketLines needs it.
  state.capturedPaymentTimeByGuestId = new Map();
  state.refundedLineIds = new Set();
  state.latestCapturedPaymentTime = undefined;
  // UX62: same reasoning as capturedPaymentTimeByGuestId just above;
  // refreshPaymentPreview (called just below) re-establishes it fresh.
  state.capturedPaymentsSummary = undefined;
    state.capturedCharges = undefined;
  // UX61: overwritten a few lines below by renderTicket -> renderTicketActions
  // -> ticketStatusLabel(), which is the one place this text is derived now;
  // set here too only so debugOrderId's neighbour never sits blank between
  // this line and that call.
  orderStatusEl.textContent = state.orderStatus ?? "";
  debugOrderId.textContent = state.orderId;
  // PAY7 Section 3 test 9: reflects whatever this order's own persisted
  // pay key already restored (Section 2.8) as soon as this ticket enters
  // view, not only after the next mint/clear call.
  renderPayKeyDebug();
  showRingScreen();
  refreshTileDisabledState();
  renderTicket();
  void recomputeQuote();
  // UX13-F1 (docs/backlog.md; DEC-026): awaited, not fire-and-forget like
  // the other reads in this function, because maybeShowZeroDueOnReopen
  // right below needs state.paymentPreview to already reflect this
  // ticket's own live balance before it decides anything.
  await refreshPaymentPreview();
  if (!floorSaleContextIsCurrent(ticketContext)) return;
  maybeShowZeroDueOnReopen();
  // TAPSPEED1: split-plan and saved-card reads stay independent and run alongside the hold read. The hold read can
  // record a provider-mismatch exception, so its dependent queue refresh must wait for that write before reading.
  // These reads still start after the payment preview above, and entry settles only when every one of them has.
  //
  // Split plan: render the server-derived split state once this read settles (M14). This does not close M15's
  // pre-settlement interactive window.
  //
  // Saved cards (Payments-track Slice 3): so the ticket rail's own "Manage saved cards" entry point, independent of
  // Pay's own enabled state, reflects reality as soon as a ticket is resumed, not only once the pay-confirm sheet has
  // been opened at least once.
  //
  // Hold (Payments-track Slice 4, DEC-002): the rail's hold-status badge and "Keep tab open" entry point, same "reflect
  // reality on resume" reasoning as the saved-cards load.
  //
  // Manager exceptions (AU8): the ticket's own banner, so an order flagged by the overnight batch (or by a Walkout
  // blocked on an earlier exception) warns a bartender BEFORE they reach Pay/Close's own refusal, not only after. It is
  // awaited, unlike loadOpenTabs' own fire-and-forget call to the same function: this banner is a safety surface, so
  // entering a ticket must not visibly settle before it is known whether this exact order carries one. Also refreshes
  // the Open Tabs badge as a side effect (one shared tenant-wide read).
  await Promise.all([
    loadActiveSplitPlan().then(() => {
      if (!floorSaleContextIsCurrent(ticketContext)) return;
      renderTicketActions();
    }),
    loadSavedCards(order.id),
    // Hold status, then the exceptions queue (not beside it): the server records a hold_provider_mismatch exception while
    // answering the hold read, so a queue read that overtakes it misses this ticket's banner (UX53). It is also the last
    // read this function awaits, which PAY19's reopen-sweep ordering relies on. MAINE4 keeps the current-ticket guard.
    loadHoldStatus(order.id).then(() => {
      if (!floorSaleContextIsCurrent(ticketContext)) return;
      return refreshManagerExceptions();
    }),
  ]);
}

// ---------------------------------------------------------------------
// Menu loading and the "from" price estimate
// ---------------------------------------------------------------------

async function loadMenu(): Promise<void> {
  const session = requireSession();
  // AU13-F1: captured before the first await below; every checkpoint in
  // this function compares against this, not against the (possibly later,
  // possibly different) session sessionGeneration reflects once execution
  // resumes.
  const gen = sessionGeneration;
  const stock86Revision = stock86.writeRevision;
  // Inventory track Slice 3, Amendment 2026-08-22 (twelfth): the
  // alphabetical menu now sells more than recipe cocktails, so it loads
  // from the one typed union GET .../menu-items (src/api/server.ts's own
  // route), not listMenuDrinks alone. No activeOnly param: defaults to
  // true server-side for every kind in that union, exactly what the
  // bartender menu needs (ALPHA-10 item 3's "the bartender menu shows
  // active drinks only" is enforced server-side here, not by filtering a
  // larger list client-side).
  const [itemsResult, stockListResult, menuStockResult] = await Promise.all([
    getMenuItems(session.apiBaseUrl, session.tenantId),
    // STK1: the 86 list's own product list (the topbar count), read only for
    // a session that can see the 86 list button at all.
    canRecordStockFlag()
      ? listLiquorProducts(session.apiBaseUrl, session.tenantId, { locationId: terminalConfigState?.locationId, activeOnly: true })
      : Promise.resolve(undefined),
    // CORE7: and the drinks and ingredients, for the same count.
    canRecordStockFlag()
      ? getMenuStockState(session.apiBaseUrl, session.tenantId)
      : Promise.resolve(undefined),
  ]);
  // AU13-F1: the session that started this request may have ended (sign-
  // out, idle timeout) while it was in flight. A response for a dead
  // session writes nothing, toasts nothing, and renders nothing.
  if (!sessionIsCurrent(gen)) {
    return;
  }
  if (itemsResult.status !== 200) {
    showToast(
      `Could not load the menu. ${capitalizeServerMessage(itemsResult.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  state.menuItems = itemsResult.body.items;
  // TAPSPEED1: drink details read before this menu are not kept across a reload (sign-in, return from Settings), so a
  // recipe changed in Settings is read again, and read ahead again below.
  state.menuDetailCache.clear();
  // state.menuDrinks stays a cocktail-only derived view: loadCheapestLiquorPrices
  // and tilePriceLabel both key off MenuDrinkListItemDto specifically (its
  // liquor_slots/build_charge_cents/requires_liquor_selection fields have
  // no equivalent on a pour/draft-beer/packaged item), so re-deriving it
  // here keeps both working unchanged rather than porting them onto the
  // union type for no behavioral gain.
  state.menuDrinks = itemsResult.body.items
    .filter((item): item is Extract<MenuItemDto, { kind: "cocktail" }> => item.kind === "cocktail")
    .map((item) => item.menuDrink);
  // CORE12: the pricing model comes WITH the menu. It used to come from GET .../settings, which needs
  // admin.tenant_settings.view; the bartender role does not hold it, so the floor silently fell back to
  // per ounce and printed a rate (and offered pour sizes) for a pour the server prices by shelf tier.
  // The menu response is readable by exactly the sessions that ring drinks.
  state.pricingModel =
    itemsResult.body.pricingModel === "shelf" || itemsResult.body.pricingModel === "per_ounce"
      ? itemsResult.body.pricingModel
      : "per_ounce";
  // ALPHA-08: the second, active-and-inactive-alike menu fetch this used
  // to need (so tab-resume reconstruction could still find a seasonally
  // disabled drink by name) is gone: reconstructOrderLine now reads the
  // resumed line's stored menu_drink_id/liquor_product_id directly
  // (order_lines' own ALPHA-08 columns) instead of searching a cached
  // menu list by name, so a disabled drink's name no longer needs to be
  // resolvable client-side at all. See reconstructOrderLine's own header
  // comment.
  // STK1: a flag the server holds that the menu payload predates is reconciled onto the tiles. A failed read leaves the
  // topbar count blank (render86ListButton), never a wrong zero. REGFIX5: applied as soon as the menu arrives, before the
  // per-category price reads below, so the 86 list has its products as early as the screen does.
  if (
    stockListResult !== undefined &&
    stockListResult.status === 200 &&
    stock86.writeRevision === stock86Revision
  ) {
    applyStock86ProductList(stockListResult.body.liquorProducts);
  }
  await loadCheapestLiquorPrices();
  // AU13-F1: loadCheapestLiquorPrices guards its own writes independently,
  // but this function's own render calls after it need their own check
  // too, since the session can go stale during that inner await and
  // loadCheapestLiquorPrices returning cleanly (because IT found itself
  // stale) says nothing about whether this continuation is still current.
  if (!sessionIsCurrent(gen)) {
    return;
  }
  renderAlphabetStrip();
  renderMenuGrid();
  warmMenuDrinkDetails();
  if (
    menuStockResult !== undefined &&
    menuStockResult.status === 200 &&
    stock86.writeRevision === stock86Revision
  ) {
    applyMenuStockState(menuStockResult.body);
    const since = sinceFromMenuState(menuStockResult.body.drinks, menuStockResult.body.ingredients);
    for (const [key, value] of since) {
      stock86.since.set(key, value);
    }
  }
}

// Inventory track (track/inventory) Slice 3: LiquorProductDto's
// per_ounce_price_cents/pour_ounce_options went nullable this slice
// (null for itemKind "packaged"; see api.ts's own comment). Every
// call site below is inside the COCKTAIL liquor-selection sheet, which
// only ever fetches itemKind "liquor" (loadLiquorProducts/
// loadCheapestLiquorPrices both pass itemKind: "liquor" explicitly, never
// "beer"/"packaged"), and db/schema.ts's
// liquor_products_kind_fields_check guarantees an itemKind "liquor" row
// always carries a non-null per_ounce_price_cents. This assertion is
// therefore unreachable in practice; it fails loud rather than silently
// pricing at $0 if that invariant were ever somehow violated, matching
// this codebase's own "no estimated-price fallback anywhere in the money
// path" rule (SheetState.matchMiss's own comment).
async function loadCheapestLiquorPrices(): Promise<void> {
  const session = requireSession();
  // AU13-F1: captured before the first await below.
  const gen = sessionGeneration;
  // Shelf estimates need recipe-specific governed quotes, never the legacy ounce-rate estimate.
  if (state.pricingModel === "shelf") {
    state.cheapestPerOunceCentsByCategory.clear();
    return;
  }
  const categories = new Set<string>();
  for (const drink of state.menuDrinks) {
    for (const category of drink.liquor_slots) {
      categories.add(category);
    }
  }
  let anyFailed = false;
  const entries = await Promise.all(
    [...categories].map(async (category): Promise<[string, number | undefined]> => {
      const result = await listLiquorProducts(session.apiBaseUrl, session.tenantId, { locationId: terminalConfigState?.locationId,
        category,
        itemKind: "liquor",
      });
      if (result.status !== 200) {
        anyFailed = true;
        return [category, undefined];
      }
      let cheapest: number | undefined;
      for (const product of result.body.liquorProducts) {
        // DEC-208: this estimate feeds the cocktail tile's own "From $X"
        // label, advertising a price for the SAME category/itemKind the
        // liquor selection sheet (loadLiquorProducts) offers a bartender
        // to actually fulfil it with; a hide_on_menu item is excluded
        // from that sheet, so it must not set the advertised floor here
        // either, or "From" could name a price nothing sellable matches.
        const selling = floorProductSelling(product);
        if (!selling || !selling.active || selling.hideOnMenu || selling.state === "unconfigured" || selling.perOuncePriceCents === null) {
          continue;
        }
        const perOunce = selling.perOuncePriceCents;
        if (cheapest === undefined || perOunce < cheapest) {
          cheapest = perOunce;
        }
      }
      return [category, cheapest];
    }),
  );
  // AU13-F1: the session that started this request may have ended while
  // it was in flight. A response for a dead session writes nothing.
  if (!sessionIsCurrent(gen)) {
    return;
  }
  state.cheapestPerOunceCentsByCategory = new Map(
    entries.filter((entry): entry is [string, number] => entry[1] !== undefined),
  );
  if (anyFailed) {
    // Non-fatal: tiles fall back to "See liquor menu" instead of a "From"
    // estimate (tilePriceLabel), so this degrades gracefully. Still worth
    // one toast rather than staying silent, since a total outage here
    // usually means the same outage is about to block adding drinks too.
    // Neutral, not danger: nothing has failed for the bartender yet, this
    // is a heads-up about a degraded estimate, not an error.
    showToast("Could not load some drink price estimates. Check your connection.", "neutral");
  }
}

// ITEM2 Slice 5 (Section 3.4). A user-defined type predicate rather than
// an inline `item.kind === "cocktail"` comparison: TypeScript disables
// automatic discriminated-union narrowing on a union whose discriminant
// property is not a LITERAL type on every member, and MenuItemDto's own
// non-cocktail member now carries `kind: string` (this item's whole
// point: a tenant-added kind cannot be a compile-time literal). An
// inline `===`/`!==` comparison against "cocktail" would silently stop
// narrowing at all, leaving `item.menuDrink`/`item.liquorProduct` typed
// as though either branch might be present; an explicit predicate keeps
// narrowing working by assertion instead of by literal-type inference.
function isCocktailMenuItem(item: MenuItemDto): item is Extract<MenuItemDto, { kind: "cocktail" }> {
  return item.kind === "cocktail";
}

// A tile's price is either the exact price (a drink with no liquor slot:
// the full price is just its build charge) or a "From" estimate at an
// assumed 1.5 oz pour against the CHEAPEST active liquor across every one
// of the drink's allowed categories (ALPHA-10 item 2: a multi-category
// drink like an espresso martini quotes from whichever of vodka or
// tequila is cheapest), clearly marked "From" per P0.4's acceptance
// criterion ("A cocktail tile with variable liquor pricing displays a
// configured starting price rather than a misleading fixed price"). The
// EXACT price is always computed by the real price-quote endpoint once a
// liquor is selected in the sheet; nothing here is ever used to charge a
// guest.
const TILE_ESTIMATE_OUNCES = 1.5;

// RINGUX1: a cocktail tile says what its price means. A drink with no liquor to pick shows its final price. A drink
// that needs a liquor says "Choose liquor" and that the price comes after the selection; a From estimate, when there is
// one, stays the price line (captioned "Choose liquor") and is never presented as final.
const TILE_CAPTION_FINAL = "Final drink price";
const TILE_CAPTION_AFTER_SELECTION = "Price after selection";
const TILE_CHOOSE_LIQUOR = "Choose liquor";
const TILE_NO_PRICE_TEXT = "No price set";
// ZEROPRICE1: the server's own refusal wording, matched here only to name a Choose liquor row.
const NO_PRICE_REASON_FRAGMENT = "has no price yet";

function tilePriceLabel(menuDrink: MenuDrinkListItemDto): {
  text: string;
  variable: boolean;
  caption?: string;
} {
  if (!menuDrink.requires_liquor_selection) {
    // ZEROPRICE1 (DEC-608): the server's own build charge is $0 and there is no liquor to add to it, so the drink has
    // no price and the server refuses to ring it. Say so instead of "$0.00 Final drink price".
    if (menuDrink.build_charge_cents <= 0) {
      return { text: TILE_NO_PRICE_TEXT, variable: true };
    }
    return {
      text: formatCents(menuDrink.build_charge_cents),
      variable: false,
      caption: TILE_CAPTION_FINAL,
    };
  }
  if (state.pricingModel === "shelf") {
    return { text: TILE_CHOOSE_LIQUOR, variable: true, caption: TILE_CAPTION_AFTER_SELECTION };
  }
  let cheapest: number | undefined;
  for (const category of menuDrink.liquor_slots) {
    const categoryCheapest = state.cheapestPerOunceCentsByCategory.get(category);
    if (categoryCheapest !== undefined && (cheapest === undefined || categoryCheapest < cheapest)) {
      cheapest = categoryCheapest;
    }
  }
  if (cheapest === undefined) {
    return { text: TILE_CHOOSE_LIQUOR, variable: true, caption: TILE_CAPTION_AFTER_SELECTION };
  }
  const estimateCents = menuDrink.build_charge_cents + Math.round(TILE_ESTIMATE_OUNCES * cheapest);
  return {
    text: `From ${formatCents(estimateCents)}`,
    variable: true,
    caption: TILE_CHOOSE_LIQUOR,
  };
}

// Inventory track Slice 3, Amendment 2026-08-22 (thirteenth): a pour/
// draft-beer tile's price label. Under "per_ounce," the item's own
// per_ounce_price_cents is an exact, real rate (not an estimate the way a
// cocktail tile's "From" figure is: the tile shows the flat per-ounce
// price, which applies identically no matter which pour size the
// bartender ends up picking in the size sheet). Under "shelf," a
// straight pour is charged one flat price (the bottle's shelf tier plus any size override, unrelated
// to ounces), so the tile shows exactly that price (CORE12, quoted by the server); beer and wine
// under shelf say "Shelf price" because their price depends on the size picked. The size sheet always
// shows the real, server-quoted price per option regardless of which pricing model applies. A
// 'packaged' item's own unit_price_cents is exact and unconditional
// (Amendment twelfth item 3: flat regardless of pricing model).
//
// ITEM2 Slice 5 (Section 3.4): the whole-unit-versus-per-ounce dispatch
// used to be `item.kind === "packaged"`, which a tenant-added whole-unit
// kind's own item.kind (its own value, never "packaged") could never
// match. Generalized to read the PRODUCT'S OWN already-fetched field
// shape instead: `unit_price_cents` is non-null exactly on a whole-unit
// product and null on a per-ounce one (db/schema.ts's liquor_products_
// kind_fields_check trigger guarantees exactly one of the two is set),
// so this needs no separate fetch of the kind's own sale_mechanism.
function floorProductSelling(product: LiquorProductDto) {
  return currentProductSelling(product, terminalConfigState?.locationId);
}

function tilePriceLabelForNonCocktailItem(item: Exclude<MenuItemDto, { kind: "cocktail" }>): {
  text: string;
  variable: boolean;
} {
  const selling = floorProductSelling(item.liquorProduct);
  if (!selling || !selling.active || selling.state === "unconfigured")
    return { text: "Price unavailable", variable: true };
  const unitPriceCents = selling.unitPriceCents;
  if (unitPriceCents !== null) {
    return { text: formatCents(unitPriceCents), variable: false };
  }
  if (state.pricingModel === "shelf") {
    // CORE12: a straight pour is charged one flat price (its shelf tier, plus any size override), so
    // that is what the tile shows. Beer and wine under shelf are priced per size in the size sheet.
    if (item.kind === "pour") {
      const flatCents = item.shelfPourPriceCents;
      return typeof flatCents === "number"
        ? { text: formatCents(flatCents), variable: false }
        : { text: "See menu", variable: true };
    }
    return { text: "Shelf price", variable: true };
  }
  const perOunceCents = selling.perOuncePriceCents;
  if (perOunceCents === null) return { text: "See menu", variable: true };
  // ONB2 S4: a beer or wine tile shows what a serving costs (a 16 oz pint at $0.40 an ounce reads $6.40), not the rate.
  // Spirits and tenant-added kinds keep the rate; the size sheet still shows the server's own quote per size.
  if (item.kind === "beer" || item.kind === "wine") {
    const serving = servingPriceLabel(
      perOunceCents,
      selling.pourOunceOptions === null ? null : [...selling.pourOunceOptions],
    );
    if (serving !== null) return { text: serving.text, variable: serving.variable };
  }
  return { text: `${formatCents(perOunceCents)}/oz`, variable: true };
}

function tilePriceLabelForItem(item: MenuItemDto): {
  text: string;
  variable: boolean;
  caption?: string;
} {
  return isCocktailMenuItem(item)
    ? tilePriceLabel(item.menuDrink)
    : tilePriceLabelForNonCocktailItem(item);
}

// ---------------------------------------------------------------------
// Menu rendering: alphabetical list with an A-Z jump strip (ALPHA-10
// item 1). Spirit-category grouping is gone from the bartender menu; see
// this file's ALPHABET_JUMP_LETTERS / alphaBucketFor header comment for
// the full owner reasoning. Inventory track Slice 3, Amendment
// 2026-08-22 (twelfth): "all appear in the alphabetical menu alongside
// cocktails," so this now sorts/groups state.menuItems (the full union),
// not state.menuDrinks.
// ---------------------------------------------------------------------

function renderAlphabetStrip(): void {
  // UX121: only the letters of the items the Drinks, Bottles and Base filters leave.
  const present = new Set(
    applyMenuFilters(floorMenuItems(), menuFilters).map((item) => alphaBucketFor(item.name)),
  );
  alphabetStrip.innerHTML = "";
  for (const letter of ALPHABET_JUMP_LETTERS) {
    // UX14 (DEC-314, owner ruling 2026-10-07): the strip is one swipeable row
    // of ONLY the letters that have a drink. A dimmed letter that did nothing
    // took a 44px slot and pushed real letters off the row; this supersedes
    // ALPHA-10's "a letter with no drinks stays visible but disabled".
    if (!present.has(letter)) {
      continue;
    }
    const button = document.createElement("button");
    button.type = "button";
    button.className = "alphabet-letter";
    button.dataset["testid"] = "alphabet-letter";
    button.dataset["letter"] = letter;
    button.textContent = letter;
    button.addEventListener("click", () => {
      const section = menuScroll.querySelector<HTMLElement>(
        `[data-letter-section="${CSS.escape(letter)}"]`,
      );
      section?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    alphabetStrip.append(button);
  }
  updateAlphabetStripFade();
}

// UX14: the right edge of the strip fades to say "swipe" while there is more
// to the right, and stops fading once the end is reached (or when every
// letter fits), so the last letter is never dimmed for nothing.
function updateAlphabetStripFade(): void {
  const more =
    alphabetStrip.scrollWidth > alphabetStrip.clientWidth + 1 &&
    alphabetStrip.scrollLeft + alphabetStrip.clientWidth < alphabetStrip.scrollWidth - 1;
  alphabetStrip.classList.toggle("has-more", more);
}
alphabetStrip.addEventListener("scroll", updateAlphabetStripFade, { passive: true });
window.addEventListener("resize", updateAlphabetStripFade);

// Amendment 2026-08-22 (twelfth): "type-appropriate tiles." A cocktail
// tile carries no badge (today's unchanged look); a pour/draft-beer/
// packaged tile gets a small kind badge so a bartender scanning the grid
// can tell a direct-sale item from a cocktail at a glance, before ever
// tapping it. Text only, no icon (per the no-decorative-imagery-standing-
// in-for-real-photos rule; a plain word is not imagery).
//
// ITEM2 Slice 5 (Section 3.4): widened from a closed, TOTAL five-entry
// Record to a Partial<Record<string, string>>, one entry per SEEDED kind
// only, its own established wording preserved verbatim ("Pour", not
// "Liquor," for a straight pour). A tenant-added kind is not a key here;
// its badge falls back to MenuItemDto's own itemKindLabel (see the tile
// render below), the kind's own CONFIGURED label rather than a guess
// derived from its raw stored value.
const NON_COCKTAIL_TILE_BADGE_LABEL: Readonly<Partial<Record<string, string>>> = {
  pour: "Pour",
  beer: "Beer",
  // WIN1. "Wine" rather than "Glass": the badge names WHAT it is, the
  // way the other three do, and the pour-size sheet that opens next is
  // what says how much.
  wine: "Wine",
  // ITEM1 Slice 4: same reasoning as wine above.
  cider: "Cider",
  packaged: "Packaged",
};

// VOC1-F5 Slice 1 (docs/build-briefs/VOC1-F5-stock-states-on-the-floor-
// and-86-blocks-selection.md, Section 2). One plain-word badge per
// liquor-sheet row and non-cocktail menu tile, text only, never color
// alone (this item's own false-greens list): flagged86 always wins
// ("86'd") regardless of quantityState, since a manager's explicit
// assertion is the fact that matters most; then Low; then Out (the
// ledger-zero-but-unflagged case INV6's own header names, "a bottle can
// read 0 without anyone having 86'd it" -- not a PRD-named word, a
// display choice within this criterion's existing data model). "ok" and
// a mixed-unit product's null quantityState both render no badge:
// Available needs none, per the criterion's own wording.
function stockStateBadge(
  product: Pick<LiquorProductDto, "id" | "quantityState" | "flagged86"> &
    Partial<Pick<LiquorProductDto, "par_level" | "onHandInCountUnit">>,
): { label: string; modifier: "flagged" | "low" | "out" } | undefined {
  if (product.flagged86) {
    return { label: "86'd", modifier: "flagged" };
  }
  // NIGHTFIX1 (3): Low means below par, as on Stock levels. A menu tile's product has no par, so it is read from the
  // location's stock list when that has loaded.
  const counted =
    product.par_level !== undefined ? product : stock86.products.find((candidate) => candidate.id === product.id);
  const quantityState = floorQuantityState(product.quantityState, counted?.onHandInCountUnit, counted?.par_level);
  if (quantityState === "low") {
    return { label: "Low", modifier: "low" };
  }
  if (quantityState === "out_of_stock") {
    return { label: "Out", modifier: "out" };
  }
  return undefined;
}

function isMenuItemFlagged86(item: MenuItemDto): boolean {
  return isCocktailMenuItem(item) ? item.flagged86 : item.liquorProduct.flagged86;
}

// VOC1-F5 Slice 3. Whether the CURRENT session may sell past an 86 flag at
// all; used only to decide whether the tile/row itself renders disabled
// and whether the "Sell anyway" affordance opens, never to decide what a
// request may do -- the server's own requirePermission call is the real
// gate. Reads state.session directly (never requireSession()'s throwing
// form): a render/teardown path calling this with no signed-in session at
// all must fail closed, not throw.
function canOverride86(): boolean {
  return (
    state.session !== undefined && hasPermission(state.session, STOCK_FLAG_OVERRIDE_PERMISSION)
  );
}

// VOC1-F5 Slice 3, AU14-F15 / DEC-276 (docs/build-briefs/AU14-F15-86-override-reason.md;
// design/mockups/dec276-required-reasons-6 to -9). Selling an 86'd item asks WHY
// first: the shared required-reason sheet (openReasonSheet, a second sheet inside
// #choice-scrim, because check:chrome-ratchet refuses a new scrim), with "Back in
// stock" on top for a session that can clear the 86, then the reasons, then one
// primary button that stays blocked until a tile is picked. The button says the
// truth about who has to approve, decided from the permission snapshot, never a
// role name: "Sell anyway" when this session can override alone, "Ask a manager to
// approve" when its inv.stock_flag.override is approval_required. The reason comes
// BEFORE any manager PIN. The server is the real gate either way.
function override86NeedsApproval(): boolean {
  return state.session?.permissions[STOCK_FLAG_OVERRIDE_PERMISSION] === "approval_required";
}

async function openOverride86ConfirmSheet(
  product: Pick<LiquorProductDto, "id" | "name">,
  onConfirm: (choice: { reasonId: string; reasonLabel: string }) => void,
  /** CORE7: for a menu drink. `clear` is Clear 86 for the drink's OWN flag (absent when it is 86'd only
   * through an ingredient, which is cleared on the ingredient); `flaggedVia` names those ingredients. */
  drink?: { clear: (() => void) | undefined; flaggedVia: readonly string[] },
): Promise<void> {
  const session = requireSession();
  const gen = sessionGeneration;
  const result = await listActionReasons(session.apiBaseUrl, session.tenantId, {
    kind: "stock_override",
  });
  if (gen !== sessionGeneration || sessionOrUndefined() === undefined) {
    return;
  }
  const reasons: ActionReasonDto[] =
    result.status === 200 ? sortByName(result.body.actionReasons, (reason) => reason.label) : [];
  if (result.status !== 200 || reasons.length === 0) {
    showToast(
      result.status === 200
        ? "No reasons are set up for selling an 86'd item. Ask an admin to add one in Settings."
        : "Could not load the reasons. Check your connection and try again.",
      "danger",
    );
    return;
  }
  // STK1 (DEC-269): a session that also holds inv.stock_flag.record gets
  // "Clear 86" first, the fastest clear. Keyed off its OWN permission, never
  // implied by override: the permissions are independent.
  const canClear = canRecordStockFlag() && (drink === undefined || drink.clear !== undefined);
  const since = stock86SinceFor(product.id);
  const needsApproval = override86NeedsApproval();
  openReasonSheet({
    title: `${product.name} is 86'd`,
    subtitle:
      since === undefined
        ? drink !== undefined && drink.flaggedVia.length > 0
          ? `Not available: ${drink.flaggedVia.join(", ")} ${drink.flaggedVia.length === 1 ? "is" : "are"} 86'd.`
          : "This item is flagged 86."
        : `Marked out at ${formatStock86Time(since.since)}${since.actorName === null ? "" : ` by ${since.actorName}`}.`,
    heading: "Or sell it anyway. Why?",
    reasons,
    confirmLabel: needsApproval ? "Ask a manager to approve" : "Sell anyway",
    pickedNote: needsApproval
      ? "Needs a manager's approval. You pick the manager and they enter their PIN."
      : "Written down with your name, the reason and the time.",
    pickedNoteTone: needsApproval ? "info" : "plain",
    extraAction: canClear
      ? {
          label: "Clear 86",
          note: "Use this if there is more on the shelf. It clears the 86 for everyone.",
          testid: "override86-back-in-stock",
          onPick: () =>
            drink?.clear !== undefined
              ? drink.clear()
              : void setStockFlag({ productId: product.id, name: product.name, flagged: false }),
        }
      : undefined,
    onConfirm: (picked) => onConfirm({ reasonId: picked.id, reasonLabel: picked.label }),
  });
}

// ---------------------------------------------------------------------
// STK1 (docs/build-briefs/STK1-86-setter.md; DEC-269; design/mockups/
// core-86-setter-*). Marking an item 86'd and back in stock: one tap, an
// Undo, no confirmation, no reason, never auto-cleared. The write is the
// existing POST .../inventory/stock-flags; this block is the floor panel,
// the topbar entry and the one shared setStockFlag every surface calls
// (Settings too), so a failed call reverts the SAME way everywhere and no
// surface is ever left showing a state the server did not accept.
// ---------------------------------------------------------------------

interface Stock86State {
  /** Every active product (the same list the liquor sheet reads), with its
   * flagged86 and quantityState. The source of the topbar count and the
   * panel's two lists. */
  products: LiquorProductDto[];
  /** CORE7: the menu drinks and ingredients of this location, with their own and effective 86. */
  drinks: MenuStockDrinkDto[];
  ingredients: MenuStockIngredientDto[];
  /** False until a list load has succeeded; the topbar then shows no count
   * rather than a wrong zero. */
  loaded: boolean;
  /** CORE7: the same for the drink and ingredient read. The count and the panel need both. */
  menuStateLoaded: boolean;
  /** When and by whom each out item was set, where this session may know:
   * the history read (inv.stock_flag.view) plus anything set here. */
  since: Map<string, Stock86Since>;
  /** CORE7-F1: every stored set and clear per entry key, read on first use (a History tap) and dropped by any
   * write, so it is never older than the last thing this device did. Undefined until read. */
  history: Map<string, Stock86HistoryEvent[]> | undefined;
  category: string;
  query: string;
  /** Rows just marked out (highlighted at the top) or just cleared. */
  freshOut: Set<string>;
  freshBack: Set<string>;
  /** Products with a write in flight: their buttons are disabled so a
   * double tap cannot send two writes. */
  pending: Set<string>;
  /** Bumped every time a write starts or settles. A list read that began
   * before a write and lands after it describes the server BEFORE that
   * write, and must not overwrite the optimistic or confirmed answer; the
   * read compares this against the value it captured and drops itself. */
  writeRevision: number;
}

function emptyStock86State(): Stock86State {
  return {
    products: [],
    drinks: [],
    ingredients: [],
    loaded: false,
    menuStateLoaded: false,
    since: new Map(),
    history: undefined,
    category: "",
    query: "",
    freshOut: new Set(),
    freshBack: new Set(),
    pending: new Set(),
    writeRevision: 0,
  };
}

let stock86: Stock86State = emptyStock86State();
let stock86ToastTimer: ReturnType<typeof setTimeout> | undefined;
const STOCK86_TOAST_MS = 8000;

function resetStock86State(): void {
  stock86 = emptyStock86State();
  stock86SearchInput.value = "";
  render86ListButton();
}

/** Exported: Settings (admin.ts) shares this one write path and gate. */
export function canRecordStockFlag(): boolean {
  return state.session !== undefined && hasPermission(state.session, STOCK_FLAG_RECORD_PERMISSION);
}

/** The 86 list's count (UX119: on the Reports hub button, no longer the topbar), only with inv.stock_flag.record.
 * The count is the number of items out now; zero renders the grey variant, and an unloaded list shows N/A (UX100:
 * the count always draws). */
function render86ListButton(): void {
  // UX119: the button lives on the Reports hub (refreshAccountMenuRows owns its visibility); this only keeps its
  // count current.
  if (!canRecordStockFlag()) {
    return;
  }
  if (!stock86.loaded || !stock86.menuStateLoaded) {
    // UX100: the count always draws. Before the first read, or after a failed one, it says a dash, never a blank
    // and never a zero it has not read.
    stock86CountEl.hidden = false;
    stock86CountEl.textContent = "-";
    stock86CountEl.classList.remove("account-menu-badge-red");
    stock86CountEl.classList.add("account-menu-badge-none");
    return;
  }
  const outCount = stock86AllEntries().filter((entry) => entry.flagged86).length;
  stock86CountEl.hidden = false;
  stock86CountEl.textContent = String(outCount);
  stock86CountEl.classList.toggle("account-menu-badge-none", outCount === 0);
  stock86CountEl.classList.toggle("account-menu-badge-red", outCount > 0);
}

/** Every inventory item, drink and ingredient as one list of entries (CORE7). */
function stock86AllEntries(): Stock86Entry[] {
  return stock86Entries(stock86.products, stock86.drinks, stock86.ingredients);
}

/** Brings the cocktail tiles in step with the drinks list; true when a tile changed. */
function syncCocktailTiles(): boolean {
  let changed = false;
  for (const drink of stock86.drinks) {
    for (const item of state.menuItems) {
      if (
        isCocktailMenuItem(item) &&
        item.id === drink.menuDrinkId &&
        (item.flagged86 !== drink.flagged86 || item.flagged86Via.join("|") !== drink.via.join("|"))
      ) {
        item.flagged86 = drink.flagged86;
        item.flagged86Via = drink.via;
        changed = true;
      }
    }
  }
  return changed;
}

/** CORE7: sets one drink's or ingredient's flag on every local copy. An ingredient flip recomputes every drink
 * that uses it, so the tiles, the panel and the count move together. */
function applyMenuStock86Locally(
  kind: "menu_drink" | "ingredient",
  subjectId: string,
  flagged: boolean,
): void {
  if (kind === "menu_drink") {
    const drink = stock86.drinks.find((candidate) => candidate.menuDrinkId === subjectId);
    if (drink !== undefined) {
      drink.own = flagged;
    }
  } else {
    const ingredient = stock86.ingredients.find((candidate) => candidate.key === subjectId);
    if (ingredient !== undefined) {
      ingredient.flagged86 = flagged;
    }
  }
  const flaggedLabels = new Map(
    stock86.ingredients
      .filter((ingredient) => ingredient.flagged86)
      .map((ingredient) => [ingredient.key, ingredient.label] as const),
  );
  for (const drink of stock86.drinks) {
    const next = recomputeDrink86({
      own: drink.own,
      ingredientKeys: drink.ingredientKeys,
      viaItems: drink.viaItems,
      flaggedIngredientLabels: flaggedLabels,
    });
    drink.flagged86 = next.flagged86;
    drink.via = next.via;
  }
  if (syncCocktailTiles()) {
    const scrollTop = menuScroll.scrollTop;
    renderMenuGrid();
    renderAlphabetStrip();
    menuScroll.scrollTop = scrollTop;
  }
  render86ListButton();
  if (!stock86Backdrop.hidden) {
    renderStock86Panel();
  }
}

/** Takes the server's drinks and ingredients as the truth. */
function applyMenuStockState(body: {
  drinks: MenuStockDrinkDto[];
  ingredients: MenuStockIngredientDto[];
}): void {
  stock86.drinks = body.drinks;
  stock86.ingredients = body.ingredients;
  stock86.menuStateLoaded = true;
  if (syncCocktailTiles()) {
    const scrollTop = menuScroll.scrollTop;
    renderMenuGrid();
    renderAlphabetStrip();
    menuScroll.scrollTop = scrollTop;
  }
  render86ListButton();
}

/** Sets the flag on every local copy of one product: this module's list,
 * the floor menu's tiles, and an open liquor sheet's rows. */
function applyStock86Locally(productId: string, flagged: boolean): void {
  const own = stock86.products.find((product) => product.id === productId);
  if (own !== undefined) {
    own.flagged86 = flagged;
  }
  let menuChanged = false;
  for (const item of state.menuItems) {
    if (!isCocktailMenuItem(item) && item.liquorProduct.id === productId) {
      item.liquorProduct.flagged86 = flagged;
      menuChanged = true;
    }
  }
  if (menuChanged) {
    const scrollTop = menuScroll.scrollTop;
    renderMenuGrid();
    renderAlphabetStrip();
    menuScroll.scrollTop = scrollTop;
  }
  if (state.sheet !== undefined) {
    const sheetRow = state.sheet.liquorProducts.find((product) => product.id === productId);
    if (sheetRow !== undefined) {
      sheetRow.flagged86 = flagged;
      renderLiquorList();
    }
  }
  render86ListButton();
  if (!stock86Backdrop.hidden) {
    renderStock86Panel();
  }
}

function hideStock86Toast(): void {
  if (stock86ToastTimer !== undefined) {
    clearTimeout(stock86ToastTimer);
    stock86ToastTimer = undefined;
  }
  stock86ToastEl.hidden = true;
}

/** The 8 second toast with an Undo, at the UX47 position. */
function showStock86Toast(message: string, onUndo: () => void, actionLabel = "Undo"): void {
  hideStock86Toast();
  toastEl.hidden = true;
  dockToastSlot();
  stock86ToastText.textContent = message;
  stock86ToastUndo.textContent = actionLabel;
  stock86ToastUndo.onclick = () => {
    hideStock86Toast();
    onUndo();
  };
  stock86ToastEl.hidden = false;
  stock86ToastTimer = setTimeout(hideStock86Toast, STOCK86_TOAST_MS);
}

/** The one write path every 86 set or clear runs through, for an inventory item, a menu drink and an ingredient.
 * Optimistic: the local copies change first, the write goes out, and a refusal or network failure puts every copy
 * back and shows the error toast. `key` is the entry key (stock86.pending, stock86.since, the panel rows).
 * Returns whether the server accepted it. */
async function performStock86Write(params: {
  key: string;
  name: string;
  flagged: boolean;
  /** Changes this module's copy and the menu tiles. */
  applyLocal: (flagged: boolean) => void;
  /** `onLocalChange` lets a caller keep its own copy (Settings' list) in step. */
  onLocalChange?: (flagged: boolean) => void;
  send: (session: Session) => Promise<ApiResult<Partial<ApiErrorBody>>>;
  /** False for an Undo itself, which confirms with a plain toast. */
  offerUndo?: boolean;
  /** Runs the opposite write; the toast's Undo button. */
  redo: (flagged: boolean) => void;
}): Promise<boolean> {
  const session = state.session;
  if (session === undefined || !canRecordStockFlag()) {
    return false;
  }
  if (stock86.pending.has(params.key)) {
    return false;
  }
  const gen = sessionGeneration;
  const previousSince = stock86.since.get(params.key);
  stock86.pending.add(params.key);
  stock86.writeRevision += 1;
  const change = (flagged: boolean): void => {
    params.applyLocal(flagged);
    params.onLocalChange?.(flagged);
  };
  if (params.flagged) {
    stock86.freshOut.add(params.key);
    stock86.freshBack.delete(params.key);
  } else {
    stock86.freshBack.add(params.key);
    stock86.freshOut.delete(params.key);
  }
  change(params.flagged);
  const result = await params.send(session);
  if (!sessionIsCurrent(gen)) {
    return false;
  }
  stock86.pending.delete(params.key);
  stock86.writeRevision += 1;
  stock86.history = undefined;
  if (result.status !== 200 && result.status !== 201) {
    stock86.freshOut.delete(params.key);
    stock86.freshBack.delete(params.key);
    if (previousSince !== undefined) {
      stock86.since.set(params.key, previousSince);
    }
    change(!params.flagged);
    showToast(
      `Could not ${params.flagged ? "mark" : "clear"} ${params.name}. ${capitalizeServerMessage(result.body.message ?? result.body.error ?? "Check your connection.")}`,
      "danger",
    );
    return false;
  }
  if (params.flagged) {
    stock86.since.set(params.key, { since: new Date(), actorName: session.employeeId });
  } else {
    stock86.since.delete(params.key);
  }
  // The caller's own copy re-renders once more now that "since by" is known
  // (the optimistic pass above ran before it existed). Idempotent.
  params.onLocalChange?.(params.flagged);
  if (params.offerUndo === false) {
    hideStock86Toast();
    showToast(
      params.flagged ? markedOutToast(params.name) : backInStockToast(params.name),
      "success",
    );
  } else {
    showStock86Toast(
      params.flagged ? markedOutToast(params.name) : backInStockToast(params.name),
      () => params.redo(!params.flagged),
    );
  }
  if (!stock86Backdrop.hidden) {
    renderStock86Panel();
  }
  return true;
}

/** Sets or clears one inventory item's 86. Shared by every surface (the floor panel, Settings). */
export async function setStockFlag(params: {
  productId: string;
  name: string;
  flagged: boolean;
  onLocalChange?: (flagged: boolean) => void;
  /** False for an Undo itself, which confirms with a plain toast. */
  offerUndo?: boolean;
}): Promise<boolean> {
  return performStock86Write({
    key: params.productId,
    name: params.name,
    flagged: params.flagged,
    applyLocal: (flagged) => applyStock86Locally(params.productId, flagged),
    ...(params.onLocalChange !== undefined ? { onLocalChange: params.onLocalChange } : {}),
    send: (session) =>
      recordStockFlag(session.apiBaseUrl, session.tenantId, {
        liquorProductId: params.productId,
        flagged: params.flagged,
      }),
    ...(params.offerUndo !== undefined ? { offerUndo: params.offerUndo } : {}),
    redo: (flagged) =>
      void setStockFlag({
        productId: params.productId,
        name: params.name,
        flagged,
        ...(params.onLocalChange !== undefined ? { onLocalChange: params.onLocalChange } : {}),
        offerUndo: false,
      }),
  });
}

/** CORE7: sets or clears a menu drink's or an ingredient's 86 at this location. `subjectId` is the menu drink id, or
 * the ingredient key. */
export async function setMenuStockFlag(params: {
  kind: "menu_drink" | "ingredient";
  subjectId: string;
  name: string;
  flagged: boolean;
  offerUndo?: boolean;
  /** Lets Settings keep its own rows in step, as setStockFlag's does. */
  onLocalChange?: (flagged: boolean) => void;
}): Promise<boolean> {
  const key =
    params.kind === "menu_drink"
      ? stock86DrinkKey(params.subjectId)
      : stock86IngredientKey(params.subjectId);
  return performStock86Write({
    key,
    name: params.name,
    flagged: params.flagged,
    applyLocal: (flagged) => applyMenuStock86Locally(params.kind, params.subjectId, flagged),
    ...(params.onLocalChange !== undefined ? { onLocalChange: params.onLocalChange } : {}),
    send: (session) =>
      recordMenuStockFlag(
        session.apiBaseUrl,
        session.tenantId,
        params.kind === "menu_drink"
          ? { kind: "menu_drink", menuDrinkId: params.subjectId, flagged: params.flagged }
          : { kind: "ingredient", ingredientName: params.name, flagged: params.flagged },
      ),
    ...(params.offerUndo !== undefined ? { offerUndo: params.offerUndo } : {}),
    redo: (flagged) =>
      void setMenuStockFlag({
        kind: params.kind,
        subjectId: params.subjectId,
        name: params.name,
        flagged,
        offerUndo: false,
        ...(params.onLocalChange !== undefined ? { onLocalChange: params.onLocalChange } : {}),
      }),
  });
}

/** Takes a fresh product list as the truth (a menu load or a panel open)
 * and brings the menu tiles in step with it. */
function applyStock86ProductList(products: LiquorProductDto[]): void {
  stock86.products = products;
  stock86.loaded = true;
  let menuChanged = false;
  for (const product of products) {
    for (const item of state.menuItems) {
      if (
        !isCocktailMenuItem(item) &&
        item.liquorProduct.id === product.id &&
        item.liquorProduct.flagged86 !== product.flagged86
      ) {
        item.liquorProduct.flagged86 = product.flagged86;
        menuChanged = true;
      }
    }
  }
  if (menuChanged) {
    const scrollTop = menuScroll.scrollTop;
    renderMenuGrid();
    renderAlphabetStrip();
    menuScroll.scrollTop = scrollTop;
  }
  render86ListButton();
}

async function refreshStock86Data(): Promise<void> {
  const session = state.session;
  if (session === undefined) {
    return;
  }
  const gen = sessionGeneration;
  const revision = stock86.writeRevision;
  const canView = hasPermission(session, STOCK_FLAG_VIEW_PERMISSION);
  const [productsResult, flagsResult, menuStockResult] = await Promise.all([
    listLiquorProducts(session.apiBaseUrl, session.tenantId, { locationId: terminalConfigState?.locationId, activeOnly: true }),
    canView
      ? listStockFlags(session.apiBaseUrl, session.tenantId, { latestOnly: true })
      : Promise.resolve(undefined),
    getMenuStockState(session.apiBaseUrl, session.tenantId),
  ]);
  if (!sessionIsCurrent(gen)) {
    return;
  }
  if (productsResult.status !== 200) {
    showToast(
      `Could not load the 86 list. ${capitalizeServerMessage(productsResult.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  if (stock86.writeRevision !== revision) {
    // A write started or settled while this read was in flight, so it
    // describes the server before that write. The write already updated
    // every local copy; read again rather than overwrite it.
    void refreshStock86Data();
    return;
  }
  applyStock86ProductList(productsResult.body.liquorProducts);
  // The history read is best-effort: without it the "Out since" line is
  // simply absent for items this session did not set itself.
  if (flagsResult !== undefined && flagsResult.status === 200) {
    stock86.since = sinceByProduct(flagsResult.body.stockFlags);
  }
  // CORE7: the drinks and ingredients (who and when come back only for a session holding view).
  if (menuStockResult.status === 200) {
    applyMenuStockState(menuStockResult.body);
    for (const [key, value] of sinceFromMenuState(
      menuStockResult.body.drinks,
      menuStockResult.body.ingredients,
    )) {
      stock86.since.set(key, value);
    }
  } else {
    showToast(
      `Could not load the drinks and ingredients on the 86 list. ${capitalizeServerMessage(menuStockResult.body.message ?? "Check your connection.")}`,
      "danger",
    );
  }
  if (!stock86Backdrop.hidden) {
    renderStock86Panel();
  }
}

/** When and by whom an item was marked out, where this session knows
 * (Settings' editor line reads this). */
export function stock86SinceFor(productId: string): Stock86Since | undefined {
  return stock86.since.get(productId);
}

/** Reads the flag history (inv.stock_flag.view only) into the same map the
 * panel uses, so the panel and Settings never disagree about who and when.
 * A session without the permission, or a failed read, changes nothing. */
export async function loadStock86Since(): Promise<void> {
  const session = state.session;
  if (session === undefined || !hasPermission(session, STOCK_FLAG_VIEW_PERMISSION)) {
    return;
  }
  const gen = sessionGeneration;
  const revision = stock86.writeRevision;
  const result = await listStockFlags(session.apiBaseUrl, session.tenantId, { latestOnly: true });
  // A read that predates a write must not replace what the write recorded.
  if (!sessionIsCurrent(gen) || result.status !== 200 || stock86.writeRevision !== revision) {
    return;
  }
  stock86.since = sinceByProduct(result.body.stockFlags);
}

/** CORE7-F1: the drinks and ingredients of this location as 86 entries, for Settings > Stock levels. Empty until
 * loadMenuStock86 has succeeded. */
export function stock86MenuEntries(): Stock86Entry[] {
  return stock86Entries([], stock86.drinks, stock86.ingredients);
}

/** CORE7-F1: reads the location's drinks and ingredients (and who and when, where this session may know) for
 * Settings. Returns false when the read failed, so Settings shows no rows rather than a wrong empty list. */
export async function loadMenuStock86(): Promise<boolean> {
  const session = state.session;
  if (session === undefined || !canRecordStockFlag()) {
    return false;
  }
  const gen = sessionGeneration;
  const revision = stock86.writeRevision;
  const result = await getMenuStockState(session.apiBaseUrl, session.tenantId);
  if (!sessionIsCurrent(gen) || stock86.writeRevision !== revision) {
    // A write landed while this read was in flight; its answer already updated every copy.
    return stock86.menuStateLoaded;
  }
  if (result.status !== 200) {
    return false;
  }
  applyMenuStockState(result.body);
  for (const [key, value] of sinceFromMenuState(result.body.drinks, result.body.ingredients)) {
    stock86.since.set(key, value);
  }
  return true;
}

/** Who and when are history: only a session holding inv.stock_flag.view gets a History control at all. */
export function canViewStock86History(): boolean {
  return state.session !== undefined && hasPermission(state.session, STOCK_FLAG_VIEW_PERMISSION);
}

/** True once the history has been read and no write since has made it stale. */
export function stock86HistoryIsCurrent(): boolean {
  return stock86.history !== undefined;
}

export async function ensureStock86History(): Promise<boolean> {
  const session = state.session;
  if (session === undefined || !canViewStock86History()) {
    return false;
  }
  const gen = sessionGeneration;
  // REGFIX6: a write that lands while a read is in flight makes that read stale and it is dropped. The read is then made
  // again, so a person who taps Out right after opening the panel still gets a history that includes their own tap
  // (before this, the dropped read left the panel on "No 86 history yet." for good). Bounded, so a busy list cannot loop.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (stock86.history !== undefined) {
      return true;
    }
    const revision = stock86.writeRevision;
    const [productFlags, menuState] = await Promise.all([
      listStockFlags(session.apiBaseUrl, session.tenantId, {}),
      getMenuStockState(session.apiBaseUrl, session.tenantId, { history: true }),
    ]);
    if (!sessionIsCurrent(gen)) {
      return false;
    }
    if (productFlags.status !== 200 || menuState.status !== 200) {
      return false;
    }
    if (stock86.writeRevision === revision) {
      stock86.history = historyByEntryKey(productFlags.body.stockFlags, menuState.body.history ?? []);
    }
  }
  return stock86.history !== undefined;
}

/** The 86 history of one entry (an item id, `drink:<id>`, `ingredient:<key>`), newest first, or N/A. Read after
 * ensureStock86History. */
export function stock86HistoryLinesFor(key: string): string[] {
  return historyLines(stock86.history?.get(key));
}

/** CORE7-F1: after a receipt or a count touched these items, offers to clear any that are 86'd. One toast with a
 * "Clear 86" button; ignoring it leaves every 86 exactly as it was (never automatic). `lead` keeps the
 * caller's own confirmation in the same line, since this toast takes the slot the caller's toast was in. */
export async function offerClearStock86(productIds: readonly string[], lead = ""): Promise<void> {
  const session = state.session;
  if (session === undefined || !canRecordStockFlag() || productIds.length === 0) {
    return;
  }
  const gen = sessionGeneration;
  const result = await listLiquorProducts(session.apiBaseUrl, session.tenantId, { locationId: terminalConfigState?.locationId,
    activeOnly: true,
  });
  if (!sessionIsCurrent(gen) || result.status !== 200) {
    return;
  }
  const offers = clearOfferProducts(productIds, result.body.liquorProducts);
  if (offers.length === 0) {
    return;
  }
  const message = lead === "" ? clearOfferText(offers) : `${lead} ${clearOfferText(offers)}`;
  showStock86Toast(
    message,
    () => {
      for (const offer of offers) {
        void setStockFlag({
          productId: offer.id,
          name: offer.name,
          flagged: false,
          offerUndo: false,
        });
      }
    },
    "Clear 86",
  );
}

/** CORE7-F2: after an invoice is confirmed, offers to clear the 86 of any product on its confirmed lines. The
 * lines name purchase order lines; the order maps them to products. Same helper, same permission gate. */
async function offerClearForConfirmedInvoice(
  orderId: string,
  purchaseOrderLineIds: readonly string[],
): Promise<void> {
  const session = state.session;
  if (session === undefined || !canRecordStockFlag() || purchaseOrderLineIds.length === 0) {
    return;
  }
  const result = await getPurchaseOrder(session.apiBaseUrl, session.tenantId, orderId);
  if (result.status !== 200) {
    return;
  }
  const wanted = new Set(purchaseOrderLineIds);
  const productIds = result.body.order.lines
    .filter((line) => wanted.has(line.id))
    .map((line) => line.liquorProductId);
  await offerClearStock86(productIds, "Invoice confirmed.");
}

function openStock86Panel(): void {
  if (!canRecordStockFlag()) {
    return;
  }
  stock86.category = "";
  stock86.query = "";
  stock86.freshOut.clear();
  stock86.freshBack.clear();
  stock86SearchInput.value = "";
  if (stock86.loaded && stock86.menuStateLoaded) {
    stock86Backdrop.hidden = false;
    renderStock86Panel();
    stock86DoneButton.focus();
    void refreshStock86Data();
    return;
  }
  // REGFIX5: the first read has not landed (the screen was only just loaded). The list opens when it has, with every
  // category chip drawn, instead of opening on "Loading the 86 list..." and a lone "All" chip. A failed read still
  // opens the list, with the toast the read raises.
  if (stock86Opening) {
    return;
  }
  stock86Opening = true;
  void refreshStock86Data().finally(() => {
    stock86Opening = false;
    stock86Backdrop.hidden = false;
    renderStock86Panel();
    stock86DoneButton.focus();
  });
}

/** REGFIX5: true while the 86 list waits for its first read, so a second tap does not start a second read. */
let stock86Opening = false;

function closeStock86Panel(): void {
  stock86Backdrop.hidden = true;
}

/** CORE7-F1: the History button and the lines it opens under the row. The lines are read on the first tap. */
export function buildStock86HistoryControl(row: HTMLElement, key: string): HTMLElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "stock86-btn-history";
  button.dataset["testid"] = "stock86-history-toggle";
  button.textContent = "History";
  button.setAttribute("aria-expanded", "false");
  button.addEventListener("click", () => {
    const open = row.querySelector<HTMLElement>(".stock86-history");
    if (open !== null) {
      open.remove();
      button.setAttribute("aria-expanded", "false");
      return;
    }
    button.disabled = true;
    void ensureStock86History().then((ok) => {
      button.disabled = false;
      if (!ok) {
        showToast("Could not load the history.", "danger");
        return;
      }
      const list = document.createElement("ul");
      list.className = "stock86-history";
      list.dataset["testid"] = "stock86-history";
      for (const line of stock86HistoryLinesFor(key)) {
        const item = document.createElement("li");
        item.textContent = line;
        list.append(item);
      }
      row.append(list);
      button.setAttribute("aria-expanded", "true");
    });
  });
  return button;
}

/** One row of the 86 list. `kind` of row: out (86'd, with Clear 86 when this entry's own flag is set), a
 * candidate (Mark 86), or out of stock (no stock on hand, nobody 86'd it: information only). */
function buildStock86Row(params: {
  entry: Stock86Entry;
  subText: string | undefined;
  row: "out" | "candidate" | "outOfStock";
}): HTMLElement {
  const { entry } = params;
  const row = document.createElement("div");
  row.className = "stock86-row";
  row.dataset["testid"] =
    params.row === "out"
      ? "stock86-out-row"
      : params.row === "outOfStock"
        ? "stock86-oos-row"
        : "stock86-candidate-row";
  row.dataset["stock86Kind"] = entry.kind;
  if (entry.kind === "product") {
    row.dataset["liquorProductId"] = entry.subjectId;
  } else if (entry.kind === "drink") {
    row.dataset["menuDrinkId"] = entry.subjectId;
  } else {
    row.dataset["ingredientKey"] = entry.subjectId;
  }
  if (params.row === "out" && stock86.freshOut.has(entry.id)) {
    row.classList.add("stock86-row--fresh");
  }
  const main = document.createElement("div");
  main.className = "stock86-row-main";
  const name = document.createElement("div");
  name.className = "stock86-row-name";
  name.textContent = entry.name;
  main.append(name);
  if (params.subText !== undefined) {
    const sub = document.createElement("div");
    sub.className = "stock86-row-sub";
    sub.textContent = params.subText;
    main.append(sub);
  }
  row.append(main);
  // An out-of-stock row is information; a drink 86'd only through an ingredient is cleared on the ingredient.
  // CORE7-F1: a History control on a row in "Out right now", for a session that may see who and when.
  if (params.row === "out" && canViewStock86History()) {
    row.append(buildStock86HistoryControl(row, entry.id));
  }
  const hasButton = params.row === "candidate" || (params.row === "out" && entry.own);
  if (hasButton) {
    const out = params.row === "out";
    const button = document.createElement("button");
    button.type = "button";
    button.disabled = stock86.pending.has(entry.id);
    button.className = out ? "stock86-btn-back" : "stock86-btn-mark";
    button.dataset["testid"] = out ? "stock86-back-in-stock" : "stock86-mark-out";
    button.textContent = out ? "Clear 86" : "Mark 86";
    button.setAttribute("aria-label", `${out ? "Clear 86" : "Mark 86"}: ${entry.name}`);
    button.addEventListener("click", () => {
      if (entry.kind === "product") {
        void setStockFlag({ productId: entry.subjectId, name: entry.name, flagged: !out });
      } else {
        void setMenuStockFlag({
          kind: entry.kind === "drink" ? "menu_drink" : "ingredient",
          subjectId: entry.subjectId,
          name: entry.name,
          flagged: !out,
        });
      }
    });
    row.append(button);
  }
  return row;
}

function stock86ProductCategoryLabel(category: string): string {
  return stock86CategoryLabel(category, categoryLabel);
}

function renderStock86Panel(): void {
  const entries = stock86AllEntries();
  const ready = stock86.loaded && stock86.menuStateLoaded;
  const out = outRightNow(entries, stock86.since);
  stock86OutCountEl.textContent = String(out.length);
  stock86OutListEl.innerHTML = "";
  if (!ready) {
    const loading = document.createElement("p");
    loading.className = "stock86-empty";
    loading.textContent = "Loading the 86 list...";
    stock86OutListEl.append(loading);
  } else if (out.length === 0) {
    const none = document.createElement("p");
    none.className = "stock86-empty";
    none.dataset["testid"] = "stock86-out-empty";
    none.textContent = "Nothing is 86'd right now.";
    stock86OutListEl.append(none);
  }
  for (const entry of out) {
    stock86OutListEl.append(
      buildStock86Row({
        entry,
        subText: outRowSubText(entry, stock86.since.get(entry.id)),
        row: "out",
      }),
    );
  }

  // Out of stock: the tile's OUT badge, as a list, so the badge and the list are one answer.
  const outOfStock = outOfStockEntries(entries);
  stock86OosCountEl.textContent = String(outOfStock.length);
  stock86OosListEl.innerHTML = "";
  if (ready && outOfStock.length === 0) {
    const none = document.createElement("p");
    none.className = "stock86-empty";
    none.dataset["testid"] = "stock86-oos-empty";
    none.textContent = "Nothing is out of stock.";
    stock86OosListEl.append(none);
  }
  for (const entry of outOfStock) {
    stock86OosListEl.append(
      buildStock86Row({ entry, subText: "No stock on hand", row: "outOfStock" }),
    );
  }

  // Category chips: All plus the categories some active item has.
  const categories = candidateCategories(entries, stock86ProductCategoryLabel);
  if (stock86.category !== "" && !categories.includes(stock86.category)) {
    stock86.category = "";
  }
  stock86ChipsEl.innerHTML = "";
  for (const value of ["", ...categories]) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "stock86-chip";
    chip.dataset["testid"] = "stock86-chip";
    chip.dataset["value"] = value;
    chip.setAttribute("aria-pressed", value === stock86.category ? "true" : "false");
    chip.textContent = value === "" ? "All" : stock86ProductCategoryLabel(value);
    chip.addEventListener("click", () => {
      stock86.category = value;
      renderStock86Panel();
    });
    stock86ChipsEl.append(chip);
  }

  const candidates = markOutCandidates(entries, {
    category: stock86.category,
    query: stock86.query,
  });
  stock86CandidateListEl.innerHTML = "";
  if (ready && candidates.length === 0) {
    const none = document.createElement("p");
    none.className = "stock86-empty";
    none.dataset["testid"] = "stock86-candidate-empty";
    none.textContent =
      stock86.query.trim() === "" && stock86.category === ""
        ? "Every active item is already 86'd."
        : "No items match.";
    stock86CandidateListEl.append(none);
  }
  for (const entry of candidates) {
    const row = buildStock86Row({
      entry,
      subText: candidateEntrySubText(entry, stock86ProductCategoryLabel(entry.category)),
      row: "candidate",
    });
    if (stock86.freshBack.has(entry.id)) {
      row.classList.add("stock86-row--gone");
    }
    stock86CandidateListEl.append(row);
  }
}

stock86OpenButton.addEventListener("click", openStock86Panel);
menu86Button.addEventListener("click", openStock86Panel);
stock86DoneButton.addEventListener("click", closeStock86Panel);
stock86Backdrop.addEventListener("click", (event) => {
  // A tap on the dimmed area outside the panel closes it; a tap inside the
  // panel bubbles up here too, so only a direct hit on the backdrop counts.
  if (event.target === stock86Backdrop) {
    closeStock86Panel();
  }
});
stock86SearchInput.addEventListener("input", () => {
  stock86.query = stock86SearchInput.value;
  renderStock86Panel();
});

// UX109 (docs/build-briefs/UX109-floor-menu-search.md): the floor menu's
// search field. The query is transient on purpose: it is not saved to the
// URL or to sessionStorage (UX108), so a refresh returns the full menu.
let menuSearchQuery = "";
/** menuScroll.scrollTop at the moment the query went from empty to non-empty,
 * restored when it is cleared. */
let menuScrollBeforeSearch = 0;

function syncMenuSearchChrome(): void {
  const active = menuQueryWords(menuSearchQuery).length > 0;
  menuSearchClear.hidden = menuSearchInput.value === "";
  menuIndexRow.classList.toggle("is-searching", active);
  syncPopularViewChrome();
  if (!active) {
    updateAlphabetStripFade();
  }
}

function applyMenuSearch(value: string): void {
  const wasActive = menuQueryWords(menuSearchQuery).length > 0;
  menuSearchQuery = value;
  const active = menuQueryWords(value).length > 0;
  if (active && !wasActive) {
    menuScrollBeforeSearch = menuScroll.scrollTop;
  }
  syncMenuSearchChrome();
  renderMenuGrid();
  if (active) {
    menuScroll.scrollTop = 0;
  } else if (wasActive) {
    menuScroll.scrollTop = menuScrollBeforeSearch;
  }
}

/** Empties the search without keeping its scroll (a new ticket, a sign-out). */
function resetMenuSearch(): void {
  if (menuSearchQuery === "" && menuSearchInput.value === "") {
    return;
  }
  menuSearchQuery = "";
  menuSearchInput.value = "";
  menuScrollBeforeSearch = 0;
  syncMenuSearchChrome();
  if (state.menuItems.length > 0) {
    renderMenuGrid();
  }
}

/** LOUNGERMAJ1 (lounger-05): a drink added from a search leaves the box empty, so the next drink starts from the full
 * menu. The Drinks, Bottles and Base filters stay (UX121: kept while the same lounger is signed in). */
function clearMenuSearchAfterAdd(): void {
  if (menuSearchQuery === "" && menuSearchInput.value === "") {
    return;
  }
  menuSearchInput.value = "";
  applyMenuSearch("");
}

menuSearchInput.addEventListener("input", () => applyMenuSearch(menuSearchInput.value));
menuSearchClear.addEventListener("click", () => {
  menuSearchInput.value = "";
  applyMenuSearch("");
  menuSearchInput.focus();
});

// UX121 (docs/build-briefs/UX121-floor-menu-filters.md): the Drinks, Bottles and Base filters. Kept for as long as
// the same bartender is signed in (a ticket closing does not clear them); cleared on sign-out.
let menuFilters: MenuFilters = { ...NO_MENU_FILTERS };

/** The location facts the floor grid needs: under shelf pricing an item with no shelf cannot be sold (INVIMPORT2). */
function floorMenuContext(): { pricingModel: string } {
  return { pricingModel: state.pricingModel };
}

/** The items that belong on the floor grid, before any filter or search. */
function floorMenuItems(): MenuItemDto[] {
  // UX122: 86'd and OUT items are not on the floor menu (the server already leaves out modifier kinds).
  return floorMenuGridItems(state.menuItems, floorMenuContext());
}

// UX124 (docs/build-briefs/UX124-floor-menu-stock-refresh.md): the floor re-reads the menu's stock and 86 state
// without a reload. One lighter read than loadMenu (the menu-items route only: no prices, no 86 lists), run on a
// modest interval while the ring screen shows (provisional 60s), each time the ring screen is shown, and after this
// device adds an item to a ticket.
const FLOOR_MENU_STOCK_REFRESH_MS = 60_000;
let floorMenuStockRefreshInFlight: { generation: number; promise: Promise<void> } | undefined;

// UX125 (docs/build-briefs/UX125-popular-now-on-the-floor-menu.md): the ranking behind Popular Now, item ids best seller
// first. It is read with the menu refresh below (each time the ring screen shows, on the 60s interval, after an add),
// kept when a read fails, and emptied on sign-out. The section itself is drawn by renderMenuGrid.
let popularMenuIds: string[] = [];
/** RINGUX1: the default view is the Popular section only, so it needs an honest state before and after the read:
 * loading, ready (possibly empty) or failed. Never a blank screen. */
let popularReadState: "loading" | "ready" | "failed" = "loading";

function setPopularReadState(next: "loading" | "ready" | "failed"): boolean {
  const changed = popularReadState !== next;
  popularReadState = next;
  return changed;
}

async function refreshPopularNow(
  session: Session,
  gen: number,
  background: boolean,
): Promise<void> {
  try {
    const result = await getPopularMenuItems(session.apiBaseUrl, session.tenantId, { background });
    if (!sessionIsCurrent(gen)) {
      return;
    }
    if (result.status !== 200) {
      // Known ranking stays; only a never-read ranking becomes "failed".
      if (popularReadState === "loading" && setPopularReadState("failed") && state.menuItems.length > 0) {
        renderMenuGrid();
      }
      return;
    }
    const ids = result.body.items.map((entry) => entry.id);
    const stateChanged = setPopularReadState("ready");
    if (!stateChanged && ids.join("|") === popularMenuIds.join("|")) {
      return;
    }
    popularMenuIds = ids;
    if (state.menuItems.length > 0) {
      const scrollTop = menuScroll.scrollTop;
      renderMenuGrid();
      menuScroll.scrollTop = scrollTop;
    }
  } catch {
    // A failed read keeps any known ranking; a never-read one is shown as unavailable on the default view.
    if (sessionIsCurrent(gen) && popularReadState === "loading" && setPopularReadState("failed")) {
      if (state.menuItems.length > 0) renderMenuGrid();
    }
  }
}

// `background` is true only for the 60 second timer: it is not something the person did, so a support session's idle
// clock (TEN11-F3, DEC-414) must not count it.
function refreshFloorMenuStock(background = false): Promise<void> {
  const session = state.session;
  if (session === undefined) {
    return Promise.resolve();
  }
  const gen = sessionGeneration;
  if (floorMenuStockRefreshInFlight?.generation === gen) {
    return floorMenuStockRefreshInFlight.promise;
  }
  const stock86Revision = stock86.writeRevision;
  const run = async (): Promise<void> => {
    // UX125: the Popular Now ranking is read alongside, by the same triggers.
    const popularRead = refreshPopularNow(session, gen, background);
    try {
      const result = await getMenuItems(session.apiBaseUrl, session.tenantId, { background });
      // A dead session writes nothing; a local 86 write that landed while the read was in flight is newer than it.
      if (
        !sessionIsCurrent(gen) ||
        result.status !== 200 ||
        stock86.writeRevision !== stock86Revision
      ) {
        return;
      }
      if (JSON.stringify(result.body.items) === JSON.stringify(state.menuItems)) {
        return;
      }
      state.menuItems = result.body.items;
      state.menuDrinks = result.body.items
        .filter(
          (item): item is Extract<MenuItemDto, { kind: "cocktail" }> => item.kind === "cocktail",
        )
        .map((item) => item.menuDrink);
      const scrollTop = menuScroll.scrollTop;
      renderAlphabetStrip();
      renderMenuGrid();
      menuScroll.scrollTop = scrollTop;
      if (!menuFilterList.hidden) {
        renderMenuBaseList();
      }
    } finally {
      await popularRead;
      // Work from a signed-out employee must not clear the next employee's refresh owner.
      if (floorMenuStockRefreshInFlight?.promise === pending) {
        floorMenuStockRefreshInFlight = undefined;
      }
    }
  };
  const pending = run();
  floorMenuStockRefreshInFlight = { generation: gen, promise: pending };
  return pending;
}

setInterval(() => {
  if (state.session !== undefined && !ringScreen.hidden && document.visibilityState === "visible") {
    void refreshFloorMenuStock(true);
  }
}, FLOOR_MENU_STOCK_REFRESH_MS);

function closeMenuBaseList(): void {
  menuFilterList.hidden = true;
  menuFilterScrim.hidden = true;
  menuFilterBaseButton.setAttribute("aria-expanded", "false");
}

function renderMenuBaseList(): void {
  menuFilterList.innerHTML = "";
  const options = baseOptions(floorMenuItems(), menuFilters);
  const entries = [
    { value: "", label: "All bases", count: undefined as number | undefined },
    ...options,
  ];
  for (const entry of entries) {
    const button = document.createElement("button");
    button.type = "button";
    button.dataset["testid"] = "menu-filter-base-option";
    button.dataset["value"] = entry.value;
    if (entry.value === "") {
      button.classList.add("is-all");
    }
    button.setAttribute("aria-pressed", entry.value === menuFilters.base ? "true" : "false");
    const label = document.createElement("span");
    label.textContent = entry.label;
    button.append(label);
    if (entry.count !== undefined) {
      const count = document.createElement("span");
      count.className = "menu-filter-count";
      count.dataset["testid"] = "menu-filter-base-count";
      count.textContent = String(entry.count);
      button.append(count);
    }
    button.addEventListener("click", () => {
      closeMenuBaseList();
      setMenuFilters({ ...menuFilters, base: entry.value });
    });
    menuFilterList.append(button);
  }
}

function syncMenuFilterChrome(): void {
  syncPopularViewChrome();
  // RINGUX1: Popular is the home view, pressed while no Drinks, Bottles or Base filter narrows the list.
  menuFilterPopular.setAttribute("aria-pressed", menuFiltersActive(menuFilters) ? "false" : "true");
  menuFilterDrinks.setAttribute("aria-pressed", menuFilters.kind === "drinks" ? "true" : "false");
  menuFilterBottles.setAttribute("aria-pressed", menuFilters.kind === "bottles" ? "true" : "false");
  const hasBase = menuFilters.base !== "";
  menuFilterBase.classList.toggle("has-base", hasBase);
  menuFilterBaseButton.classList.toggle("is-on", hasBase);
  menuFilterBaseClear.hidden = !hasBase;
  menuFilterBaseLabel.textContent = hasBase ? categoryLabel(menuFilters.base) : "Base";
}

function setMenuFilters(next: MenuFilters): void {
  menuFilters = next;
  syncMenuFilterChrome();
  renderAlphabetStrip();
  renderMenuGrid();
  menuScroll.scrollTop = 0;
}

/** Sign-out: every filter off, the list closed. */
function resetMenuFilters(): void {
  menuFilters = { ...NO_MENU_FILTERS };
  closeMenuBaseList();
  syncMenuFilterChrome();
}

menuFilterPopular.addEventListener("click", () => setMenuFilters({ ...NO_MENU_FILTERS }));
menuFilterDrinks.addEventListener("click", () =>
  setMenuFilters(toggleKindFilter(menuFilters, "drinks")),
);
menuFilterBottles.addEventListener("click", () =>
  setMenuFilters(toggleKindFilter(menuFilters, "bottles")),
);
menuFilterBaseClear.addEventListener("click", () => setMenuFilters({ ...menuFilters, base: "" }));
menuFilterBaseButton.addEventListener("click", () => {
  if (!menuFilterList.hidden) {
    closeMenuBaseList();
    return;
  }
  renderMenuBaseList();
  menuFilterList.hidden = false;
  menuFilterScrim.hidden = false;
  menuFilterBaseButton.setAttribute("aria-expanded", "true");
});
menuFilterScrim.addEventListener("click", closeMenuBaseList);

function buildMenuFilterResultLine(count: number): HTMLElement {
  const line = document.createElement("div");
  line.className = "menu-filter-result";
  line.dataset["testid"] = "menu-filter-result";
  const text = document.createElement("span");
  text.className = "menu-filter-result-text";
  text.dataset["testid"] = "menu-filter-result-text";
  text.textContent = [
    itemCountLabel(count),
    kindFilterLabel(menuFilters.kind),
    menuFilters.base === "" ? "" : categoryLabel(menuFilters.base),
  ]
    .filter((part) => part !== "")
    .join(" · ");
  const clear = document.createElement("button");
  clear.type = "button";
  clear.dataset["testid"] = "menu-filter-clear";
  clear.textContent = "Clear filters";
  clear.addEventListener("click", () => setMenuFilters({ ...NO_MENU_FILTERS }));
  line.append(text, clear);
  return line;
}

/** One menu tile, the grid's own and Popular Now's (UX125): the same tile, the same tap, the same Recipe pill. */
function buildMenuTile(item: MenuItemDto, popular: boolean): HTMLElement {
  const price = tilePriceLabelForItem(item);
  const tile = document.createElement("button");
  tile.type = "button";
  tile.className = "tile";
  // UX125: a Popular Now tile is the same tile under its own test id and id attributes, so the specs and code that
  // find the grid's tiles by menu-tile, data-menu-item-id or data-menu-drink-id do not meet a second match.
  tile.dataset["testid"] = popular ? "popular-tile" : "menu-tile";
  tile.dataset["menuItemKind"] = item.kind;
  if (popular) {
    tile.dataset["popularItemId"] = item.id;
  } else {
    tile.dataset["menuItemId"] = item.id;
  }
  if (!popular && item.kind === "cocktail") {
    // Kept alongside menuItemId for now: e2e specs and any other
    // existing caller keyed off the pre-Slice-3 attribute name.
    tile.dataset["menuDrinkId"] = item.id;
  }
  // VOC1-F5 Slice 2/3: a flagged tile is disabled through the real
  // `disabled` attribute, not merely styled -- a tap on a disabled
  // button is a platform no-op, so no click handler fires and no
  // request is ever sent. The server's own refusal in
  // resolveAndPriceInventorySaleLine/resolveAndPriceLine is the
  // backstop, never the only guard (defence in depth). Slice 3: a
  // session holding inv.stock_flag.override is NOT blocked here -- the
  // tile stays enabled and its click handler opens the override
  // confirm sheet instead (handleMenuItemTileClick below). The "86'd"
  // badge stays visible either way (rendered below, unconditional on
  // permission).
  const flagged86 = isMenuItemFlagged86(item);
  const isCocktail = isCocktailMenuItem(item);
  const badge = isCocktail
    ? flagged86
      ? ({ label: "86'd", modifier: "flagged" } as const)
      : undefined
    : stockStateBadge(item.liquorProduct);
  tile.disabled = state.orderId === undefined || (flagged86 && !canOverride86());
  // UX110: a straight pour with a shelf tier assigned shows the tier's name bottom left, with the price bottom right; no tier, nothing.
  const shelfLabel = item.kind === "pour" ? (item.shelfTierLabel ?? "") : "";
  // UX14 (DEC-314): POUR and OUT share one row above the name (they
  // were two stacked rows, 22px each).
  tile.innerHTML = `
    ${
      isCocktail && badge === undefined
        ? ""
        : `<div class="tile-badges">${isCocktail ? "" : '<div class="tile-kind-badge"></div>'}${
            badge === undefined
              ? ""
              : `<div class="stock-state-badge stock-state-badge--${badge.modifier}" data-testid="stock-badge"></div>`
          }</div>`
    }
    <div class="tile-name"></div>
    <div class="tile-price-row${item.kind === "pour" ? " tile-price-row--pour" : ""}">${
      shelfLabel === "" ? "" : '<div class="tile-shelf" data-testid="tile-shelf"></div>'
    }<div class="tile-price${price.variable ? " variable" : ""}"></div></div>${
      price.caption === undefined ? "" : '<div class="tile-price-caption" data-testid="tile-price-caption"></div>'
    }
  `;
  if (!isCocktail) {
    // ITEM2 Slice 5: a seeded kind's own established badge wording
    // wins where one exists; a tenant-added kind falls back to its
    // own configured item_kind label (never the raw stored value).
    tile.querySelector(".tile-kind-badge")!.textContent =
      NON_COCKTAIL_TILE_BADGE_LABEL[item.kind] ?? item.itemKindLabel;
  }
  if (badge !== undefined) {
    tile.querySelector(".stock-state-badge")!.textContent = badge.label;
  }
  if (shelfLabel !== "") {
    tile.querySelector(".tile-shelf")!.textContent = shelfLabel;
  }
  tile.querySelector(".tile-name")!.textContent = item.name;
  tile.querySelector(".tile-price")!.textContent = price.text;
  if (price.caption !== undefined) {
    tile.querySelector(".tile-price-caption")!.textContent = price.caption;
  }
  // UX102 (click-through review, Ring screen tiles): "Shelf price" repeated on every spirit tile says what the
  // header's Shelf chip already says, so the tile does not draw it (it stays in the page for assistive tech).
  tile
    .querySelector(".tile-price")!
    .classList.toggle(
      "tile-price-quiet",
      state.pricingModel === "shelf" && price.text === "Shelf price",
    );
  tile.addEventListener("click", () => void handleMenuItemTileClick(item));
  // VOC1-F8 (docs/build-briefs/VOC1-F8-view-recipe-on-the-floor.md,
  // Section 2.1): a cocktail tile gets a second, independently
  // tappable control rather than a second tap target nested inside
  // the existing add-to-ticket button (invalid HTML, and a click on
  // it would bubble to the outer button's own handler). A wrapper
  // holds both as siblings; a non-cocktail tile keeps its old,
  // unwrapped shape (no recipe to show: neither a pour, a draft beer,
  // a wine glass nor a packaged item carries menu_drink_components).
  if (isCocktail) {
    const wrapper = document.createElement("div");
    wrapper.className = "tile-wrapper";
    wrapper.append(tile);
    const recipeButton = document.createElement("button");
    recipeButton.type = "button";
    recipeButton.className = "tile-recipe-button";
    recipeButton.dataset["testid"] = popular ? "popular-recipe-button" : "tile-recipe-button";
    // A DIFFERENT attribute name than the add-tile's own
    // data-menu-drink-id (line ~5251 above): several existing e2e
    // specs (e.g. e2e/ux25-single-guest-skips-pay-mode-sheet.spec.ts's
    // own addDrinkTile) select the add-tile with a bare
    // `[data-menu-drink-id="..."]` attribute selector, unscoped to
    // data-testid. Reusing that same attribute name here made every
    // one of those locators ambiguous (two matches instead of one) --
    // caught live by running that spec, not assumed.
    recipeButton.dataset[popular ? "popularRecipeId" : "recipeMenuDrinkId"] = item.id;
    // No title attribute (check:touch-only): the button's own label
    // text is the recovery, matching the ticket-line edit button's
    // aria-label convention rather than a hover tooltip.
    recipeButton.setAttribute("aria-label", `Recipe for ${item.name}`);
    recipeButton.textContent = "Recipe";
    // Never handleMenuItemTileClick: this must add nothing to the
    // ticket and open no liquor sheet (P0.4's "without adding an
    // item"). Independent of the add-tile's own disabled state
    // (line 5267 above): a session signed in with no open ticket can
    // still look up a recipe.
    recipeButton.addEventListener("click", () => void handleViewRecipeClick(item.id, item.name));
    // VOC1-F8-F8 (independent review, 2026-09-22): rendered only
    // while the CURRENT permission snapshot still holds
    // admin.menu_drink.view, re-evaluated on every re-render this
    // grid gets, including the one refreshPermissionSnapshotOnNavigation
    // now triggers after a navigation refreshes the snapshot (see its
    // own header comment). hidden, not omitted, so an already-open
    // recipe sheet from before a revoke is unaffected by this render
    // pass, and closing/reopening the grid does not throw.
    recipeButton.hidden = !hasPermission(requireSession(), MENU_DRINK_VIEW_PERMISSION);
    // UX40 (docs/build-briefs/UX40-recipe-button-beside-price.md,
    // Section 1.2 Part B): .tile-price only reserves room for the
    // button when this tile is actually rendering one, toggled
    // together with recipeButton.hidden above so a tile whose
    // permission is revoked mid-session gives the price its full
    // width back on the very next re-render, same as the button
    // itself.
    wrapper.classList.toggle("tile-wrapper--recipe-visible", !recipeButton.hidden);
    wrapper.append(recipeButton);
    return wrapper;
  }
  return tile;
}

/** UX125: the Popular Now section, built from the grid's own tile. */
function buildPopularNowSection(items: readonly MenuItemDto[]): HTMLElement {
  const section = document.createElement("section");
  section.className = "menu-section menu-section--popular";
  section.dataset["testid"] = "popular-now";
  const title = document.createElement("h2");
  title.className = "menu-section-title";
  title.textContent = "Popular drinks";
  section.append(title);
  const grid = document.createElement("div");
  grid.className = "tile-grid";
  for (const item of items) {
    grid.append(buildMenuTile(item, true));
  }
  section.append(grid);
  return section;
}

/** RINGUX1: the default view, no filter and no search. */
function isPopularView(): boolean {
  return !menuFiltersActive(menuFilters) && menuQueryWords(menuSearchQuery).length === 0;
}

/** RINGUX1 (Kerry's concept description, DEC-531): on the default view with popular drinks to show, the screen is the
 * Popular drinks only, with no A to Z strip and no alphabetical list. Without any (a new location with no sales, or the
 * ranking unavailable) the full menu and the strip show under a plain sentence, so the screen is never empty. */
function popularOnlyView(): boolean {
  return (
    isPopularView() &&
    (popularReadState === "loading" ||
      popularNowItems(popularMenuIds, state.menuItems, undefined, floorMenuContext()).length > 0)
  );
}

function syncPopularViewChrome(): void {
  menuIndexRow.classList.toggle("is-popular-view", popularOnlyView());
}

function buildPopularNote(): HTMLElement {
  const note = document.createElement("p");
  note.className = "menu-popular-note";
  note.dataset["testid"] = "popular-empty";
  note.textContent =
    popularReadState === "failed"
      ? "Popular drinks are not available right now. Showing the full menu."
      : "No popular drinks yet at this location. Showing the full menu.";
  return note;
}

function renderMenuGrid(): void {
  // LOUNGERMAJ1 (lounger-14): the 86 button follows the permission snapshot, re-read on every redraw like the Recipe pill.
  menu86Button.hidden = !canRecordStockFlag();
  menuScroll.innerHTML = "";
  // UX122: floorMenuItems() already leaves out 86'd and OUT items.
  if (floorMenuItems().length === 0) {
    menuScroll.innerHTML = '<p class="menu-empty">No active menu items for this location yet.</p>';
    return;
  }
  if (popularOnlyView()) {
    const popular = popularNowItems(popularMenuIds, state.menuItems, undefined, floorMenuContext());
    if (popular.length > 0) {
      menuScroll.append(buildPopularNowSection(popular));
    } else {
      const loading = document.createElement("p");
      loading.className = "menu-empty";
      loading.textContent = "Loading popular drinks.";
      menuScroll.append(loading);
    }
    syncPopularViewChrome();
    return;
  }
  syncPopularViewChrome();
  if (isPopularView()) {
    menuScroll.append(buildPopularNote());
  }
  // UX109: the search field's query narrows the list; a group with no match
  // is never built, so its heading disappears too. UX121: the Drinks, Bottles
  // and Base filters narrow it first, and combine with the search.
  const sortedItems = applyMenuFilters(floorMenuItems(), menuFilters)
    .filter((item) => nameMatchesMenuQuery(item.name, menuSearchQuery))
    .sort((a, b) => compareNames(a.name, b.name));
  if (menuFiltersActive(menuFilters)) {
    menuScroll.append(buildMenuFilterResultLine(sortedItems.length));
  }
  // LOCFLOOR1 (sweep B18, B17): what the search found that the grid does not offer, each with its reason.
  const unavailableMatches =
    menuQueryWords(menuSearchQuery).length === 0
      ? []
      : applyMenuFilters(
          state.menuItems.filter((item) => floorMenuUnavailableReason(item, floorMenuContext()) !== undefined),
          menuFilters,
        )
          .filter((item) => nameMatchesMenuQuery(item.name, menuSearchQuery))
          .sort((a, b) => compareNames(a.name, b.name));
  if (sortedItems.length === 0) {
    if (unavailableMatches.length > 0) {
      menuScroll.append(buildUnavailableMatches(unavailableMatches));
      return;
    }
    menuScroll.insertAdjacentHTML(
      "beforeend",
      '<p class="menu-empty" data-testid="menu-search-empty">No matches</p>',
    );
    return;
  }
  const byLetter = new Map<string, MenuItemDto[]>();
  for (const item of sortedItems) {
    const letter = alphaBucketFor(item.name);
    const list = byLetter.get(letter) ?? [];
    list.push(item);
    byLetter.set(letter, list);
  }

  for (const letter of ALPHABET_JUMP_LETTERS) {
    const items = byLetter.get(letter);
    if (items === undefined || items.length === 0) {
      continue;
    }

    const section = document.createElement("section");
    section.className = "menu-section";
    section.dataset["letterSection"] = letter;

    const title = document.createElement("h2");
    title.className = "menu-section-title";
    title.textContent = letter;
    section.append(title);

    const grid = document.createElement("div");
    grid.className = "tile-grid";

    for (const item of items) {
      grid.append(buildMenuTile(item, false));
    }

    section.append(grid);
    menuScroll.append(section);
  }
  if (unavailableMatches.length > 0) {
    menuScroll.append(buildUnavailableMatches(unavailableMatches));
  }
}

/** LOCFLOOR1 (sweep B18, B17): items a search matched but the floor cannot sell, said plainly and never selectable
 * (rows, not buttons): "Woodford Reserve is 86'd", "X is out of stock", "X: No recipe yet, ask a manager". */
function buildUnavailableMatches(items: readonly MenuItemDto[]): HTMLElement {
  const section = document.createElement("section");
  section.className = "menu-unavailable";
  section.dataset["testid"] = "menu-unavailable";
  const title = document.createElement("h2");
  title.className = "menu-section-title";
  title.textContent = "Not available right now";
  section.append(title);
  for (const item of items) {
    const reason = floorMenuUnavailableReason(item, floorMenuContext());
    if (reason === undefined) continue;
    const row = document.createElement("div");
    row.className = "menu-unavailable-row";
    row.dataset["testid"] = "menu-unavailable-row";
    row.dataset["menuItemId"] = item.id;
    row.dataset["reason"] = reason;
    row.textContent = floorMenuUnavailableText(item.name, reason);
    section.append(row);
  }
  return section;
}

function refreshTileDisabledState(): void {
  const orderMissing = state.orderId === undefined;
  const overridable = canOverride86();
  for (const tile of menuScroll.querySelectorAll<HTMLButtonElement>(
    '[data-testid="menu-tile"], [data-testid="popular-tile"]',
  )) {
    // VOC1-F5 Slice 2/3: an 86'd tile stays disabled regardless of orderId
    // for a session without the override permission, so this refresh
    // (called whenever the order changes) never re-enables a flagged tile
    // it did not itself disable. A session holding the permission is never
    // blocked here (mirrors renderMenuGrid's own tile.disabled logic).
    const itemId = tile.dataset["menuItemId"] ?? tile.dataset["popularItemId"];
    const item = state.menuItems.find((candidate) => candidate.id === itemId);
    const flagged86 = item !== undefined && isMenuItemFlagged86(item);
    // RCPT1-S3: a ticket whose card is authorized for its total takes no more drinks; the server refuses a capture if it changed.
    tile.disabled = orderMissing || state.awaitingTip !== undefined || (flagged86 && !overridable);
  }
}

// ---------------------------------------------------------------------
// Menu drink detail cache
// ---------------------------------------------------------------------

// TAPSPEED1: the first tap on a drink used to wait for this read and only then send the line, two round trips before the
// line appeared. The detail is now read ahead of the tap, silently: a warm that fails shows nothing and leaves the tap to
// fetch (and toast) exactly as before. Cleared with the cache on sign-out.
const menuDetailWarming = new Map<string, Promise<void>>();
/** How many drinks are read ahead after a menu load, and how many reads run at once. A drink past the limit is read
 * when its tile is first touched (warmMenuDrinkDetailForTile) or tapped. */
const MENU_DETAIL_WARM_LIMIT = 60;
const MENU_DETAIL_WARM_CONCURRENCY = 3;

function warmMenuDrinkDetail(menuDrinkId: string): Promise<void> {
  if (state.session === undefined || state.menuDetailCache.has(menuDrinkId)) return Promise.resolve();
  const inFlight = menuDetailWarming.get(menuDrinkId);
  if (inFlight !== undefined) return inFlight;
  const generation = sessionGeneration;
  const session = requireSession();
  const run = (async (): Promise<void> => {
    try {
      const result = await getMenuDrink(session.apiBaseUrl, session.tenantId, menuDrinkId);
      if (!sessionIsCurrent(generation) || state.session === undefined) return;
      if (result.status !== 200 || state.menuDetailCache.has(menuDrinkId)) return;
      state.menuDetailCache.set(menuDrinkId, {
        menuDrink: result.body.menuDrink,
        components: result.body.components,
        liquorSlots: result.body.liquorSlots,
        modifiers: result.body.modifiers,
      });
    } catch {
      // Silent by design: the tap path reads the drink itself and reports a failure to the person tapping.
    } finally {
      menuDetailWarming.delete(menuDrinkId);
    }
  })();
  menuDetailWarming.set(menuDrinkId, run);
  return run;
}

/** Reads ahead the drink details of the menu just loaded, a few at a time, in menu order. */
function warmMenuDrinkDetails(): void {
  const generation = sessionGeneration;
  const queue = state.menuDrinks.slice(0, MENU_DETAIL_WARM_LIMIT).map((drink) => drink.id);
  const worker = async (): Promise<void> => {
    for (let next = queue.shift(); next !== undefined; next = queue.shift()) {
      if (!sessionIsCurrent(generation)) return;
      await warmMenuDrinkDetail(next);
    }
  };
  for (let index = 0; index < MENU_DETAIL_WARM_CONCURRENCY; index += 1) void worker();
}

async function ensureMenuDrinkDetail(menuDrinkId: string): Promise<MenuDrinkDetail | undefined> {
  if (state.session === undefined) return undefined;
  const generation = sessionGeneration;
  const cached = state.menuDetailCache.get(menuDrinkId);
  if (cached !== undefined) {
    return cached;
  }
  const warming = menuDetailWarming.get(menuDrinkId);
  if (warming !== undefined) {
    await warming;
    if (!sessionIsCurrent(generation) || state.session === undefined) return undefined;
    const warmed = state.menuDetailCache.get(menuDrinkId);
    if (warmed !== undefined) return warmed;
  }
  const session = requireSession();
  const result = await getMenuDrink(session.apiBaseUrl, session.tenantId, menuDrinkId);
  if (!sessionIsCurrent(generation) || state.session === undefined) return undefined;
  if (result.status !== 200) {
    showToast(
      `Could not load drink details. ${capitalizeServerMessage(result.body.message ?? result.body.error ?? String(result.status))}`,
      "danger",
    );
    return undefined;
  }
  const detail: MenuDrinkDetail = {
    menuDrink: result.body.menuDrink,
    components: result.body.components,
    liquorSlots: result.body.liquorSlots,
    modifiers: result.body.modifiers,
  };
  state.menuDetailCache.set(menuDrinkId, detail);
  return detail;
}

// VOC1-F8-F8 (independent review, 2026-09-22): ensureMenuDrinkDetail's own
// cache is shared with the liquor sheet and the change-liquor edit-sheet
// option, both of which legitimately warm it. View Recipe must never serve
// a cached copy: a drink cached by an earlier recipe view, or by opening
// the liquor sheet, kept being openable after admin.menu_drink.view was
// revoked from this session, because the cache hit skipped the route's
// own permission check entirely -- the client never made a request for
// getMenuDrink to refuse. This bypasses the cache on every call (still
// warming it afterward for the shared consumers above), so View Recipe
// always performs a real, permission-checked read. A denied response also
// clears any earlier cached copy, so a stale cache entry left behind by an
// EARLIER successful view can never satisfy a later ensureMenuDrinkDetail
// cache read either, once the server has said no.
async function fetchMenuDrinkDetailForRecipeView(
  menuDrinkId: string,
): Promise<MenuDrinkDetail | undefined> {
  if (state.session === undefined) return undefined;
  const generation = sessionGeneration;
  const session = requireSession();
  const result = await getMenuDrink(session.apiBaseUrl, session.tenantId, menuDrinkId);
  if (!sessionIsCurrent(generation) || state.session === undefined) return undefined;
  if (result.status !== 200) {
    state.menuDetailCache.delete(menuDrinkId);
    showToast(
      `Could not load drink details. ${capitalizeServerMessage(result.body.message ?? result.body.error ?? String(result.status))}`,
      "danger",
    );
    return undefined;
  }
  const detail: MenuDrinkDetail = {
    menuDrink: result.body.menuDrink,
    components: result.body.components,
    liquorSlots: result.body.liquorSlots,
    modifiers: result.body.modifiers,
    recipeMethod: result.body.recipeMethod ?? null,
  };
  state.menuDetailCache.set(menuDrinkId, detail);
  return detail;
}

// VOC1-F8-F8: the permission View Recipe's own route enforces
// (admin.menu_drink.view). Gates only whether the CONTROL renders --
// renderMenuGrid's own tile-recipe-button and buildTicketLineRow's own
// line-recipe-button/line-edit-view-recipe below -- never a role name.
// The route itself (fetchMenuDrinkDetailForRecipeView above) is the real,
// unbypassable gate regardless of this snapshot's own staleness between
// navigations.
const MENU_DRINK_VIEW_PERMISSION = "admin.menu_drink.view";

// ---------------------------------------------------------------------
// VOC1-F8 (docs/build-briefs/VOC1-F8-view-recipe-on-the-floor.md,
// Section 2.3): read-only recipe view, reached from a cocktail tile
// (renderMenuGrid) or a cocktail ticket line (buildTicketLineRow).
// ---------------------------------------------------------------------

// Reuses ensureMenuDrinkDetail (the same cache-and-toast-on-failure
// fetch the liquor sheet and the ticket-line edit sheet already share)
// rather than calling getMenuDrink and duplicating its failure toast: an
// identical fetch, and a recipe view benefits from the same cache a
// bartender re-opening the same drink's liquor sheet would already have
// warmed.
async function handleViewRecipeClick(menuDrinkId: string, drinkName: string): Promise<void> {
  if (state.session === undefined) return;
  const generation = sessionGeneration;
  const screen = currentScreenId;
  const visit = floorTicketVisit;
  const detail = await fetchMenuDrinkDetailForRecipeView(menuDrinkId);
  if (detail === undefined || !sessionIsCurrent(generation) ||
      state.session === undefined || currentScreenId !== screen || floorTicketVisit !== visit) {
    return;
  }
  const components = detail.components;
  // RECUX2-F1: the franchisor's glass, garnish and numbered steps, shown after the ingredients as plain read-only rows.
  const methodRows: ChoiceOption[] = [];
  const method = detail.recipeMethod;
  if (method !== undefined && method !== null) {
    if (method.glass !== null) {
      methodRows.push({ id: "method-glass", label: "Glass", sublabel: method.glass, disabled: true, testid: "recipe-method-glass" });
    }
    if (method.garnish !== null) {
      methodRows.push({ id: "method-garnish", label: "Garnish", sublabel: method.garnish, disabled: true, testid: "recipe-method-garnish" });
    }
    method.steps.forEach((step, index) => {
      methodRows.push({ id: `method-step-${index + 1}`, label: `Step ${index + 1}`, sublabel: step, disabled: true, testid: "recipe-method-step" });
    });
  }
  openChoiceSheet({
    title: `Recipe: ${drinkName}`,
    options: [
      ...(components.length === 0
        ? [
            {
              id: "none",
              label: "No recipe components on file for this drink.",
              disabled: true,
            },
          ]
        : components
            .slice()
            // The liquor-slot component (at most one) sorts first and
            // carries its own plain-language marker below, since it is
            // the one ingredient a guest's own choice determines rather
            // than a fixed quantity.
            .sort((a, b) => (a.is_liquor_slot === b.is_liquor_slot ? 0 : a.is_liquor_slot ? -1 : 1))
            .map((component) => ({
              id: component.id,
              label: component.component_reference,
              // UX41 (docs/build-briefs/UX41-recipe-quantities-two-decimals.md,
              // Section 1.2, DEC-193/DEC-197): display-only formatting;
              // component.quantity itself (the server's own
              // NUMERIC(12,4) string) is never reassigned, so nothing
              // downstream of this sheet ever sees a rounded value.
              sublabel: component.is_liquor_slot
                ? `${formatRecipeQuantity(component.quantity)} ${component.unit} · selected liquor`
                : `${formatRecipeQuantity(component.quantity)} ${component.unit}`,
              // Every row is non-interactive (openChoiceSheet's own
              // disabled: true shape, rendered as a plain div rather than
              // a <button>): nothing here is tappable to select, matching
              // P0.4's "without adding an item". No cost or price field
              // exists anywhere on MenuDrinkComponentDto, so "without ...
              // exposing restricted cost data" holds by construction, not
              // by an added filter that could be gotten wrong.
              disabled: true,
            }))),
      ...methodRows,
    ],
    cancelLabel: "Close",
    onPick: () => {},
  });
}

// ---------------------------------------------------------------------
// Menu tile tap
// ---------------------------------------------------------------------

// Inventory track (track/inventory) Slice 3, Amendment 2026-08-22
// (twelfth): the one dispatch point renderMenuGrid's tile click handler
// calls, routing to the cocktail flow (unchanged) or one of the three
// direct-sale flows by the tapped tile's own kind. "All appear in the
// alphabetical menu alongside cocktails" (the amendment's own words)
// means the menu grid itself, not a separate browse screen, IS the
// "bottle picker" the amendment describes for a pour: each active
// liquor/draft-beer/packaged item is already its own tile (GET
// .../menu-items), so tapping the tile is the picking step; a pour or
// draft beer then needs one more tap to pick a size (openPourSizeSheet),
// while a packaged item needs none at all ("one tap").
async function handleMenuItemTileClick(item: MenuItemDto): Promise<void> {
  if (state.orderId === undefined) {
    showToast("Resume or start a ticket first.", "danger");
    return;
  }
  const saleContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(saleContext)) return;
  if (isCocktailMenuItem(item)) {
    // CORE7: a drink that reads 86'd (its own flag, or an 86'd ingredient) asks the same question an 86'd bottle
    // does. A session without the override never gets here: the tile is disabled.
    if (item.flagged86 && canOverride86()) {
      const drinkKey = stock86DrinkKey(item.id);
      const ownFlag = stock86.drinks.find((drink) => drink.menuDrinkId === item.id)?.own === true;
      void openOverride86ConfirmSheet(
        { id: drinkKey, name: item.name },
        (choice) => {
          if (floorSaleContextIsCurrent(saleContext)) void handleMenuTileClick(item.menuDrink, choice);
        },
        {
          clear: ownFlag
            ? () =>
                void setMenuStockFlag({
                  kind: "menu_drink",
                  subjectId: item.id,
                  name: item.name,
                  flagged: false,
                })
            : undefined,
          flaggedVia: item.flagged86Via,
        },
      );
      return;
    }
    await handleMenuTileClick(item.menuDrink);
    return;
  }
  // VOC1-F5 Slice 3 (DEC-161): the canOverride86() check here, not only the
  // tile's own disabled attribute, is deliberate defence in depth on the
  // CLIENT side too -- a tile whose disabled attribute is stripped by a
  // compromised or scripted client (see this item's own "defence in
  // depth" e2e test) must fall through to the unchanged Slice 2 path
  // (proceedMenuItemTileClick with override86 false) for a session that
  // does NOT hold the permission, not open a confirm sheet it has no
  // right to see. The server's own requirePermission call is still the
  // real, unbypassable gate either way.
  if (isMenuItemFlagged86(item) && canOverride86()) {
    void openOverride86ConfirmSheet(
      item.liquorProduct,
      (choice) => {
        if (floorSaleContextIsCurrent(saleContext)) void proceedMenuItemTileClick(item, choice);
      },
    );
    return;
  }
  await proceedMenuItemTileClick(item, false);
}

// Inventory track (track/inventory) Slice 3, Amendment 2026-08-22
// (twelfth): the routing this file's own handleMenuItemTileClick header
// comment above describes, factored out so VOC1-F5 Slice 3's override
// confirm can re-enter it with override86 true without duplicating the
// routing itself.
async function proceedMenuItemTileClick(
  item: Exclude<MenuItemDto, { kind: "cocktail" }>,
  override86: Override86,
): Promise<void> {
  const saleContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(saleContext)) return;
  const selling = floorProductSelling(item.liquorProduct);
  if (
    !selling ||
    !selling.active ||
    selling.hideOnMenu ||
    selling.state === "unconfigured" ||
    (selling.saleMechanism === "whole_unit" && selling.unitPriceCents === null)
  ) {
    showToast("This item is unavailable at this location.", "danger");
    return;
  }
  // ITEM2 Slice 5 (Section 3.4): the one-tap-add-versus-open-pour-sheet
  // dispatch used to be `item.kind === "packaged"`, which a tenant-added
  // whole-unit kind's own item.kind (its own value, never "packaged")
  // could never match, so it would wrongly fall through to the pour-size
  // sheet below with no pour sizes to offer. Generalized to the PRODUCT'S
  // OWN already-fetched field shape, mirroring tilePriceLabelForNonCocktail
  // Item's identical derivation just above: `unit_price_cents` is
  // non-null exactly on a whole-unit product.
  if (item.liquorProduct.unit_price_cents !== null) {
    // VOC1-F4-F2 (Section 2.5, Section 1.4 item 1): a whole-unit product
    // with at least one active attached modifier opens the modifiers
    // sheet instead of adding directly; empty stays byte-identical to
    // today's one-tap add.
    const modifiers = await activeInventorySaleModifiers(item.liquorProduct.id);
    // Review fix, 2026-09-23 (PR #68, floor): a failed fetch (undefined) must
    // not be read as "no modifiers"; activeInventorySaleModifiers already
    // toasted the failure, so just leave the ticket unchanged.
    if (modifiers === undefined || !floorSaleContextIsCurrent(saleContext)) {
      return;
    }
    await addInventorySaleWithModifiersGate(
      item.liquorProduct,
      item.kind,
      undefined,
      override86,
      modifiers,
    );
    return;
  }
  // POURREC1: obtain the standard volume from the authenticated quote, never a literal fallback.
  if (item.kind === "pour") {
    const session = requireSession();
    const quote = await quoteInventorySale(session.apiBaseUrl, session.tenantId, {
      saleKind: "pour",
      liquorProductId: item.liquorProduct.id,
      ...(override86 ? { override86: true } : {}),
    });
    if (!floorSaleContextIsCurrent(saleContext)) return;
    const base = quote.body.pourOunces;
    if (
      quote.status !== 200 ||
      base === undefined ||
      !Number.isFinite(Number(base)) ||
      Number(base) <= 0
    ) {
      showToast(quote.body.message ?? "Could not load the standard pour.", "danger");
      return;
    }
    const modifiers = await activeInventorySaleModifiers(item.liquorProduct.id);
    if (modifiers === undefined || !floorSaleContextIsCurrent(saleContext)) return;
    openInventorySaleModifiersSheet(item.liquorProduct, "pour", base, override86, modifiers);
    return;
  }
  await openPourSizeSheet(item.liquorProduct, item.kind, override86);
}

async function handleMenuTileClick(
  menuDrink: MenuDrinkDto,
  /** CORE7: the reason picked on the Sell anyway sheet when the drink itself is 86'd. */
  drinkOverride86: Override86 = false,
): Promise<void> {
  if (state.orderId === undefined) {
    showToast("Resume or start a ticket first.", "danger");
    return;
  }
  const saleContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(saleContext)) return;
  const detail = await ensureMenuDrinkDetail(menuDrink.id);
  if (detail === undefined || !floorSaleContextIsCurrent(saleContext)) {
    return;
  }
  if (
    !menuDrink.requires_liquor_selection &&
    menuDrink.build_charge_cents <= 0 &&
    detail.modifiers.length === 0
  ) {
    // ZEROPRICE1 (DEC-608): nothing to add to a $0 build charge, so the server would refuse the line. Same wording.
    showToast(`${menuDrink.name} has no price yet. Ask a manager to set its price.`, "danger");
    return;
  }
  if (menuDrink.requires_liquor_selection) {
    openLiquorSheet(detail, undefined, drinkOverride86);
  } else if (detail.modifiers.length > 0) {
    // VOC1-F4 (Section 2.5): "shown ... straight after the tile tap for a
    // drink with no liquor slot" -- this drink needs no liquor sheet at
    // all, but still carries at least one attached modifier, so the same
    // shared sheet opens in modifiers-only mode rather than skipping
    // straight to Add to Ticket.
    openModifiersOnlySheet(detail, drinkOverride86);
  } else {
    await addDrinkToTicketWithGuestPick(
      detail,
      drinkOverride86 ? { override86: drinkOverride86 } : undefined,
    );
  }
}

// ALPHA-12: "a drink added while multiple guests exist requires choosing
// whose it is" (docs/product-requirements.md P0.19, no default). With
// zero or exactly one guest, addDrinkToTicket is called directly (guestId
// omitted); src/domain/guests.ts's resolveLineGuestId does the zero/one
// resolution server-side, so there is nothing for the client to decide
// either way. With two or more guests, this opens the shared choice sheet
// first and only calls addDrinkToTicket once the bartender has picked.
async function addDrinkToTicketWithGuestPick(
  detail: MenuDrinkDetail,
  selection?: DrinkSelection,
): Promise<void> {
  const saleContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(saleContext)) return;
  if (state.guests.length <= 1) {
    await addDrinkToTicket(detail, selection);
    return;
  }
  openChoiceSheet({
    title: `Whose ${detail.menuDrink.name}?`,
    options: state.guests.map((guest) => ({
      id: guest.id,
      label: guest.label,
      testid: "add-drink-guest-option",
    })),
    onPick: (guestId) => {
      if (floorSaleContextIsCurrent(saleContext)) void addDrinkToTicket(detail, selection, guestId);
    },
  });
}

// ---------------------------------------------------------------------
// Inventory track (track/inventory) Slice 3, Amendment 2026-08-22
// (twelfth/thirteenth): direct inventory sales (pour, draft beer,
// packaged). See src/domain/inventorySale.ts's module header for the
// full pricing/depletion write-up this UI is driving; every dollar
// figure shown here (the size sheet's per-option price, the ticket
// line's own total) comes from a live server quote
// (quoteInventorySale/priceQuote's saleKind branch), never computed
// client-side, matching this codebase's "no estimated-price fallback
// anywhere in the money path" rule.
// ---------------------------------------------------------------------

// Amendment 2026-08-22 (twelfth) item 1/2: "picks the pour size from the
// bottle's pour options" (pour), "sold by pour size" (draft beer). Both
// share the identical per-ounce/pour-size shape (src/domain/catalog.ts's
// own comment on why draft beer reuses pourOunceOptions), so one sheet
// serves both; only the sale kind sent to the server differs. Reuses
// openChoiceSheet (the same touch-first, themed option-list sheet the
// guest picker and "whose drink" picker already use) rather than a new
// chip-and-confirm control: quoting every option up front lets picking a
// size ALSO be the confirm, one tap, matching "draft (size pick)" in the
// product owner's own framing of this flow.
async function openPourSizeSheet(
  product: LiquorProductDto,
  // ITEM2 Slice 5 (Section 3.4): widened from a closed four-literal
  // Extract to InventorySaleKind's own now-open `string` (never "packaged"
  // in practice, since proceedMenuItemTileClick's whole-unit branch above
  // never reaches this function; nothing in this function's own body
  // depends on which literal it is beyond passing it straight through to
  // quoteInventorySale/addInventorySaleToTicketWithGuestPick).
  saleKind: InventorySaleKind,
  // VOC1-F5 Slice 3 (DEC-161): true only when this call originated from
  // the override confirm sheet (proceedMenuItemTileClick above). Sent on
  // every quote this sheet requests AND on whichever size the bartender
  // finally picks, so the live preview does not 400 for a flagged product
  // under the permission.
  override86: Override86 = false,
): Promise<void> {
  const saleContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(saleContext)) return;
  const selling = floorProductSelling(product);
  if (!selling || !selling.active || selling.hideOnMenu || selling.state === "unconfigured") {
    showToast("This item is unavailable at this location.", "danger");
    return;
  }
  const pourOunceOptions = selling.pourOunceOptions ?? [];
  if (pourOunceOptions.length === 0) {
    showToast(`${product.name} has no pour sizes configured. Tell a manager.`, "danger");
    return;
  }
  const ounceStrings = pourOunceOptions.map((value) => String(value));
  // VOC1-F4-F2 (Section 2.5): fetched ONCE, up front -- every pour-size
  // option this sheet offers below is the same product, so this is folded
  // into both the single-pour-size direct-add branch immediately below and
  // the multi-size choice sheet's own onPick further down, rather than
  // re-fetched per size.
  const modifiers = await activeInventorySaleModifiers(product.id);
  // Review fix, 2026-09-23 (PR #68, floor): fetched once for every branch
  // below (the single-size direct-add and the multi-size choice sheet's own
  // onPick), so the failure check belongs here, before either can reach
  // addInventorySaleWithModifiersGate with a guessed empty list. See
  // activeInventorySaleModifiers's own comment for why undefined must not
  // be read as "no modifiers".
  if (modifiers === undefined || !floorSaleContextIsCurrent(saleContext)) {
    return;
  }
  // DEC-099: "the floor asks no size when an item has one." Applies to all
  // three sale kinds this function serves (pour, beer, wine), not
  // only to draft: a one-pour wine or a one-pour liquor gets the same
  // treatment. This skips the size-quote-and-price sheet entirely and adds
  // the line directly with the one available size.
  if (ounceStrings.length === 1) {
    const [theOnlyOunces] = ounceStrings;
    if (theOnlyOunces === undefined) {
      showToast(`${product.name} has no pour sizes configured. Tell a manager.`, "danger");
      return;
    }
    // VOC1-F4-F2 (Section 2.5, Section 1.4's "further fork"): identical
    // modifiers-sheet interception, the third of Section 1.4's no-sheet
    // paths.
    await addInventorySaleWithModifiersGate(
      product,
      saleKind,
      theOnlyOunces,
      override86,
      modifiers,
    );
    return;
  }
  const session = requireSession();
  const quotes = await Promise.all(
    ounceStrings.map(async (ounces) => {
      const result = await quoteInventorySale(session.apiBaseUrl, session.tenantId, {
        saleKind,
        liquorProductId: product.id,
        ounces,
        ...(override86 ? { override86: true } : {}),
      });
      return { ounces, result };
    }),
  );
  if (!floorSaleContextIsCurrent(saleContext)) return;
  openChoiceSheet({
    title: product.name,
    subtitle: "Pick a pour size",
    // INV19 Slice 1 (docs/build-briefs/INV19-pour-size-price-override.md):
    // a pour-price override can make one size's quote refuse (a negative
    // effective price after a tier reprice) while every other size still
    // prices fine. Before this, every option rendered as a live, tappable
    // button regardless of its own quote result, so a bartender could tap
    // the exact size already showing "Could not price" and trigger a
    // second, redundant failure at add-line time. `disabled: true` reuses
    // openChoiceSheet's own existing mechanism (ChoiceOption.disabled,
    // built for an already-collected split share): a non-2xx quote renders
    // as a plain, distinctly-styled div carrying its own refusal message,
    // never a still-tappable button.
    options: quotes.map(({ ounces, result }) => ({
      id: ounces,
      label: `${ounces} oz`,
      sublabel:
        result.status === 200
          ? formatCents(result.body.lineTotalCents)
          : (result.body.message ?? "Could not price"),
      disabled: result.status !== 200,
      testid: "pour-size-option",
    })),
    // VOC1-F4-F2 (Section 2.5): "picking a size still opens (or reuses) the
    // modifiers sheet rather than adding directly, when product.modifiers
    // is non-empty; when empty, onPick behaves exactly as today" -- the
    // fourth of Section 1.4's no-sheet paths, using the SAME `modifiers`
    // fetched once above rather than a second fetch per size pick.
    onPick: (ounces) => {
      if (floorSaleContextIsCurrent(saleContext)) {
        void addInventorySaleWithModifiersGate(product, saleKind, ounces, override86, modifiers);
      }
    },
  });
}

// ALPHA-12 parity: "a drink added while multiple guests exist requires
// choosing whose it is" applies identically to a direct-sale line; mirrors
// addDrinkToTicketWithGuestPick exactly.
async function addInventorySaleToTicketWithGuestPick(
  product: LiquorProductDto,
  saleKind: InventorySaleKind,
  ounces?: string,
  // VOC1-F5 Slice 3 (DEC-161). See openPourSizeSheet's own comment.
  override86: Override86 = false,
  // VOC1-F4-F2 (Section 2.5). The chip picks made on the inventory-sale
  // modifiers sheet, mirroring DrinkSelection.modifierSelections' own
  // identical contract; empty/omitted for a product with no attached
  // modifiers, the unchanged, byte-identical no-sheet add path.
  modifierSelections: string[] = [],
  // VOC1-F4-F2. This product's own attached modifiers (value/label/price),
  // needed only to build the ticket line's own display badges
  // (addInventorySaleToTicket's resolvedAppliedModifiers), mirroring
  // addDrinkToTicket's identical use of MenuDrinkDetail.modifiers. Empty
  // when modifierSelections is empty.
  offeredModifiers: MenuDrinkModifierDetailDto[] = [],
): Promise<void> {
  const saleContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(saleContext)) return;
  if (state.guests.length <= 1) {
    await addInventorySaleToTicket(
      product,
      saleKind,
      ounces,
      undefined,
      override86,
      modifierSelections,
      offeredModifiers,
    );
    return;
  }
  openChoiceSheet({
    title: `Whose ${product.name}?`,
    options: state.guests.map((guest) => ({
      id: guest.id,
      label: guest.label,
      testid: "add-drink-guest-option",
    })),
    onPick: (guestId) => {
      if (!floorSaleContextIsCurrent(saleContext)) return;
      void addInventorySaleToTicket(
        product,
        saleKind,
        ounces,
        guestId,
        override86,
        modifierSelections,
        offeredModifiers,
      );
    },
  });
}

async function addInventorySaleToTicket(
  product: LiquorProductDto,
  saleKind: InventorySaleKind,
  ounces?: string,
  guestId?: string,
  // VOC1-F5 Slice 3 (DEC-161). See openPourSizeSheet's own comment; the one
  // signal src/api/server.ts's resolveOverride86 reads before letting a
  // flagged product's line resolve.
  override86: Override86 = false,
  // VOC1-F4-F2 (Section 2.4). See addInventorySaleToTicketWithGuestPick's
  // own comment.
  modifierSelections: string[] = [],
  offeredModifiers: MenuDrinkModifierDetailDto[] = [],
): Promise<void> {
  const session = requireSession();
  const saleContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(saleContext)) return;

  if (state.orderId === undefined) {
    showToast("Resume or start a ticket first.", "danger");
    return;
  }
  const orderIdForSale = state.orderId;
  // One key for the whole flow, including any approval retry, so an approved
  // retry replays rather than selling twice (requestApproval's own contract).
  const saleKey = crypto.randomUUID();
  const result = await runPersistingSale(
    override86,
    `Sell 86'd item: ${product.name}`,
    (approval) =>
      addInventorySaleLine(session.apiBaseUrl, session.tenantId, orderIdForSale, {
        idempotencyKey: saleKey,
        saleKind,
        liquorProductId: product.id,
        ounces,
        guestId,
        // VOC1-F4-F2 (Section 2.4): the chip picks made on the inventory-sale
        // modifiers sheet, sent as bare `value` identifiers; the server
        // resolves each against this liquor product's own attachments and the
        // catalog's CURRENT label/price, never trusting a client-submitted
        // amount, identical contract to the cocktail add-line route.
        modifierSelections,
        ...override86Fields(override86),
        ...(approval ?? {}),
      }),
    () => floorSaleContextIsCurrent(saleContext),
  );
  if (result === undefined || !floorSaleContextIsCurrent(saleContext)) {
    return;
  }
  if (result.status !== 200 && result.status !== 201) {
    if (result.body.error === "guest_selection_required") {
      // Same real race addDrinkToTicket's own identical branch guards
      // against: the guest list changed between the picker opening and
      // this call landing.
      showToast(
        "This ticket's guest list changed. Reopen the menu and pick a guest again.",
        "danger",
      );
      return;
    }
    showToast(
      `Could not add ${product.name} (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  const orderLine = result.body.orderLine;
  // UX124: this device's own sale can move an item to Low or OUT.
  void refreshFloorMenuStock();
  // B5: for a shelf-priced straight pour, "ounces" (the client argument)
  // is undefined (the sell screen carries no pour-size picker on that
  // path at all, per handleMenuItemTileClick below); fall back to the
  // SERVER's own resolved amount (orderLine.pour_ounces, the location's
  // configured standard pour) so the ticket line shows the real amount
  // that left the bottle rather than nothing. For every other path
  // "ounces" is already the picker's own display value (e.g. "1.5"),
  // preferred here rather than the server's NUMERIC(4,2) echo (e.g.
  // "1.50"), a real formatting difference this fallback shape avoids
  // introducing into a display that never showed it before.
  const serverOunces = ounces ?? orderLine.pour_ounces ?? undefined;
  // VOC1-F4-F2 (Section 2.5): the catalog's own already-fetched label/price
  // for each SELECTED value, for the ticket line's own badges below,
  // mirroring addDrinkToTicket's identical resolvedAppliedModifiers.
  // Display only: money itself is exactly what the server just priced
  // (orderLine.line_total_cents, read through recomputeQuote below), never
  // recomputed from this list.
  const resolvedAppliedModifiers = offeredModifiers
    .filter((modifier) => modifierSelections.includes(modifier.value))
    .map((modifier) => ({
      label: modifier.label,
      priceAdjustmentCents: modifier.priceAdjustmentCents,
    }));
  const appliedModifiers =
    resolvedAppliedModifiers.length > 0 ? resolvedAppliedModifiers : undefined;
  const lineState: TicketLineState = {
    orderLineId: orderLine.id,
    lineKind: saleKind,
    name: product.name,
    // Mirrors a cocktail line's own "detail" row (there, the selected
    // liquor's name, since the line's own name is the drink); here the
    // line's name IS the item, so detail carries the pour size instead
    // (a whole-unit sale has no size to show, one whole unit, always).
    // ITEM2 Slice 5: used to also check `saleKind === "packaged"`, redundant
    // with `serverOunces === undefined` alone -- a whole-unit sale is never
    // sent an "ounces" argument and the server never returns a pour_ounces
    // for one either (src/domain/inventorySale.ts's own whole_unit branch
    // returns no pourOunces at all), so serverOunces is always undefined
    // for exactly the cases the old literal comparison caught, generalized
    // to any tenant-added whole-unit kind with no server-shape assumption.
    detail: serverOunces === undefined ? undefined : `${serverOunces} oz`,
    liquorProductId: product.id,
    ounces: serverOunces,
    appliedModifiers,
    modifierSelections: modifierSelections.length > 0 ? modifierSelections : undefined,
    preparedState: orderLine.prepared_state === "prepared" ? "prepared" : "not_prepared",
    guestId: orderLine.guest_id ?? undefined,
  };
  state.lines.push(lineState);
  renderTicket();
  clearMenuSearchAfterAdd();
  if (override86 !== false) {
    void refreshLineStockOverride(orderLine.id);
  }
  await recomputeQuote();
  if (!floorSaleContextIsCurrent(saleContext)) return;
  void refreshPaymentPreview();
  // Same best-effort signal a cocktail add carries; a pour, a draft beer
  // and a can grow the ticket the hold is measured against identically.
  handleHoldIncrementOutcome(result.body.holdIncrement);
}

// ---------------------------------------------------------------------
// Liquor selection sheet
// ---------------------------------------------------------------------

function liquorSheetSubtitleText(): string {
  if (state.sheet === undefined) {
    return "";
  }
  if (state.sheet.matchMiss) {
    return "This drink needs a recipe update before it can be rung.";
  }
  // VOC1-F4-F2: this function is only ever called from openLiquorSheet and
  // the cocktail row/expansion handlers below it, never while the sheet is
  // showing an inventory-sale item's own modifiers (that mode sets
  // liquorSheetSubtitle.textContent directly, openInventorySaleModifiers
  // Sheet's own comment); guarded here for the compiler's own sake, since
  // menuDrink is optional on SheetState now that a direct inventory-sale
  // item is not a menu drink at all.
  if (state.sheet.menuDrink === undefined) {
    return "";
  }
  const buildChargeText = `${formatCents(state.sheet.menuDrink.build_charge_cents)} build charge included`;
  if (state.sheet.ounces === undefined) {
    return buildChargeText;
  }
  return `${state.sheet.ounces} oz pour · ${buildChargeText}`;
}

function openLiquorSheet(
  detail: MenuDrinkDetail,
  changeLineOrderId?: string,
  drinkOverride86: Override86 = false,
  carriedModifiers?: PriceQuoteModifierInput[],
): void {
  if (detail.liquorSlots.length === 0) {
    showToast(
      "This drink has no configured liquor category; cannot open the selection sheet.",
      "danger",
    );
    return;
  }
  // ALPHA-07: is_liquor_slot (db/schema.ts's menuDrinkComponents) is now
  // server data the UI trusts directly. matchMiss covers both gaps
  // Review 5 flagged: no component marked is_liquor_slot at all, or one
  // marked but whose unit could not be parsed as a pour amount. Neither
  // ever falls back to an estimate; both block Add to Ticket below.
  const changedLine =
    changeLineOrderId === undefined
      ? undefined
      : state.lines.find((line) => line.orderLineId === changeLineOrderId);
  if (
    changeLineOrderId !== undefined &&
    (changedLine === undefined || carriedModifiers === undefined)
  ) {
    showToast("This drink is no longer on this ticket.", "danger");
    return;
  }
  const recipeMatch = findLiquorSlotComponent(detail.components);
  const matchMiss = recipeMatch === undefined;
  state.sheet = {
    saleContext: captureFloorSaleContext(),
    menuDrink: detail.menuDrink,
    components: detail.components,
    allowedCategories: detail.liquorSlots,
    liquorProducts: [],
    selectedLiquorProductId: undefined,
    ounces: recipeMatch?.ounces,
    browseAllActive: false,
    matchMiss,
    changeLineOrderId,
    carriedModifiers,
    drinkOverride86,
    // VOC1-F7 Slice 2 (DEC-172): a fresh, deliberate look at this drink's
    // liquor sheet every time it opens, whether a first Add or a Change
    // Liquor reopen; never carried over from a previous selection or a
    // previous look at this same drink.
    recipeDefaultOunces: recipeMatch?.ounces,
    pourModifierApplied: false,
    // VOC1-F4 (Section 2.4a): a liquor CHANGE carries a line's existing
    // modifiers through automatically, server-side, with no fresh
    // selection on this sheet (the design's own "no re-resolution against
    // the catalog is attempted here" for that path); the chip row only
    // ever offers a fresh choice for a genuinely new line.
    menuDrinkModifiers: changeLineOrderId === undefined ? detail.modifiers : [],
    selectedModifierValues: new Set(),
    modifiersOnlyMode: false,
    quoteRequestSeq: 0,
  };
  setLiquorSheetLiquorControlsVisible(true);
  renderLiquorRequiredFlag();

  if (matchMiss) {
    state.matchMissCount += 1;
    state.lastMatchMissDrinkName = detail.menuDrink.name;
    renderMatchMissDebug();
  }

  liquorSheetTitle.textContent =
    changeLineOrderId === undefined
      ? detail.menuDrink.name
      : `Change liquor: ${detail.menuDrink.name}`;
  liquorSheetSubtitle.textContent = liquorSheetSubtitleText();
  // Live-verification finding, ALPHA-08: this submit button's label was
  // static "Add to Ticket" markup in index.html, never updated for the
  // change-liquor purpose, even though the sheet title and the actual
  // command handleLiquorAddClick dispatches (change-liquor vs add-line)
  // both already branch correctly on changeLineOrderId. A bartender
  // changing a line's liquor saw "Add to Ticket" on the confirm button,
  // which reads as "this adds a second line," not "this changes the
  // existing one." Purely a label fix; no behavior changed.
  liquorAddButton.textContent = changeLineOrderId === undefined ? "Add to Ticket" : "Change Liquor";
  liquorSearch.value = "";
  if (matchMiss) {
    liquorPricePreview.textContent = "Recipe update needed";
    liquorPriceBreakdown.textContent =
      "This drink needs a recipe update before it can be rung. Tell a manager which liquor it should use.";
  } else {
    liquorPricePreview.textContent = "Select a liquor";
    liquorPriceBreakdown.textContent = "";
  }
  liquorPricePreview.classList.add("placeholder");
  liquorAddButton.disabled = true;
  liquorAddButton.classList.remove("enabled");
  liquorAddButton.classList.add("disabled");
  // ALPHA-10 item 2, 2026-08-20 amendment: the sheet always opens on the
  // drink's own allowed categories; "More spirits" is a fresh, deliberate
  // tap every time the sheet opens, never carried over from a previous
  // drink or a previous look at this same drink. Hidden entirely on a
  // matchMiss drink: Add to Ticket is blocked regardless of selection
  // there, so there is nothing a substitution could unlock.
  moreSpiritsButton.hidden = matchMiss;
  substitutionNoteEl.hidden = true;
  // VOC1-F7 Slice 2: no product is selected yet at open, so there is
  // nothing to offer sizes for; renderPourSizeChips re-shows this once a
  // row is picked.
  liquorPourSizeRow.hidden = true;
  liquorPourSizeRow.innerHTML = "";
  // VOC1-F4 (Section 2.5): rendered up front (chip prices are the
  // catalog's own already-fetched values, never a live quote), hidden
  // entirely by renderModifierChips itself when this drink has none.
  renderModifierChips();
  liquorScrim.hidden = false;
  liquorSearch.focus();

  void loadLiquorProducts(detail.liquorSlots, "");
  void loadRecentLiquors(detail.liquorSlots);
}

/** VOC1-F3: asks once, at open, for this employee's recent liquors in the drink's spirit types. A failed or late
 * read shows no strip and never blocks the sheet; a read for a sheet that has closed, or a session that has ended,
 * writes nothing. */
async function loadRecentLiquors(categories: readonly string[]): Promise<void> {
  const sheet = state.sheet;
  if (sheet === undefined || categories.length === 0) {
    return;
  }
  const session = requireSession();
  const gen = sessionGeneration;
  const result = await listRecentLiquors(session.apiBaseUrl, session.tenantId, categories);
  if (state.sheet !== sheet || !sessionIsCurrent(gen) || result.status !== 200) {
    return;
  }
  sheet.recentLiquorIds = result.body.recent.map((entry) => entry.liquorProductId);
  renderLiquorList();
}

/** VOC1-F3: the Recent strip. Built from the sheet's own product list (so a retired or hidden liquor can never
 * show), in the server's newest-first order, and only while the search box is empty. An 86'd or Out liquor is left
 * out. Nothing is selected here: a tap is the same explicit pick a list row is. */
function renderLiquorRecent(): void {
  const sheet = state.sheet;
  liquorRecentChipsEl.replaceChildren();
  const ids = sheet?.recentLiquorIds;
  if (sheet === undefined || ids === undefined || liquorSearch.value.trim() !== "") {
    liquorRecentEl.hidden = true;
    return;
  }
  const byId = new Map(sheet.liquorProducts.map((product) => [product.id, product]));
  const chips: LiquorProductDto[] = [];
  for (const id of ids) {
    const product = byId.get(id);
    if (product === undefined || product.flagged86 || product.quantityState === "out_of_stock") {
      continue;
    }
    chips.push(product);
  }
  liquorRecentEl.hidden = chips.length === 0;
  for (const product of chips) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "liquor-recent-chip";
    chip.dataset["testid"] = "liquor-recent-chip";
    chip.dataset["liquorProductId"] = product.id;
    chip.classList.toggle("selected", product.id === sheet.selectedLiquorProductId);
    const name = document.createElement("span");
    name.className = "liquor-recent-chip-name";
    name.textContent = product.name;
    const price = document.createElement("span");
    price.className = "liquor-recent-chip-price";
    const finalCents = sheet.liquorRowFinalCents?.get(product.id);
    price.textContent = finalCents === undefined ? "" : formatCents(finalCents);
    chip.append(name, price);
    chip.addEventListener("click", () => void handleLiquorRowClick(product));
    liquorRecentChipsEl.append(chip);
  }
}

// VOC1-F4 (Section 2.5): shows or hides the liquor-picking half of this
// shared sheet (search, browse list, More spirits, the substitution note,
// and the "Liquor selection required" banner), independent of the
// modifier chip row and the confirm control both modes share. True for
// every ordinary liquor sheet open; false only from openModifiersOnlySheet.
function setLiquorSheetLiquorControlsVisible(visible: boolean): void {
  liquorRequiredFlag.hidden = !visible;
  liquorSearchLine.hidden = !visible;
  liquorSearchRow.hidden = !visible;
  liquorList.hidden = !visible;
  moreSpiritsButton.hidden = !visible;
  if (!visible) {
    substitutionNoteEl.hidden = true;
  }
}

// UX14 (DEC-314): the status flag shares the pour line's row. Until a
// liquor is explicitly picked it reads "Liquor selection required" (nothing
// is ever pre-selected); once one is picked it names that liquor, so the
// state is still stated in words and not only by the highlighted row.
function renderLiquorRequiredFlag(): void {
  const sheet = state.sheet;
  const picked =
    sheet?.selectedLiquorProductId === undefined
      ? undefined
      : sheet.liquorProducts.find((candidate) => candidate.id === sheet.selectedLiquorProductId);
  if (picked === undefined) {
    liquorRequiredFlag.textContent = "Liquor selection required";
    liquorRequiredFlag.classList.remove("chosen");
    return;
  }
  // UIFIX5: once a liquor is picked the highlighted row names it; the flag says only that one is chosen, so the sheet
  // does not move on a pick and the name is not printed twice.
  liquorRequiredFlag.textContent = "Liquor chosen";
  liquorRequiredFlag.classList.add("chosen");
}

function closeLiquorSheet(): void {
  liquorScrim.hidden = true;
  state.sheet = undefined;
  liquorRecentEl.hidden = true;
  liquorRecentChipsEl.replaceChildren();
}

// VOC1-F4 (Section 2.5): a drink with requires_liquor_selection false (no
// liquor slot at all) that still carries at least one attached modifier.
// Reuses the SAME sheet and scrim openLiquorSheet uses (check:chrome-
// ratchet, and the design's own "reuse an existing sheet container"
// direction) rather than a new one: only the liquor-picking controls are
// hidden (setLiquorSheetLiquorControlsVisible(false)); the title, the
// modifier chip row and the confirm control are the same shared elements.
function openModifiersOnlySheet(
  detail: MenuDrinkDetail,
  drinkOverride86: Override86 = false,
): void {
  state.sheet = {
    saleContext: captureFloorSaleContext(),
    drinkOverride86,
    menuDrink: detail.menuDrink,
    components: detail.components,
    allowedCategories: [],
    liquorProducts: [],
    selectedLiquorProductId: undefined,
    ounces: undefined,
    browseAllActive: false,
    matchMiss: false,
    changeLineOrderId: undefined,
    recipeDefaultOunces: undefined,
    pourModifierApplied: false,
    menuDrinkModifiers: detail.modifiers,
    selectedModifierValues: new Set(),
    modifiersOnlyMode: true,
    quoteRequestSeq: 0,
  };
  setLiquorSheetLiquorControlsVisible(false);
  liquorSheetTitle.textContent = detail.menuDrink.name;
  liquorSheetSubtitle.textContent = `${formatCents(detail.menuDrink.build_charge_cents)} build charge included`;
  liquorAddButton.textContent = "Add to Ticket";
  liquorPourSizeRow.hidden = true;
  liquorPourSizeRow.innerHTML = "";
  liquorPricePreview.textContent = "Pricing...";
  liquorPricePreview.classList.add("placeholder");
  liquorPriceBreakdown.textContent = "";
  liquorAddButton.disabled = true;
  liquorAddButton.classList.remove("enabled");
  liquorAddButton.classList.add("disabled");
  renderModifierChips();
  liquorScrim.hidden = false;
  void updateModifiersOnlyPricePreview();
}

// VOC1-F4 (Section 2.5): the modifiers-only counterpart to
// updateLiquorPricePreview -- there is no product and no ounces to quote,
// only the drink's own build charge plus whichever modifiers are
// currently picked. Every price shown is still a live, server-computed
// quote, never client math.
function invalidateSheetQuote(sheet: SheetState): void {
  sheet.quoteReady = false;
  sheet.publicationBinding=undefined;
  liquorPricePreview.textContent = "Pricing...";
  liquorPricePreview.classList.add("placeholder");
  liquorPriceBreakdown.textContent = "";
  liquorAddButton.disabled = true;
  liquorAddButton.classList.remove("enabled");
  liquorAddButton.classList.add("disabled");
}

async function updateModifiersOnlyPricePreview(): Promise<void> {
  const sheet = state.sheet;
  if (sheet === undefined || !sheet.modifiersOnlyMode) {
    return;
  }
  // VOC1-F4-F2: modifiersOnlyMode is a cocktail-with-no-liquor-slot mode
  // only (openModifiersOnlySheet always sets menuDrink); guarded here for
  // the compiler's own sake now that SheetState.menuDrink is optional.
  if (sheet.menuDrink === undefined) {
    return;
  }
  invalidateSheetQuote(sheet);
  const session = requireSession();
  const menuDrinkId = sheet.menuDrink.id;
  const modifierSelections = [...sheet.selectedModifierValues];
  // VOC1-F4-F4: captured immediately before the request, so a response
  // arriving after a LATER modifier-chip tap has already incremented this
  // counter is recognized as stale below, even though it prices the same
  // menuDrinkId as the newer request.
  sheet.quoteRequestSeq += 1;
  const requestSeq = sheet.quoteRequestSeq;
  const drinkOverride86 = sheet.drinkOverride86 ?? false;
  const result = await priceQuote(session.apiBaseUrl, session.tenantId, [
    { menuDrinkId, modifierSelections, ...(drinkOverride86 ? { override86: true } : {}) },
  ]);
  if (
    state.sheet !== sheet ||
    !sheet.modifiersOnlyMode ||
    sheet.menuDrink === undefined ||
    sheet.menuDrink.id !== menuDrinkId ||
    sheet.quoteRequestSeq !== requestSeq
  ) {
    return; // the sheet closed, moved on, or a newer quote request has started
  }
  if (result.status !== 200) {
    // VOC1-F4-F3 (Section 2.1.b): a required or selected modifier retired
    // while this sheet stayed open. Drop it from this sheet's own copy and
    // re-quote with the corrected selection, rather than showing "Could
    // not price this drink" for a refusal the sheet itself can recover
    // from. Runs only after the staleness guard above has already
    // confirmed this response belongs to the current request.
    if (
      result.status === 400 &&
      result.body.error === "modifier_retired" &&
      result.body.retiredValue !== undefined
    ) {
      if (dropRetiredModifierAndRerender(sheet, result.body.retiredValue)) {
        await updateModifiersOnlyPricePreview(); // self-retry with the corrected selection
        return;
      }
    }
    liquorPricePreview.textContent = "Could not price this drink";
    liquorPricePreview.classList.add("placeholder");
    liquorPriceBreakdown.textContent = result.body.message ?? "";
    liquorAddButton.disabled = true;
    liquorAddButton.classList.remove("enabled");
    liquorAddButton.classList.add("disabled");
    return;
  }
  const line = result.body.lines[0];
  if (line === undefined || line.priceable === false) {
    liquorPricePreview.textContent = "Could not price this drink";
    liquorPricePreview.classList.add("placeholder");
    liquorPriceBreakdown.textContent =
      line !== undefined && line.priceable === false ? line.unpriceableReason : "";
    liquorAddButton.disabled = true;
    liquorAddButton.classList.remove("enabled");
    liquorAddButton.classList.add("disabled");
    return;
  }
  sheet.quoteReady = true;
  sheet.publicationBinding=line.publicationBinding;
  liquorPricePreview.textContent = formatCents(line.lineTotalCents);
  liquorPricePreview.classList.remove("placeholder");
  liquorPriceBreakdown.textContent = `${formatCents(sheet.menuDrink.build_charge_cents)} build charge`;
  const modifiersReady = allRequiredModifiersSelected(sheet);
  liquorAddButton.disabled = !modifiersReady;
  liquorAddButton.classList.toggle("enabled", modifiersReady);
  liquorAddButton.classList.toggle("disabled", !modifiersReady);
}

// VOC1-F4-F2 (Section 2.5). Fetches ONE liquor product's own attached
// modifiers, filtered to ACTIVE attachments only -- a retired attachment
// must never gate or render (mirrors the cocktail sheet's own
// active-only chip discipline, commit 6b28c73, not yet landed in this
// worktree's base; pre-filtering here means renderModifierChips/
// allRequiredModifiersSelected need no change of their own to get the
// same result for this mode). The data-fetch note in the brief's own
// Section 2.5 designs an eager bulk merge onto GET .../menu-items; that
// merge is not built server-side yet (VOC1-F4-F2 backend verification
// record, "What is not built"), so this is the per-product GET fallback
// that same section names, the identical lazy-fetch shape
// ensureMenuDrinkDetail already uses for a cocktail's own detail.
//
// Review fix, 2026-09-23 (PR #68, floor): a non-200 here (a customized
// session holding pos.line.inventory_sale but not admin.liquor_product.view
// is exactly this shape) used to return [], which every caller below reads
// as "no active modifiers" and routes straight to add-line. The server
// accepts an empty modifierSelections without complaint, so a product with
// a REQUIRED modifier got added with none, only failing later at Prepared
// and leaving a line that can never progress, which blocks Pay/Close.
// Returning undefined on failure -- mirroring ensureMenuDrinkDetail's own
// fail-closed shape just above for the cocktail path -- makes every caller
// (proceedMenuItemTileClick's two branches, openPourSizeSheet) refuse to
// add rather than guess.
async function activeInventorySaleModifiers(
  liquorProductId: string,
): Promise<MenuDrinkModifierDetailDto[] | undefined> {
  const session = requireSession();
  const result = await getLiquorProduct(session.apiBaseUrl, session.tenantId, liquorProductId);
  if (result.status !== 200) {
    showToast(
      `Could not check this item's options. ${capitalizeServerMessage(result.body.message ?? result.body.error ?? String(result.status))}`,
      "danger",
    );
    return undefined;
  }
  return result.body.modifiers.filter((modifier) => modifier.active);
}

// VOC1-F4-F2 (Section 2.5). The single gate every one of Section 1.4's
// no-sheet inventory-sale add paths (whole-unit, B5 shelf pour,
// single-pour-size) and the multi-pour-size choice sheet's own onPick all
// route through: a product with no active attached modifier adds exactly
// as it did before this item (one tap, no sheet, byte-identical); a
// product with at least one opens the shared sheet instead, and the
// sheet's own confirm becomes the add action.
async function addInventorySaleWithModifiersGate(
  product: LiquorProductDto,
  saleKind: InventorySaleKind,
  ounces: string | undefined,
  override86: Override86,
  modifiers: MenuDrinkModifierDetailDto[],
): Promise<void> {
  if (modifiers.length === 0) {
    await addInventorySaleToTicketWithGuestPick(product, saleKind, ounces, override86);
    return;
  }
  openInventorySaleModifiersSheet(product, saleKind, ounces, override86, modifiers);
}

// VOC1-F4-F2 (Section 2.5). Opens the shared liquor/modifiers sheet in a
// THIRD mode: no liquor picking at all, since this line's own liquor
// product IS the sold item, already chosen by the tile tap (or the
// pour-size pick that led here). Reuses the identical DOM elements
// openModifiersOnlySheet already uses; mirrors that function's own shape,
// substituting this product's own name/pour size for a cocktail's own
// name/build charge.
function openInventorySaleModifiersSheet(
  product: LiquorProductDto,
  saleKind: InventorySaleKind,
  ounces: string | undefined,
  override86: Override86,
  modifiers: MenuDrinkModifierDetailDto[],
): void {
  state.sheet = {
    saleContext: captureFloorSaleContext(),
    menuDrink: undefined,
    components: [],
    allowedCategories: [],
    liquorProducts: [],
    selectedLiquorProductId: undefined,
    ounces,
    browseAllActive: false,
    matchMiss: false,
    changeLineOrderId: undefined,
    recipeDefaultOunces: saleKind === "pour" ? ounces : undefined,
    pourModifierApplied: false,
    menuDrinkModifiers: modifiers,
    selectedModifierValues: new Set(),
    modifiersOnlyMode: false,
    inventorySaleProduct: { product, saleKind, ounces, override86 },
    inventorySaleQuoteRequestSeq: 0,
    // The cocktail sheet's own counter (f917ebf) is required on every
    // SheetState; this sheet quotes through its sibling counter above.
    quoteRequestSeq: 0,
  };
  setLiquorSheetLiquorControlsVisible(false);
  liquorSheetTitle.textContent = product.name;
  liquorSheetSubtitle.textContent = ounces === undefined ? "" : `${ounces} oz`;
  liquorAddButton.textContent = "Add to Ticket";
  liquorPourSizeRow.hidden = true;
  liquorPourSizeRow.innerHTML = "";
  if (saleKind === "pour") renderPourSizeChips(product);
  liquorPricePreview.textContent = "Pricing...";
  liquorPricePreview.classList.add("placeholder");
  liquorPriceBreakdown.textContent = "";
  liquorAddButton.disabled = true;
  liquorAddButton.classList.remove("enabled");
  liquorAddButton.classList.add("disabled");
  renderModifierChips();
  liquorScrim.hidden = false;
  void updateInventorySaleModifiersPreview();
}

// VOC1-F4-F2 (Section 2.5). The inventory-sale counterpart to
// updateModifiersOnlyPricePreview: no menu drink and no build charge here,
// the live quote is quoteInventorySale itself carrying the currently
// selected modifierSelections on top of this product's own per-ounce/
// flat price. Every price shown is still a live, server-computed quote,
// never client math. inventorySaleQuoteRequestSeq guards against a
// slower, earlier response landing after a later chip tap has already
// started a newer request (the same discard-a-superseded-response shape
// commit 6b28c73 gives the cocktail sheet's own quoteRequestSeq).
async function updateInventorySaleModifiersPreview(): Promise<void> {
  const sheet = state.sheet;
  if (sheet === undefined || sheet.inventorySaleProduct === undefined) {
    return;
  }
  const { product, saleKind, ounces, override86 } = sheet.inventorySaleProduct;
  invalidateSheetQuote(sheet);
  const session = requireSession();
  const modifierSelections = [...sheet.selectedModifierValues];
  sheet.inventorySaleQuoteRequestSeq = (sheet.inventorySaleQuoteRequestSeq ?? 0) + 1;
  const requestSeq = sheet.inventorySaleQuoteRequestSeq;
  const result = await quoteInventorySale(session.apiBaseUrl, session.tenantId, {
    saleKind,
    liquorProductId: product.id,
    ounces,
    modifierSelections,
    ...(override86 ? { override86: true } : {}),
  });
  if (
    state.sheet !== sheet ||
    state.sheet.inventorySaleProduct === undefined ||
    state.sheet.inventorySaleProduct.product.id !== product.id ||
    state.sheet.inventorySaleQuoteRequestSeq !== requestSeq
  ) {
    return; // the sheet closed, moved on, or a newer quote request has started
  }
  if (result.status !== 200) {
    // VOC1-F4-F3 (Section 2.1.b): a required or selected modifier retired
    // while this sheet stayed open. Drop it from this sheet's own copy and
    // re-quote with the corrected selection, rather than showing "Could
    // not price this item" for a refusal the sheet itself can recover
    // from. Runs only after the staleness guard above has already
    // confirmed this response belongs to the current request.
    if (
      result.status === 400 &&
      result.body.error === "modifier_retired" &&
      result.body.retiredValue !== undefined
    ) {
      if (dropRetiredModifierAndRerender(state.sheet, result.body.retiredValue)) {
        await updateInventorySaleModifiersPreview(); // self-retry with the corrected selection
        return;
      }
    }
    liquorPricePreview.textContent = "Could not price this item";
    liquorPricePreview.classList.add("placeholder");
    liquorPriceBreakdown.textContent = result.body.message ?? "";
    liquorAddButton.disabled = true;
    liquorAddButton.classList.remove("enabled");
    liquorAddButton.classList.add("disabled");
    return;
  }
  sheet.quoteReady = true;
  liquorPricePreview.textContent = formatCents(result.body.lineTotalCents);
  liquorPricePreview.classList.remove("placeholder");
  liquorPriceBreakdown.textContent = "";
  const modifiersReady = allRequiredModifiersSelected(state.sheet);
  liquorAddButton.disabled = !modifiersReady;
  liquorAddButton.classList.toggle("enabled", modifiersReady);
  liquorAddButton.classList.toggle("disabled", !modifiersReady);
}

// VOC1-F7 Slice 2 (DEC-172): resets the pour-size choice back to the
// recipe's own default. Called from loadLiquorProducts (a fresh browse or
// search is itself a fresh look) and selectLiquorRow (picking a different
// liquor row), so a modifier chosen for one bottle is never silently
// carried onto another whose own configured pour sizes may differ; the
// bartender must tap a chip again for the newly selected product. A no-op
// when the sheet is already closed.
function resetPourSelection(): void {
  if (state.sheet === undefined) {
    return;
  }
  state.sheet.ounces = state.sheet.recipeDefaultOunces;
  state.sheet.pourModifierApplied = false;
}

// categories: the drink's own allowed set (default view) or undefined
// (ALPHA-10 item 2's "More spirits" expansion: every active liquor,
// regardless of category). A single category behaves exactly as it
// always has; see api.ts's listLiquorProducts for the comma-separated
// multi-category request shape.
/** LIQSEARCH1: whether a pick of this product is a substitution: More spirits was tapped, or the product is an out-of-category search match. */
function isSubstitutionFor(sheet: SheetState, productId: string | undefined): boolean {
  return sheet.browseAllActive || (productId !== undefined && sheet.extraLiquorIds?.has(productId) === true);
}

async function loadLiquorProducts(categories: string[] | undefined, query: string): Promise<void> {
  const sheet = state.sheet;
  if (sheet === undefined) return;
  sheet.liquorListRequestSeq = (sheet.liquorListRequestSeq ?? 0) + 1;
  const listRequestSeq = sheet.liquorListRequestSeq;
  if (!sheet.matchMiss) {
    invalidateSheetQuote(sheet);
    liquorPricePreview.textContent = "Select a liquor";
  }
  sheet.quoteRequestSeq += 1;
  sheet.selectedLiquorProductId = undefined;
  liquorPourSizeRow.hidden = true;
  liquorPourSizeRow.replaceChildren();
  renderLiquorRequiredFlag();
  const session = requireSession();
  // AU13-F1: captured before the first await below.
  const gen = sessionGeneration;
  // VOC1-F7 Slice 2: a fresh list load prices every row against the
  // recipe-default ounces (loadLiquorRowPrices below), never a
  // chip-modified value from whichever product was previously selected --
  // that value may not be a configured size for every OTHER product in
  // this new list, which would fail the whole batched quote (Section 1's
  // "resolveAndPriceLine is not caught per-line" finding). The
  // just-selected row's own chip choice is re-made fresh once selected
  // again (selectLiquorRow's own identical reset).
  resetPourSelection();
  liquorList.innerHTML = '<p class="liquor-empty">Loading…</p>';
  // Inventory track Slice 3: itemKind "liquor" always, even under "More
  // spirits" (categories undefined): a cocktail's liquor slot must never
  // offer a draft beer or a packaged item as a substitution, so "every
  // active liquor, regardless of category" (this function's own header
  // comment) still means itemKind "liquor" specifically.
  // LIQSEARCH1: with text in the search box, search every active liquor at
  // once; the matches outside the drink's allowed categories are split off
  // below into their own substitution group.
  const searchAll = categories !== undefined && categories.length > 0 && query.trim().length > 0 && !sheet.matchMiss;
  const result = await listLiquorProducts(session.apiBaseUrl, session.tenantId, { locationId: terminalConfigState?.locationId,
    category: searchAll ? undefined : categories,
    q: query.length > 0 ? query : undefined,
    itemKind: "liquor",
  });
  if (
    state.sheet !== sheet ||
    sheet.liquorListRequestSeq !== listRequestSeq ||
    !sessionIsCurrent(gen)
  ) {
    return; // sheet was closed, or the session ended, while this request was in flight
  }
  if (result.status !== 200) {
    const reason = result.body.message ?? result.body.error ?? String(result.status);
    liquorList.innerHTML = `<p class="liquor-empty">Could not load liquors (${reason}).</p>`;
    showToast(
      `Could not load liquors. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  // DEC-208 (INV26 S2 follow-up): a hide_on_menu item is not offered
  // anywhere a bartender selects a drink or spirit, including this
  // cocktail liquor-slot selection sheet (the default category view and
  // the "More spirits" expansion, categories undefined, both reach this
  // same function). GET .../liquor-products itself stays unfiltered (the
  // admin list, counts, purchasing and inventory all read this same
  // route and must keep showing a hidden item); filtered here, client-
  // side, rather than with a new server query param, because hide_on_menu
  // is not sensitive data (already visible to every admin.liquor_product
  // .view session in the unfiltered admin list) and this floor session
  // already reads and re-filters this exact response by itemKind/query
  // right above -- one more boolean check is the smallest correct change,
  // not a new route contract threaded through every one of this route's
  // other callers (the two purchasing product searches deliberately stay
  // unfiltered, per DEC-208's own "purchasing... unfiltered").
  state.sheet.liquorProducts = result.body.liquorProducts.filter(
    (product) => { const selling = floorProductSelling(product); return selling !== undefined && selling.active && !selling.hideOnMenu && selling.state !== "unconfigured"; },
  );
  const allowedForSplit = categories;
  sheet.extraLiquorIds = searchAll && allowedForSplit !== undefined
    ? new Set(state.sheet.liquorProducts.filter((p) => !allowedForSplit.includes(p.category)).map((p) => p.id))
    : undefined;
  if (!sheet.browseAllActive) {
    substitutionNoteEl.hidden = true;
  }
  // VOC1-F2. A fresh list means every previous row's final price is
  // stale (a different product set, possibly a different category or
  // search), so the price and unavailable state are cleared before the row
  // renders, never left holding the PREVIOUS list's numbers while the new
  // batch is in flight.
  state.sheet.liquorRowFinalCents = undefined;
  state.sheet.liquorRowUnavailableIds = undefined;
  state.sheet.liquorRowNoPriceIds = undefined;
  renderLiquorList();
  // Prices the SAME filtered set renderLiquorList just rendered, so a
  // hidden row never gets a batched price request (or the "unavailable"
  // treatment loadLiquorRowPrices' own flagged-86 handling reserves for a
  // real, sellable row).
  await loadLiquorRowPrices(gen, state.sheet.liquorProducts);
}

// VOC1-F2 (Section 2.2): one batched POST .../price-quote per liquor-list
// load, never one request per row. `products` is the exact array this
// load just populated state.sheet.liquorProducts with (captured by the
// caller before this function's own first await); if state.sheet's own
// liquorProducts no longer IS that array by the time the response
// arrives, a newer load has already superseded this one and this
// response is discarded rather than overwriting a fresher list's prices.
async function loadLiquorRowPrices(
  gen: number,
  products: readonly LiquorProductDto[],
): Promise<void> {
  if (state.sheet === undefined) {
    return;
  }
  // VOC1-F4-F2: this function is only ever reached from loadLiquorProducts,
  // a cocktail-sheet-only browse/search load; an inventory-sale-modifiers
  // sheet has no liquor list at all. Captured into a local const (rather
  // than a non-null assertion at the one read site below) so the
  // compiler's own narrowing survives the await further down, now that
  // SheetState.menuDrink is optional.
  const menuDrinkForBatch = state.sheet.menuDrink;
  if (menuDrinkForBatch === undefined) {
    return;
  }
  const ounces = state.sheet.ounces;
  // ALPHA-07: matchMiss guarantees ounces is undefined (Section 1); there
  // is no recipe pour amount to price against, so every row is left
  // without a resolved price rather than sending the server a request it
  // would 400 for a missing "ounces". The sheet already blocks Add to
  // Ticket entirely in this state (openLiquorSheet).
  if (ounces === undefined) {
    return;
  }
  // Section 2.4: a flagged-86 row is excluded from the batch entirely,
  // since including it 400s the WHOLE request (Section 1's own finding).
  const unflagged = products.filter((product) => !product.flagged86);
  if (unflagged.length === 0) {
    return; // nothing priceable in this list; every row is flagged.
  }
  const session = requireSession();
  // ALPHA-10 item 2: mirrors updateLiquorPricePreview's own isSubstitution
  // source exactly (the sheet's browseAllActive at request time; LIQSEARCH1:
  // or the row is an out-of-category search match).
  const sheetForBatch = state.sheet;
  // VOC1-F7 Slice 2: mirrors updateLiquorPricePreview's own
  // pourModifierApplied source exactly. In practice this is always false
  // here (loadLiquorProducts resets it on every fresh list load, above),
  // since a chip-modified ounces value is specific to one product and this
  // batch prices every row in the list at the SAME ounces value; sent
  // anyway for the same reason isSubstitution is, so a future caller of
  // this function is not the first to need it.
  const pourModifierApplied = state.sheet.pourModifierApplied;
  const drinkOverrideForBatch = state.sheet.drinkOverride86 ?? false;
  const carriedModifiers = state.sheet.carriedModifiers;
  const changeLineOrderId = state.sheet.changeLineOrderId;
  const lines: PriceQuoteLineInput[] = unflagged.map((product) => ({
    menuDrinkId: menuDrinkForBatch.id,
    modifiers: carriedModifiers,
    changeLineOrderId,
    liquorProductId: product.id,
    ounces,
    isSubstitution: isSubstitutionFor(sheetForBatch, product.id),
    pourModifierApplied,
    ...(drinkOverrideForBatch ? { override86: true } : {}),
  }));
  const result = await priceQuote(session.apiBaseUrl, session.tenantId, lines);
  if (
    state.sheet === undefined ||
    state.sheet.liquorProducts !== products ||
    !sessionIsCurrent(gen)
  ) {
    return; // superseded by a newer list load, the sheet closed, or the session ended
  }
  const finalCents = new Map<string, number>();
  const unavailableIds = new Set<string>();
  const noPriceIds = new Set<string>();
  if (result.status !== 200) {
    // Section 2.4's second fallback: the whole batch failed outright.
    for (const product of unflagged) {
      unavailableIds.add(product.id);
    }
  } else {
    for (const line of result.body.lines) {
      if (line.liquorProductId === undefined) {
        continue;
      }
      if (line.priceable === false) {
        unavailableIds.add(line.liquorProductId);
        if (line.unpriceableReason.includes(NO_PRICE_REASON_FRAGMENT)) noPriceIds.add(line.liquorProductId);
      } else {
        finalCents.set(line.liquorProductId, line.lineTotalCents);
      }
    }
    // Defensive: a listed, unflagged product with no corresponding
    // response line at all (should not happen; the server returns one
    // line per submitted line, Section 1) is still marked unavailable
    // rather than silently showing nothing, matching this file's own
    // no-stale-no-silent-zero rule.
    for (const product of unflagged) {
      if (!finalCents.has(product.id) && !unavailableIds.has(product.id)) {
        unavailableIds.add(product.id);
      }
    }
  }
  state.sheet.liquorRowFinalCents = finalCents;
  state.sheet.liquorRowUnavailableIds = unavailableIds;
  state.sheet.liquorRowNoPriceIds = noPriceIds;
  renderLiquorList();
}

// Groups the current result list by liquor category, in a stable order:
// the drink's own allowedCategories first (in the order the server
// returned them, already sorted), then any other category present (only
// possible once "More spirits" has widened the search to every active
// liquor). Each group gets its own heading, matching the sheet's default
// single-category behavior when only one category is present.
const liquorMoreBelow = mustFind<HTMLElement>("#liquor-more-below");

/** UIFIX5: a fade and "More below" under the last visible row while the list has rows past the edge. */
function updateLiquorMoreHint(): void {
  const more = liquorList.scrollHeight - liquorList.clientHeight - liquorList.scrollTop > 8;
  liquorMoreBelow.hidden = liquorList.hidden || !more;
}
liquorList.addEventListener("scroll", () => updateLiquorMoreHint());

function renderLiquorList(): void {
  if (state.sheet === undefined) {
    return;
  }
  renderLiquorRecent();
  liquorList.innerHTML = "";
  // LIQPICK1 (Kerry 2026-10-24; DEC-269, DEC-276): an 86'd or out-of-stock liquor is not offered. Only a session
  // holding the sell-past-86 override can reveal them, through one line at the bottom of the list. LOW stays.
  const isUnavailable = (product: LiquorProductDto): boolean =>
    product.flagged86 || product.quantityState === "out_of_stock";
  const canReveal = canOverride86();
  const revealed = canReveal && state.sheet.showUnavailableLiquors === true;
  const hiddenCount = state.sheet.liquorProducts.filter(isUnavailable).length;
  const shown = revealed
    ? state.sheet.liquorProducts
    : state.sheet.liquorProducts.filter((product) => !isUnavailable(product));
  const revealLine = (): void => {
    if (!canReveal || revealed || hiddenCount === 0) {
      return;
    }
    const reveal = document.createElement("button");
    reveal.type = "button";
    reveal.className = "liquor-show-unavailable";
    reveal.dataset["testid"] = "liquor-show-unavailable";
    reveal.textContent = `Show ${hiddenCount} unavailable`;
    reveal.addEventListener("click", () => {
      if (state.sheet !== undefined) {
        state.sheet.showUnavailableLiquors = true;
        renderLiquorList();
      }
    });
    liquorList.append(reveal);
  };
  if (shown.length === 0) {
    const allUnavailable = hiddenCount > 0 && liquorSearch.value.trim() === "";
    liquorList.innerHTML = allUnavailable
      ? '<p class="liquor-empty" data-testid="liquor-none-available">No liquors available for this drink right now</p>'
      : '<p class="liquor-empty">No active liquors match. Try a different search.</p>';
    revealLine();
    liquorMoreBelow.hidden = true;
    return;
  }
  const byCategory = new Map<string, LiquorProductDto[]>();
  for (const product of shown) {
    const group = byCategory.get(product.category) ?? [];
    group.push(product);
    byCategory.set(product.category, group);
  }
  // UX88 (DEC-339): the spirit groups read A to Z by the heading shown,
  // whether they are the drink's own allowed categories or the wider
  // "More spirits" set, and the liquors inside each group read A to Z too.
  const orderedCategories = sortByName([...byCategory.keys()], (category) =>
    categoryLabel(category),
  );

  // LIQSEARCH1: the allowed-category matches keep their category headings;
  // out-of-category search matches follow under one plain substitution heading.
  const extraIds = state.sheet.extraLiquorIds;
  const groups: Array<{ title: string; products: LiquorProductDto[]; extra: boolean }> = [];
  for (const category of orderedCategories) {
    const inGroup = (byCategory.get(category) ?? []).filter((p) => extraIds?.has(p.id) !== true);
    if (inGroup.length > 0) {
      groups.push({ title: categoryLabel(category), products: sortByName(inGroup, (p) => p.name), extra: false });
    }
  }
  const extras = shown.filter((p) => extraIds?.has(p.id) === true);
  if (extras.length > 0) {
    groups.push({ title: "Other spirits, picked as a substitution", products: sortByName(extras, (p) => p.name), extra: true });
  }

  for (const { title, products, extra } of groups) {
    const groupTitle = document.createElement("div");
    groupTitle.className = "liquor-group-title";
    if (extra) {
      groupTitle.dataset["testid"] = "liquor-group-substitution";
    }
    groupTitle.textContent = title;
    liquorList.append(groupTitle);

    for (const product of products) {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "liquor-row";
      row.dataset["testid"] = "liquor-row";
      row.dataset["liquorProductId"] = product.id;
      if (product.id === state.sheet.selectedLiquorProductId) {
        row.classList.add("selected");
      }
      const badge = stockStateBadge(product);
      // VOC1-F5 Slice 2/3: the real `disabled` attribute, not a class or a
      // guard inside the click handler -- a tap on a disabled button is a
      // platform no-op, so no click handler fires and no request is ever
      // sent for a flagged product. The "86'd" badge below is the visible
      // reason, never a title tooltip (floor rule). The server's own
      // refusal (resolveAndPriceLine) is the backstop, not the only guard.
      // Slice 3: a session holding inv.stock_flag.override is not blocked
      // here, in "Change liquor" mode (changeLineOrderId defined) or a
      // fresh add -- changeLineLiquor now accepts override86 the same way
      // add-line does (server fix, DEC-161), so the same permission gates
      // both flows identically.
      row.disabled = product.flagged86 && !canOverride86();
      row.innerHTML = `
        <span class="liquor-check">&#10003;</span>
        <span class="liquor-name-block">
          <span class="liquor-name"></span>
          <span class="liquor-spirit-type"></span>
        </span>
        ${
          badge === undefined
            ? ""
            : `<span class="stock-state-badge stock-state-badge--${badge.modifier}" data-testid="stock-badge"></span>`
        }
        <span class="liquor-price">
          <span class="liquor-price-final"></span>
        </span>
      `;
      row.querySelector(".liquor-name")!.textContent = product.name;
      // UIFIX5: a row the count says is out is dimmed and says so in words in place of its price. It stays
      // selectable: whether an out-of-stock pour may be sold is a stock rule, not a display rule.
      const rowIsOut = badge?.modifier === "out";
      row.classList.toggle("liquor-row--out", rowIsOut);
      // DEC-162: the spirit type (the product's own category) shows per
      // row, reading the same human vocabulary label the group heading
      // above already uses, never the raw stored key.
      // UX14 (DEC-314): under a spirit group heading the type is the
      // heading, so the row does not repeat it (that repeat is what made
      // each row 59px). A search result keeps it: with a query typed the
      // rows are matches, and the type is the cue that says which spirit
      // each one is.
      const typeEl = row.querySelector<HTMLElement>(".liquor-spirit-type")!;
      if (liquorSearch.value.trim() === "") {
        typeEl.remove();
      } else {
        typeEl.textContent = categoryLabel(product.category);
      }
      if (badge !== undefined) {
        row.querySelector(".stock-state-badge")!.textContent = badge.label;
      }
      // VOC1-F2 (Section 2.1, 2.4): every row's price block is ONE of
      // three states, and never a fourth: no text at all for a flagged-86
      // row (the badge above is already the full explanation); "Price
      // unavailable" for a row this load's batch could not price; or the
      // server's own final price. A row not yet covered by either map
      // (the batch is still in flight) intentionally renders no price
      // text, the same blank state a flagged row shows, rather than a
      // stale or estimated figure.
      const priceFinalEl = row.querySelector<HTMLElement>(".liquor-price-final")!;
      if (rowIsOut) {
        priceFinalEl.textContent = "Out of stock";
        priceFinalEl.classList.add("out-of-stock");
      } else if (!product.flagged86) {
        if (state.sheet.liquorRowUnavailableIds?.has(product.id)) {
          const noPrice = state.sheet.liquorRowNoPriceIds?.has(product.id) === true;
          priceFinalEl.textContent = noPrice ? TILE_NO_PRICE_TEXT : "Price unavailable";
          priceFinalEl.classList.add("unavailable");
        } else {
          const finalCents = state.sheet.liquorRowFinalCents?.get(product.id);
          if (finalCents !== undefined) {
            priceFinalEl.textContent = formatCents(finalCents);
          }
        }
      }
      row.addEventListener("click", () => void handleLiquorRowClick(product));
      liquorList.append(row);
    }
  }
  revealLine();
  requestAnimationFrame(() => updateLiquorMoreHint());
}

async function handleLiquorRowClick(product: LiquorProductDto): Promise<void> {
  if (state.sheet === undefined) {
    return;
  }
  // VOC1-F5 Slice 3 (DEC-161): this branch is only ever reached for a
  // flagged row when the row was NOT disabled, which per renderLiquorList
  // above only happens for a session holding inv.stock_flag.override,
  // whether adding a new line or changing an existing one's liquor.
  // Confirming re-enters selectLiquorRow with override86 true, which
  // updateLiquorPricePreview and handleLiquorAddClick both read off
  // state.sheet.overrideSelected.
  if (product.flagged86 && canOverride86()) {
    void openOverride86ConfirmSheet(product, (choice) => void selectLiquorRow(product, choice));
    return;
  }
  await selectLiquorRow(product, false);
}

async function selectLiquorRow(product: LiquorProductDto, override86: Override86): Promise<void> {
  if (state.sheet === undefined) {
    return;
  }
  // VOC1-F7 Slice 2 (DEC-172): picking a (possibly different) liquor row is
  // itself a fresh look; whatever chip was previously tapped for a
  // different product is never carried over, since that product's own
  // configured pour sizes may not include it.
  resetPourSelection();
  state.sheet.selectedLiquorProductId = product.id;
  state.sheet.overrideSelected = override86;
  // LIQSEARCH1: an out-of-category search match carries the same plain note More spirits shows.
  substitutionNoteEl.hidden = !isSubstitutionFor(state.sheet, product.id);
  renderLiquorList();
  renderLiquorRequiredFlag();
  if (state.sheet.matchMiss) {
    // No component matched this drink's liquor slot at all (see
    // SheetState.matchMiss's doc comment). The sheet just shows which
    // liquor is highlighted and leaves Add to Ticket disabled and the
    // "needs a recipe update" message in place, no matter which liquor is
    // tapped: there is no back door into pricing a guess.
    liquorPourSizeRow.hidden = true;
    return;
  }
  // matchMiss false guarantees state.sheet.ounces was already set to the
  // server-sourced pour amount when the sheet opened (openLiquorSheet);
  // there is no client-side pour fallback to compute here any more.
  liquorSheetSubtitle.textContent = liquorSheetSubtitleText();
  renderPourSizeChips(product);
  await updateLiquorPricePreview(product);
}

// POURREC1/DEC-463: base is automatic; only Double and Triple are explicit alternatives.
function pourSizeChipOptions(): string[] {
  const base = state.sheet?.recipeDefaultOunces;
  if (base === undefined) return [];
  const normalized = normalizeOunces(base);
  const hundredths = BigInt(normalized.replace(".", ""));
  if (hundredths <= 0n) return [];
  return [2n, 3n].map(multiplier => hundredths * multiplier)
    .filter(amount => amount <= 9999n)
    .map(amount => `${amount / 100n}.${String(amount % 100n).padStart(2, "0")}`);
}

function renderPourSizeChips(product: LiquorProductDto): void {
  liquorPourSizeRow.replaceChildren();
  if (state.sheet === undefined || state.sheet.matchMiss) {
    liquorPourSizeRow.hidden = true;
    return;
  }
  const options = pourSizeChipOptions();
  liquorPourSizeRow.hidden = options.length === 0;
  const base = normalizeOunces(state.sheet.recipeDefaultOunces!);
  const selected = state.sheet.ounces === undefined ? base : normalizeOunces(state.sheet.ounces);
  const baseHundredths = BigInt(base.replace(".", ""));
  for (const ounces of options) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "pour-size-chip";
    chip.dataset["testid"] = "pour-size-chip";
    chip.dataset["ounces"] = ounces;
    const multiple = BigInt(ounces.replace(".", "")) / baseHundredths;
    chip.textContent = `${multiple === 2n ? "Double" : "Triple"} · ${ounces} oz`;
    chip.classList.toggle("selected", ounces === selected);
    chip.setAttribute("aria-pressed", String(ounces === selected));
    chip.onclick = () => void handlePourSizeChipClick(product, ounces);
    liquorPourSizeRow.append(chip);
  }
}

async function handlePourSizeChipClick(product: LiquorProductDto, ounces: string): Promise<void> {
  const sheet = state.sheet;
  if (sheet === undefined || sheet.recipeDefaultOunces === undefined) return;
  const base = normalizeOunces(sheet.recipeDefaultOunces);
  const next = sheet.ounces !== undefined && normalizeOunces(sheet.ounces) === ounces ? base : ounces;
  sheet.ounces = next;
  sheet.pourModifierApplied = next !== base;
  renderPourSizeChips(product);
  if (sheet.inventorySaleProduct !== undefined) {
    sheet.inventorySaleProduct.ounces = next;
    liquorSheetSubtitle.textContent = `${next} oz`;
    await updateInventorySaleModifiersPreview();
  } else {
    liquorSheetSubtitle.textContent = liquorSheetSubtitleText();
    await updateLiquorPricePreview(product);
  }
}

// VOC1-F4-F3 (Section 2.1.b): drops one retired attachment from THIS
// sheet's own in-memory copy and its own selection, then re-renders so the
// identical .active filter renderModifierChips/allRequiredModifiersSelected
// already apply (below, unchanged by this item) removes the chip and
// un-gates confirm, the same way it already does for a modifier retired
// BEFORE the sheet opened (VOC1-F4-F4). Returns false, doing nothing, when
// `value` names no currently-active entry on this sheet: already dropped
// by an earlier call this same interaction, or a value this sheet was
// never showing at all. That false is what stops the caller's own retry
// from ever looping (see each preview function's own retry below).
function dropRetiredModifierAndRerender(sheet: SheetState, value: string): boolean {
  const modifier = sheet.menuDrinkModifiers.find((m) => m.value === value && m.active);
  if (modifier === undefined) {
    return false;
  }
  modifier.active = false;
  sheet.selectedModifierValues.delete(value);
  renderModifierChips();
  showToast(`${modifier.label} is no longer available and was removed.`, "neutral");
  return true;
}

// VOC1-F4 (Section 2.5): true whenever every REQUIRED, ACTIVE attachment
// has a matching chip pick. An optional attachment never affects this,
// and neither does a RETIRED one (VOC1-F4-F4): listMenuDrinkModifiers
// returns every attachment regardless of the catalog row's current
// active flag, so a required-but-retired attachment must not keep
// blocking a sheet the bartender has no active chip left to resolve it
// with. Empty menuDrinkModifiers (the common case, a drink with no
// attachments at all) is vacuously true, so this never blocks a drink
// that carries no modifiers.
function allRequiredModifiersSelected(sheet: SheetState): boolean {
  const all=sheet.menuDrinkModifiers;
  if(!all.filter(option=>option.groupId==null&&option.required).every(option=>option.active&&sheet.selectedModifierValues.has(option.value)))return false;
  const requiredGroups=new Set(all.filter(option=>option.groupId!=null&&option.groupRequired).map(option=>option.groupId));
  return [...requiredGroups].every(id=>all.filter(option=>option.groupId===id&&option.active&&sheet.selectedModifierValues.has(option.value)).length===1);
}

// VOC1-F4 (Section 2.5): one chip per modifier attached to this drink,
// hidden entirely when there are none. Every price shown is the catalog's
// own server-resolved value (MenuDrinkModifierDetailDto.priceAdjustmentCents,
// already fetched with the drink's own detail), never a fresh quote just
// to paint a chip: the live quote below (updateLiquorPricePreview /
// updateModifiersOnlyPricePreview) is what prices the WHOLE line, this row
// only shows each modifier's own fixed adjustment. No numeric input exists
// on this row at all (money-never-keyed-during-a-transaction).
function renderModifierChips(): void {
  liquorModifierRow.replaceChildren();
  const sheet=state.sheet;
  if(sheet===undefined||sheet.menuDrinkModifiers.length===0){liquorModifierRow.hidden=true;return}
  const groups=new Map<string,{label:string;required:boolean;options:MenuDrinkModifierDetailDto[]}>();
  for(const option of sheet.menuDrinkModifiers){
    const key=option.groupId??`option:${option.value}`;
    const group=groups.get(key)??{label:option.groupId==null?option.label:option.groupLabel??"Ingredient",required:option.groupId==null?option.required:option.groupRequired===true,options:[]};
    group.options.push(option);groups.set(key,group);
  }
  liquorModifierRow.hidden=groups.size===0;
  for(const group of groups.values()){
    const active=sortByName(group.options.filter(option=>option.active),option=>option.label);
    if(active.length===0&&!group.required)continue;
    const label=document.createElement("label");label.className="ingredient-choice-control";
    label.append(document.createTextNode(group.label+(group.required?" (Required)":"")));
    const select=document.createElement("select");select.className="field-input";select.setAttribute("aria-label",group.label);
    const empty=document.createElement("option");empty.value="";empty.textContent=active.length===0?"No choices":group.required?`Choose ${group.label}`:"No choice";empty.disabled=group.required;select.append(empty);
    for(const option of active){
      const entry=document.createElement("option");entry.value=option.value;
      const amount=option.quantityPerUse==null?"":`${Number(option.quantityPerUse).toFixed(2)} ${option.unit==="unit"?"each":option.unit??""}`;
      entry.textContent=`${option.label}${amount?` · ${amount}`:""} · +${formatCents(option.priceAdjustmentCents)}`;select.append(entry);
    }
    select.value=active.find(option=>sheet.selectedModifierValues.has(option.value))?.value??"";
    select.disabled=active.length===0;
    select.addEventListener("change",()=>{
      if(state.sheet!==sheet)return;
      const selected=group.options.find(option=>sheet.selectedModifierValues.has(option.value));
      const next=select.value||selected?.value;
      if(next!==undefined)void handleModifierChipClick(next);
    });
    label.append(select);liquorModifierRow.append(label);
  }
}

// VOC1-F4 (Section 2.5): toggles this one chip's selection, re-renders the
// row (selected styling) and re-quotes the whole line so the total shown
// includes (or drops) this modifier's adjustment and Add to Ticket's own
// enablement (which also depends on every required chip being picked, see
// allRequiredModifiersSelected) is re-evaluated -- the identical
// "toggle, re-render, re-quote" shape handlePourSizeChipClick already uses
// for the pour-size row just above it.
async function handleModifierChipClick(value: string): Promise<void> {
  if (state.sheet === undefined) {
    return;
  }
  if (state.sheet.selectedModifierValues.has(value)) {
    state.sheet.selectedModifierValues.delete(value);
  } else {
    const chosen = state.sheet.menuDrinkModifiers.find((modifier) => modifier.value === value);
    if (chosen?.groupId != null) {
      for (const option of state.sheet.menuDrinkModifiers)
        if (option.groupId === chosen.groupId)
          state.sheet.selectedModifierValues.delete(option.value);
    }
    state.sheet.selectedModifierValues.add(value);
  }
  renderModifierChips();
  if (state.sheet.modifiersOnlyMode) {
    await updateModifiersOnlyPricePreview();
    return;
  }
  // VOC1-F4-F2 (Section 2.5): the inventory-sale-modifiers sheet's own live
  // quote (quoteInventorySale, not priceQuote -- there is no menu drink to
  // price against).
  if (state.sheet.inventorySaleProduct !== undefined) {
    await updateInventorySaleModifiersPreview();
    return;
  }
  const product = state.sheet.liquorProducts.find(
    (candidate) => candidate.id === state.sheet?.selectedLiquorProductId,
  );
  if (product !== undefined) {
    await updateLiquorPricePreview(product);
  }
}

async function updateLiquorPricePreview(product: LiquorProductDto): Promise<void> {
  const sheet = state.sheet;
  if (sheet === undefined) {
    return;
  }
  // VOC1-F4-F2: this function is only ever reached from a cocktail liquor
  // row selection or its own pour-size/modifier chip taps, never from the
  // inventory-sale-modifiers sheet (that mode's own live quote is
  // updateInventorySaleModifiersPreview below). Captured into a local
  // const so the compiler's own narrowing survives the await further down,
  // now that SheetState.menuDrink is optional.
  const menuDrink = sheet.menuDrink;
  if (menuDrink === undefined) {
    return;
  }
  const ounces = sheet.ounces;
  if (ounces === undefined) {
    return;
  }
  invalidateSheetQuote(sheet);
  const session = requireSession();
  // ALPHA-10 item 2: isSubstitution mirrors browseAllActive exactly, at
  // the moment this selection was made, not recomputed from category
  // membership. Tapping "More spirits" is the deliberate act that makes a
  // selection a substitution (SheetState.browseAllActive's own comment);
  // whether the picked liquor happens to also be in allowedCategories
  // does not change that.
  const isSubstitution = isSubstitutionFor(sheet, sheet.selectedLiquorProductId);
  // VOC1-F5 Slice 3 (DEC-161): the live preview must carry the same
  // override86 signal the eventual add-line will, so a flagged product's
  // preview does not 400 for a session that already confirmed the override
  // (selectLiquorRow above).
  const override86 =
    sheet.overrideSelected || (sheet.drinkOverride86 ?? false) || false;
  // VOC1-F7 Slice 2 (Section 2.5): the chip-picked value, exactly as
  // recorded by handlePourSizeChipClick; the recipe default itself always
  // carries false, matching the server's own "ignored when ounces already
  // equals the recipe default" rule.
  const pourModifierApplied = sheet.pourModifierApplied;
  // VOC1-F4 (Section 2.5): the chip picks made this sheet session, so the
  // live preview's own total already includes every selected modifier's
  // adjustment; a partial or empty selection is accepted unconditionally
  // by the server (Section 2.4's own "not enforcing every required
  // modifier is selected here"), so this quote never fails merely because
  // a required chip is still unpicked -- allRequiredModifiersSelected
  // below is what gates Add to Ticket for that.
  const modifierSelections = [...sheet.selectedModifierValues];
  // VOC1-F4-F4: captured immediately before the request, so a response
  // arriving after a LATER pour-size or modifier chip tap (or a fresh row
  // selection) has already incremented this counter is recognized as
  // stale below, even when both requests priced the same product: the
  // product-id-only guard this replaced could not tell "the same row,
  // re-quoted with a newer ounces/modifier selection" apart from "a
  // genuinely stale response", and an earlier, slower response landing
  // last would overwrite the preview -- and Add to Ticket's own
  // enablement -- with the previous selection's price while the CURRENT
  // selection is what Add to Ticket would actually submit.
  sheet.quoteRequestSeq += 1;
  const requestSeq = sheet.quoteRequestSeq;
  const result = await priceQuote(session.apiBaseUrl, session.tenantId, [
    {
      menuDrinkId: menuDrink.id,
      liquorProductId: product.id,
      ounces,
      isSubstitution,
      pourModifierApplied,
      modifierSelections,
      modifiers: sheet.carriedModifiers,
      changeLineOrderId: sheet.changeLineOrderId,
      ...(override86 ? { override86: true } : {}),
    },
  ]);
  if (
    state.sheet !== sheet ||
    sheet.selectedLiquorProductId !== product.id ||
    sheet.quoteRequestSeq !== requestSeq
  ) {
    return; // a different row was selected, or a newer quote request has started, while this one was in flight
  }
  if (result.status !== 200) {
    // VOC1-F4-F3 (Section 2.1.b): a required or selected modifier retired
    // while this sheet stayed open. Drop it from this sheet's own copy and
    // re-quote with the corrected selection, rather than showing "Could
    // not price this drink" for a refusal the sheet itself can recover
    // from. Runs only after the staleness guard above has already
    // confirmed this response belongs to the current request.
    if (
      result.status === 400 &&
      result.body.error === "modifier_retired" &&
      result.body.retiredValue !== undefined
    ) {
      if (dropRetiredModifierAndRerender(sheet, result.body.retiredValue)) {
        await updateLiquorPricePreview(product); // self-retry with the corrected selection
        return;
      }
    }
    liquorPricePreview.textContent = "Could not price this drink";
    liquorPricePreview.classList.add("placeholder");
    liquorPriceBreakdown.textContent = result.body.message ?? "";
    liquorAddButton.disabled = true;
    liquorAddButton.classList.remove("enabled");
    liquorAddButton.classList.add("disabled");
    return;
  }
  // INV19 Slice 2 made a quoted line a union: priced, or unpriceable with
  // the server's own reason and NO lineTotalCents. Refuse here rather
  // than falling back to a number, because the only fallback available
  // is 0, and rendering $0.00 beside an enabled Add button for a drink
  // whose price did not resolve is a money defect: it offers a free
  // drink. This mirrors the non-200 branch directly above.
  //
  // A shelf cocktail can also be unpriceable when its configured pour delta makes its base negative.
  const line = result.body.lines[0];
  if (line === undefined || line.priceable === false) {
    liquorPricePreview.textContent = "Could not price this drink";
    liquorPricePreview.classList.add("placeholder");
    liquorPriceBreakdown.textContent =
      line !== undefined && line.priceable === false ? line.unpriceableReason : "";
    liquorAddButton.disabled = true;
    liquorAddButton.classList.remove("enabled");
    liquorAddButton.classList.add("disabled");
    return;
  }
  sheet.quoteReady = true;
  sheet.publicationBinding=line.publicationBinding;
  liquorPricePreview.textContent = formatCents(line.lineTotalCents);
  liquorPricePreview.classList.remove("placeholder");
  const breakdownText = state.pricingModel === "shelf"
    ? `${product.name} · Shelf price`
    : floorProductSelling(product)?.perOuncePriceCents == null ? product.name
    : `${formatCents(menuDrink.build_charge_cents)} build charge + ` +
      `${product.name} ${ounces} oz × ${formatCents(floorProductSelling(product)!.perOuncePriceCents!)}/oz`;
  // Plain language on the price breakdown itself, not only the persistent
  // banner above the list: the substitution stays visible right where the
  // bartender is about to confirm it.
  liquorPriceBreakdown.textContent = isSubstitution
    ? `${breakdownText} (substitution)`
    : breakdownText;
  // VOC1-F4 (Section 2.5): a priced line does not by itself mean Add to
  // Ticket may fire -- every required modifier chip must also be picked.
  const modifiersReady = allRequiredModifiersSelected(sheet);
  liquorAddButton.disabled = !modifiersReady;
  liquorAddButton.classList.toggle("enabled", modifiersReady);
  liquorAddButton.classList.toggle("disabled", !modifiersReady);
}

async function handleLiquorSearchInput(): Promise<void> {
  if (state.sheet === undefined) {
    return;
  }
  if (searchDebounceTimer !== undefined) {
    clearTimeout(searchDebounceTimer);
  }
  const query = liquorSearch.value;
  searchDebounceTimer = setTimeout(() => {
    // UX14-F3: the categories are read when the timer fires, not when the key
    // was typed, so a More spirits tap inside the 150 ms is not overwritten by
    // the narrow list. The sheet may also have closed meanwhile.
    if (state.sheet === undefined) {
      return;
    }
    const categories = state.sheet.browseAllActive ? undefined : state.sheet.allowedCategories;
    void loadLiquorProducts(categories, query);
  }, 150);
}

// ALPHA-10 item 2, 2026-08-20 amendment: the explicit, plainly-labeled
// expansion from the drink's own allowed categories to every active
// liquor. A one-way switch for the life of this sheet: there is no UI to
// collapse back to the default view once tapped, matching "standard
// choices first, substitution behind an explicit expansion" without
// adding a second control to undo it mid-selection.
async function handleMoreSpiritsClick(): Promise<void> {
  if (state.sheet === undefined || state.sheet.matchMiss) {
    return;
  }
  state.sheet.browseAllActive = true;
  state.sheet.selectedLiquorProductId = undefined;
  renderLiquorRequiredFlag();
  moreSpiritsButton.hidden = true;
  substitutionNoteEl.hidden = false;
  liquorPricePreview.textContent = "Select a liquor";
  liquorPricePreview.classList.add("placeholder");
  liquorPriceBreakdown.textContent = "";
  liquorAddButton.disabled = true;
  liquorAddButton.classList.remove("enabled");
  liquorAddButton.classList.add("disabled");
  await loadLiquorProducts(undefined, liquorSearch.value);
}

async function handleLiquorAddClick(): Promise<void> {
  if (state.sheet === undefined || state.sheet.quoteReady !== true ||
      !floorSaleContextIsCurrent(state.sheet.saleContext)) {
    return;
  }
  // VOC1-F4-F2 (Section 2.5): the inventory-sale-modifiers branch (no
  // liquor slot at all -- the line's own liquor product IS the sold item,
  // already chosen by the tile tap; openInventorySaleModifiersSheet).
  // Defensive, matching every other guard here: the confirm control is
  // already disabled until every required chip is picked
  // (allRequiredModifiersSelected, threaded through
  // updateInventorySaleModifiersPreview).
  if (state.sheet.inventorySaleProduct !== undefined) {
    if (!allRequiredModifiersSelected(state.sheet)) {
      return;
    }
    const { product, saleKind, ounces, override86 } = state.sheet.inventorySaleProduct;
    const offeredModifiers = state.sheet.menuDrinkModifiers;
    const modifierSelections = [...state.sheet.selectedModifierValues];
    closeLiquorSheet();
    await addInventorySaleToTicketWithGuestPick(
      product,
      saleKind,
      ounces,
      override86,
      modifierSelections,
      offeredModifiers,
    );
    return;
  }
  // VOC1-F4 (Section 2.5): the modifiers-only branch (no liquor slot on
  // this drink at all, openModifiersOnlySheet). Defensive, matching every
  // other guard here: the confirm control is already disabled until every
  // required chip is picked (allRequiredModifiersSelected, threaded
  // through updateModifiersOnlyPricePreview), so this should be
  // unreachable with a required chip unpicked, but a stripped-attribute
  // client is exactly what this codebase's own defence-in-depth
  // convention (see the VOC1-F5 Slice 3 override-86 comment on
  // handleMenuItemTileClick) checks for regardless of the disabled
  // attribute.
  if (state.sheet.modifiersOnlyMode) {
    if (!allRequiredModifiersSelected(state.sheet)) {
      return;
    }
    const { menuDrink, components, menuDrinkModifiers, selectedModifierValues } = state.sheet;
    // VOC1-F4-F2: openModifiersOnlySheet always sets menuDrink; guarded
    // here for the compiler's own sake now that SheetState.menuDrink is
    // optional (a direct inventory-sale item's own equivalent mode is the
    // inventorySaleProduct branch above, which never sets modifiersOnlyMode).
    if (menuDrink === undefined) {
      return;
    }
    const detail: MenuDrinkDetail = {
      menuDrink,
      components,
      liquorSlots: [],
      modifiers: menuDrinkModifiers,
    };
    const modifierSelections = [...selectedModifierValues];
    const publicationBinding=state.sheet.publicationBinding;
    const modifiersOnlyOverride86 = state.sheet.drinkOverride86 ?? false;
    closeLiquorSheet();
    await addDrinkToTicketWithGuestPick(detail, {
      publicationBinding,
      modifierSelections,
      ...(modifiersOnlyOverride86 ? { override86: modifiersOnlyOverride86 } : {}),
    });
    return;
  }
  if (state.sheet.selectedLiquorProductId === undefined || state.sheet.matchMiss) {
    return;
  }
  // VOC1-F4 (Section 2.5): defensive, the identical reasoning the
  // modifiersOnlyMode branch above states in full.
  if (!allRequiredModifiersSelected(state.sheet)) {
    return;
  }
  const {
    menuDrink,
    components,
    selectedLiquorProductId,
    ounces,
    changeLineOrderId,
    publicationBinding,
    overrideSelected,
    drinkOverride86,
    pourModifierApplied,
    menuDrinkModifiers,
    selectedModifierValues,
  } = state.sheet;
  // VOC1-F4-F2: openLiquorSheet always sets menuDrink; guarded here for
  // the compiler's own sake now that SheetState.menuDrink is optional.
  if (menuDrink === undefined) {
    return;
  }
  const product = state.sheet.liquorProducts.find((p) => p.id === selectedLiquorProductId);
  if (product === undefined || ounces === undefined) {
    return;
  }
  const browseAllActive = isSubstitutionFor(state.sheet, product.id);

  if (changeLineOrderId !== undefined) {
    closeLiquorSheet();
    // VOC1-F5 Slice 3 (DEC-161): overrideSelected is set only by
    // selectLiquorRow's own override branch (handleLiquorRowClick), never
    // inferred here from product.flagged86 directly, mirroring
    // addDrinkToTicketWithGuestPick's own override86 a few lines below.
    await submitLiquorChange(
      changeLineOrderId,
      product.id,
      ounces,
      browseAllActive,
      overrideSelected ?? false,
      // VOC1-F7 Slice 2: the chip pick recorded on this SAME sheet session,
      // never inferred here.
      pourModifierApplied,
      false,
      publicationBinding,
    );
    return;
  }

  const detail: MenuDrinkDetail = {
    menuDrink,
    components,
    liquorSlots: state.sheet.allowedCategories,
    modifiers: menuDrinkModifiers,
  };
  const modifierSelections = [...selectedModifierValues];
  closeLiquorSheet();
  await addDrinkToTicketWithGuestPick(detail, {
    publicationBinding,
    liquorProductId: product.id,
    ounces,
    name: product.name,
    isSubstitution: browseAllActive,
    // VOC1-F5 Slice 3 (DEC-161): overrideSelected is set only by
    // selectLiquorRow's own override branch, never inferred here from
    // product.flagged86 directly.
    override86: overrideSelected || (drinkOverride86 ?? false) || false,
    // VOC1-F7 Slice 2: the chip pick recorded on this same sheet session.
    pourModifierApplied,
    // VOC1-F4 (Section 2.5): the chip picks recorded on this same sheet
    // session.
    modifierSelections,
  });
}

// ---------------------------------------------------------------------
// ALPHA-08: line editing (remove / change liquor)
// ---------------------------------------------------------------------

async function submitLiquorChange(
  orderLineId: string,
  liquorProductId: string,
  ounces: string,
  isSubstitution: boolean,
  // VOC1-F5 Slice 3 (DEC-161). See handleLiquorAddClick's own comment;
  // true only when this selection went through the override confirm
  // sheet. The route's own resolveOverride86 is the real, unbypassable
  // gate either way.
  override86: Override86 = false,
  // VOC1-F7 Slice 2. See handleLiquorAddClick's own comment; the chip pick
  // recorded on this same sheet session, never inferred here.
  pourModifierApplied = false,
  // M5 (DEC-282, notes rule 8): true only after the bartender said yes to
  // "this puts the price back to the menu price".
  confirmPriceRestore = false,
  publicationBinding?:PublicationQuoteBinding,
): Promise<void> {
  const session = requireSession();
  const changeKey = crypto.randomUUID();
  const result = await runPersistingSale(override86, "Sell 86'd item: change liquor", (approval) =>
    changeLineLiquor(session.apiBaseUrl, session.tenantId, orderLineId, {
      idempotencyKey: changeKey,
      publicationBinding,
      liquorProductId,
      ounces,
      isSubstitution,
      pourModifierApplied,
      ...override86Fields(override86),
      ...(confirmPriceRestore ? { confirmPriceRestore: true } : {}),
      ...(approval ?? {}),
    }),
  );
  if (result === undefined) {
    return;
  }
  if (result.status === 409 && result.body.error === "price_change_in_force") {
    // The drink's price was typed at the till. Ask before dropping it.
    const changedLine = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
    openChoiceSheet({
      title: "Change the liquor?",
      subtitle: changedLine?.name,
      infoRows: [],
      infoNote: "This puts the price back to the menu price. Continue?",
      cancelLabel: "Cancel",
      primaryAction: {
        id: "confirm-price-restore",
        label: "Continue",
        testid: "price-restore-confirm",
      },
      onPick: () =>
        void submitLiquorChange(
          orderLineId,
          liquorProductId,
          ounces,
          isSubstitution,
          override86,
          pourModifierApplied,
          true,
          publicationBinding,
        ),
    });
    return;
  }
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not change this line's liquor (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  const line = state.lines.find((l) => l.orderLineId === orderLineId);
  if (line !== undefined) {
    line.liquorProductId = result.body.orderLine.liquor_product_id ?? undefined;
    line.ounces = result.body.orderLine.pour_ounces ?? undefined;
    line.isSubstitution = result.body.orderLine.is_substitution;
    // VOC1-F7 Slice 2 (DEC-171). This route's own response carries no
    // pour_modifier_applied fact (no such column exists; Slice 2 was
    // scoped to no migration and no server change), so this is exactly the
    // flag this same sheet session just verified against the server via
    // the 200/201 above, never re-derived from the response.
    line.pourModifierApplied = pourModifierApplied;
    // Display name for the new liquor: the product the bartender just
    // picked from the sheet, already known client-side, no extra round
    // trip needed.
    const productResult = await getLiquorProduct(
      session.apiBaseUrl,
      session.tenantId,
      liquorProductId,
    );
    if (productResult.status === 200) {
      line.detail = productResult.body.liquorProduct.name;
    }
  }
  renderTicket();
  if (override86 !== false) {
    void refreshLineStockOverride(orderLineId);
  }
  await recomputeQuote();
  void refreshPaymentPreview();
  showToast("Liquor changed.", "success");
  // A move to a more expensive pour raises the ticket without adding a
  // line to it, which is the one growth path no add-line ever sees.
  handleHoldIncrementOutcome(result.body.holdIncrement);
}

function openLineEditSheet(orderLineId: string): void {
  if (refuseWhileAwaitingTip()) {
    return;
  }
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  if (line === undefined) {
    return;
  }
  state.lineEditTargetOrderLineId = orderLineId;
  noteTicketSheetOpen("lineEdit", orderLineId);
  lineEditSubtitle.textContent = line.name;
  // VOC1-F8 build-time amendment, dated 2026-09-22: shown only for a
  // cocktail line (menuDrinkId set; a direct inventory sale has no
  // menu_drink_components at all). This sheet is only ever reached
  // through the row's own edit trigger, which still hides once the line
  // is covered (buildTicketLineRow's own `if (!covered)`, unchanged from
  // before this item, and required by e2e/ui.spec.ts's own "removing a
  // line is blocked once covered" tests). A covered cocktail line's own
  // View Recipe reachability is a SEPARATE control instead
  // (buildTicketLineRow's own .btn-line-recipe, rendered exactly where
  // the edit trigger would have been), not this sheet.
  // VOC1-F8-F8 (independent review, 2026-09-22): also hidden once the
  // current permission snapshot no longer holds admin.menu_drink.view,
  // the same gate renderMenuGrid's own tile-recipe-button now carries.
  lineEditViewRecipeButton.hidden =
    line.menuDrinkId === undefined || !hasPermission(requireSession(), MENU_DRINK_VIEW_PERMISSION);
  // "Change liquor" only makes sense for a COCKTAIL line that has one; a
  // line with no liquor selection, an orphaned/unpriceable line
  // (priceable === false), or a direct inventory-sale line (lineKind
  // other than "cocktail": pour/beer/packaged are the item picked,
  // not a cocktail's liquor slot, and changeLineLiquor is a cocktail-only
  // command) only offers Remove.
  // AU1, 2026-08-29: and never on a drink that has already been poured.
  // PRD P0.4 scopes this command to before the pour ("Changing a liquor
  // BEFORE preparation..."), and the server now refuses a Prepared line
  // outright (409 line_already_prepared), so leaving the button on a
  // Prepared line offered the floor a dead end. The correction for a
  // poured drink is the Void button below, which asks for a reason.
  lineEditChangeLiquorButton.hidden =
    line.liquorProductId === undefined ||
    line.priceable === false ||
    line.lineKind !== "cocktail" ||
    line.preparedState === "prepared";
  // Amendment 2026-08-22 (fifth), item 2: reassignment is now offered on
  // BOTH unassigned and already-assigned lines (previously gated to
  // "two or more guests," which hid this on the common case the item
  // exists for: a single-guest ticket with a stray drink added before
  // that one guest existed). Hidden only when there is truly no guest at
  // all to assign to; the label itself distinguishes "nothing assigned
  // yet" from "reassign to someone else."
  // AU9, 2026-09-02, hid this on a Prepared line too, the same reasoning
  // lineEditChangeLiquorButton's own AU1 hide carries just above. DEC-205
  // (product owner, 2026-09-28, docs/decision-register.md) supersedes
  // that: the owner ruled a drink can move between guests up until it is
  // paid for, not only until it is prepared (AU15, docs/build-briefs/
  // AU15-move-prepared-drink-between-guests.md), so preparedState no
  // longer gates this button. It now hides only when there is no guest to
  // assign to; the server's own payment-coverage refusal
  // (OrderAlreadyPaidError, src/domain/guests.ts's changeLineGuest) still
  // surfaces through the usual error-toast path if a covered line somehow
  // reaches this sheet.
  lineEditChangeGuestButton.hidden = state.guests.length === 0;
  lineEditChangeGuestButton.textContent =
    line.guestId === undefined ? "Assign to guest" : "Change guest";
  // CORE6: a permission decision (the same right as merging tabs), never a role one; the sheet is only
  // reachable on an unpaid line, and the server refuses a paid one regardless.
  lineEditMoveToTabButton.hidden =
    state.session === undefined ||
    !hasPermission(state.session, "pos.tab.merge") ||
    isLineCovered(orderLineId);
  // M0, owner ruling 2026-08-27: "The only way to void out a drink is to
  // say what happened." A drink that has been Prepared was really poured,
  // so taking it off the ticket is a VOID and the button says so. The
  // label change is not decoration: the two actions are different
  // commands, need different rights, and one of them will ask the person
  // a question. Telling them that before they tap is the honest order.
  // The server enforces the same split regardless of this label
  // (removeLine refuses a Prepared line outright), so a stale screen
  // cannot slip a poured drink off a ticket without a reason.
  // Discounts slice A. Hidden for anyone without money.discount.apply (a
  // permission decision, never a role one), and on a line already covered
  // by a captured payment, which the server refuses anyway.
  lineEditDiscountButton.hidden = !canApplyDiscounts() || isLineCovered(orderLineId);
  // DSC35-S7b. Only for a session holding money.scheduled_special.remove
  // (permission, never role) and a drink whose special actually took
  // something off (the same rule as the strip), and not once the drink is paid.
  lineEditRemoveSpecialButton.hidden =
    !canRemoveScheduledSpecial() ||
    isLineCovered(orderLineId) ||
    scheduledSpecialOnLine(orderLineId) === undefined;
  // M5 (DEC-282). Shown for a session whose snapshot is allowed or
  // approval_required on money.price_override (a permission decision, never
  // a role one); a lounger or inventory manager (denied) never sees it. Not
  // on a line a captured payment already covers, which the server refuses.
  lineEditChangePriceButton.hidden =
    !canChangePrice() || isLineCovered(orderLineId) || line.priceable === false;
  // M5-R10 (DEC-355). Anyone who can open this sheet (and so sees the ticket)
  // may read the history; it is read only. Shown for any line with at least
  // one ledger row, including a put-back line that carries no strip.
  const historyCount = line.priceChangeCount ?? 0;
  lineEditPriceHistoryButton.hidden = historyCount === 0;
  lineEditPriceHistoryCountEl.textContent = changeCountLabel(historyCount);
  const isPrepared = line.preparedState === "prepared";
  lineEditRemoveButton.textContent = isPrepared ? "Void drink" : "Remove from ticket";
  lineEditRemoveButton.dataset["preparedState"] = line.preparedState;
  // UX28: names the standing depletion before the Void tap. "Pour or
  // unit" rather than one sale kind, since preparedState is shared by
  // every line kind (cocktail, pour, beer, packaged); the void-reason
  // picker's own note and the completion toast are unchanged by this.
  lineEditPreparedNote.hidden = !isPrepared;
  if (isPrepared) {
    // VER72-F1: the stored fact decides. A drink that posted nothing at
    // Prepared (no catalog product, DEC-218) is not described as deducted.
    lineEditPreparedNote.textContent =
      line.inventoryPosted === false
        ? "Nothing was taken out of stock for this drink. Removing it drops the charge."
        : "This drink was already taken out of stock. Removing it drops the charge, but the stock stays used up.";
  }
  // M1: the undo of a mis-tapped Prepared. Only on a Prepared line, only
  // for a session whose snapshot does not deny money.line.unprepare (a
  // permission decision, never a role one; approval_required tiers still
  // see it and go through the manager-PIN flow), and never on a covered
  // line, which the server refuses anyway.
  lineEditUnprepareButton.hidden =
    !isPrepared ||
    state.session === undefined ||
    !hasPermission(state.session, "money.line.unprepare") ||
    isLineCovered(orderLineId);
  // UIFIX5: six actions sit in a 3 by 2 grid; seven or more keep the four column grid so the sheet stays two rows.
  const lineEditActions = lineEditSheetEl.querySelector<HTMLElement>(".line-edit-actions");
  lineEditActions?.classList.toggle(
    "is-many",
    Array.from(lineEditActions.querySelectorAll<HTMLElement>("button")).filter(
      (action) => !action.hidden,
    ).length > 6,
  );
  lineEditScrim.hidden = false;
}

function closeLineEditSheet(): void {
  dropDraft(TICKET_SHEET_DRAFT);
  lineEditScrim.hidden = true;
  state.lineEditTargetOrderLineId = undefined;
  // M5: the Change price sheet shares this scrim. Closing the scrim closes
  // it too (a tap outside, Escape, sign-out), and the next open of the line
  // sheet starts on the line sheet again.
  priceChangeSheet = undefined;
  priceChangeSheetEl.hidden = true;
  lineEditSheetEl.hidden = false;
}

async function handleLineEditRemoveClick(): Promise<void> {
  const orderLineId = state.lineEditTargetOrderLineId;
  if (orderLineId === undefined) {
    return;
  }
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  const removalContext = captureFloorSaleContext();
  if (removalContext === undefined || !floorSaleContextIsCurrent(removalContext) ||
      line === undefined) return;
  // M0: a Prepared line takes the void path, which cannot complete
  // without a reason. Everything else is the unchanged removal below.
  if (line?.preparedState === "prepared") {
    closeLineEditSheet();
    await openVoidReasonPicker(orderLineId, line.name, removalContext);
    return;
  }
  const session = requireSession();
  closeLineEditSheet();
  const result = await removeLine(session.apiBaseUrl, session.tenantId, orderLineId, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (!floorSaleContextIsCurrent(removalContext) || !state.lines.includes(line)) return;
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not remove that line (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  state.lines = state.lines.filter((line) => line.orderLineId !== orderLineId);
  renderTicket();
  await recomputeQuote();
  if (!floorSaleContextIsCurrent(removalContext)) return;
  void refreshPaymentPreview();
  showToast("Line removed.", "success");
}

// ---------------------------------------------------------------------
// M0: the void flow. Owner ruling 2026-08-27, "The only way to void out a
// drink is to say what happened", and the reason list is the tenant's own
// (Location settings > Void reasons).
//
// TOUCH-ONLY, and strictly, because this is the floor: the reason arrives
// as a picker of tappable rows through the same openChoiceSheet the guest
// picker already uses, with NO blank option, NO preselected default, and
// no free-text field anywhere in the path. Cancel closes the sheet and
// changes nothing, so there is no way to tap through this screen without
// choosing. The one text entry in the whole flow is the manager's PIN,
// which is the existing numeric pad, not a keyboard.
//
// The reasons are fetched live rather than cached at sign-in, so an Admin
// retiring a reason mid-shift takes it off the floor's picker on the next
// void rather than at the next reload.
// ---------------------------------------------------------------------

/** VER72-F1: true only when the stored fact says Prepared posted no
 * inventory for this line. Unknown keeps the original (safe) wording. */
function lineNothingPosted(orderLineId: string): boolean {
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  return line?.inventoryPosted === false;
}

async function openVoidReasonPicker(
  orderLineId: string, drinkName: string, removalContext: FloorSaleContext,
): Promise<void> {
  if (!floorSaleContextIsCurrent(removalContext)) return;
  const session = requireSession();
  const result = await listVoidReasons(session.apiBaseUrl, session.tenantId);
  if (!floorSaleContextIsCurrent(removalContext)) return;
  if (result.status !== 200) {
    showToast(
      `Could not load the void reasons. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const reasons = sortByName(result.body.voidReasons, (reason) => reason.label);
  if (reasons.length === 0) {
    // Never expected (every location is seeded with six), but an Admin
    // who retired all of them would otherwise get an empty sheet with no
    // explanation. Say what is wrong and where to fix it.
    showToast(
      "There are no void reasons set up for this location. An admin adds them in Settings.",
      "danger",
    );
    return;
  }
  openChoiceSheet({
    title: "What happened?",
    subtitle: drinkName,
    // VER72-F1: only a drink that posted inventory at Prepared still comes
    // out of stock; a drink with no catalog product posted nothing.
    note: lineNothingPosted(orderLineId)
      ? "The drink was already made. Nothing was taken out of stock for it. Pick the reason it is coming off the ticket."
      : "The drink was already made. It was taken out of stock. Pick the reason it is coming off the ticket.",
    options: reasons.map((reason) => ({
      id: reason.id,
      label: reason.label,
      testid: "void-reason-option",
    })),
    columns: 2,
    onPick: (voidReasonId) => void submitLineVoid(orderLineId, voidReasonId, drinkName, undefined, removalContext),
  });
}

async function submitLineVoid(
  orderLineId: string,
  voidReasonId: string,
  drinkName: string,
  note: string | undefined,
  removalContext: FloorSaleContext,
): Promise<void> {
  if (!floorSaleContextIsCurrent(removalContext)) return;
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  if (line === undefined) return;
  const session = requireSession();
  // One key for the whole flow, including any approval retry, so an
  // approved retry replays rather than voiding twice and
  // recordPermissionApproval writes one audit row (see requestApproval's
  // own section header).
  const idempotencyKey = crypto.randomUUID();
  const gated = await requestApproval(
    `Void drink: ${drinkName}`,
    (approval) =>
      voidOrderLine(session.apiBaseUrl, session.tenantId, orderLineId, {
        idempotencyKey,
        voidReasonId,
        note,
        ...approval,
      }),
    () => {
      if (floorSaleContextIsCurrent(removalContext)) showRingScreen();
    },
    { isCurrent: () => floorSaleContextIsCurrent(removalContext) },
  );
  if (gated.canceled || !floorSaleContextIsCurrent(removalContext) ||
      !state.lines.includes(line)) {
    return;
  }
  const result = gated.result;
  // Read before the line leaves state.lines below.
  const nothingPosted = lineNothingPosted(orderLineId);
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not void that drink (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    showRingScreen();
    return;
  }
  showRingScreen();
  state.lines = state.lines.filter((candidate) => candidate.orderLineId !== orderLineId);
  renderTicket();
  await recomputeQuote();
  if (!floorSaleContextIsCurrent(removalContext)) return;
  void refreshPaymentPreview();
  // Plain language, and honest about the half people get wrong: the
  // ticket total drops, the stock does not come back.
  showToast(nothingPosted ? "Drink voided." : "Drink voided. The stock stays used up.", "success");
}

// M1: undo a mis-tapped Prepared (docs/build-briefs/
// M1-undo-accidental-prepared.md). The mirror image of the void above in
// both directions, and the copy says so: a void keeps the depletion
// because the drink was poured; this undo returns it because the drink
// never existed. Same requestApproval contract as every other
// approval_required call site (the picker only appears if the server's
// first, no-approval attempt answers approval_required).
async function handleLineEditUnprepareClick(): Promise<void> {
  const orderLineId = state.lineEditTargetOrderLineId;
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  if (orderLineId === undefined || line === undefined) {
    return;
  }
  const session = requireSession();
  const preparationContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(preparationContext)) return;
  closeLineEditSheet();
  const idempotencyKey = crypto.randomUUID();
  const gated = await requestApproval(
    `Undo Prepared: ${line.name}`,
    (approval) =>
      unprepareOrderLine(session.apiBaseUrl, session.tenantId, orderLineId, {
        idempotencyKey,
        ...approval,
      }),
    () => {
      if (floorSaleContextIsCurrent(preparationContext)) showRingScreen();
    },
    { isCurrent: () => floorSaleContextIsCurrent(preparationContext) },
  );
  if (gated.canceled || !floorSaleContextIsCurrent(preparationContext) ||
      !state.lines.includes(line)) {
    return;
  }
  const result = gated.result;
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not undo Prepared (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    showRingScreen();
    return;
  }
  showRingScreen();
  // UX6's rule: the write refreshes what it changed. The line stays on
  // the ticket, back at Not Prepared. CORE4: Pay no longer blocks on that;
  // a captured payment prepares it again.
  line.preparedState = "not_prepared";
  line.inventoryPosted = undefined;
  renderTicket();
  await recomputeQuote();
  if (!floorSaleContextIsCurrent(preparationContext)) return;
  void refreshPaymentPreview();
  showToast("Prepared undone. The stock is back.", "success");
}

// ---------------------------------------------------------------------
// M4: refund a captured payment by selecting drinks (docs/build-briefs/
// M4-refund-by-line.md; DEC-035). The floor flow is two stages: the
// Payments button reads this ticket's payments LIVE (never cached across
// opens) and lists the captured ones through the reusable choice sheet;
// picking one opens the refund sheet, whose every row is a tap-to-toggle
// drink carrying its server-derived captured price plus tax share. The
// client's only arithmetic is SUMMING the selected rows' server figures
// for display; the write sends order-line ids and nothing else, and the
// server re-derives the money. No numeric input exists anywhere in the
// path except the approval PIN pad.
// ---------------------------------------------------------------------

/** UX13: which of the two flows this chip can reach for the signed-in
 * session, right now. The same two independent permission checks the two
 * standalone buttons already made before this item merged them into one
 * chip (money.refund for "View payments", money.payment_link.send for
 * "Send payment link"), unchanged. */
function ticketPaymentsChipEligibility(): { canView: boolean; canSend: boolean } {
  const session = state.session;
  if (state.orderId === undefined || session === undefined) {
    return { canView: false, canSend: false };
  }
  return {
    canView: hasPermission(session, "money.refund"),
    canSend: hasPermission(session, "money.payment_link.send"),
  };
}

/** UX39: this used to hide/show a standalone #payments-button; that
 * button is gone (folded into #btn-ticket-more), so this function's own
 * job is now only to keep #btn-ticket-more's own visibility in sync with
 * Payments' own eligibility, alongside the other three (renderTicket
 * MoreButton recomputes all four itself, so this call is what makes a
 * Payments-only eligibility change -- a permission grant mid-shift --
 * reach the button without waiting on one of the other three to also
 * change). */
function renderTicketPaymentsChip(): void {
  renderTicketMoreButton();
}

/** UX13 (owner ruling: "ONE Payments control in the header, not the
 * several the current screen carries"). #payment-link-button is gone as a
 * standalone element; this is #payments-button's own click handler.
 * When both flows are available it opens the same reusable choice sheet
 * every other floor picker in this file already uses, to let the
 * bartender pick between them; when only one is available it goes
 * straight there, matching the single-tap flow either standalone button
 * used to offer on its own. Neither underlying flow
 * (handlePaymentsButtonClick, handlePaymentLinkButtonClick) changed;
 * only how each is REACHED did. */
function handleTicketPaymentsChipClick(): void {
  const { canView, canSend } = ticketPaymentsChipEligibility();
  if (canView && canSend) {
    openChoiceSheet({
      title: "Payments",
      options: [
        { id: "view", label: "View payments and refunds", testid: "payments-menu-view" },
        { id: "link", label: "Send payment link", testid: "payments-menu-link" },
      ],
      onPick: (id) => {
        if (id === "view") {
          void handlePaymentsButtonClick();
        } else {
          void handlePaymentLinkButtonClick();
        }
      },
    });
    return;
  }
  if (canView) {
    void handlePaymentsButtonClick();
    return;
  }
  if (canSend) {
    void handlePaymentLinkButtonClick();
  }
}

/** CASH1-S2-U1. The tender of each payment the refund picker listed, by payment id. */
const refundPaymentTender = new Map<string, "card" | "cash">();

/** CASH1-S2-U1. A cash refund leaves the drawer on this device, so there must be an open one. The open flow runs when there is
 * none; cancelling it sends nothing. A card refund needs nothing here. */
async function readyForRefund(paymentAttemptId: string): Promise<boolean> {
  if (refundPaymentTender.get(paymentAttemptId) !== "cash") return true;
  return await cashDrawer.ensureOpenForTender({ refund: true });
}

async function handlePaymentsButtonClick(orderIdOverride?: string): Promise<void> {
  const orderId = orderIdOverride ?? state.orderId;
  if (orderId === undefined) {
    return;
  }
  const session = requireSession();
  const result = await getPayments(session.apiBaseUrl, session.tenantId, orderId);
  if (result.status !== 200) {
    showToast(
      `Could not load this tab's payments. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const captured = result.body.payments
    .filter((p) => p.paymentAttempt.status === "Captured")
    .sort((a, b) => a.paymentAttempt.updated_at.localeCompare(b.paymentAttempt.updated_at));
  if (captured.length === 0) {
    showToast("No payments on this tab yet.", "neutral");
    return;
  }
  // CASH1-S2-U1: which of these payments were cash, so the refund can ask for an open drawer first.
  for (const p of captured) {
    refundPaymentTender.set(
      p.paymentAttempt.id,
      p.paymentAttempt.tender === "cash" ? "cash" : "card",
    );
  }
  openChoiceSheet({
    title: "Payments on this tab",
    note: "Tap a payment to refund drinks from it.",
    options: captured.map((p) => ({
      id: p.paymentAttempt.id,
      label: formatCents(
        parseDollarsToCentsExact(p.paymentAttempt.amount, "captured payment amount"),
      ),
      sublabel:
        (p.paymentAttempt.tender === "cash" ? "Cash · " : "") +
        (p.splitShare === null
          ? ""
          : `Guest ${p.splitShare.shareIndex} of ${p.splitShare.shareCount} · `) +
        new Date(p.paymentAttempt.updated_at).toLocaleString(),
      testid: "refund-payment-option",
    })),
    onPick: (paymentAttemptId) => void openRefundSheet(paymentAttemptId),
  });
}

// ---------------------------------------------------------------------
// PL1: the card-not-present payment LINK (docs/build-briefs/
// PL1-payment-link.md; DEC-011 items 6 and 7). Never a keyed-entry
// virtual terminal: the guest enters their own card on their own
// device, so no numeric or card-data input exists anywhere in this
// sheet. The amount is never shown as something this client computed;
// it is the server's own figure on the returned link. Completion is
// read back through an explicit "Check status" tap this slice, never a
// push notification (no webhook exists yet; see src/domain/
// paymentLinks.ts's own header).
// ---------------------------------------------------------------------

// UX13: the standalone renderPaymentLinkButton is gone; the merged chip's
// own visibility (renderTicketPaymentsChip above) already accounts for
// money.payment_link.send eligibility.

function paymentLinkStatusLabel(status: string): string {
  switch (status) {
    case "Created":
      return "Awaiting payment";
    case "Completed":
      return "Paid";
    case "Expired":
      return "Expired, unpaid";
    case "Canceled":
      return "Canceled";
    default:
      return status;
  }
}

function renderPaymentLinkSheet(link: PaymentLinkDto | undefined): void {
  if (link === undefined || link.status !== "Created") {
    paymentLinkSendButton.hidden = false;
    paymentLinkActive.hidden = true;
    paymentLinkSubtitle.textContent = "";
    return;
  }
  paymentLinkSendButton.hidden = true;
  paymentLinkActive.hidden = false;
  paymentLinkSubtitle.textContent = formatCents(link.amount_cents);
  paymentLinkStatusEl.textContent = paymentLinkStatusLabel(link.status);
  paymentLinkUrlEl.textContent = link.url;
  renderPaymentLinkAutoNote();
}

async function handlePaymentLinkButtonClick(): Promise<void> {
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  const session = requireSession();
  const result = await listPaymentLinks(session.apiBaseUrl, session.tenantId, orderId);
  if (result.status !== 200) {
    showToast(
      `Could not load this tab's payment links. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const active = result.body.links.find((l) => l.status === "Created");
  renderPaymentLinkSheet(active);
  paymentLinkScrim.hidden = false;
}

function closePaymentLinkSheet(): void {
  paymentLinkScrim.hidden = true;
}

async function handlePaymentLinkSendClick(): Promise<void> {
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  const session = requireSession();
  const result = await createPaymentLink(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not send a payment link. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  renderPaymentLinkSheet(result.body.link);
  showToast("Payment link sent.", "success");
  if (result.body.link.status === "Created") {
    startTicketPaymentLinkWatch(orderId, result.body.link.id);
  }
}

async function handlePaymentLinkCheckClick(): Promise<void> {
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  const session = requireSession();
  const result = await listPaymentLinks(session.apiBaseUrl, session.tenantId, orderId);
  if (result.status !== 200) {
    showToast(
      `Could not check this link's status. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const active = result.body.links.find((l) => l.status === "Created");
  if (active === undefined) {
    stopPaymentLinkWatches();
    renderPaymentLinkSheet(undefined);
    return;
  }
  const reconciled = await reconcilePaymentLink(session.apiBaseUrl, session.tenantId, active.id);
  if (reconciled.status !== 200) {
    showToast(
      `Could not check this link's status. ${capitalizeServerMessage(reconciled.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  renderPaymentLinkSheet(reconciled.body.link);
  if (reconciled.body.link.status === "Completed") {
    stopPaymentLinkWatches();
    showTicketLinkPaid(reconciled.body.link.id, reconciled.body.settlementClose);
  } else if (reconciled.body.link.status === "Created") {
    showToast("Still awaiting the guest.", "neutral");
  } else {
    stopPaymentLinkWatches();
  }
}

async function handlePaymentLinkCancelClick(): Promise<void> {
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  const session = requireSession();
  const result = await listPaymentLinks(session.apiBaseUrl, session.tenantId, orderId);
  if (result.status !== 200) {
    return;
  }
  const active = result.body.links.find((l) => l.status === "Created");
  if (active === undefined) {
    renderPaymentLinkSheet(undefined);
    return;
  }
  const canceled = await cancelPaymentLink(session.apiBaseUrl, session.tenantId, active.id, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (canceled.status !== 200) {
    showToast(
      `Could not cancel this link. ${capitalizeServerMessage(canceled.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  stopPaymentLinkWatches();
  renderPaymentLinkSheet(undefined);
  showToast("Payment link canceled.", "neutral");
}

// ---------------------------------------------------------------------
// PL3 (docs/build-briefs/PL3-payment-link-auto-check.md): the automatic
// status check. The cadence, backoff, cap and no-overlap rules live in the
// pure ./paymentLinkPoller.ts; this block only decides WHAT one check does
// and WHEN the screen no longer wants one. It calls the same reconcile
// route the manual Check button calls, which is idempotent (verified by
// test/pl3-reconcile-idempotent.test.ts), and shows a paid link through
// the same function the manual Check uses, once per link.
// ---------------------------------------------------------------------

const PAYMENT_LINK_STOPPED_MESSAGE = "Stopped checking automatically. Tap Check status.";

let ticketLinkPoller: PaymentLinkPoller | undefined;
/** The (order, link) the ticket poller is for; start is a no-op when the
 * same pair is already being watched. */
let ticketLinkWatchKey: string | undefined;
let tabsLinkPoller: PaymentLinkPoller | undefined;
/** True while the ticket poller runs, drives the sheet's own status line. */
let ticketLinkPollerRunning = false;
/** Links already shown as Paid, so a manual Check racing a background check
 * (or two background checks) never toasts Paid or raises the closed-ticket
 * prompt twice. */
const paymentLinkIdsShownPaid = new Set<string>();

function renderPaymentLinkAutoNote(): void {
  paymentLinkAutoNote.textContent = ticketLinkPollerRunning ? "Checking automatically." : "";
  paymentLinkAutoNote.hidden = !ticketLinkPollerRunning;
}

function stopPaymentLinkWatches(): void {
  ticketLinkPoller?.stop();
  ticketLinkPoller = undefined;
  ticketLinkWatchKey = undefined;
  ticketLinkPollerRunning = false;
  tabsLinkPoller?.stop();
  tabsLinkPoller = undefined;
  renderPaymentLinkAutoNote();
}

/** The one place a paid ticket link reaches the screen, for the manual
 * Check and the automatic check alike: close the sheet, say Paid, and when
 * the settlement closed the ticket, the same closed-ticket prompt. */
function showTicketLinkPaid(linkId: string, settlementClose: { kind: string } | undefined): void {
  if (paymentLinkIdsShownPaid.has(linkId)) {
    return;
  }
  paymentLinkIdsShownPaid.add(linkId);
  closePaymentLinkSheet();
  showToast("Paid.", "success");
  if (settledAndClosed(settlementClose)) {
    // UX102 (owner directive 2026-10-10): the settled ticket is closed and the floor returns to Open Tabs.
    finishClosedTicket();
  }
}

function startTicketPaymentLinkWatch(orderId: string, linkId: string): void {
  const key = `${orderId}:${linkId}`;
  if (ticketLinkPoller !== undefined && ticketLinkWatchKey === key) {
    return;
  }
  ticketLinkPoller?.stop();
  const gen = sessionGeneration;
  const poller = createPaymentLinkPoller({
    check: () => checkTicketPaymentLink(orderId, linkId, gen),
    onStop: (reason) => {
      // A stale poller (replaced or stopped on purpose) says nothing.
      if (ticketLinkPoller !== poller) {
        return;
      }
      ticketLinkPoller = undefined;
      ticketLinkWatchKey = undefined;
      ticketLinkPollerRunning = false;
      renderPaymentLinkAutoNote();
      announcePaymentLinkPollStop(reason, PAYMENT_LINK_STOPPED_MESSAGE);
    },
  });
  ticketLinkPoller = poller;
  ticketLinkWatchKey = key;
  ticketLinkPollerRunning = true;
  renderPaymentLinkAutoNote();
  poller.start();
}

/** A poll that ends because it hit its cap or kept failing is never silent:
 * the bartender is told the link is no longer being checked for them. */
function announcePaymentLinkPollStop(reason: PaymentLinkPollStopReason, message: string): void {
  if (reason === "cap" || reason === "errors") {
    showToast(message, "neutral");
  }
}

async function checkTicketPaymentLink(
  orderId: string,
  linkId: string,
  gen: number,
): Promise<PaymentLinkCheckOutcome> {
  const session = sessionOrUndefined();
  if (
    session === undefined ||
    !sessionIsCurrent(gen) ||
    state.orderId !== orderId ||
    ringScreen.hidden
  ) {
    return "stop";
  }
  const reconciled = await reconcilePaymentLink(session.apiBaseUrl, session.tenantId, linkId);
  // The ticket or session may have moved on while the request was in flight.
  if (!sessionIsCurrent(gen) || state.orderId !== orderId || ringScreen.hidden) {
    return "stop";
  }
  if (reconciled.status !== 200) {
    return "error";
  }
  const link = reconciled.body.link;
  if (link.status === "Created") {
    return "pending";
  }
  renderPaymentLinkSheet(link);
  if (link.status === "Completed") {
    showTicketLinkPaid(link.id, reconciled.body.settlementClose);
  } else {
    showToast(`Payment link ${paymentLinkStatusLabel(link.status).toLowerCase()}.`, "neutral");
  }
  return "resolved";
}

/** Called whenever the ring screen is shown: when this ticket already has a
 * link out for it (sent earlier, or from another terminal), watch it. One
 * list read, only for a session allowed to send and check links. */
async function resumeTicketPaymentLinkWatch(): Promise<void> {
  const orderId = state.orderId;
  const session = sessionOrUndefined();
  if (
    orderId === undefined ||
    session === undefined ||
    !hasPermission(session, "money.payment_link.send")
  ) {
    return;
  }
  const gen = sessionGeneration;
  const result = await listPaymentLinks(session.apiBaseUrl, session.tenantId, orderId);
  if (
    !sessionIsCurrent(gen) ||
    state.orderId !== orderId ||
    ringScreen.hidden ||
    result.status !== 200
  ) {
    return;
  }
  const active = result.body.links.find((l) => l.status === "Created");
  if (active !== undefined) {
    startTicketPaymentLinkWatch(orderId, active.id);
  }
}

/** Open Tabs: one poller for the screen, checking each listed tab whose
 * link still awaits the guest (the list rows carry active_payment_link_id,
 * so no request per tab is needed to know which). Called after every tab
 * list load. */
function syncOpenTabsPaymentLinkWatch(): void {
  const session = sessionOrUndefined();
  const hasAnyLink =
    session !== undefined &&
    hasPermission(session, "money.payment_link.send") &&
    state.openTabs.some((tab) => tab.active_payment_link_id !== null);
  if (!hasAnyLink) {
    tabsLinkPoller?.stop();
    tabsLinkPoller = undefined;
    return;
  }
  if (tabsLinkPoller !== undefined) {
    return;
  }
  const gen = sessionGeneration;
  const poller = createPaymentLinkPoller({
    check: () => checkOpenTabsPaymentLinks(gen),
    onStop: (reason) => {
      if (tabsLinkPoller !== poller) {
        return;
      }
      tabsLinkPoller = undefined;
      announcePaymentLinkPollStop(
        reason,
        "Stopped checking payment links automatically. Open the ticket and tap Check status.",
      );
    },
  });
  tabsLinkPoller = poller;
  poller.start();
}

async function checkOpenTabsPaymentLinks(gen: number): Promise<PaymentLinkCheckOutcome> {
  const stillHere = (): boolean =>
    sessionOrUndefined() !== undefined && sessionIsCurrent(gen) && !openTabsScreen.hidden;
  if (!stillHere()) {
    return "stop";
  }
  const awaiting = state.openTabs.filter((tab) => tab.active_payment_link_id !== null);
  if (awaiting.length === 0) {
    return "resolved";
  }
  let anyResolved = false;
  let anyError = false;
  let anyPending = false;
  // One tab at a time, so checks never overlap each other either.
  for (const tab of awaiting) {
    const session = sessionOrUndefined();
    const linkId = tab.active_payment_link_id;
    if (session === undefined || linkId === null || !stillHere()) {
      return "stop";
    }
    const reconciled = await reconcilePaymentLink(session.apiBaseUrl, session.tenantId, linkId);
    if (!stillHere()) {
      return "stop";
    }
    if (reconciled.status !== 200) {
      anyError = true;
      continue;
    }
    const link = reconciled.body.link;
    if (link.status === "Created") {
      anyPending = true;
      continue;
    }
    anyResolved = true;
    if (link.status === "Completed") {
      if (!paymentLinkIdsShownPaid.has(link.id)) {
        paymentLinkIdsShownPaid.add(link.id);
        showToast(`Paid: ${tab.name ?? "a tab"}.`, "success");
      }
    } else {
      showToast(
        `Payment link ${paymentLinkStatusLabel(link.status).toLowerCase()}: ${tab.name ?? "a tab"}.`,
        "neutral",
      );
    }
  }
  if (anyResolved) {
    // The list is reloaded, which also drops the paid tab if it closed and
    // restarts this watch for whatever still awaits a link.
    void loadOpenTabs();
    return "resolved";
  }
  if (anyError) {
    return "error";
  }
  return anyPending ? "pending" : "resolved";
}

interface RefundSheetState {
  /** The payment the refund goes to. On an equal split (REFDISC2, DEC-305) it
   * starts as the payment the manager opened and changes when another guest's
   * card is tapped; the server sends the same drinks and amounts either way. */
  paymentAttemptId: string;
  lines: RefundableLineDto[];
  selected: Set<string>;
  /** REFDISC2: the cards of an equal split; empty for any other payment. */
  cards: RefundCardDto[];
}

let refundSheetState: RefundSheetState | undefined;

async function openRefundSheet(paymentAttemptId: string): Promise<void> {
  const session = requireSession();
  const result = await listRefundableLines(session.apiBaseUrl, session.tenantId, paymentAttemptId);
  if (result.status !== 200) {
    showToast(
      `Could not load this payment's drinks. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  // SPLIT1 (owner ruling Q4): a payment taken by amount has no drinks to pick; the manager keys the amount to return.
  if (result.body.amountRefundable || result.body.amountPayment) {
    if (result.body.refundableCents <= 0) {
      showToast("Nothing to refund. This payment has already been refunded in full.", "neutral");
      return;
    }
    openRefundAmountPad(paymentAttemptId, result.body.refundableCents);
    return;
  }
  // REFDISC3 (DEC-308 a): nothing here can be refunded and a comped drink is
  // the reason, so say so instead of opening a sheet of disabled rows.
  const lines = result.body.lines;
  if (
    !lines.some((line) => !line.refunded && !line.comped) &&
    lines.some((line) => !line.refunded && line.comped)
  ) {
    showToast("Nothing to refund. Every drink on this payment was comped.", "neutral");
    return;
  }
  refundSheetState = {
    paymentAttemptId,
    lines,
    selected: new Set(),
    cards: result.body.equalSplit?.payments ?? [],
  };
  refundSubtitle.textContent = `${formatCents(result.body.capturedTotalCents)} payment`;
  renderRefundLines();
  refundTicketSheetEl.hidden = true;
  refundDrinksSheetEl.hidden = false;
  refundScrim.hidden = false;
}

function closeRefundSheet(): void {
  refundScrim.hidden = true;
  refundSheetState = undefined;
  refundTicketState = undefined;
}

/** A selected row is marked by more than color alone: its sublabel gains
 * a literal "Refunding" prefix alongside the .selected styling, the same
 * never-color-alone rule every other stateful row in this file follows. */
function refundRowSublabel(line: RefundableLineDto, selected: boolean): string {
  const money =
    line.tax_share_cents > 0
      ? `${formatCents(line.line_total_cents_at_capture)} + ${formatCents(line.tax_share_cents)} tax` +
        ((line.card_price_share_cents ?? 0) > 0
          ? ` + ${formatCents(line.card_price_share_cents ?? 0)} card price`
          : "")
      : formatCents(line.line_total_cents_at_capture);
  return selected ? `Refunding · ${money}` : money;
}

/** REFDISC2 (DEC-305): the "Refund to" list, shown only on an equal split.
 * One tap-row per guest's card, the chosen one marked by a literal prefix
 * alongside the .selected styling (never color alone). */
function renderRefundCards(sheet: RefundSheetState): void {
  const show = sheet.cards.length > 0;
  refundCardsNote.hidden = !show;
  refundCardsList.hidden = !show;
  refundCardsList.innerHTML = "";
  const chosen = sheet.cards.find((card) => card.paymentAttemptId === sheet.paymentAttemptId);
  if (chosen !== undefined) {
    refundSubtitle.textContent = `${formatCents(chosen.capturedCents)} payment`;
  }
  for (const card of sheet.cards) {
    const selected = card.paymentAttemptId === sheet.paymentAttemptId;
    const button = document.createElement("button");
    button.type = "button";
    button.className = selected ? "choice-option selected" : "choice-option";
    button.dataset["testid"] = "refund-card-option";
    button.setAttribute("aria-pressed", selected ? "true" : "false");
    const labelEl = document.createElement("span");
    labelEl.className = "choice-option-label";
    labelEl.textContent = refundCardLabel(card);
    const sublabelEl = document.createElement("span");
    sublabelEl.className = "choice-option-sublabel";
    sublabelEl.textContent = refundCardSublabel(card, selected);
    button.append(labelEl, sublabelEl);
    button.addEventListener("click", () => {
      sheet.paymentAttemptId = card.paymentAttemptId;
      renderRefundLines();
    });
    refundCardsList.append(button);
  }
}

function renderRefundLines(): void {
  const sheet = refundSheetState;
  if (sheet === undefined) {
    return;
  }
  renderRefundCards(sheet);
  refundLinesList.innerHTML = "";
  // REFDISC3 polish: a stable partition. Everything else (payable rows and
  // already-refunded rows) keeps its order; comped rows follow, in their order.
  const isCompedRow = (line: RefundableLineDto): boolean => line.comped && !line.refunded;
  const orderedLines = [
    ...sheet.lines.filter((line) => !isCompedRow(line)),
    ...sheet.lines.filter(isCompedRow),
  ];
  for (const line of orderedLines) {
    if (line.refunded) {
      const row = document.createElement("div");
      row.className = "choice-option-done";
      row.dataset["testid"] = "refund-line-done";
      const labelEl = document.createElement("span");
      labelEl.className = "choice-option-label";
      labelEl.textContent = line.item_reference;
      const sublabelEl = document.createElement("span");
      sublabelEl.className = "choice-option-sublabel";
      sublabelEl.textContent = "Refunded";
      row.append(labelEl, sublabelEl);
      refundLinesList.append(row);
      continue;
    }
    if (line.comped) {
      // REFDISC3 (DEC-308 a): shown so the manager sees why the drink is not
      // on offer, but disabled: a real disabled button, never selectable.
      const row = document.createElement("button");
      row.type = "button";
      row.disabled = true;
      row.className = "choice-option choice-option-comped";
      row.dataset["testid"] = "refund-line-comped";
      const labelEl = document.createElement("span");
      labelEl.className = "choice-option-label";
      labelEl.textContent = line.item_reference;
      const sublabelEl = document.createElement("span");
      sublabelEl.className = "choice-option-sublabel";
      sublabelEl.textContent = "Comped, nothing to refund";
      row.append(labelEl, sublabelEl);
      refundLinesList.append(row);
      continue;
    }
    const selected = sheet.selected.has(line.order_line_id);
    const button = document.createElement("button");
    button.type = "button";
    button.className = selected ? "choice-option selected" : "choice-option";
    button.dataset["testid"] = "refund-line-option";
    const labelEl = document.createElement("span");
    labelEl.className = "choice-option-label";
    labelEl.textContent = line.item_reference;
    const sublabelEl = document.createElement("span");
    sublabelEl.className = "choice-option-sublabel";
    sublabelEl.textContent = refundRowSublabel(line, selected);
    button.append(labelEl, sublabelEl);
    button.addEventListener("click", () => {
      if (sheet.selected.has(line.order_line_id)) {
        sheet.selected.delete(line.order_line_id);
      } else {
        sheet.selected.add(line.order_line_id);
      }
      renderRefundLines();
    });
    refundLinesList.append(button);
  }
  let totalCents = 0;
  for (const line of sheet.lines) {
    if (sheet.selected.has(line.order_line_id)) {
      totalCents +=
        line.line_total_cents_at_capture + line.tax_share_cents + (line.card_price_share_cents ?? 0);
    }
  }
  refundTotalEl.textContent = formatCents(totalCents);
  refundConfirmButton.disabled = sheet.selected.size === 0;
  refundConfirmButton.textContent =
    sheet.selected.size === 0
      ? "Refund selected drinks"
      : `Refund ${sheet.selected.size} drink${sheet.selected.size === 1 ? "" : "s"} · ${formatCents(totalCents)}`;
}

async function handleRefundConfirmClick(): Promise<void> {
  const sheet = refundSheetState;
  if (sheet === undefined || sheet.selected.size === 0) {
    return;
  }
  const orderLineIds = [...sheet.selected];
  const totalLabel = refundTotalEl.textContent ?? "";
  const paymentAttemptId = sheet.paymentAttemptId;
  closeRefundSheet();
  // REF1 (DEC-011): the reason picker sits AFTER the drink tap-select
  // sheet's confirm and BEFORE the write, exactly where the void flow's
  // own reason picker sits after its own drink tap. See
  // openRefundReasonPicker below.
  await openRefundReasonPicker(paymentAttemptId, orderLineIds, totalLabel);
}

// ---------------------------------------------------------------------
// REF1 (DEC-011, 2026-08-29): the refund reason picker, a one-tap sheet
// between the drink tap-select confirm and the write. Same shape as
// openVoidReasonPicker above: openChoiceSheet, live-read (never cached at
// sign-in, so an Admin retiring a reason mid-shift takes it off the
// floor's picker on the next refund), NO blank option, NOTHING
// preselected, and no free-text field anywhere in the path.
// ---------------------------------------------------------------------

async function openRefundReasonPicker(
  paymentAttemptId: string,
  orderLineIds: string[],
  totalLabel: string,
): Promise<void> {
  const session = requireSession();
  const result = await listRefundReasons(session.apiBaseUrl, session.tenantId);
  if (result.status !== 200) {
    showToast(
      `Could not load the refund reasons. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const reasons: RefundReasonDto[] = sortByName(
    result.body.refundReasons,
    (reason) => reason.label,
  );
  if (reasons.length === 0) {
    // Never expected (every location is seeded with five), but an Admin
    // who retired all of them would otherwise get an empty sheet with no
    // explanation. Say what is wrong and where to fix it, matching
    // openVoidReasonPicker's own handling.
    showToast(
      "There are no refund reasons set up for this location. An admin adds them in Settings.",
      "danger",
    );
    return;
  }
  openChoiceSheet({
    title: "Why is this being refunded?",
    subtitle: totalLabel,
    note: "Pick the reason before the refund is sent.",
    options: reasons.map((reason) => ({
      id: reason.id,
      label: reason.label,
      testid: "refund-reason-option",
    })),
    onPick: (refundReasonId) =>
      void submitRefund(paymentAttemptId, orderLineIds, totalLabel, refundReasonId),
  });
}

async function openRefundAmountReasonPicker(
  paymentAttemptId: string,
  amountCents: number,
): Promise<void> {
  const session = requireSession();
  const result = await listRefundReasons(session.apiBaseUrl, session.tenantId);
  if (result.status !== 200) {
    showToast(
      `Could not load the refund reasons. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const reasons: RefundReasonDto[] = sortByName(
    result.body.refundReasons,
    (reason) => reason.label,
  );
  if (reasons.length === 0) {
    showToast(
      "There are no refund reasons set up for this location. An admin adds them in Settings.",
      "danger",
    );
    return;
  }
  openChoiceSheet({
    title: "Why is this being refunded?",
    subtitle: formatCents(amountCents),
    note: "Pick the reason before the refund is sent.",
    options: reasons.map((reason) => ({
      id: reason.id,
      label: reason.label,
      testid: "refund-reason-option",
    })),
    onPick: (refundReasonId) =>
      void submitRefundAmount(paymentAttemptId, amountCents, refundReasonId),
  });
}

// ---------------------------------------------------------------------
// REFUNDALL1 (docs/build-briefs/REFUNDALL1-one-tap-ticket-refund.md; owner, 2026-10-31). Refund the whole ticket from
// one sheet. Every figure is the server's (refundable-lines); nothing is typed or computed here except sums of those
// figures. One refund call per payment, each with its own idempotency key. A tip refund is optional, off by default.
// ---------------------------------------------------------------------

interface RefundTicketPlan {
  paymentAttemptId: string;
  kind: "lines" | "amount";
  lineIds: string[];
  /** Drinks plus tax this payment returns, server-derived. */
  amountCents: number;
  tipRefundableCents: number;
  label: string;
  key: string;
  done: boolean;
  /** REFUNDRECEIPT1: set once this payment's refund is Succeeded: the refund its receipt is for. */
  receipt?: { refundId: string; returnedCents: number };
}

interface RefundTicketState {
  orderId: string;
  plans: RefundTicketPlan[];
  /** Equal-split payments: refunded card by card through the per-drink flow. */
  manualCount: number;
  includeTip: boolean;
  reasons: RefundReasonDto[];
  reasonId: string | undefined;
  busy: boolean;
}

let refundTicketState: RefundTicketState | undefined;

/** The header Refund button: shown when this login may refund and a captured payment still has something to return. */
function renderTicketRefundButton(): void {
  const session = state.session;
  ticketRefundButton.hidden =
    session === undefined ||
    state.orderId === undefined ||
    !hasPermission(session, "money.refund") ||
    state.ticketRefundable !== true;
}

function refundTicketPaymentLabel(
  payment: PaymentWithTipSplitsDto,
  detail: { cardBrand: string | null; cardLast4: string | null },
): string {
  if (payment.paymentAttempt.tender === "cash") return "Cash";
  return detail.cardBrand !== null && detail.cardLast4 !== null
    ? `${detail.cardBrand} ••••${detail.cardLast4}`
    : "the card";
}

async function openRefundTicketSheet(orderId: string, ticketLabel: string): Promise<void> {
  const session = requireSession();
  const paid = await getPayments(session.apiBaseUrl, session.tenantId, orderId);
  if (paid.status !== 200) {
    showToast(
      `Could not load this ticket's payments. ${capitalizeServerMessage(paid.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const captured = paid.body.payments
    .filter((p) => p.paymentAttempt.status === "Captured")
    .sort((a, b) => a.paymentAttempt.updated_at.localeCompare(b.paymentAttempt.updated_at));
  const plans: RefundTicketPlan[] = [];
  let manualCount = 0;
  for (const payment of captured) {
    const id = payment.paymentAttempt.id;
    const detail = await listRefundableLines(session.apiBaseUrl, session.tenantId, id);
    if (detail.status !== 200) {
      showToast(
        `Could not load this ticket's drinks. ${capitalizeServerMessage(detail.body.message ?? "Check your connection.")}`,
        "danger",
      );
      return;
    }
    refundPaymentTender.set(id, payment.paymentAttempt.tender === "cash" ? "cash" : "card");
    const body = detail.body;
    const label = refundTicketPaymentLabel(payment, body);
    if (body.equalSplit !== null) {
      if (body.lines.some((line) => !line.refunded && !line.comped)) manualCount += 1;
      continue;
    }
    if (body.amountRefundable || body.amountPayment) {
      if (body.refundableCents > 0) {
        plans.push({
          paymentAttemptId: id,
          kind: "amount",
          lineIds: [],
          amountCents: body.refundableCents,
          tipRefundableCents: body.tipRefundableCents,
          label,
          key: crypto.randomUUID(),
          done: false,
        });
      }
      continue;
    }
    const open = body.lines.filter((line) => !line.refunded && !line.comped);
    if (open.length === 0) continue;
    plans.push({
      paymentAttemptId: id,
      kind: "lines",
      lineIds: open.map((line) => line.order_line_id),
      amountCents: open.reduce(
        (sum, line) =>
          sum + line.line_total_cents_at_capture + line.tax_share_cents + (line.card_price_share_cents ?? 0),
        0,
      ),
      tipRefundableCents: body.tipRefundableCents,
      label,
      key: crypto.randomUUID(),
      done: false,
    });
  }
  if (plans.length === 0 && manualCount === 0) {
    showToast("Nothing to refund on this ticket.", "neutral");
    return;
  }
  const reasonsResult = await listRefundReasons(session.apiBaseUrl, session.tenantId);
  if (reasonsResult.status !== 200) {
    showToast(
      `Could not load the refund reasons. ${capitalizeServerMessage(reasonsResult.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const reasons = sortByName(reasonsResult.body.refundReasons, (reason) => reason.label);
  if (reasons.length === 0) {
    showToast(
      "There are no refund reasons set up for this location. An admin adds them in Settings.",
      "danger",
    );
    return;
  }
  refundTicketState = {
    orderId,
    plans,
    manualCount,
    includeTip: false,
    reasons,
    reasonId: undefined,
    busy: false,
  };
  refundTicketSubtitle.textContent = ticketLabel;
  renderRefundTicketSheet();
  refundDrinksSheetEl.hidden = true;
  refundTicketSheetEl.hidden = false;
  refundTicketScrim.hidden = false;
}

function closeRefundTicketSheet(): void {
  closeRefundSheet();
}

function refundTicketTotals(sheet: RefundTicketState): {
  drinksCents: number;
  tipCents: number;
  totalCents: number;
} {
  const open = sheet.plans.filter((plan) => !plan.done);
  const drinksCents = open.reduce((sum, plan) => sum + plan.amountCents, 0);
  const tipCents = open.reduce((sum, plan) => sum + plan.tipRefundableCents, 0);
  return { drinksCents, tipCents, totalCents: drinksCents + (sheet.includeTip ? tipCents : 0) };
}

function renderRefundTicketSheet(): void {
  const sheet = refundTicketState;
  if (sheet === undefined) return;
  const { tipCents, totalCents } = refundTicketTotals(sheet);
  refundTicketCards.innerHTML = "";
  for (const plan of sheet.plans) {
    const row = document.createElement("div");
    row.className = "choice-note";
    row.dataset["testid"] = "refund-ticket-card-line";
    const tipPart = sheet.includeTip && !plan.done ? plan.tipRefundableCents : 0;
    row.textContent = plan.done
      ? `Refunded to ${plan.label}.`
      : `${formatCents(plan.amountCents + tipPart)} to ${plan.label}`;
    refundTicketCards.append(row);
  }
  if (tipCents > 0) {
    refundTicketTipNote.hidden = false;
    refundTicketTipNote.textContent = sheet.includeTip
      ? `The ${formatCents(tipCents)} tip goes back with the drinks.`
      : `The ${formatCents(tipCents)} tip stays (tips are not refunded).`;
    refundTicketTipList.hidden = false;
    refundTicketTipToggle.setAttribute("aria-pressed", sheet.includeTip ? "true" : "false");
    refundTicketTipToggle.classList.toggle("selected", sheet.includeTip);
    refundTicketTipLabel.textContent = `Also refund the ${formatCents(tipCents)} tip`;
    refundTicketTipSublabel.textContent = sheet.includeTip
      ? "On. The tip is taken back from the staff."
      : "Off. The tip stays with the staff.";
  } else {
    refundTicketTipNote.hidden = true;
    refundTicketTipList.hidden = true;
  }
  const open = sheet.plans.filter((plan) => !plan.done);
  if (sheet.manualCount > 0) {
    refundTicketFallback.hidden = false;
    refundTicketFallback.textContent =
      open.length === 0
        ? "This ticket was paid with an equal split, so each card is refunded on its own. Use Refund some drinks instead."
        : "A part of this ticket was paid with an equal split. That part is not in this refund; use Refund some drinks instead for it.";
  } else {
    refundTicketFallback.hidden = true;
  }
  refundTicketReasons.innerHTML = "";
  for (const reason of sheet.reasons) {
    const selected = sheet.reasonId === reason.id;
    const button = document.createElement("button");
    button.type = "button";
    button.className = selected ? "choice-option selected" : "choice-option";
    button.dataset["testid"] = "refund-ticket-reason-option";
    button.setAttribute("aria-pressed", selected ? "true" : "false");
    const labelEl = document.createElement("span");
    labelEl.className = "choice-option-label";
    labelEl.textContent = reason.label;
    button.append(labelEl);
    if (selected) {
      const sub = document.createElement("span");
      sub.className = "choice-option-sublabel";
      sub.textContent = "Selected";
      button.append(sub);
    }
    button.addEventListener("click", () => {
      sheet.reasonId = reason.id;
      renderRefundTicketSheet();
    });
    refundTicketReasons.append(button);
  }
  refundTicketConfirm.hidden = open.length === 0;
  refundTicketConfirm.textContent = `Refund ${formatCents(totalCents)}`;
  refundTicketConfirm.disabled = sheet.busy || sheet.reasonId === undefined || open.length === 0;
}

function handleRefundTicketTipToggle(): void {
  const sheet = refundTicketState;
  if (sheet === undefined || sheet.busy) return;
  sheet.includeTip = !sheet.includeTip;
  // The server binds a key to what it first saw, so a changed choice is a new request with a new key.
  for (const plan of sheet.plans) plan.key = crypto.randomUUID();
  renderRefundTicketSheet();
}

function handleRefundTicketSomeClick(): void {
  const sheet = refundTicketState;
  if (sheet === undefined || sheet.busy) return;
  const orderId = sheet.orderId;
  closeRefundTicketSheet();
  void handlePaymentsButtonClick(orderId);
}

async function handleRefundTicketConfirmClick(): Promise<void> {
  const sheet = refundTicketState;
  if (sheet === undefined || sheet.busy || sheet.reasonId === undefined) return;
  const session = requireSession();
  const refundReasonId = sheet.reasonId;
  sheet.busy = true;
  renderRefundTicketSheet();
  let returnedCents = 0;
  let unconfirmed = false;
  const problems: string[] = [];
  for (const plan of sheet.plans) {
    if (plan.done) continue;
    if (!(await readyForRefund(plan.paymentAttemptId))) {
      problems.push(`Nothing was sent to ${plan.label}: the cash drawer is not open.`);
      break;
    }
    const includeTip = sheet.includeTip && plan.tipRefundableCents > 0;
    const gated = await requestApproval(
      `Refund ${formatCents(plan.amountCents + (includeTip ? plan.tipRefundableCents : 0))} to ${plan.label}`,
      (approval) =>
        plan.kind === "lines"
          ? refundPaymentLines(session.apiBaseUrl, session.tenantId, plan.paymentAttemptId, {
              idempotencyKey: plan.key,
              orderLineIds: plan.lineIds,
              refundReasonId,
              ...(includeTip ? { includeTip: true } : {}),
              ...approval,
            })
          : refundAmountPayment(session.apiBaseUrl, session.tenantId, plan.paymentAttemptId, {
              idempotencyKey: plan.key,
              amountCents: plan.amountCents,
              refundReasonId,
              ...(includeTip ? { includeTip: true } : {}),
              ...approval,
            }),
      () => returnAfterRefund(),
    );
    if (gated.canceled) {
      problems.push(`Nothing was sent to ${plan.label}: the approval was canceled.`);
      break;
    }
    const result = gated.result;
    if (result.status !== 200 && result.status !== 201) {
      problems.push(
        `Could not refund ${plan.label}: ${result.body.message ?? result.body.error ?? result.status}.`,
      );
      break;
    }
    const refund = result.body.refund;
    if (refund.status === "Succeeded" || refund.status === "Unknown") {
      plan.done = true;
      returnedCents += refund.amount_cents + (refund.tip_cents ?? 0);
      if (refund.status === "Unknown") unconfirmed = true;
      else plan.receipt = { refundId: refund.id, returnedCents: refund.amount_cents + (refund.tip_cents ?? 0) };
    } else {
      problems.push(`The card provider declined the refund to ${plan.label}. It can be retried.`);
      break;
    }
  }
  sheet.busy = false;
  const orderId = sheet.orderId;
  if (problems.length === 0) {
    closeRefundTicketSheet();
    showToast(
      `Refunded ${formatCents(returnedCents)}.` +
        (unconfirmed
          ? " The card provider has not confirmed every refund yet. Check back before retrying. No receipt until the refund is confirmed."
          : ""),
      unconfirmed ? "danger" : "success",
    );
    // REFUNDRECEIPT1: each Succeeded refund gets the receipt choices a payment gets.
    offerRefundReceipts(
      sheet.plans.flatMap((plan) =>
        plan.receipt === undefined
          ? []
          : [{ id: plan.receipt.refundId, orderId, paymentAttemptId: plan.paymentAttemptId, returnedCents: plan.receipt.returnedCents }],
      ),
    );
  } else {
    // Never a silent partial: say what went back and what did not, and keep the sheet open for the rest.
    const done = sheet.plans.filter((plan) => plan.done);
    const head =
      done.length > 0
        ? `Refunded ${formatCents(returnedCents)} to ${done.map((plan) => plan.label).join(", ")}. `
        : "";
    const message = `${head}${problems.join(" ")}${done.length > 0 ? " Tap Refund again to send the rest." : ""}`;
    refundTicketFallback.hidden = false;
    refundTicketFallback.textContent = message;
    refundTicketFallback.setAttribute("role", "alert");
    showToast(message, "danger");
    renderRefundTicketSheet();
    refundTicketFallback.hidden = false;
    refundTicketFallback.textContent = message;
  }
  if (returnedCents > 0 && state.orderId === orderId) {
    await refreshPaymentPreview();
  }
}

/** REFUNDALL1. A refund can be started from the ticket or from a closed ticket's card on Open Tabs; it returns to where it began. */
function returnAfterRefund(): void {
  if (state.orderId === undefined) {
    showOpenTabsScreen();
    void loadOpenTabs();
    return;
  }
  showRingScreen();
}

/** REFUNDRECEIPT1: the per-drink and by-amount refund flows. Only a Succeeded refund is offered a receipt. */
function offerReceiptForSingleRefund(
  refund: { id: string; status: string; amount_cents: number; tip_cents?: number; order_id?: string },
  paymentAttemptId: string,
): void {
  if (refund.status !== "Succeeded" || refund.order_id === undefined) return;
  offerRefundReceipts([
    {
      id: refund.id,
      orderId: refund.order_id,
      paymentAttemptId,
      returnedCents: refund.amount_cents + (refund.tip_cents ?? 0),
    },
  ]);
}

async function submitRefundAmount(
  paymentAttemptId: string,
  amountCents: number,
  refundReasonId: string,
): Promise<void> {
  const session = requireSession();
  if (!(await readyForRefund(paymentAttemptId))) {
    returnAfterRefund();
    return;
  }
  const idempotencyKey = crypto.randomUUID();
  const gated = await requestApproval(
    `Refund ${formatCents(amountCents)}`,
    (approval) =>
      refundAmountPayment(session.apiBaseUrl, session.tenantId, paymentAttemptId, {
        idempotencyKey,
        amountCents,
        refundReasonId,
        ...approval,
      }),
    () => returnAfterRefund(),
  );
  if (gated.canceled) {
    return;
  }
  const result = gated.result;
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not refund (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    returnAfterRefund();
    return;
  }
  returnAfterRefund();
  const refund = result.body.refund;
  if (refund.status === "Succeeded") {
    showToast(
      `Refunded ${formatCents(refund.amount_cents)}.` +
        (refundPaymentTender.get(paymentAttemptId) === "cash" ? " Cash from the drawer." : ""),
      "success",
    );
  } else if (refund.status === "Unknown") {
    showToast(
      "The refund was sent but the card provider has not confirmed it. Check back before retrying. No receipt until the refund is confirmed.",
      "danger",
    );
  } else {
    showToast("The card provider declined the refund. It can be retried.", "danger");
  }
  offerReceiptForSingleRefund(refund, paymentAttemptId);
  if (refund.status === "Succeeded" || refund.status === "Unknown") {
    await refreshPaymentPreview();
  }
}

async function submitRefund(
  paymentAttemptId: string,
  orderLineIds: string[],
  totalLabel: string,
  refundReasonId: string,
): Promise<void> {
  const session = requireSession();
  if (!(await readyForRefund(paymentAttemptId))) {
    returnAfterRefund();
    return;
  }
  // One key for the whole flow, approval retry included, same as every
  // other money command from the floor.
  const idempotencyKey = crypto.randomUUID();
  const gated = await requestApproval(
    `Refund ${orderLineIds.length} drink${orderLineIds.length === 1 ? "" : "s"} (${totalLabel})`,
    (approval) =>
      refundPaymentLines(session.apiBaseUrl, session.tenantId, paymentAttemptId, {
        idempotencyKey,
        orderLineIds,
        refundReasonId,
        ...approval,
      }),
    () => returnAfterRefund(),
  );
  if (gated.canceled) {
    return;
  }
  const result = gated.result;
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not refund (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    returnAfterRefund();
    return;
  }
  returnAfterRefund();
  const refund = result.body.refund;
  if (refund.status === "Succeeded") {
    // Honest about the half people get wrong, the same stance the void
    // toast takes: the money moves, the stock does not.
    showToast(
      `Refunded ${formatCents(refund.amount_cents)}.` +
        (refundPaymentTender.get(paymentAttemptId) === "cash" ? " Cash from the drawer." : "") +
        " The stock does not come back.",
      "success",
    );
  } else if (refund.status === "Unknown") {
    showToast(
      "The refund was sent but the card provider has not confirmed it. Check back before retrying. No receipt until the refund is confirmed.",
      "danger",
    );
  } else {
    showToast("The card provider declined the refund. The drinks can be retried.", "danger");
  }
  offerReceiptForSingleRefund(refund, paymentAttemptId);
  if (refund.status === "Succeeded" || refund.status === "Unknown") {
    // UX62-F1 (docs/build-briefs/UX62-F1-refunded-line.md): both statuses
    // hold real money (DEC-035's own "non-Failed counts" rule), so the
    // ticket rail's new Refunded row -- and Paid/Tips alongside it -- must
    // reflect this refund immediately, not only after the next unrelated
    // refresh or a full ticket reload. A Failed refund moved nothing, so
    // it is skipped here.
    await refreshPaymentPreview();
  }
}

async function handleLineEditChangeLiquorClick(): Promise<void> {
  const orderLineId = state.lineEditTargetOrderLineId;
  const orderId = state.orderId;
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  if (
    orderId === undefined ||
    orderLineId === undefined ||
    line === undefined ||
    line.menuDrinkId === undefined
  ) {
    return;
  }
  const session = requireSession();
  const gen = sessionGeneration;
  closeLineEditSheet();
  const [detail, orderResult] = await Promise.all([
    ensureMenuDrinkDetail(line.menuDrinkId),
    getOrder(session.apiBaseUrl, session.tenantId, orderId),
  ]);
  if (!sessionIsCurrent(gen) || state.orderId !== orderId) return;
  if (detail === undefined || orderResult.status !== 200) {
    showToast("Could not load this drink's liquor options.", "danger");
    return;
  }
  const storedLine = orderResult.body.lines.find((candidate) => candidate.id === orderLineId);
  if (
    storedLine === undefined ||
    storedLine.removed_at !== null ||
    storedLine.menu_drink_id !== line.menuDrinkId
  ) {
    showToast("This drink is no longer on this ticket.", "danger");
    return;
  }
  openLiquorSheet(
    detail,
    orderLineId,
    false,
    storedLine.modifiers.map((modifier) => ({
      label: modifier.label,
      amountCents: modifier.price_adjustment_cents,
    })),
  );
}
// ALPHA-12 / Amendment 2026-08-22 (fifth), item 2: the explicit,
// append-only reattribution command (src/domain/guests.ts's
// changeLineGuest). Reachable whenever the ticket has at least one named
// guest (openLineEditSheet's own gate), on both an unassigned and an
// already-assigned line; nothing is preselected in the picker either way,
// matching this sheet's "no soft default" pattern elsewhere.
function openGuestPickerForLine(orderLineId: string): void {
  openChoiceSheet({
    title: "Whose drink is this?",
    options: state.guests.map((guest) => ({
      id: guest.id,
      label: guest.label,
      testid: "line-edit-guest-option",
    })),
    onPick: (guestId) => void submitLineGuestChange(orderLineId, guestId),
  });
}

async function handleLineEditChangeGuestClick(): Promise<void> {
  const orderLineId = state.lineEditTargetOrderLineId;
  if (orderLineId === undefined) {
    return;
  }
  closeLineEditSheet();
  openGuestPickerForLine(orderLineId);
}

// VOC1-F8 build-time amendment, dated 2026-09-22: the ticket line's own
// recipe view, reached through the edit sheet rather than a fourth
// always-visible row button (see buildTicketLineRow's own comment).
async function handleLineEditViewRecipeClick(): Promise<void> {
  const orderLineId = state.lineEditTargetOrderLineId;
  if (orderLineId === undefined) {
    return;
  }
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  if (line === undefined || line.menuDrinkId === undefined) {
    return;
  }
  const menuDrinkId = line.menuDrinkId;
  const drinkName = line.name;
  closeLineEditSheet();
  await handleViewRecipeClick(menuDrinkId, drinkName);
}

// ---------------------------------------------------------------------
// CORE5: +1 (the header chip repeats the newest drink; Add another in a row's menu repeats that row). Adds another drink with the SAME selections the row's drink already carries
// (liquor, pour, extras, guest), through the ordinary add paths, so the server prices it at the current
// price and Prepared, stock and payment stay per drink. Never replays an 86 override.
// ---------------------------------------------------------------------

const repeatInFlightLineIds = new Set<string>();

async function handleRepeatLineClick(
  orderLineId: string,
  button: HTMLButtonElement,
): Promise<void> {
  if (repeatInFlightLineIds.has(orderLineId)) {
    return;
  }
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  if (line === undefined) {
    return;
  }
  const repeatContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(repeatContext)) return;
  repeatInFlightLineIds.add(orderLineId);
  button.disabled = true;
  try {
    const needsGuestPick = state.guests.length >= 2 && line.guestId === undefined;
    const guestId = state.guests.length >= 2 ? line.guestId : undefined;
    if (line.lineKind === "cocktail") {
      if (line.menuDrinkId === undefined) {
        showToast(`Could not add another ${line.name}. Ring it from the menu.`, "danger");
        return;
      }
      const detail = await ensureMenuDrinkDetail(line.menuDrinkId);
      if (!floorSaleContextIsCurrent(repeatContext)) return;
      if (detail === undefined) {
        showToast(`Could not add another ${line.name}. Ring it from the menu.`, "danger");
        return;
      }
      const selection: DrinkSelection = {
        liquorProductId: line.liquorProductId,
        ounces: line.ounces,
        name: line.detail,
        isSubstitution: line.isSubstitution === true,
        pourModifierApplied: line.pourModifierApplied === true,
        modifierSelections: line.modifierSelections ?? [],
      };
      if (needsGuestPick) {
        await addDrinkToTicketWithGuestPick(detail, selection);
      } else {
        await addDrinkToTicket(detail, selection, guestId);
      }
      return;
    }
    const session = requireSession();
    const productResult = await getLiquorProduct(
      session.apiBaseUrl,
      session.tenantId,
      line.liquorProductId ?? "",
    );
    if (!floorSaleContextIsCurrent(repeatContext)) return;
    if (line.liquorProductId === undefined || productResult.status !== 200) {
      showToast(`Could not add another ${line.name}. Ring it from the menu.`, "danger");
      return;
    }
    const before = state.lines.length;
    const modifierSelections = line.modifierSelections ?? [];
    if (needsGuestPick) {
      await addInventorySaleToTicketWithGuestPick(
        productResult.body.liquorProduct,
        line.lineKind,
        line.ounces,
        false,
        modifierSelections,
      );
    } else {
      await addInventorySaleToTicket(
        productResult.body.liquorProduct,
        line.lineKind,
        line.ounces,
        guestId,
        false,
        modifierSelections,
      );
    }
    if (!floorSaleContextIsCurrent(repeatContext)) return;
    // The new row shows the same extras badges the original carries.
    const added = state.lines.length > before ? state.lines[state.lines.length - 1] : undefined;
    if (added !== undefined && line.appliedModifiers !== undefined) {
      added.appliedModifiers = line.appliedModifiers;
      renderTicket();
    }
  } finally {
    repeatInFlightLineIds.delete(orderLineId);
    if (floorSaleContextIsCurrent(repeatContext)) button.disabled = false;
  }
}

// ---------------------------------------------------------------------
// CORE6: Move to tab, from the row's menu. One unpaid drink goes to another open tab at this location; it
// arrives with no guest. The server refuses a paid drink, a drink with a discount, and a tab that is
// mid-payment (src/domain/lineMove.ts); the refusal text is shown as the server wrote it.
// ---------------------------------------------------------------------

async function handleLineEditRepeatClick(): Promise<void> {
  const orderLineId = state.lineEditTargetOrderLineId;
  if (orderLineId === undefined) {
    return;
  }
  closeLineEditSheet();
  await handleRepeatLineClick(orderLineId, lineEditRepeatButton);
}

async function handleTicketRepeatClick(): Promise<void> {
  if (refuseWhileAwaitingTip()) {
    return;
  }
  const newest = state.lines[state.lines.length - 1];
  if (newest === undefined) {
    return;
  }
  await handleRepeatLineClick(newest.orderLineId, ticketRepeatButton);
}

async function handleLineEditMoveToTabClick(): Promise<void> {
  const orderLineId = state.lineEditTargetOrderLineId;
  if (orderLineId === undefined) {
    return;
  }
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  const session = requireSession();
  const currentOrderId = state.orderId;
  closeLineEditSheet();
  if (line === undefined || currentOrderId === undefined) {
    return;
  }
  const result = await listOpenTabs(session.apiBaseUrl, session.tenantId);
  if (result.status !== 200) {
    showToast(
      `Could not load the open tabs (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  const current = result.body.tabs.find((tab) => tab.id === currentOrderId);
  const others = result.body.tabs.filter(
    (tab) =>
      tab.id !== currentOrderId &&
      (current === undefined || tab.business_date === current.business_date),
  );
  openChoiceSheet({
    title: `Move ${line.name} to`,
    options: others.map((tab) => ({
      id: tab.id,
      label: tabDisplayLabel(tab.name, tab.guest_labels),
      ...(tab.opened_by_name === null ? {} : { sublabel: tab.opened_by_name }),
      testid: "move-to-tab-option",
    })),
    ...(others.length === 0 ? { note: "No other open tabs to move to." } : {}),
    onPick: (targetOrderId) => {
      const label = tabDisplayLabel(
        others.find((tab) => tab.id === targetOrderId)?.name ?? null,
        others.find((tab) => tab.id === targetOrderId)?.guest_labels ?? [],
      );
      void submitMoveLineToTab(orderLineId, targetOrderId, label);
    },
  });
}

async function submitMoveLineToTab(
  orderLineId: string,
  targetOrderId: string,
  targetLabel: string,
): Promise<void> {
  const session = requireSession();
  const result = await moveLineToTab(session.apiBaseUrl, session.tenantId, orderLineId, {
    idempotencyKey: crypto.randomUUID(),
    targetOrderId,
  });
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not move that drink (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  state.lines = state.lines.filter((candidate) => candidate.orderLineId !== orderLineId);
  renderTicket();
  await recomputeQuote();
  void refreshPaymentPreview();
  showToast(`Moved to ${targetLabel}.`, "success");
}

async function submitLineGuestChange(orderLineId: string, guestId: string): Promise<void> {
  const session = requireSession();
  const result = await changeLineGuest(session.apiBaseUrl, session.tenantId, orderLineId, {
    idempotencyKey: crypto.randomUUID(),
    guestId,
  });
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not change this line's guest (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  const line = state.lines.find((l) => l.orderLineId === orderLineId);
  if (line !== undefined) {
    line.guestId = result.body.orderLine.guest_id ?? undefined;
  }
  renderTicket();
  // SPLIT1: the By guest list reads the server's guest shares, which move with the drink, so the preview is re-read.
  await refreshPaymentPreview();
  showToast("Guest changed.", "success");
}

// ---------------------------------------------------------------------
// Adding a line to the ticket
// ---------------------------------------------------------------------

function buildLineComponents(
  detail: MenuDrinkDetail,
  selection?: DrinkSelection,
): OrderLineComponentInput[] | undefined {
  // VOC1-F4: liquorProductId now guards this branch (rather than
  // `selection === undefined` alone), since a modifiers-only selection
  // (openModifiersOnlySheet, DrinkSelection's own comment) is a real,
  // defined selection object that still carries no liquor pick at all --
  // it must build components the identical no-liquor way a bare
  // addDrinkToTicketWithGuestPick(detail) call already does.
  if (selection?.liquorProductId === undefined) {
    return detail.components.map((component) => ({
      componentReference: component.component_reference,
      quantity: Number(component.quantity),
      unit: component.unit,
    }));
  }
  const { ounces, name } = selection;
  if (ounces === undefined || name === undefined) {
    // Every call site that sets liquorProductId sets ounces and name
    // together (the liquor sheet's own confirm, handleLiquorAddClick);
    // this should be unreachable in practice. Undefined (rather than
    // silently guessing which component to overwrite) lets the caller
    // fail loudly with a toast instead of mispricing or dropping the
    // selection, the same shape the matchMiss branch just below uses.
    return undefined;
  }
  const match = findLiquorSlotComponent(detail.components);
  if (match === undefined) {
    // handleLiquorAddClick only reaches here when state.sheet.matchMiss
    // was false, so this should be unreachable in practice; undefined
    // (rather than silently guessing which component to overwrite, the
    // old ALPHA-06 fallback this replaced) lets the caller fail loudly
    // with a toast instead of mispricing or dropping the selection.
    return undefined;
  }
  return detail.components.map((component) => {
    if (component.id === match.component.id) {
      // Gate finding C fix: this is the ONE component the recipe itself
      // marks as the liquor slot (match came from findLiquorSlotComponent,
      // which reads menu_drink_components.is_liquor_slot, never a name
      // guess). Marking it here lets the server stamp liquor_product_id by
      // this explicit flag instead of matching submitted component text
      // against the product's name, which a coincidentally-named garnish
      // or mixer could otherwise satisfy.
      return {
        componentReference: name,
        quantity: Number(ounces),
        unit: "oz",
        isLiquorSlot: true,
      };
    }
    return {
      componentReference: component.component_reference,
      quantity: Number(component.quantity),
      unit: component.unit,
    };
  });
}

async function addDrinkToTicket(
  detail: MenuDrinkDetail,
  selection?: DrinkSelection,
  /** ALPHA-12. Omitted for a zero- or one-guest ticket (server resolves
   * automatically); required by the caller, addDrinkToTicketWithGuestPick,
   * whenever the ticket has two or more guests. */
  guestId?: string,
): Promise<void> {
  const session = requireSession();
  const saleContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(saleContext)) return;

  if (state.orderId === undefined) {
    showToast("Resume or start a ticket first.", "danger");
    return;
  }
  const components = buildLineComponents(detail, selection);
  if (components === undefined) {
    showToast(
      `Could not add ${detail.menuDrink.name}: its liquor selection could not be matched to the recipe. Tell a manager.`,
      "danger",
    );
    return;
  }
  // LOCFLOOR1 (sweep B17): a drink saved with no ingredients cannot take a line. The menu does not offer it, but a menu
  // read from before the drink was fixed or a recipe link can still reach here; say it plainly, never the server's text.
  if (components.length === 0) {
    showToast(`${detail.menuDrink.name}: No recipe yet, ask a manager.`, "danger");
    return;
  }
  const isSubstitution = selection?.isSubstitution ?? false;
  const pourModifierApplied = selection?.pourModifierApplied ?? false;
  // VOC1-F4 (Section 2.4): the chip picks made on the liquor sheet or the
  // modifiers-only sheet, sent as bare `value` identifiers; the server
  // resolves each against this drink's own attachments and the catalog's
  // CURRENT label/price, never trusting a client-submitted amount.
  const modifierSelections = selection?.modifierSelections ?? [];
  let publicationBinding=selection?.publicationBinding;
  if(publicationBinding===undefined&&detail.menuDrink.network_publication_id!=null){
    const freshQuote=await priceQuote(session.apiBaseUrl,session.tenantId,[{menuDrinkId:detail.menuDrink.id,liquorProductId:selection?.liquorProductId,ounces:selection?.ounces,isSubstitution,pourModifierApplied,modifierSelections,...override86Fields(selection?.override86??false)}]);
    if(!floorSaleContextIsCurrent(saleContext))return;
    const priced=freshQuote.status===200?freshQuote.body.lines[0]:undefined;
    if(priced===undefined||!priced.priceable){showToast("The recipe changed. Choose it again.","danger");return;}
    publicationBinding=priced.publicationBinding;
  }
  const orderIdForAdd = state.orderId;
  const addOverride86: Override86 = selection?.override86 ?? false;
  const addKey = crypto.randomUUID();
  const result = await runPersistingSale(
    addOverride86,
    `Sell 86'd item: ${selection?.name ?? detail.menuDrink.name}`,
    (approval) =>
      addLine(session.apiBaseUrl, session.tenantId, orderIdForAdd, {
        idempotencyKey: addKey,
        publicationBinding,
        itemReference: detail.menuDrink.name,
        quantity: 1,
        components,
        isSubstitution,
        // ALPHA-08: the server now resolves and prices this line itself
        // (src/domain/lineResolution.ts), using exactly these three fields
        // the same way price-quote already does; the client's own job is
        // just to send what it already knows from the tile/sheet interaction.
        menuDrinkId: detail.menuDrink.id,
        liquorProductId: selection?.liquorProductId,
        ounces: selection?.ounces,
        guestId,
        pourModifierApplied,
        modifierSelections,
        ...override86Fields(addOverride86),
        ...(approval ?? {}),
      }),
    () => floorSaleContextIsCurrent(saleContext),
  );
  if (result === undefined || !floorSaleContextIsCurrent(saleContext)) {
    return;
  }
  if (result.status !== 200 && result.status !== 201) {
    if (result.body.error === "guest_selection_required") {
      // A real race (a guest was added or removed by another tap between
      // the guest picker opening and this call landing): plain language,
      // never the raw domain error, matching this codebase's own
      // translated-error convention for pay/close (translatePayCloseError).
      showToast(
        "This ticket's guest list changed. Reopen the menu and pick a guest again.",
        "danger",
      );
      return;
    }
    showToast(
      `Could not add ${detail.menuDrink.name} (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  const orderLine = result.body.orderLine;
  // UX124: this device's own sale can move an item to Low or OUT.
  void refreshFloorMenuStock();
  // VOC1-F4 (Section 2.5): the catalog's own already-fetched label/price
  // for each SELECTED value, for the ticket line's own badges below.
  // Display only: money itself is exactly what the server just priced
  // (orderLine.line_total_cents), never recomputed from this list.
  const resolvedAppliedModifiers = detail.modifiers
    .filter((modifier) => modifierSelections.includes(modifier.value))
    .map((modifier) => ({
      label: modifier.label,
      priceAdjustmentCents: modifier.priceAdjustmentCents,
    }));
  const appliedModifiers =
    resolvedAppliedModifiers.length > 0 ? resolvedAppliedModifiers : undefined;
  const lineState: TicketLineState = {
    orderLineId: orderLine.id,
    lineKind: "cocktail",
    menuDrinkId: detail.menuDrink.id,
    name: detail.menuDrink.name,
    detail: selection?.name,
    liquorProductId: selection?.liquorProductId,
    ounces: selection?.ounces,
    isSubstitution,
    pourModifierApplied,
    appliedModifiers,
    modifierSelections: modifierSelections.length > 0 ? modifierSelections : undefined,
    preparedState: orderLine.prepared_state === "prepared" ? "prepared" : "not_prepared",
    guestId: orderLine.guest_id ?? undefined,
  };
  state.lines.push(lineState);
  renderTicket();
  clearMenuSearchAfterAdd();
  if (addOverride86 !== false) {
    void refreshLineStockOverride(orderLine.id);
  }
  await recomputeQuote();
  if (!floorSaleContextIsCurrent(saleContext)) return;
  void refreshPaymentPreview();
  // Payments-track Slice 4 (DEC-002 Section 3): best-effort, never
  // blocking the add-line action that just succeeded above.
  handleHoldIncrementOutcome(result.body.holdIncrement);
}

// ---------------------------------------------------------------------
// ALPHA-07/ALPHA-08: reconstructing a resumed tab's lines
// ---------------------------------------------------------------------
//
// Before ALPHA-08, order_lines/order_line_components stored only
// item_reference (the drink's display name) and each raw ingredient
// reference/quantity/unit, so the ticket rail had to RE-DERIVE the
// menuDrinkId/liquorProductId/ounces triple a resumed line needs by
// matching item_reference back to a live menu drink by exact name and
// inferring the liquor selection from which order-line component did not
// match a canonical ingredient reference. That heuristic broke the moment
// a drink was renamed (docs/review-record.md Review 6/7's own flagged
// gap): a resumed line for a renamed drink could not be re-priced at all.
//
// order_lines now stores the exact facts directly (menu_drink_id,
// liquor_product_id, pour_ounces, is_substitution; see db/schema.ts's own
// comment), written once at add time by src/api/server.ts's add-line
// route. This function is the durable fix: it reads those facts back
// rather than re-deriving anything. The one remaining "needs a manager"
// case is a line with no stored menu_drink_id at all (added before this
// migration, or a genuinely orphaned reference no earlier session could
// have written); every other line reconstructs exactly, regardless of
// whether its menu drink or liquor product has since been renamed
// (renaming does not change an id), deactivated (Prepared/Pay/Close for
// an existing line never depend on the referenced row still being active,
// per test/api.test.ts's own ALPHA-10 item 3 coverage), or had its price
// changed (a resumed line's price still comes from a fresh price-quote
// call in recomputeQuote/summarizeTab, using the stored menuDrinkId/
// liquorProductId/ounces/isSubstitution as that call's input, not from
// order_lines.line_total_cents directly, so it always reflects the
// CURRENT catalog price the same way a freshly-added line does).
//
// A liquor product's display name (TicketLineState.detail) is looked up
// by id (getLiquorProduct), not searched by name: the id is already known
// exactly, so there is nothing to match ambiguously the way the old
// heuristic's step 4 had to.

// Inventory track (track/inventory) Slice 3: line_kind distinguishes a
// genuinely orphaned cocktail line (menu_drink_id null because the drink
// reference could not be resolved; see this function's own module header)
// from a direct inventory-sale line (menu_drink_id ALWAYS null by design,
// per src/domain/inventorySale.ts's own header: "a direct inventory sale
// has no menu drink at all"). Before this slice every null menu_drink_id
// meant the former; now it can mean either, so the branch below reads
// line_kind first rather than treating every null menu_drink_id as an
// orphan.
// ITEM2 Slice 5 (Section 3.4): used to be a closed five-literal membership
// test, which a tenant-added kind's own line_kind (its own value, never one
// of the five) could never match, sending it down the "genuinely orphaned"
// path below with no menu_drink_id to find either -- the exact "Price
// unavailable... needs a manager" symptom ITEM1-F4 already named for a
// different site. Generalized to the one fact that actually distinguishes
// the two cases: 'cocktail' is the only line_kind that is NOT a direct
// inventory sale. Every other value order_lines can possibly hold is
// already guaranteed to be a real sale kind by the database itself
// (order_lines_line_kind_check, migration 0119's trigger conversion:
// 'cocktail', or 'pour', or an existing item_kind whose sale_mechanism is
// set), so no closed list is needed to recognize one client-side.
function isInventorySaleLineKind(lineKind: string): lineKind is InventorySaleKind {
  return lineKind !== "cocktail";
}

// VOC1-F7 Slice 2 (DEC-171). Compares a cocktail line's real, stored pour
// ounces against its recipe's own snapshotted default, using the same
// cached menu-drink detail (ensureMenuDrinkDetail) and slot-matching logic
// (findLiquorSlotComponent) the liquor sheet itself uses, so the ticket
// badge on a RESUMED tab and every later recompute/resume carry the same
// pourModifierApplied signal resolveAndPriceLine now requires whenever the
// two differ (see buildPriceQuoteLine's own comment). No migration and no
// order_lines column exist for this fact (Slice 2 was scoped to neither);
// this is derived fresh from the same two facts the server itself already
// returns (menu_drink_id, pour_ounces), never a guess.
async function pourDiffersFromRecipeDefault(
  menuDrinkId: string | undefined,
  ounces: string | undefined,
): Promise<boolean> {
  if (menuDrinkId === undefined || ounces === undefined) {
    return false;
  }
  const detail = await ensureMenuDrinkDetail(menuDrinkId);
  if (detail === undefined) {
    return false;
  }
  const recipeMatch = findLiquorSlotComponent(detail.components);
  if (recipeMatch === undefined) {
    // No recipe default to compare against (a recipe edited since this
    // line was added, matchMiss's own condition). resolveAndPriceLine's
    // own "recipe default missing" branch requires pourModifierApplied
    // unconditionally in this state; matched here rather than guessing
    // "unchanged".
    return true;
  }
  return normalizeOunces(ounces) !== normalizeOunces(recipeMatch.ounces);
}

async function reconstructOrderLine(
  orderLine: OrderLineWithComponentsDto,
): Promise<ReconstructedLine> {
  const preparedState: "not_prepared" | "prepared" =
    orderLine.prepared_state === "prepared" ? "prepared" : "not_prepared";
  const lineKind: TicketLineState["lineKind"] = isInventorySaleLineKind(orderLine.line_kind)
    ? orderLine.line_kind
    : "cocktail";
  // VOC1-F4 (Section 2.5, frontend gap fix). The line's own stored
  // order_line_modifiers rows, read back directly rather than re-derived
  // from the drink's CURRENT attachments or catalog price -- the identical
  // "read the stored fact" discipline menuDrinkId/liquorProductId/
  // pourOunces already establish for a resumed tab.
  const appliedModifiers =
    orderLine.modifiers.length > 0
      ? orderLine.modifiers.map((modifier) => ({
          label: modifier.label,
          priceAdjustmentCents: modifier.price_adjustment_cents,
        }))
      : undefined;
  // VOC1-F4: the bare values, resent on every later recompute
  // (buildPriceQuoteLine's own comment on why this must be resent rather
  // than assumed from the line already being on the order).
  const modifierSelections =
    orderLine.modifiers.length > 0
      ? orderLine.modifiers.map((modifier) => modifier.modifier_value)
      : undefined;

  if (lineKind === "cocktail" && orderLine.menu_drink_id === null) {
    return {
      ticketLine: {
        orderLineId: orderLine.id,
        lineKind: "cocktail",
        // No stored menu drink reference at all; nothing downstream ever
        // reads menuDrinkId for a priceable: false line (both
        // recomputeQuote and summarizeTab filter these out of the
        // price-quote batch first).
        name: orderLine.item_reference,
        preparedState,
        inventoryPosted: orderLine.inventory_posted,
        priceable: false,
        guestId: orderLine.guest_id ?? undefined,
      },
      priceable: false,
    };
  }

  // A direct inventory-sale line (pour/beer/packaged) always has a
  // liquor_product_id (the item sold; addInventorySale's own route
  // guarantees this) and no menu_drink_id; a cocktail line with no liquor
  // selection at all (no liquor slot in its recipe) is priceable purely
  // on its build charge, same as before this slice.
  if (orderLine.liquor_product_id === null) {
    return {
      ticketLine: {
        orderLineId: orderLine.id,
        lineKind: "cocktail",
        menuDrinkId: orderLine.menu_drink_id ?? undefined,
        name: orderLine.item_reference,
        appliedModifiers,
        modifierSelections,
        preparedState,
        inventoryPosted: orderLine.inventory_posted,
        guestId: orderLine.guest_id ?? undefined,
      },
      priceable: true,
    };
  }

  const session = requireSession();
  const productResult = await getLiquorProduct(
    session.apiBaseUrl,
    session.tenantId,
    orderLine.liquor_product_id,
  );
  if (productResult.status !== 200) {
    // The stored liquor_product_id itself is real (it was resolved and
    // verified server-side at add time); a lookup failure here means the
    // liquor product row was deleted outright since. No delete path
    // exists anywhere in this codebase's liquor-product domain today, so
    // this branch is defensive, not a known live gap.
    return {
      ticketLine: {
        orderLineId: orderLine.id,
        lineKind,
        menuDrinkId: orderLine.menu_drink_id ?? undefined,
        name: orderLine.item_reference,
        preparedState,
        inventoryPosted: orderLine.inventory_posted,
        priceable: false,
        guestId: orderLine.guest_id ?? undefined,
      },
      priceable: false,
    };
  }

  // TKTPRICE1-F1 (DEC-360): a line on a product that has been deactivated since it was rung is NOT judged here. It is
  // reconstructed like any other and sent to the quote, and the server's answer decides: the stored price (the amount
  // Pay charges) when the only change is the menu, "Price unavailable" only for a true pricing conflict (INV19, PQ1)
  // or a missing stored price. The browser no longer infers unpriceable from its own catalog read, so the ticket
  // screen, Open Tabs and Pay agree.

  // VOC1-F7 Slice 2 (DEC-171): only meaningful for a cocktail line (a
  // direct-sale line has no recipe default to differ from). Computed here,
  // ahead of the return, since ReconstructedLine's own shape is built as
  // one literal below.
  const pourModifierApplied =
    lineKind === "cocktail"
      ? await pourDiffersFromRecipeDefault(
          orderLine.menu_drink_id ?? undefined,
          orderLine.pour_ounces ?? undefined,
        )
      : undefined;

  return {
    ticketLine: {
      orderLineId: orderLine.id,
      lineKind,
      menuDrinkId: orderLine.menu_drink_id ?? undefined,
      name: orderLine.item_reference,
      // Mirrors addInventorySaleToTicket's own detail convention: a
      // cocktail line's detail is the selected liquor's name; a direct-
      // sale line's detail is its pour size instead (a whole-unit sale has
      // none). ITEM2 Slice 5: used to also check `lineKind === "packaged"`,
      // redundant with `orderLine.pour_ounces === null` alone -- a
      // whole-unit sale is never stored with a pour_ounces value, the same
      // reasoning addInventorySaleToTicket's own identical simplification
      // above gives, generalized to any tenant-added whole-unit kind.
      detail:
        lineKind === "cocktail"
          ? productResult.body.liquorProduct.name
          : orderLine.pour_ounces === null
            ? undefined
            : `${orderLine.pour_ounces} oz`,
      liquorProductId: orderLine.liquor_product_id,
      ounces: orderLine.pour_ounces ?? undefined,
      isSubstitution: orderLine.is_substitution,
      pourModifierApplied,
      appliedModifiers,
      modifierSelections,
      preparedState,
      inventoryPosted: orderLine.inventory_posted,
      guestId: orderLine.guest_id ?? undefined,
    },
    priceable: true,
  };
}

// Design-gate fix (2026-08-20): reconstructOrderLine no longer drops a
// line (see its own header comment), so every order line the server
// returns comes back as exactly one ReconstructedLine; there is nothing
// left for this function to lose track of.
async function reconstructOrderLines(
  orderId: string,
): Promise<
  | {
      order: OrderDto;
      lines: ReconstructedLine[];
      guests: GuestDto[];
      lastReopen: LastReopenDto | null;
    }
  | undefined
> {
  const session = requireSession();
  const result = await getOrder(session.apiBaseUrl, session.tenantId, orderId);
  if (result.status !== 200) {
    return undefined;
  }
  const lines: ReconstructedLine[] = [];
  for (const orderLine of result.body.lines) {
    const reconstructed = await reconstructOrderLine(orderLine);
    // TKTPRICE1 (DEC-360): the stored price from the same read that lists the
    // line, so a reopened ticket draws what Pay will charge from the first
    // paint. A null stored price (a line from before ALPHA-08) leaves the
    // figure undefined: no figure, never a guess.
    if (orderLine.line_total_cents !== null && orderLine.line_total_cents !== undefined) {
      reconstructed.ticketLine.lineTotalCents = orderLine.line_total_cents;
    }
    // M5-R10: the ledger row count rides along for the strip and the line menu.
    if ((orderLine.priceChangeCount ?? 0) > 0) {
      reconstructed.ticketLine.priceChangeCount = orderLine.priceChangeCount;
    }
    // AU14-F15: the server-resolved "sold past an 86" record rides along.
    if (orderLine.stockOverride !== undefined && orderLine.stockOverride !== null) {
      reconstructed.ticketLine.stockOverride = orderLine.stockOverride;
    }
    lines.push(reconstructed);
  }
  return {
    order: result.body.order,
    lines,
    guests: result.body.guests,
    lastReopen: result.body.lastReopen ?? null,
  };
}

// Inventory track (track/inventory) Slice 3: shared by summarizeTab and
// recomputeQuote below, so a mixed-kind ticket's price-quote request is
// built identically wherever the ticket's running total is requoted. A
// cocktail line prices through menuDrinkId (unchanged); a direct
// inventory-sale line (lineKind "pour"/"beer"/"packaged") prices
// through saleKind instead, per src/api/validation.ts's
// PriceQuoteLineInput.saleKind comment.
function buildPriceQuoteLine(line: TicketLineState): PriceQuoteLineInput {
  if (line.lineKind === "cocktail") {
    return {
      menuDrinkId: line.menuDrinkId,
      liquorProductId: line.liquorProductId,
      ounces: line.ounces,
      isSubstitution: line.isSubstitution,
      // VOC1-F7 Slice 2: required on every recompute for a line whose
      // ounces differs from its recipe default (resolveAndPriceLine has no
      // "already on the order" bypass for this check the way override86
      // gets via resolveExistingLineOverride86; the price-quote route
      // passes this straight through on every call). Without this, adding
      // a second drink to a ticket that already carries a pour-modified
      // line would fail the WHOLE recompute batch, not just degrade one
      // line.
      pourModifierApplied: line.pourModifierApplied,
      // VOC1-F4: resent on every recompute, the identical reasoning
      // pourModifierApplied's own comment states two lines up --
      // resolveAndPriceLine has no "already on the order" bypass for this
      // either, so an omitted modifierSelections would silently reprice a
      // modified line back down to its base price.
      modifierSelections: line.modifierSelections,
      // VOC1-F5 Slice 3 gap (a) fix (independent-verifier finding, server
      // commit 4209bb6): every TicketLineState carries a real orderLineId
      // (the field is required, never optional; reconstructOrderLine's
      // every branch and addDrinkToTicket/addInventorySaleToTicket's own
      // line push all set it from the server's own response), so this line
      // is always already on the order by the time a recompute sends it.
      // The server looks the id up tenant-scoped and treats a re-price as
      // already-authorized rather than a fresh selection, so no separate
      // override86 signal is needed or sent here any more: a fresh
      // selection's own override86 (addDrinkToTicket/addInventorySaleToTicket's
      // own add-line request body) is a one-time signal that authorized the
      // add itself, never persisted onto TicketLineState or resent on a
      // later recompute.
      orderLineId: line.orderLineId,
    };
  }
  return {
    saleKind: line.lineKind,
    liquorProductId: line.liquorProductId,
    ounces: line.ounces,
    orderLineId: line.orderLineId,
    // VOC1-F4-F2 (Section 2.4): resent on every recompute, the identical
    // reasoning the cocktail branch's own modifierSelections comment
    // states above -- resolveAndPriceInventorySaleLine has no "already on
    // the order" bypass for a modifier selection either, so an omitted
    // modifierSelections would silently reprice a modified inventory-sale
    // line back down to its base price on the very next recompute (adding
    // a second line, a guest change, anything that calls recomputeQuote/
    // summarizeTab again).
    modifierSelections: line.modifierSelections,
  };
}

// ---------------------------------------------------------------------
// Order-level price quote and totals
// ---------------------------------------------------------------------

// TKTPRICE1 (DEC-360): the per-line figures this returns for a line already on
// the order are the server's STORED prices (the amount Pay charges), not a
// re-price from today's menu, and the totals are the sum of them. The browser
// adds nothing of its own.
// FLOORQUOTE4: global and monotonic, including empty-ticket calls. A late
// response must not publish totals after another quote, ticket or sign-in.
let ticketQuoteSequence = 0;

function ticketQuoteInputSignature(line: TicketLineState): string {
  return JSON.stringify([
    buildPriceQuoteLine(line),
    line.priceable,
    line.unpriceableReason,
    line.lineTotalCents,
  ]);
}

async function recomputeQuote(): Promise<void> {
  const sequence = ++ticketQuoteSequence;
  // INV19-S2-F1: `priceable === false` covers two different origins and
  // they are NOT re-quoted the same way. An INV19 pricing-conflict line
  // (unpriceableReason set, the server's own message) has every field a
  // quote needs; the server refused it on a configured tier/override
  // price that an admin can change a minute later, so it stays in the
  // batch and re-prices on every later recompute, recovering by itself
  // the moment the price is fixed. An ALPHA-07 resumed match-miss line
  // (unpriceableReason left undefined; see TicketLineState.priceable's
  // own comment) has no valid menuDrinkId/liquorProductId pair to quote
  // at all, so it stays excluded permanently: there is nothing to send.
  const priceableLines = state.lines.filter(
    (line) => line.priceable !== false || line.unpriceableReason !== undefined,
  );
  if (priceableLines.length === 0) {
    state.quote = undefined;
    renderTicketLines();
    renderTotals();
    renderTicketActions();
    return;
  }
  const session = requireSession();
  const generation = sessionGeneration;
  const orderId = state.orderId;
  const ticketLines = [...state.lines];
  const signatures = ticketLines.map(ticketQuoteInputSignature);
  const result = await priceQuote(
    session.apiBaseUrl,
    session.tenantId,
    priceableLines.map((line) => buildPriceQuoteLine(line)),
  );
  if (
    sequence !== ticketQuoteSequence ||
    generation !== sessionGeneration ||
    orderId !== state.orderId ||
    ticketLines.length !== state.lines.length ||
    !ticketLines.every(
      (line, index) =>
        state.lines[index] === line && signatures[index] === ticketQuoteInputSignature(line),
    )
  ) {
    return;
  }
  if (result.status !== 200) {
    showToast(
      `Could not price the ticket (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  state.quote = result.body;
  // INV19 Slice 2: a per-line result of `priceable: false` (a pour-size
  // override resolving negative since this line was added, DEC-139)
  // degrades that ONE line rather than the whole recompute. This reuses
  // the ALPHA-07 unpriceable mechanism exactly (TicketLineState.priceable),
  // so the line is excluded from every future recompute batch and Pay
  // stays blocked while Close does not, the identical gating a resumed
  // match-miss line already gets (renderTicketActions).
  result.body.lines.forEach((priced, index) => {
    const line = priceableLines[index];
    if (line === undefined) {
      return;
    }
    if (priced.priceable) {
      line.priceable = true;
      line.unpriceableReason = undefined;
      line.lineTotalCents = priced.lineTotalCents;
      line.publicationBinding=priced.publicationBinding;
    } else {
      line.priceable = false;
      line.unpriceableReason = priced.unpriceableReason;
    }
  });
  debugTaxRate.textContent = `${formatBasisPointsAsPercent(result.body.taxRateBasisPoints)}`;
  renderTicketLines();
  renderTotals();
  renderTicketActions();
}

// ---------------------------------------------------------------------
// ALPHA-08 delta payments (owner ruling 2026-08-21) + design/QA gate
// finding G: the live server read of what Pay would charge right now.
// Called at every point the order's uncovered set could have changed
// (enterTicket, add/remove/change-liquor, after a payment). The ring Pay
// button reads this untipped answer; checkout has its own scoped preview.
// ---------------------------------------------------------------------

// T5: existing shared monotonic ordering for ring and checkout preview reads.
//
// Six call sites invoke refreshPaymentPreview as fire-and-forget
// `void refreshPaymentPreview()`. Without ordering, overlapping reads resolve
// last-writer-wins by COMPLETION order rather than by request order and an
// older response can overwrite a newer one.
//
// That was not theoretical. Reopening a settled tab fires a preview that
// legitimately reports nothing uncovered; adding a drink fires a second
// one that reports it. With the first landing last, the rail believed
// nothing was owed on a tab carrying an unpaid drink, and because
// isLineCovered reads the same uncoveredLines, EVERY line rendered as
// "Paid" with its Prepared button disabled. Pay then read "Pay $0.00" and
// sat disabled saying "1 drink not prepared", with the one control that
// could have cleared that blocked. A hard deadlock on the floor, escapable
// only by leaving the ticket and resuming it. See
// e2e/payment-preview-race.spec.ts, which forces exactly that interleaving.
//
// Preserve the shared ordering across checkout scopes and ring mutations;
// checkout's potentially tipped answer never writes the ring preview.
let paymentPreviewSequence = 0;

/** UX73: ids of the ticket lines that existed when the read behind
 * state.paymentPreview was requested. See isLineCovered. */
let paymentPreviewKnownLineIds: ReadonlySet<string> = new Set();

/** VER72-F3: the id-and-price-inputs signature of every line the read behind
 * state.paymentPreview was asked about (ui/src/paymentPreviewStaleness.ts).
 * Set alongside paymentPreviewKnownLineIds on the untipped ring read.
 * A removal, a void or a price change makes the ticket differ from this, so the amount
 * is stale until a read for the new ticket lands. */
let paymentPreviewKnownLineSignatures: ReadonlySet<string> = new Set();

/** UX74-F1 (DEC-286): current reads of refreshPaymentPreview that returned
 * a non-200 since the last one that landed. Above zero means the amount is
 * unread, so it counts as stale whatever the line ids say. Reset when a
 * read lands and whenever the ticket is opened, closed or switched. */
let paymentPreviewFailedCount = 0;

/** UX74-F1: reads of refreshPaymentPreview currently out. Used only to
 * choose between the waiting note and the failed note (and to refuse a
 * second Try again while one is out). Not part of paymentPreviewStale(). */
let paymentPreviewReadsInFlight = 0;

function claimPaymentPreviewToken(): number {
  paymentPreviewSequence += 1;
  return paymentPreviewSequence;
}

/** True while no later read has been started since this token was claimed
 * AND the ticket is still the one it was claimed for. The order check
 * matters on its own: a response in flight when the bartender leaves a
 * ticket must never repopulate the rail for a different one. */
function paymentPreviewTokenStillCurrent(token: number, orderId: string): boolean {
  return token === paymentPreviewSequence && state.orderId === orderId;
}

async function refreshPaymentPreview(): Promise<void> {
  if (state.orderId === undefined) {
    await readPaymentPreview();
    return;
  }
  // UX74-F1: counted BEFORE the first await and painted at once, so a
  // Try again tap swaps the failed note for the waiting note on the same
  // frame and a second tap cannot start a second read.
  paymentPreviewReadsInFlight += 1;
  renderTicketActions();
  let failedCurrentRead: boolean;
  try {
    failedCurrentRead = await readPaymentPreview();
  } finally {
    paymentPreviewReadsInFlight -= 1;
  }
  if (failedCurrentRead) {
    paymentPreviewFailedCount += 1;
    renderTicketActions();
  }
}

/** One preview read. Returns true only when a CURRENT (not superseded)
 * read came back non-200, which the caller counts as a failure. */
async function readPaymentPreview(): Promise<boolean> {
  if (state.orderId === undefined) {
    state.paymentPreview = undefined;
    paymentPreviewFailedCount = 0;
    state.orderDiscounts = undefined;
    return false;
  }
  // Claimed BEFORE the first await, so ordering reflects the order the
  // calls were actually made in, not the order their awaits happen to
  // resume in.
  const token = claimPaymentPreviewToken();
  const tokenOrderId = state.orderId;
  const session = sessionOrUndefined();
  if (session === undefined) return false;
  const gen = sessionGeneration;
  const isCurrent = () =>
    sessionIsCurrent(gen) &&
    state.session?.token === session.token &&
    paymentPreviewTokenStillCurrent(token, tokenOrderId);
  // UX73: the lines this read is asked about, snapshotted with the token.
  const askedLineIds = new Set(state.lines.map((line) => line.orderLineId));
  const askedLineSignatures = new Set(state.lines.map(lineSignature));
  // Discounts slice A: refreshed HERE rather than at each of the dozen
  // call sites below, because every ticket mutation in this file already
  // ends by refreshing the payment preview, and the two answers have to
  // move together. A caller that added a line and refreshed only one of
  // them would leave a Discount row describing a ticket that no longer
  // exists.
  // PAYSPEED1: the discounts, the preview and the payments reads are independent of one another, so all three
  // are requested here, in the same tick, and applied below in the order they were applied in before (discounts,
  // preview, awaiting tip, payments), each behind the same isCurrent guard. Nothing is applied before its own
  // answer lands. A request not yet awaited has its rejection marked handled, so a throw in the one awaited
  // first cannot leave the others as unhandled rejections; each rejection still surfaces where it is awaited.
  const orderId = tokenOrderId;
  const discountsRead = getOrderDiscounts(session.apiBaseUrl, session.tenantId, orderId);
  const previewRead = getPaymentPreview(session.apiBaseUrl, session.tenantId, orderId, {
    tip: { type: "none" },
  });
  const paymentsRead = getPayments(session.apiBaseUrl, session.tenantId, orderId);
  for (const read of [discountsRead, previewRead, paymentsRead]) read.catch(() => undefined);
  await refreshOrderDiscounts(isCurrent, discountsRead);
  if (!isCurrent()) return false;
  const result = await previewRead;
  if (result.status !== 200) {
    // Left in place rather than cleared: a transient read failure should
    // not suddenly hide every line's edit control or zero out the Pay
    // button, both of which read state.paymentPreview below. UX74-F1
    // (DEC-286): the failure is now COUNTED, so the amount reads as
    // stale (Pay shows no figure) and the rail offers Try again instead of
    // looking like a read still in flight. A superseded read's failure is
    // ignored, same rule as a superseded success: the newer read owns the
    // answer. The pay-confirm sheet's own read (loadPayWarning, a separate
    // endpoint) keeps its own fail-CLOSED handling.
    return isCurrent();
  }
  // T5: a superseded response is DISCARDED rather than applied. Dropping
  // it is always safe, because whichever read superseded this one writes
  // its own fresher answer.
  if (!isCurrent()) {
    return false;
  }
  state.paymentPreview = result.body;
  paymentPreviewFailedCount = 0;
  paymentPreviewKnownLineIds = askedLineIds;
  paymentPreviewKnownLineSignatures = askedLineSignatures;
  // RCPT1-S3: only a location that takes tips on the receipt can have an Awaiting tip ticket, so only it pays for the extra read.
  if (result.body.tipMode === "receipt") {
    await loadAwaitingTip(orderId, isCurrent);
    if (!isCurrent()) return false;
  } else if (state.awaitingTip !== undefined) {
    state.awaitingTip = undefined;
    refreshTileDisabledState();
  }
  // CORE4: a line a captured payment covers was prepared by that payment when the drink had not been, so the rail must
  // not keep reading Not Prepared for it. Covered implies prepared on the server; this only catches the screen up.
  for (const line of state.lines) {
    if (line.preparedState !== "prepared" && isLineCovered(line.orderLineId)) {
      line.preparedState = "prepared";
    }
  }
  // Amendment 2026-08-22 (fifth), item 3 / UX62: refreshCapturedPaymentTimes
  // now also establishes state.capturedPaymentsSummary (the ticket rail's
  // persistent Paid row, docs/build-briefs/UX62-paid-ticket-totals.md),
  // which must be known on every ticket regardless of guest count, so this
  // is unconditional as of UX62 -- it used to be skipped entirely on a
  // no-named-guests ticket (section-ordering has nothing to do there), but
  // a single-guest or guest-free ticket needs the Paid row too.
  await refreshCapturedPaymentTimes(orderId, isCurrent, paymentsRead);
  if (!isCurrent()) return false;
  renderTicketLines();
  renderTicketActions();
  // UX62: state.capturedPaymentsSummary just changed above; renderTotals
  // is what actually paints #paid-row from it.
  renderTotals();
  return false;
}

// Amendment 2026-08-22 (fifth), item 3: "Order within the paid group by
// payment time." getPayments' own guestId field (src/domain/payments.ts's
// PaymentWithTipSplits.guestId) is null for a payment that covered more
// than one guest's lines at once (most commonly a whole-ticket payment
// finishing off several guests' remaining drinks together, or an
// Unassigned-only payment), so this cannot always attribute a captured
// payment to exactly one section by guestId alone. What it CAN do
// precisely: track the latest Captured payment time per guestId (a
// "pay my drinks" payment always carries exactly one guestId), plus the
// latest Captured time across the whole order as a fallback for any
// section (Unassigned, most commonly) that a guestId lookup cannot place.
// Display-only ordering, never a money computation; a failed read leaves
// the previous answer in place rather than reshuffling the rail on a
// transient error, same reasoning as refreshPaymentPreview's own failure
// handling just above.
//
// UX62 (docs/build-briefs/UX62-paid-ticket-totals.md): this function also
// establishes state.capturedPaymentsSummary, the ticket rail's persistent
// Paid row, from the SAME getPayments read this function already makes --
// one fetch, two derived views, rather than a second round trip. Unlike
// the section-ordering half above, the Paid summary IS a money
// computation (summarizeCapturedPayments, integer cents), and it is why
// this function's own call site in refreshPaymentPreview no longer skips
// it on a guest-free ticket: a single-guest or guest-free ticket needs the
// Paid row exactly as much as a multi-guest one does.
async function refreshCapturedPaymentTimes(
  orderId: string,
  isCurrent: () => boolean = () => true,
  /** PAYSPEED1: a payments read the caller already started, so it runs alongside its other reads. */
  startedRead?: ReturnType<typeof getPayments>,
): Promise<void> {
  const session = sessionOrUndefined();
  if (session === undefined || state.orderId !== orderId || !isCurrent()) return;
  const gen = sessionGeneration;
  const result = await (startedRead ?? getPayments(session.apiBaseUrl, session.tenantId, orderId));
  if (
    !sessionIsCurrent(gen) ||
    state.session?.token !== session.token ||
    state.orderId !== orderId ||
    !isCurrent() ||
    result.status !== 200
  ) {
    return;
  }
  const byGuest = new Map<string, string>();
  let latest: string | undefined;
  for (const payment of result.body.payments) {
    if (payment.paymentAttempt.status !== "Captured") {
      continue;
    }
    const at = payment.paymentAttempt.updated_at;
    if (latest === undefined || at > latest) {
      latest = at;
    }
    if (payment.guestId !== null) {
      const existing = byGuest.get(payment.guestId);
      if (existing === undefined || at > existing) {
        byGuest.set(payment.guestId, at);
      }
    }
  }
  state.capturedPaymentTimeByGuestId = byGuest;
  // CORE3: every line any non-failed refund on this ticket returned.
  state.refundedLineIds = new Set(
    result.body.payments.flatMap((payment) => payment.refundedOrderLineIds),
  );
  state.latestCapturedPaymentTime = latest;
  state.ticketRefundable = result.body.payments.some((payment) => {
    if (payment.paymentAttempt.status !== "Captured") return false;
    const chargedCents = parseDollarsToCentsExact(payment.paymentAttempt.amount, "captured payment amount");
    return chargedCents - (payment.paymentAttempt.tip_cents ?? 0) - payment.refundedCents > 0;
  });
  renderTicketRefundButton();
  state.capturedCharges = result.body.payments
    .filter((payment) => payment.paymentAttempt.status === "Captured")
    .map((payment) => ({
      label:
        payment.paymentAttempt.tender === "cash"
          ? "Charged to cash"
          : `Charged to ${closedTenderLabel(payment).replace(" ****", " ending ")}`,
      at: payment.paymentAttempt.updated_at,
      amountCents: parseDollarsToCentsExact(payment.paymentAttempt.amount, "captured payment amount"),
    }));
  state.capturedPaymentsSummary = summarizeCapturedPayments(
    result.body.payments.map((payment) => ({
      status: payment.paymentAttempt.status,
      amount: payment.paymentAttempt.amount,
      tipCents: payment.paymentAttempt.tip_cents,
      // UX62-F1 (docs/build-briefs/UX62-F1-refunded-line.md): already on
      // this same getPayments response, server-computed per payment.
      refundedCents: payment.refundedCents,
    })),
  );
}

/** Amendment 2026-08-22 (fifth), item 3. undefined guestId means the
 * Unassigned section. See refreshCapturedPaymentTimes's own header
 * comment for the fallback this implements. */
function sectionPaidAt(guestId: string | undefined): string | undefined {
  if (guestId !== undefined) {
    const specific = state.capturedPaymentTimeByGuestId.get(guestId);
    if (specific !== undefined) {
      return specific;
    }
  }
  return state.latestCapturedPaymentTime;
}

// ---------------------------------------------------------------------
// ALPHA-12: the pay-confirm sheet's own, scope-aware preview. See
// state.payConfirmPreview's own doc comment for why this is kept separate
// from refreshPaymentPreview/state.paymentPreview.
// ---------------------------------------------------------------------

// VER33-F1 gate-finding fix. Return signal for the two reads
// openPayConfirmSheet depends on before it ever shows the sheet
// (refreshPayConfirmPreview here, loadPayWarning below): "pinRequired"
// means handlePinChangeRequiredOnPayClose already opened the mandatory
// change-PIN pad as a side effect (hideAllScreens, a full screen
// replacement -- see that function's own header comment), so the caller
// must stop immediately rather than going on to show the Pay confirm
// sheet underneath/after it.
type PayConfirmReadOutcome = "ok" | "failed" | "pinRequired";

/** SPLIT1: the server's refusal of the last scope preview (an amount over the balance names the live balance in
 * `remainingCents`), kept so openPayConfirmSheet can say why a By amount sheet did not open. */
let lastPayConfirmPreviewRefusal:
  { error?: string; message?: string; remainingCents?: number } | undefined;

/** PAYFIX1. Which tip choice the figures in state.payConfirmPreview were read for, and whether the latest read of them failed.
 * The sheet shows a tip as chosen, and lets Confirm go, only while this matches state.selectedTip. */
let payConfirmPreviewTipKey: string | undefined;
let payConfirmPreviewReadFailed = false;

function tipSelectionKey(tip: TipSelectionInput | undefined): string {
  if (tip === undefined) return "unset";
  if (tip.type === "preset") return `preset:${tip.basisPoints}`;
  if (tip.type === "custom") return `custom:${tip.amountCents}`;
  return "none";
}

/** PAYFIX1. "synced": the tip and total on the sheet are the server's answer for the tip now chosen. "pending": that answer is on
 * its way. "failed": the read for it failed. A sheet with no tip chosen has nothing to be out of step with. */
function payTipSyncState(): "synced" | "pending" | "failed" {
  if (state.selectedTip === undefined) return "synced";
  if (payConfirmPreviewTipKey === tipSelectionKey(state.selectedTip)) return "synced";
  return payConfirmPreviewReadFailed ? "failed" : "pending";
}

async function refreshPayConfirmPreview(
  isCurrent: () => boolean = () => true,
): Promise<PayConfirmReadOutcome> {
  if (!isCurrent()) return "ok";
  resetReaderCartDisplay();
  const scope = state.payScope ?? { type: "whole" as const };
  if (state.orderId === undefined) {
    state.payConfirmPreview = undefined;
    payConfirmPreviewTipKey = undefined;
    return "ok";
  }
  const requestedTipKey = tipSelectionKey(state.selectedTip);
  // Keep the existing shared ordering guard for checkout and ring reads.
  // Claim before any await so a newer scope or tip read supersedes this one.
  // Only checkout state receives these potentially tipped figures.
  const token = claimPaymentPreviewToken();
  const tokenOrderId = state.orderId;
  const session = requireSession();
  if (scope.type === "share") {
    const result = await getSharePaymentPreview(
      session.apiBaseUrl,
      session.tenantId,
      scope.shareId,
      {
        tip: state.selectedTip,
      },
    );
    if (!isCurrent()) return "ok";
    if (result.status !== 200) {
      // VER33-F1 gate-finding fix: a mustChangePin session gets this
      // refusal here too (every route behind requirePermission does), so
      // it is checked before the generic "left in place" fallback below,
      // exactly like loadPayWarning's own check.
      if (handlePinChangeRequiredOnPayClose(result.body.error)) {
        return "pinRequired";
      }
      if (paymentPreviewTokenStillCurrent(token, tokenOrderId)) {
        payConfirmPreviewReadFailed = true;
      }
      return "failed"; // left in place, same "transient read failure" reasoning as refreshPaymentPreview
    }
    // T5 (finding 1): state.payConfirmPreview is this function's OWN
    // field, and it drives every dollar figure on the pay-confirm sheet
    // (updatePayConfirmSummary, renderTipPresets), so it needs the same
    // discard-if-superseded guard as state.paymentPreview below, not just
    // a share of it. Without this, tapping one tip preset then another
    // lets the first preset's response overwrite the second's if it
    // happens to resolve later, showing a stale tip/total on screen.
    if (!paymentPreviewTokenStillCurrent(token, tokenOrderId)) {
      return "ok";
    }
    // Adapted into PaymentPreviewDto's shape so updatePayConfirmSummary/
    // renderTipPresets (design/QA gate finding G: server-computed only)
    // work unchanged for every scope. uncoveredLines stays empty on
    // purpose: a share is not a claim on any particular line (see
    // src/domain/splitPayments.ts's own module header), so the "Charging
    // now" line list naturally hides itself (its own hidden check is
    // "uncoveredLines.length === 0"); the share's own "Share X of N"
    // context renders separately, in updatePayConfirmScope.
    state.payConfirmPreview = {
      uncoveredLines: [],
      ...(result.body.cartPauseEnabled === undefined ? {} : { cartPauseEnabled: result.body.cartPauseEnabled }),
      subtotalCents: result.body.subtotalCents,
      // Discounts slice A: a SHARE carries no discount row of its own.
      // The whole ticket's discount was already taken before the plan
      // snapshotted its totals and divided them N ways, so this share's
      // subtotal IS the discounted figure and there is nothing further to
      // subtract from it. Zero here is a statement, not a placeholder:
      // showing a Discount row on a share would double-count what the
      // plan already applied.
      subtotalBeforeDiscountCents: result.body.subtotalCents,
      discountCents: 0,
      taxCents: result.body.taxCents,
      tipCents: result.body.tipCents,
      totalCents: result.body.totalCents,
      presetOptions: result.body.presetOptions,
      provisional: true,
      ...(result.body.testEnvironment === undefined
        ? {}
        : { testEnvironment: result.body.testEnvironment }),
      // TERM10: a share is a card payment on the reader like any other, so the same flag decides who asks for the tip.
      ...(result.body.readerTipOnReader === undefined
        ? {}
        : { readerTipOnReader: result.body.readerTipOnReader }),
    };
    payConfirmPreviewTipKey = requestedTipKey;
    payConfirmPreviewReadFailed = false;
    return "ok";
  }
  const result = await getPaymentPreview(session.apiBaseUrl, session.tenantId, state.orderId, {
    tip: state.selectedTip,
    guestId: scope.type === "guest" ? scope.guestId : undefined,
    // SPLIT1: the No guest row and By amount are previewed by the server, which decides every figure.
    unassigned: scope.type === "unassigned" ? true : undefined,
    byAmount: scope.type === "amount" ? scope.selection : undefined,
  });
  if (!isCurrent()) return "ok";
  if (result.status !== 200) {
    // VER33-F1 gate-finding fix: same check as the share branch above.
    if (handlePinChangeRequiredOnPayClose(result.body.error)) {
      return "pinRequired";
    }
    lastPayConfirmPreviewRefusal = result.body;
    if (paymentPreviewTokenStillCurrent(token, tokenOrderId)) {
      payConfirmPreviewReadFailed = true;
    }
    return "failed";
  }
  lastPayConfirmPreviewRefusal = undefined;
  // Keep the checkout answer only when no newer preview has superseded it.
  if (!paymentPreviewTokenStillCurrent(token, tokenOrderId)) {
    return "ok";
  }
  state.payConfirmPreview = result.body;
  payConfirmPreviewTipKey = requestedTipKey;
  payConfirmPreviewReadFailed = false;
  return "ok";
}

/** ALPHA-08 delta payments: whether a specific order line is currently
 * covered by a captured payment. Undefined state.paymentPreview (not yet
 * loaded for this ticket) defaults to "not covered," matching this
 * codebase's pre-delta-payments default (every line's edit control showed
 * until the first payment); refreshPaymentPreview is called eagerly enough
 * (enterTicket, every line edit) that this gap is brief. This default is
 * deliberately different from loadPayWarning's own fail-CLOSED default: an
 * edit-button-visibility question is not a money-safety gate the way "was
 * this tab already paid" is, and the server independently re-enforces the
 * real block on every remove/change-liquor call regardless of what this
 * function guesses. */
function isLineCovered(orderLineId: string): boolean {
  if (state.paymentPreview === undefined) {
    return false;
  }
  // UX73: uncoveredLines lists only what is NOT covered, so a line the
  // preview was never asked about (added after it was requested) looks
  // identical to a covered one. Unknown is never Paid: it stays uncovered
  // until a preview that knew the line lands.
  if (!paymentPreviewKnownLineIds.has(orderLineId)) {
    return false;
  }
  return !state.paymentPreview.uncoveredLines.some((line) => line.orderLineId === orderLineId);
}

/** UX74 and UX73-F1: true while the payment preview has not been asked
 * about every line now on the ticket (or has not loaded at all). Derived,
 * never stored. Ids only, deliberately no in-flight counter: a settled
 * ticket must not flicker between paid and open on every edit. While true,
 * the preview's amount and its "nothing uncovered" answer both describe a
 * ticket that no longer exists, so Pay shows no amount, the ticket status
 * never reads "paid", and an all-Prepared ticket says "Updating the
 * amount.". Same knowledge as isLineCovered's UX73 check. VER72-F3: a
 * removal, a void or a price change counts too (see
 * ui/src/paymentPreviewStaleness.ts). */
function paymentPreviewStale(): boolean {
  return (
    state.paymentPreview === undefined ||
    // UX74-F1: a failed read always counts as stale, even when every line
    // id is known (a failed read after a tip change or Prepared leaves the
    // old preview in place, and its amount must not stay on Pay).
    paymentPreviewFailedCount > 0 ||
    // VER72-F3: any line change (added, removed, voided, repriced) since the
    // read was asked, not only an unseen added line.
    previewLinesChanged(paymentPreviewKnownLineSignatures, state.lines)
  );
}

// ---------------------------------------------------------------------
// ALPHA-12 equal split: the order's own current in-progress plan, if any.
// ---------------------------------------------------------------------

async function loadActiveSplitPlan(): Promise<void> {
  const orderId = state.orderId;
  if (orderId === undefined) {
    state.activeSplitPlan = undefined;
    state.activeSplitPlanLoadState = "ready";
    return;
  }
  state.activeSplitPlanLoadState = "loading";
  renderTicketActions();
  const session = requireSession();
  const result = await listSplitPayments(session.apiBaseUrl, session.tenantId, orderId);
  // M15: a delayed answer belongs only to the ticket that started it. A tab
  // switch or sign-out while this request is pending must not rewrite the next
  // ticket's Pay state or route.
  if (state.orderId !== orderId) {
    return;
  }
  if (result.status !== 200) {
    // VER33-F1 gate-finding fix: this read runs on every ticket entry
    // (renderTicketActions gates Pay entirely on activeSplitPlanLoadState,
    // "failed" showing only a "Retry split check" button, see that
    // function's own M15 comment below), so a mustChangePin session hits
    // this refusal before ever reaching loadPayWarning or Pay at all.
    // Checked first, same as every other read on this path.
    if (handlePinChangeRequiredOnPayClose(result.body.error)) {
      // Left at "loading" deliberately: the mandatory PIN pad now owns
      // the screen (hideAllScreens), and returning to this ticket
      // (submitChangePinStep's "pay-ticket" arm, showRingScreen) re-enters
      // it fresh, which re-issues this same read. Marking "failed" here
      // would show a stale "Retry split check" flash underneath the pad
      // for no reason.
      return;
    }
    // M15: a failed read is not proof that no split exists. Keep any known
    // plan in memory, but fail the action closed behind the explicit retry
    // state so ordinary Pay cannot open on an unresolved answer.
    state.activeSplitPlanLoadState = "failed";
    renderTicketActions();
    return;
  }
  // Architect-gate CRITICAL correction, 2026-08-21 ("abandoned split
  // deadlock"): before this fix, this check only excluded a COMPLETED
  // plan, the identical mistake the server-side checks in payments.ts and
  // splitPayments.ts originally made before this same gate corrected them
  // (see those files' own "cancelled_at IS NULL" comments). Caught live,
  // in the browser, not by an automated test: cancelling a split left the
  // rail permanently stuck on "Resume split" naming a plan the server had
  // already marked cancelled, because this client-side read still found
  // that same plan (completed_at null, cancelled_at now set but never
  // checked) and treated it as active.
  const active = result.body.plans.find(
    (p) => p.plan.completed_at === null && p.plan.cancelled_at === null,
  );
  state.activeSplitPlan =
    active === undefined ? undefined : { plan: active.plan, shares: active.shares };
  state.activeSplitPlanLoadState = "ready";
  renderTicketActions();
}

/** ALPHA-12: the three pay modes are only worth a picker screen when more
 * than one is actually meaningful ("modes hidden entirely on single-guest
 * single-payer tickets to keep the common case one-tap"). "Pay my drinks"
 * needs two or more guests; equal split needs more than one uncovered
 * drink (the owner's own wording: "with more than one drink the ticket
 * can split EQUALLY"). Neither true means the ordinary one-tap Pay flow,
 * byte for byte the pre-ALPHA-12 behavior. */
// UX25 / DEC-167: the picker fires only when it is genuinely a choice
// between "pay the whole ticket" and "pay my drinks" -- two or more named
// guests. A ticket with 0 or 1 guests, however many drinks it carries, goes
// straight to the whole-ticket confirm sheet, which carries its own Split
// control (see #pay-confirm-split-button and updatePayConfirmSplitButton
// below) reaching the equal-split flow with no guest naming required.
function payModesAvailable(): boolean {
  return state.guests.length >= 2;
}

// ---------------------------------------------------------------------
// ALPHA-12: reusable choice sheet. See index.html's own #choice-scrim
// comment for the four callers this one component serves.
// ---------------------------------------------------------------------

interface ChoiceOption {
  id: string;
  label: string;
  sublabel?: string;
  detailLines?: string[];
  /** True for a non-interactive row (an already-collected split share):
   * rendered as a plain, distinctly-styled div, never a <button>, so it
   * can never be mistaken for a still-tappable option. */
  disabled?: boolean;
  testid?: string;
  /** Architect-gate CRITICAL correction ("abandoned split deadlock"),
   * 2026-08-21: "danger" marks a genuinely different kind of row (the
   * split-progress sheet's own "Cancel split") from an ordinary pick,
   * distinguished by more than color alone (see .choice-option-danger's
   * own comment in styles.css), matching this codebase's own touch-only
   * "never color alone" rule elsewhere (design/README.md). */
  variant?: "danger" | "primary";
}

/** GATE4 follow-up (owner rejection of the GATE4 visual regression): a
 * plain, non-interactive fact row for the informational-body mode
 * (`infoRows` below) -- never rendered as `.choice-option` (tappable) or
 * `.choice-option-done` (already acted on), both of which read as "you
 * could tap this" or "this was tapped" and neither of which is true of a
 * fact like "Lines: every drink moves". */
interface ChoiceInfoRow {
  label: string;
  detail: string;
  testid?: string;
}

/** GATE4 follow-up: the footer's own paired Cancel/primary-action row
 * (`primaryAction` below), the same side-by-side shape
 * `.admin-confirm-actions` already established for admin's own
 * destructive-confirm sheet (`#admin-confirm-scrim`), reused here by
 * class rather than invented fresh. Picking it calls `onPick(id)`, the
 * identical contract an ordinary `options` row's click already has. */
interface ChoicePrimaryAction {
  id: string;
  label: string;
  variant?: "danger";
  testid?: string;
}

let choiceSheetOnCancel: (() => void) | undefined;
let choicePrimaryActionOnPick: (() => void) | undefined;

function openChoiceSheet(params: {
  title: string;
  subtitle?: string;
  note?: string;
  /** DSC36 (DEC-343): a red line above the list saying why the last action was
   * refused. The sheet stays open under it so another choice can be made. */
  errorNote?: string;
  /** A tappable/already-done option list. Mutually exclusive with
   * `infoRows`: pass exactly one of the two per call. */
  options?: ChoiceOption[];
  /** GATE4 follow-up: the informational-body mode. Renders each row
   * neutrally in `#choice-list` instead of `options`, and pairs with
   * `primaryAction` below for the footer (TM1's own restored shape:
   * subtitle, neutral detail rows, an info note, then Cancel | the real
   * action, side by side, matching the owner-approved layout). */
  infoRows?: ChoiceInfoRow[];
  /** Shown after the rows, before the footer, in a distinct info tone
   * (`.choice-info-note`) -- never `note` above, which existing callers
   * already use for a differently-toned instructional note BEFORE the
   * list. */
  infoNote?: string;
  cancelLabel?: string;
  /** GATE4 follow-up: when present, the footer shows this button beside
   * Cancel instead of routing the "real" action through one more
   * `options` row. */
  primaryAction?: ChoicePrimaryAction;
  /** UX14 (DEC-314): 2 lays the options out in two columns, left to right
   * then down, and puts the subtitle on the title's own row. Used by the
   * void reasons; every other caller keeps the one-column sheet. */
  columns?: 2;
  onPick: (id: string) => void;
  onCancel?: () => void;
}): void {
  choiceSheetEl.classList.toggle("choice-sheet-two-col", params.columns === 2);
  // HIGFIX1: the two-column sheet (void reasons) is a floor sheet and takes the one floor sheet kit; the shared scrim keeps its
  // other users (business day refusal, required reason, amount pad) on their own placement.
  choiceScrim.classList.toggle("scrim-top", params.columns === 2);
  choiceScrim.classList.toggle("scrim-floor", params.columns === 2);
  choiceTitle.textContent = params.title;
  choiceSubtitle.hidden = params.subtitle === undefined;
  choiceSubtitle.textContent = params.subtitle ?? "";
  choiceNote.hidden = params.note === undefined;
  choiceNote.textContent = params.note ?? "";
  choiceError.hidden = params.errorNote === undefined;
  choiceError.textContent = params.errorNote ?? "";
  choiceList.innerHTML = "";
  if (params.infoRows !== undefined) {
    for (const row of params.infoRows) {
      const rowEl = document.createElement("div");
      rowEl.className = "choice-detail-row";
      rowEl.dataset["testid"] = row.testid ?? "choice-detail-row";
      const labelEl = document.createElement("span");
      labelEl.className = "choice-detail-row-label";
      labelEl.textContent = row.label;
      const detailEl = document.createElement("span");
      detailEl.className = "choice-detail-row-detail";
      detailEl.textContent = row.detail;
      rowEl.append(labelEl, detailEl);
      choiceList.append(rowEl);
    }
  } else {
    for (const option of params.options ?? []) {
      if (option.disabled === true) {
        const row = document.createElement("div");
        row.className = "choice-option-done";
        row.dataset["testid"] = option.testid ?? "choice-option-done";
        const labelEl = document.createElement("span");
        labelEl.className = "choice-option-label";
        labelEl.textContent = option.label;
        row.append(labelEl);
        if (option.sublabel !== undefined) {
          const sublabelEl = document.createElement("span");
          sublabelEl.className = "choice-option-sublabel";
          sublabelEl.textContent = option.sublabel;
          row.append(sublabelEl);
        }
        for (const detail of option.detailLines ?? []) {
          const detailEl = document.createElement("span");
          detailEl.className = "choice-option-sublabel";
          detailEl.textContent = detail;
          row.append(detailEl);
        }
        choiceList.append(row);
        continue;
      }
      const button = document.createElement("button");
      button.type = "button";
      button.className =
        option.variant === "danger"
          ? "choice-option choice-option-danger"
          : option.variant === "primary"
            ? "choice-option choice-option-primary"
            : "choice-option";
      button.dataset["testid"] = option.testid ?? "choice-option";
      const labelEl = document.createElement("span");
      labelEl.className = "choice-option-label";
      labelEl.textContent = option.label;
      button.append(labelEl);
      if (option.sublabel !== undefined) {
        const sublabelEl = document.createElement("span");
        sublabelEl.className = "choice-option-sublabel";
        sublabelEl.textContent = option.sublabel;
        button.append(sublabelEl);
      }
      for (const detail of option.detailLines ?? []) {
        const detailEl = document.createElement("span");
        detailEl.className = "choice-option-sublabel";
        detailEl.textContent = detail;
        button.append(detailEl);
      }
      button.addEventListener("click", () => {
        closeChoiceSheet();
        params.onPick(option.id);
      });
      choiceList.append(button);
    }
  }
  choiceInfoNote.hidden = params.infoNote === undefined;
  choiceInfoNote.textContent = params.infoNote ?? "";
  choiceCancelButton.textContent = params.cancelLabel ?? "Cancel";
  choiceActionsCancelButton.textContent = params.cancelLabel ?? "Cancel";
  if (params.primaryAction !== undefined) {
    const action = params.primaryAction;
    choicePrimaryActionButton.textContent = action.label;
    choicePrimaryActionButton.className =
      action.variant === "danger" ? "btn-danger" : "btn-primary";
    choicePrimaryActionButton.dataset["testid"] = action.testid ?? "choice-primary-action";
    choicePrimaryActionOnPick = () => params.onPick(action.id);
    choiceFooterDefault.hidden = true;
    choiceFooterActions.hidden = false;
  } else {
    choicePrimaryActionOnPick = undefined;
    choiceFooterDefault.hidden = false;
    choiceFooterActions.hidden = true;
  }
  choiceSheetOnCancel = params.onCancel;
  reasonSheetEl.hidden = true;
  businessDaySheetEl.hidden = true;
  amountPadSheetEl.hidden = true;
  choiceScrim.classList.remove("bd-scrim-centered");
  choiceSheetEl.hidden = false;
  choiceScrim.hidden = false;
}

function closeChoiceSheet(): void {
  choiceScrim.hidden = true;
  choiceScrim.classList.remove("scrim-top", "scrim-floor");
  choiceError.hidden = true;
  choiceError.textContent = "";
  // CL6-F1-F3: the business day refusal sheet shares this scrim too, and
  // centres it while shown; every way the scrim closes leaves both reset.
  businessDaySheetEl.hidden = true;
  amountPadSheetEl.hidden = true;
  choiceScrim.classList.remove("bd-scrim-centered");
  // AU14-F4 / DEC-276: the required-reason sheet shares this scrim, so every
  // way the scrim closes (Escape, a tap outside, sign-out) leaves it reset.
  reasonSheetEl.hidden = true;
  reasonSheetState = undefined;
  const onCancel = choiceSheetOnCancel;
  choiceSheetOnCancel = undefined;
  onCancel?.();
}

// ---------------------------------------------------------------------
// AU14-F4 / DEC-276 (docs/build-briefs/AU14-F4-reopen-reason.md): the
// required-reason sheet. Pick one reason, then confirm. Nothing is
// preselected, there is no "Other", no free text and no skip, and the
// primary button stays blocked (with an amber line saying why) until a tile
// is picked. TOUCH ONLY: tiles are 56px high, nothing needs a keyboard or a
// hover. The sheet only reflects the rule: the server refuses a missing,
// unknown or retired reason whatever this screen does.
// ---------------------------------------------------------------------

interface ReasonSheetParams {
  title: string;
  subtitle?: string;
  /** The sunken card on top saying what is about to change. Omitted by the 86
   * sheet, whose title already names the item. */
  contextTitle?: string;
  contextDetail?: string;
  /** "Closed to Open": the word after the arrow is drawn bold. */
  contextFrom?: string;
  contextTo?: string;
  heading: string;
  reasons: readonly { id: string; label: string }[];
  /** What the primary button says once a reason is picked. */
  confirmLabel: string;
  /** The quiet note shown in place of the amber line once a tile is picked. Empty means none. */
  pickedNote: string;
  /** "plain" (grey, the default) or "info" (blue, for "needs a manager's approval"). */
  pickedNoteTone?: "plain" | "info";
  /** AU14-F15: a first action above the reasons (the 86 sheet's "Clear 86"),
   * which closes the sheet and runs, with its own one-line note beneath it. */
  extraAction?: { label: string; note: string; testid: string; onPick: () => void };
  onConfirm: (reason: { id: string; label: string }) => void;
  /** Cancel, the close X, Escape, a tap outside and sign-out. Not called on Confirm. */
  onCancel?: () => void;
}

let reasonSheetState: { pickedId: string | undefined } | undefined;

function renderReasonSheetPick(
  reasons: ReasonSheetParams["reasons"],
  pickedId: string | undefined,
): void {
  const picked = pickedId !== undefined;
  reasonStateEl.textContent = picked ? "" : "Pick one";
  reasonStateEl.classList.toggle("is-picked", picked);
  reasonBlockedEl.hidden = picked;
  // A picked note with no words (the direct path) stays hidden rather than showing an empty box.
  reasonNoteEl.hidden = !picked || (reasonNoteEl.textContent ?? "") === "";
  reasonConfirmButton.disabled = !picked;
  for (const tile of reasonTilesEl.querySelectorAll<HTMLButtonElement>("[data-reason-id]")) {
    const on = tile.dataset["reasonId"] === pickedId;
    tile.classList.toggle("is-on", on);
    tile.setAttribute("aria-pressed", on ? "true" : "false");
  }
  void reasons;
}

function openReasonSheet(params: ReasonSheetParams): void {
  reasonTitleEl.textContent = params.title;
  reasonSubtitleEl.hidden = params.subtitle === undefined;
  reasonSubtitleEl.textContent = params.subtitle ?? "";
  reasonContextEl.hidden = params.contextTitle === undefined;
  reasonContextTitleEl.textContent = params.contextTitle ?? "";
  reasonContextDetailEl.hidden = params.contextDetail === undefined;
  reasonContextDetailEl.textContent = params.contextDetail ?? "";
  reasonContextChangeEl.textContent = "";
  if (params.contextFrom !== undefined && params.contextTo !== undefined) {
    const from = document.createElement("span");
    from.textContent = `${params.contextFrom} \u2192 `;
    const to = document.createElement("strong");
    to.textContent = params.contextTo;
    reasonContextChangeEl.append(from, to);
  }
  const extra = params.extraAction;
  reasonExtraEl.hidden = extra === undefined;
  if (extra !== undefined) {
    reasonExtraActionButton.textContent = extra.label;
    reasonExtraActionButton.dataset["testid"] = extra.testid;
    reasonExtraNoteEl.textContent = extra.note;
    reasonExtraActionButton.onclick = () => {
      // Not a dismissal and not a confirm: drop the cancel callback, close, run.
      choiceSheetOnCancel = undefined;
      closeChoiceSheet();
      extra.onPick();
    };
  }
  reasonHeadingEl.textContent = params.heading;
  reasonNoteEl.textContent = params.pickedNote;
  reasonNoteEl.classList.toggle("po-note-info", params.pickedNoteTone === "info");
  reasonNoteEl.classList.toggle("po-note-plain", params.pickedNoteTone !== "info");
  reasonConfirmButton.textContent = params.confirmLabel;
  reasonTilesEl.innerHTML = "";
  reasonSheetState = { pickedId: undefined };
  for (const reason of params.reasons) {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = "po-reason";
    tile.dataset["testid"] = "reason-tile";
    tile.dataset["reasonId"] = reason.id;
    tile.setAttribute("aria-pressed", "false");
    tile.textContent = reason.label;
    tile.addEventListener("click", () => {
      if (reasonSheetState === undefined) {
        return;
      }
      reasonSheetState.pickedId = reason.id;
      renderReasonSheetPick(params.reasons, reason.id);
    });
    reasonTilesEl.append(tile);
  }
  renderReasonSheetPick(params.reasons, undefined);
  reasonConfirmButton.onclick = () => {
    const pickedId = reasonSheetState?.pickedId;
    const picked = params.reasons.find((reason) => reason.id === pickedId);
    if (picked === undefined) {
      return;
    }
    // Confirming is not a dismissal: drop the cancel callback before the
    // shared close runs it.
    choiceSheetOnCancel = undefined;
    closeChoiceSheet();
    params.onConfirm(picked);
  };
  choiceSheetOnCancel = params.onCancel;
  choiceSheetEl.hidden = true;
  businessDaySheetEl.hidden = true;
  amountPadSheetEl.hidden = true;
  choiceScrim.classList.remove("bd-scrim-centered");
  reasonSheetEl.hidden = false;
  choiceScrim.hidden = false;
}

// ---------------------------------------------------------------------
// CL6-F1-F3 (docs/build-briefs/CL6-F1-F3-timezone-required.md; DEC-281,
// DEC-286; design/mockups/cl6-f1-f3-timezone-required-floor.html): a live card
// payment the server refused with 409 business_day_not_confirmed. The refusal
// is raised before any provider call, so nothing was charged and the ticket is
// unchanged; the sheet says so first, then who can fix it. The wording lives in
// ui/src/businessDayConfirm.ts. "Open Settings" is offered by PERMISSION
// (admin.location_settings.manage, the right the confirm save needs), never by
// a role name. OK dismisses to #pay-status, which keeps the reason.
// ---------------------------------------------------------------------

/** True when `errorCode` is the live-money gate's refusal and this opened the sheet. */
function maybeShowBusinessDaySheet(errorCode: string | undefined): boolean {
  if (errorCode !== "business_day_not_confirmed") {
    return false;
  }
  const activeSession = state.session;
  const canOpenSettings =
    activeSession !== undefined && hasPermission(activeSession, "admin.location_settings.manage");
  const copy = businessDaySheetCopy(canOpenSettings);
  businessDaySheetTitleEl.textContent = copy.title;
  businessDaySheetBodyEl.textContent = copy.body;
  businessDaySheetSafeEl.textContent = copy.safeLine;
  businessDaySheetNextEl.textContent = copy.nextStep;
  businessDaySheetSettingsButton.hidden = !copy.showOpenSettings;
  choiceSheetOnCancel = undefined;
  choiceSheetEl.hidden = true;
  reasonSheetEl.hidden = true;
  amountPadSheetEl.hidden = true;
  businessDaySheetEl.hidden = false;
  choiceScrim.classList.add("bd-scrim-centered");
  choiceScrim.hidden = false;
  return true;
}

function handleBusinessDaySheetSettingsClick(): void {
  closeChoiceSheet();
  showAdminScreen();
  void admin.openLocationSettings(requireSession());
}

// ---------------------------------------------------------------------
// ALPHA-12: "Add Guest", redesigned per Amendment 2026-08-22 (item 3).
// No chips row: one persistent control inside the ticket rail. Guests
// themselves render as section headers inside #ticket-lines instead (see
// renderTicketLines below), not here.
// ---------------------------------------------------------------------

function renderAddGuestControl(): void {
  addGuestRowEl.hidden = state.orderId === undefined || state.closedView !== undefined;
  // CORE5: the +1 chip repeats the newest drink (the top row, TF5), so it shows once there is one.
  const newest = state.lines[state.lines.length - 1];
  ticketRepeatButton.hidden = newest === undefined;
  if (newest !== undefined) {
    ticketRepeatButton.setAttribute("aria-label", `Add another ${newest.name}`);
  }
}

// MyTime track (track/mytime) Slice 2: server-enforced minimum, per
// src/domain/mytimeClients.ts's CLIENT_SEARCH_MIN_QUERY_LENGTH. Mirrored
// here client-side purely to avoid firing a request on every one- or
// two-character keystroke; the server is the real, unbypassable gate
// (Review 21's own "the minimum is server-enforced" finding this track
// does not weaken).
const ADD_GUEST_SEARCH_MIN_LENGTH = 3;
// Amendment 2026-08-23 (nineteenth)'s own "client-side debounce ~250ms."
const ADD_GUEST_SEARCH_DEBOUNCE_MS = 250;

/** Shows only the last 4 digits of a phone key on screen ("•••• 0142"),
 * per the task's own "masked phone tail (last 4 only on screen)" rule.
 * phoneSearchKey is already digits-only (src/domain/mytimeClients.ts's
 * normalizePhoneSearchKey); this never re-derives or displays a raw,
 * unmasked number. */
function maskPhoneTail(phoneSearchKey: string | null): string {
  if (phoneSearchKey === null || phoneSearchKey.length === 0) {
    return "";
  }
  const tail = phoneSearchKey.slice(-4);
  return `•••• ${tail}`;
}

/** Switches the sheet between its two mutually exclusive modes: "search"
 * (the default: search input, results, the free-text fallback all
 * visible) and "newClient" (the phone-ask capture panel, everything else
 * hidden so a bartender mid-capture is not distracted by search results
 * behind it). */
function setAddGuestMode(mode: "search" | "newClient"): void {
  const showSearch = mode === "search";
  addGuestSearchStateEl.hidden = !showSearch;
  addGuestFreetextDividerEl.hidden = !showSearch;
  addGuestFreetextRowEl.hidden = !showSearch;
  addGuestNewClientPanelEl.hidden = showSearch;
}

function renderAddGuestFreshness(freshness: { lastSyncedAt: string | null } | undefined): void {
  if (freshness === undefined) {
    addGuestFreshnessEl.textContent = "";
    addGuestFreshnessEl.classList.remove("stale");
    return;
  }
  if (freshness.lastSyncedAt === null) {
    // Proposal Section 4: "Client directory not yet available," never a
    // blank line that could be misread as "no matching guests" or, worse,
    // silently fabricated as fresh.
    addGuestFreshnessEl.textContent = "Client directory not yet available";
    addGuestFreshnessEl.classList.add("stale");
    return;
  }
  addGuestFreshnessEl.classList.remove("stale");
  const lastSynced = new Date(freshness.lastSyncedAt);
  addGuestFreshnessEl.textContent = Number.isNaN(lastSynced.getTime())
    ? ""
    : `Client directory as of ${lastSynced.toLocaleString()}`;
}

function buildAddGuestResultRow(result: ClientSearchResultDto): HTMLElement {
  const row = document.createElement("button");
  row.type = "button";
  row.className = "add-guest-result-row";
  row.dataset["testid"] = "add-guest-result-row";
  row.dataset["clientId"] = result.id;

  const nameBlock = document.createElement("div");
  nameBlock.className = "add-guest-result-name-block";
  const nameEl = document.createElement("div");
  nameEl.className = "add-guest-result-name";
  nameEl.textContent = result.displayName;
  nameBlock.appendChild(nameEl);
  const phoneTail = maskPhoneTail(result.phoneSearchKey);
  const shopAndPhone = [result.originLocationName?.trim(), phoneTail].filter((part) => part !== undefined && part.length > 0).join(" · ");
  if (shopAndPhone.length > 0) {
    const phoneEl = document.createElement("div");
    phoneEl.className = "add-guest-result-phone";
    phoneEl.textContent = shopAndPhone;
    nameBlock.appendChild(phoneEl);
  }
  const visitText = clientLastVisitText(result);
  if (visitText !== "") {
    const visit = document.createElement("div"); visit.className = "add-guest-result-phone"; visit.textContent = visitText; nameBlock.append(visit);
  }
  row.appendChild(nameBlock);

  const badges = document.createElement("div");
  badges.className = "add-guest-result-badges";
  if (result.possibleDuplicate === true) {
    const duplicate = document.createElement("span"); duplicate.className = "add-guest-badge"; duplicate.textContent = "Possible duplicate"; badges.append(duplicate);
  }
  if (result.membershipActive) {
    const memberBadge = document.createElement("span");
    memberBadge.className = "add-guest-badge add-guest-badge-member";
    memberBadge.dataset["testid"] = "add-guest-badge-member";
    memberBadge.textContent = "Member";
    badges.appendChild(memberBadge);
  }
  if (result.provisional) {
    const provisionalBadge = document.createElement("span");
    provisionalBadge.className = "add-guest-badge add-guest-badge-provisional";
    provisionalBadge.dataset["testid"] = "add-guest-badge-provisional";
    provisionalBadge.textContent = "New client";
    badges.appendChild(provisionalBadge);
  }
  row.appendChild(badges);

  row.addEventListener("click", () => void handleAddGuestResultTap(result.id));
  return row;
}

function renderAddGuestResults(query: string): void {
  addGuestResultsEl.replaceChildren();
  const trimmedLength = query.trim().length;

  if (trimmedLength < ADD_GUEST_SEARCH_MIN_LENGTH) {
    addGuestNewClientPromptButton.hidden = true;
    return;
  }

  if (addGuestSearchFailed) {
    // LOUNGERMAJ1 (lounger-13): unavailable is not zero. A search that could not run never says "No matching guests", and
    // "+ New client" waits for a search that answered, so a member the directory could not look up is not entered twice.
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "add-guest-results-retry";
    retry.dataset["testid"] = "add-guest-search-retry";
    retry.textContent = "Could not search. Tap to try again.";
    retry.addEventListener("click", () => void executeAddGuestSearch(addGuestSearchInput.value));
    addGuestResultsEl.appendChild(retry);
    addGuestNewClientPromptButton.hidden = true;
    return;
  }

  if (addGuestSearchResults.length === 0) {
    const empty = document.createElement("div");
    empty.className = "add-guest-results-empty";
    empty.dataset["testid"] = "add-guest-results-empty";
    empty.textContent = "No matching guests.";
    addGuestResultsEl.appendChild(empty);
    addGuestNewClientPromptButton.hidden = false;
    return;
  }

  for (const result of addGuestSearchResults) {
    addGuestResultsEl.appendChild(buildAddGuestResultRow(result));
  }
  // A real match already exists; "+ New client" stays offered anyway
  // (a search can genuinely miss the right person, e.g. two guests with
  // very similar names), but is not the default emphasis once real
  // results are showing.
  addGuestNewClientPromptButton.hidden = false;
}

/** Runs the actual server-side search (proposal Section 3: server-side
 * trigram query, never a client-side directory index) and renders the
 * result. Cancels any still-in-flight PRIOR search first (AbortController)
 * so a fast typist never has a stale response land after a fresher one,
 * per the proposal's own "cancels a superseded in-flight request"
 * recommendation. */
async function executeAddGuestSearch(query: string): Promise<void> {
  if (addGuestSearchAbortController !== undefined) {
    addGuestSearchAbortController.abort();
  }
  const controller = new AbortController();
  addGuestSearchAbortController = controller;

  const session = requireSession();
  const result = await searchClients(
    session.apiBaseUrl,
    session.tenantId,
    { query },
    controller.signal,
  );
  if (controller.signal.aborted) {
    // Superseded by a later keystroke's own search; that later call is
    // the one responsible for rendering, not this one.
    return;
  }
  if (result.status !== 200) {
    // A validation 400 (e.g. a punctuation-only query the server's
    // normalized-key minimum rejects, Review 21's own regression) reads
    // as "nothing to show yet," not an error toast: the bartender is
    // still typing, and an error toast on every partial keystroke would
    // be pure noise. A genuine network failure is the same story here
    // (result.networkError): the search sheet just shows no results
    // rather than interrupting typing with a toast.
    addGuestSearchResults = [];
    // A 400 is a query the server will not search (still typing); anything else is a search that did not answer.
    addGuestSearchFailed = result.status !== 400;
    renderAddGuestResults(query);
    return;
  }
  addGuestSearchFailed = false;
  addGuestSearchResults = result.body.results;
  renderAddGuestFreshness(result.body.freshness);
  renderAddGuestResults(query);
}

function handleAddGuestSearchInput(): void {
  if (addGuestSearchDebounceTimer !== undefined) {
    clearTimeout(addGuestSearchDebounceTimer);
  }
  const query = addGuestSearchInput.value;
  if (query.trim().length < ADD_GUEST_SEARCH_MIN_LENGTH) {
    if (addGuestSearchAbortController !== undefined) {
      addGuestSearchAbortController.abort();
    }
    addGuestSearchResults = [];
    addGuestSearchFailed = false;
    renderAddGuestResults(query);
    return;
  }
  addGuestSearchDebounceTimer = setTimeout(() => {
    void executeAddGuestSearch(query);
  }, ADD_GUEST_SEARCH_DEBOUNCE_MS);
}

/** Shared by both attach paths (tapping a search result, and saving the
 * new-client capture panel): the actual addGuest call plus the identical
 * success handling (close the sheet, add the guest to ticket state,
 * render, toast). */
async function attachClientAsGuest(
  clientRefKind: "directory" | "provisional",
  clientRefId: string,
  label: string,
): Promise<boolean> {
  if (state.orderId === undefined) {
    return false;
  }
  const session = requireSession();
  const result = await addGuest(session.apiBaseUrl, session.tenantId, state.orderId, {
    idempotencyKey: crypto.randomUUID(),
    label,
    clientRefKind,
    clientRefId,
  });
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not add this guest (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return false;
  }
  closeAddGuestSheet();
  state.guests.push(result.body.guest);
  renderTicketLines();
  showToast(`${result.body.guest.label} added.`, "success");
  return true;
}

async function handleAddGuestResultTap(clientId: string): Promise<void> {
  const result = addGuestSearchResults.find((r) => r.id === clientId);
  if (result === undefined) {
    return;
  }
  await attachClientAsGuest(
    result.provisional ? "provisional" : "directory",
    result.id,
    result.displayName,
  );
}

function handleAddGuestNewClientPromptClick(): void {
  addGuestNewClientNameInput.value = addGuestSearchInput.value.trim();
  addGuestNewClientPhoneInput.value = "";
  setAddGuestMode("newClient");
  addGuestNewClientNameInput.focus();
}

function handleAddGuestNewClientBackClick(): void {
  setAddGuestMode("search");
  addGuestSearchInput.focus();
}

async function handleAddGuestNewClientSaveClick(): Promise<void> {
  const displayName = addGuestNewClientNameInput.value.trim();
  const phone = addGuestNewClientPhoneInput.value.trim();
  if (displayName.length === 0) {
    showToast("Enter this guest's name.", "danger");
    return;
  }
  if (phone.length === 0) {
    // The phone-ask ruling: a provisional client always carries a phone
    // number (docs/decision-proposals/mytime-client-sync-proposal.md's
    // owner-approved "same-day new clients" design).
    showToast("Enter this guest's phone number.", "danger");
    return;
  }
  const session = requireSession();
  const result = await createProvisionalClient(session.apiBaseUrl, session.tenantId, {
    displayName,
    phone,
  });
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not create this client (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  // matchedExisting covers BOTH "this phone is already a real directory
  // client" (source "directory") and "this phone already has a
  // provisional record" (source "provisional"); either way the returned
  // id/displayName/source are the record to attach, never a fresh
  // duplicate (src/domain/mytimeClients.ts's createProvisionalClient own
  // dedup rule).
  await attachClientAsGuest(result.body.source, result.body.id, result.body.displayName);
}

/** UX51: keeps the free-text guest label's remaining-character count
 * visible so the 40-character limit (index.html's own maxlength, read
 * here rather than duplicated as a literal) is seen before it is hit,
 * not discovered only once typing silently stops. Switches to the fixed
 * warning pair inside the last 5 characters. */
function renderAddGuestLabelCounter(): void {
  const max = addGuestLabelInput.maxLength;
  const used = addGuestLabelInput.value.length;
  addGuestLabelCounterEl.textContent = `${used}/${max}`;
  addGuestLabelCounterEl.classList.toggle("add-guest-label-counter-near-limit", max - used <= 5);
}

function openAddGuestSheet(): void {
  addGuestLabelInput.value = "";
  addGuestSearchInput.value = "";
  addGuestSearchResults = [];
  addGuestSearchFailed = false;
  renderAddGuestResults("");
  renderAddGuestFreshness(undefined);
  renderAddGuestLabelCounter();
  setAddGuestMode("search");
  addGuestScrim.hidden = false;
  noteTicketSheetOpen("addGuest");
  addGuestSearchInput.focus();
}

function closeAddGuestSheet(): void {
  if (addGuestSearchDebounceTimer !== undefined) {
    clearTimeout(addGuestSearchDebounceTimer);
    addGuestSearchDebounceTimer = undefined;
  }
  if (addGuestSearchAbortController !== undefined) {
    addGuestSearchAbortController.abort();
    addGuestSearchAbortController = undefined;
  }
  addGuestScrim.hidden = true;
  dropDraft(TICKET_SHEET_DRAFT);
}

async function handleAddGuestSaveClick(): Promise<void> {
  const label = addGuestLabelInput.value.trim();
  if (label.length === 0) {
    showToast("Enter a name or seat label for this guest.", "danger");
    return;
  }
  if (state.orderId === undefined) {
    return;
  }
  const session = requireSession();
  const result = await addGuest(session.apiBaseUrl, session.tenantId, state.orderId, {
    idempotencyKey: crypto.randomUUID(),
    label,
  });
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not add this guest (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  closeAddGuestSheet();
  state.guests.push(result.body.guest);
  // The new guest needs its own section header in #ticket-lines the
  // instant it exists, even before it has any drinks (Amendment
  // 2026-08-22, item 3: "each added guest becomes a section header").
  renderTicketLines();
  showToast(`${result.body.guest.label} added.`, "success");
}

// ---------------------------------------------------------------------
// Ticket rendering
// ---------------------------------------------------------------------

function renderTicket(): void {
  noOrderPanel.hidden = state.orderId !== undefined;
  renderAddGuestControl();
  renderTicketPaymentsChip();
  ticketLinesEl.hidden = state.orderId === undefined;
  ticketTotalsEl.hidden = state.orderId === undefined;
  ticketActionsEl.hidden = state.orderId === undefined;
  // UX13: the header row's own identity text drops the explicit drink
  // count (design/ux13-ticket-rail-rationale.md: "a real information loss,
  // named rather than hidden, traded because the entire point of this
  // rail is that the count is now visible by looking at the list
  // itself"). Falls back to the literal "Ticket" this element's markup
  // used to carry as static text, for an open, unnamed walk-in.
  ticketSub.textContent = state.orderId === undefined ? "" : (state.orderName ?? "Walk-in");
  renderReopenStrip();
  renderTicketLines();
  renderIdVerifyRow();
  renderTotals();
  renderTicketActions();
}

// ---------------------------------------------------------------------
// ID1: the ID Verified row (PRD P0.10; design/mockups/id1/)
// ---------------------------------------------------------------------

/** What the last read of the ticket's ID Verified state was for: the order and its drinks. A different value means the
 * ticket changed (a drink added or removed can change whether it has alcohol), so the state is read again. */
let idVerifyReadKey = "";

const ID_VERIFY_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="3"/><circle cx="9" cy="11" r="2"/><path d="M6 16c.6-1.6 1.7-2.2 3-2.2s2.4.6 3 2.2M14.5 10h4M14.5 13.5h3"/></svg>';

async function refreshIdVerification(key: string): Promise<void> {
  const session = state.session;
  const orderId = state.orderId;
  if (session === undefined || orderId === undefined) {
    return;
  }
  const result = await getOrder(session.apiBaseUrl, session.tenantId, orderId);
  // Dropped when the ticket changed again, or another ticket is open now.
  if (state.orderId !== orderId || idVerifyReadKey !== key) {
    return;
  }
  if (result.status === 200 && result.body.idVerification !== undefined) {
    state.idVerification = result.body.idVerification;
    renderIdVerifyRow();
  }
}

/** The row between the drinks and the totals. Shown only when the location's switch is on AND the ticket has alcohol. Until
 * a check is recorded it is an "ID Verified" button; after, who and when with Change. The button and Change need
 * pos.ticket.id_verify (the resolved permission, never a role name) and an open ticket. It never reads or changes
 * Pay. */
function renderIdVerifyRow(): void {
  const orderId = state.orderId;
  ticketIdRowEl.replaceChildren();
  if (orderId === undefined || state.closedView !== undefined) {
    ticketIdRowEl.hidden = true;
    return;
  }
  const key = `${orderId}|${state.lines.map((line) => line.orderLineId).join(",")}`;
  if (key !== idVerifyReadKey) {
    if (!idVerifyReadKey.startsWith(`${orderId}|`)) {
      // Another ticket: never show the last one's row while this one is read.
      state.idVerification = undefined;
    }
    idVerifyReadKey = key;
    void refreshIdVerification(key);
  }
  const view = state.idVerification;
  if (view === undefined || !view.enabled || !view.hasAlcohol) {
    ticketIdRowEl.hidden = true;
    return;
  }
  const session = state.session;
  const mayRecord =
    session !== undefined &&
    hasPermission(session, "pos.ticket.id_verify") &&
    state.orderStatus !== "closed" &&
    state.orderStatus !== "canceled";
  const current = view.current;
  if (current === null && view.skip !== null) {
    // ID1-F2: a recorded skip, in the done-row shape. No Change: a check can still be added by the Verify button.
    const skipped = document.createElement("div");
    skipped.className = "id-done";
    skipped.dataset["testid"] = "ticket-id-skipped";
    const text = document.createElement("div");
    text.className = "tx";
    const title = document.createElement("b");
    title.dataset["testid"] = "ticket-id-skipped-title";
    title.textContent = `ID check skipped \u00b7 ${view.skip.reasonLabel}`;
    const who = document.createElement("span");
    who.dataset["testid"] = "ticket-id-skipped-who";
    who.textContent = `${view.skip.employeeName} \u00b7 approved by ${view.skip.approverName}`;
    text.append(title, who);
    skipped.append(text);
    ticketIdRowEl.append(skipped);
    ticketIdRowEl.hidden = false;
    return;
  }
  if (current === null) {
    if (!mayRecord) {
      ticketIdRowEl.hidden = true;
      return;
    }
    const button = document.createElement("button");
    button.type = "button";
    button.className = "id-btn";
    button.dataset["testid"] = "ticket-id-verify-button";
    button.innerHTML = ID_VERIFY_ICON;
    button.append("ID Verified");
    button.addEventListener("click", () => openIdMethodSheet(false));
    ticketIdRowEl.append(button);
    ticketIdRowEl.hidden = false;
    return;
  }
  const done = document.createElement("div");
  done.className = "id-done";
  done.dataset["testid"] = "ticket-id-verified";
  const check = document.createElement("span");
  check.className = "ck";
  check.setAttribute("aria-hidden", "true");
  check.textContent = "\u2713";
  const text = document.createElement("div");
  text.className = "tx";
  const title = document.createElement("b");
  title.dataset["testid"] = "ticket-id-verified-title";
  title.textContent = `ID Verified \u00b7 ${current.methodLabel}`;
  const when = new Date(current.verifiedAt).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const who = document.createElement("span");
  who.dataset["testid"] = "ticket-id-verified-who";
  who.textContent = `${current.employeeName} \u00b7 ${when}`;
  text.append(title, who);
  done.append(check, text);
  if (mayRecord) {
    const change = document.createElement("button");
    change.type = "button";
    change.className = "chg";
    change.dataset["testid"] = "ticket-id-change-button";
    change.textContent = "Change";
    change.addEventListener("click", () => openIdMethodSheet(true));
    done.append(change);
  }
  ticketIdRowEl.append(done);
  ticketIdRowEl.hidden = false;
}

/** The method sheet: "How was it checked?" and the methods the server lists. A pick records it. Change re-picks and
 * writes a new record beside the old one (PROVISIONAL, brief section 0). */
function openIdMethodSheet(change: boolean, thenPay = false): void {
  const view = state.idVerification;
  if (view === undefined) {
    return;
  }
  openChoiceSheet({
    title: "ID Verified",
    subtitle: "How was it checked?",
    columns: 2,
    options: view.methods.map((method) => ({
      id: method.key,
      label: method.label,
      testid: `id-method-${method.key}`,
    })),
    onPick: (methodKey) => {
      void recordIdVerification(methodKey, change, thenPay);
    },
  });
}

async function recordIdVerification(
  methodKey: string,
  change: boolean,
  thenPay = false,
): Promise<void> {
  const session = state.session;
  const orderId = state.orderId;
  const method = state.idVerification?.methods.find((candidate) => candidate.key === methodKey);
  if (session === undefined || orderId === undefined || method === undefined) {
    return;
  }
  const result = await postIdVerification(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
    method: method.key,
    change,
  });
  if (state.orderId !== orderId) {
    return;
  }
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not record the ID check (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    // The row may be stale (the switch changed, or someone else checked it): read it again.
    void refreshIdVerification(idVerifyReadKey);
    return;
  }
  state.idVerification = result.body.idVerification;
  renderIdVerifyRow();
  if (thenPay) {
    handlePayButtonClick();
  }
}

/** ID1-F2 (DEC-398; design/mockups/id1-f2/). At a Required location, Pay on an alcohol ticket with no check opens this
 * sheet: Verify now (the method sheet), or Skip (a picked reason, then a manager's PIN through the approval flow). Each
 * button shows only when the session holds its permission (resolved permissions, never a role name). The server refuses
 * Pay too; this sheet is the way through, not the protection. */
function openIdRequiredSheet(): void {
  const session = state.session;
  const view = state.idVerification;
  if (session === undefined || view === undefined) {
    return;
  }
  const options: ChoiceOption[] = [];
  if (hasPermission(session, "pos.ticket.id_verify")) {
    options.push({
      id: "verify",
      label: "Verify now",
      testid: "id-required-verify",
      variant: "primary",
    });
  }
  if (hasPermission(session, "pos.ticket.id_check_skip")) {
    options.push({ id: "skip", label: "Skip", testid: "id-required-skip" });
  }
  if (options.length === 0) {
    showToast("ID not checked. Ask a manager.", "danger");
    return;
  }
  openChoiceSheet({
    title: "ID not checked",
    options,
    onPick: (id) => {
      if (id === "verify") {
        openIdMethodSheet(false, true);
      } else {
        openIdSkipReasonSheet();
      }
    },
  });
}

function openIdSkipReasonSheet(): void {
  const view = state.idVerification;
  if (view === undefined) {
    return;
  }
  openReasonSheet({
    title: "Skip ID check",
    heading: "Reason",
    reasons: view.skipReasons.map((reason) => ({ id: reason.key, label: reason.label })),
    confirmLabel: "Ask manager",
    pickedNote: "",
    onConfirm: (picked) => {
      void skipIdCheck(picked);
    },
  });
}

async function skipIdCheck(picked: { id: string; label: string }): Promise<void> {
  const session = requireSession();
  const orderId = state.orderId;
  const reason = state.idVerification?.skipReasons.find((candidate) => candidate.key === picked.id);
  if (orderId === undefined || reason === undefined) {
    return;
  }
  // One key for the whole flow, including the retry after a manager's PIN.
  const idempotencyKey = crypto.randomUUID();
  const label = `Skip ID check: ${picked.label}`;
  const gated = await requestApproval(
    label,
    (approval) =>
      postIdCheckSkip(session.apiBaseUrl, session.tenantId, orderId, {
        idempotencyKey,
        reason: reason.key,
        ...approval,
      }),
    () => showRingScreen(),
    { pinContext: label },
  );
  if (gated.canceled) {
    return;
  }
  const result = gated.result;
  showRingScreen();
  if (state.orderId !== orderId) {
    return;
  }
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not skip the ID check (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    void refreshIdVerification(idVerifyReadKey);
    return;
  }
  state.idVerification = result.body.idVerification;
  renderIdVerifyRow();
  handlePayButtonClick();
}

/** AU14-F4 / DEC-276. The grey "Reopened" record strip at the top of the
 * ticket: who reopened it, who approved, when, and why, all resolved on the
 * server. A reopen from before the reason was required says so plainly, never
 * blank and never an invented reason. Hidden on a ticket never reopened. */
function renderReopenStrip(): void {
  const reopen = state.orderId === undefined ? null : (state.lastReopen ?? null);
  ticketReopenStripEl.innerHTML = "";
  ticketReopenStripEl.hidden = reopen === null;
  if (reopen === null) {
    return;
  }
  const tag = document.createElement("span");
  tag.className = "rec-tag";
  tag.textContent = "Reopened";
  const when = new Date(reopen.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const who = document.createElement("span");
  who.className = "rec-who";
  who.dataset["testid"] = "ticket-reopen-who";
  who.textContent =
    when +
    (reopen.byName === null ? "" : `, ${reopen.byName}`) +
    (reopen.approvedByName === null ? "" : `, approved by ${reopen.approvedByName}`);
  const why = document.createElement("span");
  why.className = reopen.reason === null ? "rec-why rec-why-none" : "rec-why";
  why.dataset["testid"] = "ticket-reopen-reason";
  why.textContent = reopen.reason ?? "No reason recorded (before this change)";
  ticketReopenStripEl.append(tag, who, why);
}

// Touch-only audit fix (ALPHA-09 release evidence, category 2): a title
// attribute is never a touch-reachable recovery path (product rule "no
// title-attribute tooltips as the sole recovery for hidden or truncated
// content", CLAUDE.md), so a truncated ticket-line detail row (the
// selected/substituted liquor name, or the "needs a manager" warning
// note) now expands to its full text on tap and collapses again on a
// second tap, the same element either way. Applied to wrapperEl (the
// .line-detail row, toggled so CSS can also relax the row's own
// align-items for the wrapped state) via a listener on textEl (the
// actual truncating child span, the natural tap target since it is what
// the bartender sees cut off).
//
// UI-batch gate fix (drink-name touch recovery): buildTicketLineRow's
// nameEl/nameTextEl reuse this same helper for the drink-name row, which
// used to be left title-only (the same category-2 gap this function was
// originally written to close, just missed on this one element). The
// wrapperEl passed in does not have to be .line-detail itself; any
// element styled to react to the shared "line-detail-expanded" class
// works, which is why nameEl's own styles.css rules mirror .line-detail's
// shape rather than reusing its class name.
//
// Deliberately a no-op when the text is not actually truncated
// (textEl.scrollWidth <= textEl.clientWidth): the compact two-row layout
// (Review 5's line-height fixes, this file's renderTicketLines header
// comment) must render identically to before when nothing needs
// recovering, not gain a permanently-expandable affordance on every
// line regardless of length.
// UX49 (docs/build-briefs/UX49-ticket-rail-name-word-truncation.md):
// extended, backward-compatibly, for a row whose collapsed text has
// already been replaced with word-boundary-truncated text (only
// refreshLineNameWordTruncation below ever sets textEl.dataset.fullText/
// truncatedText, only for .line-name-text). For such a row, live-
// remeasuring textEl.scrollWidth > textEl.clientWidth at click time would
// wrongly say "not truncated" (the word-truncated text already fits
// exactly), so `isTruncated` instead reads the wrapper's own
// "is-truncated" class, which refreshLineNameWordTruncation already set
// from the FULL text's own measurement before truncating it -- and
// expand/collapse swap the stored full/truncated strings back in, so
// expanding always shows the true original name (never the word-
// truncated stand-in) and collapsing restores the word-boundary text
// rather than falling back to plain CSS character clipping. A row with
// neither dataset key set (every .line-detail/.line-warning row, and any
// .line-name row short enough to never need truncation) takes exactly
// the same path as before this change.
function attachTruncatedTextTapToggle(
  wrapperEl: HTMLElement,
  textEl: HTMLElement,
  /** UX75: told whenever a tap opens or closes the row, so the caller can
   * carry the state across a rail redraw. */
  onToggle?: (expanded: boolean) => void,
): void {
  const expandedClass = "line-detail-expanded";
  textEl.addEventListener("click", () => {
    if (wrapperEl.classList.contains(expandedClass)) {
      wrapperEl.classList.remove(expandedClass);
      textEl.setAttribute("aria-expanded", "false");
      onToggle?.(false);
      const truncatedText = textEl.dataset["truncatedText"];
      if (truncatedText !== undefined) {
        textEl.textContent = truncatedText;
      }
      return;
    }
    const fullText = textEl.dataset["fullText"];
    const isTruncated =
      fullText !== undefined
        ? wrapperEl.classList.contains("is-truncated")
        : textEl.scrollWidth > textEl.clientWidth || textEl.scrollHeight > textEl.clientHeight + 1;
    if (isTruncated) {
      if (fullText !== undefined) {
        textEl.textContent = fullText;
      }
      wrapperEl.classList.add(expandedClass);
      textEl.setAttribute("aria-expanded", "true");
      onToggle?.(true);
    }
  });
}

// UX75 (docs/build-briefs/UX75-expanded-line-name-survives-rail-redraw.md).
// renderTicketLines rebuilds every line node, so the expanded state that
// lives on a node's class would be lost on each redraw. The ids of the
// order lines whose name the bartender has tapped open are carried here
// instead. Pruned to the lines still on the ticket on every redraw, and
// cleared by resetTicketState when the ticket changes.
const expandedLineNameIds = new Set<string>();

// Re-opens, after a redraw has measured truncation, each row whose id is
// still in expandedLineNameIds and which still needs expanding. Runs after
// refreshLineNameWordTruncation (which sets the data-full-text this reads).
function reapplyExpandedLineNames(): void {
  const live = new Set(state.lines.map((line) => line.orderLineId));
  for (const id of expandedLineNameIds) {
    if (!live.has(id)) {
      expandedLineNameIds.delete(id);
    }
  }
  ticketLinesEl.querySelectorAll<HTMLElement>(".line").forEach((row) => {
    const id = row.dataset["orderLineId"];
    if (id === undefined || !expandedLineNameIds.has(id)) {
      return;
    }
    const wrapper = row.querySelector<HTMLElement>(".line-name");
    const textEl = row.querySelector<HTMLElement>(".line-name-text");
    if (wrapper === null || textEl === null || !wrapper.classList.contains("is-truncated")) {
      return;
    }
    wrapper.classList.add("line-detail-expanded");
    textEl.setAttribute("aria-expanded", "true");
  });
}

// UX13 (docs/backlog.md; design/ux13-ticket-rail-rationale.md "Problem 2").
// attachTruncatedTextTapToggle above already gave a truncated row a
// tap-to-expand path; what it lacked was any VISIBLE sign it exists
// (cursor: pointer alone, invisible with no hover on a touchscreen).
// Appends a small, always-in-the-DOM chevron (styles.css's own
// .trunc-chevron, display:none by default) as a sibling of the truncating
// text span; refreshLineTruncationChevrons below is what actually shows
// it, once the row is genuinely measurable. Never a second interaction
// pattern: this chevron is purely visual, the click listener stays on
// textEl via attachTruncatedTextTapToggle exactly as before.
function appendTruncationChevron(wrapperEl: HTMLElement): void {
  const chevron = document.createElement("span");
  chevron.className = "trunc-chevron";
  chevron.setAttribute("aria-hidden", "true");
  chevron.textContent = "▾";
  wrapperEl.append(chevron);
}

// UX44 Section 2a (docs/build-briefs/UX44-ticket-line-name-whole-price-
// below.md, real-app redesign, DEC-198). The price moves beneath the name
// ONLY when the whole name would render with no ellipsis once the price is
// out of the way; otherwise the row stays exactly as it renders today
// (single row, name truncated beside the price). A pure CSS flex-wrap
// trigger cannot see this: it wraps against a flex child's own PREFERRED
// (max-content) width, not its truncated rendered width, so an
// unconditional flex-wrap: wrap on .line-top wrapped every long real name
// too, not only a name that would render whole, costing row height for
// nothing (the regression that clipped row 8 of the rail). Deferred to a
// call AFTER the rows are appended to the live document (renderTicketLines'
// own two return paths, below), same reasoning as refreshLineTruncationChevrons
// just below: scrollWidth and clientWidth are both 0 on a detached node.
// nameTextEl.scrollWidth is the name's real, full (un-truncated) content
// width regardless of the row's current wrap state, because .line-name-text
// keeps white-space: nowrap; overflow: hidden in both states. top.clientWidth
// does not depend on whether .line-top is currently wrapped, because
// .line-top is a child of .line-info (display: flex; flex-direction:
// column), whose default cross-axis stretch sizes .line-top's width from
// its column-flex parent, never from its own children's content -- the same
// available width the name would get if it had the row to itself.
function refreshLineNameStacking(): void {
  const tops = ticketLinesEl.querySelectorAll<HTMLElement>(".line-top");
  tops.forEach((top) => {
    const nameTextEl = top.querySelector<HTMLElement>(".line-name-text");
    const priceEl = top.querySelector<HTMLElement>(".line-price");
    if (nameTextEl === null || priceEl === null) {
      top.classList.remove("line-top-stacked");
      return;
    }
    // UX14: measured against the line's own info column, not .line-top
    // itself: .line-top is display: contents now (its name and price are
    // grid items of .line-info), whose own clientWidth is 0.
    const available = top.parentElement?.clientWidth ?? top.clientWidth;
    const wholeNameFits = nameTextEl.scrollWidth <= available;
    top.classList.toggle("line-top-stacked", wholeNameFits);
  });
}

// Measures every truncatable row currently in #ticket-lines and marks it
// "is-truncated" (styles.css: only then does its chevron show) so the
// affordance appears exactly for rows that need it, never as a permanent
// decoration. Deferred to a call AFTER the rows are appended to the live
// document (renderTicketLines' own two return paths, below): scrollWidth
// and clientWidth are both 0 on a detached node, so measuring inside
// buildTicketLineRow itself (before the row has a parent) would always
// read "not truncated". Mirrors design/mockups/ticket-rail-redesign-
// blocked.html's own inline script, which uses the identical
// scrollWidth > clientWidth test against the real rendered mockup markup.
// UX49 (docs/build-briefs/UX49-ticket-rail-name-word-truncation.md): only
// .line-detail now (the liquor-detail row, unchanged character-boundary
// ellipsis, out of this item's own scope). .line-name's own "is-truncated"
// determination moved into refreshLineNameWordTruncation below, which MUST
// run first and decide it from the FULL, untruncated name -- if this
// function still measured .line-name-text too, it would run against
// whatever refreshLineNameWordTruncation already wrote there and, once
// that is the word-truncated string (which fits its own box exactly by
// construction), wrongly conclude the row was never truncated, hiding the
// chevron.
function refreshLineTruncationChevrons(): void {
  const wrappers = ticketLinesEl.querySelectorAll<HTMLElement>(".line-detail");
  wrappers.forEach((wrapper) => {
    const textEl = wrapper.querySelector<HTMLElement>(".line-detail-text");
    if (textEl === null) {
      return;
    }
    wrapper.classList.toggle("is-truncated", textEl.scrollWidth > textEl.clientWidth);
  });
}

// UX49 (docs/build-briefs/UX49-ticket-rail-name-word-truncation.md). A
// ticket line's own drink name used to rely on plain browser
// text-overflow: ellipsis, which cuts at a character boundary with no
// idea where a word ends ("Cucumber Basil Smash" -> "Cucu..."). This
// truncates to the last WHOLE word that fits instead, using
// textTruncation.ts's pure truncateToWholeWord with a live pixel-width
// `fits` callback, the same scrollWidth/clientWidth measurement technique
// refreshLineTruncationChevrons above already uses. Must run AFTER
// refreshLineNameStacking (whose "line-top-stacked" class can change how
// much width .line-name-text actually has) and BEFORE
// refreshLineTruncationChevrons (see that function's own updated comment
// for why). Deferred to a call after the rows are appended to the live
// document for the same reason as its siblings: scrollWidth/clientWidth
// are both 0 on a detached node.
function refreshLineNameWordTruncation(): void {
  // UX114: the name wraps to two lines and the browser ends the second with an
  // ellipsis (line-clamp), so the text is never rewritten here; this only marks
  // a clamped name so its chevron shows and a tap opens the full text.
  const wrappers = ticketLinesEl.querySelectorAll<HTMLElement>(".line-name");
  wrappers.forEach((wrapper) => {
    const textEl = wrapper.querySelector<HTMLElement>(".line-name-text");
    if (textEl === null) {
      return;
    }
    if (wrapper.classList.contains("line-detail-expanded")) {
      return;
    }
    wrapper.classList.toggle("is-truncated", textEl.scrollHeight > textEl.clientHeight + 1);
  });
}

// Builds one ticket-line row. Factored out of renderTicketLines so the
// same per-line rendering (paid/substitution badges, price, prepared
// state, edit control) is shared between the flat, no-guests list and the
// per-guest grouped sections below (Amendment 2026-08-22, item 3); the
// two used to be one function before grouping existed.
function buildTicketLineRow(
  line: TicketLineState,
  /** Amendment 2026-08-22 (fifth), item 2: true only for a line rendered
   * under the "Unassigned" section (renderTicketLines' own call site),
   * with at least one guest already named on the ticket to assign it to.
   * Adds a prominent, always-visible "Assign to guest" action distinct
   * from the "..." edit-sheet control every line already offers (which
   * exposes the identical reattribution, per the item's own "offered on
   * unassigned and assigned lines alike" instruction; see
   * openLineEditSheet/openGuestPickerForLine). */
  /** CORE5: `group` holds every line this row stands for, newest first (display order), and is only
   * passed for two or more identical unprepared, unpaid, untouched drinks. `line` is then the oldest. */
  options?: { promptAssignGuest?: boolean; group?: TicketLineState[] },
): HTMLElement {
  const { row, info, top, name: nameEl, nameText: nameTextEl } = createTicketLineFrame();
  row.dataset["orderLineId"] = line.orderLineId;
  const group =
    options?.group !== undefined && options.group.length > 1 ? options.group : undefined;
  if (group !== undefined) {
    row.classList.add("line-group");
    row.dataset["groupCount"] = String(group.length);
  }
  const covered = isLineCovered(line.orderLineId);

  // Compact two-row layout, design gate fix (2026-08-20): name and
  // price share one row instead of stacking as separate lines, and the
  // liquor detail (a real inventory name, up to ~57 characters, see
  // fixtures/inventory-snapshot.json) is a single truncated row with a
  // title attribute carrying the full text, rather than being left to
  // wrap onto two or three lines. See styles.css's .ticket-lines
  // comment for the min-height this layout was retuned against.
  // UI-batch gate fix (drink-name touch recovery): this used to be a
  // single div (.line-name) carrying its own truncation and a title
  // attribute as the only recovery path, exactly the pattern the
  // liquor-detail row below (detailEl/detailTextEl) already had to fix
  // for the same product rule ("no title-attribute tooltips as the sole
  // recovery for hidden or truncated content", CLAUDE.md). Split the
  // same way: nameEl is now the flex wrapper (styles.css's .line-name,
  // unchanged position as .line-top's flex item) and nameTextEl is the
  // truncating child attachTruncatedTextTapToggle actually toggles,
  // matching detailEl/detailTextEl's own wrapperEl/textEl split below.
  nameTextEl.textContent = line.name;
  // Non-touch fallback only, same as detailTextEl/noteTextEl below; the
  // tap toggle attached just after is the real, touch-first recovery.
  nameTextEl.title = line.name;
  nameEl.append(nameTextEl);
  appendTruncationChevron(nameEl);
  attachTruncatedTextTapToggle(nameEl, nameTextEl, (expanded) => {
    if (expanded) {
      expandedLineNameIds.add(line.orderLineId);
    } else {
      expandedLineNameIds.delete(line.orderLineId);
    }
  });
  top.append(nameEl);
  if (group !== undefined) {
    // CORE5: the count of identical drinks this row stands for. Text, never colour alone.
    const countEl = document.createElement("span");
    countEl.className = "line-group-count";
    countEl.dataset["testid"] = "line-group-count";
    countEl.textContent = `x${group.length}`;
    countEl.setAttribute("aria-label", `${group.length} drinks`);
    top.append(countEl);
  }
  // ALPHA-08 delta payments (owner ruling 2026-08-21), design/QA gate
  // finding D: a covered line is marked plainly on the ticket rail itself,
  // not only inside the pay sheet, so a bartender scanning a reopened tab
  // can tell at a glance which drinks are already paid for versus which
  // ones Pay would actually charge next. Amendment 2026-08-22 (sixth),
  // item 2 replaced the separate ".line-badge-paid" pill this used to
  // render here with a single state button: the same "Paid" signal now
  // lives on the existing Prepared/Not-Prepared button below (the state-btn
  // built further down), which becomes the terminal "Paid" state once
  // `covered` is true, rather than stacking a second control beside it.
  // See that button's own comment for the three-state logic.
  // ALPHA-12 guest attribution used to render as a per-line badge here
  // ("Lines display their guest on the rail when more than one guest
  // exists", docs/product-requirements.md P0.19). Amendment 2026-08-22
  // (item 3) replaced that with grouping: which guest a line belongs to
  // is now conveyed by the .line-section-header it renders beneath (see
  // renderTicketLines below), which also restores the full width this
  // badge used to take from the drink name.
  if (line.priceable === false) {
    // ALPHA-07 resume path, and (INV19 Slice 2) a live line whose price
    // stopped resolving on a later recompute; see TicketLineState.priceable
    // and .unpriceableReason.
    const priceEl = createTicketLinePrice(true);
    priceEl.textContent = "Price unavailable";
    top.append(priceEl);
  } else if (line.lineTotalCents !== undefined) {
    const priceEl = createTicketLinePrice();
    // CORE5: a group shows the combined price, and only when every member is priced.
    const groupTotals = group?.map((member) => member.lineTotalCents) ?? [line.lineTotalCents];
    priceEl.textContent = groupTotals.every((cents) => cents !== undefined)
      ? formatCents(groupTotals.reduce<number>((sum, cents) => sum + (cents ?? 0), 0))
      : formatCents(line.lineTotalCents);
    top.append(priceEl);
  }
  info.append(top);
  if (line.priceable === false) {
    // Design-gate fix (2026-08-20): this used to be the only signal an
    // unresolvable line existed at all (a 4-second toast on resume,
    // easy to miss), and for a renamed drink there was no line to look
    // at in the first place (reconstructOrderLine's header comment).
    // The "needs a manager" state now lives on the line itself for as
    // long as the line is on screen, not only in a toast.
    const noteEl = document.createElement("div");
    noteEl.className = "line-detail line-warning";
    // INV19 Slice 2: a live pricing-conflict result carries the server's
    // own message (unpriceableReason), distinct from the fixed ALPHA-07
    // resume-mismatch text below, since the two problems send whoever is
    // asked to fix it to two different screens (a tier/override reprice
    // versus a menu recipe edit).
    const noteText =
      line.unpriceableReason !== undefined
        ? line.unpriceableReason
        : line.detail !== undefined
          ? `Needs a manager · recipe changed since "${line.detail}" was selected`
          : "Needs a manager to fix the menu";
    const noteTextEl = document.createElement("span");
    noteTextEl.className = "line-detail-text";
    noteTextEl.textContent = noteText;
    // Touch-only audit fix (ALPHA-09 release evidence, category 2,
    // main.ts:1790): the title attribute used to be set on noteEl (the
    // flex wrapper) while the truncating text lives on noteTextEl (the
    // child span) - the two elements a live inspection actually checks
    // never agreed, which is how this rendered as an empty title in
    // practice. Set on the same element that carries the text now,
    // matching nameEl's own class+title+text-on-one-element pattern
    // above. This is no longer the only recovery path (see the tap
    // toggle below); it stays as a non-touch fallback.
    noteTextEl.title = noteText;
    noteEl.append(noteTextEl);
    appendTruncationChevron(noteEl);
    attachTruncatedTextTapToggle(noteEl, noteTextEl);
    info.append(noteEl);
  } else if (line.detail !== undefined) {
    // ALPHA-10 item 2, corrected during the design gate re-review
    // (2026-08-20): "Substituted" used to be appended as literal text
    // onto this SAME truncating row as the liquor name (e.g. "Woodford
    // Reserve Bourbon, Kentucky Straight Whiskey · Substituted"), so a
    // sufficiently long real liquor name (up to ~57 characters,
    // fixtures/inventory-snapshot.json) truncated with an ellipsis
    // before "Substituted" ever rendered, at exactly the widths this
    // line is tuned for (1024x768 and 1280x800). A marker that gets
    // truncated away on the drinks most likely to need one (a long
    // liquor name substituted from outside the drink's allowed
    // category) is not a marker. Fixed by giving the marker its own
    // non-truncating badge element, a flex sibling of the truncating
    // liquor-name text rather than appended text inside it, so the
    // badge's width is never subject to the liquor name's own
    // ellipsis; see styles.css's .line-detail/.line-badge comment.
    const detailEl = document.createElement("div");
    detailEl.className = "line-detail";
    const detailTextEl = document.createElement("span");
    detailTextEl.className = "line-detail-text";
    detailTextEl.textContent = line.detail;
    // Touch-only audit fix (ALPHA-09 release evidence, category 2,
    // main.ts:1814, "confirmed live... worse than documented"): title
    // used to be set on detailEl (the flex wrapper holding both the
    // text span and the substitution badge) while the truncating text
    // lives on detailTextEl (the child span) - a live inspection of
    // detailTextEl (the element that actually truncates, the one this
    // fixture's 57-character liquor name exercised) found an empty
    // title because it was never set there. Moved onto the same
    // element that carries the text, matching nameEl's own pattern.
    // No longer the sole recovery path either way; see the tap toggle
    // below, which is the real, touch-first fix.
    detailTextEl.title = line.detail;
    detailEl.append(detailTextEl);
    appendTruncationChevron(detailEl);
    if (line.isSubstitution === true) {
      const badgeEl = document.createElement("span");
      badgeEl.className = "line-badge line-badge-substitution";
      badgeEl.dataset["testid"] = "substitution-badge";
      badgeEl.textContent = "Substituted";
      detailEl.append(badgeEl);
    }
    // VOC1-F7 Slice 2 (DEC-171): a plain-language pour label whenever this
    // line's stored pour differs from its recipe default, the same
    // non-truncating badge shape .line-badge-substitution already
    // establishes above, so a manager reading the ticket rail never infers
    // pour size from price alone.
    if (line.pourModifierApplied === true && line.ounces !== undefined) {
      const pourBadgeEl = document.createElement("span");
      pourBadgeEl.className = "line-badge line-badge-pour";
      pourBadgeEl.dataset["testid"] = "pour-badge";
      pourBadgeEl.textContent = `${line.ounces} oz`;
      detailEl.append(pourBadgeEl);
    }
    attachTruncatedTextTapToggle(detailEl, detailTextEl);
    info.append(detailEl);
  }
  // VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
  // 2.5): one non-truncating badge per applied modifier, independent of
  // the line.detail branch above -- a modifiers-only drink (no liquor
  // slot at all, openModifiersOnlySheet) carries modifier badges with no
  // liquor detail to sit beside, so this cannot live inside that branch.
  // Each badge shows the catalog's own label and price adjustment, the
  // exact facts TicketLineState.appliedModifiers carries (display only;
  // the line's own price is always the server's lineTotalCents above).
  if (line.appliedModifiers !== undefined && line.appliedModifiers.length > 0) {
    const modifiersEl = document.createElement("div");
    modifiersEl.className = "line-detail line-modifiers";
    modifiersEl.dataset["testid"] = "line-modifiers";
    for (const modifier of line.appliedModifiers) {
      const badgeEl = document.createElement("span");
      badgeEl.className = "line-badge line-badge-modifier";
      badgeEl.dataset["testid"] = "modifier-badge";
      badgeEl.textContent = `${modifier.label} ${formatCents(modifier.priceAdjustmentCents)}`;
      modifiersEl.append(badgeEl);
    }
    info.append(modifiersEl);
  }
  // Amendment 2026-08-22 (fifth), item 2: the prominent quick action for
  // an unassigned line. Never shown once the line is covered (matching
  // the "..." edit control's own covered-line gate just below: an action
  // a tap could not act on is worse than no action at all), and only
  // when there is at least one named guest to assign it to.
  const readOnlyClosed = state.closedView !== undefined;
  if (options?.promptAssignGuest === true && !covered && !readOnlyClosed && state.guests.length > 0) {
    const assignButton = document.createElement("button");
    assignButton.type = "button";
    assignButton.className = "line-assign-guest-button";
    assignButton.dataset["testid"] = "assign-guest-button";
    assignButton.dataset["orderLineId"] = line.orderLineId;
    assignButton.textContent = "Assign";
    assignButton.setAttribute("aria-label", "Assign to guest");
    assignButton.addEventListener("click", () => openGuestPickerForLine(line.orderLineId));
    info.append(assignButton);
  }
  row.append(info);

  // Amendment 2026-08-22 (sixth), item 2, "one state button, not two": this
  // single control now carries all three states a line can be in - Not
  // Prepared (the only interactive one; tapping it calls handlePreparedClick
  // below), Prepared (reached once the drink is made, still pre-payment),
  // and Paid (terminal, once `covered` is true - the same signal that used
  // to render as a separate ".line-badge-paid" pill beside this button,
  // per that pill's own removed comment above). Paid takes priority over
  // Prepared when both are true (the ordinary case: a line is always
  // Prepared before it can be paid, per CLAUDE.md's "Pay and Close are
  // blocked while an included drink remains Not Prepared"), so the button
  // never regresses from Paid back to showing Prepared. Both Prepared and
  // Paid are non-interactive end states - disabled, not merely styled to
  // look non-interactive - since a tap on either could not do anything
  // (retrying handlePreparedClick on an already-prepared line is a no-op
  // there too; see that function's own early return), and a disabled
  // control is a more honest signal than a live one that silently no-ops.
  const button = createTicketStateButton();
  button.dataset["orderLineId"] = line.orderLineId;
  const prepared = line.preparedState === "prepared";
  // CORE3: a paid line a refund returned reads Refunded, not Paid. The state class stays "paid" for layout and colour;
  // the label and a data attribute carry the refund.
  const refunded = covered && state.refundedLineIds.has(line.orderLineId);
  const stateName = covered ? "paid" : prepared ? "prepared" : "not-prepared";
  paintTicketState(button, stateName, refunded);
  button.disabled = covered || prepared || readOnlyClosed;
  if (refunded) {
    button.dataset["refunded"] = "true";
  }
  // CORE5: on a group, one tap marks ONE drink Prepared (the oldest), never all of them.
  const preparedTargetId =
    group !== undefined ? (group[group.length - 1] ?? line).orderLineId : line.orderLineId;
  button.addEventListener("click", () => void handlePreparedClick(preparedTargetId));
  row.append(button);

  // VOC1-F8 build-time amendment, dated 2026-09-22 (docs/build-briefs/
  // VOC1-F8-view-recipe-on-the-floor.md, Section 2, verification record):
  // the ticket line's own recipe control does NOT render as a permanent
  // third row button alongside .state-btn and .btn-line-edit. A build-
  // time regression showed a real, long drink name's own truncation
  // (e2e/ui.spec.ts's "reachable in full by tapping it" case) broke once
  // a third always-visible control joined those two inside the row's own
  // tight flex width budget (styles.css's .btn-line-edit comment names
  // that budget). For an uncovered cocktail line, View Recipe lives inside
  // the line's existing "..." edit sheet instead (openLineEditSheet/
  // index.html's #line-edit-view-recipe), reached through the SAME
  // .btn-line-edit trigger, adding no new row control at all.
  //
  // UX60 (docs/backlog.md), owner ruling on a fully paid ticket, 2026-09-28:
  // "the recipe button is no longer necessary after the drink has been paid
  // for." This SUPERSEDES VOC1-F8-F8's own design, which added a dedicated
  // .btn-line-recipe control precisely so View Recipe stayed reachable on a
  // paid line -- the opposite of what the owner asked for here. A covered
  // line now renders neither the edit trigger nor a recipe control: once
  // paid, this row's trailing slot is simply empty, matching the edit
  // trigger's own already-established "no control once covered" shape
  // (below) rather than the special-cased fallback VOC1-F8-F8 built. This
  // is a KNOWN, deliberate regression of e2e/voc1-f8-view-recipe-on-the-
  // floor.spec.ts's "stays reachable once the line is paid and covered"
  // assertion (line ~485: `line-recipe-button` visible on a covered line);
  // DEC-204 forbids editing that spec from this item, so it is left failing
  // and named here and in this item's build brief for the owner/lead to
  // retire or rewrite deliberately, not silently.

  // ALPHA-08 delta payments (owner ruling 2026-08-21): remove / change
  // liquor, blocked once THIS LINE (not the whole order) is covered by a
  // captured payment (src/domain/lineEditing.ts's assertLineNotCovered;
  // matching that server-side rule here rather than showing a control
  // that would just 409 on every tap). A second, uncovered line on a
  // partially-paid tab keeps its edit control. Icon-only to protect the
  // compact two-row line's tight width budget; see styles.css's
  // .btn-line-edit comment. UNCHANGED from before VOC1-F8: this trigger
  // still hides once covered, exactly what
  // e2e/ui.spec.ts's own "removing a line is blocked once the order has
  // a captured payment" and "reopening a paid tab..." tests both assert
  // (`line-edit-button` count 0 on a covered line). A build-time attempt
  // to keep this trigger visible on a covered cocktail line, so View
  // Recipe's own edit-sheet option would stay reachable there too, broke
  // both of those tests directly (observed failing, then reverted): the
  // sheet's own accessibility is not the same contract as those two
  // tests' stronger claim that the edit CONTROL itself is gone.
  if (!covered && !readOnlyClosed) {
    const editButton = createTicketLineEditButton();
    editButton.dataset["orderLineId"] = line.orderLineId;
    editButton.setAttribute("aria-label", `Edit ${line.name}`);
    editButton.addEventListener("click", () => openLineEditSheet(line.orderLineId));
    row.append(editButton);
  }
  // UX60: a covered (paid) line renders no trailing control at all, neither
  // the edit trigger above nor a recipe control -- see this function's own
  // VOC1-F8/UX60 comment above for why the recipe control this branch used
  // to add for a covered cocktail line is gone.

  return row;
}

/** Amendment 2026-08-22 (fifth), item 3: `paid` marks a section whose
 * every line is already covered by a captured payment ("visibly marked
 * paid", the item's own wording: "Willy, paid"). Text-suffixed, never
 * color alone, matching this codebase's own touch-only/colorblind rule
 * elsewhere (design/README.md; see styles.css's .line-badge-substitution
 * comment for the same principle applied to a different badge). */
let serviceTodayPendingContext: FloorSaleContext | undefined;

function appendGuestServiceToday(section: TicketSection): void {
  const guest = state.guests.find((candidate) => candidate.id === section.guestId);
  if (guest?.client?.membershipActive !== true) return;
  const paid = section.lines.some((line) => isLineCovered(line.orderLineId));
  const applied = (state.orderDiscounts?.memberBenefits ?? []).some((benefit) =>
    benefit.guestId === guest.id && benefit.kind === "daily_comp" && benefit.reductionCents > 0);
  const holder = document.createElement("div");
  holder.className = "guest-service-today";
  const status = document.createElement("span");
  status.className = "guest-service-today-status";
  status.textContent = guest.service_today !== true ? "No service today: 10% only"
    : paid ? "Service today"
    : guest.daily_comp_claim_state === "elsewhere" ? "Service today: claim already held"
    : applied ? "Service today: comp applied"
    : guest.daily_comp_claim_state === "held" ? "Service today: comp ready"
    : "Service today: comp not applied";
  const session = requireSession();
  if (!paid && state.closedView === undefined && hasPermission(session, "pos.guest.service_today")) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "guest-service-today-toggle";
    button.setAttribute("role", "switch");
    button.setAttribute("aria-checked", guest.service_today === true ? "true" : "false");
    button.setAttribute("aria-label", `Service today for ${guest.label}`);
    button.textContent = "Service today";
    button.disabled = floorSaleContextIsCurrent(serviceTodayPendingContext);
    button.addEventListener("click", () => void changeGuestServiceToday(guest));
    holder.append(button);
  }
  holder.append(status);
  ticketLinesEl.append(holder);
}

async function changeGuestServiceToday(guest: GuestDto): Promise<void> {
  const context = captureFloorSaleContext();
  if (context === undefined || !floorSaleContextIsCurrent(context) || floorSaleContextIsCurrent(serviceTodayPendingContext)) return;
  const session = requireSession();
  serviceTodayPendingContext = context;
  renderTicketLines();
  try {
    const result = await saveGuestServiceToday(session.apiBaseUrl, session.tenantId, context.orderId, guest.id,
      { serviceToday: guest.service_today !== true, idempotencyKey: crypto.randomUUID() });
    if (!floorSaleContextIsCurrent(context)) return;
    if (result.status !== 200 && result.status !== 201) {
      showToast(result.body.message ?? "Could not update service today.", "danger");
      return;
    }
    const pricing = await getOrderDiscounts(session.apiBaseUrl, session.tenantId, context.orderId);
    if (!floorSaleContextIsCurrent(context)) return;
    state.guests = result.body.guests;
    if (pricing.status === 200) {
      state.orderDiscounts = pricing.body;
      renderTotals();
    } else {
      showToast("Service updated. Reopen this ticket to refresh pricing.", "danger");
    }
  } finally {
    if (serviceTodayPendingContext === context) serviceTodayPendingContext = undefined;
    if (floorSaleContextIsCurrent(context)) renderTicketLines();
  }
}

function buildLineSectionHeader(label: string, options?: { paid?: boolean }): HTMLElement {
  const header = document.createElement("div");
  const paid = options?.paid === true;
  header.className = paid ? "line-section-header line-section-header-paid" : "line-section-header";
  header.dataset["testid"] = "line-section-header";
  header.textContent = paid ? `${label}, paid` : label;
  return header;
}

interface TicketSection {
  /** Guest id for a real guest section; undefined for "Unassigned". */
  guestId: string | undefined;
  label: string;
  lines: TicketLineState[];
}

// Amendment 2026-08-22 (item 3): grouping replaces the old guest-chips-
// plus-per-line-badge design. A ticket with no named guests renders its
// flat list exactly as before this item ever existed (product-
// requirements.md P0.19's own "zero added friction" rule); once at least
// one guest exists, lines render grouped under a section header per
// guest, in the order guests were added, followed by an "Unassigned"
// section for any line with no guest at all. That last case is real, not
// defensive: a line added before the ticket's first guest existed keeps
// guestId undefined forever unless explicitly reattributed
// (src/domain/guests.ts's resolveLineGuestId only ever back-fills a
// guest onto a NEW line, never an existing one), so adding the first
// guest to an already-started ticket is exactly when this section shows
// up with real content.
//
// Amendment 2026-08-22 (fifth), item 3 adds a second pass on top of that
// grouping: any section whose every line is already covered sinks below
// every section that still has at least one uncovered line, so the rail's
// top stays devoted to what still needs attention. A section with zero
// lines (a named guest nobody has ordered for yet) is never "paid" - there
// is nothing to have been paid for - and stays in the active group in its
// normal position. Sections within each group keep their own relative
// order from the pass above (guest add order, Unassigned last) EXCEPT the
// paid group is then re-sorted by payment time (sectionPaidAt), per the
// item's own "Order within the paid group by payment time" instruction.
//
// TF5, owner 2026-08-29, with a screenshot of an 11-drink tab: "The
// newest drink added should always be at the top of the list, not at the
// bottom." DISPLAY ORDER ONLY. state.lines itself stays in the server's
// own created_at-ascending order (oldest first); every closure elsewhere
// keyed off state.lines by orderLineId (handlePrepareAllClick,
// notPreparedCount, discountTargetLabel, the line-edit sheet) is
// unaffected because none of them read position, only identity. The
// reversal happens exactly once, right here, on a shallow copy, so
// nothing upstream of rendering ever sees the flipped order.
/** CORE5: true for a drink that may share a row with identical ones: not Prepared, not paid, priced, and
 * nothing hangs off it that is keyed to its own row (a price change, a stock override, a discount or
 * scheduled special). Anything else stays its own row, so every strip keyed by line id keeps its row. */
function isGroupableLine(line: TicketLineState): boolean {
  if (line.preparedState !== "not_prepared" || isLineCovered(line.orderLineId)) {
    return false;
  }
  if (
    line.priceable === false ||
    (line.priceChangeCount ?? 0) > 0 ||
    line.stockOverride !== undefined
  ) {
    return false;
  }
  return !(state.orderDiscounts?.applications ?? []).some(
    (application) => application.orderLineId === line.orderLineId,
  );
}

function groupKeyFor(line: TicketLineState): string {
  return JSON.stringify([
    line.lineKind,
    line.menuDrinkId ?? null,
    line.liquorProductId ?? null,
    line.ounces ?? null,
    line.isSubstitution === true,
    line.pourModifierApplied === true,
    [...(line.modifierSelections ?? [])].sort(),
    line.name,
    line.detail ?? null,
    line.guestId ?? null,
    // The price each drink was rung at (TKTPRICE1): two of the same drink at different stored prices
    // are two rows, never one row with a blended total.
    line.lineTotalCents ?? null,
  ]);
}

/** CORE5: folds identical groupable lines (given newest first) into one entry each, at the position of the
 * newest member. A line that is not groupable, or has no twin, is an entry of one. */
function groupIdenticalLines(lines: TicketLineState[]): TicketLineState[][] {
  const entries: TicketLineState[][] = [];
  const byKey = new Map<string, TicketLineState[]>();
  for (const line of lines) {
    if (!isGroupableLine(line)) {
      entries.push([line]);
      continue;
    }
    const key = groupKeyFor(line);
    const existing = byKey.get(key);
    if (existing !== undefined) {
      existing.push(line);
      continue;
    }
    const entry = [line];
    byKey.set(key, entry);
    entries.push(entry);
  }
  return entries;
}

function appendTicketLineEntries(
  lines: TicketLineState[],
  options?: { promptAssignGuest?: boolean },
): void {
  for (const entry of groupIdenticalLines(lines)) {
    // The row stands on the OLDEST drink of its group (entries are newest first), so the row's identity
    // does not change when another identical drink is added: an expanded name stays expanded and the
    // Prepared tap goes to the oldest. The row sits where the newest drink would.
    const head = entry[entry.length - 1];
    if (head === undefined) {
      continue;
    }
    ticketLinesEl.append(
      buildTicketLineRow(head, { ...options, ...(entry.length > 1 ? { group: entry } : {}) }),
    );
  }
}

function renderTicketLines(): void {
  ticketLinesEl.innerHTML = "";
  // AU14-F4 / DEC-276: innerHTML above detached the record strip; the same
  // element goes back as the first row (renderReopenStrip fills it).
  ticketLinesEl.append(ticketReopenStripEl);
  const displayLines = [...state.lines].reverse();
  if (state.guests.length === 0) {
    appendTicketLineEntries(displayLines);
    renderDiscountLines();
    renderLineSpecialStrips();
    renderLinePriceChangeStrips();
    renderLineStockOverrideStrips();
    refreshLineNameStacking();
    refreshLineNameWordTruncation();
    reapplyExpandedLineNames();
    refreshLineTruncationChevrons();
    return;
  }

  const sections: TicketSection[] = state.guests.map((guest) => ({
    guestId: guest.id,
    label: guest.label,
    lines: displayLines.filter((line) => line.guestId === guest.id),
  }));
  const unassignedLines = displayLines.filter((line) => line.guestId === undefined);
  if (unassignedLines.length > 0) {
    sections.push({ guestId: undefined, label: "Unassigned", lines: unassignedLines });
  }

  const active: TicketSection[] = [];
  const paid: TicketSection[] = [];
  for (const section of sections) {
    const isPaid =
      section.lines.length > 0 && section.lines.every((line) => isLineCovered(line.orderLineId));
    (isPaid ? paid : active).push(section);
  }
  paid.sort((a, b) =>
    (sectionPaidAt(a.guestId) ?? "").localeCompare(sectionPaidAt(b.guestId) ?? ""),
  );

  const renderSection = (section: TicketSection, isPaidGroup: boolean): void => {
    ticketLinesEl.append(buildLineSectionHeader(section.label, { paid: isPaidGroup }));
    appendGuestServiceToday(section);
    appendTicketLineEntries(section.lines, { promptAssignGuest: section.guestId === undefined });
  };
  for (const section of active) {
    renderSection(section, false);
  }
  for (const section of paid) {
    renderSection(section, true);
  }
  renderDiscountLines();
  renderLineSpecialStrips();
  renderLinePriceChangeStrips();
  renderLineStockOverrideStrips();
  refreshLineNameStacking();
  refreshLineNameWordTruncation();
  reapplyExpandedLineNames();
  refreshLineTruncationChevrons();
}

/** The drink a reduction landed on, for a discount row's detail line.
 * A ticket-level discount has no line of its own, which is a real state
 * and not a missing value, so it is named as what it is. */
function discountTargetLabel(orderLineId: string | null): string {
  if (orderLineId === null) {
    return "Whole ticket";
  }
  return state.lines.find((line) => line.orderLineId === orderLineId)?.name ?? "This ticket";
}

/** Builds one row in the discount list. `remove` is undefined for a
 * derived member benefit, which is what makes the two kinds visibly
 * different rather than one of them carrying a control that would fail. */
function buildDiscountLineRow(params: {
  name: string;
  detail: string;
  reductionCents: number;
  remove?: () => void;
}): HTMLElement {
  const row = document.createElement("div");
  row.className = "discount-line";
  row.dataset["testid"] = "discount-line";

  const info = document.createElement("div");
  info.className = "discount-line-info";
  const nameEl = document.createElement("span");
  nameEl.className = "discount-line-name";
  nameEl.dataset["testid"] = "discount-line-name";
  nameEl.textContent = params.name;
  const detailEl = document.createElement("span");
  detailEl.className = "discount-line-detail";
  detailEl.dataset["testid"] = "discount-line-detail";
  detailEl.textContent = params.detail;
  info.append(nameEl, detailEl);

  const amountEl = document.createElement("span");
  amountEl.className = "discount-line-amount";
  amountEl.dataset["testid"] = "discount-line-amount";
  amountEl.textContent = `-${formatCents(params.reductionCents)}`;

  row.append(info, amountEl);

  if (params.remove === undefined) {
    // DSC2. A member benefit is recomputed on every pricing pass and has
    // no discount_applications row behind it, so there is nothing to
    // remove and the next pass would rebuild it anyway. The bartender is
    // TOLD that, in a word, rather than handed a button that would do
    // nothing or fail: a dead control is the same discoverability defect
    // as an invisible one, pointed the other way.
    const badge = document.createElement("span");
    badge.className = "discount-line-badge";
    badge.dataset["testid"] = "discount-line-automatic";
    badge.textContent = "Automatic";
    row.append(badge);
    return row;
  }

  const removeButton = document.createElement("button");
  removeButton.type = "button";
  removeButton.className = "btn-discount-remove";
  removeButton.dataset["testid"] = "discount-line-remove";
  removeButton.setAttribute("aria-label", `Take off ${params.name}`);
  removeButton.textContent = "Take off";
  removeButton.addEventListener("click", params.remove);
  row.append(removeButton);
  return row;
}

/** TF1, owner testing feedback 2026-08-28: "all discounts should show up
 * as their own line item, not just a total", and "there is no way to
 * remove a discount if you apply the wrong one."
 *
 * Both halves are one change. Removal was already fully built and gated
 * (removeDiscountApplication, POST .../discount-applications/:id/remove);
 * what was missing was anywhere to put a control for ONE discount, since
 * the floor showed only the summed row. Giving each discount its own row
 * gives each one somewhere to carry its own labelled Remove.
 *
 * TWO KINDS OF ROW, and the difference is deliberate and visible:
 *   - An APPLICATION is a row somebody applied. It has an id, it is
 *     removable, and it carries a Remove button.
 *   - A MEMBER BENEFIT is derived on every pricing pass (DSC2). It has no
 *     application row, nothing to remove, and the next pass would rebuild
 *     it, so it carries an "Automatic" badge where the button would be.
 *
 * Read-only for an actor without money.discount.apply: the rows still
 * show, because seeing the money is not the same permission as changing
 * it, and the Remove buttons do not. A permission decision, never a role
 * one (CLAUDE.md).
 *
 * A zero-reduction member benefit is not rendered at all: a benefit that
 * took nothing off did not happen. A zero-reduction APPLICATION still is,
 * because somebody applied it and must be able to take it back off. */
/** Whether the folded Specials summary line is expanded (QAWALK1-F4). */
let discountSpecialsExpanded = false;

function renderDiscountLines(): void {
  discountLinesEl.innerHTML = "";
  // Every current server reduction appears once here, including automatic scheduled specials.
  // A reduction of zero from a scheduled special that lost to a larger discount is not rendered
  // (the money is unchanged); a manual application still shows so it can be taken back off.
  const applications = (state.orderDiscounts?.applications ?? []).filter(
    (application) => application.reductionCents > 0 || application.origin === "manual",
  );
  const benefits = (state.orderDiscounts?.memberBenefits ?? []).filter(
    (benefit) => benefit.reductionCents > 0,
  );
  // AU14-F5 / DEC-276: a manual discount taken off stays on the ticket as a struck
  // row with its grey "Taken off" strip, so the money is never silently changed.
  const removedManual = state.orderDiscounts?.removedManual ?? [];
  ticketLinesEl.querySelector(".ticket-discount-history")?.remove();
  if (removedManual.length > 0) {
    const history = document.createElement("div");
    history.className = "ticket-discount-history";
    for (const removed of removedManual) history.append(buildRemovedDiscountRow(removed));
    ticketLinesEl.append(history);
  }
  if (applications.length === 0 && benefits.length === 0) {
    discountLinesEl.hidden = true;
    return;
  }
  discountLinesEl.hidden = false;

  const automatic = applications.filter((application) => application.origin !== "manual");
  const header = document.createElement("div");
  header.className = "line-section-header";
  header.dataset["testid"] = "discount-lines-header";
  header.textContent = "Discounts";
  const canRemove = canApplyDiscounts() && state.closedView === undefined;
  const onlyFoldedSummary = automatic.length >= 3 && applications.length === automatic.length;
  // The folded summary names itself, so the "Discounts" header would only spend rail height.
  if (benefits.length > 0 || !onlyFoldedSummary || discountSpecialsExpanded) {
    discountLinesEl.append(header);
  }
  // QAWALK1-F4: three or more automatic specials fold into ONE summary line ("Specials -$18.00"),
  // tap to expand, so they cannot fill the rail. Each drink still carries its own Auto strip.
  const foldSpecials = automatic.length >= 3 && !discountSpecialsExpanded;
  if (automatic.length >= 3) {
    const summary = document.createElement("button");
    summary.type = "button";
    summary.className = "discount-specials-summary";
    summary.dataset["testid"] = "discount-specials-summary";
    summary.setAttribute("aria-expanded", String(!foldSpecials));
    const label = document.createElement("span");
    label.textContent = `Specials (${automatic.length}) ${foldSpecials ? "\u25B8" : "\u25BE"}`;
    const amount = document.createElement("span");
    amount.textContent = `-${formatCents(automatic.reduce((sum, a) => sum + a.reductionCents, 0))}`;
    summary.append(label, amount);
    summary.addEventListener("click", () => {
      discountSpecialsExpanded = !discountSpecialsExpanded;
      renderDiscountLines();
    });
    discountLinesEl.append(summary);
  }
  for (const application of applications) {
    if (foldSpecials && application.origin !== "manual") continue;
    const target = discountTargetLabel(application.orderLineId);
    discountLinesEl.append(
      buildDiscountLineRow({
        name: application.discountName,
        detail:
          application.discountReasonLabel === null
            ? target
            : `${target} · ${application.discountReasonLabel}`,
        reductionCents: application.reductionCents,
        ...(canRemove && application.origin === "manual"
          ? { remove: () => void submitDiscountRemoval(application.id) }
          : {}),
      }),
    );
  }
  for (const benefit of benefits) {
    const guestLabel = state.guests.find((guest) => guest.id === benefit.guestId)?.label;
    const target = discountTargetLabel(benefit.orderLineId);
    discountLinesEl.append(
      buildDiscountLineRow({
        name: benefit.discountName,
        detail: guestLabel === undefined ? target : `${target} · ${guestLabel}`,
        reductionCents: benefit.reductionCents,
      }),
    );
  }
}

/** AU14-F5 / DEC-276. A manual discount that was taken off: the row struck
 * through (it takes nothing now), and under it the grey record strip, a bold
 * "Taken off" tag, when and who and who approved, and the reason in bold. All of
 * it is the server's (state.orderDiscounts.removedManual). No control. */
function buildRemovedDiscountRow(removed: RemovedManualDiscountDto): HTMLElement {
  const group = document.createElement("div");
  group.className = "discount-removed";
  group.dataset["testid"] = "discount-removed";
  group.dataset["applicationId"] = removed.applicationId;
  const row = buildDiscountLineRow({
    name: removed.discountName,
    detail: discountTargetLabel(removed.orderLineId),
    reductionCents: removed.reductionCents,
  });
  row.classList.add("discount-line-removed");
  // buildDiscountLineRow marks a row without a Remove control as automatic; this one is neither.
  row.querySelector("[data-testid='discount-line-automatic']")?.remove();
  // Its own test ids, so a count of live discount rows never includes a removed one.
  row.dataset["testid"] = "discount-removed-line";
  for (const [from, to] of [
    ["discount-line-name", "discount-removed-name"],
    ["discount-line-detail", "discount-removed-detail"],
    ["discount-line-amount", "discount-removed-amount"],
  ] as const) {
    const el = row.querySelector<HTMLElement>(`[data-testid='${from}']`);
    if (el !== null) {
      el.dataset["testid"] = to;
    }
  }
  const strip = document.createElement("div");
  strip.className = "rec-strip";
  strip.dataset["testid"] = "discount-removed-strip";
  const tag = document.createElement("span");
  tag.className = "rec-tag";
  tag.textContent = "Taken off";
  const when = new Date(removed.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const who = document.createElement("span");
  who.className = "rec-who";
  who.dataset["testid"] = "discount-removed-who";
  who.textContent =
    when +
    (removed.byName === null ? "" : `, ${removed.byName}`) +
    (removed.approvedByName === null ? "" : `. Approved by ${removed.approvedByName}`);
  const why = document.createElement("span");
  why.className = "rec-why";
  why.dataset["testid"] = "discount-removed-reason";
  why.textContent = removed.reason;
  strip.append(tag, who, why);
  group.append(row, strip);
  return group;
}

/** UX62 (docs/build-briefs/UX62-paid-ticket-totals.md): the persistent
 * Paid row directly under Total, and the Tips row directly under Paid.
 * UX62-F1 (docs/build-briefs/UX62-F1-refunded-line.md) adds a third row,
 * Refunded, directly under Tips. Independent of the discount/quote
 * branches below (it reads state.capturedPaymentsSummary alone), so it
 * runs once, before renderTotals' own early returns, rather than being
 * duplicated at each of that function's three exit points. All three rows
 * hidden whenever no payment has captured yet (summary undefined, or a
 * known zero count), matching every ordinary ticket's totals block today.
 *
 * Owner ruling, 2026-09-28 (payments-review finding): Paid shows the
 * drinks-plus-tax portion actually paid (summary.paidCents, amount minus
 * that payment's own tip), so it agrees with Total once the ticket is
 * covered; Tips is its own separate row, shown ONLY when captured tips
 * are above zero, never folded into Paid.
 *
 * Owner ruling, 2026-09-28 (UX62-F1, "Separate Refunded line"): Refunded
 * shows the sum of every completed refund on the ticket (drinks plus tax;
 * tip is never refunded, DEC-035), shown ONLY when that sum is above
 * zero. Paid is NOT netted for it: Paid keeps reading the gross amount
 * charged, exactly as UX62 built it, so Refunded is additional
 * information, not a correction to a figure already on screen. */
function renderPaidRow(): void {
  // SPLIT1: Balance shows only once a payment taken by amount exists, and is the server's own figure.
  const amountPayments = state.paymentPreview?.amountPayments;
  balanceRowEl.hidden =
    amountPayments === undefined ||
    amountPayments.count <= 0 ||
    state.paymentPreview?.balanceCents === undefined;
  if (!balanceRowEl.hidden) {
    balanceAmountEl.textContent = formatCents(state.paymentPreview?.balanceCents ?? 0);
  }
  const summary = state.capturedPaymentsSummary;
  const count = summary?.count ?? 0;
  paidRowEl.hidden = count <= 0;
  tipsRowEl.hidden = (summary?.tipCents ?? 0) <= 0;
  refundedRowEl.hidden = (summary?.refundedCents ?? 0) <= 0;
  // TOTALSDESIGN1: one "Charged to <card>" line per captured payment, the amount actually charged
  // (tip included), as stored on the payment; no new money.
  chargedRowEl.hidden = count <= 0 || (state.capturedCharges ?? []).length === 0;
  chargedRowEl.replaceChildren();
  for (const charge of state.capturedCharges ?? []) {
    const line = document.createElement("div");
    line.className = "charged-line";
    line.dataset["testid"] = "charged-line";
    const label = document.createElement("span");
    label.className = "charged-label";
    label.dataset["testid"] = "charged-label";
    label.textContent = charge.label;
    const when = document.createElement("small");
    when.dataset["testid"] = "charged-time";
    when.textContent = new Date(charge.at).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    label.append(when);
    const amount = document.createElement("span");
    amount.className = "charged-amount";
    amount.dataset["testid"] = "charged-amount";
    amount.textContent = formatCents(charge.amountCents);
    line.append(label, amount);
    chargedRowEl.append(line);
  }
  // TOTALSDESIGN1: a fully paid ticket shows its balance, $0.00 (the split-by-amount balance above is unchanged).
  if (balanceRowEl.hidden && count > 0) {
    const totalCents = state.orderDiscounts?.totals.totalCents ?? state.quote?.totalCents;
    if (totalCents !== undefined && totalCents > 0 && (summary?.paidCents ?? 0) >= totalCents) {
      balanceRowEl.hidden = false;
      balanceAmountEl.textContent = formatCents(Math.max(totalCents - (summary?.paidCents ?? 0), 0));
    }
  }
  if (count <= 0) {
    return;
  }
  paidAmountEl.textContent = formatCents(summary?.paidCents ?? 0);
  paidCountEl.textContent = formatPaidCountLabel(count);
  if (!tipsRowEl.hidden) {
    tipsAmountEl.textContent = formatCents(summary?.tipCents ?? 0);
  }
  if (!refundedRowEl.hidden) {
    refundedAmountEl.textContent = formatCents(summary?.refundedCents ?? 0);
  }
}

/** DSC35-S7 (docs/build-briefs/DSC35-S7-rail.md): the green Auto strip under
 * a drink that carries a scheduled special: an "Auto" tag, the special's
 * name, and minus the amount, plus the line's price after the special in
 * the success colour (the mockup's "now" price). Only a scheduled
 * application that actually took something off is drawn; one that lost to
 * a larger manual discount on its drink reduced nothing, and a strip that
 * does not reflect a reduction would be a lie. Every figure is the
 * server's (state.orderDiscounts); the only arithmetic is the display of
 * the line's price less that application's own reduction.
 *
 * Painted in place on the existing rows, because the drink rows are drawn
 * before the discounts read returns and are not redrawn when it does
 * (refreshOrderDiscounts calls this, and so does renderTicketLines). The
 * name wraps rather than truncates, so no content is hidden on a touch
 * screen. Read-only: there is no control in the strip (S4 owns removal). */
function renderLineSpecialStrips(): void {
  const byLine = new Map<string, OrderDiscountApplicationDto>();
  for (const application of state.orderDiscounts?.applications ?? []) {
    if (
      application.origin === "scheduled" &&
      application.orderLineId !== null &&
      application.reductionCents > 0 &&
      !byLine.has(application.orderLineId)
    ) {
      byLine.set(application.orderLineId, application);
    }
  }
  const removedByLine = new Map<string, RemovedScheduledSpecialDto>();
  for (const removed of state.orderDiscounts?.removedSpecials ?? []) {
    if (removed.orderLineId !== null) {
      removedByLine.set(removed.orderLineId, removed);
    }
  }
  ticketLinesEl.querySelectorAll<HTMLElement>(".line[data-order-line-id]").forEach((row) => {
    row.querySelector(".line-special")?.remove();
    row.querySelector(".line-special-removed")?.remove();
    row.classList.remove("line-has-special");
    const orderLineId = row.dataset["orderLineId"] ?? "";
    const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
    const priceEl = row.querySelector<HTMLElement>(".line-price");
    // CORE5: a grouped row already shows its members' combined price; repainting it from the head
    // line alone would show one drink's price on six.
    const isGroupRow = row.dataset["groupCount"] !== undefined;
    if (
      priceEl !== null &&
      !isGroupRow &&
      line?.priceable !== false &&
      line?.lineTotalCents !== undefined
    ) {
      priceEl.className = "line-price";
      priceEl.textContent = formatCents(line.lineTotalCents);
    }
    const application = byLine.get(orderLineId);
    if (application === undefined) {
      // DSC35-S7b: a special a manager took off this drink leaves a plain
      // muted line, so the ticket says why the drink is full price.
      const removed = removedByLine.get(orderLineId);
      if (removed !== undefined) {
        row.classList.add("line-has-special");
        const note = document.createElement("div");
        note.className = "line-special-removed";
        note.dataset["testid"] = "line-special-removed";
        note.textContent = `Special removed by manager: ${removed.discountName} (${removed.reasonLabel})`;
        row.append(note);
      }
      return;
    }
    row.classList.add("line-has-special");
    if (priceEl !== null && line?.priceable !== false && line?.lineTotalCents !== undefined) {
      priceEl.className = "line-price line-price-now";
      priceEl.textContent = formatCents(line.lineTotalCents - application.reductionCents);
    }
    // QAWALK1-F4: one compact chip beside the price ("Special -$2.00"), not a full-width strip per drink.
    // The name is in the hover title and kept as hidden text for assistive tech.
    const strip = document.createElement("div");
    strip.className = "line-special";
    strip.dataset["testid"] = "line-special-strip";
    strip.title = application.discountName;
    const tag = document.createElement("span");
    tag.className = "sr-only";
    tag.textContent = `Auto ${application.discountName} `;
    const amountEl = document.createElement("span");
    amountEl.className = "line-special-amount";
    amountEl.textContent = `Special -${formatCents(application.reductionCents)}`;
    strip.append(tag, amountEl);
    (row.querySelector<HTMLElement>(".line-top") ?? row).append(strip);
  });
}

function renderTotals(): void {
  renderPaidRow();
  renderAwaitingTipTotals();
  // Gross subtotal and actual aggregate tax/total come from one authoritative server pricing result.
  // Named reductions are rendered once by renderDiscountLines, never repeated as aggregate deductions.
  const discounted = state.orderDiscounts;
  // M5: the one sentence about price changes. A changed price is NOT a row in
  // Specials or Discount (it is not a discount; the Subtotal already
  // reflects it), so it is only ever this note, set from the server's own sum.
  const priceChangesCents = discounted?.totals.priceChangesCents ?? 0;
  priceChangesNoteEl.hidden = priceChangesCents === 0;
  priceChangesNoteEl.textContent =
    priceChangesCents === 0
      ? ""
      : ` (includes price changes ${priceChangesCents < 0 ? "minus" : "plus"} ${formatCents(Math.abs(priceChangesCents))})`;
  // DUALPRICE1: both totals when dual pricing is on; the Total row above is the cash price and says so.
  const dualTotals = discounted?.dualPricing ?? null;
  dualPricingTotalsEl.hidden = dualTotals === null;
  totalLabelEl.textContent = dualTotals === null ? "Total" : "Total (cash price)";
  if (dualTotals !== null) {
    dualCashAmountEl.textContent = formatCents(dualTotals.cashTotalCents);
    dualCardAmountEl.textContent = formatCents(dualTotals.cardTotalCents);
    dualCardNoteEl.textContent = `Card includes a ${formatCents(dualTotals.cardPriceCents)} card price (${(dualTotals.basisPoints / 100).toFixed(2)}%) and tax on it.`;
  }
  if (discounted !== undefined) {
    subtotalAmountEl.textContent = formatCents(discounted.totals.subtotalBeforeDiscountCents);
    taxAmountEl.textContent = formatCents(discounted.totals.taxCents);
    totalAmountEl.textContent = formatCents(discounted.totals.totalCents);
    return;
  }
  if (state.quote === undefined) {
    subtotalAmountEl.textContent = formatCents(0);
    taxAmountEl.textContent = formatCents(0);
    totalAmountEl.textContent = formatCents(0);
    return;
  }
  subtotalAmountEl.textContent = formatCents(state.quote.subtotalCents);
  taxAmountEl.textContent = formatCents(state.quote.taxCents);
  totalAmountEl.textContent = formatCents(state.quote.totalCents);
}

function notPreparedCount(): number {
  return state.lines.filter((line) => line.preparedState !== "prepared").length;
}

// UX61 (docs/backlog.md), owner screenshot, stage0 2026-09-28: a fully paid
// ticket's own header still read "Ticket draft" (ticketSub's "Ticket"
// fallback plus orderStatusEl showing state.orderStatus raw), because
// db/schema.ts's `orders.status` never leaves 'draft' for the ordinary card-
// payment path until the ticket is actually closed (a separate, later
// action -- see enterTicket/handleZeroDueCloseClick). 'draft' is a lifecycle
// value for the database, never a word written for a bartender to read, and
// it does not change just because the balance reached zero. This derives a
// short, real label instead of echoing that raw value: "paid" once nothing
// is left uncovered (the exact state.paymentPreview.uncoveredLines signal
// every other Pay/Close gate in this file already reads, and the same word
// this file's own line-section headers and the zero-due prompt already use
// for the identical state -- buildLineSectionHeader's "<label>, paid" and
// showZeroDuePrompt's "All paid"), otherwise the existing status mapped to
// a word actually meant to be read. Only reachable states from
// db/schema.ts's own orders.status CHECK constraint are named; anything
// else (there is none today) falls back to the raw value rather than
// showing nothing.
function ticketStatusLabel(): string {
  // CLOSEDVIEW1: the ticket being looked at is closed, whatever its payments say.
  if (state.closedView !== undefined) {
    return "closed";
  }
  if (state.awaitingTip !== undefined) {
    return "awaiting tip";
  }
  if (
    state.lines.length > 0 &&
    state.paymentPreview !== undefined &&
    !paymentPreviewStale() &&
    state.paymentPreview.uncoveredLines.length === 0
  ) {
    return "paid";
  }
  switch (state.orderStatus) {
    case "draft":
    case "open":
      return "open";
    case "reopened":
      return "reopened";
    case "closing":
      return "closing";
    case "closed":
      return "closed";
    case "canceled":
      return "canceled";
    default:
      return state.orderStatus ?? "";
  }
}

/** UX74-F1: shows the failed amount note and Try again when the last
 * current read failed and no read is out. First failure and later ones read
 * differently (DEC-286): the second names the likely cause. Returns whether
 * it showed. */
function showPayFailureIfUnread(): boolean {
  if (paymentPreviewFailedCount === 0 || paymentPreviewReadsInFlight > 0) {
    return false;
  }
  payFailText.textContent =
    paymentPreviewFailedCount === 1
      ? "Could not update the amount."
      : "Still no luck. Check the connection, then try again.";
  payFailEl.hidden = false;
  return true;
}

/** UX74-F1: Try again re-reads the amount. A read, never a charge. Ignored
 * while a read is already out, so a double tap cannot start two. */
function handlePayRetryClick(): void {
  if (state.orderId === undefined || paymentPreviewReadsInFlight > 0) {
    return;
  }
  void refreshPaymentPreview();
}

function renderTicketActions(): void {
  orderStatusEl.textContent = ticketStatusLabel();
  // CLOSEDVIEW1: a closed ticket opened to look at has no Pay, Prepare, tip or Close controls at all.
  if (state.closedView !== undefined) {
    ticketActionsEl.hidden = true;
    return;
  }
  // RCPT1-S3: an Awaiting tip ticket shows Reprint and Enter tip in place of Prepare All and Pay.
  if (renderAwaitingTipRail()) {
    return;
  }
  // UX102: set only by the nothing-unpaid branch below; the CSS hides the Pay row and its reason while it holds.
  ticketActionsEl.classList.remove("is-nothing-due");
  // UX39: Discount used to be its own standalone header chip, hidden
  // without money.discount.apply and on a ticket with nothing to
  // discount yet (discountOptionEligible below carries that exact rule
  // now); it is folded into #btn-ticket-more, so this call keeps that
  // button's own visibility in sync with Discount's own eligibility too.
  renderTicketMoreButton();
  // ALPHA-08 delta payments (owner ruling 2026-08-21): the Pay button's
  // own label shows what Pay would ACTUALLY charge right now (the
  // server's live uncovered-lines read), not the ticket's full total,
  // which may include lines a prior payment already covered. UX74: while
  // that read has not seen every line on the ticket, the label is the bare
  // word "Pay". There is deliberately no fallback to the quote total: with
  // delta payments the total can still be the wrong charge, and the stale
  // preview figure is the wrong number outright.
  const previewStale = paymentPreviewStale();
  const totalCents = state.paymentPreview?.totalCents ?? 0;
  if (!previewStale && state.lines.length === 0) {
    // VER72-F3: an empty ticket has no amount to show: the bare word, and
    // nothing is being waited for, so it is neither stale nor busy.
    payButton.textContent = "Pay";
    payButton.classList.remove("is-stale");
    payButton.removeAttribute("aria-busy");
  } else if (previewStale) {
    payButton.textContent = "Pay";
    payButton.classList.add("is-stale");
    // UX74-F1: busy only while the amount is being waited for. A failed
    // read with nothing out is not busy: the failed note says so.
    if (paymentPreviewFailedCount > 0 && paymentPreviewReadsInFlight === 0) {
      payButton.removeAttribute("aria-busy");
    } else {
      payButton.setAttribute("aria-busy", "true");
    }
  } else {
    // DUALPRICE1: with dual pricing on, one amount would mislead (cash and card differ, both shown above), so the button says Pay.
    payButton.textContent =
      state.paymentPreview?.dualPricing === undefined ? `Pay ${formatCents(totalCents)}` : "Pay";
    payButton.classList.remove("is-stale");
    payButton.removeAttribute("aria-busy");
  }
  // A3, ANSWERED 2026-08-29: hidden by default, every early return below
  // included; shown only at the one path that reaches the bottom of this
  // function with something payable at exactly $0.00 (see that path's own
  // comment for why that is the correct, purely client-derived signal).
  noTipButton.hidden = true;

  // UX60 (docs/backlog.md), owner ruling on a fully paid two-guest ticket:
  // "If all drinks are prepared, the prepare all button is no longer
  // necessary. Especially after the ticket is already paid." notPreparedCount
  // is zero both while every line is Prepared-but-unpaid and once the whole
  // ticket is paid (a paid line was necessarily Prepared first, per CLAUDE.md's
  // Pay/Close gate), so one condition covers both cases the owner named. Set
  // on every render, ahead of every early return below, so an empty ticket
  // (nothing to prepare either) and every other path all agree.
  prepareAllButton.hidden = notPreparedCount() === 0;

  // UX74-F1 (DEC-286): the failed row is hidden by default and shown only
  // by showPayFailure below, from the two places a failed amount read is
  // the reason Pay is off. Never while a read is out: that reads "Updating
  // the amount." instead.
  payFailEl.hidden = true;

  // PAYFIX1: set below only for a ticket whose last payment answer was lost.
  payCheckButton.hidden = true;
  if (state.lines.length === 0) {
    payButton.disabled = true;
    payBlockReason.hidden = false;
    payBlockReason.textContent = "Add a drink to start this ticket.";
    return;
  }
  // PAYFIX1: the answer to the last payment on this ticket was lost, so Pay stays off, on every redraw, until Check payment
  // learns from the server whether it went through.
  if (payUncertain !== undefined && payUncertain.orderId === state.orderId) {
    payButton.disabled = true;
    payCheckButton.hidden = false;
    payCheckButton.disabled = payCheckBusy;
    payBlockReason.hidden = !payStatusEl.hidden;
    payBlockReason.textContent = "Payment may not have gone through. Check before retrying.";
    return;
  }
  // CORE4 (owner ruling 2026-10-10, "Pay marks them prepared"): Not Prepared drinks no longer block Pay. The server
  // prepares the drinks a payment covers, as the paying user, when the payment captures. Close is still gated by the
  // server on every line being Prepared, which a captured payment satisfies.
  // Every line is Prepared. Pay itself is additionally disabled once this
  // ticket has already been paid this session, purely to stop a second,
  // unrelated payment attempt against the same order. UX13 removed the
  // persistent Close Ticket button this comment used to gate alongside
  // Pay (product-requirements.md's own Pay/Close gate, P0.6, is
  // unaffected: Close is still blocked while Not Prepared, both early
  // returns above still cover it, and Close is no longer a control a
  // bartender can tap at all outside #zero-due-close).

  // Design/QA gate HIGH correction, 2026-08-21 ("rail blindness to active
  // splits"): before this fix, renderTicketActions never once read
  // state.activeSplitPlan, so with a split in progress the Pay button kept
  // showing the ticket's own full, stale total (misleading: tapping it
  // actually resumes the split at a per-share amount, per
  // handlePayButtonClick's own priority check, never charges that total)
  // and nothing on the rail said a split was even happening. This takes
  // priority over every check below (unpriceable lines, nothing-unpaid):
  // both of those reason about payOrder's own uncovered-lines total, which
  // is not the figure governing the ticket right now. Close itself stays
  // enabled (set above): closing mid-split is allowed, per the product
  // owner's own ruling, gated by a confirm sheet at the click handler
  // instead of a disabled button here (handleCloseClick).
  // M15: undefined activeSplitPlan has two meanings until the read settles.
  // Keep ordinary payment unavailable until a successful response establishes
  // the ready state. A failed read offers only a retry of that GET.
  if (state.activeSplitPlanLoadState === "loading") {
    payButton.disabled = true;
    payButton.textContent = "Checking split…";
    payBlockReason.hidden = false;
    payBlockReason.textContent = "Checking for an active split.";
    return;
  }
  if (state.activeSplitPlanLoadState === "failed") {
    payButton.disabled = false;
    payButton.textContent = "Retry split check";
    payBlockReason.hidden = false;
    payBlockReason.textContent = "Couldn’t check for an active split. Tap to retry.";
    return;
  }

  const active = state.activeSplitPlan;
  if (active !== undefined) {
    const sharesCollected = active.shares.filter((share) => share.status === "captured").length;
    const shareCount = active.plan.share_count;
    const remainingCents = active.shares
      .filter((share) => share.status !== "captured")
      .reduce((sum, share) => sum + share.subtotal_cents + share.tax_cents, 0);
    payButton.disabled = false;
    payButton.textContent = "Resume split";
    payBlockReason.hidden = false;
    payBlockReason.textContent =
      `Split in progress: ${sharesCollected} of ${shareCount} shares collected, ` +
      `${formatCents(remainingCents)} remaining`;
    return;
  }

  const unpriceableCount = state.lines.filter((line) => line.priceable === false).length;
  if (unpriceableCount > 0) {
    // Design-gate fix (2026-08-20): a resumed line whose price could not
    // be reconstructed (TicketLineState.priceable) can still be Prepared,
    // and the ticket can still be Closed, but Pay stays blocked: the
    // total on screen already silently excludes this line's cost
    // (recomputeQuote only ever quotes priceable lines), and charging a
    // guest a total that quietly omits a drink is worse than refusing to
    // charge at all. Plain language, never the raw server rejection this
    // used to surface once the bartender tried Pay anyway.
    payButton.disabled = true;
    payBlockReason.hidden = false;
    payBlockReason.textContent =
      "One drink on this ticket has no price. A manager needs to fix the menu before this ticket can be paid. It can still be closed.";
    return;
  }

  // ALPHA-08 delta payments (owner ruling 2026-08-21): Pay is blocked
  // specifically when the server's live read says nothing is left
  // uncovered, never by a simple "already paid once this session" flag; a
  // paid tab that later gains new, genuinely uncovered lines becomes
  // payable again. Only fires once state.paymentPreview has actually
  // loaded (undefined means "not yet known," not "nothing to pay").
  // UX73-F1: a preview that has not seen every line cannot say nothing is
  // unpaid, so Pay stays blocked, and a tap cannot start a charge against
  // an unknown figure, until the read that knows the line lands.
  if (previewStale) {
    payButton.disabled = true;
    // UX74-F1: a failed read with nothing out reads as the failed note
    // with Try again; anything else stale is the waiting note.
    if (showPayFailureIfUnread()) {
      payBlockReason.hidden = true;
      return;
    }
    payBlockReason.hidden = false;
    payBlockReason.textContent = "Updating the amount.";
    return;
  }
  if (state.paymentPreview !== undefined && state.paymentPreview.uncoveredLines.length === 0) {
    payButton.disabled = true;
    payBlockReason.hidden = false;
    payBlockReason.textContent = "Nothing unpaid on this ticket.";
    // UX102: nothing is due, so the disabled "Pay $0.00" and its warning are not drawn.
    ticketActionsEl.classList.add("is-nothing-due");
    return;
  }
  payButton.disabled = false;
  payBlockReason.hidden = true;
  // Sixteenth amendment item 2: something is due again (a new line landed
  // on a previously fully-paid tab), so a stale "All paid. Close this
  // ticket?" prompt from an earlier payment must not keep sitting there.
  hideZeroDuePrompt();
  // A3, ANSWERED 2026-08-29. Pay is enabled (something is still
  // UNCOVERED) and would charge exactly $0.00 before any tip: a comp, a
  // discount, or the Walkout already zeroed the balance, and this ticket
  // is genuinely still open on screen, which by construction (src/domain/
  // close.ts's own CL1 tip gate, src/domain/tabs.ts's closeIfSettled)
  // means the tip question has not been addressed yet -- an already-
  // resolved zero-balance ticket would have closed itself and left this
  // screen. Never inferred from a typed amount: totalCents is the SAME
  // server-computed figure the Pay button's own label already shows.
  noTipButton.hidden = totalCents !== 0;
}

// ---------------------------------------------------------------------
// Sixteenth amendment item 2: the "All paid. Close this ticket?" prompt.
// Shown exactly once, from handlePayConfirmClick, at the moment a payment
// brings this ticket's uncovered set to zero (never from a general render
// path, so resuming an already-paid tab later never re-triggers it).
// Dismissible (dismiss just hides it, the tab stays open, matching the
// owner's "dismissible; dismiss keeps the tab open" ruling); hidden again
// whenever the ticket leaves this state (resetTicketState, or Pay becomes
// payable again in renderTicketActions above) so it never goes stale.
// ---------------------------------------------------------------------

// CL2, owner ruling 2026-08-28. Two states, and the difference matters
// on the floor:
//
//   selfClosed: the SERVER closed this ticket on the back of the command
//     that just succeeded. Nothing is being asked. The prompt says so and
//     offers one way onward, and the pay status stays on screen
//     underneath it so the bartender still sees what was charged and how
//     the tip split, which is the moment they actually need. Jumping
//     straight back to Open Tabs took that away.
//
//   not selfClosed: the ticket owes nothing but is deliberately still
//     open, which today means a comp or discount settled it and the guest
//     has not been offered a tip yet. That is the question this prompt
//     was written for and it keeps asking it.
function showZeroDuePrompt(options: { selfClosed: boolean }): void {
  zeroDuePromptEl.hidden = false;
  state.ticketSelfClosed = options.selfClosed;
  // UX102: a ticket the server closed no longer shows this block (the floor returns to Open Tabs), so it only ever
  // asks the question, for a settled ticket that is deliberately still open.
  zeroDuePromptText.textContent = "Nothing left to pay. Close this ticket?";
  zeroDueDismissButton.hidden = false;
  zeroDueCloseButton.textContent = "Close ticket";
  // UX53: the stranded-hold ticket banner's own visibility depends on
  // whether this prompt is showing and which state it is in (see that
  // function's own comment), so re-check it every time this changes.
  renderTicketManagerExceptionBanner();
}

function hideZeroDuePrompt(): void {
  zeroDuePromptEl.hidden = true;
  state.ticketSelfClosed = false;
  renderTicketManagerExceptionBanner();
}

function handleZeroDueDismissClick(): void {
  hideZeroDuePrompt();
}

// UX13-F1 (docs/backlog.md, DEC-026). Every OTHER call site above reaches
// showZeroDuePrompt off a write response that itself carries
// settlementClose info: a payment, a discount, a comp, or the Walkout
// discount actually bringing the balance to zero this session. A ticket
// REOPENED while its balance is already zero (money.tab.reopen, DEC-026's
// own manager-or-assistant-manager-approval path) takes none of those
// actions, so none of those triggers ever fire, and before this fix the
// floor had no route back to closed at all: UX13 removed the persistent
// Close Ticket button DEC-026 itself rules out restoring ("Close is a
// consequence, never a bartender action"). This reaches the SAME prompt
// instead of adding a second close control.
//
// Scoped to state.orderStatus === "reopened" specifically, the one status
// literal reopenOrder (src/domain/tabs.ts) ever writes (see
// payWarningRequiresAcknowledgment's identical read a few hundred lines
// down for the same literal, same reasoning). Never fires for a
// freshly created ticket ("open"): a brand-new, never-rung ticket's
// balance is trivially zero too, and prompting to close it the instant it
// is opened would be new behavior this item was never asked to add.
//
// Reads state.paymentPreview.uncoveredLines, the exact SERVER-computed
// figure every other Pay/Close gate in this file already reads (never a
// client-side recomputation of what is owed), so this can only ever agree
// with what closeOrder itself will separately re-check and enforce; it
// adds no new route to a balance-carrying close; a nonzero balance simply
// never reaches this call at all, and even if it somehow did, performClose
// still relays closeOrder's own CL1 refusal rather than closing anything.
// Also requires every line Prepared (notPreparedCount() === 0), matching
// P0.6's "Pay and Close stay blocked while an included drink is Not
// Prepared" for the one path this function adds.
function maybeShowZeroDueOnReopen(): void {
  if (state.orderStatus !== "reopened") {
    return;
  }
  if (state.paymentPreview === undefined || state.paymentPreview.uncoveredLines.length > 0) {
    return;
  }
  if (notPreparedCount() > 0) {
    return;
  }
  showZeroDuePrompt({ selfClosed: false });
}

function handleZeroDueCloseClick(): void {
  const selfClosed = state.ticketSelfClosed;
  hideZeroDuePrompt();
  if (selfClosed) {
    // CL2: already closed. There is nothing to command, only a screen to
    // leave.
    finishClosedTicket();
    return;
  }
  // Reuses the exact same Close path the ticket rail's own Close Ticket
  // button uses, rather than a second, narrower copy of that logic here.
  handleCloseClick();
}

// ---------------------------------------------------------------------
// Prepared
// ---------------------------------------------------------------------

async function handlePreparedClick(orderLineId: string): Promise<void> {
  const session = requireSession();
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  if (line === undefined || line.preparedState === "prepared") {
    return;
  }
  const preparationContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(preparationContext)) return;
  const result = await prepareLine(session.apiBaseUrl, session.tenantId, orderLineId, {
    idempotencyKey: crypto.randomUUID(),
    preparedQuantity: 1,
  });
  // CORE4: Pay no longer waits for this tap, so the ticket can be paid and closed while the tap is still out. The
  // screen has moved on; whatever the tap answered is no longer about anything shown.
  if (!floorSaleContextIsCurrent(preparationContext) || !state.lines.includes(line)) {
    return;
  }
  // CORE4: a payment can prepare the same drink a moment before this tap lands; the server then refuses the second
  // preparation (its inventory was posted once). The drink is Prepared, which is what the tap wanted, so the screen
  // catches up instead of showing an error.
  if (result.body.error === "line_already_prepared") {
    line.preparedState = "prepared";
    renderTicketLines();
    renderTicketActions();
    void refreshPaymentPreview();
    return;
  }
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not mark Prepared (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  line.preparedState = "prepared";
  line.inventoryPosted = result.body.inventoryPosted;
  // CORE1. The Prepared tap can be the command that settles a ticket (a comp or a Walkout left at zero while a drink
  // was still on the rail), and the server then closes it and says so here. Every other closing command sends the
  // floor back to Open Tabs on that answer; this one did not, so the order closed on the server while the ticket sat
  // on screen at "Pay $0.00" and then answered "already closed".
  if (settledAndClosed(result.body.settlementClose)) {
    finishClosedTicket();
    return;
  }
  renderTicketLines();
  renderTicketActions();
}

async function handlePrepareAllClick(): Promise<void> {
  const preparationContext = captureFloorSaleContext();
  if (!floorSaleContextIsCurrent(preparationContext)) return;
  const pending = state.lines.filter((line) => line.preparedState !== "prepared");
  for (const line of pending) {
    // CORE1: a tap that closed the ticket leaves the screen, so the remaining taps would be on a closed order.
    if (!floorSaleContextIsCurrent(preparationContext)) {
      return;
    }
    await handlePreparedClick(line.orderLineId);
  }
}

// ---------------------------------------------------------------------
// ALPHA-07 Open Tabs: list, resume, New Ticket, reopen
// ---------------------------------------------------------------------

/** The age badge text and state for one tab; the rules live in tabAge.ts (TABSLAYOUT1). */
function formatAge(createdAtIso: string): TabAge {
  return formatTabAge(tabAgeInMinutes(createdAtIso));
}

/** UX20 (docs/build-briefs/UX20-open-tabs-guest-names.md, DEC-114): the
 * one rule for how a tab is labeled, shared by the Open Tabs card and the
 * Recently closed card so the two never disagree. A typed name wins once
 * it is non-empty after trimming (a whitespace-only name does not count);
 * otherwise the ticket's own guests, in the order the server sent them;
 * otherwise "Walk-in", the word the ticket rail itself shows (LOUNGERMAJ1, lounger-08: it used to say "Unnamed tab" here
 * and "Walk-in" on the rail, so a closed ticket could not be matched to the one the guest meant). */
function tabDisplayLabel(name: string | null, guestLabels: readonly string[]): string {
  const trimmed = name?.trim() ?? "";
  if (trimmed.length > 0) {
    return trimmed;
  }
  if (guestLabels.length > 0) {
    return guestLabels.join(", ");
  }
  return "Walk-in";
}

const EMPTY_TAB_MATCH: TabMatch = {
  matches: true,
  inLabel: false,
  guestLabel: undefined,
  inOpener: false,
};

/** Appends `text` to `parent` with every typed word wrapped in <mark>.
 * Built from DOM nodes, never innerHTML, so tab text cannot inject markup. */
function appendHighlighted(parent: HTMLElement, text: string, tokens: readonly string[]): void {
  for (const segment of highlightSegments(text, tokens)) {
    if (segment.marked) {
      const mark = document.createElement("mark");
      mark.textContent = segment.text;
      parent.append(mark);
    } else {
      parent.append(document.createTextNode(segment.text));
    }
  }
}

/** TAB1: the one place that writes a card's name, why-line and meta line,
 * so a search can re-highlight a card in place without rebuilding it (its
 * figures, Paid badge and merge marks stay). The opener shows on every
 * card; when the typed words matched the opener the meta line says so
 * ("Opened by ..."). */
function renderTabCardText(
  card: HTMLElement,
  order: OpenTabDto,
  match: TabMatch,
  tokens: readonly string[],
): void {
  const label = tabDisplayLabel(order.name, order.guest_labels);
  const nameEl = card.querySelector<HTMLElement>(".tab-name");
  const whyEl = card.querySelector<HTMLElement>(".tab-why");
  const metaEl = card.querySelector<HTMLElement>(".tab-server");
  if (nameEl === null || whyEl === null || metaEl === null) {
    return;
  }
  nameEl.replaceChildren();
  appendHighlighted(nameEl, label, tokens);
  // LOCFLOOR1 (sweep B16): a long name wraps to two lines so similar tabs can be told apart; the title keeps the whole name
  // for the rare name that runs past two.
  nameEl.title = label;

  whyEl.replaceChildren();
  const showGuestReason = match.guestLabel !== undefined && !match.inLabel;
  whyEl.hidden = !showGuestReason;
  if (showGuestReason && match.guestLabel !== undefined) {
    whyEl.append(document.createTextNode("Guest: "));
    appendHighlighted(whyEl, match.guestLabel, tokens);
  }

  metaEl.replaceChildren();
  if (order.opened_by_name !== null && order.opened_by_name !== undefined) {
    const opener = document.createElement("span");
    opener.dataset["testid"] = "tab-opener";
    if (match.inOpener) {
      opener.append(document.createTextNode("Opened by "));
    }
    appendHighlighted(opener, order.opened_by_name, match.inOpener ? tokens : []);
    metaEl.append(opener);
  }
  // TABSLAYOUT1: the server line always holds its space, so every card is the same height.
}

/** TAB3: whether each open tab's balance is zero, learned from the one summaries read. A tab with no entry is
 * unknown and matches neither status chip. Cleared on every list load and at sign-out. */
const tabPaidByOrderId = new Map<string, boolean>();
/** RCPT1-S3: which open tabs are authorized and waiting for a tip, from the same summaries read. */
const tabAwaitingByOrderId = new Map<string, boolean>();
/** SHIFT1: drinks on each open tab, from the same read. A tab with none owes nothing, so the sign-out sheet skips it. */
const tabDrinkCountByOrderId = new Map<string, number>();

function currentTabChips(): TabChipFilters {
  return { status: state.tabStatusFilter, over1hr: state.tabOver1Hr };
}

/** The same rounded minutes formatAge uses for its aging badge, so Over 1 hr and the badge never disagree. */
function tabAgeMinutes(createdAtIso: string): number {
  return tabAgeInMinutes(createdAtIso);
}

function searchableTab(order: OpenTabDto): SearchableTab {
  return {
    paid: tabPaidByOrderId.get(order.id),
    awaitingTip: tabAwaitingByOrderId.get(order.id) === true,
    ageMinutes: tabAgeMinutes(order.created_at),
    label: tabDisplayLabel(order.name, order.guest_labels),
    guestLabels: order.guest_labels,
    openedByUserId: order.opened_by_user_id ?? null,
    openedByName: order.opened_by_name ?? null,
  };
}

function buildTabFindEmpty(
  heading: string,
  help: string,
  actions: Array<{ label: string; primary: boolean; testid: string; onTap: () => void }>,
): void {
  tabFindEmptyEl.replaceChildren();
  const h2 = document.createElement("h2");
  h2.textContent = heading;
  const p = document.createElement("p");
  p.textContent = help;
  p.hidden = help.length === 0;
  const row = document.createElement("div");
  row.className = "tab-find-empty-actions";
  row.hidden = actions.length === 0;
  for (const action of actions) {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = action.label;
    button.dataset["testid"] = action.testid;
    if (action.primary) {
      button.classList.add("is-primary");
    }
    button.addEventListener("click", action.onTap);
    row.append(button);
  }
  tabFindEmptyEl.append(h2, p, row);
}

function setTabFilterMode(mode: TabFilterMode): void {
  state.tabFilterMode = mode;
  applyTabSearch();
}

/** TAB3: Unpaid and Paid are a single select, and tapping the pressed one clears it. */
function toggleTabStatusChip(status: "unpaid" | "awaiting" | "paid"): void {
  state.tabStatusFilter = state.tabStatusFilter === status ? undefined : status;
  applyTabSearch();
}

function toggleTabOver1HrChip(): void {
  state.tabOver1Hr = !state.tabOver1Hr;
  applyTabSearch();
}

function clearTabSearch(): void {
  state.tabSearchText = "";
  tabSearchInput.value = "";
  applyTabSearch();
}

/** TAB1 (DEC-270): re-runs the find row over the loaded tabs. Called on
 * every input event, every toggle tap, and after every data load (a merge
 * reloads the list, so it is covered too). Cards are never rebuilt here:
 * hidden, re-highlighted in place. A card hidden by the filter that is a
 * merge mark stays marked in state (mergeSourceOrderIds/mergeTargetOrderId are
 * untouched), and the merge action bar still names the receiving tab. */
function applyTabSearch(): void {
  const tabs = state.openTabs;
  tabFindEl.hidden = tabs.length === 0;
  if (tabs.length === 0) {
    tabFindEmptyEl.hidden = true;
    return;
  }
  const tokens = tokenizeSearch(state.tabSearchText);
  const userId = sessionOrUndefined()?.userId;
  const result = filterTabs(
    tabs.map(searchableTab),
    state.tabSearchText,
    state.tabFilterMode,
    userId,
    currentTabChips(),
  );
  tabChipUnpaidCountEl.textContent = String(result.unpaidCount);
  tabChipPaidCountEl.textContent = String(result.paidCount);
  tabChipAwaitingCountEl.textContent = String(result.awaitingCount);
  tabChipAwaitingButton.setAttribute("aria-pressed", String(state.tabStatusFilter === "awaiting"));
  tabChipOver1HrCountEl.textContent = String(result.over1hrCount);
  tabChipUnpaidButton.setAttribute("aria-pressed", String(state.tabStatusFilter === "unpaid"));
  tabChipPaidButton.setAttribute("aria-pressed", String(state.tabStatusFilter === "paid"));
  tabChipOver1HrButton.setAttribute("aria-pressed", String(state.tabOver1Hr));
  const anyChipPressed = [
    tabChipUnpaidButton,
    tabChipAwaitingButton,
    tabChipPaidButton,
    tabChipOver1HrButton,
  ].some((chip) => chip.getAttribute("aria-pressed") === "true");
  // TABSLAYOUT1 section 4: a zero chip goes quiet but stays tappable; Awaiting tip is hidden at zero unless pressed,
  // so a pressed chip whose count dropped to zero stays on screen to be tapped clear.
  for (const [button, count] of [
    [tabChipUnpaidButton, result.unpaidCount],
    [tabChipAwaitingButton, result.awaitingCount],
    [tabChipPaidButton, result.paidCount],
    [tabChipOver1HrButton, result.over1hrCount],
  ] as const) {
    // UIFIX5: in the default view a chip with nothing behind it is not drawn. Once a chip is pressed, Mine is chosen or
    // a search is typed the counts follow that choice, so every chip is drawn (quiet at zero) and stays the way to change it.
    const quiet = count === 0 && button.getAttribute("aria-pressed") !== "true";
    button.classList.toggle("is-zero", quiet);
    button.hidden =
      quiet && !anyChipPressed && state.tabSearchText.length === 0 && state.tabFilterMode === "all";
  }
  tabChipAwaitingButton.hidden = result.awaitingCount === 0 && state.tabStatusFilter !== "awaiting";
  // HIGFIX2 (F-14): a status chip whose count repeats the All count is the same list, so it is not drawn unless pressed.
  for (const [button, count] of [
    [tabChipUnpaidButton, result.unpaidCount],
    [tabChipPaidButton, result.paidCount],
  ] as const) {
    if (count > 0 && count === result.allCount && button.getAttribute("aria-pressed") !== "true") {
      button.hidden = true;
    }
  }
  tabFilterAllCountEl.textContent = String(result.allCount);
  tabFilterMineCountEl.textContent = String(result.mineCount);
  tabFilterAllButton.setAttribute("aria-pressed", String(state.tabFilterMode === "all"));
  tabFilterMineButton.setAttribute("aria-pressed", String(state.tabFilterMode === "mine"));
  tabSearchClearButton.hidden = state.tabSearchText.length === 0;

  let shown = 0;
  tabs.forEach((order, index) => {
    const card = openTabsListEl.querySelector<HTMLElement>(
      `[data-order-id="${CSS.escape(order.id)}"]`,
    );
    if (card === null) {
      return;
    }
    const visible = result.visible[index] === true;
    card.hidden = !visible;
    if (visible) {
      shown += 1;
    }
    renderTabCardText(card, order, result.matchByIndex[index] ?? EMPTY_TAB_MATCH, tokens);
  });

  tabFindEmptyEl.hidden = shown > 0;
  if (shown > 0) {
    return;
  }
  const typed = state.tabSearchText.trim();
  if (typed.length === 0 && (state.tabStatusFilter !== undefined || state.tabOver1Hr)) {
    // TAB3: a chip emptied the list. The chips stay on screen and are the way back.
    buildTabFindEmpty("No tabs match this filter", "", []);
    return;
  }
  const showAll = {
    label: "Show all tabs",
    primary: false,
    testid: "tab-find-show-all",
    onTap: () => setTabFilterMode("all"),
  };
  if (typed.length === 0) {
    buildTabFindEmpty(
      "You have no open tabs",
      "Tabs you open show up here. Switch to All to see everyone's.",
      [{ ...showAll, primary: true }],
    );
  } else if (state.tabFilterMode === "mine") {
    buildTabFindEmpty(
      `No tabs match "${typed}"`,
      "Nothing of yours matches. Try All, or a different name.",
      [
        showAll,
        {
          label: "Clear search",
          primary: true,
          testid: "tab-find-clear-search",
          onTap: clearTabSearch,
        },
      ],
    );
  } else {
    buildTabFindEmpty(
      `No tabs match "${typed}"`,
      "Check the spelling, or try a guest or employee name.",
      [
        {
          label: "Clear search",
          primary: true,
          testid: "tab-find-clear-search",
          onTap: clearTabSearch,
        },
      ],
    );
  }
}

function handleTabSearchInput(): void {
  state.tabSearchText = tabSearchInput.value;
  applyTabSearch();
}

function handleTabSearchFocus(): void {
  // Scroll the find row to the top of the viewport so the header scrolls
  // away and the list gets the room the on-screen keyboard leaves.
  tabFindEl.scrollIntoView({ block: "start" });
}

function resetTabFind(): void {
  state.tabFilterMode = "all";
  state.tabSearchText = "";
  state.tabStatusFilter = undefined;
  state.tabOver1Hr = false;
  tabPaidByOrderId.clear();
  tabAwaitingByOrderId.clear();
  tabDrinkCountByOrderId.clear();
  tabSearchInput.value = "";
  tabFindEl.hidden = true;
  tabFindEmptyEl.hidden = true;
}

function buildTabCard(order: OpenTabDto): HTMLButtonElement {
  const card = document.createElement("button");
  card.type = "button";
  card.className = "tab-card";
  card.dataset["testid"] = "open-tab-card";
  card.dataset["orderId"] = order.id;

  const main = document.createElement("div");
  main.className = "tab-main";
  const name = document.createElement("span");
  name.className = "tab-name";
  const server = document.createElement("div");
  server.className = "tab-server";
  // TABSLAYOUT1: one badge row under the server line: age first, then Paid or Awaiting tip.
  const badges = document.createElement("div");
  badges.className = "tab-badges";

  // Sixteenth amendment item 2: a Paid badge, existing green success
  // tokens, hidden until updateTabCardFigures learns this tab's balance
  // is actually zero (checkFullyPaid, via summarizeTab).
  const paidBadge = document.createElement("span");
  paidBadge.className = "badge-paid";
  paidBadge.dataset["testid"] = "tab-paid-badge";
  paidBadge.textContent = "Paid";
  paidBadge.hidden = true;
  // RCPT1-S3: shown for a tab whose card is authorized and which is waiting for the tip written on its receipt.
  const awaitingBadge = document.createElement("span");
  awaitingBadge.className = "badge-awaiting";
  awaitingBadge.dataset["testid"] = "tab-awaiting-badge";
  awaitingBadge.textContent = "Awaiting tip";
  awaitingBadge.hidden = true;

  const age = formatAge(order.created_at);
  const pill = document.createElement("span");
  pill.className = age.stale ? "badge-aging badge-stale" : age.aging ? "badge-aging" : "badge-age";
  pill.dataset["testid"] = "tab-age-badge";
  pill.textContent = age.text;
  // LOCFLOOR1 (sweep B16): a ticket opened this minute carries no age chip; "0 min" on every new card is noise.
  pill.hidden = tabAgeInMinutes(order.created_at) < 1;
  badges.append(pill, paidBadge, awaitingBadge);

  // TAB1: the "why it matched" line, shown only while a typed word matched
  // a guest label and not the visible name. Filled by renderTabCardText.
  const why = document.createElement("div");
  why.className = "tab-why";
  why.dataset["testid"] = "tab-match-reason";
  why.hidden = true;
  // TM1: a two-line "Merged from" trail on a tab that absorbed another
  // one, per the owner-approved result mockup. Read directly off the
  // order row (order.merge_trail is populated by loadOpenTabs from the
  // most recent tab_merges row targeting this order, if any); absent for
  // every ordinary tab.
  // UX102 (click-through review, Open Tabs merge trail): the trail sits under the age line, not above the card
  // title it used to crowd.
  main.append(name, server, badges, why);
  if (order.merge_trail !== undefined && order.merge_trail !== null) {
    const trail = document.createElement("span");
    trail.className = "tab-merge-trail";
    trail.dataset["testid"] = "tab-merge-trail";
    trail.textContent = order.merge_trail;
    main.append(trail);
  }
  card.append(main);
  renderTabCardText(card, order, EMPTY_TAB_MATCH, []);

  const figures = document.createElement("div");
  figures.className = "tab-figures";
  const figure = document.createElement("div");
  figure.className = "figure";
  const value = document.createElement("div");
  value.className = "figure-value";
  value.dataset["testid"] = "tab-figure-value";
  value.textContent = "…";
  const label = document.createElement("div");
  label.className = "figure-label";
  label.dataset["testid"] = "tab-figure-label";
  label.textContent = "loading";
  figure.append(value, label);
  figures.append(figure);
  card.append(figures);

  const chevron = document.createElement("span");
  chevron.className = "chevron";
  chevron.setAttribute("aria-hidden", "true");
  chevron.textContent = "›";
  card.append(chevron);

  // TM1: a mark badge (1 = source, will close; 2 = target, stays open)
  // and a plain-language role label, shown only in merge mode on a
  // marked card. Built empty here; renderTabMergeSelectionState fills it
  // in on every render, so a single source of truth decides what a card
  // shows instead of this constructor guessing at mode state that can
  // change after the card is built.
  const markBadge = document.createElement("span");
  markBadge.className = "tab-merge-mark-badge";
  markBadge.dataset["testid"] = "tab-merge-mark-badge";
  markBadge.hidden = true;
  card.append(markBadge);
  const roleLabel = document.createElement("span");
  roleLabel.className = "tab-merge-role-label";
  roleLabel.dataset["testid"] = "tab-merge-role-label";
  roleLabel.hidden = true;
  main.append(roleLabel);

  card.addEventListener("click", () => {
    if (state.tabMergeModeActive) {
      handleTabMergeCardClick(order);
      return;
    }
    void handleResumeTabClick(order);
  });
  return card;
}

// ---------------------------------------------------------------------
// TM1 (docs/backlog.md; DEC-230, owner ruling "merge only"). Open Tabs
// merge-selection mode: a dedicated toggle (never a gesture-only path,
// the touch-only rule's own reason), tap-order picks source then target,
// a confirm sheet states what moves and the source tab's real hold
// outcome (DEC-248 corrects the owner-approved mockup's own wording
// against src/domain/tabs.ts's real hold lifecycle), then the merge
// commits over POST .../orders/:orderId/merge-into.
// ---------------------------------------------------------------------

function tabLabelById(orderId: string): string {
  const order = state.openTabs.find((candidate) => candidate.id === orderId);
  return order === undefined ? "this tab" : tabDisplayLabel(order.name, order.guest_labels);
}

/** TAB2: the most tabs one merge may close into the receiving tab. The server refuses more
 * (src/domain/tabMerge.ts MAX_MERGE_SOURCES); this copy only stops the tap early with a plain message. */
const MAX_MERGE_SOURCES = 20;

/** The loaded balance of each open tab, filled as each card's figures arrive, so the merge footer can
 * total the tabs it names. A tab whose total is unknown is absent: the footer then shows no total
 * rather than a partial one. */
const tabTotalCentsByOrderId = new Map<string, number>();

function tabsWord(count: number): string {
  return count === 1 ? "1 tab" : `${count} tabs`;
}

/** The one place that decides what every tab card looks like in merge
 * mode: dimmed (not part of this merge), marked as the receiving tab (badge 1),
 * marked as a tab that joins it (badge 2, 3, ... in tap order), or plain (mode is
 * off). Called after every render of #open-tabs-list and after every
 * mark/unmark, so the DOM never drifts from state.mergeTargetOrderId and
 * state.mergeSourceOrderIds. */
function renderTabMergeSelectionState(): void {
  tabMergeToggleButton.setAttribute("aria-pressed", String(state.tabMergeModeActive));
  tabMergeModeBanner.hidden = !state.tabMergeModeActive;

  const targetId = state.mergeTargetOrderId;
  const sourceIds = state.mergeSourceOrderIds;
  const anyMarked = targetId !== undefined || sourceIds.length > 0;
  const cards = openTabsListEl.querySelectorAll<HTMLElement>('[data-testid="open-tab-card"]');
  for (const card of cards) {
    const orderId = card.dataset["orderId"];
    const markBadge = card.querySelector<HTMLElement>('[data-testid="tab-merge-mark-badge"]');
    const roleLabel = card.querySelector<HTMLElement>('[data-testid="tab-merge-role-label"]');
    card.classList.remove("tab-merge-dimmed", "tab-merge-marked");
    if (markBadge !== null) {
      markBadge.hidden = true;
    }
    if (roleLabel !== null) {
      roleLabel.hidden = true;
      roleLabel.textContent = "";
    }
    if (!state.tabMergeModeActive) {
      continue;
    }
    const sourceIndex = orderId === undefined ? -1 : sourceIds.indexOf(orderId);
    if (orderId !== undefined && orderId === targetId) {
      card.classList.add("tab-merge-marked");
      if (markBadge !== null) {
        markBadge.hidden = false;
        markBadge.textContent = "1";
      }
      if (roleLabel !== null) {
        roleLabel.hidden = false;
        roleLabel.textContent = "Receives";
      }
    } else if (sourceIndex >= 0) {
      card.classList.add("tab-merge-marked");
      if (markBadge !== null) {
        markBadge.hidden = false;
        markBadge.textContent = String(sourceIndex + 2);
      }
      if (roleLabel !== null) {
        roleLabel.hidden = false;
        roleLabel.textContent =
          targetId === undefined ? "Will close" : `Will close, joins ${tabLabelById(targetId)}`;
      }
    } else if (anyMarked) {
      card.classList.add("tab-merge-dimmed");
    }
  }

  if (state.tabMergeModeActive && targetId !== undefined && sourceIds.length > 0) {
    tabMergeActionBar.hidden = false;
    let summary = `Merging ${tabsWord(sourceIds.length)} into ${tabLabelById(targetId)}`;
    const named = [targetId, ...sourceIds];
    if (named.every((id) => tabTotalCentsByOrderId.has(id))) {
      const combined = named.reduce((sum, id) => sum + (tabTotalCentsByOrderId.get(id) ?? 0), 0);
      summary += `, combined total ${formatCents(combined)}`;
    }
    tabMergeActionBarSummary.textContent = summary;
  } else {
    tabMergeActionBar.hidden = true;
  }
}

function clearTabMergeSelection(): void {
  state.mergeSourceOrderIds = [];
  state.mergeTargetOrderId = undefined;
}

function handleTabMergeToggleClick(): void {
  state.tabMergeModeActive = !state.tabMergeModeActive;
  clearTabMergeSelection();
  renderTabMergeSelectionState();
}

function handleTabMergeModeCancelClick(): void {
  state.tabMergeModeActive = false;
  clearTabMergeSelection();
  renderTabMergeSelectionState();
}

/** Tap order decides the role (TAB2, DEC-340): the first tap marks the tab that RECEIVES everything,
 * every later tap adds a tab that closes into it, as many as the bartender likes (up to
 * MAX_MERGE_SOURCES). Tapping a marked tab unselects it. Unselecting the receiving tab promotes the
 * earliest remaining tab to receive, so the selection is never left without a receiver while tabs
 * are still marked. */
function handleTabMergeCardClick(order: OpenTabDto): void {
  if (tabAwaitingByOrderId.get(order.id) === true) {
    showToast(AWAITING_TIP_FROZEN_MESSAGE, "danger");
    return;
  }
  if (order.id === state.mergeTargetOrderId) {
    state.mergeTargetOrderId = state.mergeSourceOrderIds[0];
    state.mergeSourceOrderIds = state.mergeSourceOrderIds.slice(1);
  } else if (state.mergeSourceOrderIds.includes(order.id)) {
    state.mergeSourceOrderIds = state.mergeSourceOrderIds.filter((id) => id !== order.id);
  } else if (state.mergeTargetOrderId === undefined) {
    state.mergeTargetOrderId = order.id;
  } else if (state.mergeSourceOrderIds.length >= MAX_MERGE_SOURCES) {
    showToast(
      `A merge can take at most ${MAX_MERGE_SOURCES} tabs into one. Merge the rest in a second step.`,
      "danger",
    );
    return;
  } else {
    state.mergeSourceOrderIds = [...state.mergeSourceOrderIds, order.id];
  }
  renderTabMergeSelectionState();
}

function tabMergeHoldNoteFor(
  outcome: TabMergeDto["source_hold_outcome"],
  sourceLabel: string,
): string | undefined {
  switch (outcome) {
    case "released":
      return `${sourceLabel}'s hold releases now, since it closes with nothing owed.`;
    case "stranded":
    case "release_failed":
      return `${sourceLabel}'s hold needs a manager to clear. See Manager Exceptions.`;
    case "none":
      return undefined;
  }
}

/** TAB2: the hold sentence for a merge of several tabs, from the ledger rows the server returned. */
function tabMergeHoldNoteForMany(merges: TabMergeDto[]): string | undefined {
  const needsManager = merges.filter(
    (merge) =>
      merge.source_hold_outcome === "stranded" || merge.source_hold_outcome === "release_failed",
  ).length;
  const released = merges.filter((merge) => merge.source_hold_outcome === "released").length;
  const parts: string[] = [];
  if (released > 0) {
    parts.push(`${released === 1 ? "1 hold releases" : `${released} holds release`} now.`);
  }
  if (needsManager > 0) {
    parts.push(
      `${needsManager === 1 ? "1 hold needs" : `${needsManager} holds need`} a manager to clear. See Manager Exceptions.`,
    );
  }
  return parts.length === 0 ? undefined : parts.join(" ");
}

// GATE-CLEANUP-1 (check:chrome-ratchet, ADR-004 Decision 5): reuses the
// shared confirm/choice sheet (openChoiceSheet, #choice-scrim) rather than
// a dedicated #tab-merge-confirm-scrim. GATE4 follow-up (owner rejection of
// the first rework's visual regression, which rendered the "what moves"
// rows as tappable-looking green .choice-option-done buttons and "Merge
// tabs" as one more list row instead of the primary action): uses the
// sheet's own informational-body mode instead (infoRows/infoNote/
// primaryAction), which restores the owner-approved order and styling --
// subtitle, three neutral (never green, never button-shaped)
// .choice-detail-row facts, the info note below them, then a paired
// Cancel | Merge tabs footer, the same .admin-confirm-actions shape
// #admin-confirm-scrim already established for admin's own
// destructive-confirm sheet.
function openTabMergeConfirmSheet(): void {
  const sourceIds = [...state.mergeSourceOrderIds];
  const targetId = state.mergeTargetOrderId;
  if (targetId === undefined || sourceIds.length === 0) {
    return;
  }
  const targetLabel = tabLabelById(targetId);
  // One tab keeps the original wording; several name the count (TAB2, DEC-340).
  const single = sourceIds.length === 1;
  const sourcesLabel = single ? tabLabelById(sourceIds[0] ?? "") : tabsWord(sourceIds.length);
  openChoiceSheet({
    title: `Merge ${sourcesLabel} into ${targetLabel}?`,
    subtitle: single
      ? `${sourcesLabel} closes. Everything on it moves to ${targetLabel}.`
      : `${sourcesLabel} close. Everything on them moves to ${targetLabel}.`,
    infoRows: [
      {
        label: "Lines",
        detail: "Every drink moves, including its prepared status",
        testid: "tab-merge-move-row",
      },
      {
        label: "Guests",
        detail: `Guests move onto ${targetLabel}`,
        testid: "tab-merge-move-row",
      },
      {
        label: "Comps",
        detail: single
          ? "Any comp or discount on this tab moves with it"
          : "Any comp or discount on these tabs moves with them",
        testid: "tab-merge-move-row",
      },
    ],
    // The hold outcome is not known until the merge actually runs (it
    // depends on the source's live balance and the configured provider at
    // that moment), so before Confirm this can only state the GENERAL rule
    // rather than a computed outcome, unlike the built result which shows
    // the real recorded outcome. Never the owner-approved mockup's own
    // (incorrect) "both holds stay open until whichever card pays" wording;
    // see DEC-248.
    infoNote: single
      ? `If ${sourcesLabel} has a card hold, it releases as soon as this merge closes it, since ` +
        "nothing will be owed on it anymore."
      : "If any of these tabs has a card hold, it releases as soon as this merge closes the tab, since " +
        "nothing will be owed on it anymore.",
    primaryAction: {
      id: "merge",
      label: "Merge tabs",
      variant: "danger",
      testid: "tab-merge-confirm-merge",
    },
    onPick: (id) => {
      if (id === "merge") {
        void handleTabMergeConfirmClick(sourceIds, targetId, sourcesLabel, targetLabel);
      }
    },
  });
}

async function handleTabMergeConfirmClick(
  sourceIds: string[],
  targetId: string,
  sourcesLabel: string,
  targetLabel: string,
): Promise<void> {
  const session = requireSession();
  const result = await mergeManyTabs(session.apiBaseUrl, session.tenantId, targetId, {
    idempotencyKey: crypto.randomUUID(),
    sourceOrderIds: sourceIds,
  });
  if (result.status !== 200 && result.status !== 201) {
    // TM1: routed through payCloseErrorMessages.ts's own known-message
    // map (KNOWN_PAY_CLOSE_ERROR_MESSAGES's tab_merge_refused entry),
    // never the raw server message, which names an order by its UUID.
    // "close" is passed only because translatePayCloseError's own
    // signature requires an action; the merge entry ignores it.
    const translated = translatePayCloseError("close", result.body, result.status);
    showToast(translated.plain, "danger");
    return;
  }
  state.tabMergeModeActive = false;
  clearTabMergeSelection();
  const merges: TabMergeDto[] = result.body.merges;
  const holdNote =
    merges.length === 1 && merges[0] !== undefined
      ? tabMergeHoldNoteFor(merges[0].source_hold_outcome, sourcesLabel)
      : tabMergeHoldNoteForMany(merges);
  showToast(
    holdNote === undefined
      ? `Merged ${sourcesLabel} into ${targetLabel}.`
      : `Merged ${sourcesLabel} into ${targetLabel}. ${holdNote}`,
    "success",
  );
  await loadOpenTabs();
}

function updateTabCardFigures(orderId: string, summary: TabSummary): void {
  const card = openTabsListEl.querySelector<HTMLElement>(
    `[data-order-id="${CSS.escape(orderId)}"]`,
  );
  if (card === null) {
    return;
  }
  const value = card.querySelector<HTMLElement>('[data-testid="tab-figure-value"]');
  const label = card.querySelector<HTMLElement>('[data-testid="tab-figure-label"]');
  if (value !== null) {
    value.textContent = summary.totalCents === undefined ? "-" : formatCents(summary.totalCents);
  }
  if (label !== null) {
    const drinkWordBase = `${summary.drinkCount} drink${summary.drinkCount === 1 ? "" : "s"}`;
    const drinkWord =
      summary.ticketTotalCents !== undefined &&
      summary.totalCents !== undefined &&
      summary.ticketTotalCents !== summary.totalCents
        ? `${drinkWordBase} · whole ticket ${formatCents(summary.ticketTotalCents)}`
        : drinkWordBase;
    label.textContent =
      summary.unpricedCount > 0
        ? `${drinkWord} · check pricing`
        : summary.awaitingTip === true
          ? `${drinkWord} · authorized`
          : drinkWord;
  }
  const paidBadge = card.querySelector<HTMLElement>('[data-testid="tab-paid-badge"]');
  if (paidBadge !== null) {
    paidBadge.hidden = !summary.fullyPaid;
  }
  const awaitingBadge = card.querySelector<HTMLElement>('[data-testid="tab-awaiting-badge"]');
  if (awaitingBadge !== null) {
    awaitingBadge.hidden = summary.awaitingTip !== true;
  }
}

/** REFUNDFIND1: replaces the Recently closed list with the server's tickets for this location's current business day.
 * A failed read leaves whatever this login already knows rather than blanking the list. */
async function refreshRecentlyClosedFromServer(gen: number): Promise<void> {
  const session = sessionOrUndefined();
  if (session === undefined) return;
  const result = await listRecentlyClosedTabs(session.apiBaseUrl, session.tenantId);
  if (!sessionIsCurrent(gen) || result.status !== 200) return;
  state.recentlyClosedTabs = result.body.tabs.map((tab) => ({
    orderId: tab.id,
    label: tabDisplayLabel(tab.name, tab.guestLabels),
    closedAt: Date.parse(tab.closedAt),
    totalCents: tab.totalCents,
    ...(tab.tipCents > 0 ? { tipCents: tab.tipCents } : {}),
    ...(tab.closedByName === null ? {} : { closedBy: tab.closedByName }),
  }));
  renderRecentlyClosed();
}

function renderRecentlyClosed(): void {
  recentlyClosedSection.hidden = state.recentlyClosedTabs.length === 0;
  recentlyClosedListEl.innerHTML = "";
  for (const entry of state.recentlyClosedTabs) {
    const row = document.createElement("div");
    row.className = "tab-card";
    row.dataset["testid"] = "recently-closed-card";
    row.dataset["orderId"] = entry.orderId;

    const main = document.createElement("div");
    main.className = "tab-main";
    const nameRow = document.createElement("div");
    nameRow.className = "tab-name-row";
    const name = document.createElement("span");
    name.className = "tab-name";
    name.textContent = entry.label;
    nameRow.append(name);
    main.append(nameRow);
    const meta = document.createElement("div");
    meta.className = "tab-meta";
    // UX102: the total and the time it closed, not a sentence. LOUNGERMAJ1 (lounger-08): the total is what was charged,
    // tip included, with the tip named after the time.
    const closedTime = new Date(entry.closedAt).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
    meta.textContent =
      entry.totalCents === undefined
        ? closedTime
        : `${formatCents(entry.totalCents)} · ${closedTime}${
            entry.tipCents === undefined ? "" : ` · ${formatCents(entry.tipCents)} tip`
          }`;
    main.append(meta);
    // CLOSEDVIEW1: tapping the card opens the ticket read only. Reopen below stays its own deliberate action.
    const openButton = document.createElement("button");
    openButton.type = "button";
    openButton.className = "tab-open-closed";
    openButton.dataset["testid"] = "recently-closed-open";
    openButton.setAttribute("aria-label", `View ${entry.label}, closed`);
    openButton.append(main);
    openButton.addEventListener("click", () => void openClosedTicketView(entry));
    row.append(openButton);

    // AU14-F4 / DEC-276: the control follows the permission snapshot. A role
    // denied money.tab.reopen never sees Reopen (the server refuses it anyway);
    // approval_required and allowed both see it.
    // REFUNDALL1: a closed ticket is refunded from its card, with no reopen.
    if (state.session !== undefined && hasPermission(state.session, "money.refund")) {
      const refundButton = document.createElement("button");
      refundButton.type = "button";
      refundButton.className = "btn-reopen";
      refundButton.dataset["testid"] = "recently-closed-refund";
      refundButton.textContent = "Refund";
      refundButton.addEventListener(
        "click",
        () => void openRefundTicketSheet(entry.orderId, entry.label),
      );
      row.append(refundButton);
    }
    const reopenSession = state.session;
    if (reopenSession !== undefined && hasPermission(reopenSession, REOPEN_TAB_PERMISSION)) {
      const reopenButton = document.createElement("button");
      reopenButton.type = "button";
      reopenButton.className = "btn-reopen";
      reopenButton.dataset["testid"] = "reopen-tab";
      reopenButton.textContent = "Reopen";
      reopenButton.addEventListener("click", () => void handleReopenClick(entry));
      row.append(reopenButton);
    }

    recentlyClosedListEl.append(row);

    // RCPT2 (DEC-403): under a closed tab, where its emailed receipt went and a Resend for a manager. Hidden when the
    // ticket has no emailed receipt or the session may not see receipts.
    const receiptRoot = document.createElement("div");
    receiptRoot.className = "rcptrow";
    receiptRoot.dataset["testid"] = "recently-closed-receipt";
    recentlyClosedListEl.append(receiptRoot);
    void mountTicketReceiptRow(receiptRoot, {
      apiBaseUrl: () => requireSession().apiBaseUrl,
      tenantId: () => requireSession().tenantId,
      canView: () =>
        state.session !== undefined && hasPermission(state.session, "money.signature.view"),
      canResend: () =>
        state.session !== undefined && hasPermission(state.session, "money.receipt.resend"),
      isCurrent: () => receiptRoot.isConnected && state.session !== undefined,
    }).show(entry.orderId, "closed");
  }
}

/** PERFSIGNIN1: how many Open Tabs loads are running. A caller that only wants the list to be current (the remembered
 * terminal applying right after sign-in) skips when one is already on its way rather than starting a second full pass. */
let openTabsLoadsInFlight = 0;

async function loadOpenTabs(): Promise<void> {
  openTabsLoadsInFlight += 1;
  try {
    await runOpenTabsLoad();
  } finally {
    openTabsLoadsInFlight -= 1;
  }
}

async function runOpenTabsLoad(): Promise<void> {
  // AU13-F1: this loader is reachable with the session already gone
  // before its own first line runs (completeLoginAndShowOpenTabs and
  // resumeFromStoredConfig both run `loadMenu()` and `loadOpenTabs()` together,
  // and loadMenu can now return normally, having discarded
  // its own stale response, with the session already cleared). requireSession()
  // would throw synchronously here; sessionOrUndefined() bails out quietly
  // instead, matching every other checkpoint in this function.
  const session = sessionOrUndefined();
  if (session === undefined) {
    return;
  }
  // AU13-F1: captured before the first await below.
  const gen = sessionGeneration;
  // AU8: the Open Tabs badge's own live count, loaded alongside the tabs
  // list every time this screen's data refreshes.
  void refreshManagerExceptions();
  void refreshMenuCounts();
  void refreshStartingStockMenu();
  void cashDrawer.refreshEntry();
  renderRecentlyClosed();
  // UX77 (PO7-S1 designer finding): GET /tabs needs pos.tab.view_all. A session
  // without it (a view-only inventory role) would only be refused, and the
  // refusal toasted over whatever screen it had moved on to (the order detail).
  // Resolve the permission the call needs and do not make it without it.
  if (!hasPermission(session, "pos.tab.view_all")) {
    state.openTabs = [];
    openTabsListEl.innerHTML =
      '<p class="tabs-empty">The open tabs list is not part of your access.</p>';
    return;
  }
  // REFUNDFIND1: the closed tickets of the business day come from the server, so they survive sign-out and reload and
  // show for anyone with the access, whoever closed them.
  void refreshRecentlyClosedFromServer(gen);
  openTabsListEl.innerHTML = '<p class="tabs-loading">Loading open tabs…</p>';
  // PERFSIGNIN1: the cards' figures come from ONE read for the whole location, started together with the list. They used
  // to cost about 3 + L requests per ticket (order, each line's product, payment preview, price quote), which on a
  // floor with dozens of open tickets was hundreds of requests behind the PIN pad.
  const summariesRequest = listOpenTabSummaries(session.apiBaseUrl, session.tenantId);
  const result = await listOpenTabs(session.apiBaseUrl, session.tenantId);
  // AU13-F1: the session that started this request may have ended while
  // it was in flight. A response for a dead session writes nothing,
  // toasts nothing, and renders nothing.
  if (!sessionIsCurrent(gen)) {
    return;
  }
  if (result.status !== 200) {
    openTabsListEl.innerHTML = `<p class="tabs-empty">Could not load open tabs (${result.body.message ?? result.body.error ?? result.status}).</p>`;
    showToast(
      `Could not load open tabs. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  state.openTabs = result.body.tabs;
  syncOpenTabsPaymentLinkWatch();
  if (state.openTabs.length === 0) {
    openTabsListEl.innerHTML = '<p class="tabs-empty">No open tabs. Start one with New Ticket.</p>';
    applyTabSearch();
    return;
  }
  openTabsListEl.innerHTML = "";
  for (const order of state.openTabs) {
    openTabsListEl.append(buildTabCard(order));
  }
  // TM1: a fresh render always drops any mark this screen no longer has a
  // card for (a merged-away source, say), and re-applies merge mode's own
  // dimmed/marked classes to every freshly built card.
  state.mergeSourceOrderIds = state.mergeSourceOrderIds.filter((id) =>
    state.openTabs.some((order) => order.id === id),
  );
  // Totals are re-learned per card below; a stale one must not feed the merge footer meanwhile.
  tabTotalCentsByOrderId.clear();
  tabPaidByOrderId.clear();
  tabAwaitingByOrderId.clear();
  tabDrinkCountByOrderId.clear();
  if (
    state.mergeTargetOrderId !== undefined &&
    !state.openTabs.some((order) => order.id === state.mergeTargetOrderId)
  ) {
    state.mergeTargetOrderId = undefined;
  }
  applyTabSearch();
  renderTabMergeSelectionState();
  // Each row renders immediately with name and age; the drink count and total arrive with the one summaries read.
  // Sixteenth amendment item 2: fully paid tabs sort after tabs with an unpaid balance. Paid state arrives with the
  // figures, so the already-rendered cards are reordered in place once (append() on an attached node moves it,
  // it does not clone or lose its click handler).
  const summariesResult = await summariesRequest;
  // AU13-F1: a response for a session that ended while it was in flight writes nothing.
  if (!sessionIsCurrent(gen)) {
    return;
  }
  if (summariesResult.status !== 200) {
    for (const order of state.openTabs) {
      updateTabCardFigures(order.id, {
        drinkCount: 0,
        totalCents: undefined,
        unpricedCount: 0,
        fullyPaid: false,
      });
    }
    showToast(
      `Could not load ticket totals. ${capitalizeServerMessage(summariesResult.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const fullyPaidByOrderId = new Map<string, boolean>();
  for (const summary of summariesResult.body.summaries) {
    const figures: TabSummary = {
      drinkCount: summary.drinkCount,
      // LOCFLOOR1 (sweep B1): the headline is what is owed now; the whole-ticket total rides along for a quiet second line.
      totalCents:
        (summary.owedWithTaxCents === undefined ? summary.totalCents : summary.owedWithTaxCents) ??
        undefined,
      ...(summary.totalCents === null ? {} : { ticketTotalCents: summary.totalCents }),
      unpricedCount: summary.unpricedCount,
      fullyPaid: summary.fullyPaid,
      ...(summary.awaitingTipCents === undefined ? {} : { awaitingTip: true }),
    };
    updateTabCardFigures(summary.orderId, figures);
    if (figures.totalCents === undefined) {
      tabTotalCentsByOrderId.delete(summary.orderId);
    } else {
      tabTotalCentsByOrderId.set(summary.orderId, figures.totalCents);
    }
    fullyPaidByOrderId.set(summary.orderId, summary.fullyPaid);
    tabPaidByOrderId.set(summary.orderId, summary.fullyPaid);
    tabAwaitingByOrderId.set(summary.orderId, summary.awaitingTipCents !== undefined);
    tabDrinkCountByOrderId.set(summary.orderId, summary.drinkCount);
  }
  // TAB3: the status chips and their counts depend on the paid state that just arrived.
  applyTabSearch();
  if (state.tabMergeModeActive) {
    renderTabMergeSelectionState();
  }
  const sortedOrders = [...state.openTabs].sort((a, b) => {
    const aPaid = fullyPaidByOrderId.get(a.id) ?? false;
    const bPaid = fullyPaidByOrderId.get(b.id) ?? false;
    return (aPaid ? 1 : 0) - (bPaid ? 1 : 0);
  });
  for (const order of sortedOrders) {
    const card = openTabsListEl.querySelector<HTMLElement>(
      `[data-order-id="${CSS.escape(order.id)}"]`,
    );
    if (card !== null) {
      openTabsListEl.append(card);
    }
  }
}

async function handleResumeTabClick(order: OrderDto): Promise<void> {
  const reconstructed = await reconstructOrderLines(order.id);
  if (reconstructed === undefined) {
    showToast("Could not open this ticket. Check your connection and try again.", "danger");
    return;
  }
  // Design-gate fix (2026-08-20): every reconstructed line is now shown,
  // including one that could not be priced (reconstructOrderLine's header
  // comment); its "Price unavailable" state and Prepared toggle are the
  // persistent, on-the-line signal that a manager needs to look at it, so
  // there is no separate "N lines could not be loaded" toast to fire here
  // any more, transient or otherwise.
  await enterTicket(
    order,
    reconstructed.lines.map((line) => line.ticketLine),
    reconstructed.guests,
    reconstructed.lastReopen,
  );
}


// ---------------------------------------------------------------------
// CLOSEDVIEW1 (docs/build-briefs/CLOSEDVIEW1-closed-ticket-read-only.md): look at a closed ticket without reopening it
// ---------------------------------------------------------------------
//
// Owner request, Kerry, 2026-10-31: "if I want to see the details of a closed ticket, I should be able to open a locked
// and clocked ticket without having to reopen it to look at ticket details." The ticket rail is reused as it is; while
// state.closedView is set, the rail rows that carry a control (Assign, Edit, Take off, Service today, the Prepared
// button, Add guest, More, Pay and Close, ID Verified) are not drawn, and the menu pane is hidden by CSS. Everything
// this opens is a read: GET order, GET payments, GET discounts, the payment preview (a computation) and the receipt read.

let closedViewOpening = false;
let closedViewReceiptRow: ReturnType<typeof mountTicketReceiptRow> | undefined;
let closedViewReceiptRoot: HTMLElement | undefined;

/** Ends the read-only view: the ring screen goes back to an ordinary ticket screen (or is about to be hidden). */
function clearClosedView(): void {
  state.closedView = undefined;
  ringScreen.classList.remove("is-closed-view");
  closedReceiptEl.hidden = true;
  closedReceiptEl.replaceChildren();
  closedViewReceiptRow?.reset();
}

function receiptNode<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  testid: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  created.className = className;
  if (testid !== null) created.dataset["testid"] = testid;
  if (text !== undefined) created.textContent = text;
  return created;
}

/** One receipt row: a label on the left, an amount on the right. */
function receiptRow(
  className: string,
  testid: string,
  label: string,
  amount: string,
): HTMLElement {
  const row = receiptNode("div", `closed-receipt-row ${className}`, testid);
  row.append(
    receiptNode("span", "closed-receipt-label", `${testid}-label`, label),
    receiptNode("span", "closed-receipt-amount", `${testid}-amount`, amount),
  );
  return row;
}

/** What the payment line says of the tender: the brand and last four when the payment carries them, else "Card". */
function closedTenderLabel(payment: PaymentWithTipSplitsDto): string {
  if (payment.paymentAttempt.tender === "cash") return "Cash";
  const stored = payment.paymentAttempt as { card_brand?: unknown; card_last4?: unknown };
  const brandKey = typeof stored.card_brand === "string" ? stored.card_brand.trim().toLowerCase() : "";
  const brand =
    brandKey === "amex" || brandKey === "american_express" ? "Amex"
    : brandKey.length === 0 ? ""
    : brandKey.replace(/[_-]+/g, " ").replace(/^./, (first) => first.toUpperCase());
  const last4 = typeof stored.card_last4 === "string" && /^\d{4}$/.test(stored.card_last4) ? stored.card_last4 : "";
  if (last4.length === 0) return brand.length > 0 ? brand : "Card";
  return `${brand.length > 0 ? brand : "Card"} ****${last4}`;
}

/** The closed ticket laid out as the location's emailed receipt (receiptEmail.ts renderReceiptEmail), one column:
 * header, lines with their modifiers, discounts, subtotal, tax, ONE tip line, TOTAL PAID (the sum of the captured
 * payments, tip included), one line per payment, refunds and Net paid, the receipt status, then Refund and Reopen.
 * Every figure is the server's (the order read, the discounts read, the payments read); nothing is recomputed here
 * except sums of those figures. */
function renderClosedReceipt(
  entry: RecentlyClosedTab,
  payments: PaymentWithTipSplitsDto[],
  canRefund: boolean,
): void {
  closedReceiptEl.replaceChildren();
  const captured = payments
    .filter((p) => p.paymentAttempt.status === "Captured")
    .sort((a, b) => a.paymentAttempt.updated_at.localeCompare(b.paymentAttempt.updated_at));
  const amountOf = (p: PaymentWithTipSplitsDto): number =>
    parseDollarsToCentsExact(p.paymentAttempt.amount, "captured payment amount");

  // 1. Header.
  const header = receiptNode("div", "closed-receipt-header", "closed-receipt-header");
  const locationName = floorLocationName();
  if (locationName !== undefined && locationName.length > 0) {
    header.append(receiptNode("div", "closed-receipt-location", "closed-receipt-location", locationName));
  }
  header.append(
    receiptNode(
      "div",
      "closed-receipt-head-line",
      "closed-receipt-when",
      new Date(entry.closedAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }),
    ),
  );
  const ticketLine = receiptNode("div", "closed-receipt-head-line", null);
  ticketLine.append(
    receiptNode("span", "closed-receipt-ticket", "closed-receipt-ticket", state.orderName ?? entry.label),
    receiptNode("span", "closed-receipt-tag", "closed-banner-tag", "Closed"),
  );
  header.append(ticketLine);
  if (entry.closedBy !== undefined) {
    header.append(
      receiptNode("div", "closed-receipt-head-line", "closed-receipt-closed-by", `Closed by ${entry.closedBy}`),
    );
  }
  closedReceiptEl.append(header);

  // 2. Lines, modifiers indented under their drink.
  const lines = receiptNode("div", "closed-receipt-section", "closed-receipt-lines");
  for (const line of state.lines) {
    lines.append(
      receiptRow(
        "closed-receipt-line",
        "closed-receipt-line",
        `1 x ${line.name}`,
        line.lineTotalCents === undefined ? "" : formatCents(line.lineTotalCents),
      ),
    );
    const notes: string[] = [];
    if (line.detail !== undefined && line.detail.length > 0) notes.push(line.detail);
    for (const modifier of line.appliedModifiers ?? []) {
      notes.push(
        modifier.priceAdjustmentCents === 0
          ? modifier.label
          : `${modifier.label} ${modifier.priceAdjustmentCents < 0 ? "-" : "+"}${formatCents(Math.abs(modifier.priceAdjustmentCents))}`,
      );
    }
    for (const note of notes) {
      lines.append(receiptNode("div", "closed-receipt-modifier", "closed-receipt-modifier", note));
    }
  }
  closedReceiptEl.append(lines);

  // 3 and 4. Discounts and comps, each by name; subtotal and tax.
  const figures = receiptNode("div", "closed-receipt-section", "closed-receipt-figures");
  const pricing = state.orderDiscounts;
  if (pricing !== undefined) {
    figures.append(
      receiptRow(
        "closed-receipt-subtotal",
        "closed-receipt-subtotal",
        "Subtotal",
        formatCents(pricing.totals.subtotalBeforeDiscountCents),
      ),
    );
    const reductions = [
      ...pricing.applications
        .filter((a) => a.reductionCents > 0)
        .map((a) => ({ name: a.discountName, cents: a.reductionCents })),
      ...pricing.memberBenefits
        .filter((b) => b.reductionCents > 0)
        .map((b) => ({ name: b.discountName, cents: b.reductionCents })),
    ];
    for (const reduction of reductions) {
      figures.append(
        receiptRow("closed-receipt-discount", "closed-receipt-discount", reduction.name, `-${formatCents(reduction.cents)}`),
      );
    }
    figures.append(
      receiptRow("closed-receipt-tax", "closed-receipt-tax", "Tax", formatCents(pricing.totals.taxCents)),
    );
  }
  // 5. Tip: one line, the sum of the tips.
  if (captured.some((p) => p.paymentAttempt.tip_cents !== null)) {
    const tipCents = captured.reduce((sum, p) => sum + (p.paymentAttempt.tip_cents ?? 0), 0);
    figures.append(receiptRow("closed-receipt-tip", "closed-receipt-tip", "Tip", formatCents(tipCents)));
  }
  closedReceiptEl.append(figures);

  // 6. TOTAL PAID: what was actually charged.
  const totalPaidCents = captured.reduce((sum, p) => sum + amountOf(p), 0);
  closedReceiptEl.append(
    receiptRow("closed-receipt-total", "closed-receipt-total-paid", "TOTAL PAID", formatCents(totalPaidCents)),
  );

  // 7. Payments: one line each, no tip on the line.
  const paymentsEl = receiptNode("div", "closed-receipt-section", "closed-receipt-payments");
  for (const p of captured) {
    const split = p.splitShare === null ? "" : `, guest ${p.splitShare.shareIndex} of ${p.splitShare.shareCount}`;
    paymentsEl.append(
      receiptRow(
        "closed-receipt-payment",
        "closed-receipt-payment",
        `${closedTenderLabel(p)}${split}`,
        formatCents(amountOf(p)),
      ),
    );
  }
  closedReceiptEl.append(paymentsEl);

  // 8. Refunds, then Net paid.
  const refunded = captured.filter((p) => p.refundedCents > 0);
  if (refunded.length > 0) {
    const refundsEl = receiptNode("div", "closed-receipt-section", "closed-receipt-refunds");
    for (const p of refunded) {
      refundsEl.append(
        receiptRow(
          "closed-receipt-refund",
          "closed-receipt-refund",
          `Refund (${closedTenderLabel(p)})`,
          `-${formatCents(p.refundedCents)}`,
        ),
      );
    }
    const refundedTotal = refunded.reduce((sum, p) => sum + p.refundedCents, 0);
    refundsEl.append(
      receiptRow("closed-receipt-net", "closed-receipt-net-paid", "Net paid", formatCents(totalPaidCents - refundedTotal)),
    );
    closedReceiptEl.append(refundsEl);
  }

  // 9. Receipt status (RCPT2's own row, with Resend for money.receipt.resend), or "not sent" when none was emailed.
  if (closedViewReceiptRoot === undefined || closedViewReceiptRow === undefined) {
    closedViewReceiptRoot = document.createElement("div");
    closedViewReceiptRoot.className = "rcptrow";
    closedViewReceiptRoot.dataset["testid"] = "closed-view-receipt";
    closedViewReceiptRow = mountTicketReceiptRow(closedViewReceiptRoot, {
      apiBaseUrl: () => requireSession().apiBaseUrl,
      tenantId: () => requireSession().tenantId,
      canView: () =>
        state.session !== undefined && hasPermission(state.session, "money.signature.view"),
      canResend: () =>
        state.session !== undefined && hasPermission(state.session, "money.receipt.resend"),
      isCurrent: (orderId) => state.closedView !== undefined && state.orderId === orderId,
    });
  }
  const statusEl = receiptNode("div", "closed-receipt-section closed-receipt-status", "closed-receipt-status");
  statusEl.append(
    closedViewReceiptRoot,
    receiptNode("div", "closed-receipt-nosend", "closed-receipt-not-sent", "Receipt: not sent"),
  );
  closedReceiptEl.append(statusEl);

  // Actions, below the receipt.
  const actions = receiptNode("div", "closed-receipt-actions", "closed-receipt-actions");
  const session = state.session;
  if (canRefund) {
    const refund = receiptNode("button", "closed-receipt-button", "closed-view-refund", "Refund");
    refund.type = "button";
    refund.addEventListener("click", () => void handlePaymentsButtonClick());
    actions.append(refund);
  }
  if (session !== undefined && hasPermission(session, REOPEN_TAB_PERMISSION)) {
    const reopen = receiptNode("button", "closed-receipt-button", "closed-view-reopen", "Reopen");
    reopen.type = "button";
    reopen.addEventListener("click", () => void handleReopenClick(entry));
    actions.append(reopen);
  }
  if (actions.childElementCount > 0) closedReceiptEl.append(actions);
  closedReceiptEl.hidden = false;
  void closedViewReceiptRow.show(entry.orderId, "closed");
}

/** Opens a closed ticket read only. Reads only; the ticket is not touched. */
async function openClosedTicketView(entry: RecentlyClosedTab): Promise<void> {
  if (closedViewOpening) return;
  const session = requireSession();
  const gen = sessionGeneration;
  closedViewOpening = true;
  try {
    const reconstructed = await reconstructOrderLines(entry.orderId);
    if (!sessionIsCurrent(gen)) return;
    if (reconstructed === undefined) {
      showToast("Could not open this ticket. Check your connection and try again.", "danger");
      return;
    }
    if (reconstructed.order.status !== "closed") {
      // Reopened somewhere else since this card was drawn: the card is stale, so it goes.
      state.recentlyClosedTabs = state.recentlyClosedTabs.filter(
        (candidate) => candidate.orderId !== entry.orderId,
      );
      renderRecentlyClosed();
      showToast("This ticket is not closed any more.", "neutral");
      return;
    }
    const paymentsResult = await getPayments(session.apiBaseUrl, session.tenantId, entry.orderId);
    if (!sessionIsCurrent(gen)) return;
    if (paymentsResult.status !== 200) {
      showToast("Could not open this ticket. Check your connection and try again.", "danger");
      return;
    }
    const order = reconstructed.order;
    // The same resets enterTicket makes for a ticket coming into view, without its reads that exist only to sell.
    resetMenuSearch();
    floorTicketVisit += 1;
    state.closedView = { entry };
    ringScreen.classList.add("is-closed-view");
    state.orderId = order.id;
    state.orderStatus = order.status;
    state.orderName = order.name ?? undefined;
    state.lastReopen = reconstructed.lastReopen;
    state.orderPaid = false;
    state.lines = reconstructed.lines.map((line) => line.ticketLine);
    state.quote = undefined;
    state.paymentPreview = undefined;
    paymentPreviewFailedCount = 0;
    state.guests = reconstructed.guests;
    state.payScope = undefined;
    state.activeSplitPlan = undefined;
    state.activeSplitPlanLoadState = "ready";
    state.capturedPaymentTimeByGuestId = new Map();
    state.refundedLineIds = new Set();
    state.latestCapturedPaymentTime = undefined;
    state.capturedPaymentsSummary = undefined;
    state.capturedCharges = undefined;
    state.savedCards = undefined;
    state.pendingCardDetaches = [];
    state.activeHold = undefined;
    debugOrderId.textContent = order.id;
    showRingScreen();
    refreshTileDisabledState();
    const canRefund =
      hasPermission(session, "money.refund") &&
      paymentsResult.body.payments.some((p) => p.paymentAttempt.status === "Captured");
    renderTicket();
    // The discounts and totals the ticket was priced with: a read (GET), the same one a live ticket makes.
    const discountsResult = await getOrderDiscounts(session.apiBaseUrl, session.tenantId, entry.orderId);
    if (!sessionIsCurrent(gen) || state.closedView === undefined || state.orderId !== entry.orderId) return;
    state.orderDiscounts = discountsResult.status === 200 ? discountsResult.body : undefined;
    renderClosedReceipt(entry, paymentsResult.body.payments, canRefund);
  } finally {
    closedViewOpening = false;
  }
}

// AU14-F4 / DEC-276: held from the Reopen tap until the reason sheet is
// confirmed or dismissed.
let reopenReasonFlowBusy = false;

/** The permission this flow resolves, named once. The button wording follows
 * the permission SNAPSHOT (never a role name): a session whose grant is
 * approval_required will be asked for a manager's PIN next. */
const REOPEN_TAB_PERMISSION = "money.tab.reopen";

function reopenNeedsApproval(session: { permissions: Record<string, string> }): boolean {
  return session.permissions[REOPEN_TAB_PERMISSION] === "approval_required";
}

/** Reads the tenant's active reopen reasons and asks which one applies.
 * Resolves with the picked reason, or undefined on Cancel, close, Escape, a tap
 * outside, sign-out, or a failed read (which is toasted). */
async function askForReopenReason(
  entry: RecentlyClosedTab,
  session: { apiBaseUrl: string; tenantId: string; permissions: Record<string, string> },
): Promise<{ id: string; label: string } | undefined> {
  const gen = sessionGeneration;
  const result = await listActionReasons(session.apiBaseUrl, session.tenantId, { kind: "reopen" });
  if (gen !== sessionGeneration || sessionOrUndefined() === undefined) {
    return undefined;
  }
  const reasons: ActionReasonDto[] =
    result.status === 200 ? sortByName(result.body.actionReasons, (reason) => reason.label) : [];
  if (result.status !== 200 || reasons.length === 0) {
    showToast(
      result.status === 200
        ? "No reasons are set up for reopening a ticket. Ask an admin to add one in Settings."
        : "Could not load the reasons. Check your connection and try again.",
      "danger",
    );
    return undefined;
  }
  const closedAt = new Date(entry.closedAt).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  return new Promise((resolve) => {
    openReasonSheet({
      title: "Reopen this ticket?",
      subtitle: entry.label,
      contextTitle: `Closed ${closedAt}`,
      contextFrom: "Closed",
      contextTo: "Open",
      heading: "Why are you reopening it?",
      reasons,
      confirmLabel: reopenNeedsApproval(session) ? "Ask a manager to approve" : "Reopen ticket",
      pickedNote: reopenNeedsApproval(session)
        ? "A manager approves next and sees this reason."
        : "",
      onConfirm: (picked) => resolve(picked),
      onCancel: () => resolve(undefined),
    });
  });
}

// RBAC track: money.tab.reopen is one of three live approval_required
// permissions (Assistant Manager only; src/api/server.ts's own comment on
// this route). requestApproval makes the first, no-approval attempt
// itself; only if THAT comes back approval_required does the picker+PIN
// flow ever show, so an Admin/Manager/Lounger tapping Reopen (allowed or
// denied outright, never approval_required, under the seeded role
// templates) sees no change in behavior at all.
async function handleReopenClick(entry: RecentlyClosedTab): Promise<void> {
  const session = requireSession();
  // A second tap while the reasons are still loading, or while the sheet is
  // up, must not stack a second sheet.
  if (reopenReasonFlowBusy) {
    return;
  }
  reopenReasonFlowBusy = true;
  let picked: { id: string; label: string } | undefined;
  try {
    picked = await askForReopenReason(entry, session);
  } finally {
    reopenReasonFlowBusy = false;
  }
  if (picked === undefined) {
    // Cancel (or a failed read, already toasted): nothing changed, nothing sent.
    return;
  }
  const reason = picked;
  const idempotencyKey = crypto.randomUUID();
  const actionLabel = `Reopen tab: ${entry.label}`;
  const gated = await requestApproval(
    actionLabel,
    (approval) =>
      reopenOrder(session.apiBaseUrl, session.tenantId, entry.orderId, {
        idempotencyKey,
        // AU14-F4 / DEC-276: the same reason rides every attempt, the first
        // and the retry after a manager's PIN, under the same key.
        reasonId: reason.id,
        ...approval,
      }),
    () => showOpenTabsScreen(),
    // The reason comes BEFORE the PIN, so the manager sees what they are
    // vouching for on the PIN pad's own context line.
    { pinContext: `Reopen Tab: ${entry.label} \u00b7 ${reason.label}` },
  );
  if (gated.canceled) {
    // "Cancel returns to where the flow started with nothing changed and
    // no toast of failure": requestApproval's own returnTo already
    // re-shows Open Tabs; nothing further to do here.
    return;
  }
  const result = gated.result;
  if (result.status !== 200 && result.status !== 201) {
    // Gate finding 7: "order_not_closed" is the server telling this
    // client that the tab it is offering to reopen is NOT closed any
    // more, so the row is stale by definition and must stop being
    // offered. Dropping it here is what stops a second (and third)
    // manager approval being spent on an action that can only fail.
    if (result.body.error === "order_not_closed") {
      state.recentlyClosedTabs = state.recentlyClosedTabs.filter(
        (candidate) => candidate.orderId !== entry.orderId,
      );
    }
    showToast(
      `Could not reopen this tab (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    // The approval flow leaves the pin pad showing on a terminal failure
    // (its own screens are not the caller's to own), so this call site
    // returns to where it started, matching the other two call sites
    // which already re-show their own screen on failure.
    showOpenTabsScreen();
    return;
  }
  state.recentlyClosedTabs = state.recentlyClosedTabs.filter(
    (candidate) => candidate.orderId !== entry.orderId,
  );
  // PAY19 (docs/build-briefs/PAY19-reopen-sweep-race.md): swept BEFORE
  // handleResumeTabClick now, not after. handleResumeTabClick calls
  // enterTicket, which shows the ring screen (making Pay tappable) before
  // several of its own awaited reads finish; a pay key minted for this
  // order in that window used to be wiped by this exact sweep running
  // AFTER handleResumeTabClick returned. clearPayIdempotencyKeysForOrder
  // no longer needs state.guests to find this order's own pending
  // targets (PayIdempotencyKeyMap.targetsForOrder reads the map's actual
  // keys instead), so nothing here depends on enterTicket having run
  // first any more. Same reasoning for the hold key: clearHoldIdempotencyKey
  // is keyed by orderId alone and never depended on enterTicket either.
  // Defensive coverage for a ticket closed before this fix shipped, or
  // some other closing path finishClosedTicket's own clear did not reach.
  clearPayIdempotencyKeysForOrder(entry.orderId);
  // PAY13 Section 2.2/2.9: the identical defensive coverage for this
  // order's own hold key.
  const tenantIdForHoldKey = state.session?.tenantId;
  if (tenantIdForHoldKey !== undefined) {
    clearHoldIdempotencyKey(tenantIdForHoldKey, entry.orderId);
  }
  await handleResumeTabClick(result.body.order);
}

/** NEWTICKET1: true while a create-ticket request is out. Each tap mints its own idempotency key, so without this a
 * second tap before the first answer opens a second ticket. The handler refuses on its own as well as the button
 * being disabled, because a tap already queued is not stopped by the disabled attribute. */
let newTicketInFlight = false;

async function handleNewTicketClick(): Promise<void> {
  if (newTicketInFlight) return;
  const session = requireSession();
  const name = newTicketNameInput.value.trim();
  newTicketInFlight = true;
  const restingLabel = newTicketButton.textContent;
  newTicketButton.disabled = true;
  newTicketButton.textContent = "Opening...";
  try {
    const result = await createOrder(session.apiBaseUrl, session.tenantId, {
      idempotencyKey: crypto.randomUUID(),
      // CL6-F1: no date sent; the server stamps the tenant's business date.
      name: name.length > 0 ? name : undefined,
    });
    if (result.status !== 200 && result.status !== 201) {
      showToast(
        `Could not start a ticket (${result.body.message ?? result.body.error ?? result.status}).`,
        "danger",
      );
      return;
    }
    newTicketNameInput.value = "";
    // A brand-new order has zero named guests by construction; no need to
    // ask the server, matching every other "obviously empty" initial state
    // this function already assumes (e.g. no lines).
    await enterTicket(result.body.order, [], []);
  } finally {
    // Restored on success and on failure, so Open Tabs always shows the button ready.
    newTicketInFlight = false;
    newTicketButton.disabled = false;
    newTicketButton.textContent = restingLabel;
  }
}

// ---------------------------------------------------------------------
// ALPHA-10 item 3 / ALPHA-11: manager settings screen
// ---------------------------------------------------------------------
//
// A separate screen from the bartender selling surface (showAdminScreen),
// reached only from Open Tabs' explicit "Settings" button, never
// from the ring screen, never reachable mid-ticket. ALPHA-11 expanded the
// original seasonal-toggle-only screen into a full three-section console
// (Drinks, Bottles, Location settings); admin.ts is the single module
// that owns all of that screen's internals (list rendering, add/edit
// forms, settings form), so this file's own responsibility here is only
// the screen-level show/hide wiring and handing off to admin.ts on open.

function handleOpenManagerSettingsClick(): void {
  renderSettingsSetupEntry();
  showAdminScreen();
  void admin.openAdminScreen(requireSession());
}

async function handleAdminBackToTabsClick(): Promise<void> {
  const navigationGeneration = sessionGeneration;
  if(!(await admin.confirmLeaveCatalog()) || !sessionIsCurrent(navigationGeneration) || !admin.confirmLeavePermitSettings())return;
  renderSettingsSetupEntry();
  showOpenTabsScreen();
  void loadOpenTabs();

  // Real bug found and fixed while building ALPHA-10's e2e coverage:
  // loadMenu() only ever ran at login (handleLogin) or on a session
  // resumed by a page load (init), so a manager who toggled a drink's
  // active state from THIS SAME SESSION's admin screen, then went
  // straight back and started or resumed a ticket, still saw the
  // bartender menu exactly as it was at login: the whole point of a
  // live-toggle admin screen (a manager can turn a drink off mid-shift
  // and have it actually stop being orderable) would have required every
  // logged-in bartender to log out and back in first. Refreshing here,
  // the one path that leaves the admin screen, closes that gap for this
  // session without touching loadMenu's existing login/reload call
  // sites or their own behavior. A drink disabled from a DIFFERENT
  // device or session still will not disappear from this one until its
  // own next login or reload; that residual limitation matches the
  // already-accepted, already-documented shape of tab-resume's own
  // "menu refreshes at login/reload, not continuously" limitation
  // (solution-architecture.md Section 7), not a new one.
  void loadMenu();
  // ALPHA-11: the same staleness reasoning extends to the manager
  // console's own three sections (a bottle price edited this visit must
  // not still show its old price on the next visit); admin.ts caches
  // each section's own load per screen-open, so this clears that cache
  // on the one path that leaves the screen, mirroring loadMenu() above.
  admin.invalidate();
  // ONB4-F1: setup not complete means the banner is there to reopen the wizard. After the invalidate above, which
  // would otherwise discard the banner's own read.
  if (setupWizardEnabled()) void showFirstDaySetupBanner();
}

// ---------------------------------------------------------------------
// ALPHA-08: closeout summary screen
// ---------------------------------------------------------------------

// Design/QA gate finding H: figures older than this are flagged stale.
// Chosen against this screen's own realistic use (a manager checking in
// periodically, or a lounge display left open), not a hard product
// requirement; adjustable without touching any other file.
const CLOSEOUT_STALE_THRESHOLD_MS = 2 * 60 * 1000;

let closeoutFreshnessTimer: ReturnType<typeof setInterval> | undefined;

async function handleOpenCloseoutSummaryClick(restoredDate?: string): Promise<boolean> {
  // CL6: every open starts on the tenant's current business date (from the
  // server, CL6-F1); the day arrows step from there.
  const today = await refreshTenantBusinessDate();
  if (today === undefined) {
    return false;
  }
  const session = requireSession();
  const locations = await getReportLocations(session.apiBaseUrl, session.tenantId);
  if (locations.status !== 200 || locations.body.locations.length === 0) {
    showToast("Could not load your locations for closeout.", "danger");
    return false;
  }
  closeoutLocationSelectEl.replaceChildren();
  for (const siteLocation of locations.body.locations) {
    const option = document.createElement("option");
    option.value = siteLocation.id;
    option.textContent = siteLocation.name;
    closeoutLocationSelectEl.append(option);
  }
  state.closeoutLocationId = locations.body.locations[0]!.id;
  // UX108: a refresh reopens the day that was on screen (never a day that has not happened yet).
  let openOn = restoredDate !== undefined && restoredDate <= today ? restoredDate : undefined;
  if (openOn === undefined) {
    // MGRDAYMAJ1 (manager-day-05): the morning-after job is last night, so Closeout opens on the most recent business day that
    // is over and not finalized (the same day the Needs you strip and the Closeout badge name), and on today when there is none.
    // The day is the bound location's, so that location is the one shown.
    await refreshMenuCounts().catch(() => undefined);
    const due = state.menuCounts.unfinalizedBusinessDate;
    const bound = loadTerminalConfig()?.locationId;
    if (due !== undefined && due < today) {
      openOn = due;
      if (bound !== undefined && locations.body.locations.some((site) => site.id === bound)) {
        state.closeoutLocationId = bound;
      }
    }
  }
  closeoutLocationSelectEl.value = state.closeoutLocationId;
  closeoutLocationWrapEl.hidden = locations.body.locations.length === 1;
  state.closeoutBusinessDate = openOn ?? today;
  showCloseoutScreen();
  void loadCloseoutSummary();
  // Ticks the "as of Xm ago" text and the stale note live while this
  // screen stays open, without re-fetching data itself: staleness is
  // about TIME passing, not a poll. Cleared on leaving (below).
  if (closeoutFreshnessTimer === undefined) {
    closeoutFreshnessTimer = setInterval(renderCloseoutFreshness, 15_000);
  }
  return true;
}

function handleCloseoutBackToTabsClick(): void {
  if (closeoutFreshnessTimer !== undefined) {
    clearInterval(closeoutFreshnessTimer);
    closeoutFreshnessTimer = undefined;
  }
  showOpenTabsScreen();
  void loadOpenTabs();
}

function handleCloseoutRefreshClick(): void {
  void loadCloseoutSummary();
}

// CORE13. Print: the browser's own print dialog over the print layout in styles.css (@media print shows
// only the closeout). Nothing is fetched; it prints what is on screen.
function handleCloseoutPrintClick(): void {
  window.print();
}

// CORE13, EXP1-S1. The same summary as a CSV or Excel file, fetched with the session header (a plain link could not
// carry it) and saved through an object URL. The server audits the download and needs report.export.
async function handleCloseoutExportPick(format: GateExportFormat): Promise<void> {
  const session = requireSession();
  const gen = sessionGeneration;
  const businessDate = state.closeoutBusinessDate;
  if (businessDate === undefined) {
    return;
  }
  closeoutExportStatusEl.textContent = "";
  closeoutExportButton.disabled = true;
  try {
    const result = await getCloseoutCsv(
      session.apiBaseUrl,
      session.tenantId,
      businessDate,
      state.closeoutLocationId,
      format,
    );
    if (!sessionIsCurrent(gen)) {
      return;
    }
    if (result.status !== 200 || result.blob === undefined) {
      closeoutExportStatusEl.textContent = result.networkError
        ? "Could not download this file. Check your connection."
        : capitalizeServerMessage(result.message ?? "Could not download this file.");
      return;
    }
    await deliverExportFile(
      closeoutExportStatusEl,
      result.blob,
      `closeout_${businessDate}`,
      format,
    );
  } finally {
    closeoutExportButton.disabled = false;
  }
}

// CL6: day navigation. Arrows only, never a typed date; forward stops at
// today (there is nothing to see in the future).
function handleCloseoutPrevDayClick(): void {
  if (state.closeoutBusinessDate === undefined) {
    return;
  }
  state.closeoutBusinessDate = addDaysToIsoDate(state.closeoutBusinessDate, -1);
  requestRoutePublish();
  void loadCloseoutSummary();
}

function handleCloseoutNextDayClick(): void {
  if (state.closeoutBusinessDate === undefined || state.tenantBusinessDate === undefined) {
    return;
  }
  const next = addDaysToIsoDate(state.closeoutBusinessDate, 1);
  if (next > state.tenantBusinessDate) {
    return;
  }
  state.closeoutBusinessDate = next;
  requestRoutePublish();
  void loadCloseoutSummary();
}

// CL6: the Finalize confirmation sheet. Lists the carryover preview the
// summary already carries (what finalizing right now would record), with
// every at-risk hold called out, per DEC-010's "a manager must be told a
// hold is at risk at carryover".
function openCloseoutFinalizeSheet(): void {
  const summary = state.closeoutSummary;
  if (summary === undefined) {
    return;
  }
  state.closeoutFinalizeKey = crypto.randomUUID();
  const carryover = summary.carryoverPreview;
  // MGRDAYMAJ1 (manager-day-07): the sheet names the day it will record, its net sales, a short or over drawer and the carry-over.
  const words = finalizeWords({
    businessDate: summary.businessDate,
    netSalesCents: summary.netSales.netSalesCents,
    overShortCents: summary.cash?.overShortCents,
    carryoverCount: carryover.length,
  });
  closeoutFinalizeTitleEl.textContent = words.title;
  closeoutFinalizeSummaryEl.textContent = words.summary;
  closeoutFinalizeConfirmButton.textContent = words.confirm;
  closeoutFinalizeCarryoverCountEl.textContent = `${carryover.length} tab${carryover.length === 1 ? "" : "s"}`;
  closeoutFinalizeCarryoverListEl.innerHTML = "";
  if (carryover.length === 0) {
    closeoutFinalizeCarryoverListEl.append(closeoutEmptyRow("No open tabs. Nothing carries over."));
  }
  for (const tab of carryover) {
    const row = document.createElement("div");
    row.className = "unresolved-row";
    row.dataset["testid"] = "closeout-finalize-carryover-row";
    const main = document.createElement("div");
    main.className = "unresolved-main";
    const name = document.createElement("div");
    name.className = "unresolved-name";
    name.textContent = tab.name ?? "Walk-in";
    const detail = document.createElement("div");
    detail.className = "unresolved-detail";
    detail.textContent =
      tab.ageDays === 0
        ? `Service date ${usDate(tab.serviceDate)}`
        : `Service date ${usDate(tab.serviceDate)} · already ${tab.ageDays} day${tab.ageDays === 1 ? "" : "s"} old`;
    main.append(name, detail);
    if (tab.holdAtRisk) {
      const risk = document.createElement("div");
      risk.className = "unresolved-detail closeout-hold-at-risk";
      risk.dataset["testid"] = "closeout-hold-at-risk";
      // PAY24: the fixed "Collect or contact the client soon." suffix
      // below assumes this product can still act on the hold before some
      // moment in time. That is false for a provider-mismatched hold
      // (holdAtRiskReason already tells the manager to clear it in
      // Manager Exceptions, per src/domain/closeout.ts), so the suffix is
      // omitted rather than appended to an instruction it would
      // contradict. Reviewed by the ui-ux-designer (DEC-194) this
      // session, payments batch 3: holdAtRiskReason reworded to name the
      // concrete action (Manager Exceptions' own "Clear hold") instead of
      // the vague "needs a manager's review" the backend-engineer first
      // shipped, and the two-clause semicolon sentence split into two
      // short sentences per the house copy rule.
      const suffix = tab.holdProviderMismatch ? "" : " Collect or contact the client soon.";
      risk.textContent = `Card hold at risk: ${tab.holdAtRiskReason ?? "check this hold"}${suffix}`;
      main.append(risk);
    }
    const amount = document.createElement("div");
    amount.className = "unresolved-amount";
    const amountValue = document.createElement("div");
    amountValue.className = "unresolved-amount-value";
    // CARRYOVER1-S1: the same tax-included, netted figure the banner and Open tabs show (the preview always carries it).
    const owedNow = tab.owedWithTaxCents ?? tab.outstandingCents;
    amountValue.textContent =
      owedNow === 0 ? "Nothing left to collect" : `Still to collect ${formatCents(owedNow)}`;
    amount.append(amountValue);
    row.append(main, amount);
    closeoutFinalizeCarryoverListEl.append(row);
  }
  closeoutFinalizeScrim.hidden = false;
  void renderCloseoutFinalizeSettlement(summary.settlement);
}

/** The declare-difference control: the server's computed difference, shown and never typed, and the reasons to pick
 * from. Confirm stays off until a reason is picked. */
async function renderCloseoutFinalizeSettlement(
  settlement: CloseoutSummaryDto["settlement"],
): Promise<void> {
  closeoutFinalizeDeclaration = undefined;
  closeoutFinalizeSettlementListEl.innerHTML = "";
  closeoutFinalizeSettlementReasonsEl.innerHTML = "";
  closeoutFinalizeConfirmButton.disabled = false;
  if (settlement === null || settlement.findings.length === 0) {
    closeoutFinalizeSettlementEl.hidden = true;
    return;
  }
  const session = requireSession();
  const declaration = {
    capturedDiffCents: settlement.capturedDiffCents ?? 0,
    refundedDiffCents: settlement.refundedDiffCents ?? 0,
    reasonId: undefined as string | undefined,
  };
  closeoutFinalizeDeclaration = declaration;
  closeoutFinalizeSettlementEl.hidden = false;
  closeoutFinalizeConfirmButton.disabled = true;
  const signed = (cents: number): string =>
    `${cents > 0 ? "+" : cents < 0 ? "-" : ""}${formatCents(Math.abs(cents))}`;
  for (const [testid, label, cents] of [
    ["closeout-finalize-settlement-captured", "Captured", declaration.capturedDiffCents],
    ["closeout-finalize-settlement-refunded", "Refunded", declaration.refundedDiffCents],
  ] as const) {
    const row = document.createElement("div");
    row.className = "activity-row";
    row.dataset["testid"] = testid;
    const name = document.createElement("div");
    name.className = "activity-name";
    name.textContent = label;
    const tag = document.createElement("span");
    tag.className = "activity-tag";
    tag.textContent = signed(cents);
    row.append(name, tag);
    closeoutFinalizeSettlementListEl.append(row);
  }
  const result = await listActionReasons(session.apiBaseUrl, session.tenantId, {
    kind: "settlement_difference",
  });
  if (closeoutFinalizeDeclaration !== declaration) {
    return;
  }
  const reasons: ActionReasonDto[] =
    result.status === 200 ? sortByName(result.body.actionReasons, (reason) => reason.label) : [];
  if (result.status !== 200 || reasons.length === 0) {
    showToast(
      result.status === 200
        ? "No reasons are set up for a settlement difference. Ask an admin to add one in Settings."
        : "Could not load the reasons. Check your connection and try again.",
      "danger",
    );
    return;
  }
  for (const reason of reasons) {
    const optionLabel = document.createElement("label");
    optionLabel.className = "admin-chip-option";
    const input = document.createElement("input");
    input.type = "radio";
    input.name = "closeout-finalize-settlement-reason";
    input.className = "admin-chip-option-input";
    input.dataset["testid"] = "closeout-finalize-settlement-reason-option";
    input.addEventListener("change", () => {
      if (input.checked) {
        declaration.reasonId = reason.id;
        closeoutFinalizeConfirmButton.disabled = false;
      }
    });
    const text = document.createElement("span");
    text.className = "admin-chip-option-label";
    text.textContent = reason.label;
    optionLabel.append(input, text);
    closeoutFinalizeSettlementReasonsEl.append(optionLabel);
  }
}

function closeCloseoutFinalizeSheet(): void {
  closeoutFinalizeScrim.hidden = true;
  state.closeoutFinalizeKey = undefined;
  closeoutFinalizeDeclaration = undefined;
}

async function handleCloseoutFinalizeConfirm(): Promise<void> {
  const session = requireSession();
  const businessDate = state.closeoutBusinessDate;
  const idempotencyKey = state.closeoutFinalizeKey;
  if (idempotencyKey === undefined || businessDate === undefined) {
    return;
  }
  closeoutFinalizeConfirmButton.disabled = true;
  try {
    const result = await finalizeBusinessDay(session.apiBaseUrl, session.tenantId, {
      idempotencyKey,
      businessDate,
      locationId: state.closeoutLocationId,
      ...(closeoutFinalizeDeclaration?.reasonId === undefined
        ? {}
        : {
            settlementDeclaration: {
              reasonId: closeoutFinalizeDeclaration.reasonId,
              capturedDiffCents: closeoutFinalizeDeclaration.capturedDiffCents,
              refundedDiffCents: closeoutFinalizeDeclaration.refundedDiffCents,
            },
          }),
    });
    if (result.status === 200 || result.status === 201) {
      closeCloseoutFinalizeSheet();
      showToast(`Business day ${usDate(businessDate)} finalized.`, "success");
      void refreshMenuCounts().catch(() => undefined);
      await loadCloseoutSummary();
      return;
    }
    // Plain staff language for the two designed refusals; anything else
    // falls back generically rather than leaking developer text.
    if (result.body.error === "business_day_not_over") {
      showToast(
        "This day is not over yet. A day can only be finalized after its cutoff time has passed.",
        "danger",
      );
    } else if (
      result.body.error === "settlement_difference_not_declared" ||
      result.body.error === "settlement_difference_mismatch"
    ) {
      showToast(capitalizeServerMessage(result.body.message ?? "Reload the day."), "danger");
    } else if (result.body.error === "business_day_already_finalized") {
      showToast("This day was already finalized.", "neutral");
      closeCloseoutFinalizeSheet();
      await loadCloseoutSummary();
    } else {
      showToast(
        `Could not finalize this day. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
        "danger",
      );
    }
  } finally {
    closeoutFinalizeConfirmButton.disabled =
      closeoutFinalizeDeclaration !== undefined &&
      closeoutFinalizeDeclaration.reasonId === undefined;
  }
}

function renderCloseoutFreshness(): void {
  if (state.closeoutLastLoadedAt === undefined) {
    closeoutLastUpdatedEl.textContent = "";
    closeoutStaleNoteEl.hidden = true;
    return;
  }
  const ageMs = Date.now() - state.closeoutLastLoadedAt;
  const ageMinutes = Math.floor(ageMs / 60_000);
  const ageText =
    ageMinutes < 1
      ? "As of just now"
      : `As of ${ageMinutes} minute${ageMinutes === 1 ? "" : "s"} ago`;
  closeoutLastUpdatedEl.textContent = ageText;
  closeoutStaleNoteEl.hidden = ageMs < CLOSEOUT_STALE_THRESHOLD_MS;
}

/** PAY35. Check payment from the closeout's pending list: one read at the processor, one terse toast, then a refresh. */
async function performCheckPayment(paymentAttemptId: string): Promise<void> {
  const session = requireSession();
  const result = await checkPaymentAttemptRequest(
    session.apiBaseUrl,
    session.tenantId,
    paymentAttemptId,
  );
  if (result.status !== 200) {
    showToast(CHECK_PAYMENT_FAILURE_TOAST, "danger");
  } else {
    const outcome = checkPaymentToast(result.body.check.kind);
    showToast(outcome.text, outcome.tone);
  }
  await loadCloseoutSummary();
}

/** PAY26. Records a Walkout collected in an old processor's dashboard. The amount shown is the server's; only the
 * outcome (and a note when nothing was collected) is sent. */
async function performRecordOutsideCollection(
  walkoutExceptionId: string,
  body: { outcome: "collected" | "not_collected"; note?: string },
): Promise<void> {
  const session = requireSession();
  const result = await recordOutsideCollectionRequest(
    session.apiBaseUrl,
    session.tenantId,
    walkoutExceptionId,
    body,
  );
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not save. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
  }
  await loadCloseoutSummary();
}

const CLOSEOUT_SETTLEMENT_STATUS_LABELS: Readonly<
  Record<NonNullable<CloseoutSummaryDto["settlement"]>["status"], string>
> = {
  matches: "Matches",
  differs: "Differs",
  incomplete: "Incomplete",
  account_unresolved: "Account unresolved",
  not_fetched: "Not fetched yet",
};

/** PAY1-S4-F2-F8-F5 slice 3. Settlement beside the recorded figures. Nothing at all with connected accounts off. */
function renderCloseoutSettlement(block: CloseoutSummaryDto["settlement"]): void {
  closeoutSettlementListEl.innerHTML = "";
  if (block === null) {
    closeoutSettlementSectionEl.hidden = true;
    closeoutSettlementStatusEl.textContent = "";
    return;
  }
  closeoutSettlementSectionEl.hidden = false;
  closeoutSettlementStatusEl.textContent = CLOSEOUT_SETTLEMENT_STATUS_LABELS[block.status];
  closeoutSettlementStatusEl.dataset["status"] = block.status;
  closeoutSettlementStatusEl.dataset["clean"] = String(block.clean);
  const addRow = (
    testid: string,
    label: string,
    value: string,
    flagged: boolean,
    detail?: string,
  ): void => {
    const row = document.createElement("div");
    row.className = "activity-row";
    row.dataset["testid"] = testid;
    const main = document.createElement("div");
    const name = document.createElement("div");
    name.className = "activity-name";
    name.textContent = label;
    main.append(name);
    if (detail !== undefined) {
      const sub = document.createElement("div");
      sub.className = "activity-detail";
      sub.textContent = detail;
      main.append(sub);
    }
    const tag = document.createElement("span");
    tag.className = flagged ? "activity-tag" : "activity-tag activity-tag-plain";
    tag.textContent = value;
    row.append(main, tag);
    closeoutSettlementListEl.append(row);
  };
  const signed = (cents: number): string =>
    `${cents > 0 ? "+" : cents < 0 ? "-" : ""}${formatCents(Math.abs(cents))}`;
  if (block.status === "not_fetched" || block.local === null) {
    closeoutSettlementListEl.append(closeoutEmptyRow("Card payments have not been fetched yet. They appear here after the next check."));
    return;
  }
  const compare = (
    testid: string,
    label: string,
    provider: number | null,
    local: number,
    diff: number | null,
  ): void => {
    if (provider === null || diff === null) {
      addRow(testid, label, `- · ${formatCents(local)}`, false);
      return;
    }
    if (diff === 0) {
      addRow(testid, label, formatCents(provider), false);
      return;
    }
    addRow(
      testid,
      label,
      signed(diff),
      true,
      `Stripe ${formatCents(provider)} · Recorded ${formatCents(local)}`,
    );
  };
  compare(
    "closeout-settlement-captured",
    "Captured",
    block.provider?.capturedCents ?? null,
    block.local.capturedCents,
    block.capturedDiffCents,
  );
  compare(
    "closeout-settlement-refunded",
    "Refunded",
    block.provider?.refundedCents ?? null,
    block.local.refundedCents,
    block.refundedDiffCents,
  );
  if (block.fees !== null) {
    addRow(
      "closeout-settlement-fees",
      "Plan fee",
      formatCents(block.fees.applicationFeeCents),
      false,
    );
    addRow(
      "closeout-settlement-processing-fees",
      "Processing fees",
      formatCents(block.fees.processingFeeCents),
      false,
    );
  }
  if (block.disputes !== null && block.disputes.count > 0) {
    addRow(
      "closeout-settlement-disputes",
      "Disputes",
      `${block.disputes.count} · ${formatCents(Math.abs(block.disputes.cents))}`,
      true,
    );
  }
  if (block.payouts !== null) {
    addRow("closeout-settlement-payouts", "Paid out", formatCents(block.payouts.paidCents), false);
    if (block.payouts.failedCount > 0) {
      addRow(
        "closeout-settlement-failed-payouts",
        "Failed payouts",
        String(block.payouts.failedCount),
        true,
      );
    }
  }
}

function renderCloseoutOutsideCollections(block: CloseoutSummaryDto["outsideCollections"]): void {
  closeoutOutsideCollectionsListEl.innerHTML = "";
  if (block.rows.length === 0 && block.unrecorded.length === 0) {
    closeoutOutsideCollectionsListEl.append(closeoutEmptyRow("None."));
    return;
  }
  for (const item of block.unrecorded) {
    const row = document.createElement("div");
    row.className = "activity-row";
    row.dataset["testid"] = "closeout-outside-collection-unrecorded";
    const main = document.createElement("div");
    const name = document.createElement("div");
    name.className = "activity-name";
    name.textContent = `${item.orderName ?? "Walk-in"} · ${formatCents(item.amountCents)}`;
    const tag = document.createElement("span");
    tag.className = "activity-tag";
    tag.textContent = "Not clean";
    const actions = document.createElement("div");
    actions.className = "closeout-outside-collection-actions";
    const collectedButton = document.createElement("button");
    collectedButton.type = "button";
    collectedButton.className = "btn-secondary btn-compact";
    collectedButton.dataset["testid"] = "closeout-outside-collection-collected";
    collectedButton.textContent = `Collected ${formatCents(item.amountCents)}`;
    const nothingButton = document.createElement("button");
    nothingButton.type = "button";
    nothingButton.className = "btn-secondary btn-compact";
    nothingButton.dataset["testid"] = "closeout-outside-collection-nothing";
    nothingButton.textContent = "Nothing collected";
    const noteInput = document.createElement("input");
    noteInput.type = "text";
    noteInput.className = "field-input";
    noteInput.hidden = true;
    noteInput.maxLength = 200;
    noteInput.setAttribute("aria-label", "Note");
    noteInput.dataset["testid"] = "closeout-outside-collection-note";
    collectedButton.addEventListener("click", () => {
      collectedButton.disabled = true;
      nothingButton.disabled = true;
      void performRecordOutsideCollection(item.walkoutExceptionId, { outcome: "collected" });
    });
    nothingButton.addEventListener("click", () => {
      if (noteInput.hidden) {
        noteInput.hidden = false;
        nothingButton.textContent = "Save";
        noteInput.focus();
        return;
      }
      if (noteInput.value.trim() === "") {
        noteInput.focus();
        return;
      }
      collectedButton.disabled = true;
      nothingButton.disabled = true;
      void performRecordOutsideCollection(item.walkoutExceptionId, {
        outcome: "not_collected",
        note: noteInput.value,
      });
    });
    actions.append(collectedButton, nothingButton);
    main.append(name, actions, noteInput);
    row.append(main, tag);
    closeoutOutsideCollectionsListEl.append(row);
  }
  for (const item of block.rows) {
    const row = document.createElement("div");
    row.className = "activity-row";
    row.dataset["testid"] = "closeout-outside-collection-row";
    const main = document.createElement("div");
    const name = document.createElement("div");
    name.className = "activity-name";
    name.textContent = item.orderName ?? "Walk-in";
    const detail = document.createElement("div");
    detail.className = "activity-detail";
    detail.textContent = `${item.recordedByDisplayName ?? ""} · ${new Date(item.recordedAt).toLocaleTimeString()}`;
    main.append(name, detail);
    const amount = document.createElement("span");
    amount.className = "activity-tag";
    amount.textContent = item.outcome === "collected" ? formatCents(item.amountCents) : "Nothing";
    row.append(main, amount);
    closeoutOutsideCollectionsListEl.append(row);
  }
}

async function loadCloseoutSummary(): Promise<void> {
  const session = sessionOrUndefined();
  const businessDate = state.closeoutBusinessDate;
  if (session === undefined || businessDate === undefined || currentScreenId !== "closeout-screen") {
    return;
  }
  const gen = sessionGeneration;
  const locationId = state.closeoutLocationId;
  const sequence = ++closeoutReadSequence;
  const current = () => sessionIsCurrent(gen) && state.session?.token === session.token &&
    sequence === closeoutReadSequence && currentScreenId === "closeout-screen" &&
    state.closeoutBusinessDate === businessDate && state.closeoutLocationId === locationId;
  const result = await getCloseoutSummary(
    session.apiBaseUrl,
    session.tenantId,
    businessDate,
    locationId,
  );
  if (!current()) return;
  if (result.status !== 200) {
    showToast(
      `Could not load the closeout summary. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    // Design/QA gate finding H: a failed refresh leaves the PRIOR figures
    // and their real age on screen (never fabricates a fresh timestamp
    // for data that did not actually just load); renderCloseoutFreshness
    // keeps ticking against the last successful load.
    return;
  }
  // ALPHA-12 item 3: resolve each reopened order's own guest labels, so
  // the capture list below can show "Kerry's drinks" rather than a bare
  // guest id. One extra read per reopened order with at least one
  // guest-attributed capture; the reopened-tab list is small (today's
  // reopens only), so this stays a handful of requests, not N+1 over the
  // whole day's activity.
  const guestLabelsByOrderId = new Map<string, Map<string, string>>();
  for (const activity of result.body.reopenedTabActivity) {
    const needsGuestLabels = activity.capturedPayments.some((p) => p.guestId !== null);
    if (!needsGuestLabels) {
      continue;
    }
    const orderResult = await getOrder(session.apiBaseUrl, session.tenantId, activity.orderId);
    if (!current()) return;
    if (orderResult.status !== 200) {
      continue; // best-effort: falls back to the bare id below
    }
    guestLabelsByOrderId.set(
      activity.orderId,
      new Map(orderResult.body.guests.map((g) => [g.id, g.label])),
    );
  }
  if (!current()) return;
  state.closeoutLastLoadedAt = Date.now();
  renderCloseoutSummary(result.body, guestLabelsByOrderId);
  renderCloseoutFreshness();
}

function specialsEl<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  testId?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  el.className = className;
  if (testId !== undefined) {
    el.dataset["testid"] = testId;
  }
  if (text !== undefined) {
    el.textContent = text;
  }
  return el;
}

function pluralDrinks(count: number): string {
  return `${count} drink${count === 1 ? "" : "s"}`;
}

/** DSC35-S8a. The Scheduled specials block, the "How this adds up" card and
 * the "Specials a manager removed" line. Every figure is the server's; this
 * does no money arithmetic. The whole area is hidden on a day with no special
 * that ran and no removal, and each part is hidden on its own when empty. */
function renderCloseoutSpecials(summary: CloseoutSummaryDto): void {
  closeoutSpecialsEl.innerHTML = "";
  const { scheduledSpecials, discountBreakdown, removedSpecialsByReason } = summary;
  const removedCount = summary.activityByServiceDate.removedSpecialCount;
  const ran = scheduledSpecials.rows.length > 0;
  const removed = removedCount > 0;
  closeoutSpecialsEl.hidden = !ran && !removed;
  if (!ran && !removed) {
    return;
  }

  if (ran) {
    const title = specialsEl("h2", "section-title", undefined, "Scheduled specials ");
    const count = specialsEl(
      "span",
      "section-count",
      "closeout-scheduled-specials-count",
      `${scheduledSpecials.rows.length} special${scheduledSpecials.rows.length === 1 ? "" : "s"}`,
    );
    title.append(count);
    const note = specialsEl(
      "p",
      "specials-section-note",
      undefined,
      "Specials that ran on their own and took money off a drink that was paid for on this day. Managers did not type these in.",
    );
    const card = specialsEl("div", "list-card", "closeout-scheduled-specials");
    const head = specialsEl("div", "sp-head");
    head.append(
      specialsEl("span", "", undefined, "Special"),
      specialsEl("span", "sp-num", undefined, "Drinks"),
      specialsEl("span", "sp-num", undefined, "Taken off"),
    );
    card.append(head);
    for (const row of scheduledSpecials.rows) {
      const line = specialsEl("div", "sp-row", "closeout-scheduled-special-row");
      const who = specialsEl("div", "");
      who.append(
        specialsEl("div", "sp-name", undefined, row.name),
        specialsEl("div", "sp-rule", undefined, row.rule),
      );
      line.append(
        who,
        specialsEl("div", "sp-num sp-drinks", undefined, String(row.drinkCount)),
        specialsEl("div", "sp-num sp-amt", undefined, `-${formatCents(row.takenOffCents)}`),
      );
      card.append(line);
    }
    const total = specialsEl("div", "sp-total", "closeout-scheduled-specials-total");
    total.append(
      specialsEl("span", "", undefined, "All scheduled specials"),
      specialsEl("span", "sp-num", undefined, String(scheduledSpecials.totalDrinkCount)),
      specialsEl(
        "span",
        "sp-num sp-amt",
        undefined,
        `-${formatCents(scheduledSpecials.totalCents)}`,
      ),
    );
    card.append(total);
    closeoutSpecialsEl.append(title, note, card);

    const recon = specialsEl("div", "list-card recon", "closeout-adds-up");
    const reconLine = (label: string, value: string, extra: string, testId: string) => {
      const line = specialsEl("div", `recon-line ${extra}`.trim(), testId);
      line.append(
        specialsEl("span", "recon-label", undefined, label),
        specialsEl("span", "", undefined, value),
      );
      return line;
    };
    const fig = (cents: number, n: number): string => `${formatCents(cents)} · ${n}`;
    const b = discountBreakdown;
    recon.append(
      reconLine(
        "Discounts (Revenue card above)",
        fig(b.totalCents, b.totalCount),
        "",
        "closeout-adds-up-total",
      ),
      reconLine(
        "Manager discounts",
        fig(b.managerCents, b.managerCount),
        "recon-sub",
        "closeout-adds-up-manager",
      ),
      reconLine(
        "Scheduled specials (block above)",
        fig(b.scheduledCents, b.scheduledCount),
        "recon-sub",
        "closeout-adds-up-scheduled",
      ),
      reconLine("Comps", fig(b.compCents, b.compCount), "recon-sub", "closeout-adds-up-comps"),
      ...((b.byAmountCents ?? 0) > 0
        ? [reconLine("By amount", formatCents(b.byAmountCents ?? 0), "recon-sub", "closeout-adds-up-amount")]
        : []),
      reconLine(
        `${formatCents(b.managerCents)} + ${formatCents(b.scheduledCents)} + ${formatCents(b.compCents)}${(b.byAmountCents ?? 0) > 0 ? ` + ${formatCents(b.byAmountCents ?? 0)}` : ""}`,
        `= ${formatCents(b.totalCents)}`,
        "recon-sum",
        "closeout-adds-up-sum",
      ),
    );
    recon.append(
      specialsEl(
        "p",
        "recon-foot",
        undefined,
        "Each drink is counted once. Removed specials are not in any of these lines, because that money was never taken off.",
      ),
    );
    closeoutSpecialsEl.append(
      specialsEl("h2", "section-title", undefined, "How this adds up"),
      recon,
    );
  }

  if (removed) {
    const title = specialsEl("h2", "section-title", undefined, "Specials a manager removed ");
    title.append(specialsEl("span", "section-count", undefined, pluralDrinks(removedCount)));
    const note = specialsEl(
      "p",
      "specials-section-note",
      undefined,
      "Drinks rung on this business day where a manager took the special off. The guest paid full price.",
    );
    const card = specialsEl("div", "list-card", "closeout-removed-specials");
    const details = specialsEl("details", "removed-specials");
    const summaryEl = document.createElement("summary");
    const left = specialsEl("div", "");
    left.append(
      specialsEl(
        "div",
        "rm-title",
        undefined,
        `${removedCount} special${removedCount === 1 ? "" : "s"} removed`,
      ),
      specialsEl(
        "div",
        "rm-sub",
        undefined,
        `Guests would have saved ${formatCents(summary.activityByServiceDate.removedSpecialCents)}`,
      ),
    );
    const right = specialsEl("div", "rm-right");
    right.append(
      specialsEl(
        "span",
        "rm-amt",
        undefined,
        formatCents(summary.activityByServiceDate.removedSpecialCents),
      ),
      specialsEl("span", "rm-chev", undefined, "\u203a"),
    );
    summaryEl.append(left, right);
    summaryEl.dataset["testid"] = "closeout-removed-specials-toggle";
    const reasons = specialsEl("div", "rm-reasons", "closeout-removed-reasons");
    for (const reason of removedSpecialsByReason) {
      const line = specialsEl("div", "rm-reason", "closeout-removed-reason-row");
      line.append(
        specialsEl("span", "", undefined, reason.reason),
        specialsEl(
          "span",
          "rm-reason-detail",
          undefined,
          `${pluralDrinks(reason.count)} · ${formatCents(reason.cents)}`,
        ),
      );
      reasons.append(line);
    }
    details.append(summaryEl, reasons);
    card.append(details);
    closeoutSpecialsEl.append(title, note, card);
  }

  closeoutSpecialsEl.append(
    specialsEl(
      "p",
      "specials-basis",
      "closeout-specials-basis",
      "Applied specials above count by the day they were paid. Removed specials count by the day the drink was rung, the same way Voids do.",
    ),
  );
}

/** "-$13.00" or "+$1.00": the money that moved against the menu price. */
function formatSignedCents(cents: number): string {
  if (cents === 0) {
    return formatCents(0);
  }
  return cents < 0 ? `-${formatCents(-cents)}` : `+${formatCents(cents)}`;
}

/** M5-F1-F1: the closeout's "Below cost" tag. Stronger than "Half off or more":
 * a bold lead and the deep amber border. Tokens only. */
function belowCostTag(rest: string): HTMLElement {
  const tag = specialsEl(
    "span",
    "cpc-tag cpc-tag-warn cpc-tag-below-cost",
    "closeout-price-change-below-cost",
  );
  const lead = document.createElement("strong");
  lead.textContent = "Below cost";
  tag.append(lead, rest);
  return tag;
}

/** M5 (DEC-282). The "Price changes at the till" block: each drink whose
 * price a person typed, with who set it, who approved it, why, and the two
 * flags. Every figure is the server's; this does no money arithmetic. Hidden
 * on a day with none. Plain text only, nothing wraps into hidden content and
 * nothing depends on hover. */
function renderCloseoutPriceChanges(summary: CloseoutSummaryDto): void {
  closeoutPriceChangesEl.innerHTML = "";
  const changes = summary.priceChanges;
  closeoutPriceChangesEl.hidden = changes.count === 0;
  if (changes.count === 0) {
    return;
  }
  const title = specialsEl("h2", "section-title", undefined, "Price changes at the till ");
  title.append(
    specialsEl(
      "span",
      "section-count",
      "closeout-price-changes-count",
      pluralDrinks(changes.count),
    ),
  );
  const note = specialsEl(
    "p",
    "specials-section-note",
    undefined,
    "Drinks where someone typed a different price on the ticket. Each one has a reason and the manager who approved it.",
  );
  const card = specialsEl("div", "list-card", "closeout-price-change-list");
  for (const row of changes.rows) {
    const line = specialsEl("div", "cpc-row", "closeout-price-change-row");
    line.dataset["orderLineId"] = row.orderLineId;
    const name =
      row.orderName === null ? row.itemReference : `${row.itemReference} \u00b7 ${row.orderName}`;
    const money = specialsEl("div", "cpc-money");
    money.append(
      specialsEl("span", "cpc-was", undefined, formatCents(row.wasCents)),
      specialsEl("span", "cpc-now", undefined, formatCents(row.nowCents)),
      specialsEl("span", "cpc-diff", undefined, formatSignedCents(row.differenceCents)),
    );
    const detail = specialsEl("div", "cpc-detail");
    const when = new Date(row.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const who =
      (row.setBy === null ? "" : `${row.setBy} set it`) +
      (row.approvedBy === null ? "" : `, ${row.approvedBy} approved`);
    detail.append(
      specialsEl("span", "", undefined, when),
      ...(who === "" ? [] : [specialsEl("span", "", undefined, who)]),
      specialsEl("span", "cpc-tag", "closeout-price-change-reason", row.reason),
      ...(row.halfOffOrMore
        ? [
            specialsEl(
              "span",
              "cpc-tag cpc-tag-warn",
              "closeout-price-change-flag",
              "Half off or more",
            ),
          ]
        : []),
      ...(row.wentUp
        ? [specialsEl("span", "cpc-tag", "closeout-price-change-flag", "Price went up")]
        : []),
      // M5-F1-F1 (DEC-311): from the cost snapshotted when the price was set.
      ...(row.belowCost && row.drinkCostCents !== null
        ? [
            belowCostTag(
              `: ingredients ${row.drinkCostBasis === "lower_bound" ? "at least" : "about"} ${formatCents(row.drinkCostCents)}`,
            ),
          ]
        : []),
    );
    // M5-R10 (DEC-355): the History button, with the drink's change count.
    const history = specialsEl("button", "cpc-history", "closeout-price-change-history");
    history.type = "button";
    history.setAttribute(
      "aria-label",
      `Price history for ${row.itemReference}${row.orderName === null ? "" : `, ${row.orderName}`}`,
    );
    history.append(
      specialsEl(
        "span",
        "cpc-history-count",
        "closeout-price-change-history-count",
        String(row.changeCount),
      ),
      "History",
    );
    history.addEventListener("click", () =>
      openPriceHistorySheet({
        title: row.itemReference,
        subtitle: `${row.orderName ?? "Walk-in"} \u00b7 ${businessDateLabel(summary.businessDate)}`,
        load: loadPriceHistoryFor(row.orderId, row.orderLineId),
        onClosed: () => history.focus(),
      }),
    );
    line.append(specialsEl("div", "cpc-name", undefined, name), money, history, detail);
    card.append(line);
  }
  const foot = specialsEl(
    "p",
    "specials-basis",
    "closeout-price-changes-basis",
    `Lowered ${pluralDrinks(changes.loweredCount)}, ${formatSignedCents(-changes.loweredCents)}. ` +
      `Raised ${pluralDrinks(changes.raisedCount)}, ${formatSignedCents(changes.raisedCents)}. ` +
      `Net ${formatSignedCents(changes.netCents)}. Counted on the day the drink was rung, the same way Voids are. ` +
      "Tax is figured on the new prices.",
  );
  closeoutPriceChangesEl.append(title, note, card, foot);
}

/** AU14-F4 / DEC-276. "Actions with a reason": the actions that now require
 * one, on the business night they happened, newest first. A count card per kind
 * (reopens, with "N was already paid"; 86'd drinks sold, with the items) and one
 * list: time, a tag, the ticket, who did it and who approved, and the reason on
 * the right. An action from before the reason was required reads "No reason
 * recorded (before this change)", never blank and never invented. Approved and finished, so it is
 * a record to read, not a task, and it is not on Manager Exceptions. Every
 * figure is the server's; this does no arithmetic. Hidden on a night with
 * none. */
function renderCloseoutActionsWithReason(summary: CloseoutSummaryDto): void {
  closeoutActionsWithReasonEl.innerHTML = "";
  const reopens = summary.actionsWithReason.reopens;
  // AU14-F15: 86 sales share the block; a summary from a server that predates
  // them simply has none.
  const stockOverrides = summary.actionsWithReason.stockOverrides ?? { count: 0, rows: [] };
  // AU14-F5: discounts taken off share the block too.
  const discountRemovals = summary.actionsWithReason.discountRemovals ?? {
    count: 0,
    cents: 0,
    rows: [],
  };
  const nothingToShow =
    reopens.count === 0 && stockOverrides.count === 0 && discountRemovals.count === 0;
  closeoutActionsWithReasonEl.hidden = nothingToShow;
  if (nothingToShow) {
    return;
  }
  const title = specialsEl("h2", "section-title", undefined, "Actions with a reason");
  const note = specialsEl(
    "p",
    "specials-section-note",
    undefined,
    "Each one was approved and finished. They are here so you can see who did it and why. Nothing here needs a task.",
  );
  // CLOSEOUT1: the three counts are one line of facts above the table, not three cards.
  const summaryLine = specialsEl("div", "closeout-actions-summary", "closeout-actions-cards");
  const addSummary = (cardTestId: string, label: string, countTestId: string, value: string): HTMLElement => {
    const item = specialsEl("span", "closeout-actions-summary-item", cardTestId);
    item.append(
      specialsEl("span", "closeout-actions-summary-label", undefined, `${label} `),
      specialsEl("strong", "closeout-actions-summary-value", countTestId, value),
    );
    summaryLine.append(item);
    return item;
  };
  if (reopens.count > 0) {
    const item = addSummary(
      "closeout-actions-reopened-card",
      "Tickets reopened",
      "closeout-actions-reopened-count",
      String(reopens.count),
    );
    if (reopens.alreadyPaidCount > 0) {
      item.append(
        specialsEl(
          "small",
          "closeout-actions-summary-note",
          "closeout-actions-reopened-paid",
          `${reopens.alreadyPaidCount} ${reopens.alreadyPaidCount === 1 ? "was" : "were"} already paid`,
        ),
      );
    }
  }
  if (stockOverrides.count > 0) {
    const item = addSummary(
      "closeout-actions-stock-card",
      "86'd drinks sold",
      "closeout-actions-stock-count",
      String(stockOverrides.count),
    );
    // The items, once each, in the order they were sold (newest first).
    const items = [
      ...new Set(
        stockOverrides.rows
          .map((row) => row.productName)
          .filter((name): name is string => name !== null),
      ),
    ];
    if (items.length > 0) {
      item.append(
        specialsEl("small", "closeout-actions-summary-note", "closeout-actions-stock-items", items.join(", ")),
      );
    }
  }
  if (discountRemovals.count > 0) {
    const item = addSummary(
      "closeout-actions-discount-card",
      "Discounts taken off",
      "closeout-actions-discount-count",
      String(discountRemovals.count),
    );
    item.append(
      specialsEl(
        "small",
        "closeout-actions-summary-note",
        "closeout-actions-discount-cents",
        `${formatCents(discountRemovals.cents)} back on the tickets`,
      ),
    );
  }
  // One table, newest first, the kinds interleaved by when they happened.
  interface ActionRow {
    at: string;
    kind: "reopen" | "stock" | "discount";
    orderId: string;
    what: string;
    who: string;
    reason: string | null;
  }
  const actionRows: ActionRow[] = [
    ...reopens.rows.map((row): ActionRow => ({
      at: row.at,
      kind: "reopen",
      orderId: row.orderId,
      what: row.ticketName ?? "Walk-in",
      who:
        (row.reopenedBy ?? "") + (row.approvedBy === null ? "" : `, approved by ${row.approvedBy}`),
      reason: row.reason,
    })),
    ...stockOverrides.rows.map((row): ActionRow => ({
      at: row.at,
      kind: "stock",
      orderId: row.orderId,
      what: `${row.productName ?? "An 86'd item"}, ${row.ticketName ?? "Walk-in"}`,
      who:
        (row.overriddenBy ?? "") +
        (row.approvedBy === null ? "" : `, approved by ${row.approvedBy}`),
      reason: row.reason,
    })),
    ...discountRemovals.rows.map((row): ActionRow => ({
      at: row.at,
      kind: "discount",
      orderId: row.orderId,
      what: `${row.discountName}, ${row.ticketName ?? "Walk-in"}`,
      who:
        (row.removedBy ?? "") + (row.approvedBy === null ? "" : `, approved by ${row.approvedBy}`),
      reason: row.reason,
    })),
  ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  const table = specialsEl("table", "closeout-actions-table", "closeout-actions-list");
  const head = document.createElement("thead");
  const headRow = document.createElement("tr");
  for (const heading of ["Time", "Action", "What", "By", "Reason"]) {
    const th = document.createElement("th");
    th.scope = "col";
    th.textContent = heading;
    headRow.append(th);
  }
  head.append(headRow);
  const body = document.createElement("tbody");
  for (const row of actionRows) {
    const line = specialsEl("tr", "ar-row", "closeout-actions-row");
    line.dataset["orderId"] = row.orderId;
    line.dataset["kind"] = row.kind;
    const time = specialsEl(
      "td",
      "ar-time",
      undefined,
      new Date(row.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
    );
    const action = document.createElement("td");
    action.append(
      specialsEl(
        "span",
        row.kind === "stock" ? "ar-tag ar-tag-stock" : "ar-tag",
        undefined,
        row.kind === "stock"
          ? "Sold past an 86"
          : row.kind === "discount"
            ? "Taken off"
            : "Reopened",
      ),
    );
    const what = specialsEl("td", "ar-what", undefined, row.what);
    const who = specialsEl("td", "ar-who", "closeout-actions-who", row.who);
    const why = specialsEl(
      "td",
      row.reason === null ? "ar-why ar-why-none" : "ar-why",
      "closeout-actions-reason",
      row.reason ?? "No reason recorded (before this change)",
    );
    line.append(time, action, what, who, why);
    body.append(line);
  }
  table.append(head, body);
  const tableWrap = specialsEl("div", "closeout-actions-table-wrap");
  tableWrap.append(table);
  const foot = specialsEl(
    "p",
    "specials-basis",
    "closeout-actions-basis",
    "Counted on the business night the action happened. Reasons are kept exactly as they read then, even if a manager renames one later.",
  );
  closeoutActionsWithReasonEl.append(title, note, summaryLine, tableWrap, foot);
}

/** CLOSEOUT1: the day's money as one waterfall plus the side counts. Every figure is one the closeout API returns. */
function renderCloseoutMoney(summary: CloseoutSummaryDto): void {
  const model = buildWaterfall(summary.netSales, summary.revenueByCollectionDate);
  closeoutMoneyTakeawayEl.textContent = model.takeaway;
  renderCloseoutWaterfall(closeoutWaterfallEl, model);
  const priceChanges = summary.priceChanges;
  // M5: said once, in the chart's note, because the typed prices are already inside the sales the chart starts from.
  closeoutWaterfallNoteEl.textContent = [
    model.footnote,
    priceChanges.count > 0 ? "Sales already include price changes." : "",
  ]
    .filter((part) => part !== "")
    .join(" ");
  closeoutWaterfallNoteEl.hidden = closeoutWaterfallNoteEl.textContent === "";
  // CLOSEFIX1: the test-provider note shows only while the payment provider is the simulated one.
  closeoutSimulatedNoteEl.hidden = !summary.paymentsSimulated;

  closeoutSideStatsEl.replaceChildren();
  const activity = summary.activityByServiceDate;
  const addStat = (key: string, label: string, value: string, note?: string): void => {
    const row = specialsEl("div", "closeout-stat", "closeout-stat");
    row.dataset["stat"] = key;
    row.append(specialsEl("dt", "closeout-stat-label", undefined, label));
    const dd = specialsEl("dd", "closeout-stat-value", undefined, value);
    if (note !== undefined) {
      dd.append(specialsEl("small", "closeout-stat-note", undefined, note));
    }
    row.append(dd);
    closeoutSideStatsEl.append(row);
  };
  // CORE11: Tickets paid is the Sales by day report's ticket count (a ticket with a payment collected this day), not Tabs closed.
  addStat("tickets_paid", "Tickets paid", String(summary.netSales.ticketsPaid));
  addStat("drinks_sold", "Drinks sold", String(activity.drinksSold));
  addStat("tabs_closed", "Tabs closed", String(activity.tabsClosed));
  // CORE1: only on a day a tab was walked out on. What the walked tabs owed was never collected.
  if (activity.walkoutCount > 0) {
    addStat(
      "walkouts",
      "Walkouts",
      formatCents(activity.walkoutOwedCents),
      `${activity.walkoutCount} tab${activity.walkoutCount === 1 ? "" : "s"}` +
        (activity.walkoutLateCount > 0
          ? `, ${activity.walkoutLateCount} late (${formatCents(activity.walkoutLateOwedCents)})`
          : ""),
    );
  }
  // M5 (DEC-282): only on a day that had a price change. The net of what was typed against the menu price, not a discount.
  if (priceChanges.count > 0) {
    addStat(
      "price_changes",
      "Price changes",
      formatSignedCents(priceChanges.netCents),
      `${pluralDrinks(priceChanges.count)} · not a discount`,
    );
  }
}

/** CLOSEOUT1: the verdict, the exception rows (a check that needs a look) and the compact list of checks that passed. */
function renderCloseoutChecks(summary: CloseoutSummaryDto): void {
  const checks = buildCloseoutChecks(summary);
  const verdict = buildCloseoutVerdict(checks);
  const byKey = new Map<string, CloseoutCheck>(checks.map((check) => [check.key, check]));
  for (const section of closeoutExceptionsEl.querySelectorAll<HTMLElement>(".closeout-exc")) {
    const check = byKey.get(section.dataset["check"] ?? "");
    const visible = check !== undefined && (check.state === "fail" || check.showDetail === true);
    section.hidden = !visible;
    if (!visible || check === undefined) {
      continue;
    }
    const chip = section.querySelector<HTMLElement>(".closeout-exc-chip");
    if (chip !== null) {
      chip.className = `fl-chip fl-chip-${check.tone ?? "bad"} closeout-exc-chip`;
      const symbol = chip.querySelector<HTMLElement>(".fl-chip-symbol");
      if (symbol !== null) {
        symbol.textContent = check.tone === "info" ? "i" : "!";
      }
      const chipText = chip.querySelector<HTMLElement>(".closeout-exc-chip-text");
      if (chipText !== null) {
        chipText.textContent = check.chipText ?? check.label;
      }
    }
    const title = section.querySelector<HTMLElement>(".closeout-exc-title");
    if (title !== null) {
      title.textContent = check.title ?? check.label;
    }
    const detail = section.querySelector<HTMLElement>(".closeout-exc-detail");
    if (detail !== null) {
      detail.textContent = check.detail ?? "";
    }
  }

  closeoutVerdictChipEl.className = `fl-chip fl-chip-${verdict.tone}`;
  closeoutVerdictSymbolEl.textContent = verdict.chipSymbol;
  closeoutVerdictChipTextEl.textContent = verdict.chipText;
  closeoutVerdictTitleEl.textContent = verdict.title;
  closeoutVerdictHelpEl.textContent = verdict.help;
  closeoutNotFinalizedBannerEl.dataset["tone"] = verdict.tone;

  closeoutChecksEl.replaceChildren();
  if (verdict.failCount === 0) {
    for (const check of checks) {
      const item = specialsEl("li", "closeout-check", "closeout-check");
      item.dataset["check"] = check.key;
      item.dataset["state"] = check.state;
      item.append(
        specialsEl("span", check.state === "pass" ? "closeout-check-ok" : "closeout-check-info", undefined, check.state === "pass" ? "\u2713" : "i"),
        specialsEl("span", "closeout-check-label", undefined, check.label),
        specialsEl("span", "closeout-check-value", undefined, check.value),
      );
      closeoutChecksEl.append(item);
    }
  }
  closeoutChecksEl.hidden = verdict.failCount !== 0;
  const passed = verdict.failCount === 0 ? "" : passedSummaryLine(checks);
  closeoutPassedLineEl.textContent = passed;
  closeoutPassedLineEl.hidden = passed === "";
}

function closeoutEmptyRow(text: string): HTMLElement {
  const el = document.createElement("p");
  el.className = "list-card-empty";
  el.textContent = text;
  return el;
}

function renderCloseoutSummary(
  summary: CloseoutSummaryDto,
  guestLabelsByOrderId: Map<string, Map<string, string>>,
): void {
  // UX102: the day reads as Reports read it ("Thu Sep 24"), not as an ISO date.
  closeoutBusinessDateEl.textContent = `Business day: ${dayLabel(summary.businessDate)}`;
  state.closeoutSummary = summary;
  // CL6 day navigation: never step forward past today.
  closeoutNextDayButton.disabled =
    state.tenantBusinessDate === undefined || summary.businessDate >= state.tenantBusinessDate;

  // CL6 banners: exactly one of the two shows. The finalized banner
  // names who and when; the Finalize action shows only to a holder of
  // report.closeout.finalize (the same resolved-permission snapshot
  // every other UI gate reads, never a role name).
  const session = state.session;
  if (summary.finalization !== null) {
    closeoutNotFinalizedBannerEl.hidden = true;
    closeoutFinalizedBannerEl.hidden = false;
    const finalizedAt = new Date(summary.finalization.finalizedAt);
    // PO6-S1-F2 sweep finding: this used to fall back to the raw
    // actorUserId (a UUID) whenever actorDisplayName failed to resolve.
    // ./actorDisplay.ts's shared fallback, the same one the purchase
    // order screen uses.
    const closedBy = summary.finalization.closedAutomatically
      ? "Closed automatically"
      : `Finalized by ${resolveActorDisplayName(summary.finalization.actorDisplayName)}`;
    // CARRYOVER1 (DEC-556): the day closed on time with these tickets still open; nothing is shown when none were.
    const carriedOver = summary.finalization.carriedOver;
    closeoutCarriedOverEl.hidden = carriedOver.count === 0;
    closeoutCarriedOverEl.textContent =
      carriedOver.count === 0
        ? ""
        : `Carried over: ${carriedOver.count} ticket${carriedOver.count === 1 ? "" : "s"}` +
          (carriedOver.owedWithTaxCents === null ? "" : `, ${formatCents(carriedOver.owedWithTaxCents)} still owed`);
    closeoutFinalizedDetailEl.textContent =
      `${closedBy} at ${finalizedAt.toLocaleString()}.` +
      " The recorded day cannot be edited; anything collected later counts on its own day." +
      (summary.finalization.activityByServiceDate.walkoutCount === null
        ? " Walkouts were not recorded at finalization; the figure below is current, not historical."
        : "");
  } else {
    closeoutNotFinalizedBannerEl.hidden = false;
    closeoutFinalizedBannerEl.hidden = true;
    closeoutCarriedOverEl.hidden = true;
    closeoutFinalizeButton.hidden =
      session === undefined || !hasPermission(session, "report.closeout.finalize");
    // CORE13: a day that is not over yet cannot be finalized, so say so BEFORE the tap: the button is
    // disabled and the line beside it names the time it opens (the tenant's own cutoff, from the
    // server's own rule), instead of failing with a toast after the tap.
    const availability = summary.finalizeAvailability;
    const waiting = availability !== null && !availability.dayIsOver;
    closeoutFinalizeButton.disabled = waiting;
    closeoutFinalizeAvailabilityEl.hidden = !waiting || closeoutFinalizeButton.hidden;
    closeoutFinalizeAvailabilityEl.textContent = waiting
      ? `Available after ${cutoffText(availability.cutoffTimeLocal)} on ${dayLabel(availability.availableOnDate)}.`
      : "";
  }
  // CORE13: the export is for holders of report.export (the route also needs report.closeout.view,
  // which getting this screen already requires).
  closeoutExportButton.hidden = session === undefined || !hasPermission(session, "report.export");

  renderCloseoutMoney(summary);

  renderCloseoutCash(closeoutCashEl, summary);
  renderCloseoutAwaitingTip(closeoutAwaitingTipEl, summary);
  renderCloseoutSpecials(summary);
  renderCloseoutPriceChanges(summary);
  renderCloseoutActionsWithReason(summary);

  // Design/QA gate finding F: per-maker tip totals for the day. Plain
  // staff language, same "no rows means literally none, never a
  // synthesized zero" framing as src/domain/closeout.ts's own
  // SELECT_TIPS_BY_MAKER_SQL comment.
  closeoutTipsByMakerCountEl.textContent = `${summary.tipsByMaker.length} maker${summary.tipsByMaker.length === 1 ? "" : "s"}`;
  closeoutTipsByMakerListEl.innerHTML = "";
  if (summary.tipsByMaker.length === 0) {
    closeoutTipsByMakerListEl.append(closeoutEmptyRow("No tips recorded yet today."));
  }
  // CLOSEOUT1: one row per maker, its fill length is that maker's share of the largest; the figure is the server's own.
  const largestTip = Math.max(1, ...summary.tipsByMaker.map((maker) => maker.amountCents));
  for (const maker of summary.tipsByMaker) {
    const row = document.createElement("div");
    row.className = "closeout-maker-row";
    row.dataset["testid"] = "closeout-tips-by-maker-row";
    const name = document.createElement("span");
    name.className = "closeout-maker-name";
    // PO6-S1-F2 sweep finding: this used to fall back to the raw
    // employeeId (a UUID) whenever displayName failed to resolve.
    name.textContent = resolveActorDisplayName(maker.displayName);
    const track = document.createElement("span");
    track.className = "closeout-maker-track";
    track.setAttribute("aria-hidden", "true");
    const fill = document.createElement("i");
    fill.style.width = `${Math.max(2, Math.round((maker.amountCents / largestTip) * 100))}%`;
    track.append(fill);
    const amount = document.createElement("span");
    amount.className = "closeout-maker-amount";
    amount.textContent = formatCents(maker.amountCents);
    row.append(name, track, amount);
    closeoutTipsByMakerListEl.append(row);
  }

  closeoutReopenedCountEl.textContent = `${summary.reopenedTabActivity.length} tab${summary.reopenedTabActivity.length === 1 ? "" : "s"}`;
  closeoutReopenedListEl.innerHTML = "";
  closeoutReopenedSectionEl.hidden = summary.reopenedTabActivity.length === 0;
  for (const activity of summary.reopenedTabActivity) {
    const row = document.createElement("div");
    row.className = "activity-row";
    row.dataset["testid"] = "closeout-reopened-row";
    const main = document.createElement("div");
    const name = document.createElement("div");
    name.className = "activity-name";
    name.textContent = activity.name ?? "Walk-in";
    const detail = document.createElement("div");
    detail.className = "activity-detail";
    const paymentCount = activity.capturedPayments.length;
    detail.textContent =
      paymentCount === 0
        ? `Reopened ${activity.reopenedAtTimestamps.length} time(s) · no payment captured yet`
        : `${paymentCount} payment${paymentCount === 1 ? "" : "s"} captured · most recent $${activity.capturedPayments[activity.capturedPayments.length - 1]?.amount}`;
    main.append(name, detail);
    // ALPHA-12 item 3: per-guest and per-share activity, alongside the
    // amount and time this list already showed. Only rendered when at
    // least one capture actually carries a guest or share tag (a plain
    // whole-ticket payment adds nothing here, matching "low text" for
    // staff-facing boards).
    const taggedPayments = activity.capturedPayments.filter(
      (p) => p.guestId !== null || p.splitShare !== null,
    );
    if (taggedPayments.length > 0) {
      const tagList = document.createElement("div");
      tagList.className = "activity-detail";
      tagList.dataset["testid"] = "closeout-reopened-guest-share-detail";
      const parts = taggedPayments.map((p) => {
        if (p.splitShare !== null) {
          return `$${p.amount} · Share ${p.splitShare.shareIndex} of ${p.splitShare.shareCount}`;
        }
        const label = guestLabelsByOrderId.get(activity.orderId)?.get(p.guestId as string);
        return `$${p.amount} · ${label ?? "guest"}'s drinks`;
      });
      tagList.textContent = parts.join(" · ");
      main.append(tagList);
    }
    const tag = document.createElement("span");
    tag.className = "activity-tag";
    tag.textContent = paymentCount > 1 ? `${paymentCount} payments captured` : "Reopened";
    row.append(main, tag);
    closeoutReopenedListEl.append(row);
  }

  closeoutOpenTabsListEl.innerHTML = "";
  // SHIFT1: one line per bartender with how many of the tabs they opened are still open.
  closeoutOpenTabsByBartenderEl.innerHTML = "";
  const byBartender = groupOpenTabsByOpener(summary.openTabsAtDayEnd);
  closeoutOpenTabsByBartenderEl.hidden = byBartender.length === 0;
  for (const entry of byBartender) {
    const line = document.createElement("div");
    line.className = "closeout-open-tabs-by-bartender-row";
    line.dataset["testid"] = "closeout-open-tabs-by-bartender-row";
    line.textContent = `${entry.name}: ${entry.count} open`;
    closeoutOpenTabsByBartenderEl.append(line);
  }
  if (summary.openTabsAtDayEnd.length === 0) {
    closeoutOpenTabsListEl.append(closeoutEmptyRow("No open tabs."));
  }
  for (const tab of summary.openTabsAtDayEnd) {
    const row = document.createElement("div");
    row.className = "unresolved-row";
    row.dataset["testid"] = "closeout-open-tab-row";
    const main = document.createElement("div");
    main.className = "unresolved-main";
    const name = document.createElement("div");
    name.className = "unresolved-name";
    name.textContent = tab.name ?? "Walk-in";
    if (tab.reopened) {
      const tag = document.createElement("span");
      tag.className = "unresolved-tag";
      tag.textContent = " Reopened";
      name.append(" ", tag);
    }
    const detail = document.createElement("div");
    detail.className = "unresolved-detail";
    detail.textContent = `${tab.itemCount} item${tab.itemCount === 1 ? "" : "s"}`;
    main.append(name, detail);
    const amount = document.createElement("div");
    amount.className = "unresolved-amount";
    const amountValue = document.createElement("div");
    amountValue.className = "unresolved-amount-value";
    amountValue.dataset["testid"] = "closeout-open-tab-outstanding";
    // Re-gate item 5 (Medium): a tab with an earlier delta payment already
    // captured against it (src/domain/payments.ts's uncovered-lines model)
    // used to show only the full ticket total here, with no qualifier, so
    // a manager had no way to tell "$30 owed" from "$30 total, $20
    // already collected." The outstanding balance (what a real Pay right
    // now would actually charge) is the prominent figure since collection
    // is the use case; the ticket total is shown alongside it only when
    // the two differ, in plain staff language per docs/current-state.md's
    // staff-facing-boards low-text rule.
    // LOCFLOOR1 (sweep B1): the amount is what a Pay would charge, tax included (owedWithTaxCents), so it matches the
    // Open tabs card; totalCents is the pre-tax ticket total and is labeled as such where it is shown beside it.
    if (tab.outstandingCents === 0) {
      amountValue.textContent = "Nothing left to collect";
    } else if (tab.outstandingCents === tab.totalCents) {
      amountValue.textContent = formatCents(tab.owedWithTaxCents);
    } else {
      amountValue.textContent = `Still to collect ${formatCents(tab.owedWithTaxCents)}`;
    }
    amount.append(amountValue);
    if (tab.outstandingCents > 0) {
      const amountDetail = document.createElement("div");
      amountDetail.className = "unresolved-amount-detail";
      amountDetail.textContent =
        tab.outstandingCents === tab.totalCents
          ? "with tax"
          : `with tax, of ${formatCents(tab.totalCents)} before tax`;
      amount.append(amountDetail);
    }
    row.append(main, amount);
    closeoutOpenTabsListEl.append(row);
  }

  closeoutUnpriceableListEl.innerHTML = "";
  if (summary.unpriceableLines.length === 0) {
    closeoutUnpriceableListEl.append(closeoutEmptyRow("No unpriceable lines."));
  }
  for (const line of summary.unpriceableLines) {
    const row = document.createElement("div");
    row.className = "flagged-row";
    row.dataset["testid"] = "closeout-unpriceable-row";
    const head = document.createElement("div");
    head.className = "flagged-head";
    const nameBlock = document.createElement("div");
    const name = document.createElement("div");
    name.className = "flagged-name";
    name.textContent = line.itemReference;
    const tab = document.createElement("div");
    tab.className = "flagged-tab";
    tab.textContent = `Tab: ${line.orderName ?? "Walk-in"}`;
    nameBlock.append(name, tab);
    const tag = document.createElement("span");
    tag.className = "unresolved-tag";
    tag.textContent = "Needs a manager";
    head.append(nameBlock, tag);
    const reason = document.createElement("div");
    reason.className = "flagged-reason";
    reason.textContent =
      "This line has no stored price. A manager needs to confirm it before this tab totals correctly.";
    row.append(head, reason);
    closeoutUnpriceableListEl.append(row);
  }

  renderCloseoutUnresolvedPaymentsAndSystems(summary);
  renderCloseoutChecks(summary);
}

// CL7: failed/unknown payments, terminal registration, and MyTime sync.
// Kept as its own function (rather than inline in renderCloseoutSummary,
// already long) but still called from there, same "one render pass per
// summary load" shape every other section above uses.
function renderCloseoutUnresolvedPaymentsAndSystems(summary: CloseoutSummaryDto): void {
  // Failed: decided, no block. Plain activity-row shape (a "Failed" tag,
  // not "Needs a manager"): nothing here is stuck, a fresh Pay clears it.
  closeoutFailedPaymentsListEl.innerHTML = "";
  if (summary.failedPayments.length === 0) {
    closeoutFailedPaymentsListEl.append(closeoutEmptyRow("No failed payments today."));
  }
  for (const payment of summary.failedPayments) {
    const row = document.createElement("div");
    row.className = "activity-row";
    row.dataset["testid"] = "closeout-failed-payment-row";
    const main = document.createElement("div");
    const name = document.createElement("div");
    name.className = "activity-name";
    name.textContent = payment.orderName ?? "Walk-in";
    const detail = document.createElement("div");
    detail.className = "activity-detail";
    detail.textContent = `$${payment.amount} · ${new Date(payment.attemptedAt).toLocaleTimeString()}`;
    main.append(name, detail);
    const tag = document.createElement("span");
    tag.className = "activity-tag";
    tag.textContent = "Failed";
    row.append(main, tag);
    closeoutFailedPaymentsListEl.append(row);
  }

  // Pending: a genuinely non-terminal outcome that BLOCKS this order's
  // next Pay/Close (DEC-022). Rendered like unpriceableLines' own
  // "Needs a manager" shape, since it genuinely is one: only a manager
  // confirming the real outcome with the provider clears it.
  closeoutPendingPaymentsListEl.innerHTML = "";
  if (summary.pendingReconciliationPayments.length === 0) {
    closeoutPendingPaymentsListEl.append(closeoutEmptyRow("No payments awaiting confirmation."));
  }
  for (const payment of summary.pendingReconciliationPayments) {
    const row = document.createElement("div");
    row.className = "flagged-row";
    row.dataset["testid"] = "closeout-pending-payment-row";
    const head = document.createElement("div");
    head.className = "flagged-head";
    const nameBlock = document.createElement("div");
    const name = document.createElement("div");
    name.className = "flagged-name";
    name.textContent = `${payment.orderName ?? "Walk-in"} · $${payment.amount}`;
    const tab = document.createElement("div");
    tab.className = "flagged-tab";
    tab.textContent = `Attempted ${new Date(payment.attemptedAt).toLocaleString()}`;
    nameBlock.append(name, tab);
    const tag = document.createElement("span");
    tag.className = "unresolved-tag";
    tag.textContent = "Needs a manager";
    head.append(nameBlock, tag);
    const reason = document.createElement("div");
    reason.className = "flagged-reason";
    reason.textContent =
      "This payment hasn't gotten a final answer from the provider. The ticket can't be paid again " +
      "or closed until a manager confirms what really happened.";
    // PAY35: a manager re-reads the provider now. Clears the row only when the processor shows it dead.
    const checkButton = document.createElement("button");
    checkButton.type = "button";
    checkButton.className = "btn-secondary";
    checkButton.dataset["testid"] = "closeout-pending-payment-check";
    checkButton.textContent = CHECK_PAYMENT_LABEL;
    checkButton.addEventListener("click", () => {
      checkButton.disabled = true;
      checkButton.textContent = CHECK_PAYMENT_BUSY_LABEL;
      void performCheckPayment(payment.paymentAttemptId);
    });
    row.append(head, reason, checkButton);
    closeoutPendingPaymentsListEl.append(row);
  }

  renderCloseoutOutsideCollections(summary.outsideCollections);
  renderCloseoutSettlement(summary.settlement);

  // Card readers: the registration roster (CL7) with each active reader's live status and last seen (CL8), N/A when the
  // server did not read it. A reader deactivated today keeps its CL7 tag.
  closeoutTerminalStatusListEl.innerHTML = "";
  const { activeTerminals, deactivatedToday } = summary.terminalStatus;
  if (activeTerminals.length === 0 && deactivatedToday.length === 0) {
    closeoutTerminalStatusListEl.append(closeoutEmptyRow("No card readers registered."));
  }
  for (const terminal of activeTerminals) {
    const row = document.createElement("div");
    row.className = "activity-row";
    row.dataset["testid"] = "closeout-terminal-active-row";
    const main = document.createElement("div");
    const name = document.createElement("div");
    name.className = "activity-name";
    name.textContent = terminal.displayName;
    // CL8: the reader's live status and last seen on one line under its name, N/A when the server did not read it.
    const line = document.createElement("div");
    line.className = "activity-detail";
    line.dataset["testid"] = "closeout-terminal-health";
    line.textContent = closeoutReaderLine(terminal.health, (iso) =>
      new Date(iso).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    );
    main.append(name, line);
    row.append(main);
    closeoutTerminalStatusListEl.append(row);
  }
  for (const terminal of deactivatedToday) {
    const row = document.createElement("div");
    row.className = "activity-row";
    row.dataset["testid"] = "closeout-terminal-deactivated-row";
    const main = document.createElement("div");
    const name = document.createElement("div");
    name.className = "activity-name";
    name.textContent = terminal.displayName;
    const detail = document.createElement("div");
    detail.className = "activity-detail";
    detail.textContent = `Deactivated ${new Date(terminal.deactivatedAt).toLocaleTimeString()}`;
    main.append(name, detail);
    const tag = document.createElement("span");
    tag.className = "activity-tag";
    tag.textContent = "Deactivated today";
    row.append(main, tag);
    closeoutTerminalStatusListEl.append(row);
  }

  // MyTime sync: reuses MT1's own freshness read (getMyTimeClientSyncFreshness),
  // shown here in condensed form; admin.ts's renderMyTimeSyncFreshness in
  // Settings > Operations carries the full detail.
  closeoutSyncStatusBodyEl.innerHTML = "";
  const sync = summary.syncStatus;
  const statusLine = document.createElement("div");
  statusLine.className = "activity-row";
  statusLine.dataset["testid"] = "closeout-sync-status-line";
  const statusName = document.createElement("div");
  statusName.className = "activity-name";
  statusName.textContent =
    sync.lastSucceededAt === null
      ? "Never synced"
      : `Last synced ${formatCloseoutSyncAge(sync.hoursSinceLastSuccess)}`;
  const statusTag = document.createElement("span");
  statusTag.className = "activity-tag";
  statusTag.textContent = sync.stale ? "Stale" : "Fresh";
  statusLine.append(statusName, statusTag);
  closeoutSyncStatusBodyEl.append(statusLine);
  if (sync.lastAttemptStatus === "failed") {
    const failureLine = document.createElement("div");
    failureLine.className = "flagged-reason";
    failureLine.dataset["testid"] = "closeout-sync-status-last-attempt-failed";
    failureLine.textContent = `Most recent sync attempt failed: ${
      sync.lastAttemptErrorMessage ?? "no error message recorded"
    }.`;
    closeoutSyncStatusBodyEl.append(failureLine);
  }
}

function formatCloseoutSyncAge(hoursSinceLastSuccess: number | null): string {
  if (hoursSinceLastSuccess === null) {
    return "never";
  }
  if (hoursSinceLastSuccess < 1) {
    return "less than an hour ago";
  }
  const wholeHours = Math.floor(hoursSinceLastSuccess);
  return wholeHours === 1 ? "1 hour ago" : `${wholeHours} hours ago`;
}

// ---------------------------------------------------------------------
// M12: void history screen (docs/build-briefs/M12-a-manager-can-see-
// void-history.md; DEC-101; DEC-149). Mirrors the closeout summary
// screen immediately above: day navigation is the identical arrows-only
// shape (handleCloseoutPrevDayClick/handleCloseoutNextDayClick), never a
// typed date, and this screen never fetches or auto-refreshes on a
// timer the way closeout's freshness ticker does, because a void-history
// read is a plain historical list, not a live-updating aggregation.
// ---------------------------------------------------------------------

async function handleOpenVoidHistoryClick(restoredDate?: string): Promise<boolean> {
  // M12: every open starts on the tenant's current business date (from the
  // server, CL6-F1); the day arrows step from there, the identical shape
  // handleOpenCloseoutSummaryClick uses.
  const today = await refreshTenantBusinessDate();
  if (today === undefined) {
    return false;
  }
  // UX108: a refresh reopens the day that was on screen (never a day that has not happened yet).
  state.voidHistoryBusinessDate =
    restoredDate !== undefined && restoredDate <= today ? restoredDate : today;
  showVoidHistoryScreen();
  void loadVoidHistory();
  return true;
}

function handleVoidHistoryBackToTabsClick(): void {
  showOpenTabsScreen();
  void loadOpenTabs();
}

// M12: day navigation. Arrows only, never a typed date; forward stops at
// today, the identical shape handleCloseoutPrevDayClick/
// handleCloseoutNextDayClick use.
function handleVoidHistoryPrevDayClick(): void {
  if (state.voidHistoryBusinessDate === undefined) {
    return;
  }
  state.voidHistoryBusinessDate = addDaysToIsoDate(state.voidHistoryBusinessDate, -1);
  requestRoutePublish();
  void loadVoidHistory();
}

function handleVoidHistoryNextDayClick(): void {
  if (state.voidHistoryBusinessDate === undefined || state.tenantBusinessDate === undefined) {
    return;
  }
  const next = addDaysToIsoDate(state.voidHistoryBusinessDate, 1);
  if (next > state.tenantBusinessDate) {
    return;
  }
  state.voidHistoryBusinessDate = next;
  requestRoutePublish();
  void loadVoidHistory();
}

async function loadVoidHistory(): Promise<void> {
  const session = requireSession();
  const businessDate = state.voidHistoryBusinessDate;
  if (businessDate === undefined) {
    return;
  }
  const result = await getVoidHistory(session.apiBaseUrl, session.tenantId, businessDate);
  if (result.status !== 200) {
    showToast(
      `Could not load void history. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  renderVoidHistory(result.body);
}

function voidHistoryEmptyRow(text: string): HTMLElement {
  const el = document.createElement("p");
  el.className = "list-card-empty";
  el.textContent = text;
  return el;
}

/** EXP1-S4 (DEC-402): the day's voids as CSV, Excel or PDF, audited with its format; needs report.export. */
async function handleVoidHistoryExportPick(format: GateExportFormat): Promise<void> {
  const session = requireSession();
  const gen = sessionGeneration;
  const businessDate = state.voidHistoryBusinessDate;
  if (businessDate === undefined) {
    return;
  }
  voidHistoryExportStatusEl.textContent = "";
  voidHistoryExportButton.disabled = true;
  try {
    const result = await getVoidHistoryFile(
      session.apiBaseUrl,
      session.tenantId,
      businessDate,
      format,
    );
    if (!sessionIsCurrent(gen)) {
      return;
    }
    if (result.status !== 200 || result.blob === undefined) {
      voidHistoryExportStatusEl.textContent = result.networkError
        ? "Could not download this file. Check your connection."
        : capitalizeServerMessage(result.message ?? "Could not download this file.");
      return;
    }
    await deliverExportFile(
      voidHistoryExportStatusEl,
      result.blob,
      `void-history_${businessDate}`,
      format,
    );
  } finally {
    voidHistoryExportButton.disabled = false;
  }
}

function renderVoidHistory(history: VoidHistoryDto): void {
  voidHistoryBusinessDateEl.textContent = `Business day: ${usDate(history.businessDate)}`;
  voidHistoryExportButton.hidden =
    state.session === undefined || !hasPermission(state.session, "report.export");
  // M12: never step forward past today, the identical rule
  // renderCloseoutSummary's own closeoutNextDayButton.disabled uses.
  voidHistoryNextDayButton.disabled =
    state.tenantBusinessDate === undefined || history.businessDate >= state.tenantBusinessDate;

  voidHistoryListEl.innerHTML = "";
  if (history.entries.length === 0) {
    voidHistoryListEl.append(voidHistoryEmptyRow("No voids on this business day."));
    return;
  }
  for (const entry of history.entries) {
    const row = document.createElement("div");
    row.className = "unresolved-row";
    row.dataset["testid"] = "void-history-row";

    const main = document.createElement("div");
    main.className = "unresolved-main";

    const name = document.createElement("div");
    name.className = "unresolved-name";
    // The removed drink's own write-once snapshot, plus its liquor
    // selection when one exists -- never a live menu_drinks/
    // liquor_products lookup. See src/domain/voidHistory.ts's own header.
    name.textContent =
      entry.liquorComponentReference === null
        ? entry.itemReference
        : `${entry.itemReference} (${entry.liquorComponentReference})`;
    name.dataset["testid"] = "void-history-item";

    const reason = document.createElement("div");
    reason.className = "unresolved-detail";
    // The write-once label snapshot, never a live void_reasons.label
    // join: renaming a reason after this void must never change what
    // this row appears to say.
    reason.textContent = `Reason: ${entry.voidReasonLabelAtVoid}`;
    reason.dataset["testid"] = "void-history-reason";

    const actor = document.createElement("div");
    actor.className = "unresolved-detail";
    // "Not recorded" only for a void that predates M12 (actorUserId
    // null); a resolvable actor's name shows even if their account has
    // since been deactivated (deactivation never deletes the users row).
    actor.textContent = `Voided by: ${entry.actorDisplayName ?? "Not recorded"}`;
    actor.dataset["testid"] = "void-history-actor";

    const when = document.createElement("div");
    when.className = "unresolved-detail";
    when.textContent = `At ${new Date(entry.voidedAt).toLocaleString()}`;
    when.dataset["testid"] = "void-history-time";

    main.append(name, reason, actor, when);

    if (entry.note !== null && entry.note.length > 0) {
      const note = document.createElement("div");
      note.className = "unresolved-detail";
      note.textContent = `Note: ${entry.note}`;
      note.dataset["testid"] = "void-history-note";
      main.append(note);
    }

    row.append(main);
    voidHistoryListEl.append(row);
  }
}

// M12-F1: void-history and closeout summary state plus rendered DOM
// content, reset together in one pass the same way resetTicketState pairs
// a state field with its matching DOM element, so a previous employee's
// rows never survive onto the next employee's screen on a shared tablet.
function resetCloseoutAndVoidHistoryScreens(): void {
  closeoutReadSequence += 1;
  // Void history (M12).
  state.voidHistoryBusinessDate = undefined;
  state.tenantBusinessDate = undefined;
  voidHistoryBusinessDateEl.textContent = "";
  voidHistoryListEl.innerHTML = "";
  voidHistoryNextDayButton.disabled = true;
  voidHistoryExportButton.hidden = true;
  voidHistoryExportStatusEl.textContent = "";

  // Closeout summary (CL6/CL7).
  state.closeoutBusinessDate = undefined;
  state.closeoutLocationId = undefined;
  closeoutLocationSelectEl.replaceChildren();
  closeoutLocationWrapEl.hidden = true;
  state.closeoutSummary = undefined;
  state.closeoutLastLoadedAt = undefined;
  state.closeoutFinalizeKey = undefined;
  closeoutBusinessDateEl.textContent = "";
  closeoutLastUpdatedEl.textContent = "";
  closeoutStaleNoteEl.textContent = "";
  closeoutStaleNoteEl.hidden = true;
  closeoutFinalizedDetailEl.textContent = "";
  closeoutCarriedOverEl.textContent = "";
  closeoutCarriedOverEl.hidden = true;
  closeoutExportStatusEl.textContent = "";
  closeoutFinalizeAvailabilityEl.textContent = "";
  closeoutWaterfallEl.replaceChildren();
  closeoutWaterfallNoteEl.textContent = "";
  closeoutMoneyTakeawayEl.textContent = "";
  closeoutSimulatedNoteEl.hidden = true;
  closeoutSideStatsEl.replaceChildren();
  closeoutChecksEl.replaceChildren();
  closeoutChecksEl.hidden = true;
  closeoutPassedLineEl.textContent = "";
  closeoutPassedLineEl.hidden = true;
  closeoutVerdictTitleEl.textContent = "";
  closeoutVerdictHelpEl.textContent = "";
  for (const section of closeoutExceptionsEl.querySelectorAll<HTMLElement>(".closeout-exc")) {
    section.hidden = true;
  }
  closeoutReopenedSectionEl.hidden = true;
  closeoutCashEl.innerHTML = "";
  closeoutCashEl.hidden = true;
  closeoutAwaitingTipEl.replaceChildren();
  closeoutAwaitingTipEl.hidden = true;
  closeoutPriceChangesEl.innerHTML = "";
  closeoutPriceChangesEl.hidden = true;
  closeoutActionsWithReasonEl.innerHTML = "";
  closeoutActionsWithReasonEl.hidden = true;
  closeoutTipsByMakerCountEl.textContent = "";
  closeoutTipsByMakerListEl.innerHTML = "";
  closeoutReopenedCountEl.textContent = "";
  closeoutReopenedListEl.innerHTML = "";
  closeoutOpenTabsListEl.innerHTML = "";
  closeoutOpenTabsByBartenderEl.innerHTML = "";
  closeoutOpenTabsByBartenderEl.hidden = true;
  closeoutUnpriceableListEl.innerHTML = "";
  closeoutFailedPaymentsListEl.innerHTML = "";
  closeoutPendingPaymentsListEl.innerHTML = "";
  closeoutOutsideCollectionsListEl.innerHTML = "";
  closeoutSettlementSectionEl.hidden = true;
  closeoutSettlementStatusEl.textContent = "";
  closeoutSettlementListEl.innerHTML = "";
  closeoutTerminalStatusListEl.innerHTML = "";
  closeoutSyncStatusBodyEl.innerHTML = "";
  closeoutFinalizeCarryoverCountEl.textContent = "";
  closeoutFinalizeCarryoverListEl.innerHTML = "";
}

// ---------------------------------------------------------------------
// Design-gate fix (2026-08-20): plain-language Pay/Close error translation
// ---------------------------------------------------------------------
//
// Every Pay/Close failure used to forward result.body.message straight to
// the toast/status line (`Could not close the ticket (${result.body
// .message})`), which is raw developer text pulled directly from
// src/domain/errors.ts's DomainError subclasses ("order ... has 2
// line(s) that are Not Prepared; Close is blocked..."). This maps the
// small, closed set of codes those errors actually carry (errors.ts) to
// an eighth-grade sentence a bartender can act on; an unrecognized code
// falls back to a generic message rather than ever leaking the raw text
// to the toast. The raw detail is never discarded, only relegated: it is
// kept in state.lastPayCloseErrorDetail for the debug panel
// (renderLastPayCloseErrorDebug), where a floor lead or developer can
// still see exactly what the server said.

function renderLastPayCloseErrorDebug(): void {
  debugLastPayCloseError.textContent = state.lastPayCloseErrorDetail ?? "(none)";
}

// ---------------------------------------------------------------------
// Pay (confirm/cancel) and Close
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// ALPHA-08: tips
// ---------------------------------------------------------------------

// Design/QA gate finding G, CRITICAL: previewTipCents (a client-side
// Math.round((subtotalCents * basisPoints) / 10_000) computation) and
// ensureTipConfigLoaded (a bare preset-basis-points fetch with no pricing
// context) are both GONE. Every figure the pay-confirm sheet shows now
// comes from state.paymentPreview, refreshed server-side by
// refreshPaymentPreview (this file, near recomputeQuote) for the CURRENT
// tip selection every time that selection changes; see selectTip below.
// This is also the ALPHA-08 delta-payments read (owner ruling 2026-08-21):
// the same server response that used to only preview a tip now also
// carries the order's live UNCOVERED-lines subtotal, so what this sheet
// shows is always "what Pay would actually charge right now," not the
// ticket's full historical total.

/** LABELCLAR1. The line under the tip choices that says what the tip is figured on. Shown only when the location suggests
 * tips on the price before discounts (TIPFULL1) and this payment has a discount; the wording is the owner's approved copy. */
function tipBaseNoteText(preview: PaymentPreviewDto | undefined): string {
  if (preview === undefined || preview.tipOnPreDiscountPrice !== true || preview.discountCents <= 0) {
    return "";
  }
  return `Tip is on ${formatCents(preview.subtotalBeforeDiscountCents)}, the price before discounts.`;
}

function renderTipPresets(): void {
  tipPresetRow.innerHTML = "";
  const baseNote = tipBaseNoteText(state.payConfirmPreview);
  tipBaseNote.textContent = baseNote;
  tipBaseNote.hidden = baseNote === "";
  const presetOptions = state.payConfirmPreview?.presetOptions ?? [];
  for (const preset of presetOptions) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn-tip-preset";
    button.dataset["testid"] = `tip-preset-${preset.basisPoints}`;
    // PAYFIX1: a chip is shown as chosen only once the figures above were read for it.
    const isSelected =
      payTipSyncState() === "synced" &&
      state.selectedTip?.type === "preset" &&
      state.selectedTip.basisPoints === preset.basisPoints;
    button.classList.toggle("selected", isSelected);
    button.setAttribute("aria-pressed", String(isSelected));
    const labelEl = document.createElement("span");
    labelEl.textContent = formatBasisPointsAsPercent(preset.basisPoints).replace(".00", "");
    const amountEl = document.createElement("span");
    amountEl.className = "tip-preset-amount";
    // Server-computed (preset.tipCents), not previewTipCents.
    amountEl.textContent = formatCents(preset.tipCents);
    button.append(labelEl, amountEl);
    button.addEventListener(
      "click",
      () => void selectTip({ type: "preset", basisPoints: preset.basisPoints }),
    );
    tipPresetRow.append(button);
  }
}

/** PAYFIX1. Keeps the tip choices, the "updating" line and the Retry in step with the figures on the sheet. A chip, Add or No tip
 * shows as chosen only when the server's answer for that tip is what the sheet displays; while it is on its way the line says so,
 * and when the read failed the line says so and offers Try again. Confirm is held off by updatePayConfirmButtonState. */
function renderTipSync(): void {
  const sync = payTipSyncState();
  const synced = sync === "synced";
  const selection = state.selectedTip;
  for (const button of tipPresetRow.querySelectorAll<HTMLButtonElement>(".btn-tip-preset")) {
    const on =
      synced &&
      selection?.type === "preset" &&
      button.dataset["testid"] === `tip-preset-${selection.basisPoints}`;
    button.classList.toggle("selected", on);
    button.setAttribute("aria-pressed", String(on));
  }
  const noneOn = synced && selection?.type === "none";
  const customOn = synced && selection?.type === "custom";
  tipNoneButton.classList.toggle("selected", noneOn);
  tipCustomApplyButton.classList.toggle("selected", customOn);
  tipNoneButton.setAttribute("aria-pressed", String(noneOn));
  tipCustomApplyButton.setAttribute("aria-pressed", String(customOn));
  tipSyncStatus.hidden = synced;
  tipSyncRetry.hidden = sync !== "failed";
  tipSyncStatus.classList.toggle("error", sync === "failed");
  tipSyncStatus.classList.toggle("warning", sync === "pending");
  if (sync === "pending") {
    tipSyncText.textContent = "Updating the total.";
  } else if (sync === "failed") {
    tipSyncText.textContent = "Could not update the total. Tap the tip again.";
  } else {
    tipSyncText.textContent = "";
  }
}

/** ALPHA-12: which pay mode the open sheet applies to, in plain staff
 * language. Hidden for "whole" (the ordinary case, no added text). */
function updatePayConfirmScope(): void {
  const scope = state.payScope;
  if (scope === undefined || scope.type === "whole") {
    payConfirmScope.hidden = true;
    payConfirmScope.textContent = "";
    return;
  }
  payConfirmScope.hidden = false;
  payConfirmScope.textContent =
    scope.type === "guest"
      ? `${scope.guestLabel}'s drinks`
      : scope.type === "unassigned"
        ? "Drinks with no guest"
        : scope.type === "amount"
          ? "By amount"
          : `Share ${scope.shareIndex} of ${scope.shareCount}`;
}

// UX25 / DEC-167: the confirm sheet's own Split control, reached only when
// the mode picker itself would never have appeared (0 or 1 named guests,
// scope "whole") and splitting is still meaningful (more than one uncovered
// line -- the identical uncoveredCount > 1 threshold openPayModeSheet
// already uses for offering "Split equally", since a single line gives one
// total with nothing to distinguish shares beyond an arbitrary division this
// product does not offer). Hidden, not disabled, when not meaningful, the
// same "absence over an inert button" convention openPayModeSheet already
// uses for omitting "Split equally" (main.ts's own doc comment above it).
function updatePayConfirmSplitButton(): void {
  const scope = state.payScope;
  const uncoveredCount = state.payConfirmPreview?.uncoveredLines.length ?? 0;
  // The server decides whether an equal split is open (payModes.equal, the same answer the pay-mode picker shows as
  // N/A). After a cancelled split keeps collected money as a credit, or after any amount payment, it refuses a new
  // split, so the control must not offer one that can only fail.
  const equalOpen = state.payConfirmPreview?.payModes?.equal.available !== false;
  const eligible =
    scope !== undefined &&
    scope.type === "whole" &&
    state.guests.length < 2 &&
    uncoveredCount > 1 &&
    equalOpen;
  payConfirmSplitButton.hidden = !eligible;
}

// UX25 / DEC-167: tapping the confirm sheet's Split control backs out of
// the whole-ticket confirm the same way Cancel already does, then opens the
// existing split-setup flow, byte-for-byte what a two-guest ticket already
// reaches from the mode picker's own "Split equally" option. No payment has
// been submitted yet (Confirm was never pressed), so there is nothing to
// reverse.
function handlePayConfirmSplitClick(): void {
  closePayConfirmSheet();
  openSplitSetupSheet();
}

function updatePayConfirmSummary(): void {
  // Every figure below, including the total, is read directly off the
  // server's own response; nothing here adds, multiplies, or rounds
  // anything client-side.
  const preview = state.payConfirmPreview;
  // Discounts slice A: Subtotal here is the PRE-discount figure whenever
  // there is a Discount row under it, so that this sheet and the ticket's
  // own totals block mean the same thing by the same word. Reading the
  // post-discount subtotal here instead was the first version, and it
  // made "Subtotal" say $20.00 on the ticket and $11.25 on the sheet for
  // one and the same ticket: two surfaces, one label, two numbers, which
  // is worse than a number that is merely wrong. Both blocks now read
  // gross, then Discount, then Tax, and both still arrive at the same
  // Total. With no discount the two figures are identical and nothing
  // about this sheet changes.
  const discountCents = preview?.discountCents ?? 0;
  const subtotalCents =
    discountCents > 0 ? (preview?.subtotalBeforeDiscountCents ?? 0) : (preview?.subtotalCents ?? 0);
  // DUALPRICE1: the preview's own tax and total are the CASH figures. A card payment at a location with dual pricing on pays
  // the card total (server-computed in preview.dualPricing), with a Card price row between the discount and the tax.
  const dual = state.payTender === "cash" ? undefined : preview?.dualPricing;
  const taxCents = dual?.cardTaxCents ?? preview?.taxCents ?? 0;
  const tipCents = preview?.tipCents ?? 0;
  const totalCents = dual === undefined ? (preview?.totalCents ?? 0) : dual.cardTotalCents + tipCents;
  payConfirmCardPriceRow.hidden = dual === undefined || dual.cardPriceCents <= 0;
  payConfirmCardPrice.textContent = formatCents(dual?.cardPriceCents ?? 0);
  payConfirmDualNote.hidden = dual?.unsupportedReason === undefined;
  payConfirmDualNote.textContent =
    dual?.unsupportedReason === undefined
      ? ""
      : `Dual pricing is on, and ${dual.unsupportedReason} is not built for the card price yet. Pay in cash, pay the whole ticket, one guest or chosen drinks by card, or turn dual pricing off in Settings.`;
  payConfirmSubtotal.textContent = formatCents(subtotalCents);
  // Written from the SAME preview object every other figure on this sheet
  // comes from (state.payConfirmPreview, which follows the pay SCOPE the
  // sheet is showing), never from the whole-ticket read, so the row and
  // the amount being charged can never describe different things.
  payConfirmDiscountRow.hidden = discountCents <= 0;
  payConfirmDiscountEl.textContent = `-${formatCents(discountCents)}`;
  payConfirmTax.textContent = formatCents(taxCents);
  // PAYFIX1: while the answer for the chosen tip is on its way, the tip and total say so instead of showing the last tip's figures.
  const tipSync = payTipSyncState();
  payConfirmTip.textContent = tipSync === "pending" ? "Updating..." : formatCents(tipCents);
  payConfirmTotal.textContent = tipSync === "pending" ? "Updating..." : formatCents(totalCents);
  payConfirmTotal.classList.toggle("is-stale", tipSync === "failed");
  payConfirmTip.classList.toggle("is-stale", tipSync === "failed");
  renderTipSync();

  // DOCSBUG5: the test-payment wording shows only when the server says this
  // payment cannot reach real money; anything else is worded as a live payment.
  const wording = payConfirmWording(preview?.testEnvironment);
  // SPLIT1: the payment that takes the exact remainder of a By amount ticket says it closes the ticket. Read off
  // the server's own `final` flag, never worked out here.
  payConfirmButton.textContent =
    state.payTender === "cash"
      ? "Take cash"
      : receiptSaleActive()
        ? "Authorize and print"
        : state.payScope?.type === "amount" && preview?.amountPayment?.final === true
          ? `${wording.confirmLabel} and close`
          : wording.confirmLabel;
  // CASH1-S2-U1: no tip on cash, so the totals carry no Tip row, and the "no card charged" badge (a card note) stays off.
  payConfirmTipRow.hidden =
    state.payTender === "cash" || receiptSaleActive() || readerTipSaleActive();
  payConfirmBadge.hidden = wording.badgeText === undefined || state.payTender === "cash";
  if (wording.badgeText !== undefined) payConfirmBadge.textContent = wording.badgeText;

  // ALPHA-08 delta payments (owner ruling 2026-08-21), design/QA gate
  // finding D: lists exactly the lines this payment would cover, by name,
  // distinct from the totals above it. Always shown once a preview has
  // loaded (even on a tab with no prior payment, where "charging now" is
  // simply every line) so the sheet's own behavior does not change shape
  // depending on payment history.
  const uncoveredLines = preview?.uncoveredLines ?? [];
  payConfirmChargingNow.hidden = preview === undefined || uncoveredLines.length === 0;
  payConfirmChargingNowList.innerHTML = "";
  for (const line of uncoveredLines) {
    const row = document.createElement("div");
    row.className = "pay-confirm-charging-now-row";
    row.dataset["testid"] = "pay-confirm-charging-now-row";
    // Touch-only product rule: a title attribute is never the SOLE
    // recovery for hidden text (no hover on a touchscreen). This row lets
    // the name wrap instead of truncating (styles.css's
    // .pay-confirm-charging-now-row), so nothing here depends on it; kept
    // anyway as a harmless bonus for a mouse-driven dev environment.
    const nameEl = document.createElement("span");
    nameEl.textContent = line.itemReference;
    nameEl.title = line.itemReference;
    const priceEl = document.createElement("span");
    priceEl.textContent =
      line.lineTotalCents === null ? "(no price)" : formatCents(line.lineTotalCents);
    row.append(nameEl, priceEl);
    payConfirmChargingNowList.append(row);
  }
  updatePayChargingNowOverflow();
  renderReaderCartDisplay();
}

/** UX81: a short bottom fade shows on Charging now only while the list
 * overflows and is not scrolled to its end. Recomputed after each render, on
 * scroll, and whenever the box is resized (which includes the sheet opening and
 * the viewport changing). */
function updatePayChargingNowOverflow(): void {
  const overflowing = payConfirmChargingNow.scrollHeight > payConfirmChargingNow.clientHeight + 1;
  const atEnd =
    payConfirmChargingNow.scrollTop + payConfirmChargingNow.clientHeight >=
    payConfirmChargingNow.scrollHeight - 1;
  payConfirmChargingNow.classList.toggle("has-more-below", overflowing && !atEnd);
}
payConfirmChargingNow.addEventListener("scroll", updatePayChargingNowOverflow);
new ResizeObserver(updatePayChargingNowOverflow).observe(payConfirmChargingNow);

function updatePayConfirmButtonState(): void {
  // Every one of the three factors below must independently clear before
  // Confirm activates: an explicit tip choice (docs/decision-proposals/
  // provisional-tips-config.md rule 3's own "no implicit default"), and a
  // KNOWN-safe answer on the pay-warning read. Design/QA gate finding C:
  // "known-safe" is not just "no prior payment was found" (undefined) -
  // it also requires the read to have actually SUCCEEDED
  // (!state.payWarningReadFailed). Before this correction, a FAILED read
  // left payWarningPriorPayment undefined, which this same "=== undefined"
  // check then silently treated as "no prior payment, fine to pay": the
  // exact fail-open bug the gate found. A failed read now blocks Confirm
  // unconditionally, regardless of tip choice or acknowledgment, until a
  // retry succeeds.
  //
  // Amendment 2026-08-22 (fifth), item 1: the acknowledgment half of
  // "known-safe" is now payWarningRequiresAcknowledgment() rather than a
  // bare "was a prior payment found" check - a captured payment that does
  // NOT require acknowledgment (the ordinary in-session sequential
  // multi-guest case, see that function's own header comment) must not
  // block Confirm on a checkbox that is not even shown. The fail-closed
  // half above is untouched: a failed read still blocks Confirm
  // unconditionally regardless of which case this order turns out to be.
  // RCPT1-S3: a receipt sale has no tip to choose here; the tip is entered later from the Awaiting tip sheet.
  // TERM10: on a reader-tip sale the guest picks the tip on the reader, so there is no tip to choose here either.
  const tipChosen = state.selectedTip !== undefined || receiptSaleActive() || readerTipSaleActive();
  const warningKnownSafe =
    !state.payWarningReadFailed &&
    (!payWarningRequiresAcknowledgment() || state.payWarningAcknowledged);
  // CASH1-S2-U1: with Cash offered, a tender must be picked too. Cash carries an explicit no-tip, never a chosen tip.
  const dualRefusal =
    state.payTender !== "cash" && state.payConfirmPreview?.dualPricing?.unsupportedReason !== undefined;
  payConfirmButton.disabled = dualRefusal || !confirmEnabled({
    offered: state.payCashOffered === true,
    tender: state.payTender,
    tipChosen,
    warningKnownSafe,
    cardsResolved: state.payCardsResolved !== false,
    tipSynced: payTipSyncState() === "synced",
  });
  renderPayConfirmReaderRow();
  renderReaderCartDisplay();
}

// PAY1-S4-F10: browser state is a presentation gate only. The server revalidates
// its immutable acknowledgement under the actual reader and payment locks.
let readerCartDisplay:
  | {
      phase: "showing" | "shown" | "unconfirmed" | "refused";
      signature: string;
      acknowledgementId?: string;
      message: string;
    }
  | undefined;
let readerCartDisplayGeneration = 0;
let readerCartOriginalKeyMayReplay = false;

function resetReaderCartDisplay(): void {
  readerCartDisplayGeneration += 1;
  readerCartDisplay = undefined;
  payReaderCartStatus.hidden = true;
  payReaderCartStatus.textContent = "";
}

function readerCartPauseActive(): boolean {
  return (
    !readerCartOriginalKeyMayReplay &&
    state.payConfirmPreview?.cartPauseEnabled === true &&
    state.payTender !== "cash" &&
    state.selectedSavedCardId === undefined &&
    state.activeHold?.status !== "active" &&
    !receiptSaleActive()
  );
}

// ---------------------------------------------------------------------
// TERMREADER1 (DEC-487, docs/build-briefs/TERMREADER1-remembered-card-reader.md)
// A card payment goes straight to the card reader this terminal remembers (the server keeps it per registered device,
// ui/src/session.ts keeps the copy the pay calls send). The floor asks only when it has to: no reader remembered, the
// remembered one gone or offline, or the cashier taps Switch reader. Choosing a reader never sends a charge by itself;
// the cashier taps Confirm Payment again, so no money moves on a reader pick.
// ---------------------------------------------------------------------

/** True when this payment would go to a card reader: a fresh card, not cash, a saved card, a held tab or a receipt sale. */
function cardChargeUsesReader(): boolean {
  return (
    state.payTender !== "cash" &&
    state.selectedSavedCardId === undefined &&
    state.activeHold?.status !== "active" &&
    !receiptSaleActive()
  );
}

function renderPayConfirmReaderRow(): void {
  const session = state.session;
  const name = terminalConfigState?.paymentTerminalDisplayName;
  const show =
    session !== undefined &&
    hasPermission(session, TERMINAL_PICKER_PERMISSION) &&
    cardChargeUsesReader() &&
    name !== undefined &&
    name.trim() !== "";
  payConfirmReaderRow.hidden = !show;
  payConfirmReaderName.textContent = show ? chargingReaderLine(name) : "";
}

let readerPrecheckInFlight = false;
let cardReaderAskGeneration = 0;

function closeCardReaderAsk(): void {
  cardReaderAskGeneration += 1;
  cardReaderAskScrim.hidden = true;
  cardReaderAskList.replaceChildren();
}

/** Makes the chosen reader this terminal's reader, locally and on the server. Never sends a charge. */
function adoptCardReader(reader: { id: string; display_name: string }): void {
  const config = terminalConfigState;
  if (config === undefined) return;
  terminalConfigState = savePaymentTerminalSelection(config, reader.id, reader.display_name);
  readerHealthReading = undefined;
  resetReaderCartDisplay();
  renderTopbarTerminalSegment();
  if (!payConfirmScrim.hidden) {
    updatePayConfirmSummary();
    updatePayConfirmButtonState();
  }
}

async function openCardReaderAsk(reason: ReaderAskReason): Promise<void> {
  const session = state.session;
  if (session === undefined) return;
  const generation = ++cardReaderAskGeneration;
  const currentId = terminalConfigState?.paymentTerminalId;
  const currentName = terminalConfigState?.paymentTerminalDisplayName;
  cardReaderAskTitle.textContent = READER_ASK_TITLE;
  cardReaderAskLine.textContent = readerAskLine(reason, currentName);
  cardReaderAskList.replaceChildren();
  cardReaderAskScrim.hidden = false;
  const result = await listSelectableTerminals(session.apiBaseUrl, session.tenantId);
  if (generation !== cardReaderAskGeneration || state.session?.token !== session.token) return;
  if (result.status !== 200) {
    cardReaderAskLine.textContent =
      result.body.message ?? "Could not load card readers. Check your connection.";
    return;
  }
  if (result.body.terminals.length === 0) {
    cardReaderAskLine.textContent = "No card readers are set up at this location. Ask a manager.";
    return;
  }
  for (const reader of sortByName(result.body.terminals, (listed) => listed.display_name)) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "reader-dropdown-choice";
    button.dataset["testid"] = "card-reader-ask-choice";
    button.dataset["terminalId"] = reader.id;
    const name = document.createElement("span");
    name.textContent = reader.display_name;
    button.append(name);
    if (reader.id === currentId) {
      const current = document.createElement("span");
      current.className = "reader-dropdown-status";
      current.textContent = reason === "reader_offline" ? "Current (offline)" : "Current";
      button.append(current);
    }
    button.addEventListener("click", () => void handleCardReaderAskChoice(reader, generation));
    cardReaderAskList.append(button);
  }
}

async function handleCardReaderAskChoice(
  reader: SelectableTerminalDto,
  generation: number,
): Promise<void> {
  const session = state.session;
  if (session === undefined || generation !== cardReaderAskGeneration) return;
  for (const choice of cardReaderAskList.querySelectorAll<HTMLButtonElement>("button"))
    choice.disabled = true;
  const saved = await rememberTerminal(session.apiBaseUrl, session.tenantId, {
    terminalId: reader.id,
  });
  if (generation !== cardReaderAskGeneration || state.session?.token !== session.token) return;
  if (saved.status !== 200) {
    // The server is the judge of whether this reader can take a charge; a refusal leaves the question open.
    cardReaderAskLine.textContent =
      saved.body.message ?? "Could not select this card reader. Try again.";
    for (const choice of cardReaderAskList.querySelectorAll<HTMLButtonElement>("button"))
      choice.disabled = false;
    return;
  }
  adoptCardReader(reader);
  closeCardReaderAsk();
  payStatusEl.hidden = false;
  payStatusEl.className = "pay-status";
  // The payment sheet is still open when the question came before the charge; after a refusal it is closed.
  payStatusEl.textContent = `Card reader set to ${reader.display_name}. ${
    payConfirmScrim.hidden ? "Tap Pay to continue." : "Tap Confirm Payment to continue."
  }`;
  showToast(`This terminal will use ${reader.display_name}.`, "success");
}

/** Before a card charge is sent with no usable reader. Resolves true when the charge may be sent now. */
async function resolveReaderBeforeCharge(
  decision: ReturnType<typeof decideReaderBeforeCharge>,
  opening: PayConfirmOpeningContext | undefined,
): Promise<boolean> {
  const session = state.session;
  if (session === undefined || decision.kind === "send") return true;
  if (!hasPermission(session, TERMINAL_PICKER_PERMISSION)) {
    // Nobody here may choose a reader; the server's own refusal tells the cashier to ask a manager.
    return true;
  }
  if (decision.kind === "ask") {
    void openCardReaderAsk(decision.reason);
    return false;
  }
  // No reader on this device: the server's memory for this terminal first, then this location's list.
  const remembered = await getRememberedTerminal(session.apiBaseUrl, session.tenantId);
  if (!payConfirmOpeningIsCurrent(opening)) return false;
  if (remembered.status === 200 && remembered.body.terminal !== null) {
    adoptCardReader(remembered.body.terminal);
    return true;
  }
  const listed = await listSelectableTerminals(session.apiBaseUrl, session.tenantId);
  if (!payConfirmOpeningIsCurrent(opening)) return false;
  if (listed.status !== 200) return true;
  const choice = decideFromReaderList(listed.body.terminals);
  if (choice.kind === "proceed_without_reader") return true;
  if (choice.kind === "use_only") {
    const only = listed.body.terminals.find((reader) => reader.id === choice.id);
    if (only === undefined) return true;
    adoptCardReader(only);
    void rememberTerminalChoice(only.id);
    return true;
  }
  void openCardReaderAsk("none_remembered");
  return false;
}

/** A refusal that means this reader cannot take the charge and nothing was charged: ask instead of a dead end.
 * Returns true when the question was opened (the refusal sentence is replaced by the question's own). */
function askForReaderAfterRefusal(errorCode: string | undefined, target: string): boolean {
  const reason = readerAskReasonForRefusal(errorCode);
  const session = state.session;
  if (reason === undefined || session === undefined) return false;
  if (!hasPermission(session, TERMINAL_PICKER_PERMISSION)) return false;
  // Nothing was charged and nothing is pending, so the next attempt may use a fresh key.
  clearPayIdempotencyKey(target);
  payStatusEl.textContent = readerAskLine(reason, terminalConfigState?.paymentTerminalDisplayName);
  void openCardReaderAsk(reason);
  return true;
}

function readerCartSelectionSignature(): string {
  return JSON.stringify([
    state.session?.token,
    state.orderId,
    state.payScope,
    terminalConfigState?.paymentTerminalId,
    state.payTender,
    state.selectedSavedCardId,
    state.activeHold?.id,
    state.selectedTip,
    state.payConfirmPreview,
  ]);
}

function renderReaderCartDisplay(): void {
  if (!readerCartPauseActive()) {
    if (readerCartDisplay !== undefined) resetReaderCartDisplay();
    payReaderCartStatus.hidden = true;
    payReaderCartStatus.textContent = "";
    return;
  }
  const signature = readerCartSelectionSignature();
  if (readerCartDisplay !== undefined && readerCartDisplay.signature !== signature)
    resetReaderCartDisplay();
  const display = readerCartDisplay;
  payReaderCartStatus.hidden = false;
  const readerName = terminalConfigState?.paymentTerminalDisplayName || "No reader selected";
  payReaderCartStatus.textContent = `${readerName}: ${display?.message ?? "Ready to show"}`;
  if (display?.phase !== "shown")
    payConfirmButton.textContent =
      display?.phase === "showing"
        ? "Showing on reader"
        : display?.phase === "unconfirmed"
          ? "Check reader status"
          : "Show on reader";
  if (display?.phase === "showing" || terminalConfigState?.paymentTerminalId === undefined)
    payConfirmButton.disabled = true;
}

async function showCurrentReaderCart(): Promise<void> {
  const opening = activePayConfirmOpening;
  const session = state.session;
  const orderId = state.orderId;
  const terminalId = terminalConfigState?.paymentTerminalId;
  if (!payConfirmOpeningIsCurrent(opening) || !session || !orderId || !terminalId) return;
  const signature = readerCartSelectionSignature();
  const generation = ++readerCartDisplayGeneration;
  const isCurrent = (): boolean =>
    payConfirmOpeningIsCurrent(opening) &&
    generation === readerCartDisplayGeneration &&
    readerCartPauseActive() &&
    signature === readerCartSelectionSignature();
  const recovery = readerCartDisplay?.phase === "unconfirmed";
  readerCartDisplay = {
    phase: "showing",
    signature,
    message: recovery ? "Checking reader connection" : "Showing drinks",
  };
  renderReaderCartDisplay();
  if (recovery) {
    const health = await getReaderHealth(session.apiBaseUrl, session.tenantId, terminalId);
    if (!isCurrent()) return;
    readerCartDisplay = {
      phase: "refused",
      signature,
      message:
        health.status === 200 && health.body.health.state === "online"
          ? "Connected. Show the selection again before paying."
          : "Reader connection unconfirmed. Check the reader before trying again.",
    };
    updatePayConfirmSummary();
    updatePayConfirmButtonState();
    return;
  }
  const scope = state.payScope;
  const displayRequestId = crypto.randomUUID();
  const result =
    scope?.type === "share"
      ? await showShareReaderCart(session.apiBaseUrl, session.tenantId, scope.shareId, {
          displayRequestId,
          terminalId,
        })
      : await showOrderReaderCart(session.apiBaseUrl, session.tenantId, orderId, {
          displayRequestId,
          terminalId,
          ...(scope?.type === "guest" ? { guestId: scope.guestId } : {}),
          ...(scope?.type === "unassigned" ? { unassigned: true } : {}),
          ...(scope?.type === "amount" ? { byAmount: scope.selection } : {}),
        });
  if (!isCurrent()) return;
  if (handlePinChangeRequiredOnPayClose(result.body.error)) return;
  if (result.networkError || result.body.status === "unknown") {
    readerCartDisplay = {
      phase: "unconfirmed",
      signature,
      message: "Display unconfirmed. Payment has not started.",
    };
  } else if (result.status >= 200 && result.status < 300 && result.body.status === "shown") {
    const preview = state.payConfirmPreview;
    if (
      result.body.terminalId === terminalId &&
      preview !== undefined &&
      result.body.preTipTotalCents ===
        (preview.dualPricing === undefined ? preview.totalCents - preview.tipCents : preview.dualPricing.cardTotalCents) &&
      result.body.currency.toLowerCase() === "usd"
    ) {
      readerCartDisplay = {
        phase: "shown",
        signature,
        acknowledgementId: result.body.acknowledgementId,
        message: "Drinks shown. Ready to pay.",
      };
    } else {
      await refreshPayConfirmPreview(() => payConfirmOpeningIsCurrent(opening));
      if (!payConfirmOpeningIsCurrent(opening)) return;
      readerCartDisplay = {
        phase: "refused",
        signature: readerCartSelectionSignature(),
        message: "Selection updated. Show it again before paying.",
      };
    }
  } else {
    const reason = result.body.status === "refused" ? result.body.reason : result.body.error;
    readerCartDisplay = {
      phase: "refused",
      signature,
      message:
        reason === "reader_busy" || reason === "reader_cart_reader_busy"
          ? "Reader busy. Wait for its current action to finish."
          : reason === "unsupported"
            ? "This reader cannot show the selection."
            : reason === "stale" || reason === "reader_cart_context_changed"
              ? "Selection changed. Show it again."
              : // READERMSG1: an error answer carries the server's own sentence (reader offline, merchant account not ready,
                // business day not confirmed); show it rather than hiding every cause behind one generic line.
                result.body.status !== "refused" && result.body.message
                ? capitalizeServerMessage(result.body.message)
                : "Reader unavailable. Check the reader before trying again.",
    };
  }
  updatePayConfirmSummary();
  updatePayConfirmButtonState();
}

async function recoverReaderCartContext(
  scope: PayScope,
  submissionContext: FloorSaleContext,
  submissionOpeningToken: number,
): Promise<void> {
  // An old refusal cannot reopen its scope on another ticket, session, visit,
  // or a checkout opened after this submission. The money key stays intact.
  if (!floorSaleContextIsCurrent(submissionContext) ||
      payConfirmOpeningToken !== submissionOpeningToken ||
      activePayConfirmOpening !== undefined || !payConfirmScrim.hidden) return;
  renderTicketActions();
  const pendingOpening = openPayConfirmSheet(scope);
  // Opening establishes its identity synchronously, before initialization reads.
  const recoveryOpening = activePayConfirmOpening;
  await pendingOpening;
  if (!floorSaleContextIsCurrent(submissionContext) ||
      !payConfirmOpeningIsCurrent(recoveryOpening) || payConfirmScrim.hidden) return;
  readerCartOriginalKeyMayReplay = false;
  if (readerCartPauseActive()) {
    readerCartDisplay = {
      phase: "refused",
      signature: readerCartSelectionSignature(),
      message: "Selection changed. Show it again before paying.",
    };
    updatePayConfirmSummary();
    updatePayConfirmButtonState();
  }
}

/** CASH1-S2-U1. A fresh read of this location's cash switch for the sheet that is opening. Anything but a clean answer with the
 * switch on (no permission, offline, no device bound) offers no Cash: nothing is guessed. */
async function loadCashOffer(): Promise<{ offered: boolean; lockedLabel: string | null }> {
  const read = await cashDrawer.tenderState();
  if (read === null || !cashOffered(read)) return { offered: false, lockedLabel: null };
  return { offered: true, lockedLabel: cashLockedLabel(read) };
}

/** CASH1-S2-U1. Draws the Card or Cash choice and, with Cash picked, the sheet without its tip section. */
function renderPayTender(): void {
  const offered = state.payCashOffered === true;
  payTenderRow.hidden = !offered;
  payTenderCardButton.classList.toggle("selected", state.payTender === "card");
  payTenderCardButton.setAttribute("aria-pressed", String(state.payTender === "card"));
  const lockedLabel = offered ? (state.payCashLockedLabel ?? null) : null;
  payTenderCashButton.disabled = lockedLabel !== null;
  payTenderCashButton.textContent = lockedLabel === null ? "Cash" : `Cash \u00b7 ${lockedLabel}`;
  payTenderCashButton.classList.toggle("selected", state.payTender === "cash");
  payTenderCashButton.setAttribute("aria-pressed", String(state.payTender === "cash"));
  syncReceiptSaleUi();
  updatePayConfirmCardRow();
}

/** RCPT1-S3: on a sale whose tip is written on the receipt the sheet shows no tip choice, only a short note. */
function syncReceiptSaleUi(): void {
  const receiptSale = receiptSaleActive();
  payTipSection.hidden =
    !tipSectionVisible(state.payTender) || receiptSale || readerTipSaleActive();
  payConfirmReceiptNote.hidden = !receiptSale;
}

/** CASH1-S2-U1. Card or Cash was tapped. Cash records an explicit no-tip (there is no tip on cash) so the preview reads the
 * amount cash takes; Card clears the tip so a tip is still an explicit choice. */
async function selectPayTender(tender: PayTender): Promise<void> {
  const opening = activePayConfirmOpening;
  const isCurrent = (): boolean => payConfirmOpeningIsCurrent(opening);
  if (!isCurrent()) return;
  if (state.payTender === tender) return;
  state.payTender = tender;
  state.selectedTip = tender === "cash" ? { type: "none" } : undefined;
  tipCustomInput.value = "";
  tipNoneButton.classList.toggle("selected", false);
  tipCustomApplyButton.classList.toggle("selected", false);
  tipNoneButton.setAttribute("aria-pressed", "false");
  tipCustomApplyButton.setAttribute("aria-pressed", "false");
  renderPayTender();
  updatePayConfirmButtonState();
  const outcome = await refreshPayConfirmPreview(isCurrent);
  if (!isCurrent()) return;
  if (outcome === "pinRequired" || state.payTender !== tender) return;
  if (outcome === "failed") {
    // The figures on the sheet were read for the other tender: never leave them standing under this one.
    state.payTender = undefined;
    state.selectedTip = undefined;
    renderPayTender();
    updatePayConfirmButtonState();
    showToast("Could not read this payment. Try again.", "danger");
    return;
  }
  renderTipPresets();
  updatePayConfirmSummary();
  updatePayConfirmButtonState();
}

/** CASH1-S2-U1. Take cash was tapped on a sheet with Cash picked: an open drawer on this device first (the drawer open flow
 * runs when there is none), then the Cash screen. Nothing is sent until the Cash screen's Take button. */
async function startCashTender(): Promise<void> {
  const preview = state.payConfirmPreview;
  const dueCents = preview?.totalCents ?? 0;
  // Cash takes the amount before any tip: a preview that still carries a tip was read for the card.
  if (dueCents <= 0 || (preview?.tipCents ?? 0) !== 0) {
    showToast("Nothing is due on this payment.", "danger");
    return;
  }
  payConfirmButton.disabled = true;
  try {
    const open = await cashDrawer.ensureOpenForTender();
    if (!open || state.payTender !== "cash") return;
    cashTender.openCash(dueCents, (pick) => void handlePayConfirmClick(pick));
  } finally {
    updatePayConfirmButtonState();
  }
}

async function selectTip(selection: TipSelectionInput): Promise<void> {
  const opening = activePayConfirmOpening;
  const isCurrent = (): boolean => payConfirmOpeningIsCurrent(opening);
  if (!isCurrent()) return;
  state.selectedTip = selection;
  if (selection.type !== "custom") {
    tipCustomInput.value = "";
  }
  // PAYFIX1: the choice is shown as chosen, and Confirm goes on, only when the server's figures for it have arrived. Until then
  // the total reads Updating and Confirm stays off.
  payConfirmPreviewReadFailed = false;
  updatePayConfirmSummary();
  updatePayConfirmButtonState();
  // Design/QA gate finding G: re-fetches the server's own resolved
  // tip/total for this exact selection before anything is displayed;
  // refreshPayConfirmPreview already sends state.selectedTip, scoped to
  // whichever pay mode this sheet is currently open for.
  const outcome = await refreshPayConfirmPreview(isCurrent);
  if (!isCurrent()) return;
  // VER33-F1 gate-finding fix: mustChangePin flipping true while this
  // sheet is already open (a race, but the same refusal every other read
  // on this path already handles) opens the mandatory change-PIN pad as
  // a side effect; stop rather than render tip figures from a read that
  // never completed.
  if (outcome === "pinRequired") {
    return;
  }
  renderTipPresets();
  updatePayConfirmSummary();
  updatePayConfirmButtonState();
}

function handleTipCustomApplyClick(): void {
  const raw = tipCustomInput.value.trim();
  const dollars = Number(raw);
  if (raw.length === 0 || !Number.isFinite(dollars) || dollars < 0) {
    showToast("Enter a valid custom tip amount.", "danger");
    return;
  }
  // Math.round(dollars * 100) here is input ENCODING (dollars the
  // bartender typed -> the integer cents the API contract requires), not
  // a preview computation finding G is about; the server never re-derives
  // this value from anything, it takes it exactly as given, per
  // src/domain/tips.ts's computeTipCents "custom" branch.
  void selectTip({ type: "custom", amountCents: Math.round(dollars * 100) });
}

// ---------------------------------------------------------------------
// ALPHA-08: pay warning (product owner ruling, 2026-08-20 third amendment)
// ---------------------------------------------------------------------
//
// "when a previously paid tab is reopened, Pay must WARN, not block and
// not allow silently. The bartender sees plainly that this tab already
// has a captured payment (amount and when) and must explicitly confirm
// past the warning to take another payment." Implemented as: read every
// payment on this order (GET .../payments, ALPHA-08's payment-status
// read) every time the sheet opens, never cached across sheet opens; if
// the most recent one is Captured, show the banner with its exact amount
// and time and require the acknowledgment checkbox before Confirm can
// activate. If the read itself fails, Confirm stays disabled rather than
// silently assuming "no prior payment": an unknown answer is not the same
// as a clear one, and this is exactly the situation the ruling exists to
// prevent.
//
// Design/QA gate finding C, CRITICAL, corrected 2026-08-21: the code
// comment above always claimed the fail-closed behavior it names, but the
// actual implementation did not deliver it. A failed read left
// state.payWarningPriorPayment undefined, and updatePayConfirmButtonState's
// own check ("payWarningPriorPayment === undefined || acknowledged") reads
// undefined as "no prior payment, fine to proceed" - the SAME value a
// genuinely clean read produces. The caller (openPayConfirmSheet) showed a
// toast on failure, but never disabled anything the toast didn't already
// let a bartender tap past. Net effect: a failed read behaved exactly like
// a successful one that found nothing, silently fulfilling the "unknown
// treated as no prior payment" failure mode the ruling exists to prevent.
// Fixed with an explicit tri-state signal (state.payWarningReadFailed)
// updatePayConfirmButtonState checks FIRST, independent of what
// payWarningPriorPayment happens to hold, plus a dedicated danger-colored
// banner (index.html's #pay-warning-error-banner) with its own retry
// button, so a failed read is not just a blocked Confirm with no way
// forward.
//
// Amendment 2026-08-22 (fifth), item 1, product owner split-testing
// feedback: the FULL warning above and its acknowledgment gate were
// firing on the ordinary, expected case of collecting sequential
// per-guest payments on one still-open ticket (guest A pays, then guest
// B pays: guest B's Confirm sheet found guest A's own Captured payment
// and treated it as a possible double-charge). The owner's ruling: that
// treatment is reserved for a tab that was CLOSED with a captured
// payment and then REOPENED (state.orderStatus === "reopened", the only
// status ALPHA-05's tabs.ts reopenOrder ever writes) - a genuine "is this
// bartender about to charge a guest twice" surprise. An order that has
// never been closed already has the delta-coverage model
// (state.paymentPreview's own uncoveredLines) making it impossible to
// recharge a covered line, so there is nothing left to warn about, only
// something worth stating plainly: a neutral, no-checkbox informational
// note (payWarningRequiresAcknowledgment below, and renderPayWarning's
// own branch on it).

/** Amendment 2026-08-22 (fifth), item 1. See loadPayWarning's own header
 * comment for the full reasoning; this is the single predicate both
 * renderPayWarning and updatePayConfirmButtonState read. */
function payWarningRequiresAcknowledgment(): boolean {
  return state.orderStatus === "reopened" && state.payWarningPriorPayment !== undefined;
}

async function loadPayWarning(
  orderId: string, isCurrent: () => boolean = () => true,
): Promise<PayConfirmReadOutcome> {
  const ticketContext = captureFloorSaleContext();
  if (ticketContext?.orderId !== orderId || !floorSaleContextIsCurrent(ticketContext) ||
      !isCurrent()) return "ok";
  state.payWarningPriorPayment = undefined;
  state.payWarningCaptured = [];
  state.payWarningAcknowledged = false;
  payWarningAckCheckbox.checked = false;
  const session = requireSession();
  const result = await getPayments(session.apiBaseUrl, session.tenantId, orderId);
  if (!floorSaleContextIsCurrent(ticketContext) || !isCurrent()) return "ok";
  if (result.status !== 200) {
    // VER33-F1 gate-finding fix: GET .../payments is refused
    // pin_change_required for a mustChangePin session exactly like every
    // other route behind requirePermission, and this read runs every time
    // the Pay confirm sheet opens (openPayConfirmSheet), before the
    // sheet's own scrim is ever shown. Checked first, same as
    // refreshPayConfirmPreview's own two branches.
    if (handlePinChangeRequiredOnPayClose(result.body.error)) {
      return "pinRequired";
    }
    state.payWarningReadFailed = true;
    renderPayWarning();
    return "failed";
  }
  state.payWarningReadFailed = false;
  const captured = result.body.payments
    .filter((p) => p.paymentAttempt.status === "Captured")
    .sort((a, b) => a.paymentAttempt.updated_at.localeCompare(b.paymentAttempt.updated_at));
  state.payWarningCaptured = captured.map((p) => ({
    guestId: p.guestId,
    amount: p.paymentAttempt.amount,
  }));
  const mostRecent = captured[captured.length - 1];
  if (mostRecent !== undefined) {
    state.payWarningPriorPayment = {
      amount: mostRecent.paymentAttempt.amount,
      capturedAt: mostRecent.paymentAttempt.updated_at,
    };
  }
  renderPayWarning();
  return "ok";
}

/** "$2.59" -> "$2.59"; ["$2.59", "$5.00"] -> "$2.59 and $5.00";
 * ["a", "b", "c"] -> "a, b, and c" (oxford comma). Small, fixed inputs
 * (at most one entry per named guest plus one unattributed total), so a
 * plain array join is clearer here than pulling in Intl.ListFormat. */
function joinWithAnd(parts: string[]): string {
  if (parts.length <= 1) {
    return parts.join("");
  }
  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`;
  }
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

/** Amendment 2026-08-22 (fifth), item 1's neutral note text, e.g. "Willy's
 * $2.59 already collected. This payment covers Kerry's drinks." (the
 * owner's own example wording). Groups every captured payment on this
 * order by the guest its covered lines belonged to
 * (src/domain/payments.ts's PaymentWithTipSplits.guestId; null for a
 * payment that covered more than one guest's lines, or none in
 * particular - a whole-ticket payment), sums each guest's own captured
 * total in exact integer cents (never a float sum of decimal strings, the
 * same discipline money.ts's own parseDollarsToCentsExact exists for),
 * and states what the CURRENTLY open Confirm sheet covers
 * (state.payScope) as a second sentence. */
function describeAlreadyCollectedNote(): string {
  const centsByGuest = new Map<string, number>();
  let unattributedCents = 0;
  for (const payment of state.payWarningCaptured) {
    const cents = parseDollarsToCentsExact(payment.amount, "captured payment amount");
    if (payment.guestId === null) {
      unattributedCents += cents;
    } else {
      centsByGuest.set(payment.guestId, (centsByGuest.get(payment.guestId) ?? 0) + cents);
    }
  }
  const parts: string[] = [];
  for (const [guestId, cents] of centsByGuest) {
    const label = state.guests.find((guest) => guest.id === guestId)?.label ?? "A guest";
    parts.push(`${label}'s ${formatCents(cents)}`);
  }
  if (unattributedCents > 0) {
    parts.push(formatCents(unattributedCents));
  }
  const collectedSentence = parts.length > 0 ? `${joinWithAnd(parts)} already collected.` : "";
  const scope = state.payScope ?? { type: "whole" as const };
  const coversSentence =
    scope.type === "guest"
      ? `This payment covers ${scope.guestLabel}'s drinks.`
      : scope.type === "unassigned"
        ? "This payment covers the drinks with no guest."
        : scope.type === "amount"
          ? "This payment is taken by amount."
          : scope.type === "share"
            ? "This payment covers your share."
            : "This payment covers the rest of the ticket.";
  return [collectedSentence, coversSentence].filter((sentence) => sentence.length > 0).join(" ");
}

function renderPayWarning(): void {
  // Design/QA gate finding C: the failure banner and the "this tab was
  // already paid" banner are mutually exclusive, plain-language states,
  // never shown together.
  payWarningErrorBanner.hidden = !state.payWarningReadFailed;
  if (state.payWarningReadFailed) {
    payWarningBanner.hidden = true;
    payWarningAckRow.hidden = true;
    payWarningNote.hidden = true;
    return;
  }
  const requiresAck = payWarningRequiresAcknowledgment();
  const prior = state.payWarningPriorPayment;
  payWarningBanner.hidden = !requiresAck;
  payWarningAckRow.hidden = !requiresAck;
  if (requiresAck && prior !== undefined) {
    const when = new Date(prior.capturedAt).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
    payWarningPrior.textContent = `$${prior.amount} captured at ${when} tonight`;
  }
  // Amendment 2026-08-22 (fifth), item 1: an in-session multi-guest
  // ticket that already has one or more captured guest payments, but was
  // never closed and reopened, gets the neutral informational note
  // instead of the alarming banner above - no checkbox, no
  // acknowledgment gate, just what was already collected and what this
  // payment covers.
  if (!requiresAck && state.payWarningCaptured.length > 0) {
    payWarningNote.hidden = false;
    payWarningNote.textContent = describeAlreadyCollectedNote();
  } else {
    payWarningNote.hidden = true;
    payWarningNote.textContent = "";
  }
}

function handlePayWarningAckChange(): void {
  state.payWarningAcknowledged = payWarningAckCheckbox.checked;
  updatePayConfirmButtonState();
}

// Design/QA gate finding C: the retry affordance the fail-closed banner
// promises. Re-runs the exact same read; a bartender is never stuck with a
// permanently blocked Confirm and no path forward short of closing the
// sheet.
async function handlePayWarningRetryClick(): Promise<void> {
  const opening = activePayConfirmOpening;
  const isCurrent = (): boolean => payConfirmOpeningIsCurrent(opening);
  if (!isCurrent()) return;
  if (state.orderId === undefined) {
    return;
  }
  await loadPayWarning(state.orderId, isCurrent);
  if (!isCurrent()) return;
  updatePayConfirmButtonState();
}

// Design/QA gate LOW correction, 2026-08-21: the identical fail-closed
// retry shape as handlePayWarningRetryClick above, applied to a
// network-failed SHARE collection instead of a failed payment-history
// read. Re-runs the split plan's own current-state reads (a plain GET,
// never the collectShare command itself), then reopens the split-progress
// sheet so the bartender sees the real, current answer either way: the
// share shows Collected if it actually captured despite the network
// hiccup, or is still a tappable pending share, with a fresh idempotency
// key minted the next time it is tapped, if it did not.
async function handlePayStatusRetryClick(): Promise<void> {
  payStatusRetryButton.hidden = true;
  payStatusEl.hidden = true;
  await loadActiveSplitPlan();
  await refreshPaymentPreview();
  renderTicketActions();
  if (state.activeSplitPlan !== undefined) {
    openSplitProgressSheet();
  }
}

// ---------------------------------------------------------------------
// ALPHA-12: the three pay modes. Tapping Pay routes here first; the
// common single-guest single-payer case (payModesAvailable() false) skips
// straight to the ordinary whole-ticket sheet with zero added friction.
// ---------------------------------------------------------------------

function handlePayButtonClick(): void {
  if (state.orderId === undefined || payButton.disabled) {
    return;
  }
  // ID1-F2 (DEC-398): at a Required location, an alcohol ticket with no ID check and no skip asks first. The server refuses
  // Pay too (409 id_check_required); this is the way through.
  if (state.idVerification?.payBlocked === true) {
    openIdRequiredSheet();
    return;
  }
  if (state.activeSplitPlanLoadState === "failed") {
    void loadActiveSplitPlan();
    return;
  }
  if (state.activeSplitPlanLoadState !== "ready") {
    return;
  }
  // A split already in progress on this ticket takes priority over the
  // mode picker: there is nothing to choose, the bartender is continuing
  // a split that is already underway.
  if (state.activeSplitPlan !== undefined) {
    openSplitProgressSheet();
    return;
  }
  // UX74-F1: fail closed in the handler too, not only through the
  // disabled attribute. While the amount is unread (a read out, or a
  // failed one), no charge sheet opens and nothing is charged from the
  // quote total.
  if (paymentPreviewStale()) {
    return;
  }
  if (!payModesAvailable()) {
    void openPayConfirmSheet({ type: "whole" });
    return;
  }
  openPayModeSheet();
}

function openPayModeSheet(): void {
  const preview = state.paymentPreview;
  const uncoveredCount = preview?.uncoveredLines.length ?? state.lines.length;
  const modes = preview?.payModes;
  // SPLIT1: every figure and every N/A below is the server's (balanceCents, payModes, guestShares), never worked
  // out here. N/A is shown from `payModes`, decided by the server (owner ruling 2026-10-10, DEC-363 Q2).
  const options: ChoiceOption[] = [
    {
      id: "whole",
      label: "Pay the whole ticket",
      testid: "pay-mode-whole",
      ...(preview?.balanceCents === undefined
        ? {}
        : { sublabel: formatCents(preview.balanceCents) }),
    },
  ];
  if (uncoveredCount > 1) {
    options.push(
      modes?.equal.available === false
        ? { id: "split", label: "Equal", testid: "pay-mode-split", disabled: true, sublabel: "Not available" }
        : { id: "split", label: "Equal", testid: "pay-mode-split" },
    );
  }
  if (state.guests.length > 1) {
    const noGuest = preview?.guestShares?.find((share) => share.guestId === null);
    options.push(
      modes?.byGuest.available === false
        ? {
            id: "guest",
            label: "By guest",
            testid: "pay-mode-guest",
            disabled: true,
            sublabel: "Not available",
          }
        : {
            id: "guest",
            label: "By guest",
            testid: "pay-mode-guest",
            ...(noGuest === undefined
              ? {}
              : {
                  sublabel: `${noGuest.drinkCount} ${noGuest.drinkCount === 1 ? "drink" : "drinks"}, no guest`,
                }),
          },
    );
    options.push({
      id: "amount",
      label: "By amount",
      testid: "pay-mode-amount",
      ...(preview?.balanceCents === undefined
        ? {}
        : { sublabel: `Up to ${formatCents(preview.balanceCents)}` }),
    });
  }
  openChoiceSheet({
    title: "How is this being paid?",
    options,
    onPick: (id) => {
      if (id === "whole") {
        void openPayConfirmSheet({ type: "whole" });
      } else if (id === "guest") {
        openGuestPickForPay();
      } else if (id === "amount") {
        openAmountPad();
      } else {
        openSplitSetupSheet();
      }
    },
  });
}

// ALPHA-12 "pay my drinks", now SPLIT1's "By guest": choose whose drinks. Only reachable when
// state.guests.length > 1 (openPayModeSheet's own gate).
//
// Amendment 2026-08-22 (seventh), product owner checkout feedback: only a
// guest with an outstanding (uncovered) balance is a selectable option
// here. The owner reached a real $0.00 confirm screen by picking a guest
// whose drinks were already fully covered; that path is now closed off at
// the picker itself rather than caught later at openPayConfirmSheet, using
// the same disabled-row treatment openSplitProgressSheet already
// established for a collected share (ChoiceOption's own `disabled`/
// `sublabel`, rendered as a plain non-interactive div, never a <button>,
// so it can never be mistaken for a still-tappable option; see
// .choice-option-done in styles.css). A guest with zero lines at all
// (named on the ticket, never ordered for) is disabled for the identical
// reason: "no drinks yet" is also a zero balance, and "no pay path that
// would resolve to zero due is ever offered" per the amendment's own
// wording, not only the fully-paid case. The 44px+ touch-target footprint
// is retained even though the row is inert (touch-target-comfortable,
// same as every other choice-sheet row), matching the amendment's own
// "44px target retained for layout, but disabled."
//
// SPLIT1: each tappable row now carries the server's own figures for that guest (GuestShareDto: drinks, drinks
// subtotal, tax and the amount before tip), and a "No guest" row sits last when drinks have no guest.
function openGuestPickForPay(): void {
  const shares: readonly GuestShareDto[] = state.paymentPreview?.guestShares ?? [];
  // Owner ruling 2026-08-22: selectable (unpaid) guests list first;
  // paid and no-drinks guests sink to the bottom, mirroring the ticket
  // rail's own paid-section ordering.
  const guestOptions: ChoiceOption[] = state.guests.map((guest) => {
    const share = shares.find((entry) => entry.guestId === guest.id);
    if (share === undefined) {
      const guestLines = state.lines.filter((line) => line.guestId === guest.id);
      return {
        id: guest.id,
        label: guest.label,
        testid: "pay-guest-option",
        disabled: true,
        sublabel: guestLines.length === 0 ? "No drinks yet" : "Paid",
      };
    }
    return {
      id: guest.id,
      label: guest.label,
      testid: "pay-guest-option",
      sublabel: `${formatCents(share.totalCents)} before tip`,
      detailLines: [
        `${share.drinkCount} ${share.drinkCount === 1 ? "drink" : "drinks"} · Drinks ${formatCents(share.subtotalCents)} · Tax ${formatCents(share.taxCents)}`,
      ],
    };
  });
  guestOptions.sort((a, b) => Number(a.disabled === true) - Number(b.disabled === true));
  const noGuest = shares.find((entry) => entry.guestId === null);
  if (noGuest !== undefined) {
    guestOptions.push({
      id: NO_GUEST_OPTION_ID,
      label: "No guest",
      testid: "pay-guest-unassigned",
      sublabel: `${formatCents(noGuest.totalCents)} before tip`,
      detailLines: [
        `${noGuest.drinkCount} ${noGuest.drinkCount === 1 ? "drink" : "drinks"} · Drinks ${formatCents(noGuest.subtotalCents)} · Tax ${formatCents(noGuest.taxCents)}`,
      ],
    });
  }
  openChoiceSheet({
    title: "Whose drinks?",
    options: guestOptions,
    onPick: (guestId) => {
      if (guestId === NO_GUEST_OPTION_ID) {
        openNoGuestSheet();
        return;
      }
      const guest = state.guests.find((g) => g.id === guestId);
      if (guest === undefined) {
        return;
      }
      void openPayConfirmSheet({ type: "guest", guestId: guest.id, guestLabel: guest.label });
    },
  });
}

const NO_GUEST_OPTION_ID = "__no_guest__";
const NO_GUEST_PAY_ID = "__no_guest_pay__";

// SPLIT1 (owner ruling 2026-10-10, DEC-363 Q1): the No guest row. Tapping a guest assigns EVERY drink with no guest
// to that guest (the existing DEC-205 move, one command per line, refused by the server for a paid line), and the
// list reopens recomputed; "Pay it on its own" pays those drinks without a guest. The ticket cannot close while they
// are unpaid (DEC-026).
function openNoGuestSheet(): void {
  const share = state.paymentPreview?.guestShares?.find((entry) => entry.guestId === null);
  if (share === undefined) {
    openGuestPickForPay();
    return;
  }
  const options: ChoiceOption[] = state.guests.map((guest) => ({
    id: guest.id,
    label: guest.label,
    testid: "no-guest-assign-option",
  }));
  options.push({
    id: NO_GUEST_PAY_ID,
    label: "Pay it on its own",
    testid: "no-guest-pay-option",
    sublabel: `${formatCents(share.totalCents)} before tip`,
  });
  openChoiceSheet({
    title: "No guest",
    subtitle: `Assign ${share.drinkCount === 1 ? "this drink" : "these drinks"} to`,
    options,
    cancelLabel: "Back",
    onPick: (id) => {
      if (id === NO_GUEST_PAY_ID) {
        void openPayConfirmSheet({ type: "unassigned" });
        return;
      }
      void assignNoGuestDrinks(share.orderLineIds, id);
    },
  });
}

async function assignNoGuestDrinks(
  orderLineIds: readonly string[],
  guestId: string,
): Promise<void> {
  const session = requireSession();
  for (const orderLineId of orderLineIds) {
    const result = await changeLineGuest(session.apiBaseUrl, session.tenantId, orderLineId, {
      idempotencyKey: crypto.randomUUID(),
      guestId,
    });
    if (result.status !== 200 && result.status !== 201) {
      showToast(
        `Could not assign this drink (${result.body.message ?? result.body.error ?? result.status}).`,
        "danger",
      );
      break;
    }
    const line = state.lines.find((entry) => entry.orderLineId === orderLineId);
    if (line !== undefined) {
      line.guestId = result.body.orderLine.guest_id ?? undefined;
    }
  }
  renderTicket();
  await refreshPaymentPreview();
  openGuestPickForPay();
}

// ---------------------------------------------------------------------
// SPLIT1 By amount: the pad. Each digit shifts into the cents of the amount (4, 0, 0, 0 reads $40.00), and a digit
// that would pass the balance is refused. The chips carry no figure typed by a person: Remaining and Half are
// resolved by the server (selection types "remaining" and "half"), and a guest's chip is that guest's own share
// from the server's guestShares. Continue opens the ordinary Confirm Payment sheet in the amount scope, where the
// server returns the tax, the tip choices and the total.
// ---------------------------------------------------------------------

const AMOUNT_PAD_MAX_CENTS = 9_999_999;
let amountPadCents = 0;
let amountPadSelection: AmountSelectionDto | undefined;
let amountPadHalfCents: number | undefined;
/** SPLIT1 (owner ruling Q4): the same pad refunds an amount payment. Set only while the pad is in refund mode; the
 * ceiling is the server's `refundableCents` for that payment, and the server enforces it again. */
let amountPadRefund: { paymentAttemptId: string; refundableCents: number } | undefined;

function openAmountPad(): void {
  amountPadRefund = undefined;
  amountPadCents = 0;
  amountPadSelection = undefined;
  amountPadHalfCents = undefined;
  renderAmountPad();
  choiceSheetOnCancel = undefined;
  choiceSheetEl.hidden = true;
  reasonSheetEl.hidden = true;
  businessDaySheetEl.hidden = true;
  choiceScrim.classList.remove("bd-scrim-centered");
  amountPadSheetEl.hidden = false;
  choiceScrim.hidden = false;
  void loadAmountPadHalf();
}

function openRefundAmountPad(paymentAttemptId: string, refundableCents: number): void {
  amountPadRefund = { paymentAttemptId, refundableCents };
  amountPadCents = 0;
  amountPadSelection = undefined;
  amountPadHalfCents = undefined;
  renderAmountPad();
  choiceSheetOnCancel = undefined;
  choiceSheetEl.hidden = true;
  reasonSheetEl.hidden = true;
  businessDaySheetEl.hidden = true;
  choiceScrim.classList.remove("bd-scrim-centered");
  amountPadSheetEl.hidden = false;
  choiceScrim.hidden = false;
}

function closeAmountPad(): void {
  amountPadSheetEl.hidden = true;
  choiceScrim.hidden = true;
}

/** The server's own Half figure for the chip label. Best effort: with no answer the chip simply carries no figure. */
async function loadAmountPadHalf(): Promise<void> {
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  const session = requireSession();
  const result = await getPaymentPreview(session.apiBaseUrl, session.tenantId, orderId, {
    byAmount: { type: "half" },
  });
  if (state.orderId !== orderId || amountPadSheetEl.hidden) {
    return;
  }
  if (result.status === 200 && result.body.amountPayment !== undefined) {
    amountPadHalfCents = result.body.amountPayment.amountCents;
    renderAmountPad();
  }
}

function amountPadBalanceCents(): number | undefined {
  return amountPadRefund === undefined
    ? state.paymentPreview?.balanceCents
    : amountPadRefund.refundableCents;
}

function renderAmountPad(): void {
  const balance = amountPadBalanceCents();
  amountPadTitleEl.textContent = amountPadRefund === undefined ? "By amount" : "Refund amount";
  amountPadBalanceLabelEl.textContent = amountPadRefund === undefined ? "Balance" : "Refundable";
  amountPadContinueButton.textContent = amountPadRefund === undefined ? "Continue" : "Refund";
  amountPadBalanceEl.textContent = balance === undefined ? "" : formatCents(balance);
  amountPadFieldEl.textContent = formatCents(amountPadCents);
  const over = balance !== undefined && amountPadCents > balance;
  amountPadFieldEl.classList.toggle("is-over", over);
  amountPadOverEl.hidden = !over;
  amountPadContinueButton.disabled = amountPadCents <= 0 || over || balance === undefined;
  amountPadChipsEl.innerHTML = "";
  const addChip = (
    testid: string,
    label: string,
    selection: AmountSelectionDto,
    cents: number | undefined,
  ) => {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "amount-pad-chip";
    chip.dataset["testid"] = testid;
    chip.textContent = cents === undefined ? label : `${label} ${formatCents(cents)}`;
    chip.classList.toggle(
      "selected",
      amountPadSelection !== undefined &&
        amountPadSelection.type === selection.type &&
        (selection.type !== "guest" ||
          (amountPadSelection.type === "guest" &&
            amountPadSelection.guestId === selection.guestId)),
    );
    chip.disabled = cents === undefined;
    chip.addEventListener("click", () => {
      if (cents === undefined) {
        return;
      }
      amountPadSelection = selection;
      amountPadCents = cents;
      renderAmountPad();
    });
    amountPadChipsEl.append(chip);
  };
  if (amountPadRefund !== undefined) {
    // A refund names the cents only: the whole refundable balance is one tap, nothing else is offered.
    const refundChip = document.createElement("button");
    refundChip.type = "button";
    refundChip.className = "amount-pad-chip";
    refundChip.dataset["testid"] = "amount-pad-chip-refundable";
    refundChip.textContent =
      balance === undefined ? "Refundable" : `Refundable ${formatCents(balance)}`;
    refundChip.disabled = balance === undefined || balance <= 0;
    refundChip.addEventListener("click", () => {
      if (balance !== undefined) {
        amountPadCents = balance;
        renderAmountPad();
      }
    });
    amountPadChipsEl.append(refundChip);
    return;
  }
  addChip("amount-pad-chip-remaining", "Remaining", { type: "remaining" }, balance);
  addChip("amount-pad-chip-half", "Half", { type: "half" }, amountPadHalfCents);
  // Once any drink is paid, each guest still owing is a one-tap amount.
  const anyPaid = state.lines.some((line) => isLineCovered(line.orderLineId));
  if (anyPaid) {
    for (const share of state.paymentPreview?.guestShares ?? []) {
      if (share.guestId === null) {
        continue;
      }
      const guest = state.guests.find((entry) => entry.id === share.guestId);
      if (guest !== undefined) {
        addChip(
          `amount-pad-chip-guest`,
          guest.label,
          { type: "guest", guestId: guest.id },
          share.totalCents,
        );
      }
    }
  }
}

function handleAmountPadKey(key: string): void {
  if (key === "clear") {
    amountPadCents = 0;
    amountPadSelection = undefined;
  } else if (key === "back") {
    amountPadCents = Math.trunc(amountPadCents / 10);
    amountPadSelection = undefined;
  } else if (/^[0-9]$/.test(key)) {
    const next = amountPadCents * 10 + Number(key);
    const balance = amountPadBalanceCents();
    // Refused, not clamped: a digit that would pass the balance (or the pad's own ceiling) changes nothing.
    if (next > AMOUNT_PAD_MAX_CENTS || (balance !== undefined && next > balance)) {
      amountPadOverEl.hidden = false;
      return;
    }
    amountPadCents = next;
    amountPadSelection = undefined;
  }
  renderAmountPad();
}

function handleAmountPadContinueClick(): void {
  if (amountPadContinueButton.disabled) {
    return;
  }
  if (amountPadRefund !== undefined) {
    const refund = amountPadRefund;
    const cents = amountPadCents;
    closeAmountPad();
    void openRefundAmountReasonPicker(refund.paymentAttemptId, cents);
    return;
  }
  const selection: AmountSelectionDto = amountPadSelection ?? {
    type: "cents",
    cents: amountPadCents,
  };
  closeAmountPad();
  void openPayConfirmSheet({ type: "amount", selection });
}

// ---------------------------------------------------------------------
// ALPHA-12 equal split: setup (share-count stepper) and the progress list.
// ---------------------------------------------------------------------

const SPLIT_SETUP_MIN_SHARES = 2;
const SPLIT_SETUP_MAX_SHARES = 12;

async function updateSplitSetupPreview(): Promise<void> {
  splitSetupCountEl.textContent = String(state.splitSetupShareCount);
  splitSetupDecrementButton.disabled = state.splitSetupShareCount <= SPLIT_SETUP_MIN_SHARES;
  splitSetupIncrementButton.disabled = state.splitSetupShareCount >= SPLIT_SETUP_MAX_SHARES;
  if (state.orderId === undefined) {
    return;
  }
  const session = requireSession();
  splitSetupPreviewEl.textContent = "Loading…";
  // Design/QA gate finding G's own rule applies here identically: the
  // per-share amount shown before Start is always server-computed
  // (src/domain/splitPayments.ts's getSplitPreview), never derived from
  // subtotalCents/shareCount client-side.
  const result = await getSplitPreview(session.apiBaseUrl, session.tenantId, state.orderId, {
    shareCount: state.splitSetupShareCount,
  });
  if (state.orderId === undefined) {
    return; // sheet's own order changed while this request was in flight
  }
  if (result.status !== 200) {
    splitSetupPreviewEl.textContent =
      result.body.message ?? "Could not price this split. Try again.";
    return;
  }
  const perShare = result.body.shares.map((s) => s.amountCents);
  const allEqual = perShare.every((c) => c === perShare[0]);
  splitSetupPreviewEl.textContent = allEqual
    ? `${state.splitSetupShareCount} shares of ${formatCents(perShare[0] ?? 0)} each, before tip`
    : `${state.splitSetupShareCount} shares, ${formatCents(Math.min(...perShare))}–${formatCents(Math.max(...perShare))} each, before tip`;
}

function openSplitSetupSheet(): void {
  state.splitSetupShareCount = 2;
  splitSetupScrim.hidden = false;
  void updateSplitSetupPreview();
}

function closeSplitSetupSheet(): void {
  splitSetupScrim.hidden = true;
}

function handleSplitSetupDecrementClick(): void {
  if (state.splitSetupShareCount <= SPLIT_SETUP_MIN_SHARES) {
    return;
  }
  state.splitSetupShareCount -= 1;
  void updateSplitSetupPreview();
}

function handleSplitSetupIncrementClick(): void {
  if (state.splitSetupShareCount >= SPLIT_SETUP_MAX_SHARES) {
    return;
  }
  state.splitSetupShareCount += 1;
  void updateSplitSetupPreview();
}

async function handleSplitSetupStartClick(): Promise<void> {
  if (state.orderId === undefined) {
    return;
  }
  const orderId = state.orderId;
  const session = requireSession();
  const shareCount = state.splitSetupShareCount;
  const result = await createSplitPlan(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
    shareCount,
  });
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not start the split (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  closeSplitSetupSheet();
  state.activeSplitPlan = { plan: result.body.plan, shares: result.body.shares };
  // PAY30-F1 (DEC-215, "Release hold, then split"): createSplitPlan
  // releases a matching-provider Active hold server-side as its last
  // write, but the HTTP response above carries no releasedHold field (the
  // domain fix's own brief deliberately did not thread one through, since
  // no caller needed it yet). Re-read hold status the same way
  // enterTicket and performClearHold already do, so the ticket-hold-badge
  // reflects the release immediately instead of showing a stale amount
  // until the ticket is re-entered.
  await loadHoldStatus(orderId);
  openSplitProgressSheet();
}

/** ALPHA-12: the "N of M shares collected" progress list, and the entry
 * point for collecting the next pending share. Reachable from the pay-mode
 * picker (via split setup), from tapping Pay again while a split is
 * already in progress (handlePayButtonClick), and from
 * handlePayConfirmClick after collecting a non-completing share. */
function openSplitProgressSheet(): void {
  const active = state.activeSplitPlan;
  if (active === undefined) {
    return;
  }
  const collectedCount = active.shares.filter((s) => s.status === "captured").length;
  const options: ChoiceOption[] = active.shares.map((share) => {
    const progress = share.progress;
    if (progress.state === "captured") {
      return {
        id: share.id,
        label: `Share ${share.share_index}`,
        detailLines: [
          `Drinks ${formatCents(progress.drinksCents)}`,
          `Tax ${formatCents(progress.taxCents)}`,
          `Tip ${formatCents(progress.tipCents)}`,
          `Total charged ${formatCents(progress.totalChargedCents)}`,
        ],
        sublabel: "Collected",
        disabled: true,
        testid: "split-progress-share-done",
      };
    }
    return {
      id: share.id,
      label: `Share ${share.share_index}`,
      detailLines: [
        `Drinks ${formatCents(progress.drinksCents)}`,
        `Tax ${formatCents(progress.taxCents)}`,
        `Before tip ${formatCents(progress.beforeTipCents)}`,
      ],
      sublabel: "Tap to collect",
      testid: "split-progress-share-pending",
    };
  });
  // Architect-gate CRITICAL correction ("abandoned split deadlock"),
  // 2026-08-21: the exit this sheet never had before. A distinctly-styled
  // (never color-alone; see .choice-option-danger's own comment in
  // styles.css) row at the end of the same list, not a share, so
  // cancelSplitPlanChoiceId below is checked FIRST in onPick.
  options.push({
    id: CANCEL_SPLIT_PLAN_CHOICE_ID,
    label: "Cancel split",
    sublabel: "Already-collected shares stay charged; the rest becomes payable normally.",
    testid: "split-cancel-button",
    variant: "danger",
  });
  openChoiceSheet({
    title: "Split equally",
    note: `${collectedCount} of ${active.plan.share_count} shares collected`,
    options,
    cancelLabel: "Close",
    onPick: (id) => {
      if (id === CANCEL_SPLIT_PLAN_CHOICE_ID) {
        void handleCancelSplitPlanClick(active.plan.id);
        return;
      }
      const share = active.shares.find((s) => s.id === id);
      if (share === undefined) {
        return;
      }
      void openPayConfirmSheet({
        type: "share",
        shareId: share.id,
        shareIndex: share.share_index,
        shareCount: active.plan.share_count,
      });
    },
  });
}

// Architect-gate CRITICAL correction ("abandoned split deadlock"),
// 2026-08-21: an opaque, never-collides-with-a-real-share-id sentinel for
// the "Cancel split" row above, matching this file's own established
// pattern for the same problem (SPLIT_SETUP_MIN_SHARES etc. are plain
// module-level constants, not magic literals repeated at each call site).
const CANCEL_SPLIT_PLAN_CHOICE_ID = "__cancel_split_plan__";

async function handleCancelSplitPlanClick(planId: string): Promise<void> {
  const session = requireSession();
  const result = await cancelSplitPlan(session.apiBaseUrl, session.tenantId, planId, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not cancel the split (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  const collectedLines = result.body.coveredOrderLineIds.length;
  // PAYCAN2: a taxed or reduced split keeps what was collected as an exact credit instead of covering whole lines, so
  // no line is covered even though money was taken. "Nothing had been collected" is only true when the cancel
  // operation itself recorded zero collected cents.
  const collectedCents = result.body.cancelOperation.collected_cents;
  showToast(
    collectedLines > 0
      ? `Split cancelled. ${collectedLines} drink${collectedLines === 1 ? "" : "s"} paid from the ` +
          `collected shares; the rest is payable normally.`
      : collectedCents > 0
        ? `Split cancelled. ${formatCents(collectedCents)} already collected stays paid; the rest is payable normally.`
        : "Split cancelled. Nothing had been collected yet.",
    "success",
  );
  state.activeSplitPlan = undefined;
  await Promise.all([loadActiveSplitPlan(), refreshPaymentPreview()]);
  renderTicketLines();
  renderTicketActions();
}

interface PayConfirmOpeningContext {
  token: number;
  ticket: FloorSaleContext;
}
let payConfirmOpeningToken = 0;
let activePayConfirmOpening: PayConfirmOpeningContext | undefined;

function payConfirmOpeningIsCurrent(opening: PayConfirmOpeningContext | undefined): boolean {
  return opening !== undefined && activePayConfirmOpening === opening &&
    opening.token === payConfirmOpeningToken && floorSaleContextIsCurrent(opening.ticket);
}

// ALPHA-12: `scope` defaults to "whole", the ordinary pre-ALPHA-12 case,
// reached straight from the Pay button on a single-guest single-payer
// ticket with zero added friction (payModesAvailable's own doc comment).
// "guest" and "share" are reached only through the pay-mode picker or the
// split-progress sheet, both of which already gate their own eligibility
// before calling this.
async function openPayConfirmSheet(scope: PayScope = { type: "whole" }): Promise<void> {
  const ticketContext = captureFloorSaleContext();
  if (ticketContext === undefined || !floorSaleContextIsCurrent(ticketContext)) {
    return;
  }
  const opening: PayConfirmOpeningContext = {
    token: payConfirmOpeningToken = payConfirmOpeningToken + 1, ticket: ticketContext,
  };
  activePayConfirmOpening = opening;
  resetReaderCartDisplay();
  const isCurrent = (): boolean => payConfirmOpeningIsCurrent(opening);
  state.payScope = scope;
  payConfirmScrim.hidden = true;
  state.selectedSavedCardId = undefined;
  state.payCardsResolved = false;
  updatePayConfirmButtonState();
  // QA-gate fix (double-charge race, 2026-08-20) + independent-review
  // Finding A correction (2026-08-26) + gate re-review correction
  // (2026-08-26, this pass): minted once per INTENT to collect this
  // money, not once per opening of the sheet, and tracked per TARGET
  // (state.payIdempotencyKeysByTarget's own doc comment) rather than in
  // one shared slot -- a single-slot design still lets an unrelated
  // target opened in between (a different guest, a different order)
  // silently overwrite and discard a preserved key.
  // getOrMintPayIdempotencyKey reuses the existing key when this exact
  // target already has one pending (a retry of an ambiguous failure, or
  // simply reopening this same sheet again before Confirm), and mints a
  // fresh one only the first time a target is seen.
  const target = computePayIntentTarget(ticketContext.orderId, scope);
  readerCartOriginalKeyMayReplay = state.payIdempotencyKeysByTarget.peek(target) !== undefined;
  getOrMintPayIdempotencyKey(target);
  state.selectedTip = undefined;
  // CASH1-S2-U1: neither tender is preselected, and whether Cash is offered is read fresh every time the sheet opens.
  state.payTender = undefined;
  const cashOffer = await loadCashOffer();
  if (!isCurrent()) return;
  state.payCashOffered = cashOffer.offered;
  state.payCashLockedLabel = cashOffer.lockedLabel;
  renderPayTender();
  // Payments-track Slice 3: "New card" every time the sheet opens, never
  // carried over from a previous attempt (this scope may not even be the
  // same guest as last time). Refreshed from the server, not the possibly
  // -stale list a prior ticket view left behind.
  // PAYOPEN1: readiness was cleared before the first initialization read.
  confirmedClientSave = undefined;
  paySaveClientCheckbox.checked = true;
  await loadSavedCards(ticketContext.orderId, isCurrent);
  if (!isCurrent()) return;
  // CARDDEFAULT2: the paying guest's own usable saved card is the starting choice, before the preview below reads it.
  state.selectedSavedCardId = defaultSavedCardId({
    scope,
    guests: state.guests,
    savedCards: state.savedCards,
    holdActive: state.activeHold?.status === "active",
    cash: false,
  });
  updatePayConfirmCardRow();
  tipCustomInput.value = "";
  tipNoneButton.classList.remove("selected");
  tipCustomApplyButton.classList.remove("selected");
  tipNoneButton.setAttribute("aria-pressed", "false");
  tipCustomApplyButton.setAttribute("aria-pressed", "false");
  updatePayConfirmScope();
  // ALPHA-08 delta payments + design/QA gate finding G: loads the live
  // uncovered-lines subtotal/tax and every preset's resolved tip/total for
  // THIS pay mode right now, before anything is shown.
  // SPLIT1: a By amount or No guest sheet never opens on another scope's figures. The preview is cleared first, and
  // a refused read (an amount over the live balance, a stale guest row) says so and leaves the sheet shut.
  const scopeNeedsOwnPreview = scope.type === "amount" || scope.type === "unassigned";
  if (scopeNeedsOwnPreview) {
    state.payConfirmPreview = undefined;
  }
  const previewOutcome = await refreshPayConfirmPreview(isCurrent);
  if (!isCurrent()) return;
  if (scopeNeedsOwnPreview && previewOutcome === "failed") {
    state.payScope = undefined;
    const refusal = lastPayConfirmPreviewRefusal;
    showToast(
      refusal?.error === "amount_exceeds_balance" && refusal.remainingCents !== undefined
        ? `That is more than the ticket still owes (${formatCents(refusal.remainingCents)}).`
        : (refusal?.message ?? "Could not read this payment. Try again."),
      "danger",
    );
    await refreshPaymentPreview();
    return;
  }
  // VER33-F1 gate-finding fix: a mustChangePin session gets pin_change_
  // required from this read just as easily as from loadPayWarning below,
  // and handlePinChangeRequiredOnPayClose already opened the mandatory
  // change-PIN pad (a full screen replacement) as a side effect. Stop
  // here, before this sheet's own scrim is ever shown, exactly the "never
  // sees the rail's error box flash" guarantee the four payOrder/
  // performClose/etc. call sites already hold.
  if (previewOutcome === "pinRequired") {
    return;
  }
  renderTipPresets();
  updatePayConfirmSummary();
  updatePayConfirmSplitButton();
  // RCPT1-S3: the preview names the location's tip mode, so the tip choice is hidden (or not) only now.
  syncReceiptSaleUi();
  updatePayConfirmSummary();
  const orderId = ticketContext.orderId;
  // ALPHA-12: the "was this tab already paid" warning is about the WHOLE
  // order having a prior capture, a question that stays meaningful for
  // "pay my drinks" too (docs/superpowers/plans/2026-08-19-alpha-work-
  // breakdown.md's Amendment 2026-08-21: "the pay warning banner from the
  // third amendment still shows, with the prior capture detail"). Skipped
  // for "share": an in-progress split's own already-collected shares are
  // not a surprise here, they are exactly what the split-progress sheet
  // this Confirm was opened from already shows.
  if (scope.type === "share") {
    state.payWarningReadFailed = false;
    state.payWarningPriorPayment = undefined;
    state.payWarningCaptured = [];
    state.payWarningAcknowledged = false;
    renderPayWarning();
  } else {
    const warningOutcome = await loadPayWarning(orderId, isCurrent);
    if (!isCurrent()) return;
    // Same stop as previewOutcome above: the PIN pad already opened.
    if (warningOutcome === "pinRequired") {
      return;
    }
    if (warningOutcome === "failed") {
      showToast(
        "Could not check this tab's payment history. Payment is blocked until this succeeds.",
        "danger",
      );
    }
  }
  // A previous attempt from this same sheet may have left the Confirm
  // button disabled after a definitive failure that itself never got a
  // chance to reopen the sheet; recomputed fresh from current state
  // (tip choice, warning acknowledgment, warning-read outcome) rather
  // than force-enabled.
  state.payCardsResolved = true;
  updatePayConfirmButtonState();
  payConfirmScrim.hidden = false;
}

/** CASH1-S2-U1. After a Captured cash payment, the Change due screen from the server's recorded figures (never worked out
 * here). Nothing is drawn for a card payment or an attempt without readable cash figures. */
function showCashChangeIfCash(attempt: PaymentAttemptDto): void {
  if (attempt.status !== "Captured") return;
  const figures = changeFromAttempt(attempt);
  if (figures === null) return;
  cashTender.showChange({
    changeCents: figures.changeCents,
    tenderedCents: figures.tenderedCents,
    dueCents: figures.tenderedCents - figures.changeCents,
  });
}

function closePayConfirmSheet(refreshRing = true): void {
  const refreshUntippedRing =
    refreshRing &&
    !payConfirmScrim.hidden &&
    payConfirmOpeningIsCurrent(activePayConfirmOpening);
  payConfirmOpeningToken = payConfirmOpeningToken + 1;
  activePayConfirmOpening = undefined;
  resetReaderCartDisplay();
  readerCartOriginalKeyMayReplay = false;
  payConfirmScrim.hidden = true;
  state.payScope = undefined;
  state.payTender = undefined;
  state.payCashOffered = undefined;
  state.payCashLockedLabel = undefined;
  if (prepayClientSaveGuest !== undefined) closePrepayClientSave();
  // Checkout can supersede an in-flight ring read. Replenish it through
  // the existing untipped reader and its failure/coverage/staleness guards.
  if (refreshUntippedRing) void refreshPaymentPreview();
}

// ---------------------------------------------------------------------
// Payments-track Slice 3: card-on-file. See src/domain/cardOnFile.ts's
// module header and docs/payment-provider-evaluation.md's PROV-105 launch
// gate. Three pieces: "Use saved card" at Pay (below), the "save this
// card?" consent screen offered right after a fresh card-present capture,
// and the saved-cards management sheet.
// ---------------------------------------------------------------------

async function loadSavedCards(
  orderId: string, isCurrent: () => boolean = () => true,
): Promise<void> {
  const ticketContext = captureFloorSaleContext();
  if (ticketContext?.orderId !== orderId || !floorSaleContextIsCurrent(ticketContext) ||
      !isCurrent()) return;
  const session = requireSession();
  const result = await listSavedCardsForOrder(session.apiBaseUrl, session.tenantId, orderId);
  if (!floorSaleContextIsCurrent(ticketContext) || !isCurrent()) return;
  if (result.status !== 200) {
    // Fails soft: "Use saved card" simply stays hidden, the same
    // byte-for-byte pre-slice Pay flow, rather than blocking Pay over a
    // feature this ticket may not even use.
    state.savedCards = [];
  } else {
    state.savedCards = result.body.savedCards;
  }
  renderManageSavedCardsRow();
}

/** The ticket rail's own "Manage saved cards" entry point, independent of
 * Pay's own enabled state: Pay disables itself once nothing on the ticket
 * is unpaid (renderTicketActions), but a saved card must still be
 * removable on a fully-paid ticket, per PROV-105's own "removal is a
 * first-class supported action, not an afterthought." Called from every
 * loadSavedCards call site, so it always reflects the live list. */
function renderManageSavedCardsRow(): void {
  // UX39: "Manage saved cards" used to be its own standalone header chip
  // (manageCardsOptionEligible below carries the exact rule now); folded
  // into #btn-ticket-more.
  renderTicketMoreButton();
  // Payments-track Slice 4: "Keep tab open"'s own eligibility depends on
  // savedCards too (DEC-002 Section 1: no hold when a card is already on
  // file), so every place that refreshes the saved-cards list also needs
  // this recomputed, not only the hold-status load itself.
  renderKeepTabOpenRow();
}

// ---------------------------------------------------------------------
// Payments-track Slice 4 (DEC-002, docs/decision-register.md). "Keep tab
// open" with a hold (DEC-002 Section 1's own trigger: no card on file,
// guest explicitly asks to keep the tab open) and the ticket rail's own
// hold-status badge. See src/domain/tabs.ts's module header for the full
// domain write-up this UI drives.
// ---------------------------------------------------------------------

/** LOUNGERMAJ1 (lounger-18): false once the hold read (HOLDPERM1: no longer Settings) says this location has no hold amount, so the hold action is not
 * offered at all rather than failing after the tap. Undefined until read, or when the read failed (then it is offered, as
 * before, and the tap says so). */
let holdAmountConfigured: boolean | undefined;

async function loadHoldStatus(orderId: string): Promise<void> {
  const session = requireSession();
  const result = await getHoldForOrder(session.apiBaseUrl, session.tenantId, orderId);
  // HOLDPERM1: the hold amount comes from this floor-readable read, never from Settings (a bartender gets 403 there).
  holdAmountConfigured =
    result.status === 200 && result.body.holdAmountCents !== undefined
      ? result.body.holdAmountCents !== null
      : undefined;
  renderKeepTabOpenRow();
  // Fails soft, the same convention loadSavedCards already establishes
  // immediately above: the hold badge simply stays hidden and "Keep tab
  // open" stays offered rather than blocking ticket entry over a status
  // read failing.
  state.activeHold = result.status === 200 ? result.body.hold : null;
  // PAY14 (Section 4.5): the server has already recorded the manager
  // exception whenever it reports providerMismatch, so the badge below and
  // the manager's queue can never disagree. testHoldClearedNow is true
  // only on the one read that set a test hold aside, so a second device
  // (or this one on a later entry) sees an ordinary tab with no hold.
  if (result.status === 200) {
    state.holdProviderMismatch = result.body.providerMismatch === true;
    if (result.body.testHoldClearedNow === true) {
      state.testHoldClearedNoteVisible = true;
    }
  }
  renderHoldBadge();
  renderKeepTabOpenRow();
}

/** PAY14 (Section 8.1, D2 and the Q3 note, approved by the ui-ux-designer
 * in design/reviews/pay14-order-hold-provider-recheck-2026-09-25.md). The
 * badge keeps its own row under the header, a status label, never a
 * control. Modifier classes, never inline colors: the danger trio for a
 * hold that needs a manager (not the warning palette an ordinary hold
 * already uses), the info trio for the test-hold note. */
const HOLD_BADGE_NEEDS_MANAGER_CLASS = "ticket-hold-badge--needs-manager";
const HOLD_BADGE_TEST_CLEARED_CLASS = "ticket-hold-badge--test-cleared";

function renderHoldBadge(): void {
  ticketHoldBadge.classList.remove(HOLD_BADGE_NEEDS_MANAGER_CLASS, HOLD_BADGE_TEST_CLEARED_CLASS);
  // PAY14, the Q3 note: shown in the badge's own slot on the ticket where
  // this device cleared a test hold, until the bartender leaves it.
  if (state.testHoldClearedNoteVisible) {
    ticketHoldBadge.hidden = false;
    ticketHoldBadge.classList.add(HOLD_BADGE_TEST_CLEARED_CLASS);
    ticketHoldBadge.textContent = TEST_HOLD_CLEARED_NOTE_TEXT;
    return;
  }
  const hold = state.activeHold;
  if (hold === undefined || hold === null) {
    ticketHoldBadge.hidden = true;
    return;
  }
  if (hold.status === "active" && state.holdProviderMismatch) {
    // PAY14 (D2): the hold was placed through a payment processor this
    // location no longer uses. Its manager exception row already exists.
    ticketHoldBadge.hidden = false;
    ticketHoldBadge.classList.add(HOLD_BADGE_NEEDS_MANAGER_CLASS);
    ticketHoldBadge.textContent = HOLD_NEEDS_MANAGER_BADGE_TEXT;
    return;
  }
  if (hold.status === "active") {
    ticketHoldBadge.hidden = false;
    ticketHoldBadge.textContent = `Hold: ${formatCents(hold.authorized_amount_cents)}`;
    return;
  }
  if (hold.status === "captured") {
    ticketHoldBadge.hidden = false;
    ticketHoldBadge.textContent = `Hold used: ${formatCents(hold.captured_amount_cents ?? hold.authorized_amount_cents)}`;
    return;
  }
  // "released": the money was never needed and was given back; nothing
  // left worth a bartender's attention on the ticket rail. PAY14:
  // "set_aside" hides the same way (the tab now has no hold), except
  // while the Q3 note above is showing.
  ticketHoldBadge.hidden = true;
}

/** PAY14 (Section 8.1, D2 required change 3, and the Q3 note): a Pay (or
 * split collect) refusal updates the badge in the same render, so the
 * badge and the rail can never disagree on one screen.
 * hold_provider_mismatch marks the hold as needing a manager;
 * hold_cleared_take_new_card means the server set a test hold aside, so
 * the tab now has no hold and the badge slot shows the Q3 note. */
function applyHoldRefusalToBadge(code: string | undefined): void {
  if (code === "hold_provider_mismatch") {
    state.holdProviderMismatch = true;
    renderHoldBadge();
    return;
  }
  if (code === "hold_cleared_take_new_card") {
    markActiveHoldSetAside();
    state.testHoldClearedNoteVisible = true;
    renderHoldBadge();
    renderKeepTabOpenRow();
  }
}

/** PAY14: the server reports it set this tab's active test hold aside; the
 * locally-known hold (if any) follows, so "Keep tab open" is offered again
 * (keepTabOpenOptionEligible keys on status !== "active"). */
function markActiveHoldSetAside(): void {
  const hold = state.activeHold;
  if (hold !== undefined && hold !== null && hold.status === "active") {
    state.activeHold = { ...hold, status: "set_aside" };
  }
  state.holdProviderMismatch = false;
}

/** DEC-002 Section 1: offered only when this order has no card on file
 * AND no currently-active hold (a captured or released hold from
 * earlier in this same order's life does not block opening a fresh one,
 * though DEC-002's own one-hold-at-a-time rule, HoldAlreadyExistsError,
 * still applies if one is somehow still Active).
 *
 * T19: both `state.savedCards` and `state.activeHold` must be genuinely
 * KNOWN (not `undefined`, "not yet loaded for this ticket") before this
 * can return true. Without the `hold !== undefined` guard, an unresolved
 * hold read was silently treated as "no hold" (`undefined?.status !==
 * "active"` is `true`), which could offer this option while a real active
 * hold's own read just had not resolved yet. */
function keepTabOpenOptionEligible(): boolean {
  const hold = state.activeHold;
  return (
    state.orderId !== undefined &&
    state.savedCards !== undefined &&
    state.savedCards.length === 0 &&
    holdAmountConfigured !== false &&
    hold !== undefined &&
    hold?.status !== "active"
  );
}

function renderKeepTabOpenRow(): void {
  // UX39: "Keep tab open" used to be its own standalone header chip;
  // folded into #btn-ticket-more.
  renderTicketMoreButton();
  refreshOpenMoreSheet();
}

/** FLOORGLITCH1: true while the reads that decide the hold action (saved cards, the ticket's hold) are still out for
 * this ticket and nothing known yet rules the action out. The More sheet then shows the row as Loading instead of
 * leaving it out. A ticket with a card on file, a location with no hold amount, or an active hold is not waiting. */
function keepTabOpenStillLoading(): boolean {
  if (state.orderId === undefined || state.closedView !== undefined || holdAmountConfigured === false) {
    return false;
  }
  if (state.savedCards !== undefined && state.savedCards.length > 0) {
    return false;
  }
  return state.savedCards === undefined || state.activeHold === undefined;
}

/** The More sheet's options as shown on the sheet: the header button's own list, plus the hold row as a disabled
 * Loading row while its reads are out. The header button never counts that row. */
function ticketMoreSheetOptions(): ChoiceOption[] {
  const options = ticketMoreOptions();
  if (options.length > 1 && keepTabOpenStillLoading()) {
    options.push({
      id: "keep-tab-open-loading",
      label: KEEP_TAB_OPEN_LABEL,
      sublabel: "Loading",
      disabled: true,
      testid: "more-keep-tab-open-loading",
    });
  }
  return options;
}

/** True while the sheet on screen is More and carries the Loading row, so the arrival of the data rebuilds it. */
let moreSheetShowsLoading = false;

function showTicketMoreSheet(): void {
  const options = ticketMoreSheetOptions();
  moreSheetShowsLoading = options.some((option) => option.id === "keep-tab-open-loading");
  openChoiceSheet({
    title: "More",
    options,
    onPick: (id) => pickTicketMoreOption(id),
    onCancel: () => {
      moreSheetShowsLoading = false;
    },
  });
}

/** Called whenever a read that decides one of the More rows lands: an open More sheet that showed Loading is rebuilt
 * with what is known now, in place, so the row never has to be waited for by closing and reopening the sheet. */
function refreshOpenMoreSheet(): void {
  if (!moreSheetShowsLoading) {
    return;
  }
  if (choiceScrim.hidden || choiceSheetEl.hidden || choiceTitle.textContent !== "More") {
    moreSheetShowsLoading = false;
    return;
  }
  showTicketMoreSheet();
}

// ---------------------------------------------------------------------
// UX39 (docs/backlog.md; docs/build-briefs/UX39-ticket-header-more-
// button.md, DEC-195). Discount, Payments, Manage saved cards and Keep
// tab open used to be four separate header chips; they now collapse
// behind one #btn-ticket-more icon button. The four eligibility checks
// below are the EXACT same rules those four chips already enforced
// (discountOptionEligible mirrors what renderTicketActions used to write
// straight to ticketDiscountButton.hidden; manageCardsOptionEligible
// mirrors renderManageSavedCardsRow's own former rule;
// keepTabOpenOptionEligible above mirrors renderKeepTabOpenRow's own
// former rule; Payments reuses ticketPaymentsChipEligibility unchanged),
// shared between the button's own visibility (renderTicketMoreButton,
// called from every render pass that already recomputes one of the four)
// and the sheet's own contents (handleTicketMoreClick, recomputed fresh
// at tap time) so the two can never disagree about which of the four is
// eligible.
// ---------------------------------------------------------------------

function discountOptionEligible(): boolean {
  return canApplyDiscounts() && state.orderId !== undefined && state.lines.length > 0;
}

/** LOUNGERDISC1 (DEC-598). A location that turned Apply a discount off for this login still shows Discount in More,
 * as "Discount (ask a manager)", rather than dropping the row with no explanation (finding lounger-03). Same ticket
 * conditions as discountOptionEligible; only the permission differs. A permission absent from the snapshot is not
 * offered, since that is not a decision anyone made. */
function discountAskManagerEligible(): boolean {
  const session = state.session;
  return (
    session !== undefined &&
    session.permissions[DISCOUNT_PERMISSION] === "denied" &&
    state.orderId !== undefined &&
    state.lines.length > 0
  );
}

/** The manager approval PIN step exists only for an approval-required grant; the server refuses a denied one even with
 * a manager's PIN. So this says why and who can do it, instead of offering a PIN prompt that cannot work. */
function handleDiscountAskManagerClick(): void {
  openChoiceSheet({
    title: "Discount",
    note: "Discounts are turned off for your login at this location. Ask a manager to apply the discount from their own login.",
    options: [],
    cancelLabel: "Close",
    onPick: () => undefined,
  });
}

/** CORE2. A ticket with no drink on it (never rung, or every drink voided or removed) can be cancelled instead of
 * sitting in Open Tabs and carrying over. The same rule the server enforces (pos.tab.cancel, no active line, no
 * payment attempt); the server decides, this only avoids offering a control that cannot work. */
function cancelTicketOptionEligible(): boolean {
  const session = state.session;
  return (
    session !== undefined &&
    hasPermission(session, "pos.tab.cancel") &&
    state.orderId !== undefined &&
    state.lines.length === 0 &&
    state.orderStatus !== "closed" &&
    state.orderStatus !== "canceled"
  );
}

/** VOIDTICKET1. A ticket with drinks on it can be voided in one step from More. The mirror of
 * cancelTicketOptionEligible (which is the empty ticket) with the permission a single-drink void uses, so a role
 * that may not void a made drink is not offered a ticket-wide void. A role whose grant is approval-required is
 * offered it and asked for a manager's PIN, exactly as a single-drink void does. */
function voidTicketOptionEligible(): boolean {
  const session = state.session;
  return (
    session !== undefined &&
    hasPermission(session, "pos.tab.cancel") &&
    hasPermission(session, "money.line.void_prepared") &&
    state.orderId !== undefined &&
    state.lines.length > 0 &&
    state.orderStatus !== "closed" &&
    state.orderStatus !== "canceled"
  );
}

/** T19: `undefined` ("not yet known for this ticket") is never eligible,
 * only a genuinely resolved, non-empty list is. */
function manageCardsOptionEligible(): boolean {
  return (
    state.savedCards !== undefined &&
    (state.savedCards.length > 0 || state.pendingCardDetaches.length > 0)
  );
}

/** Ruled by the ui-ux-designer, 2026-09-24 (the brief's Section 2.3):
 * #btn-ticket-more is hidden outright whenever none of the four is
 * currently eligible, the same shape each of the four individual
 * controls it replaces already followed, rather than staying visible
 * with a sheet that would open on nothing. Recomputes all four every
 * time regardless of which underlying render pass triggered this call
 * (four cheap boolean checks, no network read), so a change reaching
 * any ONE of them still keeps the OTHER three's own contribution to
 * this button's visibility current. */
/** LOUNGERMAJ1 (lounger-18): the action says what it does. It puts a hold on a card; "Keep tab open" said nothing of that. */
const KEEP_TAB_OPEN_LABEL = "Hold a card on this tab";

function ticketMoreOptions(): ChoiceOption[] {
  const { canView, canSend } = ticketPaymentsChipEligibility();
  const options: ChoiceOption[] = [];
  if (discountOptionEligible()) {
    options.push({ id: "discount", label: "Discount", testid: "more-discount-option" });} else if (discountAskManagerEligible()) {
    options.push({
      id: "discount-ask-manager",
      label: "Discount (ask a manager)",
      testid: "more-discount-ask-manager-option",
    });
  }
  if (canView || canSend) {
    options.push({ id: "payments", label: "Payments", testid: "more-payments-option" });
  }
  if (cancelTicketOptionEligible()) {
    options.push({
      id: "cancel-ticket",
      label: "Cancel ticket",
      testid: "more-cancel-ticket-option",
    });
  }
  if (voidTicketOptionEligible()) {
    options.push({
      id: "void-ticket",
      label: "Void ticket",
      testid: "more-void-ticket-option",
    });
  }
  if (manageCardsOptionEligible()) {
    options.push({
      id: "manage-cards",
      label: "Manage saved cards",
      testid: "more-manage-cards-option",
    });
  }
  if (keepTabOpenOptionEligible()) {
    options.push({
      id: "keep-tab-open",
      label: KEEP_TAB_OPEN_LABEL,
      testid: "more-keep-tab-open-option",
    });
  }
  return options;
}

function pickTicketMoreOption(id: string): void {
  if (id === "discount") {
    void handleTicketDiscountClick();
  } else if (id === "discount-ask-manager") {
    handleDiscountAskManagerClick();
  } else if (id === "payments") {
    handleTicketPaymentsChipClick();
  } else if (id === "manage-cards") {
    void openSavedCardsSheet();
  } else if (id === "keep-tab-open") {
    void handleKeepTabOpenClick();
  } else if (id === "cancel-ticket") {
    handleCancelTicketClick();
  } else if (id === "void-ticket") {
    void handleVoidTicketClick();
  }
}

/** UIFIX5: while only one action applies, the header button is that action by name and runs it on one tap, with no
 * sheet in between. From three actions up the ⋯ button opens the sheet; two keep it, since a sheet of two still
 * saves nothing over the header row. The eligibility rules are unchanged. */
/** RINGUX1: the More button says More, with a caret, instead of three dots. Its aria-label is unchanged. */
const TICKET_MORE_LABEL = "More \u25BE";

function renderTicketMoreButton(): void {
  renderTicketRefundButton();
  const options = ticketMoreOptions();
  ticketMoreButton.hidden =
    state.orderId === undefined || options.length === 0 || state.closedView !== undefined;
  const single = options.length === 1 ? options[0] : undefined;
  ticketMoreButton.classList.toggle("is-single-action", single !== undefined);
  if (single !== undefined) {
    ticketMoreButton.textContent = single.label;
    ticketMoreButton.setAttribute("aria-label", single.label);
  } else {
    ticketMoreButton.textContent = TICKET_MORE_LABEL;
    ticketMoreButton.setAttribute("aria-label", "More ticket actions");
  }
}

/** Opens the More sheet (or runs the one applicable action), recomputing the eligible options FRESH at the moment
 * of the tap, never from renderTicketMoreButton's own last run. Each pick routes to the EXACT existing handler the
 * removed standalone button already called; none of the underlying flows changes. */
function handleTicketMoreClick(): void {
  const options = ticketMoreOptions();
  if (options.length === 1) {
    pickTicketMoreOption(options[0]!.id);
    return;
  }
  showTicketMoreSheet();
}

/** CORE2. Confirms, then cancels the ticket on screen and returns to Open Tabs. A confirm, not a bare tap: a cancelled
 * ticket cannot be reopened. */
function handleCancelTicketClick(): void {
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  openChoiceSheet({
    title: "Cancel this ticket?",
    options: [{ id: "confirm", label: "Cancel ticket", testid: "cancel-ticket-confirm" }],
    cancelLabel: "Keep ticket",
    onPick: (id) => {
      if (id === "confirm") {
        void performCancelTicket(orderId);
      }
    },
  });
}

async function performCancelTicket(orderId: string, doneWord = "canceled"): Promise<void> {
  const session = requireSession();
  const result = await cancelOrder(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not cancel the ticket (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    return;
  }
  const label = tabDisplayLabel(
    state.orderName ?? null,
    state.guests.map((guest) => guest.label),
  );
  clearPayIdempotencyKeysForOrder(orderId);
  resetTicketState();
  refreshTileDisabledState();
  showOpenTabsScreen();
  showToast(`${label} ${doneWord}`, "success");
  void loadOpenTabs();
}

// ---------------------------------------------------------------------
// VOIDTICKET1: Void ticket in More. One reason, one manager approval when the role needs it, every drink off the
// ticket, then the ticket cancelled the way an emptied ticket is today. It is a sequence of the EXISTING commands:
// voidOrderLine for a made (Prepared) drink, removeLine for a drink not yet made, cancelOrder at the end. No new
// route, reason list or approval path. A ticket with any payment on it is refused first, because a refund is a
// different action.
// ---------------------------------------------------------------------

let voidTicketInFlight = false;

function voidTicketDrinkWord(count: number): string {
  return count === 1 ? "1 drink" : `${count} drinks`;
}

async function handleVoidTicketClick(): Promise<void> {
  const orderId = state.orderId;
  if (orderId === undefined || state.lines.length === 0 || voidTicketInFlight) return;
  const context = captureFloorSaleContext();
  if (context === undefined || !floorSaleContextIsCurrent(context)) return;
  const session = requireSession();
  // The first step: any payment attempt on the ticket ends the flow before anything is changed. Read live, never
  // from the rail, which can be a read behind.
  const payments = await getPayments(session.apiBaseUrl, session.tenantId, orderId);
  if (!floorSaleContextIsCurrent(context)) return;
  if (payments.status !== 200) {
    showToast(
      `Could not check this ticket's payments. ${capitalizeServerMessage(payments.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  if (payments.body.payments.length > 0) {
    openChoiceSheet({
      title: "Void ticket",
      note: "A payment has been taken on this ticket, so it cannot be voided. To give the money back, use Payments to refund it.",
      options: [],
      cancelLabel: "Close",
      onPick: () => undefined,
    });
    return;
  }
  const reasons = await listVoidReasons(session.apiBaseUrl, session.tenantId);
  if (!floorSaleContextIsCurrent(context)) return;
  if (reasons.status !== 200) {
    showToast(
      `Could not load the void reasons. ${capitalizeServerMessage(reasons.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const sorted = sortByName(reasons.body.voidReasons, (reason) => reason.label);
  if (sorted.length === 0) {
    showToast(
      "There are no void reasons set up for this location. An admin adds them in Settings.",
      "danger",
    );
    return;
  }
  const lines = state.lines.map((line) => ({
    orderLineId: line.orderLineId,
    prepared: line.preparedState === "prepared",
  }));
  const ticketLabel = tabDisplayLabel(
    state.orderName ?? null,
    state.guests.map((guest) => guest.label),
  );
  openChoiceSheet({
    title: "What happened?",
    subtitle: `${ticketLabel}: ${voidTicketDrinkWord(lines.length)}`,
    note: "Every drink comes off the ticket for this one reason. Drinks already made stay used up in stock.",
    options: sorted.map((reason) => ({
      id: reason.id,
      label: reason.label,
      testid: "void-reason-option",
    })),
    columns: 2,
    onPick: (voidReasonId) => {
      const reason = sorted.find((candidate) => candidate.id === voidReasonId);
      if (reason === undefined) return;
      openChoiceSheet({
        title: "Void this ticket?",
        subtitle: `${ticketLabel}: ${voidTicketDrinkWord(lines.length)}, ${reason.label}`,
        note: "The drinks come off and the ticket closes. This cannot be undone.",
        options: [{ id: "confirm", label: "Void ticket", testid: "void-ticket-confirm" }],
        cancelLabel: "Keep ticket",
        onPick: (id) => {
          if (id === "confirm") {
            void performVoidTicket(orderId, lines, reason.id, context);
          }
        },
      });
    },
  });
}

async function performVoidTicket(
  orderId: string,
  lines: { orderLineId: string; prepared: boolean }[],
  voidReasonId: string,
  context: FloorSaleContext,
): Promise<void> {
  if (voidTicketInFlight || !floorSaleContextIsCurrent(context)) return;
  voidTicketInFlight = true;
  try {
    const session = requireSession();
    // One key per drink, made once for the whole action: an approval retry or a second tap replays instead of
    // voiding twice, and the approval audit row is written once per drink as a single-drink void writes it.
    const keys = new Map(lines.map((line) => [line.orderLineId, crypto.randomUUID()]));
    const done = new Set<string>();
    // Made drinks first: they are the ones that ask for approval, so a refused or cancelled approval changes nothing.
    const ordered = [...lines.filter((line) => line.prepared), ...lines.filter((line) => !line.prepared)];
    const attempt = async (approval?: { approverUserId: string; approverPin: string }) => {
      let last: ApiResult<Partial<ApiErrorBody>> = {
        status: 200,
        body: {},
        networkError: false,
      };
      for (const line of ordered) {
        const key = keys.get(line.orderLineId)!;
        const step: ApiResult<Partial<ApiErrorBody>> = line.prepared
          ? await voidOrderLine(session.apiBaseUrl, session.tenantId, line.orderLineId, {
              idempotencyKey: key,
              voidReasonId,
              ...approval,
            })
          : await removeLine(session.apiBaseUrl, session.tenantId, line.orderLineId, {
              idempotencyKey: key,
            });
        if (step.status !== 200 && step.status !== 201) return step;
        done.add(line.orderLineId);
        last = step;
      }
      return last;
    };
    const gated = await requestApproval(
      "Void ticket",
      attempt,
      () => {
        if (floorSaleContextIsCurrent(context)) showRingScreen();
      },
      { isCurrent: () => floorSaleContextIsCurrent(context) },
    );
    if (gated.canceled || !floorSaleContextIsCurrent(context)) return;
    const result = gated.result;
    if (result.status !== 200 && result.status !== 201) {
      showRingScreen();
      state.lines = state.lines.filter((line) => !done.has(line.orderLineId));
      renderTicket();
      await recomputeQuote();
      if (!floorSaleContextIsCurrent(context)) return;
      void refreshPaymentPreview();
      showToast(
        `Could not void the whole ticket (${result.body.message ?? result.body.error ?? result.status}). ` +
          `${voidTicketDrinkWord(done.size)} came off; the ticket is still open with the rest.`,
        "danger",
      );
      return;
    }
    showRingScreen();
    state.lines = [];
    renderTicket();
    await performCancelTicket(orderId, "voided");
  } finally {
    voidTicketInFlight = false;
  }
}

async function handleKeepTabOpenClick(): Promise<void> {
  const session = requireSession();
  if (state.orderId === undefined) {
    return;
  }
  const orderId = state.orderId;

  // The confirm sheet names the actual dollar amount that will be held,
  // so this is a real consent moment, not a blind tap: a manager-set
  // placeholder or an average-ticket default (src/domain/tenantSettings.ts)
  // is read fresh here rather than assumed, since it can change between
  // tickets.
  // HOLDPERM1: read from the floor-allowed hold read. An error is never "not configured": it says it could not load, with retry.
  const holdRead = await getHoldForOrder(session.apiBaseUrl, session.tenantId, orderId);
  if (holdRead.status !== 200 || holdRead.body.holdAmountCents === undefined) {
    openChoiceSheet({
      title: "Could not load the hold amount",
      subtitle: "The hold amount did not load. Check the connection and try again.",
      options: [{ id: "retry", label: "Try again", testid: "keep-tab-open-retry" }],
      cancelLabel: "Cancel",
      onPick: (id) => {
        if (id === "retry") {
          void handleKeepTabOpenClick();
        }
      },
    });
    return;
  }
  const holdAmountCents = holdRead.body.holdAmountCents;
  if (holdAmountCents === null) {
    showToast(
      "No hold amount is configured yet for this location. Ask a manager to set one in Settings.",
      "danger",
    );
    return;
  }

  openChoiceSheet({
    title: `${KEEP_TAB_OPEN_LABEL}?`,
    subtitle: `A ${formatCents(holdAmountCents)} hold will be placed on a card. Nothing is charged now.`,
    options: [
      {
        id: "confirm",
        label: "Place hold and keep tab open",
        testid: "keep-tab-open-confirm",
      },
    ],
    cancelLabel: "Cancel",
    onPick: (id) => {
      if (id === "confirm") {
        void performOpenHold(orderId);
      }
    },
  });
}

/** PAY13 Section 2.2/2c (F2), DEC-200: get-or-mint-and-persist, checking
 * the page-lifetime in-memory map FIRST (state.holdIdempotencyKeysByOrderId's
 * own doc comment has the full reasoning: without this, a device whose
 * sessionStorage throws minted a BRAND NEW key on every single retry for
 * the whole page load, reproducing this brief's own Headline defect). A
 * fresh crypto.randomUUID() on every "Keep tab open" tap meant a retry
 * never carried the SAME domain idempotency key an earlier, rolled-back
 * attempt used, so it never reached the server's own adopt-vs-refuse
 * guard at all -- it derived a brand-new adapter key the provider had
 * never seen, placing a SECOND, independent hold. Persisted (both
 * in-memory and, best-effort, into storage) BEFORE the request, so it
 * survives a reload even if this exact attempt fails ambiguously. */
function getOrMintHoldIdempotencyKey(tenantId: string, orderId: string): string {
  const existing = state.holdIdempotencyKeysByOrderId.get(orderId);
  if (existing !== undefined) {
    return existing;
  }
  const key = loadPersistedHoldKey(tenantId, orderId) ?? crypto.randomUUID();
  state.holdIdempotencyKeysByOrderId.set(orderId, key);
  savePersistedHoldKey(tenantId, orderId, key);
  return key;
}

/** PAY13 Section 2c/F2: mirrors the clear into the in-memory map the same
 * way getOrMintHoldIdempotencyKey mirrors the mint INTO it, so the two
 * can never drift apart -- every existing clearPersistedHoldKey call site
 * (performOpenHold's own success path, finishClosedTicket,
 * handleReopenClick's own defensive sweep) uses this instead of calling
 * clearPersistedHoldKey directly. */
function clearHoldIdempotencyKey(tenantId: string, orderId: string): void {
  state.holdIdempotencyKeysByOrderId.delete(orderId);
  clearPersistedHoldKey(tenantId, orderId);
}

async function performOpenHold(orderId: string): Promise<void> {
  const session = requireSession();
  const idempotencyKey = getOrMintHoldIdempotencyKey(session.tenantId, orderId);
  const result = await openTabHold(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey,
    // Terminal-assignment slice: the registered terminal THIS device IS
    // (session.ts's TerminalConfig.paymentTerminalId), picked from Open
    // Tabs' own account-menu "Payment Terminal" item
    // (openTerminalPickerScreen). Undefined on a device that has not
    // picked one yet falls back to the server's own documented
    // simulated-provider placeholder (src/domain/payments.ts's
    // resolveTerminalReferenceForCharge), never a bogus value sent here.
    terminalId: terminalConfigState?.paymentTerminalId,
  });
  if (result.status !== 200 && result.status !== 201) {
    // T19: a plain message, not the raw domain-error string. See
    // payCloseErrorMessages.ts's own KNOWN_HOLD_ERROR_MESSAGES header for
    // why this stays a second, narrow map rather than folding into
    // translatePayCloseError. PAY13: the key stays persisted here (never
    // cleared on a failure response) so the NEXT tap reuses it, the same
    // "an ambiguous failure must never look safe to mint a fresh key
    // against" rule PAY7 Section 2.5 already established for Pay.
    showToast(translateHoldError(result.body, result.status), "danger");
    return;
  }
  // PAY13: resolved (200/201) -- clear the key (memory and storage both)
  // so a genuinely NEW hold later on this same order mints a fresh one.
  clearHoldIdempotencyKey(session.tenantId, orderId);
  state.activeHold = result.body.hold;
  renderHoldBadge();
  renderKeepTabOpenRow();
  showToast(
    `Tab kept open with a ${formatCents(result.body.hold.authorized_amount_cents)} hold.`,
    "success",
  );
  // M0, 2026-08-28: a tab can already be past the increment threshold at
  // the moment its hold lands, and the server checks for exactly that
  // now. Applied after the "tab kept open" toast so the badge shows the
  // amount actually authorized, not the amount first asked for.
  handleHoldIncrementOutcome(result.body.holdIncrement);
}

/** DEC-002 Section 3: surfaces the best-effort "holdIncrement" field a
 * triggering action carries back, as a non-blocking signal, never a
 * failure of the action that carried it. "incremented" refreshes the
 * badge silently (the hold simply grew, nothing the bartender needs to
 * act on); "declined"/"ceiling_reached" get the plain-language prompt
 * DEC-002 Section 3 itself specifies.
 *
 * M0, 2026-08-28: called from four places now, not one (adding a drink,
 * adding a pour or beer, changing a line's liquor, and opening the hold
 * itself), and carrying a fourth kind. "check_failed" means the check did
 * not finish, so nobody knows whether the hold still covers the ticket.
 * That gets its own words: the decline message tells the bartender to ask
 * for another card, which would be a made-up diagnosis here and would
 * send them to a guest for no reason. */
function handleHoldIncrementOutcome(outcome: HoldIncrementOutcomeDto | undefined): void {
  if (outcome === undefined) {
    return;
  }
  if (outcome.kind === "incremented" && outcome.hold !== undefined) {
    state.activeHold = outcome.hold;
    renderHoldBadge();
    return;
  }
  // PAY14 (Section 4.3): the badge, never a toast (a toast on every drink
  // added to a stale tab would repeat for the rest of the night).
  if (outcome.kind === "hold_provider_mismatch") {
    state.holdProviderMismatch = true;
    renderHoldBadge();
    return;
  }
  if (outcome.kind === "hold_set_aside") {
    markActiveHoldSetAside();
    state.testHoldClearedNoteVisible = true;
    renderHoldBadge();
    renderKeepTabOpenRow();
    return;
  }
  if (outcome.kind === "check_failed") {
    showToast(
      "Could not check this tab's hold just now. It may not cover the ticket, so take a payment or ask a manager.",
      "danger",
    );
    return;
  }
  if (outcome.kind === "declined" || outcome.kind === "ceiling_reached") {
    showToast(
      "This card's hold could not be increased. Ask for another card, or collect a partial payment now.",
      "danger",
    );
  }
}

// ---------------------------------------------------------------------
// AU8 (docs/backlog.md): the manager-exceptions queue. src/domain/
// managerExceptions.ts is the domain design write-up; this is the client
// half of the gap that item names: today the only way to learn a
// business-day-batch short capture happened is GET .../manager-exceptions
// (already existed, AU2b) or a direct table read, and an order carrying
// one only surprises a bartender at Pay/Close via the existing
// ManagerExceptionBlocksOrderError refusal. Two surfaces, one shared data
// load (refreshManagerExceptions):
//   - Settings > Operations' own badge/sheet (adminOpenManagerExceptions-
//     Button, the global #manager-exceptions-scrim overlay -- UX18/
//     DEC-061 moved the badge's entry point here from Open Tabs' own
//     former topbar button; the sheet itself was always a global overlay,
//     never floor-scoped markup): every open exception this tenant
//     carries, with a one-tap resolve.
//   - The ring screen's own ticket-rail banner
//     (renderTicketManagerExceptionBanner): a plain heads-up on any order
//     that carries one, shown BEFORE Pay/Close rather than only after the
//     refusal. STAYS on the floor per DEC-061; unaffected by the badge's
//     move.
// Both gated on admin.manager_exception.view (hasPermission); resolving is
// separately gated on admin.manager_exception.resolve, so a viewer without
// the resolve grant sees the queue but never the Resolve control, matching
// this codebase's own permission-driven rule (CLAUDE.md) rather than a
// role check.
// ---------------------------------------------------------------------

/** The one data load both surfaces share: every OPEN exception, tenant-
 * wide. Never issues the request at all for a session that cannot see
 * this queue (admin.ts's own reconciliationQueueVisible convention),
 * rather than calling the route and hiding the result -- there is no
 * server-side gap here to route around, this is purely "do not ask a
 * question this session is not allowed to ask." Fails soft on a read
 * error: the badge/banner/sheet simply keep showing whatever was last
 * known, the same loadSavedCards/loadHoldStatus convention. */
async function refreshManagerExceptions(): Promise<void> {
  const session = state.session;
  if (session === undefined || !hasPermission(session, "admin.manager_exception.view")) {
    state.managerExceptions = [];
    renderManagerExceptionsBadge();
    renderTicketManagerExceptionBanner();
    renderTopbarTerminalSegment();
    return;
  }
  // AU13-F1: captured before the first await below. Called void
  // (fire-and-forget) from loadOpenTabs, so loadOpenTabs's own gen-based
  // checks cannot wrap this after the fact; it needs its own.
  const gen = sessionGeneration;
  const result = await listManagerExceptions(session.apiBaseUrl, session.tenantId);
  // AU13-F1: the session that started this request may have ended while
  // it was in flight. A response for a dead session writes nothing.
  if (!sessionIsCurrent(gen)) {
    return;
  }
  if (result.status !== 200) {
    return;
  }
  state.managerExceptions = result.body.managerExceptions;
  renderManagerExceptionsBadge();
  renderTicketManagerExceptionBanner();
  renderTopbarTerminalSegment();
  if (!managerExceptionsScrim.hidden) {
    renderManagerExceptionsList();
  }
}

function renderManagerExceptionsBadge(): void {
  const session = state.session;
  // UX80: the row's own visibility, the group badge and the name chip count
  // are all decided in refreshAccountMenuRows from the same count.
  if (session !== undefined) {
    refreshAccountMenuRows(session);
  }
}

/** The ticket rail's own heads-up, filtered to the CURRENT order. Hidden
 * outright (never merely empty-text) for anyone whose permission snapshot
 * denies admin.manager_exception.view -- exactly today's behavior for a
 * Lounger, per this item's own scope: the existing refusal at Pay/Close
 * still applies to them unchanged, only this proactive warning is gated. */
function renderTicketManagerExceptionBanner(): void {
  const session = state.session;
  const visible = session !== undefined && hasPermission(session, "admin.manager_exception.view");
  if (!visible || state.orderId === undefined) {
    ticketManagerExceptionBanner.hidden = true;
    return;
  }
  const matches = state.managerExceptions.filter((row) => row.order_id === state.orderId);
  if (matches.length === 0) {
    ticketManagerExceptionBanner.hidden = true;
    return;
  }
  const allHoldProviderMismatch = matches.every((row) => row.kind === "hold_provider_mismatch");
  // UX53 (docs/backlog.md, designer's rule). The zero-due prompt's
  // closed state ("All paid. Ticket closed.") means nothing further is
  // being asked about this hold, so the banner's own instruction to
  // clear it before Pay works is a false instruction on a ticket that
  // has already closed; hide the banner outright while that state
  // shows, whatever its rows' kinds. In the prompt's OPEN state
  // ("Nothing left to pay. Close this ticket?") the instruction is
  // still true unless every open row is hold_provider_mismatch, the
  // exact case the D5 sentence below already exists to name -- showing
  // both the prompt asking to close AND the banner repeating the same
  // fact is redundant, so it hides there too. The D2 badge
  // (ticketHoldBadge, above) is unrelated and unchanged by this check.
  if (!zeroDuePromptEl.hidden && (state.ticketSelfClosed || allHoldProviderMismatch)) {
    ticketManagerExceptionBanner.hidden = true;
    return;
  }
  ticketManagerExceptionBanner.hidden = false;
  // PAY14 (Section 8.1, D5): when EVERY open row on this ticket is a
  // hold_provider_mismatch row, D5's sentence; the mixed-kind case keeps
  // today's text.
  if (allHoldProviderMismatch) {
    ticketManagerExceptionText.textContent = HOLD_PROVIDER_MISMATCH_TICKET_BANNER_TEXT;
    return;
  }
  const totalOutstandingCents = matches.reduce((sum, row) => sum + row.outstanding_cents, 0);
  const plural = matches.length === 1 ? "an open exception" : `${matches.length} open exceptions`;
  ticketManagerExceptionText.textContent = `This tab has ${plural} (${formatCents(
    totalOutstandingCents,
  )} outstanding). A manager must resolve it before Pay or Close will work.`;
}

/** Asks the server for this device's reader health when there is a reader, a signed-in user, and no reading younger than
 * READER_HEALTH_REFRESH_MS for it. Called from the pill's own render, which every screen change runs, so the floor asks
 * when it opens and again as the person moves around, never on a timer of its own. */
function refreshReaderHealthIfDue(): void {
  const session = state.session;
  const terminalId = terminalConfigState?.paymentTerminalId;
  if (session === undefined || terminalId === undefined || readerHealthInFlight) {
    return;
  }
  if (!hasPermission(session, TERMINAL_PICKER_PERMISSION)) {
    return;
  }
  const current = readerHealthReading;
  if (
    current !== undefined &&
    current.terminalId === terminalId &&
    Date.now() - current.atMs < READER_HEALTH_REFRESH_MS
  ) {
    return;
  }
  readerHealthInFlight = true;
  void getReaderHealth(session.apiBaseUrl, session.tenantId, terminalId)
    .then((result) => {
      if (result.status === 200 && state.session?.token === session.token && terminalConfigState?.paymentTerminalId === terminalId) {
        readerHealthReading = {
          terminalId,
          state: result.body.health.state,
          atMs: Date.now(),
        };
        renderTopbarTerminalSegment();
      }
    })
    .finally(() => {
      readerHealthInFlight = false;
    });
}

/** PAY5-F1 (docs/backlog.md, docs/build-briefs/PAY5-F1-terminal-notice-
 * in-topbar.md, DEC-196): the top bar's own terminal segment, visible on
 * EVERY floor screen with a topbar, not only while a ticket happens to
 * be open (the PAY7 ticket-header chip this replaces was gated on
 * state.orderId !== undefined; this widening is DEC-196's own "on every
 * floor screen" language). Never permission-gated, unlike
 * renderTicketManagerExceptionBanner above -- every floor role benefits
 * equally from knowing to find a manager, and showing it leaks nothing
 * sensitive (device-local config, not order or client data).
 *
 * Scoped to the terminal_not_assigned case ONLY (a device whose
 * terminalConfigState has never had a terminal picked at all), never to
 * payment_terminal_not_found or terminal_provider_mismatch: those two
 * describe the REMEMBERED terminal going stale server-side (deactivated,
 * or provider-mismatched since PAY6), and terminalConfigState is never
 * re-validated against the server until a real charge attempt, so this
 * function has no way to know either of those states without a network
 * round trip a topbar render does not otherwise make. A warning that
 * could be wrong (stale-positive or stale-negative) is worse than none
 * at all on a touch-only floor surface, so this stays narrow rather than
 * guessing. */
function renderTopbarTerminalSegment(): void {
  const shouldShowWarning = terminalConfigState?.paymentTerminalId === undefined;
  const maySelect = state.session !== undefined && hasPermission(state.session, TERMINAL_PICKER_PERMISSION);
  topbarTerminalWarningButton.hidden = !shouldShowWarning || !maySelect;
  topbarReaderTrigger.hidden = shouldShowWarning || !maySelect;
  topbarTerminalStatusEl.hidden = maySelect;
  if (!maySelect && shouldShowWarning) { topbarTerminalStatusEl.textContent = "No reader"; topbarTerminalStatusEl.dataset["health"] = "unknown"; }
  topbarReaderTrigger.disabled = !maySelect;
  topbarTerminalWarningButton.disabled = !maySelect;
  if (!maySelect) readerDropdown.close();
  if (!shouldShowWarning) {
    // CL8: the reader's live health, when the server has told this device one for THIS reader; a reading for another
    // reader, or none yet, leaves the pill as it always was.
    const health =
      readerHealthReading?.terminalId === terminalConfigState?.paymentTerminalId
        ? readerHealthReading?.state
        : undefined;
    const text = readerPillText(
      topbarTerminalLabel(terminalConfigState?.paymentTerminalDisplayName ?? ""),
      health,
    );
    if (topbarTerminalStatusEl.textContent !== text) {
      topbarTerminalStatusEl.textContent = text;
    }
    topbarReaderTrigger.textContent = `${text} ▾`;
    topbarReaderTrigger.dataset["short"] = `${readerPillShortText(health, "Reader")} ▾`;
    topbarReaderTrigger.dataset["health"] = health === "online" ? "online" : health === "offline" ? "unhealthy" : "unknown";
    topbarTerminalStatusEl.dataset["short"] = readerPillShortText(health, "Reader");
    if (readerPillShortText(health, "") === "") {
      topbarTerminalStatusEl.dataset["health"] = health === "online" ? "online" : "unknown";
    } else {
      topbarTerminalStatusEl.dataset["health"] = "unhealthy";
    }
    refreshReaderHealthIfDue();
  }
}

function openManagerExceptionsSheet(): void {
  const session = state.session;
  if (session === undefined || !hasPermission(session, "admin.manager_exception.view")) {
    return;
  }
  managerExceptionsScrim.hidden = false;
  renderManagerExceptionsList();
  void refreshManagerExceptions();
}

function closeManagerExceptionsSheet(): void {
  managerExceptionsScrim.hidden = true;
  state.managerExceptionResolvingId = undefined;
  state.managerExceptionClearHoldChoice = undefined;
  state.managerExceptionPaymentChoice = undefined;
}

// ---------------------------------------------------------------------
// RCPT1-S3 (docs/build-briefs/RCPT1-S3-receipts-and-awaiting-tip.md; PRD P0.9; DEC-401). Printed receipts and the tip written on them.
//
// At a location in receipt tip mode, Pay for a whole-ticket card sale AUTHORIZES the card (the S1 route), then queues both receipt copies to the
// location's printer. The ticket stays open as Awaiting tip: the rail shows Reprint and Enter tip instead of Pay, Open Tabs shows an Awaiting
// tip chip, and Enter tip opens the Awaiting tip sheet, where the tip the guest wrote is entered and captured through the ordinary
// payments route (receiptTipCents). Printing never blocks payment: with no online printer the authorization stays valid and the floor
// offers Print again and Send to other printer. Every figure comes from the server; the pure parts are ui/src/receiptTipModel.ts.
// ---------------------------------------------------------------------

let awaitingTipStatusNote: { text: string; tone: PayStatusTone } | undefined;

function setAwaitingTipStatus(text: string | undefined, tone: PayStatusTone = "success"): void {
  awaitingTipStatusNote = text === undefined ? undefined : { text, tone };
  renderAwaitingTipStatus();
}

function renderAwaitingTipStatus(): void {
  const note = state.awaitingTip === undefined ? undefined : awaitingTipStatusNote;
  awaitingTipStatusEl.hidden = note === undefined;
  if (note !== undefined) {
    awaitingTipStatusEl.className = `pay-status ${note.tone}`;
    awaitingTipStatusEl.textContent = note.text;
  }
}

/** RCPT1-S3-F2: the server refuses any change to an Awaiting tip ticket (409 ticket_awaiting_tip); the floor says so before asking. */
const AWAITING_TIP_FROZEN_MESSAGE =
  "This ticket is waiting for a tip. Enter the tip before changing it.";

function refuseWhileAwaitingTip(): boolean {
  if (state.awaitingTip === undefined) {
    return false;
  }
  showToast(AWAITING_TIP_FROZEN_MESSAGE, "danger");
  return true;
}

/** Reads whether this ticket has an authorization waiting for a tip. A failed read leaves what is shown: the server refuses a wrong
 * capture on its own, so the screen is never the only guard. */
async function loadAwaitingTip(
  orderId: string,
  isCurrent: () => boolean = () => true,
): Promise<void> {
  const session = sessionOrUndefined();
  if (session === undefined || state.orderId !== orderId || !isCurrent()) return;
  const gen = sessionGeneration;
  const result = await getReceiptAuthorization(session.apiBaseUrl, session.tenantId, orderId);
  if (
    !sessionIsCurrent(gen) ||
    state.session?.token !== session.token ||
    state.orderId !== orderId ||
    !isCurrent() ||
    result.status !== 200
  ) {
    return;
  }
  const body = result.body;
  const wasAwaiting = state.awaitingTip !== undefined;
  if (body.awaiting) {
    state.awaitingTip = body;
  } else {
    state.awaitingTip = undefined;
    awaitingTipStatusNote = undefined;
  }
  refreshTileDisabledState();
  if (wasAwaiting !== (state.awaitingTip !== undefined)) {
    renderTicketActions();
    renderTotals();
  }
}

/** Draws the rail for an Awaiting tip ticket in place of Prepare All and Pay. Returns true when it did. */
function renderAwaitingTipRail(): boolean {
  const on = state.awaitingTip !== undefined;
  ticketAwaitingBadge.hidden = !on;
  awaitingTipActionsEl.hidden = !on;
  actionRowPrimaryEl.hidden = on;
  renderAwaitingTipStatus();
  if (!on) {
    return false;
  }
  noTipButton.hidden = true;
  payBlockReason.hidden = true;
  payFailEl.hidden = true;
  payButton.disabled = true;
  return true;
}

function renderAwaitingTipTotals(): void {
  const awaiting = state.awaitingTip;
  authorizedRowEl.hidden = awaiting === undefined;
  awaitingTipRowEl.hidden = awaiting === undefined;
  if (awaiting === undefined) {
    return;
  }
  authorizedAmountEl.textContent = formatCents(awaiting.authorizedCents);
  authorizedCardEl.textContent = cardLabel(awaiting.cardBrand, awaiting.cardLast4);
}

/** True while the open Pay sheet is a sale whose tip is written on the receipt. */
/** TERM10 (DEC-410). True when the guest picks the tip on the card reader for this payment: the server says the location's reader
 * will ask (the preview), and this is a card payment on a fresh card that is neither a receipt sale nor a tab's hold. The pay
 * sheet then shows no tip choice and sends "no tip" so the reader is the one tip entry. */
function readerTipSaleActive(): boolean {
  return isReaderTipSale({
    readerTipOnReader: state.payConfirmPreview?.readerTipOnReader,
    tender: state.payTender,
    savedCardChosen: state.selectedSavedCardId !== undefined,
    activeHold: state.activeHold?.status === "active",
    receiptSale: receiptSaleActive(),
  });
}

function receiptSaleActive(): boolean {
  return isReceiptTipSale({
    tipMode: state.payConfirmPreview?.tipMode,
    scopeIsWhole: state.payScope?.type === "whole",
    tender: state.payTender,
    savedCardChosen: state.selectedSavedCardId !== undefined,
    activeHold: state.activeHold?.status === "active",
    suppressedForThisTicket:
      state.orderId !== undefined && state.receiptSaleSuppressedOrderId === state.orderId,
  });
}

/** Confirm on a receipt sale: authorize the card, then print. Nothing is captured here. */
async function runReceiptSale(orderId: string): Promise<void> {
  const session = requireSession();
  const target = `receipt:${orderId}`;
  const idempotencyKey = getOrMintPayIdempotencyKey(target);
  beginPayWaiting(true, target);
  const result = await authorizeReceiptSale(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey,
    terminalId: terminalConfigState?.paymentTerminalId,
  });
  endPayWaiting();
  payStatusEl.hidden = false;
  if (result.status !== 200 && result.status !== 201) {
    if (handlePinChangeRequiredOnPayClose(result.body.error)) {
      return;
    }
    payStatusEl.className = "pay-status error";
    payTipSplitEl.hidden = true;
    if (result.networkError) {
      // The answer was lost, so a card may or may not have been authorized. The key stays: asking again replays rather than doubles.
      payStatusEl.textContent = "Payment may not have gone through. Check before retrying.";
      payButton.disabled = false;
      return;
    }
    if (shouldClearPayIdempotencyKeyOnFailure(result)) {
      clearPayIdempotencyKey(target);
    }
    const translated = translatePayCloseError("pay", result.body, result.status);
    state.lastPayCloseErrorDetail = translated.detail;
    renderLastPayCloseErrorDebug();
    payStatusEl.textContent = translated.plain;
    maybeShowBusinessDaySheet(result.body.error);
    if (shouldShowPayCloseRefusalToast(result.body)) {
      showToast(translated.plain, "danger");
    }
    renderTicketActions();
    return;
  }
  clearPayIdempotencyKey(target);
  payStatusEl.hidden = true;
  if (result.body.outcome === "reader") {
    // The card cannot take a tip after the sale (or this is not a whole-ticket sale): this sale takes its tip the ordinary way.
    state.receiptSaleSuppressedOrderId = orderId;
    showToast("This card cannot add a tip later. Choose a tip.", "danger");
    renderTicketActions();
    await openPayConfirmSheet({ type: "whole" });
    return;
  }
  await refreshPaymentPreview();
  if (state.awaitingTip === undefined) {
    await loadAwaitingTip(orderId);
    renderTicketActions();
    renderTotals();
  }
  await sendReceiptToPrinter();
}

/** Queues both copies. With no online printer nothing is queued and the floor is offered Print again and Send to other printer. */
async function sendReceiptToPrinter(printerId?: string): Promise<void> {
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  const session = requireSession();
  setAwaitingTipStatus("Sending to printer", "warning");
  const result = await printReceipt(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
    ...(printerId === undefined ? {} : { printerId }),
  });
  if (state.orderId !== orderId) {
    return;
  }
  if (result.status !== 200) {
    setAwaitingTipStatus(
      capitalizeServerMessage(result.body.message ?? "Could not reach the printer."),
      "error",
    );
    return;
  }
  const answer = result.body;
  const view = receiptPrintView(answer);
  if (view.kind === "sent") {
    setAwaitingTipStatus(view.text);
    return;
  }
  setAwaitingTipStatus(view.text, "warning");
  openPrinterOfflineSheet(answer.outcome === "printer_offline" ? answer.printers : []);
}

function openPrinterOfflineSheet(printers: readonly LocationPrinterChoiceDto[]): void {
  const options: ChoiceOption[] = [
    { id: "again", label: "Print again", variant: "primary", testid: "printer-offline-again" },
  ];
  if (printers.length > 1) {
    options.push({ id: "other", label: "Send to other printer", testid: "printer-offline-other" });
  }
  openChoiceSheet({
    title: "Printer offline",
    subtitle: "The card stays authorized.",
    options,
    cancelLabel: "Not now",
    onPick: (id) => {
      if (id === "again") {
        void sendReceiptToPrinter();
        return;
      }
      openChoiceSheet({
        title: "Send to",
        options: printers.map((printer) => ({
          id: printer.id,
          label: printer.name,
          ...(printer.online ? {} : { sublabel: "Offline" }),
          testid: "printer-choice",
        })),
        onPick: (printerId) => void sendReceiptToPrinter(printerId),
      });
    },
  });
}

/** Reprint: which copy. The merchant copy needs a manager (a PIN for roles that are asked), so it is shown only to a session that may. */
function openReprintChoice(): void {
  const session = requireSession();
  const options: ChoiceOption[] = [
    { id: "customer", label: "Customer copy", testid: "reprint-customer" },
  ];
  if (hasPermission(session, "receipt.reprint")) {
    options.push({
      id: "merchant",
      label: "Merchant copy",
      ...(session.permissions["receipt.reprint"] === "approval_required"
        ? { sublabel: "A manager approves" }
        : {}),
      testid: "reprint-merchant",
    });
  }
  openChoiceSheet({
    title: "Reprint",
    options,
    onPick: (id) => void reprintCopy(id === "merchant" ? "merchant" : "customer"),
  });
}

async function reprintCopy(copy: "customer" | "merchant"): Promise<void> {
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  const session = requireSession();
  const idempotencyKey = crypto.randomUUID();
  setAwaitingTipStatus("Sending to printer", "warning");
  const gated = await requestApproval(
    "Reprint the merchant copy",
    (approval) =>
      reprintReceipt(session.apiBaseUrl, session.tenantId, orderId, copy, {
        idempotencyKey,
        ...approval,
      }),
    () => showRingScreen(),
    { pinContext: "Reprint the merchant copy" },
  );
  if (gated.canceled) {
    setAwaitingTipStatus(undefined);
    return;
  }
  showRingScreen();
  const result = gated.result;
  if (state.orderId !== orderId) {
    return;
  }
  if (result.status !== 200) {
    setAwaitingTipStatus(
      capitalizeServerMessage(result.body.message ?? "Could not reprint."),
      "error",
    );
    return;
  }
  const answer = result.body;
  const view = receiptPrintView(answer);
  if (view.kind === "sent") {
    setAwaitingTipStatus(`${copy === "merchant" ? "Merchant" : "Customer"} copy sent`);
    return;
  }
  setAwaitingTipStatus(view.text, "warning");
  openPrinterOfflineSheet(answer.outcome === "printer_offline" ? answer.printers : []);
}

function openAwaitingTipSheet(initialText?: string): void {
  const awaiting = state.awaitingTip;
  const orderId = state.orderId;
  if (awaiting === undefined || orderId === undefined) {
    return;
  }
  awaitingTipSheet.open({
    authorizedCents: awaiting.authorizedCents,
    subtotalCents: awaiting.subtotalCents,
    taxCents: awaiting.taxCents,
    tipCapCents: awaiting.tipCapCents,
    cardLabel: cardLabel(awaiting.cardBrand, awaiting.cardLast4),
    ...(initialText === undefined ? {} : { initialText }),
    onCharge: (tipCents) => chargeAwaitingTip(orderId, tipCents),
    onChargeCap: (tipCapCents, enteredText) =>
      void chargeAwaitingTipAtCap(orderId, tipCapCents, enteredText),
    onCancel: () => awaitingTipSheet.close(),
  });
}

/**
 * Captures the authorization plus the tip written on the receipt, through the ordinary payments route. Resolves with a sentence for the
 * sheet to show when it did not go through, or undefined when it did (the sheet closes). One key per distinct tip: asking again for the
 * same tip after a lost answer replays, a different tip is a different intent.
 */
async function chargeAwaitingTip(orderId: string, tipCents: number): Promise<string | undefined> {
  const session = requireSession();
  const target = `receipttip:${orderId}:${tipCents}`;
  const idempotencyKey = getOrMintPayIdempotencyKey(target);
  const result = await payOrder(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey,
    currency: "usd",
    receiptTipCents: tipCents,
    terminalId: terminalConfigState?.paymentTerminalId,
  });
  if (state.orderId !== orderId) {
    return undefined;
  }
  if (result.status === 200 || result.status === 201) {
    const attempt = result.body.paymentAttempt;
    if (attempt.status !== "Captured") {
      // Failed or Pending: the sentence the payment itself gives, and the authorization is still there.
      const view = payResultView({
        amount: attempt.amount,
        status: attempt.status,
        tipCents: attempt.tip_cents,
        failureKind: result.body.failureKind,
      });
      return view.text;
    }
    clearPayIdempotencyKey(target);
    state.awaitingTip = undefined;
    awaitingTipStatusNote = undefined;
    state.selectedTip = undefined;
    refreshTileDisabledState();
    await refreshPaymentPreview();
    if (settledAndClosed(result.body.settlementClose)) {
      finishClosedTicket();
    } else if (
      state.paymentPreview !== undefined &&
      state.paymentPreview.uncoveredLines.length === 0
    ) {
      showZeroDuePrompt({ selfClosed: false });
    }
    return undefined;
  }
  if (handlePinChangeRequiredOnPayClose(result.body.error)) {
    return undefined;
  }
  if (result.networkError) {
    return "Payment may not have gone through. Check before charging again.";
  }
  if (shouldClearPayIdempotencyKeyOnFailure(result)) {
    clearPayIdempotencyKey(target);
  }
  const translated = translatePayCloseError("pay", result.body, result.status);
  state.lastPayCloseErrorDetail = translated.detail;
  renderLastPayCloseErrorDebug();
  return translated.plain;
}

/** A manager agrees to charge the limit as the tip. The sheet is closed first so the approval screens are not covered; if the manager
 * does not approve, the sheet comes back with what was typed.
 *
 * RCPT1-S3-F1 (DEC-489). When the figure typed is readable, the manager's approval records it as an override (the figure the guest wrote and
 * a fixed reason), so the server knows the rest. After the limit is charged, the Remaining tip sheet opens: the bartender asks the guest to
 * tap or insert a card again for the rest, or records that the guest has left. */
async function chargeAwaitingTipAtCap(
  orderId: string,
  tipCapCents: number,
  enteredText: string,
): Promise<void> {
  awaitingTipSheet.close();
  const session = requireSession();
  const entry = readTipEntry(enteredText);
  const writtenTipCents = entry.kind === "ok" && entry.tipCents > tipCapCents ? entry.tipCents : undefined;
  // One key per ticket and figure: if the limit's charge fails and the manager approves again, the override replays instead of being refused
  // as a second one.
  const overrideKeyTarget = `receiptoverride:${orderId}:${writtenTipCents ?? 0}`;
  const idempotencyKey =
    writtenTipCents === undefined ? crypto.randomUUID() : getOrMintPayIdempotencyKey(overrideKeyTarget);
  // The rest of the tip, as the server recorded it on the override. Stays 0 when only the limit was approved.
  let remainderCents = 0;
  const gated = await requestApproval<Partial<ApiErrorBody>>(
    `Charge the ${formatCents(tipCapCents)} tip limit`,
    async (approval) => {
      if (writtenTipCents === undefined) {
        return approveReceiptTipCap(session.apiBaseUrl, session.tenantId, orderId, {
          idempotencyKey,
          ...approval,
        });
      }
      const recorded = await recordReceiptTipOverride(session.apiBaseUrl, session.tenantId, orderId, {
        idempotencyKey,
        writtenTipCents,
        reason: OVER_CAP_OVERRIDE_REASON,
        ...approval,
      });
      if (recorded.status === 200 || recorded.status === 201) {
        remainderCents = recorded.body.uncollectedTipCents;
      }
      return recorded;
    },
    () => showRingScreen(),
    { pinContext: `Charge the ${formatCents(tipCapCents)} tip limit` },
  );
  showRingScreen();
  if (state.orderId !== orderId) {
    return;
  }
  if (gated.canceled || (gated.result.status !== 200 && gated.result.status !== 201)) {
    if (!gated.canceled) {
      showToast(
        capitalizeServerMessage(gated.result.body.message ?? "Could not get approval."),
        "danger",
      );
    }
    openAwaitingTipSheet(enteredText);
    return;
  }
  const problem = await chargeAwaitingTip(orderId, tipCapCents);
  if (problem !== undefined && state.orderId === orderId) {
    openAwaitingTipSheet(enteredText);
    showToast(problem, "danger");
    return;
  }
  // The limit is charged. The rest, as the server recorded it, is asked of the guest now.
  clearPayIdempotencyKey(overrideKeyTarget);
  if (problem === undefined && remainderCents > 0) {
    openRemainingTipSheet(orderId, remainderCents);
  }
}

/** RCPT1-S3-F1. One idempotency key per try at collecting the rest: a lost answer asked again replays, a declined try mints a new key. */
function remainderKeyTarget(orderId: string): string {
  return `receipttipremainder:${orderId}`;
}

function remainderOutcomeFromRefusal(
  body: { error?: string | undefined; message?: string | undefined },
  status: number,
): RemainingTipOutcome {
  const refusedPhase = phaseFromRefusal(body.error);
  if (refusedPhase !== null) {
    return { phase: refusedPhase };
  }
  const translated = translatePayCloseError("pay", body, status);
  return { phase: "failed", failureText: translated.plain };
}

function openRemainingTipSheet(orderId: string, remainderCents: number): void {
  const session = requireSession();
  const target = remainderKeyTarget(orderId);
  remainingTipSheet.open({
    remainderCents,
    onCollect: async () => {
      const idempotencyKey = getOrMintPayIdempotencyKey(target);
      const result = await collectReceiptTipRemainder(session.apiBaseUrl, session.tenantId, orderId, {
        idempotencyKey,
        ...(terminalConfigState?.paymentTerminalId === undefined
          ? {}
          : { terminalId: terminalConfigState.paymentTerminalId }),
      });
      if (result.status === 200 || result.status === 201) {
        const phase = phaseFromAttempt(result.body.paymentAttempt.status);
        if (phase !== "pending") {
          // Captured or definitively failed: the next try is a new charge with a new key.
          clearPayIdempotencyKey(target);
        }
        return {
          phase,
          ...(result.body.failureKind === undefined ? {} : { failureKind: result.body.failureKind }),
        };
      }
      if (result.networkError) {
        // The answer was lost: the charge may have gone through, so the key stays and nothing more is sent from here.
        return { phase: "pending" };
      }
      if (shouldClearPayIdempotencyKeyOnFailure(result)) {
        clearPayIdempotencyKey(target);
      }
      return remainderOutcomeFromRefusal(result.body, result.status);
    },
    onCancelWait: async () => {
      const terminalId = terminalConfigState?.paymentTerminalId;
      if (terminalId !== undefined) {
        await cancelReaderAction(session.apiBaseUrl, session.tenantId, terminalId, {
          idempotencyKey: crypto.randomUUID(),
        });
      }
      return leaveRemainder(orderId, "cancelled");
    },
    onGuestLeft: () => leaveRemainder(orderId, "guest_left"),
  });
}

async function leaveRemainder(
  orderId: string,
  reason: "guest_left" | "cancelled",
): Promise<RemainingTipOutcome> {
  const session = requireSession();
  const result = await leaveReceiptTipRemainder(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
    reason,
  });
  if (result.status === 200 || result.status === 201) {
    clearPayIdempotencyKey(remainderKeyTarget(orderId));
    return { phase: "left", leaveReason: reason };
  }
  if (result.networkError) {
    return { phase: "failed", failureText: "Could not reach the server. Check before trying again." };
  }
  return remainderOutcomeFromRefusal(result.body, result.status);
}

function handleAwaitingTipEnterClick(): void {
  if (state.awaitingTip === undefined) {
    return;
  }
  // The figures on the sheet are the server's, read just now.
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  void loadAwaitingTip(orderId).then(() => openAwaitingTipSheet());
}

// AU16: every scrim-backed floor sheet this file declares, other than
// managerExceptionsScrim (which AU8 already closes defensively from
// handleLogOut, unchanged here), closed unconditionally on sign-out.
// hideAllScreens's own comment claims every one of these "is always closed
// by its own flow before navigation," which is true for that sheet's own
// Confirm/Cancel/scrim-tap/Escape paths but not for signing out: Log out
// (and an idle timeout, which calls handleLogOut the same way) is reachable
// from the persistent topbar account menu on every floor screen with a
// topbar, including the ring screen where every sheet below opens, at any
// moment one of them happens to be open. Each close function here only
// clears its own scrim's `hidden` flag and its own sheet-local state (read
// directly from every one of their bodies), so calling all of them
// unconditionally is safe whether or not their sheet is actually open,
// matching closeManagerExceptionsSheet's own existing unconditional call
// site rather than guarding each on its own scrim's hidden flag first.
function closeAllFloorSheets(): void {
  closeMenuBaseList();
  closeLiquorSheet();
  closeLineEditSheet();
  // M5-R10: the price history sheet is built at runtime, so it is closed here
  // by hand (the sign-out coverage check reads only markup scrims).
  closePriceHistorySheet();
  closePayConfirmSheet();
  // RCPT1-S3: built at runtime (a scrim in the markup would break the chrome ratchet), so it is closed here by hand.
  awaitingTipSheet.close();
  // RCPT1-S3-F1: built at runtime like the sheet above. Deliberately NOT closed when a ticket is left (it opens after the ticket closes).
  remainingTipSheet.close();
  closeCardConsentSheet();
  // TERMREADER1: built at runtime, so it is closed here by hand.
  closeCardReaderAsk();
  closeSignatureReceiptSheet();
  closeSavedCardsSheet();
  closeChoiceSheet();
  closeSplitSetupSheet();
  closeAddGuestSheet();
  closeRefundSheet();
  closePaymentLinkSheet();
  closeCloseoutFinalizeSheet();
  closeReceiveVendorSheet();
  // PO7-S5: sign-out stops the invoice scan's camera and drops the visit.
  receiveScanController.stop();
  orderingController.stop();
  // STK1: the 86 list is built from a backdrop that is not a scrim (the
  // chrome ratchet refuses a new one), so it is torn down here by hand, and
  // the next employee never inherits this one's list.
  closeStock86Panel();
  hideStock86Toast();
  resetStock86State();
  // RPT1-F4: My Tips, closed and emptied (a read still in flight is dropped),
  // so the next employee never inherits this one's figures.
  closeMyTips();
}

/** Best-effort tab-name resolution for the sheet's own rows, lazy and
 * cached (state.managerExceptionOrderLabels), never a second authorization
 * boundary: a lookup failure (a permission gap, a deleted order) degrades
 * to the plain "This tab" fallback, it never hides or blocks the row
 * itself. GET .../orders/:orderId is gated on pos.tab.open, which every
 * role holding admin.manager_exception.view also holds today (admin,
 * manager; src/domain/permissions.ts), verified by reading the templates
 * rather than assumed. */
async function loadManagerExceptionOrderLabels(orderIds: readonly string[]): Promise<void> {
  const session = state.session;
  if (session === undefined) {
    return;
  }
  await Promise.all(
    orderIds.map(async (orderId) => {
      const result = await getOrder(session.apiBaseUrl, session.tenantId, orderId);
      const label = result.status === 200 ? (result.body.order.name ?? "Walk-in") : "This tab";
      state.managerExceptionOrderLabels.set(orderId, label);
    }),
  );
  if (!managerExceptionsScrim.hidden) {
    renderManagerExceptionsList();
  }
}

function renderManagerExceptionsList(): void {
  const rows = state.managerExceptions;
  managerExceptionsListEl.innerHTML = "";
  managerExceptionsEmpty.hidden = rows.length > 0;
  if (rows.length === 0) {
    return;
  }
  const canResolve =
    state.session !== undefined && hasPermission(state.session, "admin.manager_exception.resolve");
  const missingOrderIds = new Set<string>();
  for (const row of rows) {
    if (!isLocationDayRow(row) && !state.managerExceptionOrderLabels.has(row.order_id)) {
      missingOrderIds.add(row.order_id);
    }
    managerExceptionsListEl.append(buildManagerExceptionRow(row, canResolve));
  }
  if (missingOrderIds.size > 0) {
    void loadManagerExceptionOrderLabels([...missingOrderIds]);
  }
}

function buildManagerExceptionRow(row: ManagerExceptionDto, canResolve: boolean): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "manager-exception-row";
  wrapper.dataset["testid"] = "manager-exception-row";
  wrapper.dataset["exceptionId"] = row.id;

  const header = document.createElement("div");
  header.className = "manager-exception-header";
  const tabName = document.createElement("span");
  tabName.className = "manager-exception-tab-name";
  tabName.dataset["testid"] = "manager-exception-tab-name";
  tabName.textContent = isLocationDayRow(row)
    ? drawerOpenAtCloseTitle(
        row,
        row.location_id === terminalConfigState?.locationId ? floorLocationName() : undefined,
      )
    : (state.managerExceptionOrderLabels.get(row.order_id) ?? "Loading tab…");
  const amount = document.createElement("span");
  amount.className = "manager-exception-amount";
  amount.dataset["testid"] = "manager-exception-amount";
  // PAY14 (D3): a hold_provider_mismatch row shows the held amount,
  // "{held amount} held"; every other kind shows outstanding_cents.
  amount.textContent = managerExceptionRowAmountText(row);
  if (amount.textContent === "") {
    header.append(tabName);
  } else {
    header.append(tabName, amount);
  }

  const kind = document.createElement("div");
  kind.className = "manager-exception-kind";
  kind.dataset["testid"] = "manager-exception-kind";
  kind.textContent = managerExceptionKindLabel(row.kind);

  const help = document.createElement("div");
  help.className = "manager-exception-help";
  help.textContent = managerExceptionKindHelp(row.kind);

  const age = document.createElement("div");
  age.className = "manager-exception-age";
  age.dataset["testid"] = "manager-exception-age";
  age.textContent = formatManagerExceptionAge(row.created_at);

  wrapper.append(header, kind, help, age);

  if (
    canResolve &&
    (row.kind === "hold_provider_mismatch" || row.kind === "hold_release_failed_after_close")
  ) {
    // PAY14 Slice B (Section 8.1, D4), widened by PAY21 (owner ruling,
    // Kerry, 2026-09-29): "Clear hold" replaces "Resolve" for both kinds
    // and opens the same kind-specific panel. The server resolves both
    // kinds only with a holdDisposition, which only this panel sends. The
    // list always carries the hold facts for both kinds (every row of
    // either is written from a hold); the button stays disabled in the one
    // case it could not build the panel's text.
    const clearButton = document.createElement("button");
    clearButton.type = "button";
    clearButton.className = "btn-secondary btn-full";
    clearButton.dataset["testid"] = "manager-exception-clear-hold-button";
    clearButton.textContent = CLEAR_HOLD_BUTTON_TEXT;
    clearButton.disabled = row.hold === undefined || state.managerExceptionBusyId === row.id;
    clearButton.addEventListener("click", () => {
      const opening = state.managerExceptionResolvingId !== row.id;
      state.managerExceptionResolvingId = opening ? row.id : undefined;
      state.managerExceptionClearHoldChoice = undefined;
      renderManagerExceptionsList();
    });
    wrapper.append(clearButton);
    if (state.managerExceptionResolvingId === row.id && row.hold !== undefined) {
      wrapper.append(buildClearHoldPanel(row, row.hold, row.kind));
    }
  } else if (canResolve && isPaymentDisagreementKind(row.kind)) {
    // PAY34-F1, PAY34-F2: the same Resolve button, and a panel with the Clear hold panel's own markup.
    const resolveButton = document.createElement("button");
    resolveButton.type = "button";
    resolveButton.className = "btn-secondary btn-full";
    resolveButton.dataset["testid"] = "manager-exception-resolve-button";
    resolveButton.textContent = "Resolve";
    resolveButton.disabled = state.managerExceptionBusyId === row.id;
    resolveButton.addEventListener("click", () => {
      const opening = state.managerExceptionResolvingId !== row.id;
      state.managerExceptionResolvingId = opening ? row.id : undefined;
      state.managerExceptionPaymentChoice = undefined;
      renderManagerExceptionsList();
    });
    wrapper.append(resolveButton);
    if (state.managerExceptionResolvingId === row.id) {
      wrapper.append(buildPaymentResolutionPanel(row));
    }
  } else if (canResolve) {
    const resolveButton = document.createElement("button");
    resolveButton.type = "button";
    resolveButton.className = "btn-secondary btn-full";
    resolveButton.dataset["testid"] = "manager-exception-resolve-button";
    resolveButton.textContent = "Resolve";
    resolveButton.disabled = state.managerExceptionBusyId === row.id;
    resolveButton.addEventListener("click", () => {
      state.managerExceptionResolvingId =
        state.managerExceptionResolvingId === row.id ? undefined : row.id;
      renderManagerExceptionsList();
    });
    wrapper.append(resolveButton);

    if (state.managerExceptionResolvingId === row.id) {
      wrapper.append(buildManagerExceptionResolvePanel(row));
    }
  }

  return wrapper;
}

/** The resolve flow's own confirm step (tap Resolve above, then pick/
 * confirm here), preventing an accidental one-tap resolution of what may
 * be real, uncollected money. The note is optional, except on a Walkout
 * row (PAY14 Slice B, Section 8.2, D9), where it is required and Confirm
 * resolve stays disabled until it holds a non-space character, matching
 * the server's own rule. */
function buildManagerExceptionResolvePanel(row: ManagerExceptionDto): HTMLElement {
  const panel = document.createElement("div");
  panel.className = "manager-exception-resolve-panel";
  panel.dataset["testid"] = "manager-exception-resolve-panel";

  const noteText = resolveNoteFieldText(row.kind);
  const note = document.createElement("input");
  note.type = "text";
  note.inputMode = "text";
  note.className = "manager-exception-note-input";
  note.dataset["testid"] = "manager-exception-note-input";
  note.placeholder = noteText.placeholder;
  note.setAttribute("aria-label", noteText.ariaLabel);

  const actions = document.createElement("div");
  actions.className = "manager-exception-resolve-actions";

  const cancelButton = document.createElement("button");
  cancelButton.type = "button";
  cancelButton.className = "btn-secondary";
  cancelButton.dataset["testid"] = "manager-exception-resolve-cancel";
  cancelButton.textContent = "Cancel";
  cancelButton.addEventListener("click", () => {
    state.managerExceptionResolvingId = undefined;
    renderManagerExceptionsList();
  });

  const confirmButton = document.createElement("button");
  confirmButton.type = "button";
  confirmButton.className = "btn-primary";
  confirmButton.dataset["testid"] = "manager-exception-resolve-confirm";
  confirmButton.textContent = "Confirm resolve";
  const updateConfirm = (): void => {
    confirmButton.disabled =
      state.managerExceptionBusyId === row.id || !resolveNoteSatisfied(row.kind, note.value);
  };
  updateConfirm();
  note.addEventListener("input", updateConfirm);
  confirmButton.addEventListener(
    "click",
    () => void performResolveManagerException(row.id, note.value),
  );

  actions.append(cancelButton, confirmButton);
  panel.append(note, actions);
  return panel;
}

/** PAY34-F1, PAY34-F2: the payment resolution panel. The Clear hold panel's markup and classes: a radio group
 * (nothing picked by default), the note, Cancel and Confirm. Confirm stays disabled until an action is picked and,
 * for an action the server requires a note for, a note is typed. */
function buildPaymentResolutionPanel(row: ManagerExceptionDto): HTMLElement {
  const panel = document.createElement("div");
  panel.className = "manager-exception-resolve-panel";
  panel.dataset["testid"] = "manager-exception-payment-panel";

  const questionId = `payment-resolution-question-${row.id}`;
  const question = document.createElement("div");
  question.className = "manager-exception-kind";
  question.id = questionId;
  question.textContent = PAYMENT_RESOLUTION_QUESTION;

  const group = document.createElement("div");
  group.className = "manager-exception-clear-hold-group";
  group.setAttribute("role", "radiogroup");
  group.setAttribute("aria-labelledby", questionId);
  group.dataset["testid"] = "payment-resolution-picker";

  const note = document.createElement("input");
  note.type = "text";
  note.inputMode = "text";
  note.className = "manager-exception-note-input";
  note.dataset["testid"] = "manager-exception-note-input";

  const recordLine = document.createElement("div");
  recordLine.className = "manager-exception-help";
  recordLine.dataset["testid"] = "payment-resolution-record-line";
  recordLine.textContent = PAYMENT_RESOLUTION_RECORD_LINE;

  const confirmButton = document.createElement("button");
  const noteIsRequired = (): boolean =>
    paymentResolutionOptions(row.kind).find(
      (option) => option.action === state.managerExceptionPaymentChoice,
    )?.noteRequired === true;
  const updateConfirm = (): void => {
    const required = noteIsRequired();
    recordLine.hidden = state.managerExceptionPaymentChoice !== "record_captured";
    note.placeholder = required ? PAYMENT_RESOLUTION_NOTE_REQUIRED_PLACEHOLDER : "Note (optional)";
    note.setAttribute(
      "aria-label",
      required ? PAYMENT_RESOLUTION_NOTE_REQUIRED_ARIA_LABEL : RESOLUTION_NOTE_OPTIONAL_ARIA_LABEL,
    );
    confirmButton.disabled =
      state.managerExceptionBusyId === row.id ||
      !paymentResolutionSatisfied(row.kind, state.managerExceptionPaymentChoice, note.value);
  };

  for (const option of paymentResolutionOptions(row.kind)) {
    const label = document.createElement("label");
    label.className = "ack-row";
    label.dataset["testid"] = "payment-resolution-option";
    label.dataset["action"] = option.action;
    const radio = document.createElement("input");
    radio.type = "radio";
    radio.className = "ack-checkbox";
    radio.name = `payment-resolution-${row.id}`;
    radio.value = option.action;
    radio.checked = state.managerExceptionPaymentChoice === option.action;
    radio.addEventListener("change", () => {
      if (radio.checked) {
        state.managerExceptionPaymentChoice = option.action;
        updateConfirm();
      }
    });
    const labelText = document.createElement("span");
    labelText.className = "ack-label";
    labelText.textContent = option.label;
    label.append(radio, labelText);
    group.append(label);
  }

  const actions = document.createElement("div");
  actions.className = "manager-exception-resolve-actions";

  const cancelButton = document.createElement("button");
  cancelButton.type = "button";
  cancelButton.className = "btn-secondary";
  cancelButton.dataset["testid"] = "manager-exception-resolve-cancel";
  cancelButton.textContent = CLEAR_HOLD_CANCEL_TEXT;
  cancelButton.addEventListener("click", () => {
    state.managerExceptionResolvingId = undefined;
    state.managerExceptionPaymentChoice = undefined;
    renderManagerExceptionsList();
  });

  confirmButton.type = "button";
  confirmButton.className = "btn-primary";
  confirmButton.dataset["testid"] = "manager-exception-payment-confirm";
  confirmButton.textContent = PAYMENT_RESOLUTION_CONFIRM_TEXT;
  updateConfirm();
  note.addEventListener("input", updateConfirm);
  confirmButton.addEventListener("click", () => {
    const choice = state.managerExceptionPaymentChoice;
    if (choice !== undefined) {
      void performResolvePayment(row, choice, note.value);
    }
  });

  actions.append(cancelButton, confirmButton);
  panel.append(question, group, recordLine, note, actions);
  return panel;
}

async function performResolvePayment(
  row: ManagerExceptionDto,
  action: PaymentResolutionActionDto,
  noteValue: string,
): Promise<void> {
  const session = requireSession();
  state.managerExceptionBusyId = row.id;
  renderManagerExceptionsList();
  const trimmedNote = noteValue.trim();
  const result = await resolveManagerExceptionRequest(
    session.apiBaseUrl,
    session.tenantId,
    row.id,
    {
      paymentResolution: action,
      ...(trimmedNote.length > 0 ? { resolutionNote: trimmedNote } : {}),
    },
  );
  state.managerExceptionBusyId = undefined;
  if (result.status !== 200) {
    // Never the server's raw message. Refresh first so a row resolved elsewhere leaves the list (UX54).
    showToast(PAYMENT_RESOLUTION_FAILURE_TOAST, "danger");
    await refreshManagerExceptions();
    return;
  }
  state.managerExceptionResolvingId = undefined;
  state.managerExceptionPaymentChoice = undefined;
  showToast(PAYMENT_RESOLUTION_SUCCESS_TOAST, "success");
  await refreshManagerExceptions();
}

async function performResolveManagerException(
  exceptionId: string,
  noteValue: string,
): Promise<void> {
  const session = requireSession();
  state.managerExceptionBusyId = exceptionId;
  renderManagerExceptionsList();
  const trimmedNote = noteValue.trim();
  const result = await resolveManagerExceptionRequest(
    session.apiBaseUrl,
    session.tenantId,
    exceptionId,
    trimmedNote.length > 0 ? { resolutionNote: trimmedNote } : {},
  );
  state.managerExceptionBusyId = undefined;
  if (result.status !== 200) {
    showToast(
      `Could not resolve this exception (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    renderManagerExceptionsList();
    return;
  }
  state.managerExceptionResolvingId = undefined;
  showToast("Exception resolved.", "success");
  await refreshManagerExceptions();
}

/** PAY14 Slice B (Section 8.1 D4 and Section 8.2 D4-R, D4-W, D4-C), widened
 * by PAY21 for hold_release_failed_after_close: the Clear hold panel, top
 * to bottom: line 1 (kind-specific), the Walkout line when the order has a
 * Walkout row, line 2, line 3, the question and its picker (two rows, or
 * three with a Walkout row), the optional note, then Cancel and Confirm
 * clear hold. Nothing is picked by default; Confirm clear hold is disabled
 * until one row is picked and is enabled, in its ordinary look, from the
 * moment one is, except while the clear is in flight. The note never
 * affects it. */
function buildClearHoldPanel(
  row: ManagerExceptionDto,
  hold: NonNullable<ManagerExceptionDto["hold"]>,
  kind: ManagerExceptionDto["kind"],
): HTMLElement {
  const text = clearHoldPanelText(hold, Date.now(), kind);
  const panel = document.createElement("div");
  panel.className = "manager-exception-resolve-panel";
  panel.dataset["testid"] = "manager-exception-clear-hold-panel";

  const line = (value: string, testid: string): HTMLElement => {
    const element = document.createElement("div");
    element.className = "manager-exception-help";
    element.dataset["testid"] = testid;
    element.textContent = value;
    return element;
  };
  panel.append(line(text.line1, "clear-hold-line-1"));
  if (text.walkoutLine !== null) {
    panel.append(line(text.walkoutLine, "clear-hold-walkout-line"));
  }
  panel.append(line(text.line2, "clear-hold-line-2"), line(text.line3, "clear-hold-line-3"));

  const questionId = `clear-hold-question-${row.id}`;
  const question = document.createElement("div");
  question.className = "manager-exception-kind";
  question.id = questionId;
  question.dataset["testid"] = "clear-hold-question";
  question.textContent = text.question;

  const group = document.createElement("div");
  group.className = "manager-exception-clear-hold-group";
  group.setAttribute("role", "radiogroup");
  group.setAttribute("aria-labelledby", questionId);
  group.dataset["testid"] = "clear-hold-picker";

  const confirmButton = document.createElement("button");
  const updateConfirm = (): void => {
    confirmButton.disabled =
      state.managerExceptionBusyId === row.id ||
      state.managerExceptionClearHoldChoice === undefined;
  };

  for (const option of text.options) {
    const label = document.createElement("label");
    label.className = "ack-row";
    label.dataset["testid"] = "clear-hold-option";
    label.dataset["disposition"] = option.disposition;
    const radio = document.createElement("input");
    radio.type = "radio";
    radio.className = "ack-checkbox";
    radio.name = `clear-hold-${row.id}`;
    radio.value = option.disposition;
    radio.checked = state.managerExceptionClearHoldChoice === option.disposition;
    radio.addEventListener("change", () => {
      if (radio.checked) {
        state.managerExceptionClearHoldChoice = option.disposition;
        updateConfirm();
      }
    });
    const labelText = document.createElement("span");
    labelText.className = "ack-label";
    labelText.textContent = option.label;
    label.append(radio, labelText);
    group.append(label);
  }

  const note = document.createElement("input");
  note.type = "text";
  note.inputMode = "text";
  note.className = "manager-exception-note-input";
  note.dataset["testid"] = "manager-exception-note-input";
  note.placeholder = "Note (optional)";
  note.setAttribute("aria-label", RESOLUTION_NOTE_OPTIONAL_ARIA_LABEL);

  const actions = document.createElement("div");
  actions.className = "manager-exception-resolve-actions";

  const cancelButton = document.createElement("button");
  cancelButton.type = "button";
  cancelButton.className = "btn-secondary";
  cancelButton.dataset["testid"] = "manager-exception-resolve-cancel";
  cancelButton.textContent = CLEAR_HOLD_CANCEL_TEXT;
  cancelButton.addEventListener("click", () => {
    state.managerExceptionResolvingId = undefined;
    state.managerExceptionClearHoldChoice = undefined;
    renderManagerExceptionsList();
  });

  confirmButton.type = "button";
  confirmButton.className = "btn-primary";
  confirmButton.dataset["testid"] = "manager-exception-clear-hold-confirm";
  confirmButton.textContent = CLEAR_HOLD_CONFIRM_TEXT;
  updateConfirm();
  confirmButton.addEventListener("click", () => {
    const choice = state.managerExceptionClearHoldChoice;
    if (choice !== undefined) {
      void performClearHold(row, choice, note.value);
    }
  });

  actions.append(cancelButton, confirmButton);
  panel.append(question, group, note, actions);
  return panel;
}

async function performClearHold(
  row: ManagerExceptionDto,
  disposition: ClearHoldDisposition,
  noteValue: string,
): Promise<void> {
  const session = requireSession();
  state.managerExceptionBusyId = row.id;
  renderManagerExceptionsList();
  const trimmedNote = noteValue.trim();
  const result = await resolveManagerExceptionRequest(
    session.apiBaseUrl,
    session.tenantId,
    row.id,
    {
      holdDisposition: disposition,
      ...(trimmedNote.length > 0 ? { resolutionNote: trimmedNote } : {}),
    },
  );
  state.managerExceptionBusyId = undefined;
  if (result.status !== 200) {
    // D4: never the server's raw message.
    showToast(CLEAR_HOLD_FAILURE_TOAST, "danger");
    // UX54: renderManagerExceptionsList() alone re-renders from
    // state.managerExceptions, the list this device fetched BEFORE this
    // attempt. If another device already resolved this row, that stale
    // list still shows it as open, and every "Try again" repeats this
    // same failure with no way to see the row is gone until the sheet is
    // closed and reopened (which calls refreshManagerExceptions, never
    // this failure path). Refresh from the server first, so a row
    // resolved elsewhere disappears from the list immediately.
    // refreshManagerExceptions itself calls renderManagerExceptionsList()
    // whenever the sheet is open (managerExceptionsScrim not hidden),
    // which it always is here.
    await refreshManagerExceptions();
    return;
  }
  // Read before the refresh below replaces the list: whether this order's
  // Walkout row is still open decides the follow-up toast.
  const toast = clearHoldSuccessToast(disposition, row.order_id, state.managerExceptions);
  state.managerExceptionResolvingId = undefined;
  state.managerExceptionClearHoldChoice = undefined;
  showToast(toast, "success");
  await refreshManagerExceptions();
  // The tab now has no active hold: re-read it when it is the ticket on
  // screen, so the badge hides and Keep tab open is offered again.
  if (state.orderId === row.order_id) {
    await loadHoldStatus(row.order_id);
  }
}

function savedCardLabel(entry: { savedCard: SavedCardDto; guestLabel: string }): string {
  const brand = entry.savedCard.brand.charAt(0).toUpperCase() + entry.savedCard.brand.slice(1);
  return `${brand} •••• ${entry.savedCard.last4}`;
}

function clientForNewCard(scope: PayScope | undefined): GuestDto | undefined {
  if (state.selectedSavedCardId !== undefined || state.activeHold?.status === "active")
    return undefined;
  const guest =
    scope?.type === "guest"
      ? state.guests.find((entry) => entry.id === scope.guestId)
      : scope?.type === "whole" && state.guests.length === 1
        ? state.guests[0]
        : undefined;
  return guest?.client?.mytimeClientId == null ? undefined : guest;
}

let confirmedClientSave: { guest: GuestDto; evidenceReference: string } | undefined;
let prepayClientSaveGuest: GuestDto | undefined;

function closePrepayClientSave(): void {
  prepayClientSaveGuest = undefined;
  cardConsentScrim.hidden = true;
  cardConsentAckRow.hidden = false;
  cardConsentSaveButton.textContent = "Save card";
  cardConsentSaveButton.disabled = !cardConsentAckCheckbox.checked;
  cardConsentDeclineButton.textContent = "Don't save";
}

function updatePaySaveClientRow(): void {
  paySaveClientRow.hidden =
    state.payTender === "cash" ||
    receiptSaleActive() ||
    clientForNewCard(state.payScope) === undefined;
}

/** Shown/hidden and re-labeled every time the pay-confirm sheet's saved-
 * card selection could have changed: the sheet opening, a pick from
 * openPayWithCardChoice, or a removal in the manage-cards sheet. Hidden
 * entirely for scope "share" (an equal-split share's own sequential
 * simulated payment does not support a stored-credential charge this
 * slice; see src/domain/payments.ts's own PayOrderParams.savedCardId
 * comment on scope). */
function updatePayConfirmCardRow(): void {
  const scope = state.payScope;
  // T19: `?? []` treats "not yet known" as "none" here, unchanged from
  // this function's own pre-T19 behavior byte for byte. Reachable only
  // from the pay-confirm sheet, itself only reachable once
  // refreshPaymentPreview has already run inside enterTicket, well after
  // loadSavedCards, so this is a type-safety guard, not a new eligibility
  // rule; see the brief's own Section 2 for why the More sheet's two
  // options (which CAN be reached before that point) need the stricter
  // `!== undefined` check above instead.
  const savedCards = state.savedCards ?? [];
  const eligible =
    scope !== undefined &&
    scope.type !== "share" &&
    savedCards.length > 0 &&
    state.payTender !== "cash";
  payConfirmCardRow.hidden = !eligible;
  payConfirmManageCardsLink.hidden = savedCards.length === 0;
  updatePaySaveClientRow();
  if (!eligible) {
    return;
  }
  const selected = savedCards.find((entry) => entry.savedCard.id === state.selectedSavedCardId);
  payConfirmCardValue.textContent = selected === undefined ? "New card" : savedCardLabel(selected);
  payConfirmCardValue.dataset["savedCardId"] = selected?.savedCard.id ?? "";
}

function openPayWithCardChoice(): void {
  const opening = activePayConfirmOpening;
  if (!payConfirmOpeningIsCurrent(opening) || payConfirmScrim.hidden) return;
  const options: ChoiceOption[] = [
    { id: "new-card", label: "New card", sublabel: "Present a card at the card reader" },
    ...newestSavedCardFirst(state.savedCards ?? []).map((entry) => ({
      id: entry.savedCard.id,
      label: savedCardLabel(entry),
      sublabel: entry.guestLabel,
      testid: "pay-with-card-option",
    })),
  ];
  openChoiceSheet({
    title: "Pay with",
    options,
    onPick: (id) => {
      if (!payConfirmOpeningIsCurrent(opening)) return;
      state.selectedSavedCardId = id === "new-card" ? undefined : id;
      updatePayConfirmCardRow();
      // RCPT1-S3: a saved card is charged the ordinary way, so the tip choice comes back for it.
      syncReceiptSaleUi();
      updatePayConfirmSummary();
      updatePayConfirmButtonState();
    },
  });
}

/** Offered right after a successful FRESH card-present capture (never
 * after charging a card already on file: state.selectedSavedCardId is
 * checked by the caller before this runs), only when the payment has one
 * resolvable guest. Matches ALPHA-12's own "single guest = automatic,
 * two or more = an explicit choice, none = the concept does not apply
 * yet" philosophy: scope "guest" always names its own guest; scope
 * "whole" only offers this when the WHOLE order has exactly one guest
 * (a multi-guest whole-ticket payment does not guess whose card it is).
 * Never blocks or delays the payment itself: called after the pay-status
 * banner and toast already reflect the completed payment. */
async function offerCardConsentIfEligible(
  paymentAttemptId: string,
  scope: PayScope,
  usedSavedCardId: string | undefined,
): Promise<void> {
  if (usedSavedCardId !== undefined) {
    return;
  }
  // A linked client already made the prepayment choice. Declining that
  // choice cannot reopen an after-payment save prompt.
  if (clientForNewCard(scope) !== undefined) return;
  let orderGuestId: string | undefined;
  let guestLabel: string | undefined;
  if (scope.type === "guest") {
    orderGuestId = scope.guestId;
    guestLabel = scope.guestLabel;
  } else if (scope.type === "whole" && state.guests.length === 1) {
    orderGuestId = state.guests[0]?.id;
    guestLabel = state.guests[0]?.label;
  }
  if (orderGuestId === undefined || guestLabel === undefined) {
    return;
  }
  // CARDDEFAULT2: a card already saved for this guest is never offered again. A failed or unsure check asks as before,
  // and the server saves nothing new for a card it already has.
  if (state.orderId !== undefined) {
    const session = requireSession();
    const match = await getSavedCardMatch(
      session.apiBaseUrl,
      session.tenantId,
      state.orderId,
      orderGuestId,
      paymentAttemptId,
    );
    if (match.status === 200 && match.body.status === "saved") {
      showToast(`${guestLabel}'s card is already saved.`, "success");
      return;
    }
  }
  state.pendingCardConsent = { orderGuestId, guestLabel, sourcePaymentAttemptId: paymentAttemptId };
  cardConsentAckCheckbox.checked = false;
  cardConsentSaveButton.disabled = true;
  cardConsentGuest.textContent = `For ${guestLabel}`;
  cardConsentScrim.hidden = false;
}

/** UX102: set when a payment closed the ticket while the card-on-file offer is showing; the return to Open Tabs
 * happens when that offer is answered. */
let closeTicketWhenConsentDone = false;
/** RCPT2-F2: the receipt offer that waits behind the card-on-file offer, with the return to Open Tabs. */
let receiptOfferWhenConsentDone: ReceiptOfferAfterCard | undefined;

function closeCardConsentSheet(): void {
  if (prepayClientSaveGuest !== undefined) {
    closePrepayClientSave();
    return;
  }
  cardConsentScrim.hidden = true;
  state.pendingCardConsent = undefined;
  if (closeTicketWhenConsentDone) {
    closeTicketWhenConsentDone = false;
    const offer = receiptOfferWhenConsentDone;
    receiptOfferWhenConsentDone = undefined;
    finishClosedTicketAndOfferReceipt(offer);
  }
}

async function handleCardConsentSave(): Promise<void> {
  const pending = state.pendingCardConsent;
  if (pending === undefined || !cardConsentAckCheckbox.checked) {
    // Unreachable through the UI (the button stays disabled until the
    // box is checked; see the checkbox's own change listener below), kept
    // as a fail-loud guard rather than silently proceeding some other
    // way. The domain layer re-checks this too (ConsentRequiredError),
    // but this screen must never even attempt the call without it.
    return;
  }
  cardConsentSaveButton.disabled = true;
  const session = requireSession();
  const result = await vaultCardFromCapture(
    session.apiBaseUrl,
    session.tenantId,
    state.orderId ?? "",
    pending.orderGuestId,
    {
      idempotencyKey: crypto.randomUUID(),
      sourcePaymentAttemptId: pending.sourcePaymentAttemptId,
      consentAgreed: true,
      consentEvidenceReference: `consent-screen:${crypto.randomUUID()}`,
    },
  );
  if (result.status !== 200 && result.status !== 201) {
    cardConsentSaveButton.disabled = false;
    showToast(
      `Could not save this card. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  closeCardConsentSheet();
  if (state.orderId !== undefined) {
    await loadSavedCards(state.orderId);
  }
  showToast(
    result.body.alreadySaved === true
      ? `${pending.guestLabel}'s card is already saved.`
      : `${pending.guestLabel}'s card is saved for next time.`,
    "success",
  );
}

// ---------------------------------------------------------------------
// Payments-track Slice 3: saved-cards management sheet, reachable from
// the pay-confirm sheet's "Manage saved cards" link. Removal deletes the
// provider token for real, first-class, per PROV-105's own retention
// ruling.
// ---------------------------------------------------------------------

async function openSavedCardsSheet(): Promise<void> {
  if (state.orderId !== undefined) {
    await loadSavedCards(state.orderId);
  }
  renderSavedCardsList();
  savedCardsScrim.hidden = false;
}

function closeSavedCardsSheet(): void {
  savedCardsScrim.hidden = true;
  updatePayConfirmCardRow();
}

function renderSavedCardsList(): void {
  savedCardsList.innerHTML = "";
  // T19: `?? []`, unchanged behavior. openSavedCardsSheet (this
  // function's only caller) always awaits a fresh loadSavedCards
  // immediately before rendering, so state.savedCards is never actually
  // `undefined` at this point in practice; the guard is for type safety.
  const savedCards = state.savedCards ?? [];
  if (savedCards.length === 0 && state.pendingCardDetaches.length === 0) {
    savedCardsList.innerHTML = '<p class="tabs-empty">No cards saved on this ticket.</p>';
    return;
  }
  const list = document.createElement("div");
  list.className = "saved-card-list";
  for (const pending of state.pendingCardDetaches) {
    list.append(buildPendingCardRow(pending));
  }
  for (const entry of newestSavedCardFirst(savedCards)) {
    const row = document.createElement("div");
    row.className = "saved-card-row";
    row.dataset["testid"] = "saved-card-row";
    row.dataset["savedCardId"] = entry.savedCard.id;

    const info = document.createElement("div");
    info.className = "saved-card-info";
    const name = document.createElement("div");
    name.className = "saved-card-name";
    name.textContent = savedCardLabel(entry);
    const guest = document.createElement("div");
    guest.className = "saved-card-guest";
    guest.textContent = entry.guestLabel;
    info.append(name, guest);
    row.append(info);

    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.className = "btn-secondary btn-compact";
    removeButton.dataset["testid"] = "saved-card-remove";
    removeButton.textContent = "Remove";
    removeButton.addEventListener("click", () => renderSavedCardRemoveConfirm(row, entry));
    row.append(removeButton);

    list.append(row);
  }
  savedCardsList.append(list);
}

/** AU14-F3-F3-F1-F7: a removed card whose provider delete is not confirmed.
 * Retry replays the removal's own key (the server's retry path); it moves no
 * money, so it is one tap. */
function buildPendingCardRow(pending: PendingCardDetach): HTMLElement {
  const row = document.createElement("div");
  row.className = "saved-card-row saved-card-row-pending";
  row.dataset["testid"] = "saved-card-pending-row";
  row.dataset["savedCardId"] = pending.savedCardId;
  const info = document.createElement("div");
  info.className = "saved-card-info";
  const name = document.createElement("div");
  name.className = "saved-card-name";
  name.textContent = pending.label;
  const status = document.createElement("div");
  status.className = "saved-card-guest";
  status.dataset["testid"] = "saved-card-pending-status";
  status.textContent = `${pending.guestLabel} · ${CARD_REMOVAL_PENDING_LABEL}`;
  info.append(name, status);
  const retryButton = document.createElement("button");
  retryButton.type = "button";
  retryButton.className = "btn-secondary btn-compact";
  retryButton.dataset["testid"] = "saved-card-retry";
  retryButton.textContent = CARD_REMOVAL_RETRY_LABEL;
  retryButton.addEventListener("click", () => void handleRetryCardDetach(pending));
  row.append(info, retryButton);
  return row;
}

async function handleRetryCardDetach(pending: PendingCardDetach): Promise<void> {
  const session = requireSession();
  const gated = await requestApproval(
    `Remove saved card: ${pending.label}`,
    (approval) =>
      removeSavedCard(session.apiBaseUrl, session.tenantId, pending.savedCardId, {
        idempotencyKey: pending.idempotencyKey,
        ...approval,
      }),
    () => showRingScreen(),
  );
  if (gated.canceled) {
    return;
  }
  const result = gated.result;
  if (result.status !== 200) {
    showToast(
      `Could not remove this card. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    renderSavedCardsList();
    return;
  }
  const outcome = savedCardRemovalState(result.body);
  if (outcome === "removed") {
    state.pendingCardDetaches = withoutPending(state.pendingCardDetaches, pending.savedCardId);
  }
  renderSavedCardsList();
  showToast(retryToast(outcome), outcome === "removed" ? "success" : "warning");
}

/** A deliberate two-tap remove, in place on the row itself rather than a
 * second modal: tapping Remove reveals "Remove this card? / Cancel /
 * Confirm remove" in the SAME row, so removing a card is never a single
 * accidental tap but also never leaves this screen. */
function renderSavedCardRemoveConfirm(
  row: HTMLElement,
  entry: { savedCard: SavedCardDto; guestLabel: string },
): void {
  row.innerHTML = "";
  row.className = "saved-card-row saved-card-row-confirm";
  const message = document.createElement("div");
  message.className = "saved-card-info";
  message.textContent = `Remove ${savedCardLabel(entry)}? This deletes it completely.`;
  row.append(message);

  const cancelButton = document.createElement("button");
  cancelButton.type = "button";
  cancelButton.className = "btn-secondary btn-compact";
  cancelButton.dataset["testid"] = "saved-card-remove-cancel";
  cancelButton.textContent = "Cancel";
  cancelButton.addEventListener("click", () => renderSavedCardsList());

  const confirmButton = document.createElement("button");
  confirmButton.type = "button";
  confirmButton.className = "btn-danger btn-compact";
  confirmButton.dataset["testid"] = "saved-card-remove-confirm";
  confirmButton.textContent = "Confirm remove";
  confirmButton.addEventListener("click", () => void handleRemoveSavedCard(entry));

  row.append(cancelButton, confirmButton);
}

// RBAC track: money.saved_card.remove reaches this flow whenever the
// server answers approval_required. See handleReopenClick's own comment
// on requestApproval's "only shows the picker if the first, no-approval
// attempt actually comes back approval_required" contract; every other
// role's Remove button behaves completely unchanged.
//
// Owner ruling 2026-08-27: "No role should need approval to remove a
// card. That is overkill." The SHIPPED DEFAULT for assistant_manager on
// this permission therefore moves from approval_required to allowed, and
// under that default no seeded role reaches the picker here at all.
//
// The approval wiring below stays anyway, and that is deliberate. A
// shipped template default does not decide whether a call site needs
// approval wiring: once the Edit Role screen ships, an Admin can set ANY
// permission to approval_required at runtime, and a call site that
// answered a 403 approval_required with a bare error toast would be a
// dead end for whichever shop chose that. This is a property of the
// permission MODEL, not of one seeded value. e2e/manager-approval.spec.ts
// covers this journey with an explicitly seeded grant for the same
// reason (its own seedApprovalRequired comment).
async function handleRemoveSavedCard(entry: {
  savedCard: SavedCardDto;
  guestLabel: string;
}): Promise<void> {
  const session = requireSession();
  const savedCardId = entry.savedCard.id;
  const idempotencyKey = crypto.randomUUID();
  const gated = await requestApproval(
    `Remove saved card: ${savedCardLabel(entry)}`,
    (approval) =>
      removeSavedCard(session.apiBaseUrl, session.tenantId, savedCardId, {
        idempotencyKey,
        ...approval,
      }),
    () => showRingScreen(),
  );
  if (gated.canceled) {
    return;
  }
  const result = gated.result;
  if (result.status !== 200) {
    showToast(
      `Could not remove this card. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    showRingScreen();
    renderSavedCardsList();
    return;
  }
  showRingScreen();
  if (state.selectedSavedCardId === savedCardId) {
    state.selectedSavedCardId = undefined;
  }
  if (state.orderId !== undefined) {
    await loadSavedCards(state.orderId);
  }
  const outcome = savedCardRemovalState(result.body);
  if (outcome === "pending") {
    state.pendingCardDetaches = withPending(state.pendingCardDetaches, {
      savedCardId,
      label: savedCardLabel(entry),
      guestLabel: entry.guestLabel,
      idempotencyKey,
    });
  }
  renderSavedCardsList();
  showToast(removalToast(outcome), outcome === "removed" ? "success" : "warning");
}

// Design/QA gate finding F: the per-maker tip split (payment_tip_splits,
// returned inline by payOrder's own response) was recorded from the
// start but never surfaced anywhere in the UI. Renders the maker's
// display name, LEFT JOINed tenant- and id-scoped by
// SELECT_TIP_SPLITS_FOR_PAYMENT_SQL, falling back to the raw employee id
// only for a split written before SEC4 (no matching users row). A
// zero-tip payment (empty tipSplits) hides this block entirely rather
// than showing an empty list.
//
// UX62 payments-review finding, 2026-09-28 (docs/build-briefs/
// UX62-paid-ticket-totals.md, Section 1b): this block shows ONLY the tip
// split of the single most recent payment attempt (whichever call --
// handlePayConfirmClick or the split-collect path -- last invoked this
// function), and it renders directly below the ticket-wide totals block,
// which as of UX62 carries its own "Tips $X" row summing EVERY captured
// payment's tip. The bare header "Tip split" (rendered all-caps by
// .pay-tip-split-title's own text-transform) read as though it described
// that ticket-wide Tips figure, so a manager reading "Manager $1.00"
// under "Tip split" right below "Tips $3.00" could misread the $1.00 as
// the full split of the $3.00. Retitled to name its own narrower scope.
function renderPayTipSplit(
  tipSplits: readonly { employee_id: string; display_name: string | null; amount_cents: number }[],
): void {
  if (tipSplits.length === 0) {
    payTipSplitEl.hidden = true;
    payTipSplitEl.innerHTML = "";
    return;
  }
  payTipSplitEl.innerHTML = "";
  const title = document.createElement("div");
  title.className = "pay-tip-split-title";
  title.dataset["testid"] = "pay-tip-split-title";
  title.textContent = "Tip split for this payment";
  payTipSplitEl.append(title);
  for (const split of tipSplits) {
    const row = document.createElement("div");
    row.className = "pay-tip-split-row";
    row.dataset["testid"] = "pay-tip-split-row";
    const nameEl = document.createElement("span");
    nameEl.textContent = split.display_name ?? split.employee_id;
    const amountEl = document.createElement("span");
    amountEl.textContent = formatCents(split.amount_cents);
    row.append(nameEl, amountEl);
    payTipSplitEl.append(row);
  }
  payTipSplitEl.hidden = false;
}

// ---------------------------------------------------------------------
// TERM2: the wait for a card on a reader. See
// docs/build-briefs/TERM2-physical-reader-registration.md.
// ---------------------------------------------------------------------

let payWaitTimer: ReturnType<typeof setTimeout> | undefined;
/** True once the server confirmed the cashier's Cancel stopped the payment. */
let payCancelConfirmed = false;
/** Set while the outcome of a payment is uncertain (the request's answer was
 * lost): what to do once Cancel has been answered. */
let uncertainRecovery: ((outcome: string) => Promise<void>) | undefined;

function setPayStatus(text: string, tone: PayStatusTone): void {
  payStatusEl.hidden = false;
  payStatusEl.className = `pay-status ${tone}`;
  payStatusEl.removeAttribute("role");
  payStatusEl.textContent = text;
}

function showPayWaitingLine(onReader: boolean): void {
  payStatusEl.hidden = false;
  payStatusEl.className = "pay-status waiting";
  payStatusEl.setAttribute("role", "status");
  const spinner = document.createElement("span");
  spinner.className = "pay-status-spinner";
  spinner.setAttribute("aria-hidden", "true");
  const lines = document.createElement("span");
  lines.className = "pay-status-lines";
  const title = document.createElement("span");
  title.className = "pay-status-title";
  title.textContent = onReader ? ON_READER_TITLE : WAITING_TITLE;
  const hint = document.createElement("span");
  hint.className = "pay-status-hint";
  hint.textContent = onReader ? ON_READER_HINT : WAITING_HINT;
  lines.append(title, hint);
  payStatusEl.replaceChildren(spinner, lines);
  payStatusEl.scrollIntoView({ block: "nearest" });
}

/** Starts the delayed waiting line and its Cancel button for a card payment. */
function beginPayWaiting(eligible: boolean, target: string, onReader = false): void {
  endPayWaiting();
  payCancelConfirmed = false;
  uncertainRecovery = undefined;
  if (!eligible || terminalConfigState?.paymentTerminalId === undefined) {
    return;
  }
  void target;
  payWaitTimer = setTimeout(() => {
    payWaitTimer = undefined;
    showPayWaitingLine(onReader);
    payReaderCancelButton.disabled = false;
    payReaderCancelButton.hidden = false;
  }, WAITING_LINE_DELAY_MS);
}

function endPayWaiting(): void {
  if (payWaitTimer !== undefined) {
    clearTimeout(payWaitTimer);
    payWaitTimer = undefined;
  }
  payReaderCancelButton.hidden = true;
}

/** PAYFIX1 (lounger-01). The ticket whose last payment answer was lost, and the intent key that payment was sent under. While set
 * for the open ticket, Pay is off and Check payment is offered. Cleared only by a definitive answer: the server's own payment record
 * (Check payment), the reader's confirmed cancel, or the ticket closing. */
let payUncertain: { orderId: string; target: string } | undefined;
let payCheckBusy = false;

function markPayUncertain(orderId: string, target: string): void {
  payUncertain = { orderId, target };
  renderTicketActions();
}

function clearPayUncertain(orderId?: string): void {
  if (payUncertain !== undefined && (orderId === undefined || payUncertain.orderId === orderId)) {
    payUncertain = undefined;
  }
}

/** PAYFIX1 (lounger-01). Asks the server what happened to the payment whose answer was lost. It reads the ticket's payments (the
 * existing GET, no charge) and finds the attempt recorded under the intent key that payment was sent with, the same key the server
 * uses to refuse a second charge. Captured: paid, and the ticket finishes as after any payment. A failed attempt: the reason, and Pay
 * comes back. Pending: still waiting, Pay stays off. No record: Pay comes back on the same key, so if the first request is still on
 * its way the server answers both with one charge. A failed read changes nothing. */
async function handlePayCheckClick(): Promise<void> {
  const uncertain = payUncertain;
  const session = sessionOrUndefined();
  if (uncertain === undefined || session === undefined || payCheckBusy) return;
  if (state.orderId !== uncertain.orderId) return;
  payCheckBusy = true;
  payCheckButton.disabled = true;
  payCheckButton.textContent = "Checking...";
  try {
    const result = await getPayments(session.apiBaseUrl, session.tenantId, uncertain.orderId);
    if (state.orderId !== uncertain.orderId || payUncertain !== uncertain) return;
    if (result.status !== 200) {
      if (handlePinChangeRequiredOnPayClose(result.body.error)) return;
      payStatusEl.hidden = false;
      setPayStatus("Could not check the payment. Try again.", "error");
      return;
    }
    const key = state.payIdempotencyKeysByTarget.peek(uncertain.target);
    if (key === undefined) {
      payStatusEl.hidden = false;
      setPayStatus("Could not tell whether it went through. Ask a manager.", "error");
      return;
    }
    const attempt = result.body.payments
      .map((payment) => payment.paymentAttempt)
      .find((candidate) => candidate.idempotency_key === key);
    payStatusEl.hidden = false;
    if (attempt === undefined) {
      // No record under this key. The key stays: Pay again replays it, never a second charge.
      clearPayUncertain(uncertain.orderId);
      setPayStatus("No payment was found. Tap Pay to try again.", "warning");
      return;
    }
    const view = payResultView({
      amount: attempt.amount,
      status: attempt.status,
      tipCents: attempt.tip_cents,
      ...(attempt.tender === "cash" ? { cash: true } : {}),
    });
    if (attempt.status !== "Captured" && attempt.status !== "Failed") {
      // Pending or anything unrecognised: not settled either way, so Pay stays off.
      setPayStatus(view.text, view.tone);
      return;
    }
    clearPayUncertain(uncertain.orderId);
    clearPayIdempotencyKey(uncertain.target);
    setPayStatus(view.text, view.tone);
    if (attempt.status === "Failed") return;
    state.orderPaid = true;
    state.selectedTip = undefined;
    await refreshPaymentPreview();
    if (state.orderId !== uncertain.orderId) return;
    // The server closes a ticket this payment settled; the lost answer was the only news of it, so read the ticket.
    const order = await getOrder(session.apiBaseUrl, session.tenantId, uncertain.orderId);
    if (state.orderId !== uncertain.orderId) return;
    if (order.status === 200 && order.body.order.status === "closed") {
      finishClosedTicketAndOfferReceipt(
        receiptOfferForClosedCardPayment(uncertain.orderId, attempt, { kind: "closed" }),
      );
    } else if (
      state.paymentPreview !== undefined &&
      state.paymentPreview.uncoveredLines.length === 0
    ) {
      showZeroDuePrompt({ selfClosed: false });
    }
  } finally {
    payCheckBusy = false;
    payCheckButton.textContent = "Check payment";
    if (state.orderId === uncertain.orderId) renderTicketActions();
  }
}

/** The request's answer was lost, so a card may or may not have been taken. Pay
 * stays disabled (no blind retry), and Cancel asks the reader and the provider
 * what is true, then re-reads the ticket, so Pay is never stuck. */
function offerUncertainCancel(target: string): void {
  if (terminalConfigState?.paymentTerminalId === undefined) {
    return;
  }
  uncertainRecovery = async (outcome) => {
    uncertainRecovery = undefined;
    if (outcome === "canceled" || outcome === "nothing_pending") {
      clearPayIdempotencyKey(target);
    }
    if (outcome === "canceled" || outcome === "nothing_pending" || outcome === "completed") {
      payStatusRetryButton.hidden = true;
      payReaderCancelButton.hidden = true;
      clearPayUncertain();
      await refreshPaymentPreview();
    }
  };
  payReaderCancelButton.disabled = false;
  payReaderCancelButton.hidden = false;
}

async function handlePayReaderCancelClick(): Promise<void> {
  const session = requireSession();
  const terminalId = terminalConfigState?.paymentTerminalId;
  if (terminalId === undefined || payReaderCancelButton.disabled) {
    return;
  }
  payReaderCancelButton.disabled = true;
  const result = await cancelReaderAction(session.apiBaseUrl, session.tenantId, terminalId, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (result.status !== 200) {
    setPayStatus(
      "Could not reach the reader to cancel. Check the reader screen before trying again.",
      "warning",
    );
    payReaderCancelButton.disabled = false;
    return;
  }
  const outcome = result.body.outcome;
  if (outcome === "canceled") {
    payCancelConfirmed = true;
  }
  const view = cancelOutcomeView(outcome);
  setPayStatus(view.text, view.tone);
  if (outcome === "canceled" || outcome === "completed") {
    payReaderCancelButton.hidden = true;
  } else {
    payReaderCancelButton.disabled = false;
  }
  if (uncertainRecovery !== undefined) {
    const recovery = uncertainRecovery;
    await recovery(outcome);
  }
}

async function handlePayConfirmClick(cashPick?: CashTenderPick): Promise<void> {
  // QA-gate fix (double-charge race, 2026-08-20): the primary defense.
  // Reproduced live by the gate with a 2s delayed payments response and a
  // rapid double Confirm tap: Confirm never disabled while the request
  // was in flight, and every tap minted its own fresh idempotency key, so
  // the second tap created a second, real (simulated) Captured payment
  // attempt for the same order before the first request had even
  // resolved. This guard, and the two disables immediately below it, run
  // synchronously, before this function's first `await`, so nothing can
  // slip a second tap through between them.
  if (payConfirmButton.disabled) {
    return;
  }
  const submissionOpening = activePayConfirmOpening;
  if (!payConfirmOpeningIsCurrent(submissionOpening) || submissionOpening === undefined) return;
  const submissionContext = submissionOpening.ticket;
  // ZEROPAY1 (owner report 2026-10-30, production): a fully comped ticket with no tip has nothing to charge. A $0.00 card
  // charge is refused by the provider, so Pay closes it the way "No tip, close ticket" does, with no adapter call. A tip on a
  // comped ticket still charges normally.
  const zeroPreview = state.payConfirmPreview;
  if (state.payTender !== "cash" && zeroPreview !== undefined && zeroPreview.totalCents === 0 && zeroPreview.tipCents === 0) {
    closePayConfirmSheet();
    await performResolveNoTip();
    return;
  }
  // TIPMIN1: a card charge under the provider's minimum (a tip-only charge on a comped ticket) is refused here with a plain
  // sentence before anything is sent. The server refuses the same charge too. A hold capture, a receipt sale and a tip taken on
  // the reader are not sized by this sheet's preview, so they are left to the server.
  if (state.payTender !== "cash" && zeroPreview !== undefined && state.activeHold?.status !== "active" &&
      !receiptSaleActive() && !readerTipSaleActive() && isBelowCardMinimum(zeroPreview.totalCents)) {
    showToast(CARD_MINIMUM_CHARGE_MESSAGE, "danger");
    return;
  }
  // TERMREADER1: a remembered, not-known-offline reader is charged with no question and no wait. Only a missing or
  // offline reader costs a lookup, and a second tap while it runs does nothing.
  if (state.payTender !== "cash" && cardChargeUsesReader()) {
    const before = decideReaderBeforeCharge({
      deviceReaderId: terminalConfigState?.paymentTerminalId,
      cachedHealth:
        readerHealthReading?.terminalId === terminalConfigState?.paymentTerminalId &&
        readerHealthReading?.state === "offline"
          ? "offline"
          : "unknown",
    });
    if (before.kind !== "send") {
      if (readerPrecheckInFlight) return;
      readerPrecheckInFlight = true;
      try {
        if (!(await resolveReaderBeforeCharge(before, submissionOpening))) return;
      } finally {
        readerPrecheckInFlight = false;
      }
      if (!payConfirmOpeningIsCurrent(submissionOpening)) return;
    }
  }
  if (readerCartPauseActive()) {
    if (readerCartDisplay?.phase !== "shown" ||
        readerCartDisplay.signature !== readerCartSelectionSignature()) {
      await showCurrentReaderCart();
      // READERTIP1: one press runs the whole sequence. The cart display is the first step of the same press; once the server
      // has acknowledged it for exactly this selection the payment starts here, with no second press, so the reader goes on to
      // its tip screen and then the card. Any other outcome (busy, unconfirmed, refused, stale, sheet closed, selection
      // changed) stops with its own message and nothing is charged; the next press shows the cart again.
      if (!payConfirmOpeningIsCurrent(submissionOpening) ||
          readerCartDisplay?.phase !== "shown" ||
          readerCartDisplay.signature !== readerCartSelectionSignature()) {
        return;
      }
    }
  }
  // CASH1-S2-U1: Take cash opens the Cash screen (after an open drawer); the Cash screen's Take button comes back here with
  // the tender. Nothing is sent from this click.
  if (state.payTender === "cash" && cashPick === undefined) {
    void startCashTender();
    return;
  }
  const isCash = cashPick !== undefined && state.payTender === "cash";
  // RCPT1-S3: a sale whose tip is written on the receipt authorizes the card and prints; the card is not saved on it.
  const receiptSale = !isCash && receiptSaleActive();
  const saveGuest = isCash || receiptSale ? undefined : clientForNewCard(state.payScope);
  if (
    saveGuest !== undefined &&
    paySaveClientCheckbox.checked &&
    confirmedClientSave === undefined
  ) {
    prepayClientSaveGuest = saveGuest;
    cardConsentGuest.textContent = `For ${saveGuest.label}`;
    cardConsentAckRow.hidden = true;
    cardConsentSaveButton.disabled = false;
    cardConsentSaveButton.textContent = "Pay and save card";
    cardConsentDeclineButton.textContent = "Back";
    cardConsentScrim.hidden = false;
    return;
  }
  // CASH1-S2-U1: there is no tip on cash. No tip is sent, so the server takes exactly the amount due.
  // TERM10: the reader asks the guest for the tip, so the device sends no tip of its own and the server tells the reader to ask.
  const readerTipSale = !isCash && !receiptSale && readerTipSaleActive();
  const tip = isCash ? undefined : readerTipSale ? { type: "none" as const } : state.selectedTip;
  if (tip === undefined && !isCash && !receiptSale) {
    // Unreachable through the UI (the button is disabled until a tip is
    // chosen; see updatePayConfirmButtonState), kept as a fail-loud guard
    // rather than silently defaulting to "no tip" if this is ever reached
    // some other way.
    showToast("Choose a tip amount, or No tip, before paying.", "danger");
    return;
  }
  payConfirmButton.disabled = true;
  // A fresh attempt starting: clear any retry affordance a PRIOR
  // network-failed share collection left showing.
  payStatusRetryButton.hidden = true;
  // payButton (on the ring screen behind this sheet) is disabled too: the
  // sheet closes immediately below, and without this a bartender could
  // tap Pay again while the first Confirm is still in flight, reopening
  // the sheet and minting a second attempt from there instead. Both
  // re-enable only on a definitive server answer (see the branches
  // below); an uncertain (network-error) outcome leaves both disabled
  // rather than inviting a blind, possibly duplicate retry.
  payButton.disabled = true;
  // Captured before closePayConfirmSheet() clears state.payScope.
  const scope = state.payScope ?? { type: "whole" as const };
  const clientSave = isCash ? undefined : confirmedClientSave;
  confirmedClientSave = undefined;
  const cashBody: { tender: "cash"; cash: CashTenderPick } | undefined = isCash
    ? { tender: "cash", cash: cashPick }
    : undefined;
  const acknowledgementId = readerCartPauseActive() ? readerCartDisplay?.acknowledgementId : undefined;
  // Submission keeps the existing Pay lock until the payment outcome.
  closePayConfirmSheet(false);
  const submissionOpeningToken = payConfirmOpeningToken;

  const session = requireSession();
  if (state.orderId === undefined) {
    return;
  }
  // Gate re-review correction, 2026-08-26: reads the SAME per-target map
  // openPayConfirmSheet already wrote into for this exact target (the
  // identical computePayIntentTarget call), rather than a single shared
  // slot -- see state.payIdempotencyKeysByTarget's own doc comment.
  if (receiptSale) {
    await runReceiptSale(state.orderId);
    return;
  }
  const target = computePayIntentTarget(state.orderId, scope);
  const idempotencyKey = getOrMintPayIdempotencyKey(target);

  // ALPHA-12 equal split: one share's own sequential simulated payment,
  // a different command from payOrder (src/domain/splitPayments.ts's
  // collectShare), with its own success/failure shape (a share may or may
  // not be the LAST one, which is the only thing that decides whether the
  // ticket itself is now paid).
  if (scope.type === "share") {
    // TERM2: see the waiting line before payOrder below.
    beginPayWaiting(!isCash && state.activeHold?.status !== "active", target, readerTipSale);
    const result = await collectShare(session.apiBaseUrl, session.tenantId, scope.shareId, {
      idempotencyKey,
      currency: "usd",
      ...(cashBody ?? {}),
      // PG1: the floor never names an outcome. A real reader and Stripe decide it; the simulated provider's own
      // default is Captured, so nothing changes there. A forced outcome is a test-only seam a test injects itself.
      ...(tip === undefined ? {} : { tip }),
      // Terminal-assignment slice. See performOpenHold's own identical
      // comment above.
      terminalId: terminalConfigState?.paymentTerminalId,
      ...(acknowledgementId === undefined ? {} : { acknowledgementId }),
    });
    endPayWaiting();
    // TERM6-F3: the server answers 201 for a Failed or Pending share too, and keeps it uncollected. Only a Captured
    // attempt is a collected share; anything else shows the payment's own sentence and leaves the plan as it is.
    const shareView =
      result.status === 200 || result.status === 201
        ? shareCollectView({
            amount: result.body.paymentAttempt.amount,
            status: result.body.paymentAttempt.status,
            failureKind: result.body.failureKind,
          })
        : undefined;
    if (shareView !== undefined && !shareView.collected) {
      if (!shareView.keepKey) {
        clearPayIdempotencyKey(target);
      }
      payStatusEl.hidden = false;
      payTipSplitEl.hidden = true;
      setPayStatus(shareView.text, shareView.tone);
      await loadActiveSplitPlan();
      renderTicketActions();
      return;
    }
    if (result.status === 200 || result.status === 201) {
      clearPayIdempotencyKey(target);
      state.selectedTip = undefined;
      showCashChangeIfCash(result.body.paymentAttempt);
      const completed = result.body.plan.completed_at !== null;
      if (completed) {
        payStatusEl.hidden = false;
        payStatusEl.className = "pay-status success";
        payStatusEl.textContent = `All ${result.body.plan.share_count} shares collected (test payment).`;
        renderPayTipSplit(result.body.tipSplits);
        state.orderPaid = true;
        state.activeSplitPlan = undefined;
        await refreshPaymentPreview();
        // Sixteenth amendment item 2: every share collected means this
        // payment brought the balance to zero; state.paymentPreview is the
        // authoritative check (not just "completed", to stay consistent
        // with the one rule the whole/guest branch below also uses).
        // CL2: the completing share settles the ticket, so the server has
        // already closed it and says so on this same response.
        if (settledAndClosed(result.body.settlementClose)) {
          // RCPT2-F2: the completing share was a card payment; offer the receipt choice for it.
          finishClosedTicketAndOfferReceipt(
            receiptOfferForClosedCardPayment(
              state.orderId,
              result.body.paymentAttempt,
              result.body.settlementClose,
            ),
          );
        } else if (
          state.paymentPreview !== undefined &&
          state.paymentPreview.uncoveredLines.length === 0
        ) {
          showZeroDuePrompt({ selfClosed: false });
        }
        showToast("Ticket paid: every share collected.", "success");
      } else {
        // Not the last share: no "ticket paid" status banner yet (that
        // would be premature and misleading), a toast marking this
        // share's own progress, and straight back into the split-progress
        // sheet for the next one, matching the product's own "each share
        // is taken as its own sequential simulated payment" framing.
        payStatusEl.hidden = true;
        await loadActiveSplitPlan();
        await refreshPaymentPreview();
        showToast(
          `Share ${result.body.share.share_index} of ${result.body.plan.share_count} collected.`,
          "success",
        );
        openSplitProgressSheet();
      }
      return;
    }
    if (result.status === 409 && result.body.error === "reader_cart_context_changed") {
      await recoverReaderCartContext(scope, submissionContext, submissionOpeningToken);
      return;
    }
    if (handlePinChangeRequiredOnPayClose(result.body.error)) {
      return;
    }
    payStatusEl.hidden = false;
    payStatusEl.className = "pay-status error";
    payTipSplitEl.hidden = true;
    if (result.networkError) {
      // Design/QA gate LOW correction, 2026-08-21: before this fix, a
      // network-failed share collection left the bartender stuck reading
      // this uncertain message with no path forward short of leaving the
      // ticket and re-entering it (which happens to force a fresh
      // loadActiveSplitPlan/refreshPaymentPreview on re-entry). The retry
      // button below does the SAME re-read on demand, right here: a plain
      // GET, no double-charge risk, resolving the ambiguity either way
      // (the share shows Collected if it actually captured despite the
      // network hiccup, or stays a tappable pending share if it did not).
      // PAY12 (docs/build-briefs/PAY12-network-error-double-message.md):
      // reusing PAY11's own fix shape (drop the redundant toast, keep the
      // rail's payStatusEl box, which is already visible here per
      // :15195). No showToast call: the rail already carries this exact
      // sentence, and a network-error refusal never carries
      // authentication_required (no response reached this client at
      // all), so PAY11's sign-out carve-out does not apply here.
      const uncertainMessage = "Payment may not have gone through. Check before retrying.";
      payStatusEl.textContent = uncertainMessage;
      payStatusRetryButton.hidden = false;
      // A cash payment never reached a reader or a provider, so there is nothing to cancel there.
      if (!isCash) offerUncertainCancel(target);
      return;
    }
    // Architect gate correction, 2026-08-25 (Finding 3): a definitive
    // (non-networkError) failure is only safe to treat as "the server
    // said no, nothing charged" when it actually was -- see
    // shouldClearPayIdempotencyKeyOnFailure's own header (./api.ts) for
    // the full reasoning. An ambiguous failure (chiefly a post-capture SQL
    // rollback inside collectShare's own transaction, surfacing as a bare
    // 500) now PRESERVES the key, so the next attempt replays the same
    // domain idempotency key and dedupes at the adapter instead of minting
    // a fresh one that could genuinely capture a second time.
    if (shouldClearPayIdempotencyKeyOnFailure(result)) {
      clearPayIdempotencyKey(target);
    }
    renderTicketActions();
    const translated = translatePayCloseError("pay", result.body, result.status);
    state.lastPayCloseErrorDetail = translated.detail;
    renderLastPayCloseErrorDebug();
    payStatusEl.textContent = translated.plain;
    // TERMREADER1: a reader that is gone or offline asks which one to use instead of ending here.
    if (!isCash && askForReaderAfterRefusal(result.body.error, target)) return;
    // PAY14 (D2 required change 3): the badge follows the refusal.
    applyHoldRefusalToBadge(result.body.error);
    // CL6-F1-F3: live money waits for a confirmed time zone and cutoff.
    maybeShowBusinessDaySheet(result.body.error);
    // PAY11: one message, in the rail, per refusal. The toast this call
    // used to always show duplicated that same sentence and, for a long
    // enough message, landed on top of it. shouldShowPayCloseRefusalToast
    // keeps the toast ONLY for authentication_required, the one code whose
    // own global sign-out switches the screen out from under this rail box
    // before anyone can read it.
    if (shouldShowPayCloseRefusalToast(result.body)) {
      showToast(translated.plain, "danger");
    }
    return;
  }

  // Architect gate correction, 2026-08-21: no "amount" is sent at all.
  // src/api/server.ts's payments route never reads one from the request
  // body; subtotal, tax, and the final amount are always computed
  // server-side from the order's own current uncovered lines, inside
  // payOrder's own locked transaction. See that module's header comment.
  // ALPHA-12 "pay my drinks": guestId is present only for scope "guest",
  // scoping payOrder to that guest's own uncovered lines (src/domain/
  // payments.ts's PayOrderParams.guestId).
  // Payments-track Slice 3, "Use saved card": captured before the request
  // so the post-success consent offer below (offerCardConsentIfEligible)
  // reads the choice this SPECIFIC payment actually used, not whatever
  // state.selectedSavedCardId happens to hold by the time the response
  // comes back.
  const paidWithSavedCardId = isCash ? undefined : state.selectedSavedCardId;
  // TERM2: a card on a physical reader waits for a person to tap, insert or
  // swipe. After a short delay (so a simulated reader never flashes it) the rail
  // says so and offers Cancel. Not for a saved card or a held tab, which never
  // touch a reader.
  beginPayWaiting(
    !isCash && paidWithSavedCardId === undefined && state.activeHold?.status !== "active",
    target,
    readerTipSale,
  );
  const result = await payOrder(session.apiBaseUrl, session.tenantId, state.orderId, {
    idempotencyKey,
    currency: "usd",
    // PG1: the floor never names an outcome (see the split share call above). A saved-card charge and a held tab
    // never had a forced-outcome seam, and a fresh card now carries none either.
    // CASH1-S2-U1: a cash payment sends no tip at all.
    ...(tip === undefined ? {} : { tip }),
    ...(cashBody ?? {}),
    guestId: scope.type === "guest" ? scope.guestId : undefined,
    unassigned: scope.type === "unassigned" ? true : undefined,
    byAmount: scope.type === "amount" ? scope.selection : undefined,
    savedCardId: paidWithSavedCardId,
    // Terminal-assignment slice: harmless to send even when this
    // particular payment ends up charging a saved card or an active
    // hold instead of a fresh card-present intent -- the server only
    // ever resolves it for the ONE branch (a fresh intent) that actually
    // needs a reader (src/domain/payments.ts's payOrder, the
    // "requiresFreshIntent" comment on its own final branch).
    terminalId: terminalConfigState?.paymentTerminalId,
    ...(acknowledgementId === undefined ? {} : { acknowledgementId }),
  });
  endPayWaiting();
  payStatusEl.hidden = false;
  if (result.status === 200 || result.status === 201) {
    payStatusEl.className = "pay-status success";
    // CL2: the wording no longer tells staff to tap Close, because on a
    // ticket that just settled there is nothing left to tap: the server
    // closed it and finishClosedTicket below takes them back to Open
    // Tabs. When something is still owed, the sheet says so instead.
    // TERM2: Failed and Pending no longer read "Paid"; Captured keeps its
    // wording. A Failed result that follows the cashier's own confirmed Cancel
    // says so, and is the one case that may say nothing was charged.
    const view = payCancelConfirmed
      ? cancelOutcomeView("canceled")
      : payResultView({
          amount: result.body.paymentAttempt.amount,
          status: result.body.paymentAttempt.status,
          tipCents: result.body.paymentAttempt.tip_cents,
          failureKind: result.body.failureKind,
          ...(scope.type === "guest" ? { guestLabel: scope.guestLabel } : {}),
          ...(isCash ? { cash: true } : {}),
        });
    payCancelConfirmed = false;
    setPayStatus(
      view.text,
      result.body.paymentAttempt.status === "Captured" ? "success" : view.tone,
    );
    renderPayTipSplit(result.body.tipSplits);
    state.orderPaid = true;
    clearPayIdempotencyKey(target);
    // CASH1-S2-U1: the change the server recorded, large, on top of whatever the ticket does next.
    showCashChangeIfCash(result.body.paymentAttempt);
    // ALPHA-09 release evidence fix: a custom (non-preset) tip is
    // deliberately an absolute amount independent of subtotal by design
    // (Review 8's polish pass), so leaving state.selectedTip set to the
    // just-completed payment's custom tip fed getPaymentPreview a real
    // tip amount against a genuinely empty ($0) uncovered subtotal for a
    // fully-covering payment, and the Pay button's own label (set from
    // that preview in renderTicketActions) kept showing the stale tip
    // amount (e.g. "Pay $0.35") until the tab was closed and reopened.
    // The button was never actually payable in that state (correctly
    // disabled, correct "Nothing unpaid" reason text), but the label
    // still read wrong. Cleared here, immediately after a successful
    // Confirm, rather than only the next time the sheet opens
    // (openPayConfirmSheet already clears it there for the next attempt,
    // which is a different moment: before this fix, that reset never
    // reached the button label from a tab that stayed open).
    state.selectedTip = undefined;
    // ALPHA-08 delta payments (owner ruling 2026-08-21): refreshes the
    // live uncovered-lines read, which is what actually drives each
    // line's edit control (renderTicketLines, via isLineCovered) and
    // Pay's own enabled state (renderTicketActions) now - not a blanket
    // "order paid" flag, since this same tab can gain new, genuinely
    // payable lines after this payment.
    await refreshPaymentPreview();
    // Sixteenth amendment item 2: this specific payment may have brought
    // the ticket's balance to zero (whole-ticket pay, or the last
    // uncovered guest on a "pay my drinks" scope); state.paymentPreview
    // right after the refresh above is the authoritative check, the same
    // one renderTicketActions itself uses to decide Pay is disabled.
    // CL2, owner ruling 2026-08-28: if this payment settled the ticket,
    // the SERVER already closed it and said so, so the floor is told once
    // and taken back to Open Tabs rather than shown a prompt asking it to
    // do a thing that has already happened.
    // UX102 (owner directive, Kerry 2026-10-10: "there is no need to add an extra step and button to go back to open
    // tabs. Just do it."): the moment the payment settles and the server closes the ticket, the floor returns to
    // Open Tabs. The card-on-file offer below needs this ticket's id, so when it is showing the return waits for it.
    const closedBySettlement = settledAndClosed(result.body.settlementClose);
    // RCPT2-F2: captured before finishClosedTicket clears the ticket; undefined for cash and for a ticket still open.
    const receiptOffer = receiptOfferForClosedCardPayment(
      state.orderId,
      result.body.paymentAttempt,
      result.body.settlementClose,
    );
    if (closedBySettlement) {
      closePayConfirmSheet();
    } else if (
      // Still settled, but deliberately still open: a comp with a tip not
      // yet taken, or an empty ticket. The prompt keeps its old job for
      // exactly that case, which is the one it was written for.
      state.paymentPreview !== undefined &&
      state.paymentPreview.uncoveredLines.length === 0
    ) {
      showZeroDuePrompt({ selfClosed: false });
    }
    // CASH1-S2-U1: a cash payment is never vaulted and never offers saving a card.
    if (isCash) {
      if (closedBySettlement) finishClosedTicket();
      return;
    }
    // Payments-track Slice 3: offered only after a fresh card-present
    // capture (never after charging a card already on file), and never
    // blocking or delaying anything above -- the payment is already fully
    // recorded by this point.
    if (clientSave !== undefined && result.body.paymentAttempt.status === "Captured") {
      const saved = await vaultCardFromCapture(
        session.apiBaseUrl,
        session.tenantId,
        state.orderId,
        clientSave.guest.id,
        {
          idempotencyKey: `client-save-${idempotencyKey}`,
          sourcePaymentAttemptId: result.body.paymentAttempt.id,
          consentAgreed: true,
          consentEvidenceReference: clientSave.evidenceReference,
        },
      );
      if (saved.status === 200 || saved.status === 201) {
        await loadSavedCards(state.orderId);
        showToast(
          saved.body.alreadySaved === true
            ? "This card is already saved."
            : "Card saved to this client for next time.",
          "success",
        );
      } else {
        showToast("Payment went through, but the card could not be saved.", "danger");
      }
    } else if (saveGuest === undefined) {
      await offerCardConsentIfEligible(result.body.paymentAttempt.id, scope, paidWithSavedCardId);
    }
    if (closedBySettlement) {
      if (cardConsentScrim.hidden) {
        finishClosedTicketAndOfferReceipt(receiptOffer);
      } else {
        closeTicketWhenConsentDone = true;
        receiptOfferWhenConsentDone = receiptOffer;
      }
    }
    return;
  }
  if (result.status === 409 && result.body.error === "reader_cart_context_changed") {
    await recoverReaderCartContext(scope, submissionContext, submissionOpeningToken);
    return;
  }
  if (handlePinChangeRequiredOnPayClose(result.body.error)) {
    return;
  }
  payStatusEl.className = "pay-status error";
  payTipSplitEl.hidden = true;
  if (result.networkError) {
    // The request never reached the server, so unlike a normal 4xx/5xx
    // (where the server has definitely answered "no") there is no way to
    // know from here whether the charge went through before the
    // connection dropped. Retrying blind risks a duplicate charge, so
    // this gets its own distinct, more cautious message instead of the
    // ordinary "could not X" toast every other failure uses, and Pay/
    // Confirm stay disabled (set above) rather than re-enabling for that
    // blind retry.
    // PAY12 (docs/build-briefs/PAY12-network-error-double-message.md):
    // same fix shape as the split-collect branch above, PAY11's own
    // "drop the redundant toast, keep the rail" pattern. payStatusEl is
    // already visible (:15293), so the toast added nothing but overlap
    // risk.
    const uncertainMessage = "Payment may not have gone through. Check before retrying.";
    payStatusEl.textContent = uncertainMessage;
    // PAYFIX1: the next move is Check payment, which asks the server under this payment's own key.
    markPayUncertain(state.orderId, target);
    if (!isCash) offerUncertainCancel(target);
    return;
  }
  // A definitive (non-networkError) failure: the server has answered.
  // Architect gate correction, 2026-08-25 (Finding 3): that answer is only
  // safe to treat as "no, nothing charged, try again fresh" when it
  // actually was. Before this fix, EVERY definitive failure cleared the
  // key unconditionally, reasoning "the server has answered, so it did
  // not charge" -- true for an ordinary business-rule rejection (the
  // ticket's lines changed underneath this attempt, nothing left unpaid,
  // the ticket already closed: src/domain/payments.ts's payOrder only
  // ever throws those BEFORE any adapter call), false for a bare 500,
  // which is exactly what a post-capture SQL rollback inside payOrder's
  // own transaction produces (the provider may have genuinely captured
  // money that never made it into a durable payment_attempts row).
  // shouldClearPayIdempotencyKeyOnFailure (./api.ts) draws that line;
  // when it says keep the key, the next Pay attempt replays the SAME
  // domain idempotency key, so a retry dedupes at the adapter
  // (test/payments-adapter-boundary.test.ts's own rolled-back-domain-write
  // proof) instead of minting a fresh key that could genuinely charge a
  // second time.
  if (shouldClearPayIdempotencyKeyOnFailure(result)) {
    clearPayIdempotencyKey(target);
  }
  renderTicketActions();
  const translated = translatePayCloseError("pay", result.body, result.status);
  state.lastPayCloseErrorDetail = translated.detail;
  renderLastPayCloseErrorDebug();
  payStatusEl.textContent = translated.plain;
  // TERMREADER1: a reader that is gone or offline asks which one to use instead of ending here.
  if (!isCash && askForReaderAfterRefusal(result.body.error, target)) return;
  // PAY14 (D8): the one refusal presented in the plain pay-status style,
  // since nothing went wrong (a test hold was cleared); the retry button
  // belongs to the split flow and stays hidden for it.
  if (translated.presentation === "plain") {
    payStatusEl.className = "pay-status";
    payStatusRetryButton.hidden = true;
  }
  // PAY14 (D2 required change 3, the Q3 note): the badge follows the
  // refusal on the same screen.
  applyHoldRefusalToBadge(result.body.error);
  // CL6-F1-F3: live money waits for a confirmed time zone and cutoff.
  maybeShowBusinessDaySheet(result.body.error);
  // PAY11: same guard as collectShare's own failure branch above. The one
  // real-world case this matters for, m13's own live proof: a revoked
  // session's 401 signs the terminal out and switches the screen away from
  // this rail box before this code runs, so authentication_required keeps
  // its toast; every other code drops it, since the rail box right here is
  // still on screen to carry the message alone.
  if (shouldShowPayCloseRefusalToast(result.body)) {
    showToast(translated.plain, "danger");
  }
}

// CL2, owner ruling 2026-08-28: "when the balance reaches zero the
// ticket should" close itself. Every command that can settle a ticket
// (pay, collect a share, apply a Walkout) comes back with a
// settlementClose block when the server closed it, and every one of them
// ends here: the floor is told once, in the same words a manual Close
// used to produce, and goes back to Open Tabs.
//
// Kept as one function rather than repeated at each call site precisely
// because it is the moment a ticket leaves the screen, and three
// slightly-different versions of "leave the screen" is how a tab gets
// stranded on a tablet.
function finishClosedTicket(): void {
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  // PAY7 Section 2.9: before resetTicketState() below clears
  // state.guests/state.activeSplitPlan, so every pending key for this
  // order (whole, guest, and share targets alike) is swept while they are
  // still populated.
  clearPayIdempotencyKeysForOrder(orderId);
  clearPayUncertain(orderId);
  // PAY13 Section 2.2/2.9: the identical sweep for this order's own hold
  // key, at the same choke point every settling/closing path (pay,
  // collectShare, Walkout, explicit Close) already funnels through -- a
  // hold that was captured as part of this close, or was simply never
  // retried again, must not leave a stale pending key behind.
  const tenantIdForHoldKey = state.session?.tenantId;
  if (tenantIdForHoldKey !== undefined) {
    clearHoldIdempotencyKey(tenantIdForHoldKey, orderId);
  }
  const closedLabel = tabDisplayLabel(
    state.orderName ?? null,
    state.guests.map((guest) => guest.label),
  );
  state.recentlyClosedTabs = [
    {
      orderId,
      label: closedLabel,
      closedAt: Date.now(),
      ...(state.session === undefined ? {} : { closedBy: state.session.employeeId }),
      ...(state.capturedPaymentsSummary === undefined
        ? {}
        : {
            totalCents:
              state.capturedPaymentsSummary.paidCents + state.capturedPaymentsSummary.tipCents,
            ...(state.capturedPaymentsSummary.tipCents > 0
              ? { tipCents: state.capturedPaymentsSummary.tipCents }
              : {}),
          }),
    },
    ...state.recentlyClosedTabs,
  ].slice(0, 5);
  resetTicketState();
  refreshTileDisabledState();
  showOpenTabsScreen();
  // UX102: a short, non-blocking toast on Open Tabs naming the tab.
  showToast(`${closedLabel} closed`, "success");
  void loadOpenTabs();
}

/** True when the server reports it closed the ticket on the back of the
 * command that just succeeded. */
function settledAndClosed(settlementClose: { kind: string } | undefined): boolean {
  return settlementClose?.kind === "closed";
}

// Design/QA gate HIGH correction, 2026-08-21: the actual Close command,
// split out of handleCloseClick.
//
// CL2, 2026-08-28: this is now the EXPLICIT "I am done" path only. In
// ordinary service nobody reaches it, because the ticket closes itself
// the moment the balance hits zero. It still exists, and it is still
// wired to the Close Ticket button, for the three cases auto-close
// deliberately does not cover: a ticket that was REOPENED and needs
// shutting again with no money moving, a ticket nothing was ever rung on,
// and a ticket a comp settled that the guest chose not to tip on. It also
// carries the CL1 refusal, which is the message a bartender who taps
// Close on a ticket that still owes money has to see.
async function performClose(): Promise<void> {
  const session = requireSession();
  if (state.orderId === undefined) {
    return;
  }
  const orderId = state.orderId;
  const result = await closeOrder(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (result.status !== 200 && result.status !== 201) {
    if (handlePinChangeRequiredOnPayClose(result.body.error)) {
      return;
    }
    const translated = translatePayCloseError("close", result.body, result.status);
    state.lastPayCloseErrorDetail = translated.detail;
    renderLastPayCloseErrorDebug();
    // PAY11 Finding A: this branch never used to touch payStatusEl at all,
    // so the toast below was this refusal's ONLY visible message. Reached
    // only from #zero-due-close (handleZeroDueCloseClick above hides the
    // zero-due prompt before calling this), so the rail is the one thing
    // left on screen to carry the message once the toast is no longer
    // unconditional -- fill it first, the same three assignments sites 1
    // and 2 already make, before deciding whether the toast also fires.
    payStatusEl.hidden = false;
    payStatusEl.className = "pay-status error";
    payStatusEl.textContent = translated.plain;
    if (shouldShowPayCloseRefusalToast(result.body)) {
      showToast(translated.plain, "danger");
    }
    return;
  }
  finishClosedTicket();
}

// CL1, owner ruling 2026-08-28. This used to route a Close on a ticket
// with an active, partially-collected split plan through a "N of M shares
// collected; $X not yet collected. Close anyway?" confirm sheet, because
// the server allowed that close on an earlier product-owner ruling. That
// ruling is SUPERSEDED (docs/solution-architecture.md records it as
// such): an uncollected share is an outstanding balance, so there is no
// "anyway" left to offer and a sheet promising one would be a lie the
// floor acts on. The ticket simply stays open until the rest is
// collected, discounted or walked out, and a Close tapped meanwhile
// returns the CL1 refusal with the figure in it.
//
// UX13 removed the persistent Close Ticket button that used to gate this
// on its own `.disabled` state; this is now reached ONLY from
// #zero-due-close (handleZeroDueCloseClick), which only ever shows once
// this ticket's balance is already zero, so the one guard this function
// still needs is that a ticket is actually loaded.
function handleCloseClick(): void {
  if (state.orderId === undefined) {
    return;
  }
  void performClose();
}

// ---------------------------------------------------------------------
// SIG1 (docs/build-briefs/SIG1-signature-and-receipt.md): touch-drawn
// signature capture plus DEC-016's printed/emailed/none receipt choice.
//
// Fired at the moment a comp/Walkout-settled ticket closes via the
// no-tip action (DEC-016's own "prompt at closeout"; see
// performResolveNoTip below), which is also exactly when the order's tip
// question is guaranteed already answered -- the server's own
// TipUnresolvedError gate (src/domain/signatures.ts,
// src/domain/receipts.ts) can never fire from this path, by construction,
// the same way CL1's own close gate cannot. The card-payment closeout
// path (the zero-due-prompt "Back to Open Tabs" tap) is NOT wired here
// this slice: seven existing e2e specs assert on that exact path's
// immediate navigation, and updating all seven belongs to a slice that
// budgets for it deliberately rather than as a side effect of this one
// (named in this item's own build brief and docs/backlog.md's SIG2 row).
//
// Deliberately independent of ticket-rail `state`: by the time this sheet
// opens, performResolveNoTip has already called finishClosedTicket, which
// resets state.orderId. This module keeps its own small, self-contained
// pending-order reference instead.
// ---------------------------------------------------------------------

let pendingSignatureReceiptOrderId: string | undefined;
/** RCPT2-F2: the card payment a receipt choice is for, set when the sheet opens after a card payment closed the ticket. */
let pendingReceiptPaymentAttemptId: string | undefined;
/** REFUNDRECEIPT1: set while the receipt choices are for a refund; the choice then names it. */
let pendingReceiptRefundId: string | undefined;
/** REFUNDRECEIPT1: the refunds of one Refund ticket still waiting for their receipt choice (one per refunded payment). */
let refundReceiptQueue: ReceiptOfferAfterCard[] = [];
let signatureStrokes: SignatureStrokeDto[] = [];
let currentSignatureStroke: { x: number; y: number }[] | undefined;

function signatureCanvasContext(): CanvasRenderingContext2D {
  const ctx = signatureCanvas.getContext("2d");
  if (ctx === null) {
    throw new Error("signature canvas has no 2d context");
  }
  return ctx;
}

/** Normalizes a pointer event's position to the canvas's own [0, 1] box,
 * per src/domain/signatures.ts's own storage-shape rationale (resolution-
 * independent replay). */
function normalizedSignaturePoint(event: PointerEvent): { x: number; y: number } {
  const rect = signatureCanvas.getBoundingClientRect();
  const x = rect.width === 0 ? 0 : (event.clientX - rect.left) / rect.width;
  const y = rect.height === 0 ? 0 : (event.clientY - rect.top) / rect.height;
  return { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) };
}

function redrawSignatureCanvas(): void {
  const ctx = signatureCanvasContext();
  ctx.clearRect(0, 0, signatureCanvas.width, signatureCanvas.height);
  ctx.strokeStyle = "#1a1a1a";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const stroke of signatureStrokes) {
    if (stroke.points.length === 0) {
      continue;
    }
    ctx.beginPath();
    stroke.points.forEach((point, index) => {
      const px = point.x * signatureCanvas.width;
      const py = point.y * signatureCanvas.height;
      if (index === 0) {
        ctx.moveTo(px, py);
      } else {
        ctx.lineTo(px, py);
      }
    });
    ctx.stroke();
  }
}

function updateSignatureContinueButtonState(): void {
  signatureContinueButton.disabled = signatureStrokes.every((stroke) => stroke.points.length === 0);
}

function handleSignaturePointerDown(event: PointerEvent): void {
  event.preventDefault();
  signatureCanvas.setPointerCapture(event.pointerId);
  currentSignatureStroke = [normalizedSignaturePoint(event)];
}

function handleSignaturePointerMove(event: PointerEvent): void {
  if (currentSignatureStroke === undefined) {
    return;
  }
  event.preventDefault();
  currentSignatureStroke.push(normalizedSignaturePoint(event));
  redrawSignatureCanvas();
  // Draw the in-progress stroke too, not only committed ones.
  const ctx = signatureCanvasContext();
  ctx.strokeStyle = "#1a1a1a";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  currentSignatureStroke.forEach((point, index) => {
    const px = point.x * signatureCanvas.width;
    const py = point.y * signatureCanvas.height;
    if (index === 0) {
      ctx.moveTo(px, py);
    } else {
      ctx.lineTo(px, py);
    }
  });
  ctx.stroke();
}

function finishSignatureStroke(event: PointerEvent): void {
  if (currentSignatureStroke === undefined) {
    return;
  }
  event.preventDefault();
  if (currentSignatureStroke.length > 0) {
    signatureStrokes.push({ points: currentSignatureStroke });
  }
  currentSignatureStroke = undefined;
  updateSignatureContinueButtonState();
}

function handleSignatureClear(): void {
  signatureStrokes = [];
  currentSignatureStroke = undefined;
  redrawSignatureCanvas();
  updateSignatureContinueButtonState();
}

/** DEC-016's own "prompt at closeout." Shown once a ticket has just
 * closed with a real balance history behind it (a payment or an explicit
 * no-tip resolution, never an empty or reopened-then-closed ticket with
 * nothing to evidence -- callers only invoke this from those two
 * specific moments, never unconditionally from finishClosedTicket). */
/** RCPT2: the choices the ticket's location offers, read when the sheet opens so the choice step draws only those. */
let pendingReceiptOptions: Promise<ReceiptOptionsDto | undefined> | undefined;
/** RCPT2-F5: the masked address the Emailed choice uses without asking, when the ticket's payer is a known client with one on file. */
let knownClientMasked: string | undefined;
let receiptChoiceEpoch = 0;
let pendingReceiptChoiceRequest: {fingerprint:string;key:string} | undefined;

function applyReceiptOptions(options: ReceiptOptionsDto | undefined): void {
  clientReceiptEmail.apply(options);
  const offered =
    options === undefined
      ? undefined
      : new Set<string>(options.choices.map((choice) => choice.key));
  const buttons: Array<[string, HTMLButtonElement]> = [
    ["printed", receiptChoicePrintedButton],
    ["emailed", receiptChoiceEmailedButton],
    ["none", receiptChoiceNoneButton],
  ];
  for (const [key, button] of buttons) {
    // With no answer every choice is drawn: the server still refuses one the location does not offer.
    button.hidden = offered !== undefined && !offered.has(key);
    button.className =
      options?.defaultChoice === key ? "btn-pay btn-full" : "btn-secondary btn-full";
  }
  const noneLabel = options?.choices.find((choice) => choice.key === "none")?.label;
  receiptChoiceNoneButton.textContent = noneLabel ?? "No receipt";
  // RCPT2-F5: a known client's address is shown, masked, on the Emailed choice itself and never asked for.
  knownClientMasked = options?.knownClientEmail?.masked;
  receiptChoiceEmailedButton.textContent =
    knownClientMasked === undefined ? "Emailed" : `Emailed to ${knownClientMasked}`;
}

/** HIGFIX1-F2 (F-50): the choice step is titled for what it asks. LOUNGERMAJ1 (lounger-07): after a payment the title says
 * the payment went through and for how much, and there is no Skip: "No receipt" is the one way to say none. */
function showReceiptChoiceStep(paidTitle?: string): void {
  receiptChoiceStepEl.hidden = false;
  signatureReceiptTitleEl.textContent = paidTitle ?? "Receipt";
  signatureReceiptCloseButton.hidden = true;
}

/** HIGFIX1-F2 (F-51): the Emailed choice reads as chosen once the address field is open. */
function markEmailedChoice(chosen: boolean): void {
  receiptChoiceEmailedButton.setAttribute("aria-pressed", chosen ? "true" : "false");
}

function openSignatureReceiptSheet(orderId: string, paymentAttemptId?: string, forRefund = false): void {
  receiptChoiceEpoch += 1;
  pendingReceiptChoiceRequest = undefined;
  clientReceiptEmail.reset();
  pendingSignatureReceiptOrderId = orderId;
  knownClientMasked = undefined;
  receiptChoiceEmailedButton.textContent = "Emailed";
  markEmailedChoice(false);
  signatureReceiptTitleEl.textContent = "Sign to finish";
  signatureReceiptCloseButton.hidden = false;
  const session = requireSession();
  pendingReceiptOptions = getReceiptOptions(
    session.apiBaseUrl,
    session.tenantId,
    orderId,
    paymentAttemptId,
    forRefund,
  ).then(
    (result) =>
      result.status === 200 && Array.isArray(result.body.choices) ? result.body : undefined,
    () => undefined,
  );
  signatureStrokes = [];
  currentSignatureStroke = undefined;
  redrawSignatureCanvas();
  updateSignatureContinueButtonState();
  signatureStepEl.hidden = false;
  receiptChoiceStepEl.hidden = true;
  receiptEmailRow.hidden = true;
  receiptEmailInput.value = "";
  receiptEmailSendButton.disabled = true;
  signatureReceiptScrim.hidden = false;
}

function closeSignatureReceiptSheet(keepRefundReceiptQueue = false): void {
  if (!keepRefundReceiptQueue) refundReceiptQueue = [];
  pendingReceiptRefundId = undefined;
  receiptChoiceEpoch += 1;
  pendingReceiptChoiceRequest = undefined;
  clientReceiptEmail.reset();
  signatureReceiptScrim.hidden = true;
  pendingSignatureReceiptOrderId = undefined;
  pendingReceiptPaymentAttemptId = undefined;
}

/** RCPT2-F2: what a card payment that closed its ticket leaves to offer once the floor is back on Open Tabs. */
interface ReceiptOfferAfterCard {
  orderId: string;
  paymentAttemptId: string;
  /** "Paid $31.81 (tip $5.00)": what the receipt sheet is titled, so the lounger sees the payment went through. */
  paidTitle: string;
  /** REFUNDRECEIPT1: set when the receipt is for this Succeeded refund of the payment. */
  refundId?: string;
}

/** The offer for a payment, or nothing: only a captured card payment whose settlement closed the ticket is offered a receipt. */
function receiptOfferForClosedCardPayment(
  orderId: string | undefined,
  attempt: PaymentAttemptDto,
  settlementClose: { kind: string } | undefined,
): ReceiptOfferAfterCard | undefined {
  if (
    orderId === undefined ||
    attempt.status !== "Captured" ||
    attempt.tender === "cash" ||
    !settledAndClosed(settlementClose)
  ) {
    return undefined;
  }
  return {
    orderId,
    paymentAttemptId: attempt.id,
    paidTitle: receiptPaidTitle(attempt.amount, attempt.tip_cents),
  };
}

/** RCPT2-F2: the same receipt choice the no-tip close offers, opened straight at the choice (the card reader already took
 * the card, so there is no signature step), for the payment that just closed the ticket. The choices drawn are the location's. */
function openReceiptChoiceAfterCard(offer: ReceiptOfferAfterCard): void {
  openSignatureReceiptSheet(offer.orderId, offer.paymentAttemptId, offer.refundId !== undefined);
  pendingReceiptPaymentAttemptId = offer.paymentAttemptId;
  pendingReceiptRefundId = offer.refundId;
  // The choice is drawn once the location's options are in, so Printed never flashes at an Email only location.
  signatureStepEl.hidden = true;
  receiptChoiceStepEl.hidden = true;
  void pendingReceiptOptions?.then((options) => {
    if (pendingSignatureReceiptOrderId !== offer.orderId) {
      return;
    }
    applyReceiptOptions(options);
    showReceiptChoiceStep(offer.paidTitle);
  });
}

/** REFUNDRECEIPT1: after a refund is Succeeded, the same receipt choices a payment gets, titled for what came back. Several
 * refunds (one per refunded payment of a Refund ticket) are asked in turn. */
function offerRefundReceipts(
  refunds: Array<{ id: string; orderId: string; paymentAttemptId: string; returnedCents: number }>,
): void {
  const offers = refunds.map((refund) => ({
    orderId: refund.orderId,
    paymentAttemptId: refund.paymentAttemptId,
    refundId: refund.id,
    paidTitle: `Refunded ${formatCents(refund.returnedCents)}`,
  }));
  const first = offers.shift();
  if (first === undefined) return;
  refundReceiptQueue = offers;
  openReceiptChoiceAfterCard(first);
}

/** Returns the floor to Open Tabs, then offers the receipt choice when a card payment closed the ticket. */
function finishClosedTicketAndOfferReceipt(offer: ReceiptOfferAfterCard | undefined): void {
  finishClosedTicket();
  if (offer !== undefined) {
    openReceiptChoiceAfterCard(offer);
  }
}

async function handleSignatureContinueClick(): Promise<void> {
  const session = requireSession();
  const orderId = pendingSignatureReceiptOrderId;
  if (orderId === undefined || signatureContinueButton.disabled) {
    return;
  }
  const result = await captureSignature(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
    strokes: signatureStrokes,
  });
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      typeof result.body.message === "string"
        ? result.body.message
        : "Could not save the signature.",
      "danger",
    );
    return;
  }
  applyReceiptOptions(await pendingReceiptOptions);
  signatureStepEl.hidden = true;
  showReceiptChoiceStep();
}

async function submitReceiptChoice(
  deliveryMethod: ReceiptDeliveryMethodDto,
  recipientEmail: string | undefined,
): Promise<void> {
  const session = requireSession();
  const orderId = pendingSignatureReceiptOrderId;
  if (orderId === undefined) {
    return;
  }
  const epoch = receiptChoiceEpoch;
  const paymentId = pendingReceiptPaymentAttemptId;
  const refundId = pendingReceiptRefundId;
  const preference: ReturnType<typeof clientReceiptEmail.preference> = deliveryMethod === "emailed" && recipientEmail !== undefined ? clientReceiptEmail.preference() : {};
  const payload = {
    ...(paymentId === undefined ? {} : {paymentAttemptId:paymentId}),
    ...(refundId === undefined ? {} : {refundId}),deliveryMethod,recipientEmail,...preference,
  };
  const fingerprint = JSON.stringify([orderId,payload]);
  if(pendingReceiptChoiceRequest === undefined || pendingReceiptChoiceRequest.fingerprint !== fingerprint)pendingReceiptChoiceRequest = {fingerprint,key:crypto.randomUUID()};
  let result: Awaited<ReturnType<typeof recordReceiptChoice>>;
  try {
    result = await recordReceiptChoice(session.apiBaseUrl, session.tenantId, orderId, {
      ...payload,idempotencyKey:pendingReceiptChoiceRequest.key,
    });
  } catch {
    if(epoch === receiptChoiceEpoch && state.session?.token === session.token)showToast("Could not record the receipt choice. Try again.","danger");
    return;
  }
  if(epoch !== receiptChoiceEpoch || state.session?.token !== session.token || pendingSignatureReceiptOrderId !== orderId)return;
  if (result.status !== 200 && result.status !== 201) {
    if(preference.saveClientEmail === true && result.status === 409) {
      await clientReceiptEmail.refresh().catch(() => {});
      if(epoch !== receiptChoiceEpoch || state.session?.token !== session.token)return;
    }
    showToast(
      typeof result.body.message === "string"
        ? result.body.message
        : "Could not record the receipt choice.",
      "danger",
    );
    if (deliveryMethod === "emailed" && recipientEmail === undefined) {
      // The known address could not be used after all: fall back to asking for one.
      knownClientMasked = undefined;
      receiptChoiceEmailedButton.textContent = "Emailed";
      markEmailedChoice(true);
      receiptEmailRow.hidden = false;
    }
    return;
  }
  closeSignatureReceiptSheet(true);
  // REFUNDRECEIPT1: a Refund ticket that returned money to several payments asks for each refund's receipt in turn.
  const nextRefundReceipt = refundReceiptQueue.shift();
  if (nextRefundReceipt !== undefined) openReceiptChoiceAfterCard(nextRefundReceipt);
  // RCPT2: the closed tab's Receipt row now has a receipt to show.
  renderRecentlyClosed();
  // RCPT2: an emailed choice is sent before this answer, so the toast says what the provider did, never "sent" early.
  const emailStatus = result.body.receipt?.delivery_status;
  if (deliveryMethod === "none") {
    showToast("No receipt requested.", "success");
  } else if (deliveryMethod === "emailed" && emailStatus === "sent") {
    showToast("Receipt emailed.", "success");
  } else if (deliveryMethod === "emailed" && emailStatus === "failed") {
    showToast("Email not sent. A manager can resend.", "danger");
  } else {
    showToast("Sent to the printer.", "success");
  }
}

function handleReceiptChoicePrintedClick(): void {
  void submitReceiptChoice("printed", undefined);
}

function handleReceiptChoiceNoneClick(): void {
  void submitReceiptChoice("none", undefined);
}

/** The email choice is two taps, not one: Emailed reveals the address
 * field first (free text is legitimate here, per CLAUDE.md's own
 * touch-only carve-out for a value that is not a fixed vocabulary), Send
 * submits it. Printed and None submit immediately since neither needs
 * anything typed. */
function handleReceiptChoiceEmailedClick(): void {
  if (knownClientMasked !== undefined) {
    // RCPT2-F5: the address is the server's to resolve, so nothing is sent from here but the choice.
    void submitReceiptChoice("emailed", undefined);
    return;
  }
  markEmailedChoice(true);
  receiptEmailRow.hidden = false;
  receiptEmailInput.focus();
}

function updateReceiptEmailSendButtonState(): void {
  receiptEmailSendButton.disabled = receiptEmailInput.value.trim().length === 0;
}

function handleReceiptEmailSendClick(): void {
  const email = receiptEmailInput.value.trim();
  if (email.length === 0) {
    return;
  }
  void submitReceiptChoice("emailed", email);
}

// A3 (docs/decisions-awaiting-owner.md, ANSWERED 2026-08-29). The floor's
// explicit no-tip action: a tap, never a typed amount, per this
// codebase's own money rule. renderTicketActions only shows the button
// when Pay would charge exactly $0.00, so a real tap here always targets
// a genuinely zero-balance, tip-unaddressed ticket; the server refuses
// (409, OrderBalanceOutstandingError) if that changed underneath by the
// time this request lands, translated the same plain-language way every
// other Pay/Close refusal already is.
async function performResolveNoTip(): Promise<void> {
  const session = requireSession();
  if (state.orderId === undefined) {
    return;
  }
  const orderId = state.orderId;
  const result = await resolveNoTip(session.apiBaseUrl, session.tenantId, orderId, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (result.status !== 200 && result.status !== 201) {
    if (handlePinChangeRequiredOnPayClose(result.body.error)) {
      return;
    }
    const translated = translatePayCloseError("close", result.body, result.status);
    state.lastPayCloseErrorDetail = translated.detail;
    renderLastPayCloseErrorDebug();
    // PAY11 Finding B: same defect as performClose above -- this branch
    // never used to touch payStatusEl, even though #no-tip-button and
    // #pay-status are plain siblings in the same rail column with no
    // sheet between them. Fill the rail first, then apply the same shared
    // authentication_required guard the other three sites use.
    payStatusEl.hidden = false;
    payStatusEl.className = "pay-status error";
    payStatusEl.textContent = translated.plain;
    if (shouldShowPayCloseRefusalToast(result.body)) {
      showToast(translated.plain, "danger");
    }
    return;
  }
  if (settledAndClosed(result.body.settlementClose)) {
    finishClosedTicket();
    // SIG1: DEC-016's own "prompt at closeout," fired right after the
    // ticket the floor was just looking at actually closed. orderId was
    // captured above, before finishClosedTicket reset state.orderId.
    openSignatureReceiptSheet(orderId);
    return;
  }
  // The resolution recorded, but something else still blocks the close
  // (an unprepared line landed after this render, an unresolved Pending
  // payment): refresh the ticket rather than assuming closed. The same
  // "never silently do nothing" discipline the pay-status retry path
  // already follows.
  showToast("No tip recorded.", "success");
  await refreshPaymentPreview();
}

function handleNoTipClick(): void {
  if (state.orderId === undefined || noTipButton.disabled || noTipButton.hidden) {
    return;
  }
  void performResolveNoTip();
}

// ---------------------------------------------------------------------
// Auth track (track/auth), Slice 1: terminal setup, the user-tile picker,
// the shared PIN pad (login and the three-step change-PIN flow), and My
// Profile. See index.html's own header comment on the three screens this
// replaced the old free-text #login-screen with, and session.ts's own
// header on why TerminalConfig (this device's server/tenant) is a
// separate, longer-lived value than Session (the logged-in user).
// ---------------------------------------------------------------------

// T16/DEC-133, corrected by PR #54 review: the pad used to hardcode 4-6 for
// every mode, which locked out an account the moment a tenant configured a
// max above 6 (the owner's own report from live stage0). The FIRST fix
// applied the tenant's resolved policy (state.session.pinMinLength/
// pinMaxLength) to every post-login mode, which is right for choosing a NEW
// PIN but wrong for VERIFYING an existing one: an existing PIN's hash is
// never migrated or invalidated when a tenant later tightens its policy
// (verifyPin/verifyApproverPin, src/domain/users.ts, both just compare
// against the stored hash with no length check of their own), so a 6-digit
// PIN chosen under an old policy still authenticates fine after the tenant
// raises its minimum to 8 -- unless this pad refuses to let the digits even
// be typed, which is exactly the lockout this correction removes.
//
// The rule is now about what the step DOES, not which mode it is in:
//   - Choosing a NEW credential (changePin's "new"/"confirm" steps) uses
//     the tenant's configured policy, so the new PIN actually satisfies it.
//   - Verifying an EXISTING credential (changePin's "current" step,
//     "approval" mode, and "login", which runs before any session exists to
//     read a policy from) uses this fixed GLOBAL bound instead, matching the
//     server's own outer range (validatePinLengths, tenantSettings.ts:
//     "must be a whole number from 4 to 12") within which every PIN was
//     necessarily chosen (assertValidChosenPin enforces the then-current
//     tenant policy at creation time, and that policy is itself always
//     inside 4-12), so the global bound never rejects a PIN the server
//     would accept and never truncates one the server would reject anyway.
const GLOBAL_MIN_PIN_LENGTH = 4;
const GLOBAL_MAX_PIN_LENGTH = 12;

/** True only for the two changePin steps that are choosing a NEW PIN. Every
 * other pad state (login, approval, and changePin's own "current" step) is
 * verifying a PIN that already exists, and must use the global bound: see
 * the GLOBAL_MIN_PIN_LENGTH/GLOBAL_MAX_PIN_LENGTH comment above. */
function isSettingNewPin(): boolean {
  return pinEntry?.mode === "changePin" && (pinEntry.step === "new" || pinEntry.step === "confirm");
}

/** The accepted PIN length for whatever step the pad is currently on. */
function currentPinMinLength(): number {
  if (isSettingNewPin()) {
    return state.session?.pinMinLength ?? GLOBAL_MIN_PIN_LENGTH;
  }
  return GLOBAL_MIN_PIN_LENGTH;
}

function currentPinMaxLength(): number {
  if (isSettingNewPin()) {
    return state.session?.pinMaxLength ?? GLOBAL_MAX_PIN_LENGTH;
  }
  return GLOBAL_MAX_PIN_LENGTH;
}

/** Where a PIN-entry flow lands once it finishes (or is canceled).
 * "open-tabs" covers both an ordinary login and the mandatory forced
 * change-PIN that can follow it; "profile" covers only the voluntary
 * My-Profile change, which returns to the screen that started it. */
// VER33-F1 (docs/build-briefs/VER33-F1-pin-change-required-on-pay-close.md):
// "pay-ticket" is used only by handlePinChangeRequiredOnPayClose below,
// always with mandatory: true, so handlePinCancelClick's own switch never
// actually reaches it in practice (Cancel is hidden for every mandatory
// change); its own else branch is still the correct fallback if that ever
// changed. submitChangePinStep's own success branch gets a real, new arm
// for it (below).
type PinEntryReturnTarget = "open-tabs" | "profile" | "pay-ticket";

interface PinEntryFlowState {
  mode: "login" | "changePin" | "approval";
  digits: string;
  /** True once a login attempt has come back AccountLockedError (423):
   * the pad stops accepting more digits, since no attempt can succeed
   * again until either the server's own lockout window passes or a
   * manager unlocks this user from Settings > Users (see
   * handlePinEntryFailure). */
  locked: boolean;
  // Login mode only.
  userId?: string;
  // Change-PIN mode only.
  step?: "current" | "new" | "confirm";
  currentPin?: string;
  newPin?: string;
  returnTo?: PinEntryReturnTarget;
  // RBAC track, approval mode only: the manager tile picked in step 1
  // (this file's own "Manager approval" section, below). Reuses `userId`
  // above for the same "whose PIN is this" meaning login mode already
  // gives it, rather than adding a second field with an identical
  // purpose.
}

let pinEntry: PinEntryFlowState | undefined;

interface OpenPinEntryLoginConfig {
  mode: "login";
  userId: string;
  displayName: string;
}

interface OpenPinEntryChangeConfig {
  mode: "changePin";
  returnTo: PinEntryReturnTarget;
  /** Judgment call, documented per the work item's own instruction: a
   * FORCED change immediately after a fresh login (mustChangePin true at
   * that moment) skips the "current PIN" step entirely and starts on
   * "new PIN", reusing the PIN the user just typed to log in successfully
   * rather than asking them to retype a PIN they entered three seconds
   * ago. Set only for that one path; every other caller, including the
   * voluntary My-Profile change AND the reload-mid-forced-change path
   * below, starts on "current" with this undefined, since neither has a
   * PIN sitting in memory to reuse. */
  knownCurrentPin?: string;
  /** Reload-during-forced-PIN-change fix: resumeFromStoredConfig's own
   * mustChangePin branch reopens this screen after a page reload, with no
   * knownCurrentPin (the user's PIN was never typed this page-load), but
   * the change is still mandatory, so #pin-cancel must stay hidden exactly
   * like the knownCurrentPin path (there is nothing to cancel back to: the
   * account still needs a real PIN before Open Tabs is reachable at all).
   * Independent of knownCurrentPin/startsOnNew on purpose: this is the
   * only caller that is both mandatory AND starts on "current". Every
   * other caller leaves this undefined and gets the ordinary
   * startsOnNew-only rule. */
  mandatory?: boolean;
}

// RBAC track: step 2 of the manager-approval flow, opened only from
// openApprovalPicker below (never directly) once a tile has been picked.
interface OpenPinEntryApprovalConfig {
  mode: "approval";
  approverId: string;
  approverName: string;
  /** M5. See ActiveApprovalFlow.pinContext. */
  context?: string;
}

type OpenPinEntryConfig =
  OpenPinEntryLoginConfig | OpenPinEntryChangeConfig | OpenPinEntryApprovalConfig;

function setPinDigitsEnabled(enabled: boolean): void {
  for (const button of pinDigitButtons) {
    button.disabled = !enabled;
  }
  pinClearButton.disabled = !enabled;
  pinBackspaceButton.disabled = !enabled;
}

function renderPinEntryDisplay(): void {
  const digits = pinEntry?.digits ?? "";
  // Dots only, never the digits themselves: #pin-entry-screen is a
  // shared terminal (CLAUDE.md touch-only rule's own spirit extended to
  // "nothing shoulder-surfable either").
  pinEntryDisplayEl.textContent = digits.length === 0 ? "" : "•".repeat(digits.length);
  pinSubmitButton.disabled = digits.length < currentPinMinLength() || (pinEntry?.locked ?? false);
}

function updateChangePinCopy(): void {
  if (pinEntry === undefined || pinEntry.mode !== "changePin") {
    return;
  }
  if (pinEntry.step === "current") {
    pinEntryTitleEl.textContent = "Change your PIN";
    pinEntrySubtitleEl.textContent = "Enter your current PIN";
  } else if (pinEntry.step === "new") {
    pinEntryTitleEl.textContent = "Choose a new PIN";
    pinEntrySubtitleEl.textContent = pinLengthRangeText(
      currentPinMinLength(),
      currentPinMaxLength(),
    );
  } else {
    pinEntryTitleEl.textContent = "Confirm your new PIN";
    pinEntrySubtitleEl.textContent = "Enter it once more";
  }
}

function openPinEntry(config: OpenPinEntryConfig): void {
  pinEntryErrorEl.hidden = true;
  pinEntryErrorEl.textContent = "";
  // M5: the context line belongs to the approval PIN pad only.
  const approvalContext = config.mode === "approval" ? config.context : undefined;
  pinEntryContextEl.hidden = approvalContext === undefined;
  pinEntryContextEl.textContent = approvalContext ?? "";
  setPinDigitsEnabled(true);
  if (config.mode === "login") {
    // UX10: a wrong tile tap needs a way back to the picker, so Cancel is
    // shown here too, relabeled "Back" since it returns to a full screen
    // rather than dismissing a step (see handlePinCancelClick's own login
    // branch).
    pinEntry = { mode: "login", digits: "", locked: false, userId: config.userId };
    pinEntryTitleEl.textContent = config.displayName;
    pinEntrySubtitleEl.textContent = "Enter your PIN";
    pinCancelButton.textContent = "Back";
    pinCancelButton.hidden = false;
  } else if (config.mode === "approval") {
    // RBAC track: step 2, opened only from openApprovalPicker below.
    // Cancel here goes back to the picker (handlePinCancelClick), not out
    // of the whole approval flow, so it is always shown.
    pinEntry = { mode: "approval", digits: "", locked: false, userId: config.approverId };
    pinEntryTitleEl.textContent = config.approverName;
    pinEntrySubtitleEl.textContent = "Enter your PIN to approve";
    pinCancelButton.textContent = "Cancel";
    pinCancelButton.hidden = false;
  } else {
    const startsOnNew = config.knownCurrentPin !== undefined;
    pinEntry = {
      mode: "changePin",
      digits: "",
      locked: false,
      step: startsOnNew ? "new" : "current",
      currentPin: config.knownCurrentPin,
      returnTo: config.returnTo,
    };
    // Hidden for either mandatory path (knownCurrentPin set, or the
    // reload-mid-forced-change `mandatory` flag), shown only for the
    // voluntary My-Profile change. See this function's own config doc
    // comments on both fields for why they are checked independently.
    pinCancelButton.textContent = "Cancel";
    pinCancelButton.hidden = startsOnNew || config.mandatory === true;
    updateChangePinCopy();
  }
  renderPinEntryDisplay();
  showPinEntryScreen();
}

// VER33-F1 (docs/build-briefs/VER33-F1-pin-change-required-on-pay-close.md).
// requireAuthenticatedUser (src/domain/users.ts) refuses every Pay/Close-
// family route (Pay, Close, split-collect, resolve-no-tip) with
// pin_change_required (403) whenever the acting session's own user has
// must_change_pin set. Rather than translate that code into rail wording,
// this opens the exact same mandatory change-PIN pad the login flow
// already uses (openPinEntry, mode "changePin", mandatory: true) with
// returnTo "pay-ticket", so completing the change returns straight to
// this same ticket. Called as the FIRST statement in each of the four
// failure branches, before any of them touch payStatusEl or compute
// translatePayCloseError, so a mustChangePin bartender never sees the
// rail's error box flash before the PIN pad opens over it. Returns true
// when it handled the refusal (the caller returns immediately); false for
// every other code, which the caller keeps handling exactly as before.
function handlePinChangeRequiredOnPayClose(errorCode: string | undefined): boolean {
  if (errorCode !== "pin_change_required") {
    return false;
  }
  openPinEntry({ mode: "changePin", returnTo: "pay-ticket", mandatory: true });
  return true;
}

/** PINKEYS1: a keystroke aimed at a field that takes typed text, which the PIN pad must not capture. */
function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement ||
    (target instanceof HTMLInputElement && !["button", "checkbox", "radio", "submit", "reset"].includes(target.type));
}

function handlePinDigitClick(digit: string): void {
  if (
    pinEntry === undefined ||
    pinEntry.locked ||
    pinEntry.digits.length >= currentPinMaxLength()
  ) {
    return;
  }
  pinEntry.digits += digit;
  renderPinEntryDisplay();
}

function handlePinClearClick(): void {
  if (pinEntry === undefined || pinEntry.locked) {
    return;
  }
  pinEntry.digits = "";
  renderPinEntryDisplay();
}

function handlePinBackspaceClick(): void {
  if (pinEntry === undefined || pinEntry.locked) {
    return;
  }
  pinEntry.digits = pinEntry.digits.slice(0, -1);
  renderPinEntryDisplay();
}

function handlePinCancelClick(): void {
  if (pinEntry === undefined) {
    return;
  }
  if (pinEntry.mode === "approval") {
    // RBAC track: Cancel on the pin pad goes back to the PICKER (a
    // different manager might still approve this), not out of the whole
    // approval flow -- see openPinEntry's own comment on this mode.
    // approvalPickerCancelButton/handleApprovalPickerCancelClick (this
    // file's "Manager approval" section) is the control that actually
    // ends the flow.
    const flow = activeApprovalFlow;
    if (flow === undefined || flow.inFlight) {
      // Gate finding 1: while an approval retry is on the wire it cannot
      // be called back, so this control refuses rather than pretending.
      // It is disabled at the same time (setApprovalRetryBusy); this is
      // the second of the two.
      return;
    }
    pinEntry = undefined;
    returnToApprovalPicker(flow);
    return;
  }
  if (pinEntry.mode === "login") {
    // UX10: a wrong tile tap returns to the picker rather than stranding
    // the bartender on a PIN pad for someone else. Any digits already
    // typed are discarded, not carried into the next visit.
    pinEntry = undefined;
    showUserPickerScreen();
    return;
  }
  if (pinEntry.mode !== "changePin") {
    return;
  }
  const returnTo = pinEntry.returnTo ?? "open-tabs";
  pinEntry = undefined;
  if (returnTo === "profile") {
    showProfileScreen();
  } else {
    showOpenTabsScreen();
  }
}

/** Shared failure path for a login PIN attempt. Shows the server's own
 * plain-language message verbatim (per the errors this can be:
 * InvalidPinCredentialsError 401, AccountLockedError 423) rather than
 * inventing new copy, and special-cases only the 423 status to decide
 * whether the pad stays usable, never the error `code` string. */
function handlePinEntryFailure(result: { status: number; body: Partial<ApiErrorBody> }): void {
  const message = result.body.message ?? "Something went wrong. Try again.";
  if (pinEntry !== undefined) {
    pinEntry.digits = "";
  }
  if (result.status === 423) {
    pinEntryErrorEl.textContent = `${message} Ask a manager to unlock your account from Settings.`;
    if (pinEntry !== undefined) {
      pinEntry.locked = true;
    }
    setPinDigitsEnabled(false);
    pinSubmitButton.disabled = true;
  } else {
    pinEntryErrorEl.textContent = message;
    renderPinEntryDisplay();
  }
  pinEntryErrorEl.hidden = false;
}

// ---------------------------------------------------------------------
// RBAC track: manager approval (approval_required 403s)
// ---------------------------------------------------------------------
//
// The client-side half of src/domain/permissions.ts's requirePermission:
// a first call to a gated route with no approverUserId/approverPin gets
// HTTP 403 {error: "approval_required"}. requestApproval below is the
// ONE place any call site reaches for that: it makes the first attempt
// itself, and if (and only if) that comes back approval_required, drives
// a two-step UI (the approver picker, #approval-picker-screen, then
// #pin-entry-screen reused in "approval" mode) that retries the SAME
// request with those two fields added, reusing the SAME idempotencyKey
// the first attempt used (recordPermissionApproval, server side, keys the
// audit row on exactly that, per its own header comment). See ui/src/
// approvalFlow.ts for the pure candidate-filter and retry-classification
// functions this section calls rather than re-implementing inline.
//
// Three live call sites today (src/api/server.ts's own comments name
// these as "one/two/three of three live approval_required permissions,
// Assistant Manager only"): handleReopenClick below (Reopen a closed
// tab), handleRemoveSavedCard below (Remove a saved card), and admin.ts's
// handleConfirmCloseCountSession (Close a count session).

/** What a caller gets back from requestApproval: either the flow was
 * canceled (the caller does nothing further -- no toast, no state change,
 * matching "Cancel returns to where the flow started with nothing
 * changed and no toast of failure"), or it ran to completion and the
 * caller gets the FINAL ApiResult exactly as if it had called its own API
 * function directly -- the caller's own ordinary success/failure handling
 * (a toast on a non-2xx status, etc.) applies unchanged, including for a
 * network error or an unrelated domain failure that showed up on the
 * retry (see approvalFlow.ts's classifyApprovalRetryResult "other_failure"
 * doc comment). */
export type ApprovalGatedResult<T> = { canceled: true } | { canceled: false; result: ApiResult<T> };

type ApprovalAttempt<T extends Partial<ApiErrorBody>> = (approval?: {
  approverUserId: string;
  approverPin: string;
}) => Promise<ApiResult<T>>;

// Every real call site's own success/error body shape is `X &
// Partial<ApiErrorBody>` (this codebase's api.ts convention throughout),
// so requestApproval below constrains T to Partial<ApiErrorBody> and this
// pending-flow record erases down to exactly that common shape for
// storage -- the one property submitApprovalPin's own retry loop actually
// needs to read (`body.error`/`body.message`, via
// classifyApprovalRetryResult) regardless of which of the three call
// sites is in flight. T's return-type covariance makes storing an
// ApprovalAttempt<T> here as ApprovalAttempt<Partial<ApiErrorBody>> sound
// with no cast; resolving back OUT to the caller's own more specific T
// needs one explicit cast (below, in requestApproval), which is safe
// because the object handed to `resolve` at every call site is always the
// exact same object `attempt()` itself produced -- this erasure only
// narrows what TypeScript can see, never what is actually stored.
/** One in-progress approval flow. Gate findings 1 and 2, 2026-08-27: this
 * used to be a bare module-level `let approvalFlow` that every handler
 * re-read by name, including AFTER an await, and that a second
 * requestApproval call overwrote outright. Two defects came out of that
 * one shared slot, and both are removed structurally here rather than
 * guarded against:
 *
 *   1. `resolve` is gone from this record. Nothing outside requestApproval
 *      can reach a flow's resolve function at all any more; the only way
 *      any flow ever completes is `settle` below, which is a closure over
 *      THAT flow's own resolve and its own already-settled flag. A stale
 *      read can therefore no longer resolve the wrong flow, resolve one
 *      twice, or (the live symptom the gate hit) throw "Cannot read
 *      properties of undefined (reading 'resolve')" and strand the
 *      caller's promise forever.
 *   2. There can never be a second flow to clobber the first: the module
 *      is single-entry from the FIRST, no-approval attempt onward (see
 *      approvalGateBusy in requestApproval). That covers the whole
 *      window, including the window before any screen is showing, which
 *      is where the gate's triple-tap produced three server round trips,
 *      three pickers, and two orphaned promises whose callers hung.
 *
 * Every remaining read of the module slot is an IDENTITY COMPARISON
 * against a flow the caller captured before its await, never a way to
 * obtain a flow to act on. */
interface ActiveApprovalFlow {
  isCurrent?: () => boolean;
  actionLabel: string;
  attempt: ApprovalAttempt<Partial<ApiErrorBody>>;
  /** Shows whichever screen this flow started from -- e.g. Open Tabs for
   * Reopen, the ring screen for saved-card removal (its own sheet is
   * nested inside #ring-screen and reappears unchanged once that screen
   * is shown again, since only ITS ancestor was hidden, never its own
   * `hidden` state), or the admin screen for count-session close. Called
   * on a full Cancel from the picker, so a canceled flow lands back where
   * the action itself started rather than requestApproval owning a screen
   * of its own to return to. */
  returnTo: () => void;
  /** WHICH permission this flow is getting approved, straight from the
   * 403 body requirePermission produced (src/domain/permissions.ts's
   * ApprovalRequiredError). Product-owner ruling 2026-08-28: the picker
   * asks the server who can approve THIS id rather than guessing from a
   * role tier. Undefined only when the server did not name one, which is
   * what a build older than that ruling returns; the picker then degrades
   * honestly instead of filtering on nothing. */
  permissionId: string | undefined;
  /** DSC32. When set (the discount-apply flow only), the picker asks
   * "who can approve THIS discount" (GET .../approval-candidates?
   * discountId=...) instead of the permissionId shape above, because the
   * permissionId shape is reachable only when the CALLER's own grant on
   * that id is exactly approval_required -- which is false for an actor
   * whose own money.discount.apply grant is a plain "allowed" (an
   * override, say) on a discount that itself demands approval, the
   * DSC32 symptom. Takes priority over permissionId when both are
   * present. */
  discountId: string | undefined;
  /** M5. One quiet line shown on the manager PIN pad naming what is being
   * approved (the drink, the old and new price, the reason), so the manager
   * knows what they are agreeing to. Absent for every other flow. */
  pinContext: string | undefined;
  candidates: ApprovalCandidateSource[];
  /** False when this device could not learn who can approve (the
   * GET .../approval-candidates read failed, or the 403 named no
   * permission at all), so the list on screen is every colleague on shift
   * rather than the real approver set. Drives the plain-language note on
   * the picker, and whether a tile carries a role badge at all (see
   * approvalFlow.ts's own approvalCandidateBadgeRole). Never a permission
   * decision. */
  candidatesVerified: boolean;
  /** True from the moment an approval retry leaves this device until its
   * response (or network failure) is in hand. Gate finding 1: a guarded
   * action already on the wire CANNOT be called back, so while this is
   * true every cancel control is disabled AND both cancel handlers refuse
   * outright. "Cancel" that silently let an inventory adjustment or a tab
   * reopen land anyway is not a cancel; the honest contract is that the
   * cancel controls stop being offered once the request is committed. */
  inFlight: boolean;
  /** The ONE way this flow's promise is ever completed. Idempotent: a
   * second call is a no-op, so no path can double-resolve. Clears the
   * module slot only if it still points at this same flow. */
  settle: (
    outcome: { canceled: true } | { canceled: false; result: ApiResult<Partial<ApiErrorBody>> },
  ) => void;
}

let activeApprovalFlow: ActiveApprovalFlow | undefined;

function approvalFlowIsCurrent(flow: ActiveApprovalFlow): boolean {
  if (flow.isCurrent?.() !== false) return true;
  flow.settle({ canceled: true });
  return false;
}

/** Gate finding 2, 2026-08-27. The re-entrancy lock for the WHOLE gated
 * action, held from before the first, no-approval attempt until the flow
 * finally resolves. Deliberately here and not at the three call sites: a
 * per-call-site `disabled` would have to be remembered again by every
 * future call site, and the gate's own reproduction (three taps on one
 * Reopen row producing three reopen POSTs, three GET .../users and three
 * pickers) started BEFORE any approval screen existed to disable
 * anything on. One choke point, so a call site cannot forget. */
let approvalGateBusy = false;

/**
 * Makes the first, no-approval attempt itself. If that is not a 403
 * approval_required, this is a plain pass-through: the caller gets that
 * result back exactly as if it had called its own API function directly,
 * and handles success/failure exactly as it always did (this is why
 * every existing call site's own error toast keeps working unchanged).
 * Only on approval_required does this open the picker and hand control to
 * this section's own screens until the flow resolves.
 *
 * Re-entrant calls (a second tap on the same control, or a tap on a
 * different gated control while this one is mid-flow) get
 * `{canceled: true}` immediately and send NOTHING to the server, which is
 * exactly the "nothing changed, no toast of failure" contract Cancel
 * already has. Every caller already handles that branch.
 */
export async function requestApproval<T extends Partial<ApiErrorBody>>(
  actionLabel: string,
  attempt: ApprovalAttempt<T>,
  returnTo: () => void,
  /** DSC32. Set only by the discount-apply flow, so the picker can ask
   * "who can approve THIS discount" instead of relying on the caller's
   * own posture matching permissionId's approval_required gate. See
   * ActiveApprovalFlow.discountId. */
  options?: { discountId?: string; pinContext?: string; isCurrent?: () => boolean },
): Promise<ApprovalGatedResult<T>> {
  if (approvalGateBusy || options?.isCurrent?.() === false) {
    return { canceled: true };
  }
  approvalGateBusy = true;
  try {
    const first = await attempt();
    if (options?.isCurrent?.() === false) return { canceled: true };
    if (first.networkError || first.status !== 403 || first.body?.error !== "approval_required") {
      return { canceled: false, result: first };
    }
    // Product-owner ruling 2026-08-28: the id requirePermission itself
    // just evaluated, not a route-to-permission map re-derived here. See
    // ApprovalRequiredError's own comment in src/domain/permissions.ts.
    const permissionId = first.body?.permissionId;
    return await new Promise<ApprovalGatedResult<T>>((resolve) => {
      let settled = false;
      const flow: ActiveApprovalFlow = {
        isCurrent: options?.isCurrent,
        actionLabel,
        attempt,
        returnTo,
        permissionId,
        discountId: options?.discountId,
        pinContext: options?.pinContext,
        candidates: [],
        candidatesVerified: true,
        inFlight: false,
        settle: (outcome) => {
          if (settled) {
            return;
          }
          settled = true;
          if (activeApprovalFlow === flow) {
            activeApprovalFlow = undefined;
          }
          // See ActiveApprovalFlow's own header: the object handed to
          // `resolve` is always exactly what `attempt()` itself produced,
          // so this cast back to the caller's real T is sound even though
          // it passed through the erased Partial<ApiErrorBody> storage.
          resolve(outcome as ApprovalGatedResult<T>);
        },
      };
      activeApprovalFlow = flow;
      // A throw inside the picker (requireSession with no session, say)
      // would otherwise leave this promise unresolved forever AND hold
      // the re-entrancy lock, which is the exact "caller hangs" class
      // this whole section exists to make impossible. settle is
      // idempotent, so this can never double-resolve a flow that already
      // completed normally.
      void openApprovalPicker(flow).catch(() => {
        flow.settle({ canceled: true });
        if (flow.isCurrent?.() !== false) flow.returnTo();
      });
    });
  } finally {
    approvalGateBusy = false;
  }
}

/** Every control that must stop responding while an approval retry is on
 * the wire (gate finding 1). The PIN pad's own Submit and the digit keys
 * are included so a second submit cannot start a second guarded request,
 * and both Cancel controls are included because the request cannot be
 * called back once it has left. */
function setApprovalRetryBusy(busy: boolean): void {
  approvalPickerCancelButton.disabled = busy;
  pinCancelButton.disabled = busy;
  setPinDigitsEnabled(!busy);
  if (busy) {
    pinSubmitButton.disabled = true;
  } else {
    // Restores the ordinary "enough digits and not locked out" rule
    // rather than unconditionally re-enabling.
    renderPinEntryDisplay();
  }
}

function renderApprovalPickerTiles(flow: ActiveApprovalFlow): void {
  approvalPickerListEl.innerHTML = "";
  const candidates = flow.candidates;
  if (candidates.length === 0) {
    approvalPickerEmptyEl.hidden = false;
    return;
  }
  approvalPickerEmptyEl.hidden = true;
  for (const candidate of candidates) {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = "user-tile";
    tile.dataset["testid"] = "approval-picker-tile";
    tile.dataset["userId"] = candidate.id;
    tile.setAttribute("aria-label", `Approve as ${candidate.displayName}`);
    const name = document.createElement("span");
    name.className = "user-tile-name";
    name.textContent = candidate.displayName;
    if (flow.candidatesVerified) {
      // Product-owner ruling 2026-08-28: on the authoritative path every
      // tile here can approve this exact permission, so a role word adds
      // nothing except a hint about the tenant's authority structure.
      // The server deliberately does not send one.
      tile.append(name);
    } else {
      // Degraded path only: the list is everyone on shift and the picker
      // has already said it could not check, so the one distinguishing
      // thing this device knows about a person is worth showing to help
      // them guess who to ask. Display text, never a decision -- see
      // approvalFlow.ts's own approvalCandidateBadgeRole.
      //
      // SEC3 (docs/build-briefs/SEC3-sign-in-names-only.md): widening
      // UserDto.role to optional (for the pre-login roster shape) also
      // widened ApprovalCandidateSource.role, so this can now genuinely
      // be undefined for a Lounger or Inventory Manager reaching this
      // degraded path via the roster fallback. Chosen handling: omit the
      // badge outright rather than show a placeholder, matching the
      // filtered path just above, which already renders no badge when
      // there is nothing real to show.
      const roleId = approvalCandidateBadgeRole(candidate);
      if (roleId === undefined) {
        tile.append(name);
      } else {
        const badge = document.createElement("span");
        badge.className = "user-tile-role-badge";
        badge.textContent = roleDisplayLabel(roleId);
        tile.append(name, badge);
      }
    }
    tile.addEventListener("click", () => {
      if (activeApprovalFlow !== flow || !approvalFlowIsCurrent(flow) || flow.inFlight) {
        return;
      }
      openPinEntry({
        mode: "approval",
        approverId: candidate.id,
        approverName: candidate.displayName,
        context: flow.pinContext,
      });
    });
    approvalPickerListEl.append(tile);
  }
}

async function openApprovalPicker(flow: ActiveApprovalFlow): Promise<void> {
  if (!approvalFlowIsCurrent(flow)) return;
  const session = requireSession();
  approvalPickerActionEl.textContent = flow.actionLabel;
  approvalPickerStatusEl.hidden = true;
  approvalPickerStatusEl.textContent = "";
  approvalPickerListEl.innerHTML = '<p class="tabs-loading">Loading…</p>';
  approvalPickerEmptyEl.hidden = true;
  approvalPickerCancelButton.disabled = false;
  showApprovalPickerScreen();

  // Product-owner ruling 2026-08-28 (CLAUDE.md's permission-driven rule).
  // The authoritative read: exactly the users the server will accept as
  // an approver for THIS permission (or, for the discount flow, THIS
  // discount -- see ActiveApprovalFlow.discountId and DSC32). One
  // request, and it needs no admin.user.* rights, which is the whole
  // reason the old code could not do this and fell back to role names
  // instead (GET .../users/:userId/permissions is gated on
  // admin.user.permissions.manage, which nobody an approval_required
  // grant can block actually holds).
  const candidateTarget =
    flow.discountId !== undefined
      ? { discountId: flow.discountId }
      : flow.permissionId !== undefined
        ? { permissionId: flow.permissionId }
        : undefined;
  const authoritative =
    candidateTarget === undefined
      ? undefined
      : await listApprovalCandidates(session.apiBaseUrl, session.tenantId, candidateTarget);
  if (activeApprovalFlow !== flow || !approvalFlowIsCurrent(flow)) {
    // This flow was canceled while the request was in flight. Identity
    // comparison, never a re-read of a slot to act on.
    return;
  }

  if (authoritative?.status === 200) {
    const selection = selectApprovalCandidates(authoritative.body.candidates, [], session.userId);
    flow.candidates = selection.candidates;
    flow.candidatesVerified = true;
    renderApprovalPickerTiles(flow);
    return;
  }

  // DSC32: the discount flow's own candidate-fetch failure does NOT fall
  // back to the full staff roster. "This device could not check, so
  // everyone is listed" was itself the false green DSC32 reported --
  // enumerating every tile on shift as a guess is worse than an empty
  // list, because it invites tapping someone who will always be turned
  // down and it discloses the full shift roster to whoever is holding
  // the tablet. No roster, full stop, the same shape
  // requireSecondPersonApproval's own server-side refusal already uses.
  if (flow.discountId !== undefined) {
    approvalPickerListEl.innerHTML = "";
    approvalPickerStatusEl.hidden = false;
    approvalPickerStatusEl.textContent =
      "This device could not check who can approve this discount. Ask a manager to try again.";
    approvalPickerEmptyEl.hidden = true;
    flow.candidates = [];
    flow.candidatesVerified = false;
    return;
  }

  // Honest degrade, kept from the behavior this replaced. This device
  // could not learn who can approve -- the read failed, or the server
  // never named the permission -- so it falls back to the ordinary staff
  // roster, says plainly that it could not check, and never silently
  // shows a filtered list it cannot stand behind.
  const roster = await listUsers(session.apiBaseUrl, session.tenantId, { activeOnly: true });
  if (activeApprovalFlow !== flow || !approvalFlowIsCurrent(flow)) {
    return;
  }
  if (roster.status !== 200) {
    approvalPickerListEl.innerHTML = "";
    approvalPickerStatusEl.hidden = false;
    approvalPickerStatusEl.textContent =
      roster.body.message ?? "Could not load the staff list. Check your connection.";
    approvalPickerEmptyEl.hidden = true;
    return;
  }
  const selection = selectApprovalCandidates(undefined, roster.body.users, session.userId);
  flow.candidates = selection.candidates;
  flow.candidatesVerified = false;
  // Two sentences, per the house writing rule.
  approvalPickerStatusEl.hidden = false;
  approvalPickerStatusEl.textContent =
    "This device could not check who can approve, so everyone on shift is listed. Pick a manager; anyone else will be turned down.";
  renderApprovalPickerTiles(flow);
}

/** Re-shows the picker with its existing candidate list (no second
 * GET .../users round trip) after either an explicit "back" from the pin
 * pad (handlePinCancelClick's own approval branch) or a retry outcome
 * that sends the flow back here (classifyApprovalRetryResult's
 * "return_to_picker": the approver's account is locked, they have never
 * set a real PIN, or they are not able to approve this permission). */
function returnToApprovalPicker(flow: ActiveApprovalFlow): void {
  if (!approvalFlowIsCurrent(flow)) return;
  approvalPickerCancelButton.disabled = false;
  showApprovalPickerScreen();
  renderApprovalPickerTiles(flow);
}

function handleApprovalPickerCancelClick(): void {
  const flow = activeApprovalFlow;
  if (flow === undefined || !approvalFlowIsCurrent(flow)) {
    return;
  }
  if (flow.inFlight) {
    // Gate finding 1: an approval retry is already on the wire and cannot
    // be called back. Refusing here is belt-and-braces with the button
    // being disabled at the same time.
    return;
  }
  pinEntry = undefined;
  // "Cancel returns to where the flow started with nothing changed and no
  // toast of failure": settling with `canceled: true` is exactly what
  // lets every call site's own `if (gated.canceled) return;` do nothing
  // further.
  flow.settle({ canceled: true });
  flow.returnTo();
}

async function submitApprovalPin(approverId: string, pin: string): Promise<void> {
  const flow = activeApprovalFlow;
  if (flow === undefined || !approvalFlowIsCurrent(flow) || flow.inFlight) {
    return;
  }
  flow.inFlight = true;
  setApprovalRetryBusy(true);
  let result: ApiResult<Partial<ApiErrorBody>>;
  try {
    result = await flow.attempt({ approverUserId: approverId, approverPin: pin });
  } finally {
    flow.inFlight = false;
    if (flow.isCurrent?.() !== false) setApprovalRetryBusy(false);
  }
  if (!approvalFlowIsCurrent(flow)) return;
  const classification = classifyApprovalRetryResult(result);
  if (classification.kind === "success" || classification.kind === "other_failure") {
    // Both are TERMINAL, and both are settled on the captured flow
    // itself, never on whatever the module slot happens to hold now.
    // "other_failure" is most commonly the GUARDED ACTION's own domain
    // error (e.g. the tab was already reopened by someone else) rather
    // than anything about the approval, so the caller gets the real
    // result and its ordinary error handling shows it.
    pinEntry = undefined;
    flow.settle({ canceled: false, result });
    return;
  }
  if (activeApprovalFlow !== flow || !approvalFlowIsCurrent(flow)) {
    // Not reachable while this flow held inFlight for the whole await
    // (every control that could have replaced it was disabled and both
    // cancel handlers refuse while inFlight). Kept as a hard stop so a
    // future control added to these screens cannot silently drive the
    // WRONG flow's UI; the two terminal branches above have already made
    // sure no caller can hang either way.
    return;
  }
  if (classification.kind === "retry_on_pad" || classification.kind === "network_error") {
    // Stays on the pin pad: for a wrong PIN, the SAME approver may simply
    // have mistyped it; for a network error, this is a deliberate,
    // human-initiated resubmit (never an automatic one -- see
    // approvalFlow.ts's own "network_error" doc comment on why reusing
    // the same idempotencyKey makes that resubmit safe rather than a
    // silent retry).
    handlePinEntryFailure({ status: result.status, body: result.body });
    return;
  }
  // "return_to_picker": the approver's account is locked, they have never
  // set a real PIN of their own, or they are not able to approve this
  // specific permission (including the should-be-unreachable
  // self-approval case) -- either way, THIS approver is a dead end but a
  // DIFFERENT one might not be, so this goes back to the picker rather
  // than ending the whole flow.
  pinEntry = undefined;
  approvalPickerStatusEl.hidden = false;
  approvalPickerStatusEl.textContent = classification.message;
  returnToApprovalPicker(flow);
}

// ---------------------------------------------------------------------
// Terminal-assignment slice (docs/solution-architecture.md Section 8.3,
// realized). Picks (or registers) the payment terminal THIS physical
// device IS, gated on floor.terminal.select (TERM1) and served by its own
// narrow list route (GET .../terminals/selectable), NOT the Settings route
// (GET .../terminals, admin.terminal.view), which returns provider
// references and inactive terminals a picker never needs.
// Reached from Open Tabs' own account-menu "Payment Terminal" item
// (TERMINAL_ONLY_BUTTONS above, gated on the same permission), on demand
// -- NOT a login-blocking gate: a device with no terminal picked yet
// still charges normally ONLY against the simulated provider (src/
// domain/payments.ts's resolveTerminalReferenceForCharge's own fallback,
// input.adapter.providerName === "simulated"), the only provider Release
// 1 actually runs by default (docs/decision-register.md's DEC-001). Once
// a real, non-simulated provider is configured (Stripe, per DEC-001,
// armed wherever PAYMENT_PROVIDER=stripe -- true on stage0, iac/gcp/
// run.tf), the SAME fallback branch instead throws
// TerminalNotAssignedError, and Pay fails with a plain message (PAY5,
// ui/src/payCloseErrorMessages.ts) rather than a silent simulated charge.
// So this screen is discoverable and permission-gated rather than a
// login-blocking step ONLY because the simulated provider tolerates an
// unset device; it is not a general correctness argument that holds for
// every provider, and PAY5's own proactive warning (PAY5-F1: now the top
// location's own terminal segment, renderTopbarTerminalSegment below) exists
// because this screen's own discoverability was not enough on its own
// once a real provider was configured. Updating every OTHER e2e spec's
// own shared login helper to
// force this screen before Open Tabs would still be unwarranted for the
// reason originally stated (unrelated to what most specs test); this
// correction narrows the CLAIM, not the conclusion about forcing the
// screen.
// ---------------------------------------------------------------------

function openTerminalPickerScreen(): void {
  const session = requireSession();
  showTerminalPickerScreen();
  terminalPickerStatusEl.textContent = "";
  terminalPickerRegisterNameInput.value = "";
  const current = terminalConfigState?.paymentTerminalDisplayName;
  if (current !== undefined) {
    terminalPickerCurrentEl.hidden = false;
    terminalPickerCurrentEl.textContent = `This device is currently set up as: ${current}`;
  } else {
    terminalPickerCurrentEl.hidden = true;
  }
  if (!hasPermission(session, TERMINAL_PICKER_PERMISSION)) {
    // Product-owner ruling 2026-08-28: this branch used to ask
    // isManagerTierRole(session.role) while its own comment already said
    // the route below is PERMISSION-gated, so it tested one thing and
    // documented another. It now resolves the same floor.terminal.select
    // (TERM1) the route enforces (and the same one the account-menu button that
    // opens this screen resolves), so the defensive branch and the real
    // gate can no longer disagree about who gets in. Defensive only,
    // since that account-menu item is hidden for exactly this set.
    terminalPickerManagerEl.hidden = true;
    terminalPickerBlockedEl.hidden = false;
    return;
  }
  terminalPickerManagerEl.hidden = false;
  terminalPickerBlockedEl.hidden = true;
  // TERM1: registering a terminal is administration (admin.terminal.manage).
  // A Lounger who may only choose sees the list, never the register form.
  const mayRegister = hasPermission(session, TERMINAL_REGISTER_PERMISSION);
  terminalPickerRegisterSectionEl.hidden = !mayRegister;
  terminalPickerListEl.innerHTML = '<p class="tabs-loading">Loading…</p>';
  terminalPickerEmptyEl.hidden = true;
  void loadTerminalPickerList(session);
}

async function loadTerminalPickerList(session: Session): Promise<void> {
  // PAY6: chargeableOnly (not merely activeOnly) so a terminal whose own
  // provider_name has outlived a later provider cutover -- active,
  // labeled, but permanently refused at charge time -- never appears
  // here for a bartender to pick.
  const result = await listSelectableTerminals(session.apiBaseUrl, session.tenantId);
  if (result.status !== 200) {
    terminalPickerListEl.innerHTML = "";
    terminalPickerStatusEl.textContent =
      result.body.message ?? "Could not load card readers. Check your connection.";
    return;
  }
  renderTerminalPickerTiles(result.body.terminals);
}

function renderTerminalPickerTiles(terminals: SelectableTerminalDto[]): void {
  terminalPickerListEl.innerHTML = "";
  if (terminals.length === 0) {
    terminalPickerEmptyEl.hidden = false;
    return;
  }
  terminalPickerEmptyEl.hidden = true;
  const selectedId = terminalConfigState?.paymentTerminalId;
  for (const terminal of sortByName(terminals, (listed) => listed.display_name)) {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = "user-tile";
    tile.dataset["testid"] = "terminal-picker-tile";
    tile.dataset["terminalId"] = terminal.id;
    tile.setAttribute("aria-label", `This device is ${terminal.display_name}`);
    const name = document.createElement("span");
    name.className = "user-tile-name";
    name.textContent = terminal.display_name;
    const badge = document.createElement("span");
    badge.className = "user-tile-role-badge";
    badge.textContent =
      terminal.id === selectedId
        ? "This device"
        : terminal.provider_name === "simulated"
          ? "Simulated reader"
          : terminal.provider_name;
    tile.append(name, badge);
    tile.addEventListener("click", () => handleTerminalPickerTileClick(terminal));
    terminalPickerListEl.append(tile);
  }
}

function handleTerminalPickerTileClick(terminal: SelectableTerminalDto): void {
  const config = terminalConfigState;
  if (config === undefined) {
    return;
  }
  terminalConfigState = savePaymentTerminalSelection(config, terminal.id, terminal.display_name);
  resetReaderCartDisplay();
  if (!payConfirmScrim.hidden) { updatePayConfirmSummary(); updatePayConfirmButtonState(); }
  // TERM3: the server remembers this as the user's last terminal, so the next
  // sign-in selects it again. A failure never blocks the local choice.
  void rememberTerminalChoice(terminal.id);
  // PAY5-F1: updates the topbar's own terminal segment immediately,
  // before showOpenTabsScreen() below navigates there (that navigation's
  // own showTopbarChrome call would also catch this, but writing it here
  // too keeps this function's own effect immediately observable, the
  // same PAY5-era convention every other terminalConfigState assignment
  // in this file follows).
  renderTopbarTerminalSegment();
  showToast(`This device is now set up as ${terminal.display_name}.`, "success");
  showOpenTabsScreen();
  void loadOpenTabs();
}

async function handleTerminalPickerRegisterClick(): Promise<void> {
  const displayName = terminalPickerRegisterNameInput.value.trim();
  if (displayName.length === 0) {
    terminalPickerStatusEl.textContent = "Enter a name for this card reader first.";
    return;
  }
  const session = requireSession();
  terminalPickerStatusEl.textContent = "";
  terminalPickerRegisterButton.disabled = true;
  const result = await registerTerminal(session.apiBaseUrl, session.tenantId, {
    idempotencyKey: crypto.randomUUID(),
    displayName,
  });
  terminalPickerRegisterButton.disabled = false;
  if (result.status !== 201 && result.status !== 200) {
    terminalPickerStatusEl.textContent =
      result.body.message ?? "Could not register this card reader. Check your connection.";
    return;
  }
  handleTerminalPickerTileClick(result.body.terminal);
}

function handleTerminalPickerBlockedLogoutClick(): void {
  requestLogOut();
}

function handleTerminalPickerBackClick(): void {
  showOpenTabsScreen();
}

/** TERM3: right after sign-in, selects the terminal this user used last when
 * the server says it is still active and chargeable; otherwise the device's
 * previous choice stands, and with neither the app asks as it always has. */
async function applyRememberedTerminalOnSignIn(): Promise<void> {
  const config = terminalConfigState;
  const session = requireSession();
  if (config === undefined || !hasPermission(session, TERMINAL_PICKER_PERMISSION)) {
    return;
  }
  // Bounded: a slow answer never holds the screen, and a late one is dropped.
  const answered = await Promise.race([
    getRememberedTerminal(session.apiBaseUrl, session.tenantId),
    new Promise<undefined>((resolve) => setTimeout(resolve, REMEMBERED_TERMINAL_WAIT_MS)),
  ]);
  // The user signed out or chose a terminal themselves while this was out.
  if (
    state.session?.token !== session.token ||
    terminalConfigState?.paymentTerminalId !== config.paymentTerminalId
  ) {
    return;
  }
  const remembered = answered?.status === 200 ? answered.body.terminal : null;
  // TERM4: with nothing remembered for this location, a reader saved on the device itself is trusted only if it is one
  // of this location's readers. PAY1-S4: the same list tells whether the location has exactly one reader, which a device with
  // no usable reader then takes on its own. The lookup is bounded; if it does not answer, the device's choice stands
  // and nothing is attached.
  let deviceTerminalAtThisLocation: boolean | undefined;
  let readersAtThisLocation: LocationReader[] | undefined;
  if (remembered === null) {
    const listed = await Promise.race([
      listSelectableTerminals(session.apiBaseUrl, session.tenantId),
      new Promise<undefined>((resolve) => setTimeout(resolve, REMEMBERED_TERMINAL_WAIT_MS)),
    ]);
    if (state.session?.token !== session.token) {
      return;
    }
    if (listed?.status === 200) {
      readersAtThisLocation = listed.body.terminals;
      if (config.paymentTerminalId !== undefined) {
        deviceTerminalAtThisLocation = listed.body.terminals.some(
          (terminal) => terminal.id === config.paymentTerminalId,
        );
      }
    }
  }
  const decision = decideSignInTerminal({
    remembered,
    deviceTerminalId: config.paymentTerminalId,
    deviceTerminalAtThisLocation: deviceTerminalAtThisLocation,
    readersAtThisLocation: readersAtThisLocation,
  });
  if (decision.kind === "use_only_reader") {
    attachReaderOnThisDevice(config, decision.id, decision.displayName);
    return;
  }
  if (decision.kind === "drop_device_and_ask") {
    // The saved reader is not at this location: forget it, so Pay never sends it, and show the picker.
    const { paymentTerminalId: _id, paymentTerminalDisplayName: _name, ...rest } = config;
    terminalConfigState = rest;
    saveTerminalConfig(rest);
    renderTopbarTerminalSegment();
    showToast(OTHER_LOCATION_TERMINAL_DROPPED_TOAST, "warning");
    openTerminalPickerScreen();
    return;
  }
  if (decision.kind === "keep_device") {
    // TERMREADER1: a tablet that already holds a verified reader but whose server record has none yet (it was set
    // up before the server remembered readers per device) hands that reader to the server once.
    if (deviceTerminalAtThisLocation === true && config.paymentTerminalId !== undefined) {
      void rememberTerminalChoice(config.paymentTerminalId);
    }
    return;
  }
  if (decision.kind !== "use_remembered") {
    return;
  }
  terminalConfigState = savePaymentTerminalSelection(config, decision.id, decision.displayName);
  resetReaderCartDisplay();
  if (!payConfirmScrim.hidden) { updatePayConfirmSummary(); updatePayConfirmButtonState(); }
  renderTopbarTerminalSegment();
  showToast(signInTerminalToast(decision.displayName), "success");
  // PERFSIGNIN1: choosing a reader changes nothing the list shows (the location comes from the signed-in tablet). Reload
  // only if no load is already running, so sign-in does not run the whole list twice.
  if (openTabsLoadsInFlight === 0) {
    void loadOpenTabs();
  }
}

/** PAY1-S4 (walkthrough item 14): selects the location's only reader on this device without a manager picking it, and
 * remembers it for the signed-in user like a pick would. Shows the existing "Card payments will use ..." toast. */
function attachReaderOnThisDevice(
  config: TerminalConfig,
  terminalId: string,
  displayName: string,
): void {
  terminalConfigState = savePaymentTerminalSelection(config, terminalId, displayName);
  resetReaderCartDisplay();
  if (!payConfirmScrim.hidden) { updatePayConfirmSummary(); updatePayConfirmButtonState(); }
  renderTopbarTerminalSegment();
  void rememberTerminalChoice(terminalId);
  showToast(signInTerminalToast(displayName), "success");
  if (openTabsLoadsInFlight === 0) {
    void loadOpenTabs();
  }
}

/** PAY1-S4: a manager just registered a reader in Settings. If this device has no reader and the location now has
 * exactly one, take it, so the top bar stops saying "No reader". A device that already has a reader keeps it. */
async function attachOnlyReaderIfDeviceHasNone(): Promise<void> {
  const config = terminalConfigState;
  const active = state.session;
  if (
    config === undefined ||
    active === undefined ||
    config.paymentTerminalId !== undefined ||
    !hasPermission(active, TERMINAL_PICKER_PERMISSION)
  ) {
    return;
  }
  const listed = await listSelectableTerminals(active.apiBaseUrl, active.tenantId);
  const only = listed.status === 200 ? soleReaderOf(listed.body.terminals) : undefined;
  // The user signed out, or a reader was chosen some other way, while the list was out.
  if (
    only === undefined ||
    state.session?.token !== active.token ||
    terminalConfigState?.paymentTerminalId !== undefined
  ) {
    return;
  }
  attachReaderOnThisDevice(config, only.id, only.display_name);
}

window.addEventListener(CARD_READER_REGISTERED_EVENT, () => {
  void attachOnlyReaderIfDeviceHasNone();
});

async function rememberTerminalChoice(terminalId: string): Promise<void> {
  const session = requireSession();
  const result = await rememberTerminal(session.apiBaseUrl, session.tenantId, { terminalId });
  if (result.status !== 200) {
    showToast(TERMINAL_NOT_SAVED_TOAST, "warning");
  }
}

/** PAY1 Slice 3 (docs/build-briefs/PAY1-S3-standup-onboarding.md, section 3.5). Stripe sends the browser back to the
 * app root with `?card_payments=return` (finished or left) or `?card_payments=refresh` (the link ran out). The marker
 * is read once at page load and removed from the address bar; it waits here until a session exists, then opens
 * Settings, Tabs and payment, where the Card payments section reads the account (return) or says the link expired
 * (refresh). It carries nothing but the word: the redirect proves nothing, and the server reads the account. */
let pendingCardPaymentsArrival: CardPaymentsArrival | undefined = (() => {
  const arrival = readCardPaymentsArrival(window.location.search);
  if (arrival !== undefined) {
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${withoutCardPaymentsArrival(window.location.search)}${window.location.hash}`,
    );
  }
  return arrival;
})();

/** Opens the pending Card payments arrival for a signed-in session, once. A session that cannot see the panel
 * drops it. Returns whether Settings was opened. */
function openPendingCardPaymentsArrival(activeSession: Session): boolean {
  const arrival = pendingCardPaymentsArrival;
  if (arrival === undefined) {
    return false;
  }
  pendingCardPaymentsArrival = undefined;
  if (!hasPermission(activeSession, "admin.terminal.view")) {
    return false;
  }
  // ONB4 slice 3: when the setup wizard is what sent the browser to Stripe, it reopens on its card payments step; where
  // it does not open, Settings does, as before.
  void openSetupWizardOnStripeReturn(activeSession, arrival).then((opened) => {
    if (opened || state.session?.token !== activeSession.token) return;
    showAdminScreen();
    void admin.openCardPaymentsSettings(activeSession, arrival);
  });
  return true;
}

/** PAYEXPORT1. Gusto's page sends the browser back (through the server's callback) to the app root with `?gusto=connected` or
 * `?gusto=error&reason=<word>`. Read once at page load and removed from the address; it waits until a session exists, then
 * opens Settings, Connections, Payroll (Gusto). It carries only the outcome word: the server has already done or refused the work. */
let pendingGustoArrival: { outcome: "connected" | "error"; reason: string | null } | undefined = (() => {
  const params = new URLSearchParams(window.location.search);
  const outcome = params.get("gusto");
  if (outcome !== "connected" && outcome !== "error") return undefined;
  const reason = params.get("reason");
  params.delete("gusto");
  params.delete("reason");
  const rest = params.toString();
  window.history.replaceState(null, "", `${window.location.pathname}${rest === "" ? "" : `?${rest}`}${window.location.hash}`);
  return { outcome, reason: reason !== null && /^[a-z_]{1,32}$/.test(reason) ? reason : null };
})();

function openPendingGustoArrival(activeSession: Session): void {
  const arrival = pendingGustoArrival;
  if (arrival === undefined) return;
  pendingGustoArrival = undefined;
  if (!hasPermission(activeSession, "payroll.gusto.manage") && !hasPermission(activeSession, "payroll.tips.send")) return;
  showAdminScreen();
  void admin.openPayrollSettings(activeSession, arrival.outcome, arrival.reason);
}

/** PAY2-S6. Stripe's hosted bank debit page sends the browser back to the app root with
 * `?royalty_authorization=return` (finished or left) or `=cancel`. Read once at page load and removed from the address
 * location, it waits until a session exists, then reopens the wizard on its Royalty payments step when the wizard sent the
 * browser there, else Settings. It carries nothing but the word: the server reads the session. */
let pendingRoyaltyArrival: RoyaltyArrival | undefined = (() => {
  const arrival = readRoyaltyArrival(window.location.search);
  if (arrival !== undefined) {
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${withoutRoyaltyArrival(window.location.search)}${window.location.hash}`,
    );
  }
  return arrival;
})();

function openPendingRoyaltyArrival(activeSession: Session): boolean {
  const arrival = pendingRoyaltyArrival;
  if (arrival === undefined) {
    return false;
  }
  pendingRoyaltyArrival = undefined;
  if (!hasPermission(activeSession, "admin.terminal.view")) {
    return false;
  }
  void openSetupWizardOnRoyaltyReturn(activeSession, arrival).then((opened) => {
    if (opened || state.session?.token !== activeSession.token) return;
    showAdminScreen();
    void admin.openRoyaltyAuthorizationSettings(activeSession, arrival);
  });
  return true;
}

/** BANK1. The same for the Snip & Sip subscription's hosted bank debit page: `?subscription_debit=return` or `=cancel`. Read
 * once at page load and removed from the address, it waits until a session exists, then reopens the wizard on its Bank
 * accounts step when the wizard sent the browser there, else Settings. It carries nothing but the word: the server reads
 * the session. */
let pendingSubscriptionDebitArrival: SubscriptionDebitArrival | undefined = (() => {
  const arrival = readSubscriptionDebitArrival(window.location.search);
  if (arrival !== undefined) {
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${withoutSubscriptionDebitArrival(window.location.search)}${window.location.hash}`,
    );
  }
  return arrival;
})();

function openPendingSubscriptionDebitArrival(activeSession: Session): boolean {
  const arrival = pendingSubscriptionDebitArrival;
  if (arrival === undefined) {
    return false;
  }
  pendingSubscriptionDebitArrival = undefined;
  if (!hasPermission(activeSession, "admin.terminal.view")) {
    return false;
  }
  void openSetupWizardOnSubscriptionDebitReturn(activeSession, arrival).then((opened) => {
    if (opened || state.session?.token !== activeSession.token) return;
    showAdminScreen();
    void admin.openSubscriptionDebitSettings(activeSession, arrival);
  });
  return true;
}

/** ACCTEXPORT1. QuickBooks sends the browser back to the app root with `?accounting=connected|denied|failed|invalid` after the
 * consent page. Read once at page load and removed from the address, it waits until a session exists, then opens Settings, Tabs and
 * payment, where the Accounting card reads what is true now and says what happened. It carries nothing but the word: the server
 * proved the connection with its own one-time state and the session reads the result. */
let pendingAccountingArrival: AccountingArrival | undefined = (() => {
  const arrival = readAccountingArrival(window.location.search);
  if (arrival !== undefined) {
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${withoutAccountingArrival(window.location.search)}${window.location.hash}`,
    );
  }
  return arrival;
})();

function openPendingAccountingArrival(activeSession: Session): boolean {
  const arrival = pendingAccountingArrival;
  if (arrival === undefined) {
    return false;
  }
  pendingAccountingArrival = undefined;
  if (!hasPermission(activeSession, "admin.tenant_settings.view")) {
    return false;
  }
  showAdminScreen();
  void admin.openAccountingSettings(activeSession, arrival);
  return true;
}

/** ONB2 S3: at a first sign-in, shows how far "Set up this location" has got, to a person who may see it. Absent when
 * every step is done, when the person lacks the Settings permission, or when the session ended meanwhile. */
async function showFirstDaySetupBanner(): Promise<void> {
  const active = state.session;
  if (active === undefined) {
    return;
  }
  const progress = await admin.readSetupChecklistProgress(active);
  if (progress === undefined || state.session?.token !== active.token) {
    return;
  }
  if (progress.done >= progress.total) {
    firstDayBanner.hidden = true;
    return;
  }
  firstDayBannerText.textContent = `Set up this location: ${progress.done} of ${progress.total} done`;
  firstDayBanner.hidden = false;
}

configureSetupWizard({
  getSession: () => state.session,
  locationName: () => floorLocationName(),
  readChecklist: (active) => admin.readSetupChecklistSnapshot(active),
  showScreen: showSetupWizardScreen,
  finishLater: () => {
    // Finish later: Open Tabs, with the first-day banner and its live count.
    showOpenTabsScreen();
    void loadOpenTabs();
    void showFirstDaySetupBanner();
  },
  deviceLocationId: () => terminalConfigState?.locationId,
});

/** ONB4-F1: the setup entry in the Settings checklist card. The card hides itself at 16 of 16, so the entry is only
 * ever seen while setup is not complete; it is absent where the wizard is off. */
function renderSettingsSetupEntry(): void {
  settingsSetupOpen.hidden = !setupWizardEnabled();
  settingsSetupOpen.textContent = "Open setup";
}

settingsSetupOpen.addEventListener("click", () => {
  const active = state.session;
  if (active === undefined) return;
  void openSetupWizard(active).then((opened) => {
    if (!opened && state.session?.token === active.token) {
      showOpenTabsScreen();
      void loadOpenTabs();
    }
  });
});

renderSettingsSetupEntry();
firstDayBannerOpen.addEventListener("click", () => {
  firstDayBanner.hidden = true;
  // ONB4: Open setup reopens the wizard at the first step with something left; where the wizard is off, or has
  // nothing to show this person, it opens the Settings checklist as before.
  const active = state.session;
  if (active !== undefined && setupWizardEnabled()) {
    void openSetupWizard(active).then((opened) => {
      if (!opened && state.session?.token === active.token) handleOpenManagerSettingsClick();
    });
    return;
  }
  handleOpenManagerSettingsClick();
});
firstDayBannerDismiss.addEventListener("click", () => {
  firstDayBanner.hidden = true;
});

async function openPendingReportArrival(activeSession: Session): Promise<boolean> {
  if (reportArrival("tenant", activeSession.tenantId) === undefined || !hasPermission(activeSession, "report.custom.view")) return false;
  showAdminScreen();
  await admin.openOperationsView(activeSession, "reports", {report:"custom"});
  return true;
}

async function completeLoginAndShowOpenTabs(): Promise<void> {
  firstDayBanner.hidden = true;
  const showSetupBanner = firstSignInSetupPending;
  firstSignInSetupPending = false;
  resetTicketState();
  state.matchMissCount = 0;
  state.lastMatchMissDrinkName = undefined;
  state.recentlyClosedTabs = [];
  state.lastPayCloseErrorDetail = undefined;
  renderMatchMissDebug();
  renderLastPayCloseErrorDebug();
  showOpenTabsScreen();
  // TERM3: after the screen shows, never before: the top bar updates in place.
  void applyRememberedTerminalOnSignIn();
  // PERFSIGNIN1: the menu and the open tabs do not depend on each other, so they load together. The tab list used to
  // wait for the menu, its settings and every category's liquor list first.
  await Promise.all([loadMenu(), loadOpenTabs()]);
  if (state.session !== undefined) {
    openPendingCardPaymentsArrival(state.session);
    openPendingGustoArrival(state.session);
    openPendingRoyaltyArrival(state.session);
    openPendingSubscriptionDebitArrival(state.session);
    openPendingAccountingArrival(state.session);
    if (await openPendingReportArrival(state.session)) return;
  }
  if (showSetupBanner) {
    // ONB4: a first sign-in opens the setup wizard for a person who may see it; anyone else, or a location with nothing
    // left to set up, gets the ONB2 banner path as before.
    const active = state.session;
    if (active !== undefined && setupWizardEnabled()) {
      void openSetupWizard(active).then((opened) => {
        if (!opened && state.session?.token === active.token) void showFirstDaySetupBanner();
      });
    } else {
      void showFirstDaySetupBanner();
    }
  } else if (setupWizardEnabled()) {
    // ONBW1-S3: a sign-in after the owner chose Save and finish later opens Welcome back at the first unfinished step.
    // ONB4-F1: any other later sign-in shows the banner until all 16 steps are done.
    const active = state.session;
    if (active !== undefined) {
      void openSetupWizardIfPaused(active).then((opened) => {
        if (!opened && state.session?.token === active.token) void showFirstDaySetupBanner();
      });
    }
  }
}

/** What a successful sign-in answers with (POST .../auth/login, and the welcome link's PIN choice answers the same). */
type SignInResponseBody = SignInBody;

/** Everything a successful sign-in sets up, before the screen it leads to: the token, the session, the pay keys, the
 * idle watch, the theme and the controls this person may see. Shared by the PIN pad and the welcome link, so the two
 * ways in cannot drift. */
function establishSignedInSession(config: TerminalConfig, body: SignInResponseBody): Session {
  const { token, user, permissions } = body;
  setAuthToken(token);
  const newSession: Session = {
    apiBaseUrl: config.apiBaseUrl,
    tenantId: config.tenantId,
    employeeId: user.displayName,
    token,
    userId: user.id,
    role: user.role,
    // Independent-review Finding B, 2026-08-26. The login response always
    // carries a resolved role set (src/api/server.ts's toUserResponse);
    // the fallback is a type-level formality after UserDto.roles became
    // optional for the minimal roster shape only -- see that field's own
    // comment in ui/src/api.ts.
    roles: user.roles ?? [user.role],
    permissions,
    mustChangePin: user.mustChangePin,
    defaultThemeId: user.defaultThemeId,
    // T16/DEC-133: this tenant's resolved PIN-length policy, read from the
    // same login response as everything else above.
    pinMinLength: body.pinMinLength,
    pinMaxLength: body.pinMaxLength,
  };
  saveSession(newSession);
  state.session = newSession;
  // PAY7 Section 2.8: seeds this tenant's own persisted pay keys into the
  // in-memory map, silently (restore fires no signal). Deliberately NOT
  // skipped on a fresh sign-in: a different employee signing in on the
  // SAME tablet after a sign-out must still see and reuse the SAME
  // pending key for the SAME ticket, since the safety property this
  // protects belongs to the ticket, not to who is currently signed in.
  for (const [target, key] of Object.entries(loadPersistedPayKeys(newSession.tenantId))) {
    state.payIdempotencyKeysByTarget.restore(target, key);
  }
  // AU12: a brand new session begins here, so the session-lost latch has
  // to be free again for IT. Without this, a session that ends and is
  // signed back into on the same page load would inherit a spent latch
  // from the previous one and never be able to report its own loss.
  resetSessionLostLatch();
  // AU11: arm the local idle countdown from the tenant's own window.
  noteSessionInactivityWindow(body.sessionInactivityMinutes);
  // Adversarial-gate correction (2026-08-24), Finding 5: this used to
  // skip setTheme entirely when defaultThemeId was null, which meant a
  // user with no personal theme silently INHERITED whatever theme the
  // DOM/localStorage happened to be carrying -- the previous user's own
  // choice, on a shared terminal, since logging out never reset it
  // either (see handleLogOut's own fix below). A null defaultThemeId now
  // explicitly applies the shop default (DEFAULT_THEME_ID, "Scissors &
  // Scotch"), the same way a real per-user value is applied, rather than
  // leaving whatever was already on screen untouched.
  chosenThemeId = undefined;
  setTheme(user.defaultThemeId ?? DEFAULT_THEME_ID);
  void refreshMemberBrand();
  refreshManagerControlsVisibility(newSession);
  return newSession;
}

async function submitLoginPin(userId: string, pin: string): Promise<void> {
  const config = terminalConfigState;
  if (config === undefined) {
    // Defensive only: the user picker (this function's one caller) is
    // never reachable without a configured terminal.
    return;
  }
  pinSubmitButton.disabled = true;
  // SEC2-S3/S4: this tablet's credential rides on sign-in as a header; the server refuses sign-in without it.
  const result = await login(
    config.apiBaseUrl,
    config.tenantId,
    { userId, pin },
    config.deviceToken,
  );
  pinSubmitButton.disabled = false;
  if (result.status === 401 && result.body.error === "device_not_recognized") {
    handleDeviceNotRecognized();
    return;
  }
  if (result.status !== 200) {
    handlePinEntryFailure(result);
    return;
  }
  establishSignedInSession(config, result.body);
  const { user } = result.body;
  if (user.mustChangePin) {
    // ONB2 S3: the sign-in that has to choose a PIN is the account's first; the setup banner follows it.
    firstSignInSetupPending = true;
    openPinEntry({ mode: "changePin", returnTo: "open-tabs", knownCurrentPin: pin });
    return;
  }
  pinEntry = undefined;
  await completeLoginAndShowOpenTabs();
}

async function submitChangePinStep(): Promise<void> {
  if (pinEntry === undefined || pinEntry.mode !== "changePin") {
    return;
  }
  const digits = pinEntry.digits;
  if (pinEntry.step === "current") {
    pinEntry.currentPin = digits;
    pinEntry.digits = "";
    pinEntry.step = "new";
    updateChangePinCopy();
    renderPinEntryDisplay();
    return;
  }
  if (pinEntry.step === "new") {
    pinEntry.newPin = digits;
    pinEntry.digits = "";
    pinEntry.step = "confirm";
    updateChangePinCopy();
    renderPinEntryDisplay();
    return;
  }
  // step === "confirm": both new-PIN entries are in hand. A client-side
  // match check first (a nicer failure than a round trip for a plain
  // typo), then the server call, which stays authoritative either way.
  const newPin = pinEntry.newPin ?? "";
  const newPinConfirm = digits;
  if (newPin !== newPinConfirm) {
    pinEntryErrorEl.textContent = "Those two entries did not match. Enter the new PIN again.";
    pinEntryErrorEl.hidden = false;
    pinEntry.digits = "";
    pinEntry.newPin = undefined;
    pinEntry.step = "new";
    updateChangePinCopy();
    renderPinEntryDisplay();
    return;
  }
  const oldPin = pinEntry.currentPin ?? "";
  const returnTo = pinEntry.returnTo ?? "open-tabs";
  const s = requireSession();
  pinSubmitButton.disabled = true;
  const result = await changePin(s.apiBaseUrl, s.tenantId, {
    idempotencyKey: crypto.randomUUID(),
    oldPin,
    newPin,
    newPinConfirm,
  });
  pinSubmitButton.disabled = false;
  if (result.status !== 201 && result.status !== 200) {
    // Restart at "current": the most likely cause is a mistyped current
    // PIN, which this flow (unlike login) only ever learns about here,
    // at the very end, since changePin is called once with all three
    // values rather than verified step by step.
    pinEntryErrorEl.textContent = result.body.message ?? "Could not change the PIN.";
    pinEntryErrorEl.hidden = false;
    pinEntry.digits = "";
    pinEntry.currentPin = undefined;
    pinEntry.newPin = undefined;
    pinEntry.step = "current";
    updateChangePinCopy();
    renderPinEntryDisplay();
    return;
  }
  const updatedSession: Session = { ...s, mustChangePin: result.body.user.mustChangePin };
  saveSession(updatedSession);
  state.session = updatedSession;
  pinEntry = undefined;
  showToast("PIN changed.", "success");
  // TEN17-F2: a forced-change session could not read its network's brand at sign-in; read it now.
  void refreshMemberBrand();
  if (returnTo === "profile") {
    showProfileScreen();
  } else if (returnTo === "pay-ticket") {
    // VER33-F1: session and state.orderId are untouched by a PIN change,
    // so the ticket the bartender was already looking at is still fully
    // loaded; return straight to it rather than routing back through
    // Open Tabs, matching the mockup's own "No dead end" reasoning.
    showRingScreen();
    // Gate-finding fix: the refusal that routed here may have come from a
    // read this ticket depends on to enable Pay at all (loadActiveSplitPlan,
    // left at "loading" rather than "failed" by its own pin_change_required
    // check above, precisely so this re-entry is the thing that resolves
    // it), not only from the payOrder/performClose/etc. action itself.
    // Re-run it now, the same pair handlePayStatusRetryClick already uses
    // to bring the rail back to a real, current answer, so Pay is properly
    // enabled again rather than stuck on "Checking split…" until some
    // unrelated re-render happens to fire it.
    if (state.orderId !== undefined) {
      void loadActiveSplitPlan();
      void refreshPaymentPreview();
    }
  } else {
    await completeLoginAndShowOpenTabs();
  }
}

async function handlePinSubmitClick(): Promise<void> {
  if (pinEntry === undefined || pinEntry.digits.length < currentPinMinLength()) {
    return;
  }
  if (pinEntry.mode === "login") {
    const userId = pinEntry.userId;
    if (userId === undefined) {
      return;
    }
    await submitLoginPin(userId, pinEntry.digits);
    return;
  }
  if (pinEntry.mode === "approval") {
    const approverId = pinEntry.userId;
    if (approverId === undefined) {
      return;
    }
    await submitApprovalPin(approverId, pinEntry.digits);
    return;
  }
  await submitChangePinStep();
}

// ---------------------------------------------------------------------
// User-tile picker
// ---------------------------------------------------------------------

function renderUserTiles(users: UserDto[]): void {
  userTileListEl.innerHTML = "";
  if (users.length === 0) {
    userPickerEmptyEl.hidden = false;
    userPickerEmptyEl.textContent = "No one is set up on this terminal yet.";
    return;
  }
  userPickerEmptyEl.hidden = true;
  for (const user of sortByName(users, (listed) => listed.displayName)) {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = "user-tile";
    tile.dataset["testid"] = "user-tile";
    tile.dataset["userId"] = user.id;
    tile.setAttribute("aria-label", `Sign in as ${user.displayName}`);
    const name = document.createElement("span");
    name.className = "user-tile-name";
    // UIFIX5: two lines of equal height, first name over the rest of the name. Still the name only (DEC-100/SEC3
    // keeps a role off this screen), and the text content still reads as the whole display name.
    const nameParts = user.displayName.trim().split(/\s+/);
    const firstName = document.createElement("span");
    firstName.className = "user-tile-first";
    firstName.textContent = nameParts[0] ?? user.displayName;
    const restName = document.createElement("span");
    restName.className = "user-tile-rest";
    restName.textContent = nameParts.slice(1).join(" ");
    name.append(firstName, " ", restName);
    // DEC-100/SEC3: the sign-in screen shows names only, never a role or
    // right, so unlike the approval picker's degraded path this tile
    // never appends a `.user-tile-role-badge` element at all.
    tile.append(name);
    tile.addEventListener("click", () => {
      openPinEntry({ mode: "login", userId: user.id, displayName: user.displayName });
    });
    userTileListEl.append(tile);
  }
}

async function loadUserPicker(): Promise<void> {
  const config = terminalConfigState;
  if (config === undefined) {
    showTerminalSetupScreen();
    return;
  }
  renderPickerDeviceLine(config);
  renderBrandName();
  void refreshDeviceIdentity();
  userTileListEl.innerHTML = '<p class="tabs-loading">Loading…</p>';
  userPickerEmptyEl.hidden = true;
  // Unauthenticated by design: this IS the pre-login screen (api.ts's own
  // listUsers doc comment), and auto-bootstraps the tenant's first
  // manager the first time it finds zero users.
  const result = await listUsers(
    config.apiBaseUrl,
    config.tenantId,
    { activeOnly: true },
    config.deviceToken,
  );
  // SEC2-S4: a tablet the server no longer recognises (a manager revoked it) learns it here, before any PIN is
  // typed, and goes to the setup screen with the plain reason.
  if (result.status === 401 && result.body.error === "device_not_recognized") {
    handleDeviceNotRecognized();
    return;
  }
  if (result.status !== 200) {
    userTileListEl.innerHTML = "";
    userPickerEmptyEl.hidden = false;
    userPickerEmptyEl.textContent =
      result.body.message ?? "Could not load the staff list. Check your connection.";
    return;
  }
  renderUserTiles(result.body.users);
}

/** SEC2-S3 (DEC-321, mockup sec2-s3-13): the picker's footer is the device's name alone (UX99: the location is the heading above), and has
 * no "Change terminal" button: a bartender must not be able to unbind the location's tablet mid-service. SEC2-S4: every
 * config that reaches the picker has a credential (one without it is "not set up" and shows the setup screen), so
 * the old "Set up this tablet with a code" shortcut for a tablet with none is gone. */
function renderPickerDeviceLine(config: TerminalConfig): void {
  const line = deviceLineFor(config);
  userPickerDeviceLineEl.hidden = line === undefined;
  userPickerDeviceNameEl.textContent = line?.name ?? "";
}

/** A redemption succeeded on the setup screen: the credential and the location's names are the terminal's own now. */
function handleTerminalConnected(config: TerminalConfig): void {
  saveTerminalConfig(config);
  terminalConfigState = config;
  renderBrandName();
  // PAY5-F1: defensive; #app-topbar itself stays hidden at this
  // pre-login screen (hideAllScreens/showTopbarChrome), so this has no
  // visible effect today, kept for the same reason every other
  // terminalConfigState assignment carries this call.
  renderTopbarTerminalSegment();
}

/** "Go to sign in" on the setup screen's success view. ONBW1-S1: a device bound by a code that was issued with a
 * welcome link goes on to the PIN choice instead, since the owner has no PIN to sign in with yet. */
function handleTerminalSetupContinue(): void {
  const config = terminalConfigState;
  if (config !== undefined) {
    void welcomeFlow.resumeIfPending(config).then((pending) => {
      if (!pending) {
        showUserPickerScreen();
        void loadUserPicker();
      }
    });
    return;
  }
  showUserPickerScreen();
  void loadUserPicker();
}

/** ONBW1-S1: the welcome link's token, read once from the address fragment and removed from the address bar at once,
 * so it is not left in the page's history or a copied address. Undefined when this page was not opened by one. */
function takeWelcomeToken(): string | undefined {
  const token = parseWelcomeFragment(window.location.hash);
  if (token === undefined) return undefined;
  try {
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
  } catch {
    // A browser that refuses the history API still opens the link; the fragment simply stays in the address bar.
  }
  return token;
}

/** The server no longer recognises this tablet's credential (a manager revoked it, brief 4.6): forget it and
 * show the setup screen with the plain reason. Only a sign-in attempt can learn this, so the PIN pad is what is
 * on screen; its entry state goes with it. */
function handleDeviceNotRecognized(): void {
  pinEntry = undefined;
  clearTerminalConfig();
  terminalConfigState = undefined;
  renderBrandName();
  showTerminalSetupScreen({ notice: DISCONNECTED_NOTICE });
}

// ---------------------------------------------------------------------
// My Profile
// ---------------------------------------------------------------------

function handleOpenMyProfileClick(): void {
  showProfileScreen();
}

/** RPT1-F4 (DEC-285): the account menu's My Tips opens the floor sheet over
 * whatever screen is showing. Nothing gates it: every signed-in employee has
 * their own tips, and the sheet reads only their own row. */
function handleOpenMyTipsClick(): void {
  const session = state.session;
  if (session === undefined) {
    return;
  }
  openMyTips(session);
}

function handleProfileBackClick(): void {
  // See showProfileScreen's own comment: always Open Tabs, by design.
  showOpenTabsScreen();
}

function handleProfileChangePinClick(): void {
  openPinEntry({ mode: "changePin", returnTo: "profile" });
}

// ---------------------------------------------------------------------
// USR4 (docs/build-briefs/USR4-my-profile-details.md), DEC-210/DEC-211
// corrected: "Your details" -- display name, first name, last name,
// email, phone, all editable, self-service, via
// PATCH .../me/profile (server.ts, reuses updateUserProfile as-is).
//
// profileLoaded is the baseline the dirty check and the "display name
// follows first name" behavior both compare against: the last values
// this form is known to agree with the server on (the initial load, or
// the values right after a successful save). Reset on logout
// (resetProfileFormState, called from handleLogOut) for the same
// shared-tablet reason admin.invalidate() resets admin.ts's module
// state: a stale baseline from the PREVIOUS employee's session must
// never leak into the next employee's Save/dirty-check math.
// ---------------------------------------------------------------------

interface ProfileLoadedValues {
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

let profileLoaded: ProfileLoadedValues | undefined;
// Corrected DEC-211: Display name follows First name live only while it
// still equals whatever First name held when the form was last synced.
// True on load only if the loaded displayName and firstName already
// match; set false the instant the person types directly into Display
// name (see handleProfileDisplayNameInput), and stays false for the
// rest of this editing session even if First name is later typed back
// to the old value.
let profileDisplayNameFollowsFirstName = true;
// Guards handleProfileDisplayNameInput against the input EVENT this
// same module fires when it mirrors First name into Display name below
// (assigning .value does not itself fire "input", but this file wires
// the mirror through the same listener path other screens use for
// consistency, so the guard stays cheap insurance rather than a no-op).
let profileSyncingDisplayName = false;

function resetProfileFormState(): void {
  profileLoaded = undefined;
  profileDisplayNameFollowsFirstName = true;
  profileSyncingDisplayName = false;
}

function clearProfileFieldErrors(): void {
  profileDisplayNameErrorEl.textContent = "";
  profileFirstNameErrorEl.textContent = "";
  profileLastNameErrorEl.textContent = "";
  profileEmailErrorEl.textContent = "";
  profilePhoneErrorEl.textContent = "";
}

/** Save stays disabled until at least one field differs from
 * profileLoaded, and re-disables immediately after a successful save
 * (this function is called again once profileLoaded is refreshed). */
function updateProfileDirtyState(): void {
  if (profileLoaded === undefined) {
    profileSaveButton.disabled = true;
    return;
  }
  const dirty =
    profileDisplayNameInput.value !== profileLoaded.displayName ||
    profileFirstNameInput.value !== profileLoaded.firstName ||
    profileLastNameInput.value !== profileLoaded.lastName ||
    profileEmailInput.value !== profileLoaded.email ||
    profilePhoneInput.value !== profileLoaded.phone;
  profileSaveButton.disabled = !dirty;
}

function handleProfileFirstNameInput(): void {
  profileFirstNameErrorEl.textContent = "";
  if (profileDisplayNameFollowsFirstName) {
    profileSyncingDisplayName = true;
    profileDisplayNameInput.value = profileFirstNameInput.value;
    profileSyncingDisplayName = false;
  }
  updateProfileDirtyState();
}

function handleProfileDisplayNameInput(): void {
  profileDisplayNameErrorEl.textContent = "";
  if (!profileSyncingDisplayName) {
    // A direct edit, from the person themselves, not the mirror above:
    // decouple for the rest of this editing session, per corrected
    // DEC-211 ("once the contents is overwritten... those are two
    // distinct fields").
    profileDisplayNameFollowsFirstName = false;
  }
  updateProfileDirtyState();
}

function handleProfileOtherFieldInput(errorEl: HTMLElement): void {
  errorEl.textContent = "";
  updateProfileDirtyState();
}

/** Loads the caller's own current profile fields fresh every time My
 * Profile is shown (showProfileScreen's own comment). Uses PATCH
 * .../me/profile with an EMPTY body rather than a new GET route:
 * updateUserProfile's own no-op branch (src/domain/users.ts, "Nothing
 * to write, but the caller still expects the current record back") was
 * built for exactly this, so no server change beyond the route this
 * brief already adds is needed. */
async function loadProfileDetails(): Promise<void> {
  const s = requireSession();
  const generationAtStart = sessionGeneration;
  const result = await updateMyProfile(s.apiBaseUrl, s.tenantId, {});
  // AU13-F1 pattern: discard a response that lands after a sign-out (or
  // a different sign-in) happened while this request was in flight.
  if (generationAtStart !== sessionGeneration) {
    return;
  }
  if (result.status !== 200) {
    showToast(result.body.message ?? "Could not load your profile.", "danger");
    return;
  }
  applyLoadedProfile(result.body.user);
}

function applyLoadedProfile(user: {
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}): void {
  profileLoaded = {
    displayName: user.displayName,
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    email: user.email ?? "",
    phone: user.phone ?? "",
  };
  profileDisplayNameInput.value = profileLoaded.displayName;
  profileFirstNameInput.value = profileLoaded.firstName;
  profileLastNameInput.value = profileLoaded.lastName;
  profileEmailInput.value = profileLoaded.email;
  profilePhoneInput.value = profileLoaded.phone;
  profileDisplayNameFollowsFirstName = profileLoaded.displayName === profileLoaded.firstName;
  clearProfileFieldErrors();
  updateProfileDirtyState();
}

/** Maps a validation/uniqueness error back to the ONE field it is
 * about, per the brief's inline-error convention -- never a generic
 * toast for a field-shaped failure. updateUserProfile's own
 * ValidationError messages name the field literally ("displayName"
 * must not be blank", etc, src/domain/users.ts), and
 * DuplicateActiveNameError is always about displayName, the only field
 * the uniqueness constraint applies to. Anything else (a network
 * failure, an unexpected 5xx) falls back to the toast. */
function applyProfileSaveError(body: Partial<ApiErrorBody>): void {
  if (body.error === "duplicate_active_name") {
    profileDisplayNameErrorEl.textContent = "That name is already in use.";
    return;
  }
  const message = body.message ?? "Could not save your profile.";
  if (message.includes('"displayName"')) {
    profileDisplayNameErrorEl.textContent = message;
  } else if (message.includes('"firstName"')) {
    profileFirstNameErrorEl.textContent = message;
  } else if (message.includes('"lastName"')) {
    profileLastNameErrorEl.textContent = message;
  } else if (message.includes('"email"')) {
    profileEmailErrorEl.textContent = message;
  } else if (message.includes('"phone"')) {
    profilePhoneErrorEl.textContent = message;
  } else {
    showToast(message, "danger");
  }
}

// Named separately from the `const ... = {}` assignment below, rather
// than an inline object-literal type there: scripts/check-no-client-
// pii-in-logs.ts's own outbox-payload pattern (deliberately naive, per
// that script's own header) matches `payload:\s*\{[^}]*` followed by
// any of these field names, which a same-line inline type would trip as
// a false positive. This is a client request body being BUILT for
// updateMyProfile, never a log/outbox sink.
interface ProfileSaveFields {
  displayName?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

async function handleProfileSaveClick(): Promise<void> {
  if (profileLoaded === undefined || profileSaveButton.disabled) {
    // Defensive only: a disabled button fires no click, and Save is
    // never enabled before profileLoaded lands. Client-side disabling
    // is presentation, never the control that matters (the brief's own
    // false-green to avoid) -- the real control is the server request
    // simply never being issued from here when there is nothing dirty
    // or nothing loaded yet to diff against.
    return;
  }
  const s = requireSession();
  clearProfileFieldErrors();
  const loaded = profileLoaded;
  const profileSaveFields: ProfileSaveFields = {};
  if (profileDisplayNameInput.value !== loaded.displayName) {
    profileSaveFields.displayName = profileDisplayNameInput.value;
  }
  if (profileFirstNameInput.value !== loaded.firstName) {
    profileSaveFields.firstName =
      profileFirstNameInput.value === "" ? null : profileFirstNameInput.value;
  }
  if (profileLastNameInput.value !== loaded.lastName) {
    profileSaveFields.lastName =
      profileLastNameInput.value === "" ? null : profileLastNameInput.value;
  }
  if (profileEmailInput.value !== loaded.email) {
    profileSaveFields.email = profileEmailInput.value === "" ? null : profileEmailInput.value;
  }
  if (profilePhoneInput.value !== loaded.phone) {
    profileSaveFields.phone = profilePhoneInput.value === "" ? null : profilePhoneInput.value;
  }
  profileSaveButton.disabled = true;
  const result = await updateMyProfile(s.apiBaseUrl, s.tenantId, profileSaveFields);
  if (result.status !== 200) {
    applyProfileSaveError(result.body);
    // Still dirty: re-enable so the person can correct the field and
    // try again, matching the brief's Artboard 2 ("Save enabled ... but
    // tapping it re-shows this same message rather than submitting").
    updateProfileDirtyState();
    return;
  }
  const displayNameChanged =
    profileSaveFields.displayName !== undefined &&
    result.body.user.displayName !== loaded.displayName;
  applyLoadedProfile(result.body.user);
  showToast("Profile updated.", "success");
  if (displayNameChanged) {
    // The topbar sign-in name updates immediately, no reload -- the
    // session's own employeeId field carries displayName (see
    // submitLoginPin's own comment on that naming).
    const updatedSession: Session = { ...s, employeeId: result.body.user.displayName };
    saveSession(updatedSession);
    state.session = updatedSession;
    sessionEmployeeName.textContent = result.body.user.displayName;
    profileNameEl.textContent = result.body.user.displayName;
  }
}

/** Sixteenth amendment item 1/2: the handler behind a theme change,
 * called from exactly one place since UX21 removed the account-menu
 * dropdown's own Theme item (handleProfileThemeChange, My Profile's
 * picker). Applies the choice instantly (setTheme), then persists it to
 * this user's profile via the existing PATCH .../me/theme route - "theme
 * becomes a per-user profile setting stored server-side... no longer a
 * location-level control" (Amendment 2026-08-22 sixteenth). On a save failure,
 * the local switch is left applied (never silently reverted out from
 * under the bartender), only the toast reports the profile write did not
 * stick. */
async function handleAccountThemeChange(themeId: string): Promise<void> {
  const s = requireSession();
  // TEN17-F4: the Default option saves NULL; the user then follows the network brand (or the product default).
  const saved = savedThemeForPick(themeId);
  chosenThemeId = saved;
  setTheme(startingThemeId(saved, memberBrand, DEFAULT_THEME_ID));
  const result = await setMyTheme(s.apiBaseUrl, s.tenantId, saved);
  if (result.status !== 200) {
    showToast(result.body.message ?? "Could not save your color scheme.", "danger");
    return;
  }
  const updatedSession: Session = { ...s, defaultThemeId: saved };
  saveSession(updatedSession);
  state.session = updatedSession;
}

async function handleProfileThemeChange(): Promise<void> {
  await handleAccountThemeChange(profileThemeSelect.value);
}

// ---------------------------------------------------------------------
// Login / logout
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// AU11: an expired session returns to the PIN screen, everywhere
// ---------------------------------------------------------------------

/** The tenant's own inactivity window, handed to us by the login and
 * session responses. Undefined until one of them lands, which is exactly
 * the state where there is no session to expire. */
let sessionInactivityMinutes: number | undefined;
let idleTimer: ReturnType<typeof setTimeout> | undefined;

/** Fires when the app finds out a session is over, from any of three
 * directions, each carrying its own `reason`: our own idle timer firing
 * ("idle"), a real 401 the server sent back ("revoked" -- a revoked
 * session, a deactivated account, and a PIN change that swept other
 * sessions all look identical from here), or a call api.ts caught before
 * it ever reached the network because there was no token to send
 * ("no-token", AU12's own case).
 *
 * Reuses handleLogOut rather than inventing a second teardown. Everything
 * it does is what has to happen here too: clear the token, clear the
 * stored session, reset the theme to the shop default so one person's
 * theme does not greet the next, drop cached menu and tab state, and land
 * on the user picker. A second implementation of that list would drift
 * from it.
 *
 * AU12: returns whether it actually tore a session down, so api.ts's
 * noteResponse can spend its one-shot latch on that fact instead of on
 * the mere arrival of a 401. Returning false here (state.session already
 * undefined, nothing to do) must leave the latch free, or a later, REAL
 * lost session on the SAME page load can never be reported again -- see
 * this function's own AU12 build brief for the report this produced.
 *
 * AU13: the toast used to be one constant sentence that blamed
 * inactivity for every cause, including the two it was never true of.
 * Each reason below gets its own true sentence, and each points at a
 * different recovery: idle needs nothing but signing back in, revoked is
 * the one case where a manager might need to know, and no-token means
 * this terminal was never signed in to begin with. */
function handleSessionLost(reason: SessionLostReason): boolean {
  if (state.session === undefined) {
    return false;
  }
  if (supportTab !== undefined) {
    endSupportTab("Support session ended", false);
    return true;
  }
  handleLogOut();
  showToast(sessionLostMessage(reason), "neutral");
  return true;
}

/** The three messages AU13 requires, kept together so their wording stays
 * comparable at a glance rather than scattered across the callers that
 * pick a reason. Each sentence is true only of its own cause: "idle" is
 * the pre-AU13 wording, unchanged, because it was always accurate for
 * that caller; "revoked" never says "timeout", since the server, not the
 * clock, ended this one, and it names the recovery that differs -- tell
 * a manager if it recurs, because a revoked session and a deactivated
 * account look identical from the floor and only a manager can tell them
 * apart; "no-token" is not a loss at all, it is a terminal that was never
 * signed in, so it sends the reader straight to signing in rather than to
 * investigating anything. */
function sessionLostMessage(reason: SessionLostReason): string {
  switch (reason) {
    case "idle":
      return "Signed out after a period of inactivity. Sign in again.";
    // Deliberately shares no sentence with payCloseErrorMessages.ts's own
    // generic failure tail ("Tell a manager if this keeps happening"),
    // which belongs to a DIFFERENT situation: a pay or close that failed
    // for a reason other than the session. Two causes that share a closing
    // sentence are two causes a reader cannot tell apart, which is the
    // defect this whole item is about, and it would also make
    // m13-pay-close-auth-error.spec.ts's own not.toContainText assertion
    // ambiguous about which of the two it was guarding against.
    case "revoked":
      return "Signed out: this sign-in is no longer valid. Ask a manager to check the account.";
    case "no-token":
      return "This terminal is not signed in. Sign in to continue.";
  }
}

/** Restarts the idle countdown. Called on real user interaction, and
 * after every authenticated response, so the client's idea of "idle"
 * tracks the same thing the server's sliding window does.
 *
 * NOT a poll. The obvious "ask /auth/session every minute" is worse than
 * the bug it would fix: that route is authenticated, so each poll slides
 * the server's window forward and the session would never expire at all.
 * This mirrors the rule locally instead. */
function rearmIdleTimer(): void {
  if (idleTimer !== undefined) {
    clearTimeout(idleTimer);
    idleTimer = undefined;
  }
  if (state.session === undefined || sessionInactivityMinutes === undefined) {
    return;
  }
  // A small grace beyond the server's window, deliberately. If the client
  // fired FIRST it would sign people out who still had a valid session,
  // turning a correctness fix into a nuisance; landing just after means
  // the server is always the authority on whether the session is really
  // gone.
  const graceMs = 5_000;
  // AU13: wrapped rather than passed bare. setTimeout hands its callback
  // the timer id as an argument in some runtimes, and handleSessionLost
  // now takes a real parameter of its own ("idle" here) that a stray
  // timer id must never be mistaken for.
  idleTimer = setTimeout(
    () => handleSessionLost("idle"),
    sessionInactivityMinutes * 60_000 + graceMs,
  );
}

function noteSessionInactivityWindow(minutes: number | undefined): void {
  if (typeof minutes === "number" && Number.isFinite(minutes) && minutes > 0) {
    sessionInactivityMinutes = minutes;
  }
  rearmIdleTimer();
}

function startIdleWatch(): void {
  setSessionLostHandler(handleSessionLost);
  // pointerdown and keydown, not every mousemove: this mirrors "the
  // person did something", which is what the server counts, and a
  // mousemove listener firing hundreds of times a second to reset a
  // timeout is a cost with no matching signal.
  for (const eventName of ["pointerdown", "keydown"] as const) {
    document.addEventListener(eventName, () => rearmIdleTimer(), { passive: true });
  }
}

/** SHIFT1: the two sign-outs a person chooses (the account menu and the blocked card-reader screen) come here
 * first. With unpaid tabs the user opened, one sheet offers Show my tabs or Sign out; with none it signs out at
 * once. It reads the list already in memory, so it never waits on the network and never blocks sign-out. Idle
 * and lost sessions call handleLogOut directly and skip the sheet. */
function requestLogOut(): void {
  const unpaid = countUnpaidTabsOpenedBy(
    state.openTabs.map((order) => ({
      openedByUserId: order.opened_by_user_id ?? null,
      paid: tabPaidByOrderId.get(order.id),
      drinkCount: tabDrinkCountByOrderId.get(order.id),
    })),
    sessionOrUndefined()?.userId,
  );
  if (supportTab !== undefined || unpaid === 0) {
    handleLogOut();
    return;
  }
  // LOUNGERMAJ1 (lounger-19): the sheet names the tab and its amount, and says it stays open when the lounger signs out.
  const unpaidLines = state.openTabs
    .filter(
      (order) =>
        order.opened_by_user_id === sessionOrUndefined()?.userId &&
        tabPaidByOrderId.get(order.id) === false &&
        (tabDrinkCountByOrderId.get(order.id) ?? 0) > 0,
    )
    .map((order) => ({
      label: tabDisplayLabel(order.name, order.guest_labels),
      totalCents: tabTotalCentsByOrderId.get(order.id),
    }));
  openChoiceSheet({
    title: unpaidTabsTitle(unpaid),
    ...(unpaidLines.length === 0
      ? {}
      : { subtitle: unpaidTabsSentence(unpaidLines, formatCents) }),
    options: [
      { id: "show", label: "Show my tabs", variant: "primary", testid: "sign-out-show-tabs" },
      { id: "out", label: "Sign out anyway", testid: "sign-out-confirm" },
    ],
    onPick: (id) => {
      if (id === "out") {
        handleLogOut();
        return;
      }
      showMyUnpaidTabs();
    },
  });
}

/** SHIFT1: Open Tabs with Mine and Unpaid selected and nothing typed. Goes through the screen's own Home, so a
 * screen that tidies up on the way out still does. */
function showMyUnpaidTabs(): void {
  state.tabFilterMode = "mine";
  state.tabStatusFilter = "unpaid";
  state.tabOver1Hr = false;
  state.tabSearchText = "";
  tabSearchInput.value = "";
  const registration = SCREENS.find((screen) => screen.id === currentScreenId);
  (registration?.onHome ?? defaultTopbarHome)();
  applyTabSearch();
}

function handleLogOut(): void {
  if (supportTab !== undefined) {
    // Log out in a support tab is End session.
    endSupportTab("Support session ended", true);
    return;
  }
  const session = state.session;
  if (session !== undefined) {
    // Best-effort per logoutRequest's own doc comment: called while the
    // bearer token is still set (authHeaders reads it synchronously the
    // moment this fetch is issued, below), result and errors both
    // ignored either way.
    void logoutRequest(session.apiBaseUrl, session.tenantId);
  }
  setAuthToken(undefined);
  getHelp.reset();
  helpPanel.reset();
  willy.reset();
  clearSession();
  // ONB2 S3: the first-sign-in banner and its pending flag belong to the session that is ending.
  firstDayBanner.hidden = true;
  firstSignInSetupPending = false;
  resetSetupWizard();
  cardNudge.reset();
  renderSettingsSetupEntry();
  // UX108: nothing typed under this sign-in outlives it.
  clearAllDrafts();
  state.session = undefined;
  // PL3: no payment-link poll outlives the session that started it.
  stopPaymentLinkWatches();
  // AU13-F1: advance the session generation, so any floor loader response
  // that lands after this point (even one already awaiting when this
  // runs) reads sessionIsCurrent() as false and discards itself rather
  // than writing a toast, a module binding or the DOM. Written as
  // `sessionGeneration = sessionGeneration + 1` rather than `+=`, matching
  // admin.ts's own loadGeneration reasoning: a future coverage gate
  // modeled on check:admin-invalidate-coverage would not see a compound
  // assignment as clearing anything. NEVER reset to 0.
  sessionGeneration = sessionGeneration + 1;
  // AU11: stop the idle countdown with the session it belongs to, so a
  // signed-out terminal cannot fire a "signed out for inactivity" toast
  // at whoever signs in next.
  if (idleTimer !== undefined) {
    clearTimeout(idleTimer);
    idleTimer = undefined;
  }
  sessionInactivityMinutes = undefined;
  // Adversarial-gate correction (2026-08-24), Finding 5: theme used to be
  // applied at login, at a profile change, and on resume, but never reset
  // at logout, so it leaked across users on a shared terminal -- a
  // defaultThemeId:null user (no personal theme picked) would silently
  // inherit whatever theme the PREVIOUS user last had on screen, read
  // straight out of localStorage/the DOM, since nothing here ever put it
  // back. Reset to the shop default now, on every logout, so the user
  // picker that appears next (and whichever user logs in after it, if
  // their own defaultThemeId is null) always starts from a known,
  // shop-owned baseline rather than an inherited one.
  memberBrand = null;
  chosenThemeId = undefined;
  void syncNetworkLogo(null);
  populateThemeSelects();
  setTheme(DEFAULT_THEME_ID);
  // SET16: the admin screen's own caches, for the identical reason as
  // the theme reset directly above and found the same way. That 2026-08-24
  // fix reset the theme and the menu state below and stopped there,
  // because the admin caches were not what anyone was looking at. Without
  // this, every admin load latch survives a sign-out, so the next
  // employee to open Settings on this tablet is shown records fetched
  // under the PREVIOUS employee's session and permissions, with no
  // request made and therefore no permission resolved for the person
  // actually reading the screen.
  admin.invalidate();
  // M12-F1: the void-history and closeout screens' own rows and business
  // date, cleared for the same shared-tablet reason as admin.invalidate()
  // directly above; hideAllScreens() only sets `hidden` and never clears
  // rendered content.
  resetCloseoutAndVoidHistoryScreens();
  // UX116: every figure on a hub is wiped with the session, reads in flight are dropped.
  resetFunctionHubs();
  currentHubId = undefined;
  // CASH1-S1-U1: the drawer sheet, the open drawer and the cash switch are wiped with the session.
  cashDrawer.reset();
  // CASH1-S2-U1: the Cash and Change due screens, and the tender choice, go with the session too.
  cashTender.reset();
  refundPaymentTender.clear();
  state.payCashOffered = undefined;
  state.payCashLockedLabel = undefined;
  state.payTender = undefined;
  // USR4: My Profile's "Your details" baseline and follow-first-name
  // state, cleared for the same shared-tablet reason as the two resets
  // directly above -- the next employee's Save/dirty-check math must
  // never be computed against the PREVIOUS employee's loaded profile.
  resetProfileFormState();
  notificationsPage.reset();
  state.menuDrinks = [];
  state.menuItems = [];
  popularMenuIds = [];
  popularReadState = "loading";
  resetMenuFilters();
  state.menuDetailCache.clear();
  menuDetailWarming.clear();
  resetPurchasingCartSessionState();
  state.openTabs = [];
  resetTabFind();
  state.recentlyClosedTabs = [];
  // AU8: never leaks the previous session's own exceptions view across a
  // logout on a shared terminal.
  state.managerExceptions = [];
  state.menuCounts = {};
  topbarApprovalBadge.hidden = true;
  networkOfferNotice.reset();
  closePurchaseCancelDialog();
  state.startingStockMenu = "unknown";
  state.managerExceptionOrderLabels.clear();
  state.managerExceptionResolvingId = undefined;
  state.managerExceptionBusyId = undefined;
  state.managerExceptionClearHoldChoice = undefined;
  // AU16: every OTHER scrim-backed floor sheet, closed the same defensive
  // way AU8 already closes the manager-exceptions sheet directly below, so
  // none of them can survive onto the next screen (the user picker, or
  // whichever session signs in next) whatever was open at the moment sign-
  // out happened.
  closeAllFloorSheets();
  closeManagerExceptionsSheet();
  renderManagerExceptionsBadge();
  // RBAC track defensive reset: the account-menu Log out button is not
  // reachable while the approval picker/pin screens are showing (neither
  // is nested under a screen that carries one), so this is not believed
  // reachable in practice, but a stuck, never-resolved requestApproval
  // promise would otherwise strand its caller forever if it somehow were.
  activeApprovalFlow?.settle({ canceled: true });
  pinEntry = undefined;
  resetTicketState();
  state.matchMissCount = 0;
  state.lastMatchMissDrinkName = undefined;
  state.lastPayCloseErrorDetail = undefined;
  renderMatchMissDebug();
  renderLastPayCloseErrorDebug();
  setSettingsControlsHidden(true);
  setTerminalControlsHidden(true);
  // TerminalConfig deliberately survives a logout (session.ts's own
  // header); only a revoked device credential clears it (SEC2-S3). Falls
  // back to terminal setup only in the defensive case where somehow none
  // is stored.
  if (terminalConfigState === undefined) {
    showTerminalSetupScreen();
    return;
  }
  showUserPickerScreen();
  void loadUserPicker();
}

// ---------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// UX108 (docs/build-briefs/UX108-refresh-keeps-the-screen.md): the screen's address, and coming back to it.
// ---------------------------------------------------------------------

/** The signed in person's draft scope: the tenant and the person, so one person's draft is never offered to another. */
function draftScope(): DraftScope | undefined {
  const current = state.session;
  return current === undefined ? undefined : { tenantId: current.tenantId, userId: current.userId };
}

function storeDraft(name: string, value: unknown): void {
  const scope = draftScope();
  if (scope !== undefined) saveDraft(scope, name, value);
}

function readDraft(name: string): unknown {
  const scope = draftScope();
  return scope === undefined ? undefined : loadDraft(scope, name);
}

function dropDraft(name: string): void {
  const scope = draftScope();
  if (scope !== undefined) clearDraft(scope, name);
}

/** The sheet a ticket had open. Which sheet and which line (an opaque id), never anything typed in it: a Change price
 * amount and reason are financial and are keyed again on purpose, and an Add guest search holds a guest's name or
 * phone. The Pay, tip, split, refund, payment link, card and approval sheets are never recorded at all. */
interface TicketSheetDraft {
  readonly orderId: string;
  readonly sheet: "lineEdit" | "priceChange" | "addGuest";
  readonly orderLineId?: string;
}

const TICKET_SHEET_DRAFT = "ticketSheet";

function noteTicketSheetOpen(sheet: TicketSheetDraft["sheet"], orderLineId?: string): void {
  const orderId = state.orderId;
  if (orderId === undefined) return;
  storeDraft(TICKET_SHEET_DRAFT, {
    orderId,
    sheet,
    ...(orderLineId === undefined ? {} : { orderLineId }),
  });
}

function readTicketSheetDraft(): TicketSheetDraft | undefined {
  const record = asRecord(readDraft(TICKET_SHEET_DRAFT));
  const orderId = asString(record?.["orderId"]);
  const sheet = asString(record?.["sheet"]);
  const orderLineId = asString(record?.["orderLineId"]);
  if (orderId === undefined) return undefined;
  if (sheet !== "lineEdit" && sheet !== "priceChange" && sheet !== "addGuest") return undefined;
  return orderLineId === undefined ? { orderId, sheet } : { orderId, sheet, orderLineId };
}

/** Reopens the sheet a ticket had open, once the ticket itself is back. Every sheet it can reopen asks for an
 * explicit tap before it does anything. */
async function restoreTicketSheet(
  draft: TicketSheetDraft | undefined,
  orderId: string,
): Promise<void> {
  if (draft === undefined || draft.orderId !== orderId) return;
  switch (draft.sheet) {
    case "addGuest":
      openAddGuestSheet();
      return;
    case "lineEdit":
      if (draft.orderLineId !== undefined) openLineEditSheet(draft.orderLineId);
      return;
    case "priceChange":
      if (draft.orderLineId !== undefined) await openPriceChangeSheet(draft.orderLineId);
      return;
  }
}

const RECEIVE_STOCK_DRAFT = "receiveStock";
const RECEIVE_ORDER_DRAFT = "receiveOrder";

/** Receive stock (no purchase order): the item picked and what has been typed for it. The vendor pick is not kept
 * (the vendor control has no way to be set from outside; the person picks again). Recording stays an explicit tap. */
function saveReceiveStockDraft(): void {
  const product = purchasingOutsideOrderSelectedProduct;
  if (product === undefined || purchasingOutsideOrderEntryEl.hidden) {
    dropDraft(RECEIVE_STOCK_DRAFT);
    return;
  }
  storeDraft(RECEIVE_STOCK_DRAFT, {
    productId: product.id,
    quantity: purchasingOutsideOrderQuantityInput.value,
    unit: selectedOutsideOrderUnit() ?? "",
    cost: purchasingOutsideOrderCostInput.value,
    reference: purchasingOutsideOrderReferenceInput.value,
  });
}

/** Puts the item and the typed values of a Receive stock draft back, when the item is still there to receive. */
async function restoreReceiveStockDraft(draft: unknown, session: Session): Promise<void> {
  const record = asRecord(draft);
  const productId = asString(record?.["productId"]);
  if (record === undefined || productId === undefined) return;
  const result = await getLiquorProduct(session.apiBaseUrl, session.tenantId, productId);
  if (result.status !== 200 || !result.body.liquorProduct.active) {
    dropDraft(RECEIVE_STOCK_DRAFT);
    return;
  }
  handleSelectOutsideOrderProduct(result.body.liquorProduct);
  purchasingOutsideOrderQuantityInput.value = (asString(record["quantity"]) ?? "").slice(0, 20);
  purchasingOutsideOrderCostInput.value = (asString(record["cost"]) ?? "").slice(0, 20);
  purchasingOutsideOrderReferenceInput.value = (asString(record["reference"]) ?? "").slice(
    0,
    purchasingOutsideOrderReferenceInput.maxLength > 0
      ? purchasingOutsideOrderReferenceInput.maxLength
      : 80,
  );
  const unit = asString(record["unit"]);
  const radio = Array.from(
    purchasingOutsideOrderUnitPickerEl.querySelectorAll<HTMLInputElement>('input[type="radio"]'),
  ).find((candidate) => candidate.dataset["value"] === unit);
  if (radio !== undefined) {
    radio.checked = true;
    radio.dispatchEvent(new Event("change", { bubbles: true }));
  }
  saveReceiveStockDraft();
}

/** Receive against an order: what has been typed on each line, by the line's opaque id. */
function saveReceiveOrderDraft(): void {
  const orderId = state.receivingOrderId;
  if (orderId === undefined || purchasingReceivingScreen.hidden) return;
  const lines: { lineId: string; quantity: string; unit: string; cost: string }[] = [];
  for (const row of Array.from(
    purchasingReceivingLinesEl.querySelectorAll<HTMLElement>("[data-purchase-order-line-id]"),
  )) {
    const lineId = row.dataset["purchaseOrderLineId"];
    const quantity =
      row.querySelector<HTMLInputElement>(
        '[data-testid="purchasing-receiving-line-quantity-input"]',
      )?.value ?? "";
    const cost =
      row.querySelector<HTMLInputElement>('[data-testid="purchasing-receiving-line-cost-input"]')
        ?.value ?? "";
    const unitPicker = row.querySelector<HTMLElement>(
      '[data-testid="purchasing-receiving-line-unit-picker"]',
    );
    const unit = unitPicker === null ? "" : (selectedReceivingUnit(unitPicker) ?? "");
    const started = row.querySelector<HTMLElement>(".purchasing-receiving-line-entry")?.dataset;
    const untouched =
      started !== undefined &&
      quantity === started["prefillQuantity"] &&
      cost === started["prefillCost"] &&
      unit === started["prefillUnit"];
    if (lineId !== undefined && !untouched && (quantity !== "" || cost !== "")) {
      lines.push({ lineId, quantity, unit, cost });
    }
  }
  if (lines.length === 0) {
    dropDraft(RECEIVE_ORDER_DRAFT);
  } else {
    storeDraft(RECEIVE_ORDER_DRAFT, { orderId, lines });
  }
}

/** Puts a Receive-against-an-order draft's typed values back on the rows that are on screen. */
function restoreReceiveOrderDraft(draft: unknown, orderId: string): void {
  const record = asRecord(draft);
  const lines = record?.["lines"];
  if (record === undefined || asString(record["orderId"]) !== orderId || !Array.isArray(lines)) {
    return;
  }
  for (const entry of lines) {
    const line = asRecord(entry);
    const lineId = asString(line?.["lineId"]);
    if (line === undefined || lineId === undefined) continue;
    const row = Array.from(
      purchasingReceivingLinesEl.querySelectorAll<HTMLElement>("[data-purchase-order-line-id]"),
    ).find((candidate) => candidate.dataset["purchaseOrderLineId"] === lineId);
    if (row === undefined) continue;
    const quantityInput = row.querySelector<HTMLInputElement>(
      '[data-testid="purchasing-receiving-line-quantity-input"]',
    );
    const costInput = row.querySelector<HTMLInputElement>(
      '[data-testid="purchasing-receiving-line-cost-input"]',
    );
    if (quantityInput !== null)
      quantityInput.value = (asString(line["quantity"]) ?? "").slice(0, 20);
    if (costInput !== null) costInput.value = (asString(line["cost"]) ?? "").slice(0, 20);
    const unit = asString(line["unit"]);
    const radio = Array.from(row.querySelectorAll<HTMLInputElement>('input[type="radio"]')).find(
      (candidate) => candidate.dataset["value"] === unit,
    );
    if (radio !== undefined) {
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
  saveReceiveOrderDraft();
}

/** What route is on screen right now, from this file's own state and admin.ts's. Registered with routeHistory, which
 * asks it whenever something that is part of the address changes. Undefined leaves the address alone. */
function currentScreenRoute(): Route | undefined {
  switch (currentScreenId) {
    case "open-tabs-screen":
      return { kind: "tabs" };
    case "profile-screen":
      return { kind: "profile" };
    case "ring-screen":
      return state.orderId === undefined ? undefined : { kind: "ticket", orderId: state.orderId };
    case "admin-screen":
      return admin.currentAdminRoute();
    case "function-hub-screen":
      return currentHubId === undefined ? undefined : { kind: "hub", hub: currentHubId };
    case "closeout-screen":
      return state.closeoutBusinessDate === undefined
        ? { kind: "closeout" }
        : { kind: "closeout", date: state.closeoutBusinessDate };
    case "void-history-screen":
      return state.voidHistoryBusinessDate === undefined
        ? { kind: "void-history" }
        : { kind: "void-history", date: state.voidHistoryBusinessDate };
    case "purchasing-orders-screen":
      return purchasingOrderDetailRouteId === undefined
        ? { kind: "orders" }
        : { kind: "orders", orderId: purchasingOrderDetailRouteId };
    case "purchasing-receiving-screen":
      return state.receivingOrderId === undefined
        ? { kind: "orders" }
        : { kind: "orders", orderId: state.receivingOrderId, receive: true };
    case "purchasing-cart-screen":
      return { kind: "cart" };
    case "purchasing-outside-order-screen":
      return { kind: "receive-stock" };
    default:
      return undefined;
  }
}

/** Opens the screen a route names, for a live session. Resolves true when a screen was shown and false when the
 * route cannot be opened (a permission this session lacks, an object that is gone or finished); the caller then
 * lands on the parent, Open Tabs, and says nothing. `menuLoad` is the menu read started at page load. */
async function openRoute(
  route: Route,
  session: Session,
  menuLoad: Promise<void> | undefined,
): Promise<boolean> {
  switch (route.kind) {
    case "tabs":
      return false;
    case "profile":
      showProfileScreen();
      return true;
    case "hub": {
      // RPTLAYOUT1-F1-S2: an old "#/hub/reports" address opens the Reports home, the screen that replaced the hub.
      if (route.hub === "reports") {
        if (!admin.operationsViewAllowed(session, "reports")) return false;
        showAdminScreen();
        await admin.openOperationsView(session, "reports", {});
        return true;
      }
      if (!isHubId(route.hub)) return false;
      const source = ACCOUNT_MENU_GROUPS.find((candidate) => candidate.id === route.hub);
      if (source === undefined) return false;
      if (!groupVisible(source, (id) => hasPermission(session, id))) return false;
      showFunctionHubScreen(route.hub);
      return true;
    }
    case "ticket": {
      const result = await getOrder(session.apiBaseUrl, session.tenantId, route.orderId);
      if (result.status !== 200) return false;
      const order = result.body.order;
      // A ticket that was closed or canceled on another device is not a ticket to be on.
      if (order.status === "closed" || order.status === "canceled") return false;
      // Reconstructing a line prices it against the menu, so the menu has to be in first.
      await (menuLoad ?? loadMenu());
      const reconstructed = await reconstructOrderLines(order.id);
      if (reconstructed === undefined) return false;
      // Read before entering: leaving or entering a ticket closes sheets, and closing one forgets it.
      const openedSheet = readTicketSheetDraft();
      await enterTicket(
        order,
        reconstructed.lines.map((line) => line.ticketLine),
        reconstructed.guests,
        reconstructed.lastReopen,
      );
      await restoreTicketSheet(openedSheet, order.id);
      return true;
    }
    case "settings":
      if (
        !hasAnyPermission(session, admin.SETTINGS_ENTRY_PERMISSIONS) ||
        !admin.settingsSectionAllowed(session, route.section)
      ) {
        return false;
      }
      showAdminScreen();
      await admin.openAdminScreen(session, { section: route.section });
      return true;
    case "view": {
      if (!admin.operationsViewAllowed(session, route.view)) return false;
      showAdminScreen();
      await admin.openOperationsView(session, route.view, {
        ...(route.report === undefined ? {} : { report: route.report }),
        ...(route.open === undefined ? {} : { open: route.open }),
        ...(route.range === undefined ? {} : { range: route.range }),
        ...(route.countSessionId === undefined ? {} : { countSessionId: route.countSessionId }),
      });
      return true;
    }
    case "closeout":
      if (!hasPermission(session, "report.closeout.view")) return false;
      return await handleOpenCloseoutSummaryClick(route.date);
    case "void-history":
      if (!hasPermission(session, "report.void_history.view")) return false;
      return await handleOpenVoidHistoryClick(route.date);
    case "cart":
      if (!hasPermission(session, PURCHASING_CART_MANAGE_PERMISSION)) return false;
      showPurchasingCartScreen();
      return true;
    case "receive-stock": {
      if (!hasPermission(session, INVENTORY_RECEIPT_RECORD_PERMISSION)) return false;
      // Read first: opening the screen fresh forgets the draft of an earlier visit.
      const draft = readDraft(RECEIVE_STOCK_DRAFT);
      showPurchasingOutsideOrderScreen();
      await restoreReceiveStockDraft(draft, session);
      return true;
    }
    case "orders": {
      if (!hasPermission(session, PURCHASING_ORDER_VIEW_PERMISSION)) return false;
      showPurchasingOrdersScreen();
      if (route.orderId === undefined) return true;
      // The order has to still be in the list; if it is not, the list is where this lands.
      const listed = await listPurchaseOrders(session.apiBaseUrl, session.tenantId);
      const summary =
        listed.status === 200
          ? listed.body.orders.find((order) => order.id === route.orderId)
          : undefined;
      if (summary === undefined) return true;
      if (route.receive === true) {
        if (
          purchaseOrderIsReceivable(summary.status) &&
          hasPermission(session, INVENTORY_RECEIPT_RECORD_PERMISSION)
        ) {
          const draft = readDraft(RECEIVE_ORDER_DRAFT);
          await showPurchasingReceivingScreen(summary);
          restoreReceiveOrderDraft(draft, summary.id);
        }
        return true;
      }
      await showPurchasingOrderDetail(summary.id);
      return true;
    }
  }
}

/** Puts the address back to the screen that is showing, or clears it when that screen has none. */
function restoreShownRoute(): void {
  if (currentScreenRoute() === undefined) {
    republishLastRoute();
  } else {
    requestRoutePublish({ replace: true });
  }
}

/** The one landing for "that address cannot be opened": Open Tabs, with the ticket state cleared. */
async function landOnOpenTabs(): Promise<void> {
  resetTicketState();
  refreshTileDisabledState();
  showOpenTabsScreen();
  await loadOpenTabs();
}

/** Back and Forward, and an address edited by hand, while someone is signed in. Signed out, nothing navigates and
 * the address is cleared. A sheet that is open (a payment, a count, an approval) is never navigated away from
 * underneath its owner: the address is put back to the screen that is on show instead. */
async function handleRoutePop(route: Route | undefined): Promise<void> {
  const session = state.session;
  if (session === undefined) {
    clearRoute();
    return;
  }
  const sheetOpen = Array.from(document.querySelectorAll<HTMLElement>(".scrim")).some(
    (scrim) => !scrim.hidden,
  );
  // A PIN flow owns the screen: the forced PIN change, the PIN pad in any mode, and a manager approval (its first,
  // no-approval attempt included). Nothing navigates underneath one, and an edited address is put back.
  const pinFlowOpen =
    session.mustChangePin ||
    pinEntry !== undefined ||
    approvalGateBusy ||
    !pinEntryScreen.hidden ||
    !approvalPickerScreen.hidden;
  if (pinFlowOpen || sheetOpen || route === undefined) {
    restoreShownRoute();
    return;
  }
  await withReplaceOnly(async () => {
    if (closeoutFreshnessTimer !== undefined) {
      clearInterval(closeoutFreshnessTimer);
      closeoutFreshnessTimer = undefined;
    }
    if (currentScreenId === "ring-screen") {
      resetTicketState();
      refreshTileDisabledState();
    }
    const opened = route.kind === "tabs" ? false : await openRoute(route, session, undefined);
    if (!opened) {
      await landOnOpenTabs();
    }
  });
}

// ---------------------------------------------------------------------
// TEN11-F1: the support tab (docs/build-briefs/TEN11-F1-console-open-as-support.md)
// ---------------------------------------------------------------------

let supportTab: { state: SupportTabState; bar: SupportBanner } | undefined;

/** Gets this tab a support session and shows the Support session location. False when it could not (the ended screen is
 * on, with nothing held). A reload resumes the stored session; a new open from the console always asks for a code. */
async function prepareSupportTab(): Promise<boolean> {
  const apiBaseUrl = window.location.origin;
  let stored = loadSupportTab();
  if (isFreshOpen(window.location.search)) {
    if (stored !== undefined) {
      await endSupportSessionRequest(apiBaseUrl, stored.token);
      clearSupportTab();
      clearSession();
    }
    const code = await waitForHandoffCode(window);
    const redeemed = code === null ? null : await redeemHandoff(apiBaseUrl, code);
    if (redeemed === null) {
      showSupportEnded("Support session not started");
      return false;
    }
    // SUPFRAN1: a code that opens a franchisor's console belongs to the franchisor page, never to a location tab.
    if (redeemed.location.id === null || redeemed.location.name === null) {
      await endSupportSessionRequest(apiBaseUrl, redeemed.token);
      showSupportEnded("Support session not started");
      return false;
    }
    stored = {
      token: redeemed.token,
      tenantId: redeemed.tenant.id,
      locationId: redeemed.location.id,
      customer: redeemed.tenant.name,
      locationName: redeemed.location.name,
      operator: redeemed.actor.displayName,
      userId: redeemed.actor.id,
    };
    saveSupportTab(stored);
    // The address carries no secret; the query only said "new open", so it goes once the session exists.
    window.history.replaceState(null, "", `${SUPPORT_PATH}${window.location.hash}`);
  }
  if (stored === undefined) {
    showSupportEnded("Support session not started");
    return false;
  }
  enterSupportTerminalConfig({
    apiBaseUrl,
    tenantId: stored.tenantId,
    locationId: stored.locationId,
    locationName: stored.locationName,
    businessName: stored.customer,
  });
  saveSession({
    apiBaseUrl,
    tenantId: stored.tenantId,
    employeeId: stored.operator,
    token: stored.token,
    userId: stored.userId,
    role: "admin",
    roles: [],
    permissions: {},
    mustChangePin: false,
    defaultThemeId: null,
    pinMinLength: 4,
    pinMaxLength: 6,
  });
  const shell = document.getElementById("app-shell");
  if (shell === null) throw new Error("the app shell is missing from the page");
  const bar = mountSupportBanner(shell, () => endSupportTab("Support session ended", true));
  bar.show({
    customer: stored.customer,
    locationName: stored.locationName,
    operator: stored.operator,
    expiresAt: new Date(Date.now() + 30 * 60_000).toISOString(),
  });
  bar.watch(apiBaseUrl, stored.token, () => endSupportTab("Support session ended", false));
  supportTab = { state: stored, bar };
  // TEN11-F3: closing or leaving the tab ends the session. A page restored from the back/forward cache is not leaving.
  window.addEventListener("pagehide", (event) => {
    if (event.persisted || supportTab === undefined) return;
    sendSupportCloseNotice(apiBaseUrl, supportTab.state.token);
  });
  return true;
}

/** Leaves the support tab with nothing held. `callServer` is true for the End control and false when the server
 * already ended it. */
function endSupportTab(message: string, callServer: boolean): void {
  const current = supportTab;
  if (current === undefined) return;
  supportTab = undefined;
  if (callServer) {
    void endSupportSessionRequest(window.location.origin, current.state.token);
  }
  current.bar.remove();
  setAuthToken(undefined);
  getHelp.reset();
  helpPanel.reset();
  willy.reset();
  clearSession();
  clearSupportTab();
  state.session = undefined;
  if (idleTimer !== undefined) {
    clearTimeout(idleTimer);
    idleTimer = undefined;
  }
  stopPaymentLinkWatches();
  showSupportEnded(message);
}

/** Page-load resume path: a terminal that already has a TerminalConfig
 * AND a stored Session for that SAME server/tenant validates the token
 * against the server (GET .../auth/session) rather than trusting
 * sessionStorage blindly, so a mid-shift page refresh returns straight to
 * Open Tabs on a still-valid token and falls back to the user picker on
 * an expired/invalid one - never a silent stale "logged in" state. */
async function resumeFromStoredConfig(): Promise<void> {
  // TEN11-F2: the minute poll for "Support session open" while a franchisor is in this customer's instance.
  startSupportNotice();
  // NETBILLPAUSE1: the minute poll for the "service is paused because ..." sentence.
  startBillingPauseNotice();
  // TEN11-F1: a software owner's support tab sets up its own in-memory configuration and session first, then
  // takes the same path a signed-in tablet takes.
  if (isSupportPage(window.location.pathname) && !isSupportTerminalConfig()) {
    if (!(await prepareSupportTab())) return;
  }
  // ONBW1-S1 (DEC-416): a page opened with a welcome link connects the device and goes to the owner's PIN choice.
  // Read before the route below, which would otherwise see the fragment.
  const welcomeToken = takeWelcomeToken() ?? welcomeFlow.pendingToken();
  if (welcomeToken !== undefined) {
    await welcomeFlow.openLink(welcomeToken);
    return;
  }
  // UX108: read the address the page was loaded with before any screen can publish over it.
  const initialRoute = readInitialRoute();
  const config = loadTerminalConfig();
  if (config === undefined) {
    showTerminalSetupScreen();
    return;
  }
  terminalConfigState = config;
  renderBrandName();
  void refreshDeviceIdentity();
  // PAY5-F1: defensive; #app-topbar itself stays hidden until a session
  // is confirmed valid below and a screen is actually shown, so this has
  // no visible effect yet, kept for the same reason every other
  // terminalConfigState assignment carries this call.
  renderTopbarTerminalSegment();
  const existingSession = loadSession();
  if (
    existingSession !== undefined &&
    existingSession.apiBaseUrl === config.apiBaseUrl &&
    existingSession.tenantId === config.tenantId
  ) {
    setAuthToken(existingSession.token);
    // AU11: this probe EXISTS to find out whether the session is valid,
    // so a 401 here is its return value, not an event to react to.
    // Without this, a page load on a long-dead session would fire the
    // signed-out toast at somebody who never had a session this tab.
    // UX108: the check has a limit. Only a 401 says the session is gone. A hung request, a network error or a
    // server error says nothing about the session, so it is kept (with its drafts) and a plain Retry is offered.
    let limitTimer: ReturnType<typeof setTimeout> | undefined;
    const sessionCheck = await Promise.race([
      withoutSessionLostHandling(() => getSession(config.apiBaseUrl, config.tenantId)),
      new Promise<"limit">((resolve) => {
        limitTimer = setTimeout(() => resolve("limit"), BOOT_SESSION_CHECK_LIMIT_MS);
      }),
    ]);
    clearTimeout(limitTimer);
    if (sessionCheck === "limit" || (sessionCheck.status !== 200 && sessionCheck.status !== 401)) {
      setAuthToken(undefined);
      bootRetryButton.hidden = false;
      bootShell.setAttribute("aria-busy", "false");
      return;
    }
    if (sessionCheck.status === 200) {
      const sessionUser = sessionCheck.body.user;
      // Reload-during-forced-PIN-change fix: this route is allowlisted
      // server-side for a mustChangePin session specifically so it can
      // report that signal (see src/api/server.ts's GET .../auth/session
      // comment), but until this fix, a page reload mid-forced-change
      // ignored it and routed straight to Open Tabs, leaving an account
      // that still has a temporary/reset PIN sitting on the real product
      // instead of finishing the mandatory change. Use the FRESH
      // server-reported mustChangePin (sessionUser), not the possibly
      // stale value cached in the stored session, since the whole point
      // of this branch is that the two can disagree (e.g. a manager reset
      // this user's PIN in another tab since the last save).
      // Independent-review Finding B, 2026-08-26: `role`, `roles`, and
      // `permissions` are refreshed from the server here too, the same
      // "fresh server value wins over the possibly-stale cached one"
      // reasoning mustChangePin/defaultThemeId already establish just
      // above -- a manager who ADDS this user's Inventory Manager role
      // in another tab/device must reach the resumed tab's own Settings
      // entry point and Inventory/Counts tabs on this same reload, not
      // only on a fresh login.
      const refreshedSession: Session = {
        ...existingSession,
        role: sessionUser.role,
        roles: sessionUser.roles,
        permissions: sessionCheck.body.permissions,
        mustChangePin: sessionUser.mustChangePin,
        defaultThemeId: sessionUser.defaultThemeId,
        // T16/DEC-133: same "fresh server value wins" reasoning as the
        // fields immediately above -- a manager who raises this tenant's
        // PIN-length maximum in another tab must reach a resumed tab's
        // forced-change pad with the new bound, not the one cached at last
        // login.
        pinMinLength: sessionCheck.body.pinMinLength,
        pinMaxLength: sessionCheck.body.pinMaxLength,
      };
      saveSession(refreshedSession);
      state.session = refreshedSession;
      // PAY7 Section 2.8: this is the REAL reload restore path (this
      // whole branch runs only after resumeFromStoredConfig's own reload
      // probe confirms a still-live session) -- the moment the entire
      // Section 2.8 persistence design exists to close, since a reload
      // otherwise re-executes every module-level initializer and starts
      // state.payIdempotencyKeysByTarget empty.
      for (const [target, key] of Object.entries(loadPersistedPayKeys(refreshedSession.tenantId))) {
        state.payIdempotencyKeysByTarget.restore(target, key);
      }
      // AU12: this probe just confirmed a real, live session, the same
      // "a new authenticated session begins here" moment submitLoginPin's
      // own call marks. See that call site's comment for why this has to
      // happen at every such moment, not only once at page init.
      resetSessionLostLatch();
      // AU11: a reload rearms the idle countdown, rather than leaving it
      // unset until the next sign-in.
      noteSessionInactivityWindow(sessionCheck.body.sessionInactivityMinutes);
      refreshManagerControlsVisibility(refreshedSession);
      // Sixteenth amendment item 2: the server's own stored value is
      // authoritative on resume too, not just at the moment of login
      // (submitLoginPin already applies it there). Replaces this
      // terminal's init()-time product default (a sane PRE-login default
      // only) with the real per-user value the instant a resumed session
      // confirms who is actually signed in. Adversarial-gate
      // correction (2026-08-24), Finding 5: a user who has never set a
      // profile theme (defaultThemeId null) now explicitly gets the shop
      // default here too, the same fix submitLoginPin's own identical
      // null check received, rather than keeping whatever happened to
      // already be applied.
      setTheme(sessionUser.defaultThemeId ?? DEFAULT_THEME_ID);
      void refreshMemberBrand();
      if (sessionUser.mustChangePin) {
        resetTicketState();
        // The pad is the only thing a refresh may come back to, so no screen address is kept.
        clearRoute();
        openPinEntry({ mode: "changePin", returnTo: "open-tabs", mandatory: true });
        return;
      }
      resetTicketState();
      // An earlier build kept a typed tab name here; a tab name is a customer's name, so none is kept now.
      dropDraft("newTicketName");
      // UX108: back to the screen the refresh interrupted. The menu read starts now either way (a ticket needs it
      // first); a route this session cannot open, or an object that is gone, lands on Open Tabs with no message.
      const menuLoad = loadMenu();
      const restored =
        initialRoute === undefined || initialRoute.kind === "tabs"
          ? false
          : await withReplaceOnly(() => openRoute(initialRoute, refreshedSession, menuLoad));
      if (restored) {
        // The open tabs list is not on screen, so it is left to load when someone goes back to it (it also starts
        // a payment link check that belongs to that screen). The counts the account menu shows are not.
        await menuLoad;
        void refreshManagerExceptions();
        void refreshMenuCounts();
        void refreshStartingStockMenu();
        void cashDrawer.refreshEntry();
      } else {
        showOpenTabsScreen();
        await Promise.all([menuLoad, loadOpenTabs()]);
        // ONB4-F1: a reload on Open Tabs shows the setup banner while setup is not complete.
        if (setupWizardEnabled()) void showFirstDaySetupBanner();
      }
      // PAY1 Slice 3: back from Stripe's page with a live session, so land on Card payments.
      openPendingCardPaymentsArrival(refreshedSession);
      openPendingGustoArrival(refreshedSession);
      openPendingRoyaltyArrival(refreshedSession);
      openPendingSubscriptionDebitArrival(refreshedSession);
      openPendingAccountingArrival(refreshedSession);
      await openPendingReportArrival(refreshedSession);
      return;
    }
    setAuthToken(undefined);
    clearSession();
    if (supportTab !== undefined) {
      endSupportTab("Support session ended", false);
      return;
    }
  }
  // UX108: no live session, so nothing typed under an earlier one is kept, and showUserPickerScreen clears the address.
  clearAllDrafts();
  // ONBW1-S1: a device bound through a welcome link whose owner has not yet chosen a PIN resumes at the PIN choice.
  if (await welcomeFlow.resumeIfPending(config)) return;
  showUserPickerScreen();
  await loadUserPicker();
}

function init(): void {
  // UX14-F2: sheets shrink to the area the on-screen keyboard leaves.
  installKeyboardInset();
  // UX108: the screen's address. Registered before anything can show a screen.
  setRouteProvider(currentScreenRoute);
  bootRetryButton.addEventListener("click", () => {
    bootRetryButton.hidden = true;
    bootShell.setAttribute("aria-busy", "true");
    void resumeFromStoredConfig();
  });
  setRoutePopHandler((route) => void handleRoutePop(route));
  // AU11: registered first, so an expired session found by ANY request
  // during start-up already has somewhere to go.
  startIdleWatch();
  populateThemeSelects();
  // UX21 (DEC-117): every pre-login screen (terminal setup, the user
  // picker, the PIN pad) always shows the product default, never a
  // device's own remembered choice, so a shared terminal cannot carry a
  // previous shift's scheme into a new session before anyone signs in.
  // Removing any stale key first (rather than merely ignoring it) means
  // no later code path can read it back by accident.
  // TEN11-F1: a support tab leaves the staff device's stored keys alone.
  if (!isSupportPage(window.location.pathname)) clearStoredThemeId();
  setTheme(DEFAULT_THEME_ID);

  terminalPickerRegisterButton.addEventListener(
    "click",
    () => void handleTerminalPickerRegisterClick(),
  );
  terminalPickerBackButton.addEventListener("click", () => handleTerminalPickerBackClick());
  terminalPickerBlockedLogoutButton.addEventListener("click", () =>
    handleTerminalPickerBlockedLogoutClick(),
  );
  for (const button of pinDigitButtons) {
    const digit = button.dataset["digit"];
    if (digit !== undefined) {
      button.addEventListener("click", () => handlePinDigitClick(digit));
    }
  }
  pinClearButton.addEventListener("click", () => handlePinClearClick());
  pinBackspaceButton.addEventListener("click", () => handlePinBackspaceClick());
  // PINKEYS1 (owner rule: a physical keyboard always works alongside touch): while the PIN pad is on screen, typed digits,
  // Backspace, Enter and Escape do what the on-screen keys do. A key typed into a text field is left alone.
  document.addEventListener("keydown", (event) => {
    if (pinEntry === undefined || pinEntryScreen.hidden || event.metaKey || event.ctrlKey || event.altKey) return;
    if (isTextEntryTarget(event.target)) return;
    if (/^[0-9]$/.test(event.key)) handlePinDigitClick(event.key);
    else if (event.key === "Backspace") handlePinBackspaceClick();
    else if (event.key === "Enter") {
      if (pinSubmitButton.disabled) return;
      void handlePinSubmitClick();
    } else if (event.key === "Escape") handlePinCancelClick();
    else return;
    event.preventDefault();
  });
  pinSubmitButton.addEventListener("click", () => void handlePinSubmitClick());
  pinCancelButton.addEventListener("click", () => handlePinCancelClick());
  approvalPickerCancelButton.addEventListener("click", () => handleApprovalPickerCancelClick());
  profileChangePinButton.addEventListener("click", () => handleProfileChangePinClick());
  profileThemeSelect.addEventListener("change", () => void handleProfileThemeChange());
  profileDisplayNameInput.addEventListener("input", () => handleProfileDisplayNameInput());
  profileFirstNameInput.addEventListener("input", () => handleProfileFirstNameInput());
  profileLastNameInput.addEventListener("input", () =>
    handleProfileOtherFieldInput(profileLastNameErrorEl),
  );
  profileEmailInput.addEventListener("input", () =>
    handleProfileOtherFieldInput(profileEmailErrorEl),
  );
  profilePhoneInput.addEventListener("input", () =>
    handleProfileOtherFieldInput(profilePhoneErrorEl),
  );
  profileSaveButton.addEventListener("click", () => void handleProfileSaveClick());
  noOrderBackToTabsButton.addEventListener("click", () => handleBackToTabsClick());
  // UX18 (DEC-061): moved here from Open Tabs' own former topbar.
  adminOpenCloseoutSummaryButton.addEventListener(
    "click",
    () => void handleOpenCloseoutSummaryClick(),
  );
  // M12: the Void History entry point, mirroring the line just above.
  adminOpenVoidHistoryButton.addEventListener("click", () => void handleOpenVoidHistoryClick());
  voidHistoryPrevDayButton.addEventListener("click", () => handleVoidHistoryPrevDayClick());
  voidHistoryNextDayButton.addEventListener("click", () => handleVoidHistoryNextDayClick());
  // PO6-R1 (DEC-056): each destination is its own button now, no chooser.
  adminPurchasingOpenOrdersButton.addEventListener("click", () => showPurchasingOrdersScreen());
  adminPurchasingOpenCartButton.addEventListener("click", () => showPurchasingCartScreen());
  mustFind<HTMLButtonElement>("#admin-ordering-what-to-order").addEventListener("click", () =>
    showOrderingScreen({ kind: "what-to-order" }),
  );
  mustFind<HTMLButtonElement>("#admin-ordering-open-orders").addEventListener("click", () =>
    showOrderingScreen({ kind: "open-orders" }),
  );
  adminPurchasingOpenOutsideOrderButton.addEventListener("click", () =>
    showPurchasingOutsideOrderScreen(),
  );
  // Detail is an in-screen toggle, not a navigation. The list is re-read on the way back (QAWALK1-F2-B): approving or
  // cancelling an order changes the very row being returned to, and a list left as it was showed a cancelled order as
  // Placed, under Deliveries to receive.
  purchasingOrderDetailBackButton.addEventListener("click", () => {
    purchasingOrderDetailView.hidden = true;
    purchasingOrdersListView.hidden = false;
    purchasingOrderDetailRouteId = undefined;
    requestRoutePublish();
    void loadPurchasingOrdersList();
  });
  // PO6 Slice 3: the inline Approve panel.
  purchasingOrderDetailApproveButton.addEventListener(
    "click",
    () => void handleOpenPurchasingOrderApproveClick(),
  );
  purchasingOrderDetailApproveCancelButton.addEventListener("click", () =>
    handleCancelPurchasingOrderApproveClick(),
  );
  purchasingOrderDetailApproveSubmitButton.addEventListener(
    "click",
    () => void handleSubmitPurchasingOrderApproveClick(),
  );
  // PO6 Slice 5: report/resolve exception, the same inline-panel shape.
  purchasingOrderDetailExceptionReportButton.addEventListener("click", () =>
    handleOpenPurchasingOrderExceptionReportClick(),
  );
  purchasingOrderDetailExceptionReportCancelButton.addEventListener("click", () =>
    handleCancelPurchasingOrderExceptionReportClick(),
  );
  purchasingOrderDetailExceptionReportSubmitButton.addEventListener(
    "click",
    () => void handleSubmitPurchasingOrderExceptionReportClick(),
  );
  purchasingOrderDetailExceptionResolveButton.addEventListener("click", () =>
    handleOpenPurchasingOrderExceptionResolveClick(),
  );
  purchasingOrderDetailExceptionResolveCancelButton.addEventListener("click", () =>
    handleCancelPurchasingOrderExceptionResolveClick(),
  );
  purchasingOrderDetailExceptionResolveSubmitButton.addEventListener(
    "click",
    () => void handleSubmitPurchasingOrderExceptionResolveClick(),
  );
  // PO6 Slice 2: the cart screen's own search wiring (opened from
  // Settings > Purchasing since PO6-R1, DEC-056). Navigation home is the
  // shared topbar's home button (UX17), wired once below.
  purchasingCartProductSearchInput.addEventListener("input", () =>
    handlePurchasingCartProductSearchInput(),
  );
  // UX16 (DEC-058), criterion 2.
  purchasingCartAddAllBelowParButton.addEventListener(
    "click",
    () => void handleAddAllBelowParClick(),
  );
  // INV8 (DEC-176) Section 2.5: re-sorts the already-fetched proposals
  // list, no new network call.
  purchasingCartProposalsSortSelect.addEventListener("change", () => {
    state.purchasingCartProposalsSort =
      purchasingCartProposalsSortSelect.value as ProposalSortOrder;
    renderPurchasingCartProposals();
  });
  // PO6 Slice 6: the outside-order screen's own search and entry wiring
  // (opened from Settings > Purchasing since PO6-R1, DEC-056).
  purchasingOutsideOrderProductSearchInput.addEventListener("input", () =>
    handlePurchasingOutsideOrderProductSearchInput(),
  );
  purchasingOutsideOrderEntryChangeButton.addEventListener("click", () =>
    handleChangeOutsideOrderProductClick(),
  );
  purchasingOutsideOrderSubmitButton.addEventListener(
    "click",
    () => void handleRecordOutsideOrderSubmitClick(),
  );
  // AU8: Settings > Operations' own Manager Exceptions badge (UX18: moved
  // from Open Tabs' own former topbar) and the ring screen's own ticket-
  // rail banner (stays on the floor, DEC-061) both open the SAME global
  // sheet.
  adminOpenManagerExceptionsButton.addEventListener("click", () => openManagerExceptionsSheet());
  ticketManagerExceptionViewButton.addEventListener("click", () => openManagerExceptionsSheet());
  managerExceptionsCloseButton.addEventListener("click", () => closeManagerExceptionsSheet());
  managerExceptionsScrim.addEventListener("click", (event) => {
    if (event.target === managerExceptionsScrim) {
      closeManagerExceptionsSheet();
    }
  });
  closeoutRefreshButton.addEventListener("click", () => handleCloseoutRefreshClick());
  // CLOSEOUT1: an exception row's one action. Open tabs, unpriceable lines and failed payments are all fixed from the tab.
  for (const button of closeoutExceptionsEl.querySelectorAll<HTMLButtonElement>(".closeout-exc-action")) {
    button.addEventListener("click", () => handleCloseoutBackToTabsClick());
  }
  closeoutLocationSelectEl.addEventListener("change", () => {
    state.closeoutLocationId = closeoutLocationSelectEl.value;
    state.closeoutSummary = undefined;
    state.closeoutLastLoadedAt = undefined;
    closeCloseoutFinalizeSheet();
    void loadCloseoutSummary();
  });
  closeoutPrintButton.addEventListener("click", () => handleCloseoutPrintClick());
  createExportMenu({
    trigger: voidHistoryExportButton,
    testIdPrefix: "void-history-export",
    menuActive: () => true,
    googleSheet: () => state.session,
    onPick: (format) => void handleVoidHistoryExportPick(format),
    onPlain: () => undefined,
  });
  createExportMenu({
    trigger: closeoutExportButton,
    testIdPrefix: "closeout-export",
    menuActive: () => true,
    googleSheet: () => state.session,
    onPick: (format) => void handleCloseoutExportPick(format),
    onPlain: () => undefined,
  });
  // CL6: day navigation and the finalize flow.
  closeoutPrevDayButton.addEventListener("click", () => handleCloseoutPrevDayClick());
  closeoutNextDayButton.addEventListener("click", () => handleCloseoutNextDayClick());
  closeoutFinalizeButton.addEventListener("click", () => openCloseoutFinalizeSheet());
  closeoutFinalizeCloseButton.addEventListener("click", () => closeCloseoutFinalizeSheet());
  closeoutFinalizeCancelButton.addEventListener("click", () => closeCloseoutFinalizeSheet());
  closeoutFinalizeConfirmButton.addEventListener(
    "click",
    () => void handleCloseoutFinalizeConfirm(),
  );

  // UX17: the ONE shared account-menu dropdown (Settings, Payment
  // Terminal, Log out), wired once, replacing the nine per-screen copies
  // this block used to wire one at a time. My Profile is hidden
  // specifically while the Profile screen is active
  // (setAccountMenuProfileMode, called from showTopbarChrome on every
  // navigation), not by never wiring it. UX21 removed the dropdown's own
  // Theme item; My Profile's own picker is the only theme control.
  initMyTips({ toast: (message) => showToast(message, "danger") });
  wireAccountMenu(
    { trigger: accountTrigger, dropdown: accountDropdown },
    {
      myProfileButton: accountMyProfileButton,
      myTipsButton: accountMyTipsButton,
      settingsButton: openManagerSettingsButton,
      logoutButton: logOutButton,
    },
  );
  accountNotificationsButton.addEventListener("click", () => {
    closeAccountMenu({ trigger: accountTrigger, dropdown: accountDropdown });
    showNotificationsScreen();
  });
  getHelpButton.addEventListener("click", () => {
    closeAllAccountMenus();
    getHelp.openSheet();
  });
  wireOperationsMenu();
  // UX116: what the hubs read from the rest of the app.
  configureFunctionHubs({
    businessDate: async () => {
      const session = state.session;
      if (session === undefined) return undefined;
      const result = await getReportContext(session.apiBaseUrl, session.tenantId);
      return result.status === 200 && typeof result.body.today === "string"
        ? result.body.today
        : undefined;
    },
    menuCounts: async () => {
      await refreshMenuCounts();
      return state.menuCounts;
    },
    locationName: () => floorLocationName(),
    openTarget: (target) => openHubTarget(target),
    // INVDESIGN1-S2: the Inventory home's buttons.
    openInventoryTarget: (target) => openInventoryHomeTarget(target),
    startCount: (options) => {
      showAdminScreen();
      void admin.startCountFromHome(requireSession(), options);
    },
  });
  // INVDESIGN1-S2: a count that closes lands on "Count closed" and what to order, on the Inventory home.
  admin.setCountClosedHandler((summary) => showInventoryAfterCount(summary));
  mustFind<HTMLButtonElement>("#inventory-set-par-button").addEventListener("click", () =>
    openInventoryHomeTarget({ kind: "settings" }),
  );
  // The shared HOME button: wired exactly ONCE here, never re-wired per
  // navigation (Review 31 finding 2's own named risk -- a shared
  // element's handler re-attached inside every show*Screen call can
  // silently accumulate duplicate listeners). Reads which screen is
  // CURRENTLY active from the SCREENS registry at click time and runs
  // that screen's own `onHome`, or the shared default.
  homeButton.addEventListener("click", () => {
    const registration = SCREENS.find((s) => s.id === currentScreenId);
    (registration?.onHome ?? defaultTopbarHome)();
  });
  // The shared SECONDARY-action button: same wire-once-read-at-click-time
  // shape, for the one screen (purchasing-receiving) that still carries a
  // distinct second destination beneath home. Hidden, and therefore
  // inert to a stray tap, for every other screen (showTopbarChrome).
  topbarSecondaryActionButton.addEventListener("click", () => {
    const registration = SCREENS.find((s) => s.id === currentScreenId);
    registration?.secondaryAction?.onClick();
  });
  newTicketButton.addEventListener("click", () => void handleNewTicketClick());
  // TAPSPEED1: a finger touching a drink tile starts reading that drink's detail before the tap completes, for the
  // drinks past the read-ahead limit. A tile for anything but a cocktail does nothing here.
  menuScroll.addEventListener("pointerdown", (event) => {
    const tile = event.target instanceof Element ? event.target.closest<HTMLElement>(".tile") : null;
    const itemId = tile?.dataset["menuItemId"] ?? tile?.dataset["popularItemId"];
    if (itemId === undefined) return;
    const item = state.menuItems.find((candidate) => candidate.id === itemId);
    if (item !== undefined && isCocktailMenuItem(item)) void warmMenuDrinkDetail(item.menuDrink.id);
  });
  // UX108: what has been typed on the two Receive screens survives a refresh.
  for (const eventName of ["input", "change"] as const) {
    purchasingOutsideOrderEntryEl.addEventListener(eventName, () => saveReceiveStockDraft());
    purchasingReceivingLinesEl.addEventListener(eventName, () => saveReceiveOrderDraft());
  }

  tabMergeToggleButton.addEventListener("click", () => handleTabMergeToggleClick());
  tabFilterAllButton.addEventListener("click", () => setTabFilterMode("all"));
  tabFilterMineButton.addEventListener("click", () => setTabFilterMode("mine"));
  tabChipUnpaidButton.addEventListener("click", () => toggleTabStatusChip("unpaid"));
  tabChipAwaitingButton.addEventListener("click", () => toggleTabStatusChip("awaiting"));
  tabChipPaidButton.addEventListener("click", () => toggleTabStatusChip("paid"));
  awaitingTipEnterButton.addEventListener("click", () => handleAwaitingTipEnterClick());
  awaitingTipReprintButton.addEventListener("click", () => openReprintChoice());
  tabChipOver1HrButton.addEventListener("click", () => toggleTabOver1HrChip());
  tabSearchInput.addEventListener("input", () => handleTabSearchInput());
  tabSearchInput.addEventListener("focus", () => handleTabSearchFocus());
  tabSearchInput.addEventListener("keydown", (event) => {
    // The keyboard's Search key only closes the keyboard; matching is live.
    if (event.key === "Enter") {
      tabSearchInput.blur();
    }
  });
  tabSearchClearButton.addEventListener("click", () => {
    clearTabSearch();
    tabSearchInput.focus();
  });
  tabMergeModeCancelButton.addEventListener("click", () => handleTabMergeModeCancelClick());
  // GATE-CLEANUP-1: Cancel and Merge now live inside the shared choice
  // sheet itself (openTabMergeConfirmSheet's own options/Cancel button),
  // so there is no separate confirm-scrim wiring here any more.
  tabMergeContinueButton.addEventListener("click", () => openTabMergeConfirmSheet());
  prepareAllButton.addEventListener("click", () => void handlePrepareAllClick());
  payButton.addEventListener("click", () => handlePayButtonClick());
  payRetryButton.addEventListener("click", () => handlePayRetryClick());
  payConfirmButton.addEventListener("click", () => void handlePayConfirmClick());
  payConfirmSwitchReaderButton.addEventListener("click", () => void openCardReaderAsk("switch"));
  cardReaderAskClose.addEventListener("click", closeCardReaderAsk);
  payTenderCardButton.addEventListener("click", () => void selectPayTender("card"));
  payTenderCashButton.addEventListener("click", () => void selectPayTender("cash"));
  payCancelButton.addEventListener("click", () => closePayConfirmSheet());
  payConfirmClose.addEventListener("click", () => closePayConfirmSheet());
  payConfirmScrim.addEventListener("click", (event) => {
    if (event.target === payConfirmScrim) {
      closePayConfirmSheet();
    }
  });
  tipCustomApplyButton.addEventListener("click", () => handleTipCustomApplyClick());
  tipNoneButton.addEventListener("click", () => void selectTip({ type: "none" }));
  // PAYFIX1: reads the figures again for the tip already chosen.
  tipSyncRetry.addEventListener("click", () => {
    if (state.selectedTip !== undefined) void selectTip(state.selectedTip);
  });
  // Payments-track Slice 3: "Use saved card" and its management sheet.
  payConfirmCardRow.addEventListener("click", () => openPayWithCardChoice());
  payConfirmManageCardsLink.addEventListener("click", () => void openSavedCardsSheet());
  payConfirmSplitButton.addEventListener("click", () => handlePayConfirmSplitClick());
  cardConsentAckCheckbox.addEventListener("change", () => {
    cardConsentSaveButton.disabled = !cardConsentAckCheckbox.checked;
  });
  cardConsentSaveButton.addEventListener("click", () => {
    if (prepayClientSaveGuest !== undefined) {
      const guest = clientForNewCard(state.payScope);
      if (
        guest === undefined ||
        guest.id !== prepayClientSaveGuest.id ||
        !paySaveClientCheckbox.checked
      )
        return;
      confirmedClientSave = {
        guest,
        evidenceReference: `guest-touchscreen:${crypto.randomUUID()}`,
      };
      closePrepayClientSave();
      void handlePayConfirmClick();
    } else {
      void handleCardConsentSave();
    }
  });
  cardConsentDeclineButton.addEventListener("click", () => closeCardConsentSheet());
  cardConsentCloseButton.addEventListener("click", () => closeCardConsentSheet());
  cardConsentScrim.addEventListener("click", (event) => {
    if (event.target === cardConsentScrim) {
      closeCardConsentSheet();
    }
  });
  // SIG1: touch-drawn signature (pointer events cover touch and mouse
  // alike, never a mouse-only or keyboard-only path) plus the
  // printed/emailed/none receipt choice.
  signatureCanvas.addEventListener("pointerdown", handleSignaturePointerDown);
  signatureCanvas.addEventListener("pointermove", handleSignaturePointerMove);
  signatureCanvas.addEventListener("pointerup", finishSignatureStroke);
  signatureCanvas.addEventListener("pointercancel", finishSignatureStroke);
  signatureClearButton.addEventListener("click", () => handleSignatureClear());
  signatureContinueButton.addEventListener("click", () => void handleSignatureContinueClick());
  receiptChoicePrintedButton.addEventListener("click", () => handleReceiptChoicePrintedClick());
  receiptChoiceEmailedButton.addEventListener("click", () => handleReceiptChoiceEmailedClick());
  receiptChoiceNoneButton.addEventListener("click", () => handleReceiptChoiceNoneClick());
  receiptEmailInput.addEventListener("input", () => updateReceiptEmailSendButtonState());
  receiptEmailSendButton.addEventListener("click", () => handleReceiptEmailSendClick());
  signatureReceiptCloseButton.addEventListener("click", () => closeSignatureReceiptSheet());
  signatureReceiptScrim.addEventListener("click", (event) => {
    if (event.target === signatureReceiptScrim) {
      closeSignatureReceiptSheet();
    }
  });
  savedCardsCloseButton.addEventListener("click", () => closeSavedCardsSheet());
  // UX39: Manage saved cards and Keep tab open no longer have their own
  // standalone header buttons to attach a click listener to; both are
  // reached through #btn-ticket-more's own listener below
  // (handleTicketMoreClick), which calls openSavedCardsSheet/
  // handleKeepTabOpenClick unchanged.
  savedCardsScrim.addEventListener("click", (event) => {
    if (event.target === savedCardsScrim) {
      closeSavedCardsSheet();
    }
  });
  payWarningAckCheckbox.addEventListener("change", () => handlePayWarningAckChange());
  payWarningRetryButton.addEventListener("click", () => void handlePayWarningRetryClick());
  payStatusRetryButton.addEventListener("click", () => void handlePayStatusRetryClick());
  payCheckButton.addEventListener("click", () => void handlePayCheckClick());
  payReaderCancelButton.addEventListener("click", () => void handlePayReaderCancelClick());
  // UX13: the persistent Close Ticket button is gone (DEC-026); Close is
  // still reachable, but only from #zero-due-close (handleZeroDueCloseClick,
  // which calls handleCloseClick itself).
  noTipButton.addEventListener("click", () => handleNoTipClick());
  zeroDueDismissButton.addEventListener("click", () => handleZeroDueDismissClick());
  zeroDueCloseButton.addEventListener("click", () => handleZeroDueCloseClick());
  liquorSheetClose.addEventListener("click", () => closeLiquorSheet());
  liquorScrim.addEventListener("click", (event) => {
    if (event.target === liquorScrim) {
      closeLiquorSheet();
    }
  });
  lineEditClose.addEventListener("click", () => closeLineEditSheet());
  lineEditScrim.addEventListener("click", (event) => {
    if (event.target === lineEditScrim) {
      closeLineEditSheet();
    }
  });
  lineEditRemoveButton.addEventListener("click", () => void handleLineEditRemoveClick());
  lineEditUnprepareButton.addEventListener("click", () => void handleLineEditUnprepareClick());
  // M4 (refund) / PL1 (payment link), merged behind one chip by UX13,
  // that chip folded into #btn-ticket-more by UX39 (see its own listener
  // below, handleTicketMoreClick).
  refundConfirmButton.addEventListener("click", () => void handleRefundConfirmClick());
  refundCancelButton.addEventListener("click", () => closeRefundSheet());
  ticketRefundButton.addEventListener(
    "click",
    () => state.orderId !== undefined && void openRefundTicketSheet(state.orderId, ticketSub.textContent ?? ""),
  );
  refundTicketConfirm.addEventListener("click", () => void handleRefundTicketConfirmClick());
  refundTicketSome.addEventListener("click", () => handleRefundTicketSomeClick());
  refundTicketCancel.addEventListener("click", () => closeRefundTicketSheet());
  refundTicketClose.addEventListener("click", () => closeRefundTicketSheet());
  refundTicketTipToggle.addEventListener("click", () => handleRefundTicketTipToggle());

  refundClose.addEventListener("click", () => closeRefundSheet());
  paymentLinkClose.addEventListener("click", () => closePaymentLinkSheet());
  paymentLinkSendButton.addEventListener("click", () => void handlePaymentLinkSendClick());
  paymentLinkCheckButton.addEventListener("click", () => void handlePaymentLinkCheckClick());
  paymentLinkCancelButton.addEventListener("click", () => void handlePaymentLinkCancelClick());
  refundScrim.addEventListener("click", (event) => {
    if (event.target === refundScrim) {
      closeRefundSheet();
    }
  });
  lineEditDiscountButton.addEventListener("click", () => void handleLineEditDiscountClick());
  lineEditChangePriceButton.addEventListener("click", () => void handleLineEditChangePriceClick());
  lineEditPriceHistoryButton.addEventListener("click", () => handleLineEditPriceHistoryClick());
  priceChangeCloseButton.addEventListener("click", () => closePriceChangeSheet());
  priceChangeCancelButton.addEventListener("click", () => closePriceChangeSheet());
  priceChangePadEl.addEventListener("click", (event) => handlePriceChangePadClick(event));
  priceChangeSubmitButton.addEventListener("click", () => void handlePriceChangeSubmitClick());
  priceChangeRestoreButton.addEventListener("click", () => void handlePriceChangeRestoreClick());
  lineEditRemoveSpecialButton.addEventListener(
    "click",
    () => void handleLineEditRemoveSpecialClick(),
  );
  // UX39: Discount's own standalone header button is gone too, reached
  // through #btn-ticket-more's own listener, same as Payments/Manage
  // saved cards/Keep tab open above.
  ticketMoreButton.addEventListener("click", () => handleTicketMoreClick());
  lineEditChangeLiquorButton.addEventListener(
    "click",
    () => void handleLineEditChangeLiquorClick(),
  );
  lineEditChangeGuestButton.addEventListener("click", () => void handleLineEditChangeGuestClick());
  lineEditMoveToTabButton.addEventListener("click", () => void handleLineEditMoveToTabClick());
  lineEditRepeatButton.addEventListener("click", () => void handleLineEditRepeatClick());
  ticketRepeatButton.addEventListener("click", () => void handleTicketRepeatClick());
  lineEditViewRecipeButton.addEventListener("click", () => void handleLineEditViewRecipeClick());
  // The sheets have role="dialog" aria-modal="true" but, before this,
  // only their own close controls and a scrim click actually closed them:
  // a screen reader or keyboard user has no way to know Escape is
  // expected to work on a modal dialog, but every other modal-dialog
  // convention says it should (see design gate finding, ALPHA-06). Scoped
  // to "a sheet is open" rather than a global listener with its own state
  // check, so it never fires while the bartender is just using Escape for
  // something else on the ring screen.
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") {
      return;
    }
    if (!liquorScrim.hidden) {
      closeLiquorSheet();
    } else if (!lineEditScrim.hidden) {
      closeLineEditSheet();
    } else if (!payConfirmScrim.hidden) {
      closePayConfirmSheet();
    } else if (!choiceScrim.hidden) {
      closeChoiceSheet();
    } else if (!refundScrim.hidden) {
      closeRefundSheet();
    } else if (!splitSetupScrim.hidden) {
      closeSplitSetupSheet();
    } else if (!addGuestScrim.hidden) {
      closeAddGuestSheet();
    } else if (!managerExceptionsScrim.hidden) {
      closeManagerExceptionsSheet();
    } else {
      // Sixteenth amendment item 1: the account-menu dropdowns are not
      // scrim-backed sheets (see this file's own comment on ACCOUNT_MENUS
      // for why), so they get their own Escape branch here, checked only
      // once none of the sheets above are open.
      for (const menu of ACCOUNT_MENUS) {
        if (isAccountMenuOpen(menu)) {
          closeAccountMenu(menu);
          break;
        }
      }
    }
  });
  liquorSearch.addEventListener("input", () => void handleLiquorSearchInput());
  liquorAddButton.addEventListener("click", () => void handleLiquorAddClick());
  moreSpiritsButton.addEventListener("click", () => void handleMoreSpiritsClick());

  // ALPHA-12: multi-guest tickets and split payments.
  choiceClose.addEventListener("click", () => closeChoiceSheet());
  reasonCloseButton.addEventListener("click", () => closeChoiceSheet());
  reasonCancelButton.addEventListener("click", () => closeChoiceSheet());
  choiceCancelButton.addEventListener("click", () => closeChoiceSheet());
  // GATE4 follow-up: the paired footer's own Cancel and primary action.
  choiceActionsCancelButton.addEventListener("click", () => closeChoiceSheet());
  choicePrimaryActionButton.addEventListener("click", () => {
    const onPick = choicePrimaryActionOnPick;
    closeChoiceSheet();
    onPick?.();
  });
  choiceScrim.addEventListener("click", (event) => {
    if (event.target === choiceScrim) {
      closeChoiceSheet();
    }
  });
  // CL6-F1-F3: the refusal sheet's two buttons.
  businessDaySheetOkButton.addEventListener("click", () => closeChoiceSheet());
  businessDaySheetSettingsButton.addEventListener("click", () =>
    handleBusinessDaySheetSettingsClick(),
  );
  splitSetupClose.addEventListener("click", () => closeSplitSetupSheet());
  splitSetupCancelButton.addEventListener("click", () => closeSplitSetupSheet());
  splitSetupScrim.addEventListener("click", (event) => {
    if (event.target === splitSetupScrim) {
      closeSplitSetupSheet();
    }
  });
  splitSetupDecrementButton.addEventListener("click", () => handleSplitSetupDecrementClick());
  splitSetupIncrementButton.addEventListener("click", () => handleSplitSetupIncrementClick());
  splitSetupStartButton.addEventListener("click", () => void handleSplitSetupStartClick());
  // SPLIT1 By amount pad.
  amountPadClose.addEventListener("click", () => closeAmountPad());
  amountPadCancelButton.addEventListener("click", () => {
    closeAmountPad();
    if (amountPadRefund === undefined) {
      openPayModeSheet();
    }
  });
  for (const keyButton of amountPadSheetEl.querySelectorAll<HTMLButtonElement>(
    "[data-amount-key]",
  )) {
    keyButton.addEventListener("click", () =>
      handleAmountPadKey(keyButton.dataset["amountKey"] ?? ""),
    );
  }
  amountPadContinueButton.addEventListener("click", () => handleAmountPadContinueClick());
  addGuestButtonEl.addEventListener("click", () => openAddGuestSheet());
  addGuestClose.addEventListener("click", () => closeAddGuestSheet());
  addGuestCancelButton.addEventListener("click", () => closeAddGuestSheet());
  addGuestScrim.addEventListener("click", (event) => {
    if (event.target === addGuestScrim) {
      closeAddGuestSheet();
    }
  });
  addGuestSaveButton.addEventListener("click", () => void handleAddGuestSaveClick());
  // UX51: the counter reads the input's own maxlength (40, index.html)
  // rather than a JS-side literal, so the two can never drift apart.
  addGuestLabelInput.addEventListener("input", () => renderAddGuestLabelCounter());
  // MyTime track (track/mytime) Slice 2.
  addGuestSearchInput.addEventListener("input", () => handleAddGuestSearchInput());
  addGuestNewClientPromptButton.addEventListener("click", () =>
    handleAddGuestNewClientPromptClick(),
  );
  addGuestNewClientBackButton.addEventListener("click", () => handleAddGuestNewClientBackClick());
  addGuestNewClientSaveButton.addEventListener(
    "click",
    () => void handleAddGuestNewClientSaveClick(),
  );

  renderMatchMissDebug();
  renderLastPayCloseErrorDebug();

  void resumeFromStoredConfig();
}

init();

// ---------------------------------------------------------------------
// Discounts, slice A: the floor.
//
// Owner rulings this implements, 2026-08-28:
//   "the total discount should be a line item at the bottom of the
//    ticket" -- ONE summed row in the totals block, never a per-line
//    annotation. The per-line reductions still exist underneath it (tax,
//    splits and reporting all need them); this is presentation on top.
//   A service comp zeroes the line's ACTUAL price, so a comp on the
//    picker shows no dollar figure at all: there is none to show.
//
// TOUCH-ONLY, and strictly, because this is the floor. Every path here is
// a tappable row through the same openChoiceSheet the guest picker and
// the void reason picker already use: no keyboard entry anywhere, no
// hover, no blank option and no preselected default. Cancel closes the
// sheet and changes nothing, so there is no way to tap through this and
// accidentally discount something. The one text entry in the whole flow
// is the manager's PIN, which is the existing numeric pad.
//
// The discount list is fetched LIVE at the moment the picker opens rather
// than cached at sign-in, so an Admin turning a promotion off mid-shift
// takes it off the floor's picker on the next tap rather than at the next
// reload.
// ---------------------------------------------------------------------

/** money.discount.apply. Named once, resolved through the permission
 * snapshot everywhere it is used, and never inferred from a role
 * (CLAUDE.md's permission-driven rule, enforced by
 * scripts/check-no-role-driven-gating.ts). */
const DISCOUNT_PERMISSION = "money.discount.apply";

function canApplyDiscounts(): boolean {
  const session = state.session;
  return session !== undefined && hasPermission(session, DISCOUNT_PERMISSION);
}

async function refreshOrderDiscounts(
  isCurrent: () => boolean = () => true,
  /** PAYSPEED1: a discounts read the caller already started, so it runs alongside its other reads. */
  startedRead?: ReturnType<typeof getOrderDiscounts>,
): Promise<void> {
  if (state.orderId === undefined) {
    state.orderDiscounts = undefined;
    return;
  }
  const orderId = state.orderId;
  const session = sessionOrUndefined();
  if (session === undefined || !isCurrent()) return;
  const gen = sessionGeneration;
  const result = await (startedRead ?? getOrderDiscounts(session.apiBaseUrl, session.tenantId, orderId));
  if (
    !sessionIsCurrent(gen) ||
    state.session?.token !== session.token ||
    state.orderId !== orderId ||
    !isCurrent()
  )
    return;
  if (result.status !== 200) {
    // Left in place rather than cleared, the same reasoning
    // refreshPaymentPreview's own failure branch documents: a transient
    // read failure must not silently make a discounted ticket look
    // undiscounted. The pay sheet reads the server's own preview
    // separately and is the number the card is charged from.
    return;
  }
  state.orderDiscounts = result.body;
  // TF1: the per-discount rows refresh HERE alongside the summed row, for
  // the same reason renderTotals does. renderDiscountLines rebuilds only
  // its own container, so a discount landing does not discard the
  // ticket's scroll position the way a full renderTicketLines would.
  renderDiscountLines();
  renderLineSpecialStrips();
  renderLinePriceChangeStrips();
  renderLineStockOverrideStrips();
  renderTotals();
}

/** A one-line description of what a discount does, for the picker row.
 * Plain language, no jargon: the floor reads this mid-service. */
function discountSublabel(discount: DiscountDto): string {
  // DSC21: the picker names who owns the definition first.
  return `${discountKindLabel(discount)} \u00b7 ${discountTermsLabel(discount)}`;
}

function discountTermsLabel(discount: DiscountDto): string {
  if (discount.markdown_type === "percentage") {
    // B2/C0, 2026-08-29: a comp is a percentage at 10000 basis points,
    // not its own markdown type, so it is recognized by value here
    // rather than by a "comp" markdown type that no longer exists. The
    // friendlier wording is kept for the 100% case rather than showing
    // "100.00% off", which is technically correct but not how a
    // bartender mid-service would read a comp.
    if (discount.markdown_basis_points === 10_000) {
      return "Takes the whole price off";
    }
    return `${basisPointsToPercentString(discount.markdown_basis_points ?? 0)}% off`;
  }
  return `${formatCents(discount.markdown_amount_cents ?? 0)} off`;
}

interface DiscountTarget {
  /** Undefined means the whole ticket. */
  orderLineId?: string;
  /** What the sheet's subtitle says this is being applied to. */
  label: string;
}

async function openDiscountPicker(target: DiscountTarget, refusal?: string): Promise<void> {
  const session = requireSession();
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  const result = await listDiscounts(session.apiBaseUrl, session.tenantId, { forFloor: true });
  if (result.status !== 200) {
    showToast(
      `Could not load the discounts. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  // Only the discounts that can actually be used HERE. Filtering on the
  // discount's own "Apply to" checkboxes rather than offering everything
  // and letting the server refuse: a picker that offers a choice which
  // always fails is worse than one that does not offer it.
  const isItemLevel = target.orderLineId !== undefined;
  const usable = result.body.discounts.filter((discount) =>
    isItemLevel ? discount.applies_to_items : discount.applies_to_tickets,
  );
  if (usable.length === 0) {
    showToast(
      isItemLevel
        ? "No discounts are set up for a single drink. An admin adds them in Settings."
        : "No discounts are set up for a whole ticket. An admin adds them in Settings.",
      "danger",
    );
    return;
  }
  openChoiceSheet({
    title: isItemLevel ? "Discount this drink" : "Discount this ticket",
    subtitle: target.label,
    ...(refusal === undefined ? {} : { errorNote: refusal }),
    options: sortByName(usable, (discount) => discount.name).map((discount) =>
      // LOUNGERMAJ1 (lounger-12): a discount already on this drink or ticket is marked Applied, with what it takes off
      // (the server's own figure), and is not offered again.
      appliedManualDiscount(discount, target) !== undefined
        ? {
            id: discount.id,
            label: discount.name,
            sublabel: appliedDiscountSublabel(appliedManualDiscount(discount, target)!),
            testid: "discount-option",
            disabled: true,
          }
        :
      // SPLIT1 (owner ruling Q5, 2026-10-10): once a payment was taken by amount the remainder must be paid, so the
      // Walkout (the discount that ends a ticket) is N/A. The server refuses it the same way
      // (amount_payments_in_progress); this only keeps the picker from offering a choice that always fails.
      discount.closes_ticket && (state.paymentPreview?.amountPayments?.count ?? 0) > 0
        ? {
            id: discount.id,
            label: discount.name,
            sublabel: "Payment in progress",
            testid: "discount-option",
            disabled: true,
          }
        : {
            id: discount.id,
            label: discount.name,
            sublabel: discountSublabelWithReplacement(discount, target),
            testid: "discount-option",
          },
    ),
    onPick: (discountId) => {
      const discount = usable.find((candidate) => candidate.id === discountId);
      if (discount === undefined) {
        return;
      }
      // LOUNGERMAJ1 (lounger-12): Comp and Walkout name what they take before they run.
      if (discountNeedsConfirm(discount)) {
        void confirmCompOrWalkout(discount, target);
        return;
      }
      continueDiscountPick(discount, target);
    },
  });
}

function continueDiscountPick(discount: DiscountDto, target: DiscountTarget): void {
  if (discount.requires_reason) {
    void openDiscountReasonPicker(discount, target);
    return;
  }
  void submitDiscount(discount, target, undefined);
}

/** LOUNGERMAJ1 (lounger-12): the discount already applied by hand to this drink, or to the whole ticket, if any. */
function appliedManualDiscount(
  discount: DiscountDto,
  target: DiscountTarget,
): OrderDiscountApplicationDto | undefined {
  return (state.orderDiscounts?.applications ?? []).find(
    (application) =>
      application.origin === "manual" &&
      application.discountId === discount.id &&
      (application.orderLineId ?? undefined) === target.orderLineId,
  );
}

function appliedDiscountSublabel(application: OrderDiscountApplicationDto): string {
  return application.reductionCents > 0
    ? `\u2713 Applied, ${formatCents(-application.reductionCents)}`
    : "\u2713 Applied, a bigger discount already covers it";
}

/** LOUNGERMAJ1 (lounger-12): a percentage that is bigger than the percentage already on this drink or ticket takes its
 * place, and the row says so. Anything else is left to the server, which refuses a discount that saves nothing. */
function discountSublabelWithReplacement(discount: DiscountDto, target: DiscountTarget): string {
  const base = discountSublabel(discount);
  if (discount.markdown_type !== "percentage") {
    return base;
  }
  const replaced = (state.orderDiscounts?.applications ?? []).find(
    (application) =>
      application.origin === "manual" &&
      (application.orderLineId ?? undefined) === target.orderLineId &&
      application.reductionCents > 0 &&
      application.markdownType === "percentage" &&
      (application.markdownBasisPoints ?? 0) < (discount.markdown_basis_points ?? 0),
  );
  return replaced === undefined ? base : `${base} \u00b7 Replaces ${replaced.discountName}`;
}

function discountNeedsConfirm(discount: DiscountDto): boolean {
  return discount.closes_ticket || discount.markdown_basis_points === 10_000;
}

/** LOUNGERMAJ1 (lounger-12): one tap used to comp a whole ticket or walk it out. The amounts are the server's own figures
 * (the discount read and the payment preview); a figure that is not loaded is left out, never guessed. */
/** WALKOUT1: the label of the button that confirms a comp or a walkout. It never repeats the discount's own name after a verb
 * that already contains it: a ticket-closing discount that is a form of "walkout" reads "Walk out ticket", any other
 * ticket-closing discount reads "Close with <name>", and a comp reads "Apply <name>" unless the name already starts with
 * its own verb. */
function confirmDiscountButtonLabel(discount: DiscountDto): string {
  const name = discount.name.trim();
  if (discount.closes_ticket) {
    return /walk[\s-]?outs?\b/i.test(name) ? "Walk out ticket" : `Close with ${name}`;
  }
  return /^(apply|comp)\b/i.test(name) ? name : `Apply ${name}`;
}

/** LOUNGERMAJ1 (lounger-12): one tap used to comp a whole ticket or walk it out. The amounts are the server's own figures
 * (the discount read and the payment preview); a figure that is not loaded is left out, never guessed. WALKOUT1: the
 * preview is read again as the sheet opens, because the written-off figure is what the ticket owes NOW (balanceCents: the
 * whole ticket after discounts, tax included, less amount payments), and a read taken before the last change is not that. */
async function confirmCompOrWalkout(discount: DiscountDto, target: DiscountTarget): Promise<void> {
  const isItemLevel = target.orderLineId !== undefined;
  if (state.orderId !== undefined) {
    await refreshPaymentPreview();
  }
  const lineGross = isItemLevel
    ? state.orderDiscounts?.lines.find((line) => line.orderLineId === target.orderLineId)?.grossCents
    : undefined;
  const wholeTicketCents = state.orderDiscounts?.totals.subtotalBeforeDiscountCents;
  // A preview that is missing, failed, or older than the lines is not the figure; a balance of nothing is not a write-off.
  const owed = paymentPreviewStale() ? undefined : state.paymentPreview?.balanceCents;
  const amountCents = discount.closes_ticket
    ? owed !== undefined && owed > 0
      ? owed
      : undefined
    : isItemLevel
      ? lineGross
      : wholeTicketCents;
  const title = discount.closes_ticket
    ? "Walk out this ticket?"
    : isItemLevel
      ? "Comp this drink?"
      : "Comp this ticket?";
  openChoiceSheet({
    title,
    subtitle: target.label,
    infoRows:
      amountCents === undefined
        ? []
        : [
            {
              label: discount.closes_ticket ? "Written off" : "Comes off",
              detail: formatCents(amountCents),
              testid: "discount-confirm-amount",
            },
          ],
    infoNote: discount.closes_ticket
      ? "Closes the ticket. Nothing is collected and it counts as a walkout."
      : "Takes the whole price off.",
    cancelLabel: "Cancel",
    primaryAction: {
      id: "confirm-discount",
      label: confirmDiscountButtonLabel(discount),
      variant: "danger",
      testid: "discount-confirm",
    },
    onPick: () => continueDiscountPick(discount, target),
  });
}

async function openDiscountReasonPicker(
  discount: DiscountDto,
  target: DiscountTarget,
): Promise<void> {
  const session = requireSession();
  const result = await listDiscountReasons(session.apiBaseUrl, session.tenantId);
  if (result.status !== 200) {
    showToast(
      `Could not load the discount reasons. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const reasons = sortByName(result.body.discountReasons, (reason) => reason.label);
  if (reasons.length === 0) {
    // Never expected (every location is seeded), but an Admin who retired
    // all of them would otherwise get an empty sheet with no explanation.
    showToast(
      "There are no discount reasons set up for this location. An admin adds them in Settings.",
      "danger",
    );
    return;
  }
  openChoiceSheet({
    title: "Why?",
    subtitle: `${discount.name} on ${target.label}`,
    options: reasons.map((reason) => ({
      id: reason.id,
      label: reason.label,
      testid: "discount-reason-option",
    })),
    onPick: (reasonId) => void submitDiscount(discount, target, reasonId),
  });
}

async function submitDiscount(
  discount: DiscountDto,
  target: DiscountTarget,
  discountReasonId: string | undefined,
): Promise<void> {
  const session = requireSession();
  const orderId = state.orderId;
  if (orderId === undefined) {
    return;
  }
  // One key for the whole flow, including any approval retry, so an
  // approved retry replays rather than discounting twice and
  // recordPermissionApproval writes one audit row. Two separate things
  // can each raise the PIN prompt here (the actor's own grant, and the
  // discount's own "needs approval" flag) and they both arrive as the
  // same 403, so requestApproval drives both without knowing which.
  const idempotencyKey = crypto.randomUUID();
  const gated = await requestApproval(
    `Discount: ${discount.name}`,
    (approval) =>
      applyOrderDiscount(session.apiBaseUrl, session.tenantId, orderId, {
        idempotencyKey,
        discountId: discount.id,
        ...(target.orderLineId === undefined ? {} : { orderLineId: target.orderLineId }),
        ...(discountReasonId === undefined ? {} : { discountReasonId }),
        ...approval,
      }),
    () => showRingScreen(),
    // DSC32: the picker asks "who can approve THIS discount" rather than
    // relying on the caller's own posture on money.discount.apply, which
    // is false for an actor whose own grant is "allowed" (override) on a
    // discount that itself demands approval.
    { discountId: discount.id },
  );
  if (gated.canceled) {
    return;
  }
  const result = gated.result;
  // DSC36 (DEC-343): a discount that would take nothing off the guest was NOT added
  // (the server wrote nothing and asked no manager). Say why in red on the picker and
  // leave it open so another discount can be chosen, with no toast and no screen change.
  const refusal = discountRefusalMessage(result);
  if (refusal !== undefined) {
    void openDiscountPicker(target, refusal);
    return;
  }
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not apply that discount (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    showRingScreen();
    return;
  }
  showRingScreen();
  // CL3: a Walkout ends the ticket, and the server says so on this same
  // response. The floor is told in the prompt rather than thrown back to
  // Open Tabs, so a lounger who just walked a tab out can see the ticket
  // it happened to before leaving the screen.
  if (settledAndClosed(result.body.settlementClose)) {
    showToast(`${discount.name} applied.`, "success");
    finishClosedTicket();
    return;
  }
  // refreshPaymentPreview refreshes the discount read too, so this is one
  // round trip for both, never two answers that could disagree.
  const discountBefore = state.orderDiscounts?.totals.discountCents;
  await refreshPaymentPreview();
  renderTicket();
  // LOUNGERMAJ1 (lounger-12): the toast says what this discount took off, from the server's own before and after.
  const discountAfter = state.orderDiscounts?.totals.discountCents;
  const tookCents =
    discountBefore === undefined || discountAfter === undefined
      ? undefined
      : discountAfter - discountBefore;
  showToast(
    tookCents === undefined || tookCents <= 0
      ? `${discount.name} applied.`
      : `${discount.name} applied, ${formatCents(-tookCents)}.`,
    "success",
  );
}

// ---------------------------------------------------------------------
// M5 (docs/build-briefs/M5-till-price-change.md; DEC-278, DEC-282;
// design/mockups/m5-price-override.html): change one drink's price at the
// till, and put it back.
//
// TOUCH ONLY: the new price is typed on a cents-style numeric pad (tap 1, 1,
// 0, 0 and the display reads $11.00; no decimal key, no text field), four
// chips fill it in one tap, and the reason is a tile, never typed and never
// preselected. The server enforces every rule (never $0.00, a reason, who may,
// not on a paid line); this sheet only reflects them, so a stale screen cannot
// slip anything past. The menu price and its arithmetic are read from the
// line's own stored facts, and the "half off or more" threshold comes from the
// tenant's settings, never from a number in this file.
// ---------------------------------------------------------------------

/** money.price_override. Named once, resolved through the permission
 * snapshot, never inferred from a role. */
const PRICE_OVERRIDE_PERMISSION = "money.price_override";

/** The most digits the pad accepts: five, so $999.99 is the ceiling. */
const PRICE_PAD_MAX_DIGITS = 5;

function canChangePrice(): boolean {
  const session = state.session;
  return session !== undefined && hasPermission(session, PRICE_OVERRIDE_PERMISSION);
}

/** True when this session's own grant is approval_required (an assistant
 * manager): the button then says it asks a manager. Read from the snapshot,
 * never from a role name. */
function priceChangeNeedsApproval(): boolean {
  const session = state.session;
  return (
    session !== undefined && session.permissions[PRICE_OVERRIDE_PERMISSION] === "approval_required"
  );
}

interface PriceChangeSheetState {
  orderId: string;
  orderLineId: string;
  lineName: string;
  /** The system's price: the frozen quote on a changed line, the line total
   * on an unchanged one. */
  menuPriceCents: number;
  /** The price in force right now. */
  currentPriceCents: number;
  isChanged: boolean;
  /** The pad's digits, at most five, no leading zero. */
  digits: string;
  reasonId: string | undefined;
  reasons: PriceChangeReasonDto[];
  /** The tenant's "half off or more" threshold, in basis points of the menu price. */
  flagOffBasisPoints: number;
  /** M5-F1 (DEC-282). What the drink costs to make, in cents, when the
   * server knows it in full; undefined when unknown or when the read failed.
   * Never a default: undefined shows no warning. */
  drinkCostCents: number | undefined;
  /** True when the figure is a lower bound (an untracked ingredient counts as
   * $0): the warning says "at least" instead of "about". */
  drinkCostPartial: boolean;
  submitting: boolean;
}

let priceChangeSheet: PriceChangeSheetState | undefined;

function priceFromDigits(digits: string): number {
  return digits === "" ? 0 : Number(digits);
}

/** The quick chips: each fills the pad in one tap. A chip whose price would
 * be $0.00 or less is not offered as tappable. */
function priceChangePresets(
  menuPriceCents: number,
): Array<{ label: string; cents: number; id: string }> {
  return [
    { id: "off-100", label: "$1 off", cents: menuPriceCents - 100 },
    { id: "off-200", label: "$2 off", cents: menuPriceCents - 200 },
    { id: "off-300", label: "$3 off", cents: menuPriceCents - 300 },
    // Half, rounded half-up to the cent.
    { id: "half", label: "Half off", cents: Math.floor((menuPriceCents + 1) / 2) },
  ];
}

/** "2 oz at $5.50/oz is $11.00, plus $3.00 build charge." One line (the
 * liquor's name is on the ticket line already). Read from the line's own stored facts. Shown ONLY when those facts
 * add up to the menu price: an explanation that does not match the price on
 * screen is worse than none. */
function priceChangeMathText(
  row: {
    pour_ounces: string | null;
    per_ounce_price_cents: number | null;
    build_charge_cents: number | null;
    modifiers_cents: number | null;
  },
  menuPriceCents: number,
): string {
  if (row.pour_ounces === null || row.per_ounce_price_cents === null) {
    return "";
  }
  const ounces = Number(row.pour_ounces);
  if (!Number.isFinite(ounces) || ounces <= 0) {
    return "";
  }
  const pourCents = Math.round(row.per_ounce_price_cents * ounces);
  const buildCents = row.build_charge_cents ?? 0;
  const modifierCents = row.modifiers_cents ?? 0;
  if (pourCents + buildCents + modifierCents !== menuPriceCents) {
    return "";
  }
  const ouncesLabel = String(ounces);
  let text = `${ouncesLabel} oz at ${formatCents(row.per_ounce_price_cents)}/oz is ${formatCents(pourCents)}`;
  if (buildCents > 0) {
    text += `, plus ${formatCents(buildCents)} build charge`;
  }
  if (modifierCents !== 0) {
    text += `, ${modifierCents > 0 ? "plus" : "minus"} ${formatCents(Math.abs(modifierCents))} for extras`;
  }
  return `${text}.`;
}

async function handleLineEditChangePriceClick(): Promise<void> {
  const orderLineId = state.lineEditTargetOrderLineId;
  if (orderLineId === undefined) {
    return;
  }
  await openPriceChangeSheet(orderLineId);
}

async function openPriceChangeSheet(orderLineId: string): Promise<void> {
  const session = requireSession();
  const orderId = state.orderId;
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  if (orderId === undefined || line === undefined) {
    return;
  }
  // The line's own stored row (quoted price, pour, build charge) and the
  // tenant's live reason list, fetched when the sheet opens so a reason an
  // admin retired mid-shift is gone from the next open.
  const [orderResult, reasonsResult, costResult] = await Promise.all([
    getOrder(session.apiBaseUrl, session.tenantId, orderId),
    listPriceChangeReasons(session.apiBaseUrl, session.tenantId),
    // M5-F1: a failed read or an unknown cost simply shows no warning.
    getLineDrinkCost(session.apiBaseUrl, session.tenantId, orderId, orderLineId),
  ]);
  if (orderResult.status !== 200) {
    showToast(
      `Could not load this drink. ${capitalizeServerMessage(orderResult.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  if (reasonsResult.status !== 200) {
    showToast(
      `Could not load the reasons. ${capitalizeServerMessage(reasonsResult.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const row = orderResult.body.lines.find((candidate) => candidate.id === orderLineId);
  if (row === undefined || row.line_total_cents === null) {
    showToast("This drink has no price to change.", "danger");
    return;
  }
  const reasons = sortByName(reasonsResult.body.priceChangeReasons, (reason) => reason.label);
  if (reasons.length === 0) {
    showToast(
      "There are no price change reasons set up for this location. An admin adds them in Settings.",
      "danger",
    );
    return;
  }
  const isChanged = row.price_change_cents !== null;
  const menuPriceCents = row.quoted_price_cents ?? row.line_total_cents;
  priceChangeSheet = {
    orderId,
    orderLineId,
    lineName: line.name,
    menuPriceCents,
    currentPriceCents: row.line_total_cents,
    isChanged,
    digits: "",
    reasonId: undefined,
    reasons,
    flagOffBasisPoints: reasonsResult.body.flagOffBasisPoints,
    drinkCostCents:
      costResult.status === 200 &&
      costResult.body.known === true &&
      typeof costResult.body.costCents === "number"
        ? costResult.body.costCents
        : undefined,
    drinkCostPartial: costResult.body.partial === true,
    submitting: false,
  };
  const forGuest = state.guests.find((guest) => guest.id === line.guestId);
  // UX102: the drink, and the guest when the line has one; no sentence.
  priceChangeSubtitleEl.textContent =
    forGuest === undefined ? line.name : `${line.name}, ${forGuest.label}`;
  priceChangeMenuPriceEl.textContent = formatCents(menuPriceCents);
  priceChangeMathEl.textContent = priceChangeMathText(row, menuPriceCents);
  priceChangeMathEl.hidden = priceChangeMathEl.textContent === "";
  priceChangeApprovalNoteEl.hidden = !priceChangeNeedsApproval();
  priceChangeRestoreButton.hidden = !isChanged;
  buildPriceChangePresetButtons();
  buildPriceChangeReasonTiles();
  renderPriceChangeSheet();
  // In place of the line sheet, inside the same scrim.
  lineEditSheetEl.hidden = true;
  priceChangeSheetEl.hidden = false;
  lineEditScrim.hidden = false;
  noteTicketSheetOpen("priceChange", orderLineId);
}

function closePriceChangeSheet(): void {
  closeLineEditSheet();
}

function buildPriceChangePresetButtons(): void {
  const sheet = priceChangeSheet;
  priceChangePresetsEl.innerHTML = "";
  if (sheet === undefined) {
    return;
  }
  for (const preset of priceChangePresets(sheet.menuPriceCents)) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "po-preset";
    button.dataset["testid"] = "price-change-preset";
    button.dataset["presetId"] = preset.id;
    button.textContent = preset.label;
    // A chip that would set the price to $0.00 or below is not offered, and
    // one that would equal the price already in force would change nothing.
    button.disabled = preset.cents <= 0 || preset.cents > 99_999;
    button.addEventListener("click", () => {
      const current = priceChangeSheet;
      if (current === undefined || current.submitting || preset.cents <= 0) {
        return;
      }
      current.digits = String(preset.cents);
      renderPriceChangeSheet();
    });
    priceChangePresetsEl.append(button);
  }
}

function buildPriceChangeReasonTiles(): void {
  const sheet = priceChangeSheet;
  priceChangeReasonsEl.innerHTML = "";
  if (sheet === undefined) {
    return;
  }
  for (const reason of sheet.reasons) {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = "po-reason";
    tile.dataset["testid"] = "price-change-reason";
    tile.dataset["reasonId"] = reason.id;
    tile.setAttribute("aria-pressed", "false");
    tile.textContent = reason.label;
    tile.addEventListener("click", () => {
      const current = priceChangeSheet;
      if (current === undefined || current.submitting) {
        return;
      }
      current.reasonId = reason.id;
      renderPriceChangeSheet();
    });
    priceChangeReasonsEl.append(tile);
  }
}

function handlePriceChangePadClick(event: Event): void {
  const sheet = priceChangeSheet;
  const target = event.target;
  if (sheet === undefined || sheet.submitting || !(target instanceof HTMLElement)) {
    return;
  }
  const key = target.closest<HTMLElement>("[data-key]")?.dataset["key"];
  if (key === undefined) {
    return;
  }
  if (key === "clear") {
    sheet.digits = "";
  } else if (key === "back") {
    sheet.digits = sheet.digits.slice(0, -1);
  } else if (/^[0-9]$/.test(key)) {
    // The sixth digit is ignored (the ceiling is $999.99), and a leading
    // zero adds nothing.
    if (sheet.digits.length < PRICE_PAD_MAX_DIGITS) {
      sheet.digits = `${sheet.digits}${key}`.replace(/^0+/, "");
    }
  }
  renderPriceChangeSheet();
}

/** Everything the sheet shows, drawn from its own state. The blocked line
 * says which of the two things is missing, and nothing is ever preselected. */
function renderPriceChangeSheet(): void {
  const sheet = priceChangeSheet;
  if (sheet === undefined) {
    return;
  }
  const newCents = priceFromDigits(sheet.digits);
  priceChangeNewValueEl.textContent = formatCents(newCents);
  priceChangeNewEl.classList.toggle("is-empty", newCents === 0);
  priceChangeNewEl.classList.toggle("is-set", newCents > 0);

  const differs = newCents > 0 && newCents !== sheet.currentPriceCents;
  const isMenuPriceAgain = sheet.isChanged && newCents === sheet.menuPriceCents;
  const priceOk = differs && !isMenuPriceAgain;
  if (newCents > 0 && newCents !== sheet.menuPriceCents) {
    const gap = Math.abs(sheet.menuPriceCents - newCents);
    priceChangeDiffEl.textContent =
      newCents < sheet.menuPriceCents
        ? `${formatCents(gap)} less than the menu price`
        : `${formatCents(gap)} more than the menu price`;
    priceChangeDiffEl.classList.add("is-changed");
  } else {
    priceChangeDiffEl.textContent = "";
    priceChangeDiffEl.classList.remove("is-changed");
  }

  for (const tile of priceChangeReasonsEl.querySelectorAll<HTMLElement>(".po-reason")) {
    const on = tile.dataset["reasonId"] === sheet.reasonId;
    tile.classList.toggle("is-on", on);
    tile.setAttribute("aria-pressed", on ? "true" : "false");
  }
  const reasonPicked = sheet.reasonId !== undefined;
  priceChangeReasonStateEl.textContent = reasonPicked ? "Picked" : "Pick one";
  priceChangeReasonStateEl.classList.toggle("is-picked", reasonPicked);

  // The blocked line names what is missing. UX102: a missing price or reason is already said by the disabled
  // button, so that line is read by assistive tech and not drawn; only a refusal (the menu price again, the
  // same price) is drawn.
  let blocked = "";
  if (!priceOk && !reasonPicked) {
    blocked = "Type a new price and pick a reason to continue.";
  } else if (!priceOk) {
    blocked = isMenuPriceAgain
      ? "That is the menu price. Use Put back to menu price instead."
      : newCents > 0
        ? `The price is already ${formatCents(sheet.currentPriceCents)}. Type a different price.`
        : "Type a new price to continue.";
  } else if (!reasonPicked) {
    blocked = "Pick a reason to continue.";
  }
  priceChangeBlockedEl.hidden = blocked === "";
  priceChangeBlockedEl.textContent = blocked;
  priceChangeBlockedEl.classList.toggle(
    "sr-only",
    !isMenuPriceAgain && !(newCents > 0 && !differs),
  );

  // Half off or more: new price is at or below (100% minus the threshold) of
  // the menu price. Integer arithmetic only.
  const deep =
    newCents > 0 && newCents * 10_000 <= sheet.menuPriceCents * (10_000 - sheet.flagOffBasisPoints);
  priceChangeDeepEl.hidden = !deep;

  // M5-F1 (DEC-282): warn, never block, when the typed price is below what
  // the drink's ingredient cost (a lower bound when some ingredient is untracked,
  // DEC-311), and only when some cost is known. The submit
  // button below does not read this.
  const belowCost =
    sheet.drinkCostCents !== undefined && newCents > 0 && newCents < sheet.drinkCostCents;
  priceChangeBelowCostEl.hidden = !belowCost;
  priceChangeBelowCostEl.replaceChildren();
  if (belowCost && sheet.drinkCostCents !== undefined) {
    const lead = document.createElement("strong");
    lead.textContent = "Below cost";
    priceChangeBelowCostEl.append(
      lead,
      `: ingredients ${sheet.drinkCostPartial ? "at least" : "about"} ${formatCents(sheet.drinkCostCents)}.`,
    );
  }

  const ready = priceOk && reasonPicked && !sheet.submitting;
  priceChangeSubmitButton.disabled = !ready;
  const asksManager = priceChangeNeedsApproval();
  priceChangeSubmitButton.textContent =
    priceOk && reasonPicked
      ? asksManager
        ? `Ask a manager to approve ${formatCents(newCents)}`
        : `Set price to ${formatCents(newCents)}`
      : asksManager
        ? "Ask a manager to approve"
        : "Set price";
  // M5-R2 (DEC-333): putting back a RAISED price lowers what the guest pays,
  // so it needs a reason from the same tiles (and a manager, asked on tap).
  const raised = priceChangeIsRaised(sheet);
  priceChangeRestoreButton.disabled = sheet.submitting || (raised && !reasonPicked);
  priceChangeRestoreButton.textContent = !raised
    ? "Put back to menu price"
    : !reasonPicked
      ? "Pick a reason to put back"
      : asksManager
        ? "Ask a manager to put back to menu price"
        : "Put back to menu price";
  priceChangeCancelButton.disabled = sheet.submitting;
}

async function handlePriceChangeSubmitClick(): Promise<void> {
  const sheet = priceChangeSheet;
  if (sheet === undefined || sheet.submitting) {
    return;
  }
  const newCents = priceFromDigits(sheet.digits);
  const reasonId = sheet.reasonId;
  if (reasonId === undefined || newCents <= 0) {
    return;
  }
  const reason = sheet.reasons.find((candidate) => candidate.id === reasonId);
  const session = requireSession();
  sheet.submitting = true;
  renderPriceChangeSheet();
  // One key for the whole flow, approval retry included, so an approved
  // retry replays rather than changing the price twice.
  const idempotencyKey = crypto.randomUUID();
  const gated = await requestApproval(
    `Change price: ${sheet.lineName}, ${formatCents(sheet.currentPriceCents)} to ${formatCents(newCents)}`,
    (approval) =>
      changeLinePrice(session.apiBaseUrl, session.tenantId, sheet.orderId, sheet.orderLineId, {
        idempotencyKey,
        newPriceCents: newCents,
        reasonId,
        ...approval,
      }),
    () => showRingScreen(),
    {
      pinContext: `${sheet.lineName}, ${formatCents(sheet.currentPriceCents)} to ${formatCents(newCents)}${reason === undefined ? "" : ` · ${reason.label}`}`,
    },
  );
  if (priceChangeSheet !== sheet) {
    // Signed out or closed while the request was on the wire.
    return;
  }
  sheet.submitting = false;
  if (gated.canceled) {
    renderPriceChangeSheet();
    return;
  }
  const result = gated.result;
  if (result.status !== 200 && result.status !== 201) {
    showRingScreen();
    showToast(
      `Could not change the price. ${capitalizeServerMessage(result.body.message ?? "Try again.")}`,
      "danger",
    );
    if (result.body.error === "price_change_reason_inactive") {
      // The reason was retired since the sheet opened: ask again from the live list.
      sheet.reasonId = undefined;
      const fresh = await listPriceChangeReasons(session.apiBaseUrl, session.tenantId);
      if (priceChangeSheet === sheet && fresh.status === 200) {
        sheet.reasons = sortByName(fresh.body.priceChangeReasons, (reason) => reason.label);
        buildPriceChangeReasonTiles();
      }
    }
    renderPriceChangeSheet();
    return;
  }
  showRingScreen();
  closePriceChangeSheet();
  await afterPriceChange();
  showToast(`Price set to ${formatCents(newCents)}.`, "success");
  handleHoldIncrementOutcome(result.body.holdIncrement);
}

/** True when the price in force is above the menu price, so putting it back
 * lowers what the guest pays (M5-R2, DEC-333). */
function priceChangeIsRaised(sheet: PriceChangeSheetState): boolean {
  return sheet.isChanged && sheet.currentPriceCents > sheet.menuPriceCents;
}

async function handlePriceChangeRestoreClick(): Promise<void> {
  const sheet = priceChangeSheet;
  if (sheet === undefined || sheet.submitting || !sheet.isChanged) {
    return;
  }
  const session = requireSession();
  sheet.submitting = true;
  renderPriceChangeSheet();
  const raised = priceChangeIsRaised(sheet);
  const reasonId = sheet.reasonId;
  if (raised && reasonId === undefined) {
    sheet.submitting = false;
    renderPriceChangeSheet();
    return;
  }
  // A lowered price goes back with no reason and no second person (DEC-282).
  // A raised one needs the picked reason and a manager (DEC-333): the same
  // approval flow as a price cut, one key for the whole flow so an approved
  // retry replays rather than putting back twice.
  const idempotencyKey = crypto.randomUUID();
  const reason = sheet.reasons.find((candidate) => candidate.id === reasonId);
  const gated = await requestApproval(
    `Put back to menu price: ${sheet.lineName}, ${formatCents(sheet.currentPriceCents)} to ${formatCents(sheet.menuPriceCents)}`,
    (approval) =>
      restoreLinePrice(session.apiBaseUrl, session.tenantId, sheet.orderId, sheet.orderLineId, {
        idempotencyKey,
        ...(raised && reasonId !== undefined ? { reasonId } : {}),
        ...approval,
      }),
    () => showRingScreen(),
    {
      pinContext: `${sheet.lineName}, ${formatCents(sheet.currentPriceCents)} to ${formatCents(sheet.menuPriceCents)}${reason === undefined ? "" : ` · ${reason.label}`}`,
    },
  );
  if (priceChangeSheet !== sheet) {
    return;
  }
  sheet.submitting = false;
  if (gated.canceled) {
    renderPriceChangeSheet();
    return;
  }
  const result = gated.result;
  if (result.status !== 200 && result.status !== 201) {
    showRingScreen();
    showToast(
      `Could not put the price back. ${capitalizeServerMessage(result.body.message ?? "Try again.")}`,
      "danger",
    );
    renderPriceChangeSheet();
    return;
  }
  showRingScreen();
  closePriceChangeSheet();
  await afterPriceChange();
  showToast("Price put back to the menu price.", "success");
  handleHoldIncrementOutcome(result.body.holdIncrement);
}

/** After either command: the ticket's prices, strips and totals all come from
 * the server again, never from a client-side guess. */
async function afterPriceChange(): Promise<void> {
  renderTicket();
  await recomputeQuote();
  await refreshPaymentPreview();
  await refreshLinePriceChangeCounts();
  renderTicket();
}

/** M5-R10. After a change or a put-back, takes each line's ledger row count
 * from the server's own ticket read (the strip's badge and the line menu's
 * Price history row), never from a client-side increment. Best effort: if the
 * read fails the counts stay as they were and the sheet itself still reads the
 * ledger when it is opened. */
async function refreshLinePriceChangeCounts(): Promise<void> {
  const session = sessionOrUndefined();
  const orderId = state.orderId;
  if (session === undefined || orderId === undefined) {
    return;
  }
  const result = await getOrder(session.apiBaseUrl, session.tenantId, orderId);
  if (result.status !== 200 || state.orderId !== orderId) {
    return;
  }
  const counts = new Map(result.body.lines.map((line) => [line.id, line.priceChangeCount ?? 0]));
  for (const line of state.lines) {
    const count = counts.get(line.orderLineId) ?? 0;
    if (count > 0) {
      line.priceChangeCount = count;
    } else {
      delete line.priceChangeCount;
    }
  }
}

/** M5-R10. Loads one drink's history into the shared sheet's own shape. A
 * failed read is a plain sentence the sheet shows with a retry, never blank. */
function loadPriceHistoryFor(
  orderId: string,
  orderLineId: string,
): () => Promise<PriceHistoryLoad> {
  return async () => {
    const session = sessionOrUndefined();
    if (session === undefined) {
      return { ok: false, message: "Sign in again." };
    }
    const result = await getLinePriceHistory(
      session.apiBaseUrl,
      session.tenantId,
      orderId,
      orderLineId,
    );
    if (result.status !== 200) {
      return {
        ok: false,
        message: capitalizeServerMessage(result.body.message ?? "Check your connection."),
      };
    }
    return { ok: true, view: buildPriceHistoryView(result.body.history) };
  };
}

/** M5-R10. Opens the history for a ticket line from the strip or the line menu. */
function openLinePriceHistory(orderLineId: string, opener: HTMLElement | null): void {
  const orderId = state.orderId;
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  if (orderId === undefined || line === undefined) {
    return;
  }
  openPriceHistorySheet({
    title: line.name,
    subtitle: state.orderName ?? "Ticket",
    load: loadPriceHistoryFor(orderId, orderLineId),
    onClosed: () => opener?.focus(),
  });
}

function handleLineEditPriceHistoryClick(): void {
  const orderLineId = state.lineEditTargetOrderLineId;
  if (orderLineId === undefined) {
    return;
  }
  closeLineEditSheet();
  openLinePriceHistory(orderLineId, null);
}

/** AU14-F15 / DEC-276. The grey "Sold past an 86" strip under a drink that was
 * sold while its bottle was flagged 86: who sold it, who approved, and why, all
 * resolved on the server. One more full-width element under the row (the price
 * change strip's own shape). An override from before the reason was required
 * says so plainly, never blank and never an invented reason. */
function renderLineStockOverrideStrips(): void {
  const byLine = new Map(
    state.lines
      .filter((line) => line.stockOverride !== undefined)
      .map((line) => [line.orderLineId, line.stockOverride as LineStockOverrideDto] as const),
  );
  ticketLinesEl.querySelectorAll<HTMLElement>(".line[data-order-line-id]").forEach((row) => {
    row.querySelector(".line-stock-override")?.remove();
    const record = byLine.get(row.dataset["orderLineId"] ?? "");
    if (record === undefined) {
      return;
    }
    row.classList.add("line-has-special");
    const strip = document.createElement("div");
    strip.className = "line-stock-override";
    strip.dataset["testid"] = "line-stock-override-strip";
    const tag = document.createElement("span");
    tag.className = "rec-tag";
    tag.textContent = "Sold past an 86";
    const who = document.createElement("span");
    who.className = "rec-who";
    who.dataset["testid"] = "line-stock-override-who";
    who.textContent =
      (record.byName ?? "") +
      (record.approvedByName === null ? "" : `, approved by ${record.approvedByName}`);
    const why = document.createElement("span");
    why.className = record.reason === null ? "rec-why rec-why-none" : "rec-why";
    why.dataset["testid"] = "line-stock-override-reason";
    why.textContent = record.reason ?? "No reason recorded (before this change)";
    strip.append(tag, who, why);
    row.append(strip);
  });
}

/** AU14-F15. After a line is sold past an 86 on this tablet, reads the ticket back
 * and takes the server's own record for that line (who approved is only known
 * there), then redraws. Best effort: the sale already happened, so a failed read
 * leaves the strip off until the ticket is next opened. */
async function refreshLineStockOverride(orderLineId: string): Promise<void> {
  const session = sessionOrUndefined();
  const orderId = state.orderId;
  if (session === undefined || orderId === undefined) {
    return;
  }
  const result = await getOrder(session.apiBaseUrl, session.tenantId, orderId);
  if (result.status !== 200 || state.orderId !== orderId) {
    return;
  }
  const record = result.body.lines.find((line) => line.id === orderLineId)?.stockOverride;
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  if (record === undefined || record === null || line === undefined) {
    return;
  }
  line.stockOverride = record;
  renderTicket();
}

/** The "Price changed" strip and the struck old price, drawn in place on the
 * ticket rows from the server's own resolution (state.orderDiscounts).
 * Info colour on purpose: green means a discount and amber a problem, and a
 * changed price is neither. The shipped row layout is untouched: the strip is
 * one more full-width element under the row, like the Auto special strip. */
function renderLinePriceChangeStrips(): void {
  const byLine = new Map<string, LinePriceChangeDto>();
  for (const change of state.orderDiscounts?.priceChanges ?? []) {
    byLine.set(change.orderLineId, change);
  }
  ticketLinesEl.querySelectorAll<HTMLElement>(".line[data-order-line-id]").forEach((row) => {
    row.querySelector(".line-price-change")?.remove();
    row.querySelector(".line-price-was")?.remove();
    const priceEl = row.querySelector<HTMLElement>(".line-price");
    priceEl?.classList.remove("has-price-change");
    const change = byLine.get(row.dataset["orderLineId"] ?? "");
    if (change === undefined || priceEl === null) {
      return;
    }
    row.classList.add("line-has-special");
    priceEl.className = "line-price has-price-change";
    priceEl.textContent = formatCents(change.priceCents);
    const was = document.createElement("span");
    was.className = "line-price-was";
    was.dataset["testid"] = "line-price-was";
    was.textContent = formatCents(change.quotedPriceCents);
    priceEl.append(was);

    // M5-R10 (DEC-355): the strip is one button. The whole location opens the
    // drink's price history; the badge is the ledger row count.
    const orderLineId = row.dataset["orderLineId"] ?? "";
    const ticketLine = state.lines.find((line) => line.orderLineId === orderLineId);
    const count = ticketLine?.priceChangeCount;
    const strip = document.createElement("button");
    strip.type = "button";
    strip.className = "line-price-change";
    strip.dataset["testid"] = "line-price-change-strip";
    strip.setAttribute("aria-label", `Price history for ${ticketLine?.name ?? "this drink"}`);
    // UX102 (click-through review, Ticket line after a price change): one compact line, "$7.00 to $5.00,
    // Manager's call", with the History button. The words "Price changed", who set it and who approved it stay in
    // the page for assistive tech (and the History sheet shows them in full); the blue location and the struck old
    // price already say what this is.
    const lineOne = document.createElement("span");
    lineOne.className = "lpc-l1";
    const tag = document.createElement("span");
    tag.className = "tag-price-changed sr-only";
    tag.textContent = "Price changed. ";
    const nameEl = document.createElement("span");
    nameEl.className = "line-price-change-name";
    nameEl.textContent = `${formatCents(change.quotedPriceCents)} to ${formatCents(change.priceCents)}`;
    const whyEl = document.createElement("span");
    whyEl.className = "line-price-change-why";
    whyEl.textContent = `, ${change.reason}`;
    const who = document.createElement("span");
    who.className = "line-price-change-who sr-only";
    const setBy = change.setBy === null ? "" : `, set by ${change.setBy}`;
    const approved = change.approvedBy === null ? "" : `, approved by ${change.approvedBy}`;
    who.textContent = `${setBy}${approved}`;
    lineOne.append(tag, nameEl, whyEl, who);
    const main = document.createElement("span");
    main.className = "lpc-main";
    main.append(lineOne);
    const go = document.createElement("span");
    go.className = "lpc-go";
    if (count !== undefined && count > 0) {
      const badge = document.createElement("span");
      badge.className = "lpc-count";
      badge.dataset["testid"] = "line-price-change-count";
      badge.textContent = String(count);
      go.append(badge);
    }
    // The word stays for assistive tech; what is drawn is the count and a chevron, so the location fits one line.
    const historyWord = document.createElement("span");
    historyWord.className = "sr-only";
    historyWord.textContent = "History";
    const chevron = document.createElement("span");
    chevron.className = "lpc-chevron";
    chevron.setAttribute("aria-hidden", "true");
    chevron.textContent = "\u203a";
    go.append(historyWord, chevron);
    strip.append(main, go);
    strip.addEventListener("click", () => openLinePriceHistory(orderLineId, strip));
    row.append(strip);
  });
}

/** The scheduled special that is actually reducing this drink (reduction
 * above zero), the same rule the strip draws by. */
function scheduledSpecialOnLine(orderLineId: string): OrderDiscountApplicationDto | undefined {
  return state.orderDiscounts?.applications.find(
    (application) =>
      application.origin === "scheduled" &&
      application.orderLineId === orderLineId &&
      application.reductionCents > 0,
  );
}

async function handleLineEditRemoveSpecialClick(): Promise<void> {
  const orderLineId = state.lineEditTargetOrderLineId;
  if (orderLineId === undefined) {
    return;
  }
  const application = scheduledSpecialOnLine(orderLineId);
  if (application === undefined) {
    return;
  }
  closeLineEditSheet();
  await openSpecialRemovalReasonPicker(application);
}

/** DSC35-S7b (docs/build-briefs/DSC35-S7-B1-remove-button.md; DEC-258 (3)).
 * The permission behind the line menu's Remove special: hasPermission is true for BOTH
 * allowed and approval_required, so an assistant manager sees it and is then
 * sent through the approval prompt by requestApproval; a lounger (denied)
 * never sees it. Never a role comparison. */
const SCHEDULED_SPECIAL_REMOVE_PERMISSION = "money.scheduled_special.remove";

function canRemoveScheduledSpecial(): boolean {
  const session = state.session;
  return session !== undefined && hasPermission(session, SCHEDULED_SPECIAL_REMOVE_PERMISSION);
}

/** The shared choice sheet of the tenant's discount reasons (live-read, no
 * blank option, nothing preselected, no typing), then the S4 route. */
async function openSpecialRemovalReasonPicker(
  application: OrderDiscountApplicationDto,
): Promise<void> {
  const session = requireSession();
  const result = await listDiscountReasons(session.apiBaseUrl, session.tenantId);
  if (result.status !== 200) {
    showToast(
      `Could not load the discount reasons. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  const reasons = sortByName(result.body.discountReasons, (reason) => reason.label);
  if (reasons.length === 0) {
    showToast(
      "There are no discount reasons set up for this location. An admin adds them in Settings.",
      "danger",
    );
    return;
  }
  openChoiceSheet({
    title: "Why remove this special?",
    subtitle: `${application.discountName} on ${discountTargetLabel(application.orderLineId)}`,
    options: reasons.map((reason) => ({
      id: reason.id,
      label: reason.label,
      testid: "special-removal-reason-option",
    })),
    onPick: (reasonId) => void submitSpecialRemoval(application, reasonId),
  });
}

async function submitSpecialRemoval(
  application: OrderDiscountApplicationDto,
  discountReasonId: string,
): Promise<void> {
  const session = requireSession();
  // One key for the whole flow, approval retry included.
  const idempotencyKey = crypto.randomUUID();
  const gated = await requestApproval(
    `Remove special: ${application.discountName}`,
    (approval) =>
      removeScheduledSpecial(session.apiBaseUrl, session.tenantId, application.id, {
        idempotencyKey,
        discountReasonId,
        ...approval,
      }),
    () => showRingScreen(),
  );
  if (gated.canceled) {
    return;
  }
  const result = gated.result;
  if (result.status !== 200 && result.status !== 201) {
    // The server's own plain refusal, never a raw status.
    showToast(
      `Could not remove that special. ${capitalizeServerMessage(result.body.message ?? "Try again.")}`,
      "danger",
    );
    showRingScreen();
    return;
  }
  showRingScreen();
  // Totals and the removed note come from the server, not from the response.
  await refreshPaymentPreview();
  renderTicket();
  showToast("Special removed.", "success");
}

// AU14-F5 / DEC-276: held from the Remove tap until the reason sheet is confirmed or
// dismissed, so a second tap cannot stack a second sheet.
let discountRemovalReasonFlowBusy = false;

/** The permission this flow resolves, named once. The button wording follows the
 * permission SNAPSHOT (never a role name). */
const REMOVE_DISCOUNT_PERMISSION = "money.discount.apply";

/** Reads the tenant's active discount removal reasons and asks which one applies.
 * Resolves with the picked reason, or undefined on Cancel, close, Escape, a tap
 * outside, sign-out, or a failed read (which is toasted). */
async function askForDiscountRemovalReason(
  application: OrderDiscountApplicationDto | undefined,
  session: { apiBaseUrl: string; tenantId: string; permissions: Record<string, string> },
): Promise<{ id: string; label: string } | undefined> {
  const gen = sessionGeneration;
  const result = await listActionReasons(session.apiBaseUrl, session.tenantId, {
    kind: "discount_removal",
  });
  if (gen !== sessionGeneration || sessionOrUndefined() === undefined) {
    return undefined;
  }
  const reasons: ActionReasonDto[] =
    result.status === 200 ? sortByName(result.body.actionReasons, (reason) => reason.label) : [];
  if (result.status !== 200 || reasons.length === 0) {
    showToast(
      result.status === 200
        ? "No reasons are set up for taking a discount off. Ask an admin to add one in Settings."
        : "Could not load the reasons. Check your connection and try again.",
      "danger",
    );
    return undefined;
  }
  const needsApproval = session.permissions[REMOVE_DISCOUNT_PERMISSION] === "approval_required";
  return new Promise((resolve) => {
    openReasonSheet({
      title: "Take this discount off?",
      subtitle: application === undefined ? undefined : application.discountName,
      contextTitle: application?.discountName ?? "Discount",
      contextDetail: discountTargetLabel(application?.orderLineId ?? null),
      contextFrom: "On",
      contextTo: "Off",
      heading: "Why is it coming off?",
      reasons,
      confirmLabel: needsApproval ? "Ask a manager to approve" : "Take discount off",
      pickedNote: needsApproval ? "A manager approves next and sees this reason." : "",
      onConfirm: (picked) => resolve(picked),
      onCancel: () => resolve(undefined),
    });
  });
}

async function submitDiscountRemoval(discountApplicationId: string): Promise<void> {
  const session = requireSession();
  if (discountRemovalReasonFlowBusy) {
    return;
  }
  const application = state.orderDiscounts?.applications.find(
    (candidate) => candidate.id === discountApplicationId,
  );
  discountRemovalReasonFlowBusy = true;
  let picked: { id: string; label: string } | undefined;
  try {
    picked = await askForDiscountRemovalReason(application, session);
  } finally {
    discountRemovalReasonFlowBusy = false;
  }
  if (picked === undefined) {
    // Cancel (or a failed read, already toasted): nothing changed, nothing sent.
    return;
  }
  const reason = picked;
  const idempotencyKey = crypto.randomUUID();
  const gated = await requestApproval(
    `Remove discount: ${application?.discountName ?? "discount"}`,
    (approval) =>
      removeOrderDiscount(session.apiBaseUrl, session.tenantId, discountApplicationId, {
        idempotencyKey,
        // The same reason rides every attempt, the first and the retry after a
        // manager's PIN, under the same key.
        reasonId: reason.id,
        ...approval,
      }),
    () => showRingScreen(),
    // The reason comes BEFORE the PIN, so the manager sees what they are
    // vouching for on the PIN pad's own context line.
    {
      pinContext: `Remove discount: ${application?.discountName ?? "discount"} \u00b7 ${reason.label}`,
    },
  );
  if (gated.canceled) {
    return;
  }
  const result = gated.result;
  if (result.status !== 200 && result.status !== 201) {
    showToast(
      `Could not remove that discount (${result.body.message ?? result.body.error ?? result.status}).`,
      "danger",
    );
    showRingScreen();
    return;
  }
  showRingScreen();
  await refreshPaymentPreview();
  renderTicket();
  showToast("Discount removed.", "success");
}

async function handleTicketDiscountClick(): Promise<void> {
  await openDiscountPicker({ label: state.orderName?.trim() || "Walk-in" });
}

async function handleLineEditDiscountClick(): Promise<void> {
  const orderLineId = state.lineEditTargetOrderLineId;
  if (orderLineId === undefined) {
    return;
  }
  const line = state.lines.find((candidate) => candidate.orderLineId === orderLineId);
  if (line === undefined) {
    return;
  }
  closeLineEditSheet();
  await openDiscountPicker({ orderLineId, label: line.name });
}
